import { authFetch, signInEmail, signInFloorPin, signOut as authSignOut, useAuthUser, can, friendlyAuthError, redeemHandoffFromUrl, mintHandoff } from './auth';
import { THEMES, THEME_CATALOGUE, ThemeContext, useTheme, getTheme, DEFAULT_THEME, SEMANTIC, CRASH_SCREEN } from './theme';
import { TZ_COUNTRIES } from './timezones';

// theme.js is Lindsay's file. Schemes get added, renamed and removed there, and
// App.js must never name one literally - a colour edit in a file this side does
// not control should not be able to white-screen the platform. It already has
// once: a scheme key was hardcoded in five places, renamed upstream, and every
// admin screen died reading .bgBase off undefined.
//
// Resolution order: the preferred scheme, then whatever scheme exists, then a
// literal floor. The floor matters more than it looks - it guarantees an object,
// so a missing key renders as an ignored CSS value instead of throwing. An ugly
// screen is recoverable; a blank one is not.
const SAFE_THEME_FLOOR = {
  bgBase: '#1a1512', bgSurface: '#241d18',
  textPrimary: '#f0e6d8', textSecondary: '#c4b5a0', textDim: '#8a7a68',
  accent: '#c9a227', accentDim: 'rgba(201,162,39,0.15)',
  borderSubtle: 'rgba(255,255,255,0.08)', borderMid: 'rgba(255,255,255,0.15)',
  white04: 'rgba(255,255,255,0.04)',
  success: '#7fa650', warning: '#d08a3e', danger: '#c15f4a', white: '#ffffff',
  isLight: false,
  fontSerif: "'Playfair Display', Georgia, serif",
};
function resolveTheme(preferredKey) {
  const all = THEMES || {};
  return (preferredKey && all[preferredKey]) || Object.values(all)[0] || SAFE_THEME_FLOOR;
}
const ADMIN_THEME = resolveTheme(DEFAULT_THEME.name);

import { input, inputLarge, label as labelStyleFor } from './styles';
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
// Guest text goes through tr() - see src/i18n.js and the i18n section of CLAUDE.md.
// Not t(): t is the theme object throughout this file.
import { tr, setLanguage, getLanguage, getRequestedLanguage, loadLanguage, onLanguageLoaded } from './i18n';

// tr() for a sentence with an element inside it ("Tap <strong>Done ✓</strong>
// when..."). The element goes in a {slot} like any other value, so a translator
// can move it to wherever the sentence needs it rather than the code fixing the
// word order. `nodes` fill element slots; `vars` fill text slots and pick the
// plural (count). Returns an array React renders as-is.
function trRich(key, nodes, vars) {
  return tr(key, vars).split(/(\{\w+\})/).filter(part => part !== "").map((part, i) => {
    const slot = part.match(/^\{(\w+)\}$/);
    return slot && slot[1] in nodes ? <React.Fragment key={i}>{nodes[slot[1]]}</React.Fragment> : part;
  });
}

// Locale for dates shown to a guest. English stays 'en-US' (what these calls
// used before - 'Thursday, October 29, 2026'); other languages use their own
// code, so fr-CA gets 'jeudi 29 octobre 2026'.
function dateLocale() {
  const lang = getLanguage();
  return lang === "en" ? "en-US" : lang;
}

// ─── Restaurant ID ────────────────────────────────────────────────────────────
// Detected from subdomain (corduroy.getsommsync.com → 'corduroy')
// Falls back to ?restaurant= query param for local dev, then null if neither
// resolves - see the App-level guard for what that shows the visitor.
// ─── CSV parsing (menu import) ────────────────────────────────────────────────
// Menu exports routinely contain commas inside quoted fields ("Cabernet
// Sauvignon, '18, Napa Valley, CA"), so a split(',') parser corrupts the data
// silently. This walks the string character by character instead.
function parseCsv(text) {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  const src = String(text).replace(/^\uFEFF/, ''); // strip BOM from Excel exports
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some(v => v !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some(v => v !== '')) rows.push(row);
  if (rows.length < 2) return [];
  const headers = rows[0].map(h => h.trim());
  return rows.slice(1).map(r => {
    const o = {};
    headers.forEach((h, i) => { o[h] = (r[i] || '').trim(); });
    return o;
  });
}

// ─── Menu import categories ───────────────────────────────────────────────────
// A CSV upload is the alternative to a POS sync, so it covers the same six
// categories a sync does. `key` is the importMenuData request field. Every one
// is optional - a restaurant with no cocktail program simply never attaches one.
//
// `columns` is what the manager is told to put in the file. Ids must be stable
// between uploads: enrichment, approvals and 86 flags all hang off them.
const IMPORT_CATEGORIES = [
  { key: 'wines',     label: 'Wine list',              columns: 'id, name, tier, subgroup, glassPrice, bottlePrice or priceTier' },
  { key: 'foodItems', label: 'Food menu',              columns: 'id, name, course, description, price' },
  { key: 'beers',     label: 'Beer list',              columns: 'id, name, subgroup, price' },
  { key: 'pours',     label: 'Premium pours',          columns: 'id, name, subgroup, price' },
  { key: 'cocktails', label: 'Cocktails',              columns: 'id, name, subgroup, price, description (required)' },
  { key: 'nab',       label: 'Non-alcoholic',          columns: 'id, name, subgroup, price, description (required)' },
];

// Placeholder glyph when an item has no label photo. 🍸 rather than 🍹 for
// cocktails: the tropical drink reads as a beach bar, which is the wrong room
// for a steakhouse. Mocktails keep 🍹 — there the colourful reading is right.
function itemGlyph(kind, subgroup) {
  if (kind === 'beers' || kind === 'beer') return '\u{1F37A}';
  if (kind === 'cocktails' || kind === 'cocktail') return '\u{1F378}';
  if (kind === 'nab') return (subgroup || '').toLowerCase() === 'mocktails' ? '\u{1F379}' : '\u{1F964}';
  return '\u{1F943}';
}

function readFileText(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error(`Could not read ${file.name}`));
    fr.readAsText(file);
  });
}

// ─── Base reset ──────────────────────────────────────────────────────────────
// index.html links no stylesheet and there is no index.css, so browser defaults
// apply - including `body { margin: 8px }`. That 16px of vertical margin sits
// OUTSIDE every `minHeight: 100vh` screen in this file, so each one is
// guaranteed to overflow the viewport by 16px no matter what it contains.
//
// Deliberately narrow. A global `box-sizing: border-box` belongs here too and
// would be correct, but this file is ~10,000 lines of inline styles written
// against content-box, and flipping it wholesale is a separate, testable change.
// Applied per-element where it matters instead.
if (typeof document !== 'undefined' && !document.getElementById('somm-base-reset')) {
  const resetEl = document.createElement('style');
  resetEl.id = 'somm-base-reset';
  resetEl.textContent = 'html,body{margin:0;padding:0;}';
  document.head.appendChild(resetEl);
}

// ─── Build stamp ─────────────────────────────────────────────────────────────
// Injected by deploy-hosting.yml as REACT_APP_BUILD (short commit SHA). CRA
// inlines REACT_APP_* at build time, so this is a literal in the bundle - which
// is the point: it identifies the BUNDLE, not the server. A tablet still running
// last week's cached JavaScript reports last week's SHA.
//
// 'dev' locally, where the variable is unset.
const BUILD_ID = process.env.REACT_APP_BUILD || 'dev';

// ─── Kiosk device id ─────────────────────────────────────────────────────────
// Fully Kiosk Browser's id for this tablet (e.g. 7d65e417-4ebf801f), the same one
// Fully Cloud lists the device under. Shown beside the build stamp so a restaurant
// calling about "a tablet that isn't working" can read out which one, and we can
// find it in Fully Cloud without guessing.
//
// Comes from Fully's JavaScript interface (`window.fully`), which is OFF unless the
// kiosk configuration sets `websiteIntegration: true` - confirmed on the tablets:
// no id until it was turned on. (One earlier build showed the id without it; never
// explained, so do not rely on that.) Off, or on a phone, or in any ordinary
// browser, `window.fully` does not exist and nothing is shown.
//
// Read once and cached: it never changes for the life of the page, and every call
// crosses into Fully's Java side.
let fullyDeviceIdCache;
function fullyDeviceId() {
  if (fullyDeviceIdCache !== undefined) return fullyDeviceIdCache;
  fullyDeviceIdCache = null;
  try {
    if (typeof window !== 'undefined' && window.fully && typeof window.fully.getDeviceId === 'function') {
      const id = String(window.fully.getDeviceId() || '').trim();
      if (id) fullyDeviceIdCache = id;
    }
  } catch (e) { /* interface present but refused - show nothing */ }
  return fullyDeviceIdCache;
}

// ─── Hard reload ─────────────────────────────────────────────────────────────
// window.location.reload() does NOT bypass the HTTP cache - the old forceReload
// argument was removed from the spec years ago and is ignored. On a kiosk webview
// that has cached index.html despite the no-cache header, a plain reload can hand
// back the same stale bundle indefinitely, which is exactly the hour we lost.
//
// Three levers, weakest to strongest:
//   1. Cache Storage - only used by service workers, but free to clear
//   2. Service worker registrations - CRA can register one; unregister it
//   3. A cache-busting query param on the document URL - the only lever that
//      reliably defeats the webview's own HTTP cache, because it changes the
//      resource being requested rather than asking politely
//
// The param is stripped on arrival (see stripReloadParam) following the same
// pattern auth.js uses for the handoff nonce, so it never accumulates and the
// address bar stays clean.
const RELOAD_PARAM = 'r';

// Levers 1 and 2 are best-effort cleanup, and each is capped so it can never stop
// lever 3 from running. Oct 2026: Start Over silently did nothing on a kiosk tablet
// right after an Android system update (new WebView), while every other link
// worked. The cleanup was awaited with no limit, so a cache or service-worker call
// that never settles - rather than one that throws, which the try/catch already
// handled - left the reload waiting forever. The reload is the point; the cleanup
// is a bonus.
const RELOAD_CLEANUP_MS = 800;
function settleWithin(promise, ms) {
  return Promise.race([
    Promise.resolve(promise).catch(() => {}),
    new Promise(resolve => setTimeout(resolve, ms)),
  ]);
}

let hardReloadStarted = false;

async function hardReload() {
  // Never navigate while the connection watch says we're offline: the page would
  // be replaced by the webview's own "Webpage not available" screen, which is
  // worse than the menu still sitting there with the notice above it. The watch
  // reloads by itself once the connection is confirmed back.
  if (connectionLost) { checkConnection(); return; }
  // A double tap must not start two navigations.
  if (hardReloadStarted) return;
  hardReloadStarted = true;

  // Pre-flight: prove the server answers RIGHT NOW before giving up the page.
  // Reproduced Oct 2026 in a weak-signal room: the notice had cleared, Start Over
  // was tapped, the progress bar crawled, and the page died on
  // net::ERR_CONNECTION_ABORTED - Android's "Webpage not available", which no
  // code of ours can draw over. A probe costs a second; if it fails we keep the
  // menu, raise the notice, and let the watch reload once the connection holds.
  if (!(await probeConnection())) {
    hardReloadStarted = false;
    setConnectionLost(true);
    checkConnection();
    return;
  }

  await settleWithin((async () => {
    if (window.caches && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
  })(), RELOAD_CLEANUP_MS);
  await settleWithin((async () => {
    if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
  })(), RELOAD_CLEANUP_MS);

  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, Date.now().toString(36));
  // replace() rather than assign() so Start Over does not stack history entries
  // on a kiosk that may be reset dozens of times a night.
  try {
    window.location.replace(url.toString());
  } catch (e) {
    // Never leave the button dead: fall back to a plain reload.
    window.location.reload();
  }
  // If the navigation was refused without throwing, allow another attempt.
  setTimeout(() => { hardReloadStarted = false; }, 5000);
}

// Strip the cache-buster once the document carrying it has loaded. The fetch has
// already happened by this point, so removing it costs nothing and keeps it out
// of history, referrers and any QR link a guest might share.
function stripReloadParam() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(RELOAD_PARAM)) return;
    params.delete(RELOAD_PARAM);
    const clean = window.location.pathname + (params.toString() ? `?${params}` : '');
    window.history.replaceState({}, '', clean);
  } catch (e) { /* non-fatal */ }
}

function detectRestaurantId() {
  const urlParam = new URLSearchParams(window.location.search).get('restaurant');
  if (urlParam) return urlParam;
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  // 3+ part hostname that isn't localhost or Firebase preview domains
  if (parts.length >= 3 &&
      !hostname.includes('localhost') &&
      !hostname.includes('web.app') &&
      !hostname.includes('firebaseapp.com')) {
    return parts[0];
  }
  // ⚠️ Previously defaulted to 'corduroy' - a leftover from before this app
  // was multi-tenant. That meant the bare sommsync-platform.web.app URL with
  // no ?restaurant= param silently rendered Corduroy's real, live guest menu
  // to whoever hit it. Returning null instead makes every downstream fetch
  // resolve to a restaurantId of the literal string "null", which matches no
  // real tenant and fails safely (empty results), rather than exposing a
  // paying customer's data. See the App-level guard right after this
  // function for what the person actually sees in this case.
  console.error('[detectRestaurantId] No restaurant could be determined from the URL.');
  return null;
}
const RESTAURANT_ID = detectRestaurantId();

// ─── ?lang= : open the page in a given language ──────────────────────────────
// Overrides the restaurant's default language for this page (Oct 29, 2026). For
// demo links sent to prospects (demo.getsommsync.com/?lang=fr) and for a kiosk
// whose start URL should open in a set language (a Montreal bar's English
// tablet: ?lang=en). Survives Start Over and the idle reload, which keep the
// query string. Short codes mean our variants: fr = Quebec French, es = Mexican
// Spanish (the languages decided Oct 29, 2026). Anything unsupported is ignored.
function detectUrlLanguage() {
  try {
    const v = (new URLSearchParams(window.location.search).get('lang') || '').trim().toLowerCase();
    if (v === 'en' || v.startsWith('en-')) return 'en';
    if (v === 'fr' || v.startsWith('fr-')) return v === 'fr' ? 'fr-CA' : v.replace(/-(\w+)$/, (_, r) => `-${r.toUpperCase()}`);
    if (v === 'es' || v.startsWith('es-')) return v === 'es' ? 'es-MX' : v.replace(/-(\w+)$/, (_, r) => `-${r.toUpperCase()}`);
    // Single-variant languages: the base code.
    const base = v.split('-')[0];
    if (['de', 'it', 'pl', 'cs', 'sk', 'hu', 'ro', 'ru', 'uk', 'el'].includes(base)) return base;
    if (v === 'ja' || v.startsWith('ja-')) return 'ja';
    if (v === 'ko' || v.startsWith('ko-')) return 'ko';
    if (v === 'zh' || v.startsWith('zh-')) return 'zh-CN';
  } catch (e) { /* no window */ }
  return null;
}
const URL_LANG = typeof window !== 'undefined' ? detectUrlLanguage() : null;
// At load, so the very first paint is already in this language.
if (URL_LANG) setLanguage(URL_LANG);

// Theme keys arrive from the server with appSettings, so the first paint happens
// before they exist and renders whatever the hardcoded fallback is - a visible
// flash of the wrong restaurant's colours on every load.
//
// Tablets are fixed devices showing one restaurant, so last session's theme is
// almost always this session's. Caching it per restaurant makes the first paint
// correct; the server value still wins the instant it lands, so a theme change
// upstream costs one flash, once, on one device.
const THEME_CACHE_KEY = `themeCache:${RESTAURANT_ID}`;
function readCachedThemes() {
  try { return JSON.parse(localStorage.getItem(THEME_CACHE_KEY) || 'null') || null; }
  catch { return null; }
}
function writeCachedThemes(barTheme, diningTheme) {
  try {
    if (!barTheme && !diningTheme) return;
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({ barTheme, diningTheme }));
  } catch {}
}

// Same idea for the logo URLs. Without this, a device whose getSettings call
// is slow (a cold start past the home screen's 2.5s gate) or fails paints the
// restaurant's NAME where the logo goes, because the logo URLs live only in
// settings. With it, any device that has loaded this tenant once shows the
// logo at once. The server value still replaces it when it lands.
const LOGO_CACHE_KEY = `logoCache:${RESTAURANT_ID}`;
const LOGO_CACHE_FIELDS = ["barLogo", "diningLogo", "barLogoUrl", "diningLogoUrl", "barAltLogoUrl", "diningAltLogoUrl"];
function readCachedLogos() {
  try { return JSON.parse(localStorage.getItem(LOGO_CACHE_KEY) || 'null') || {}; }
  catch { return {}; }
}
function writeCachedLogos(ds) {
  try {
    const out = {};
    LOGO_CACHE_FIELDS.forEach(k => { if (ds && ds[k]) out[k] = ds[k]; });
    localStorage.setItem(LOGO_CACHE_KEY, JSON.stringify(out));
  } catch {}
}

// ─── API Endpoints ────────────────────────────────────────────────────────────
const BASE = "https://us-central1-sommsync-platform.cloudfunctions.net";
const FIREBASE_URL      = `${BASE}/getWines`;
const BEER_URL          = `${BASE}/getBeers`;
const POURS_URL         = `${BASE}/getPours`;
const FOOD_URL          = `${BASE}/getFoodItems`;
const COCKTAILS_URL     = `${BASE}/getCocktails`;
const NAB_URL           = `${BASE}/getNAB`;
const PAIRING_URL       = `${BASE}/getPairing`;
const MATRIX_PAIRING_URL = `${BASE}/getMatrixPairing`;
const CHAT_URL          = `${BASE}/sommelierChat`;
const CHAT_LEAN_URL     = `${BASE}/sommelierChatLean`;
const SETTINGS_URL      = `${BASE}/getSettings`;
const MENU_SOURCE_URL   = `${BASE}/getMenuSource`;
const SAVE_SETTINGS_URL = `${BASE}/saveSettings`;
const SET_FLOOR_PIN_URL = `${BASE}/setFloorPin`;
const FLOOR_PIN_STATUS_URL = `${BASE}/getFloorPinStatus`;
const CLEAR_PIN_LOCKOUT_URL = `${BASE}/clearFloorPinLockout`;
const LIST_ACCOUNTS_URL  = `${BASE}/listUserAccounts`;
const CREATE_ACCOUNT_URL = `${BASE}/createUserAccount`;
const UPDATE_ACCOUNT_URL = `${BASE}/updateUserAccount`;
const DELETE_ACCOUNT_URL = `${BASE}/deleteUserAccount`;
const MANAGER_UPDATE_URL= `${BASE}/managerUpdateEnrichment`;
const REENRICH_URL      = `${BASE}/reenrichItem`;

// Hints are sent to Claude as "confirmed by manager — treat as fact", so only
// fields the manager actually CHANGED may be sent. The edit form is pre-filled
// from the existing enrichment, so sending the whole form re-asserts the item's
// own errors as ground truth and the model can never correct them: a Barolo
// wrongly tagged Sangiovese stayed Sangiovese through every re-enrichment,
// because the stale value was being handed back as fact each time.
//
// Comparing against a baseline captured when the form opened (rather than
// tracking onChange) also means typing the same value back counts as unedited,
// which is the right reading — the manager confirmed nothing new.
// Fields the UI reads that are NOT visible inputs on the edit form. A handler
// that patches only the fields it can see leaves these stale, and they drive
// real behaviour: `manuallyEdited` renders a badge AND filters the Needs
// Approval list, `approved` decides whether guests see the item at all.
// After a re-enrich the server has authoritative values for every one of them,
// so carry the lot rather than guessing which ones moved.
function enrichmentStateFields(e = {}) {
  return {
    uncertain: e.uncertain || false,
    uncertainReason: e.uncertain ? (e.uncertainReason || null) : null,
    manuallyEdited: e.manuallyEdited || false,
    identityConfirmed: e.identityConfirmed || false,
    approved: e.approved || false,
    approvedBy: e.approvedBy || null,
    pendingApproval: e.pendingApproval || false,
    grapes: e.grapes || null,
    ...(e.masterKey ? { masterKey: e.masterKey } : {}),
    ...(e.labelImageUrl ? { labelImageUrl: e.labelImageUrl } : {}),
  };
}

function changedFields(current = {}, baseline = {}, keys = []) {
  const out = {};
  keys.forEach(k => {
    const cur = current[k];
    const base = baseline[k];
    const norm = v => (v === undefined || v === null) ? '' : String(v).trim();
    if (norm(cur) !== norm(base)) out[k] = cur;
  });
  return out;
}
const SET_EXCLUSION_URL = `${BASE}/setItemExclusion`;
const SAVE_MENU_URL     = `${BASE}/saveMenu`;
const GET_MENU_URL      = `${BASE}/getMenu`;
const SEND_EMAIL_URL    = `${BASE}/sendMenuEmail`;
const FORCE_SYNC_URL    = `${BASE}/forceSync`;
const APPROVE_ITEMS_URL     = `${BASE}/approveItems`;
const APPROVE_IMAGE_URL     = `${BASE}/approveImage`;
const REJECT_IMAGE_URL      = `${BASE}/rejectImage`;
const PREV_IMAGE_URL        = `${BASE}/previousImage`;
const UPLOAD_IMAGE_URL      = `${BASE}/uploadMasterImage`;
const UPLOAD_ITEM_IMAGE_URL = `${BASE}/uploadItemImage`;
const DISQUALIFY_IMAGE_URL  = `${BASE}/disqualifyMasterItem`;
const NO_IMAGE_FOUND_URL    = `${BASE}/markNoImageFound`;
const IMAGE_QUEUE_URL       = `${BASE}/getSuperUserImageQueue`;
const LABEL_COVERAGE_URL    = `${BASE}/getLabelCoverage`;
const AUDIT_MASTER_URL      = `${BASE}/auditMasterDuplicates`;
const VERIFY_MASTER_URL     = `${BASE}/verifyMasterPointers`;
const CALL_LOG_URL          = `${BASE}/getCallLog`;
const EXPORT_LOG_URL        = `${BASE}/exportSessionLog`;
const SET_CALL_LOG_URL      = `${BASE}/setCallLog`;
const RETRY_LABELS_URL      = `${BASE}/retryLabelImages`;
const MASTER_CORRECTIONS_URL = `${BASE}/getMasterCorrections`;
const RESOLVE_CORRECTION_URL = `${BASE}/resolveMasterCorrection`;
const REPORT_IMAGE_URL      = `${BASE}/reportLabelImage`;
const IMAGE_REPORTS_URL     = `${BASE}/imageReports`;
const SYNC_STATUS_URL       = `${BASE}/getSyncStatus`;
const START_ONBOARDING_URL  = `${BASE}/startOnboardingSync`;
const PAUSE_ONBOARDING_URL  = `${BASE}/pauseOnboardingSync`;
const RESUME_ONBOARDING_URL = `${BASE}/resumeOnboardingSync`;
const RESTART_ONBOARDING_URL = `${BASE}/restartOnboardingSync`;
const PIPELINE_STATUS_URL   = `${BASE}/getPipelineStatus`;
const APPROVE_LARGE_LIST_URL = `${BASE}/approveLargeListOnboarding`;
const GENERATE_MATRIX_URL   = `${BASE}/generatePairingMatrix`;
const MATRIX_STATUS_URL     = `${BASE}/getMatrixStatus`;
const INSPECT_TOAST_URL     = `${BASE}/inspectToastItem`;
const INCR_TEST_URL         = `${BASE}/testIncrementalScoring`;
const SETUP_RESTAURANT_URL  = `${BASE}/setupRestaurant`;
// setupRestaurant CREATES a tenant. Changing a secret afterwards is a separate
// endpoint, because .set() on the credentials node would drop posType and
// re-running Add Restaurant also resets syncPipeline.
const TEST_POS_CONNECTION_URL = `${BASE}/testPosConnection`;
const UPDATE_CREDENTIALS_URL  = `${BASE}/updateRestaurantCredentials`;
// Menu schedules are evaluated in the restaurant's timezone, so the zones that
// do NOT follow their neighbours' daylight-saving rules matter most: Phoenix
// and Regina stay on standard time all year.
// ── Time zones by country ──────────────────────────────────────────────────────────────────────────────────────
// A restaurant's time zone decides when each menu is shown (lunch, dinner, happy hour), so it has to be right. The
// picker asks for the COUNTRY first and then the zone, from the whole tz database (src/timezones.js: every country,
// every zone), not a short list. The 15 zones the app offered before keep their familiar names and come first in
// their country. The offset shown (UTC-5) is today's, so it moves with daylight saving.
const TZ_FRIENDLY = {
  'America/New_York': 'Eastern (New York)', 'America/Toronto': 'Eastern (Toronto)', 'America/Chicago': 'Central (Chicago)',
  'America/Winnipeg': 'Central (Winnipeg)', 'America/Regina': 'Saskatchewan (no daylight saving)', 'America/Denver': 'Mountain (Denver)',
  'America/Edmonton': 'Mountain (Edmonton)', 'America/Phoenix': 'Arizona (no daylight saving)', 'America/Los_Angeles': 'Pacific (Los Angeles)',
  'America/Vancouver': 'Pacific (Vancouver)', 'America/Anchorage': 'Alaska', 'Pacific/Honolulu': 'Hawaii',
  'America/Halifax': 'Atlantic (Halifax)', 'America/St_Johns': 'Newfoundland', 'America/Puerto_Rico': 'Puerto Rico',
};
// The obvious main zone of each large country that has several, so it is not buried among remote ones (Sydney before
// Perth, Sao Paulo before Eirunepe). Anything not named here follows, from west to east.
const TZ_MAIN_FIRST = [
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Perth', 'Australia/Adelaide', 'America/Sao_Paulo',
  'America/Mexico_City', 'Europe/Moscow', 'Asia/Shanghai', 'Asia/Jakarta', 'Asia/Almaty', 'America/Argentina/Buenos_Aires',
  'America/Santiago', 'Pacific/Auckland', 'Europe/Madrid', 'Europe/Lisbon', 'Europe/Berlin', 'America/Guayaquil',
  'Africa/Kinshasa', 'Asia/Ulaanbaatar', 'Asia/Tashkent', 'Pacific/Port_Moresby', 'America/Nuuk',
];
const TZ_LEAD = [...Object.keys(TZ_FRIENDLY), ...TZ_MAIN_FIRST];
const TZ_ZONE_COUNTRY = {}; const TZ_ZONE_NOTE = {};
TZ_COUNTRIES.forEach(([code, , zs]) => zs.forEach(([id, note]) => { TZ_ZONE_COUNTRY[id] = code; if (note) TZ_ZONE_NOTE[id] = note; }));
const TZ_COUNTRY_LIST = (() => {
  let dn = null; try { dn = new Intl.DisplayNames(['en'], { type: 'region' }); } catch (e) { dn = null; }
  return TZ_COUNTRIES.map(([code, name, zones]) => { let n = name; try { n = (dn && dn.of(code)) || name; } catch (e) { n = name; } return { code, name: n, zones }; })
    .sort((x, y) => x.name.localeCompare(y.name, 'en'));
})();
const TZ_MOST_USED = ['US', 'CA'];
function tzOffsetMinutes(id, at = new Date()) {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: id, timeZoneName: 'longOffset' }).formatToParts(at).find(x => x.type === 'timeZoneName');
    if (!part) return null;
    if (part.value === 'GMT') return 0;
    const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(part.value);
    return m ? (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3] || '0', 10)) : null;
  } catch (e) { return null; }
}
function tzOffsetLabel(id, at) {
  const m = tzOffsetMinutes(id, at);
  if (m === null) return '';
  if (m === 0) return 'UTC';
  const a = Math.abs(m), h = Math.floor(a / 60), mm = a % 60;
  return `UTC${m < 0 ? '\u2212' : '+'}${h}${mm ? ':' + String(mm).padStart(2, '0') : ''}`;
}
function tzZoneLabel(id, note) {
  const city = id.split('/').pop().replace(/_/g, ' ');
  const base = TZ_FRIENDLY[id] || (note ? `${city} \u00b7 ${note}` : city);
  const off = tzOffsetLabel(id);
  return off ? `${base} \u00b7 ${off}` : base;
}
// A zone's full label, for a pop-up or a status line. A value the list does not know (an old or unusual one) shows as it is.
function tzLabelFor(id) { return id ? (TZ_ZONE_COUNTRY[id] ? tzZoneLabel(id, TZ_ZONE_NOTE[id]) : id) : ''; }
function tzZonesFor(code) {
  const c = TZ_COUNTRY_LIST.find(x => x.code === code);
  if (!c) return [];
  const now = new Date();
  // Order: the familiar zones the app always had, then each big country's main zone (TZ_MAIN_FIRST), then the rest
  // from west to east.
  return c.zones.map(([id, note]) => ({ id, label: tzZoneLabel(id, note), order: TZ_LEAD.indexOf(id), off: tzOffsetMinutes(id, now) }))
    .sort((x, y) => {
      const fx = x.order >= 0, fy = y.order >= 0;
      if (fx !== fy) return fx ? -1 : 1;
      if (fx) return x.order - y.order;
      return ((x.off || 0) - (y.off || 0)) || x.label.localeCompare(y.label);
    });
}
// Country, then time zone. mode "form" is two labelled full-width fields (Add Restaurant); mode "card" is two small
// selects in a line (the restaurant card). `onChange(zone)` gets the chosen zone, or '' in the form while a country with
// several zones is picked and no zone yet, so a form can never save the old country's clock by accident. In the card
// nothing is saved until a zone is chosen; wandering off leaves it as it was. A country with one zone needs no second choice.
function TimezonePicker({ t, mode = 'form', value, onChange, selectStyle }) {
  const [picked, setPicked] = useState(null);
  useEffect(() => { if (value) setPicked(null); }, [value]);
  const currentCountry = TZ_ZONE_COUNTRY[value] || null;
  const country = picked || currentCountry || (value ? '__other' : '');
  const pending = !!picked && picked !== currentCountry;
  const zones = country && country !== '__other' ? tzZonesFor(country) : [];
  const stop = e => e.stopPropagation();
  function chooseCountry(code) {
    if (code === currentCountry) { setPicked(null); return; }
    const zs = tzZonesFor(code);
    if (zs.length === 1) { setPicked(null); onChange(zs[0].id); return; }
    setPicked(code);
    if (mode === 'form') onChange('');
  }
  function chooseZone(id) { setPicked(null); if (id) onChange(id); }
  const mostUsed = TZ_MOST_USED.map(c => TZ_COUNTRY_LIST.find(x => x.code === c)).filter(Boolean);
  const countrySelect = (
    <select aria-label="Country" title="The country the restaurant is in" value={country} onClick={stop}
      onChange={e => { stop(e); chooseCountry(e.target.value); }} style={mode === 'card' ? { ...selectStyle, maxWidth: 190 } : selectStyle}>
      {country === '__other' && <option value="__other">Other (not in the list)</option>}
      {!country && <option value="">Choose a country</option>}
      <optgroup label="Most used">{mostUsed.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}</optgroup>
      <optgroup label="All countries">{TZ_COUNTRY_LIST.filter(c => !TZ_MOST_USED.includes(c.code)).map(c => <option key={c.code} value={c.code}>{c.name}</option>)}</optgroup>
    </select>
  );
  const zoneSelect = (
    <select aria-label="Time zone" title="Timezone used for menu hours" value={pending ? '' : (value || '')} onClick={stop}
      onChange={e => { stop(e); chooseZone(e.target.value); }}
      style={{ ...(mode === 'card' ? { ...selectStyle, maxWidth: 300 } : selectStyle), ...(pending && mode === 'card' ? { borderColor: t.warning } : {}) }}>
      {(pending || !value) && <option value="">Choose a time zone</option>}
      {country === '__other' && value && <option value={value}>{value}</option>}
      {zones.map(z => <option key={z.id} value={z.id}>{z.label}</option>)}
    </select>
  );
  if (mode === 'card') {
    // wandering off with a country chosen and no zone leaves the card as it was
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
      onBlur={e => { if (picked && !e.currentTarget.contains(e.relatedTarget)) setPicked(null); }}>{countrySelect}{zoneSelect}</span>;
  }
  const label = { color: t.textSecondary, fontSize: 11, marginBottom: 4 };
  return (
    <>
      <div><div style={label}>Country</div>{countrySelect}</div>
      <div><div style={label}>Time zone</div>{zoneSelect}</div>
    </>
  );
}
const IMPORT_MENU_DATA_URL  = `${BASE}/importMenuData`;
const LIST_RESTAURANTS_URL  = `${BASE}/listRestaurants`;
const UPDATE_RESTAURANT_PLAN_URL = `${BASE}/updateRestaurantPlan`;
const UPLOAD_RESTAURANT_LOGO_URL = `${BASE}/uploadRestaurantLogo`;
const GENERATE_PDF_URL      = `${BASE}/generateMenuPDF`;
const SCRAPE_ABOUT_US_URL   = `${BASE}/scrapeAboutUs`;
const SAVE_ABOUT_US_URL     = `${BASE}/saveAboutUs`;
const GET_PROFILE_URL       = `${BASE}/getProfile`;
const TRIGGER_ENRICHMENT_URL = `${BASE}/triggerEnrichment`;
const ENRICH_PROGRESS_URL   = `${BASE}/getEnrichmentProgress`;
const MENU_COUNTS_URL       = `${BASE}/getMenuCounts`;

// Per-session cache of the category counts. HomeScreen unmounts every time a
// guest opens a list and remounts when they come back, so without this the
// first-paint gate below would re-block on every return trip.
// Only a COMPLETE answer is kept for the session. A partial one (the server
// omits any category it could not check) is used for this mount and fetched
// again, so a failure on a cold start never sticks until the next reload.
let cachedMenuCounts = null;
const MENU_COUNT_KEYS = ["wine", "beer", "pours", "cocktails", "nab"];
const GET_PDF_PROGRESS_URL  = `${BASE}/getPdfProgress`;
const DRAFT_ABOUT_US_URL    = `${BASE}/draftAboutUs`;
const IDENTIFY_LABEL_URL    = `${BASE}/identifyLabel`;
const GET_POS_ADAPTERS_URL  = `${BASE}/getPosAdapters`;
const GET_USAGE_STATS_URL   = `${BASE}/getUsageStats`;
const GET_RELEASE_STATE_URL = `${BASE}/getReleaseState`;
const SET_RELEASE_STATE_URL = `${BASE}/setReleaseState`;
const SET_DEMO_STATUS_URL   = `${BASE}/setDemoStatus`;
const SET_SERP_PAUSED_URL   = `${BASE}/setSerpApiPaused`;
const GET_SERP_PAUSED_URL   = `${BASE}/getSerpApiPaused`;
const EXPORT_ENRICHMENT_URL = `${BASE}/exportEnrichment`;

// PINs are no longer compiled into the bundle. The floor PIN is verified
// server-side against a hash; owners and super users use email + password.
const PIN_LENGTH = 6;
const LARGE_LIST_THRESHOLD = 500; // Must match LARGE_LIST_THRESHOLD in functions/index.js



// ─── Restaurant-Aware Fetch ───────────────────────────────────────────────────
// Automatically injects restaurantId into every API call.
// GET requests: added as ?restaurantId= query param
// POST requests: added to JSON body as well (belt and suspenders)
// Async now, so it can attach the bearer token. Existing `rfetch(x).then(...)`
// call sites keep working — an async function returns a promise either way.
// One id per page load, per device. Not persisted: a reload is a new guest as far
// as costing is concerned, and that is the unit a test day wants to measure.
// It also seeds pairing selection server-side, so a table sees consistent
// recommendations while the next table sees different ones.
const GUEST_SESSION_ID = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

// ─── Public Demo Token ────────────────────────────────────────────────────────
// Deliberately the opposite lifetime of GUEST_SESSION_ID above: that one resets
// every page load so a reload counts as a new guest for costing purposes. This
// one is persisted in localStorage because the whole point is to survive a
// reload - it is what lets a public-demo tenant (see consumeDemoTurn on the
// backend) cap how much a single visitor can use before hitting the email
// wall. Harmless on every other tenant: the backend only acts on it when
// profile.publicDemo is true, so it rides along unused everywhere else.
const DEMO_TOKEN_KEY = 'ss_demo_token';
function getDemoToken() {
  const KEY = DEMO_TOKEN_KEY;
  try {
    let t = window.localStorage.getItem(KEY);
    if (!t) {
      t = `d_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(KEY, t);
    }
    return t;
  } catch (e) {
    // Private browsing or storage blocked - fall back to a per-load token.
    // Turns still get capped, just not across a reload for this visitor.
    return `d_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  }
}
const GUEST_DEMO_TOKEN = getDemoToken();

// Once a public-demo visitor has used their free turns, every further chat
// attempt can only end at the wall - there is no point letting them open the
// chat, type a message and wait for the server to say so. The flag is set when
// the server first answers demo_turn_limit (see rfetch), and read wherever the
// chat opens or sends. sessionStorage, not localStorage, on purpose: a stale
// flag must never lock a visitor out for good (an owner can raise the cap), so
// it lasts for the browser session only. A new session just goes to the server
// once, which either lets them through or re-sets the flag. Only ever set on a
// public-demo tenant, because only those return demo_turn_limit.
const DEMO_EXHAUSTED_KEY = 'ss_demo_turns_exhausted';
function markDemoTurnsExhausted() {
  try { window.sessionStorage.setItem(DEMO_EXHAUSTED_KEY, '1'); } catch (e) {}
}
function demoTurnsExhausted() {
  try { return window.sessionStorage.getItem(DEMO_EXHAUSTED_KEY) === '1'; } catch (e) { return false; }
}
function showDemoWall() {
  window.dispatchEvent(new CustomEvent('ss-demo-wall', { detail: { error: 'demo_turn_limit' } }));
}

// Staff testing aid. The cap is per visitor token, so a new token is a new
// visitor: the limit itself stays exactly as guests meet it, and can be tested
// again from zero. Reachable only from the Manager screen. A reload is needed
// because GUEST_DEMO_TOKEN is read once at load.
function resetDemoVisitor() {
  try { window.localStorage.removeItem(DEMO_TOKEN_KEY); } catch (e) {}
  try { window.sessionStorage.removeItem(DEMO_EXHAUSTED_KEY); } catch (e) {}
  window.location.reload();
}

// ─── Public Demo Wall ─────────────────────────────────────────────────────────
// Rendered when `ss-demo-wall` fires (see rfetch above). Two cases:
//   - turn_limit: THIS visitor used their free turns. Offer three ways forward,
//     each its own process, in this order: sign up for the 3-month free trial,
//     book a sales call, or fill out the contact form (we reply within 24 hours).
//     None of them unlocks more turns - per plan this converts into a real
//     conversation or a real signup, not a re-up.
//   - daily_limit: the TENANT's shared daily ceiling tripped (a traffic spike).
//     Nothing to choose here - just say so and let them keep browsing the
//     read-only menu.
// Deliberately does not block browsing the menu itself - only pairing and chat
// call an AI endpoint, so this only ever appears after someone tries one of
// those, and dismissing it just means the guest goes back to looking at wines.
//
// History: the HubSpot scheduler replaced the old email form (Oct 2026), and is
// now one of three options rather than the whole wall. captureDemoLead was
// unreachable anyway (new function, no allUsers invoker; see the IAM section of
// CLAUDE.md) and is still left in index.js, unused - the form option below goes
// through HubSpot instead.
//
// ⚠️ The embedded options are plain iframes, NOT HubSpot's MeetingsEmbedCode.js.
// That script scans the page for .meetings-iframe-container once, when it
// loads - in a single-page app the container mounts later, so the scheduler
// would only appear on the first wall of a page load, or not at all. The iframe
// is what the script builds anyway; the only thing lost is its auto-height,
// which the fixed-height, scrollable frame below covers.
//
// The trial card links to the free-trial page on the marketing site
// (syncbev.com/trial, built in the SyncBev marketing repo). If DEMO_TRIAL_URL is
// ever emptied, the card still shows but tapping it does nothing - that was how
// it behaved before the page existed.
//
// ⚠️ The form is NOT an iframe and NOT a one-shot <script> tag in index.html.
// HubSpot's loader scans for .hs-form-frame elements once, when it executes, and
// this one mounts later (only when someone opens the wall and picks the form) -
// the same single-page-app trap as the scheduler above. So DemoWallForm injects
// the loader itself, AFTER its container exists, and removes its previous copy
// first so a second wall in the same page load gets a fresh scan too. If the
// form still hasn't appeared after a few seconds it says so instead of leaving a
// blank white box.
const DEMO_MEETINGS_URL = 'https://meetings-na3.hubspot.com/lindsay-billings?embed=true';
// The contact form, from HubSpot's embed snippet (Share -> Embed). These are
// public identifiers, not secrets. scriptSrc is HubSpot's own loader for this
// portal; it looks for .hs-form-frame elements and renders the form into them.
const DEMO_FORM = {
  scriptSrc: 'https://js-na3.hsforms.net/forms/embed/343514110.js',
  region: 'na3',
  portalId: '343514110',
  formId: 'cdd41d60-3f8e-4a07-a1f3-5fa6cec035dd',
};
const DEMO_TRIAL_URL = 'https://syncbev.com/trial/';  // marketing site's trial page - opens in a new tab so the demo stays open

function DemoWallForm({ cfg, fontFamily }) {
  const boxRef = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    // Fresh copy every time: drop any loader this component injected earlier so
    // the new one scans the container that exists now, not the one that is gone.
    document.querySelectorAll('script[data-demo-wall-form]').forEach(s => s.remove());
    const s = document.createElement('script');
    s.src = cfg.scriptSrc;
    s.async = true;
    s.setAttribute('data-demo-wall-form', '1');
    document.body.appendChild(s);
    // The loader renders asynchronously and gives no callback. An empty
    // container after this long means it never did.
    const timer = setTimeout(() => {
      if (boxRef.current && boxRef.current.childElementCount === 0) setFailed(true);
    }, 8000);
    return () => { clearTimeout(timer); s.remove(); };
  }, [cfg.scriptSrc]);
  return (
    <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0, maxHeight: '100%', overflowY: 'auto', borderRadius: 10, background: '#fff', padding: 16, textAlign: 'left' }}>
      <div ref={boxRef} className="hs-form-frame" data-region={cfg.region} data-form-id={cfg.formId} data-portal-id={cfg.portalId} />
      {failed && (
        <div style={{ color: '#666', fontSize: 13, lineHeight: 1.5, fontFamily, textAlign: 'center', padding: 20 }}>
          The form didn't load. Please go back and choose another option, or try again in a moment.
        </div>
      )}
    </div>
  );
}

function DemoWallCard({ wall, theme, onClose }) {
  const [view, setView] = useState('choose');
  const [loaded, setLoaded] = useState(false);
  const t = theme;
  // Every new wall starts at the three-way choice, and closing it (wall back to
  // null) clears whichever embed was open, so a second wall never reopens
  // mid-form.
  useEffect(() => { setView('choose'); setLoaded(false); }, [wall]);
  if (!wall) return null;
  const daily = wall.reason === 'daily_limit';

  // Order is deliberate: the free trial is the strongest commitment and goes
  // first, the sales call second, the plain contact form last. The trial card
  // is always listed; with no DEMO_TRIAL_URL yet it renders as an inert card.
  const options = [
    {
      id: 'trial', title: tr('demo.wall.trialTitle'),
      body: tr('demo.wall.trialBody'),
      href: DEMO_TRIAL_URL || undefined,
    },
    {
      id: 'call', title: tr('demo.wall.callTitle'),
      body: tr('demo.wall.callBody'),
      embed: DEMO_MEETINGS_URL, frameTitle: tr('demo.wall.callFrame'),
    },
    {
      id: 'form', title: tr('demo.wall.formTitle'),
      body: tr('demo.wall.formBody'),
      hsForm: DEMO_FORM,
    },
  ].filter(Boolean);
  // With only one option configured there is nothing to choose between, so go
  // straight to it (this is exactly how the wall behaved before the chooser).
  const hasView = (o) => !!(o.embed || o.hsForm);
  const active = options.length === 1 && hasView(options[0])
    ? options[0]
    : options.find(o => o.id === view && hasView(o));

  const cardStyle = {
    display: 'block', width: '100%', textAlign: 'left', boxSizing: 'border-box',
    background: t.bgCard, border: `1px solid ${t.accentBorder}`, borderRadius: 10,
    padding: '14px 16px', marginBottom: 10, cursor: 'pointer', textDecoration: 'none',
    fontFamily: t.fontSerif,
  };
  const cardInner = (o) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ color: t.textPrimary, fontSize: 15, marginBottom: 3 }}>{o.title}</div>
        <div style={{ color: t.textSecondary, fontSize: 12.5, lineHeight: 1.45 }}>{o.body}</div>
      </div>
      <div style={{ color: t.accent, fontSize: 20, flexShrink: 0 }}>›</div>
    </div>
  );
  const dismissLink = (
    <button onClick={onClose} style={{ background: 'none', border: 'none', color: t.textDim, fontSize: 12, fontFamily: t.fontSerif, cursor: 'pointer', textDecoration: 'underline', marginTop: 10 }}>
      {tr('demo.wall.noThanks')}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 20000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: t.bgSurface, borderRadius: 14, padding: daily ? 28 : (active ? '22px 18px 14px' : '28px 22px 18px'), maxWidth: daily ? 380 : (active ? 760 : 480), width: '100%', maxHeight: 'calc(100vh - 32px)', overflowY: active ? 'visible' : 'auto', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', border: `1px solid ${t.accentBorder}`, textAlign: 'center' }}>
        <div style={{ color: t.accent, fontSize: 13, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 10, fontFamily: t.fontSerif }}>
          {daily ? tr('demo.wall.backInMoment') : tr('demo.wall.limitReached')}
        </div>
        {daily ? (
          <>
            <div style={{ color: t.textSecondary, fontSize: 14, lineHeight: 1.5, marginBottom: 20, fontFamily: t.fontSerif }}>
              {tr('demo.wall.busy')}
            </div>
            <button onClick={onClose} style={{ background: t.accent, color: t.accentText || '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontFamily: t.fontSerif, fontSize: 13, cursor: 'pointer', alignSelf: 'center' }}>
              {tr('demo.wall.keepBrowsing')}
            </button>
          </>
        ) : active ? (
          <>
            {options.length > 1 && (
              <button onClick={() => { setView('choose'); setLoaded(false); }} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: t.textSecondary, fontSize: 12, fontFamily: t.fontSerif, cursor: 'pointer', padding: 0, marginBottom: 10 }}>
                {tr('demo.wall.backToOptions')}
              </button>
            )}
            {active.id === 'form' && (
              <div style={{ color: t.textSecondary, fontSize: 14, lineHeight: 1.5, marginBottom: 14, fontFamily: t.fontSerif }}>
                {tr('demo.wall.formBody')}
              </div>
            )}
            {active.hsForm ? (
              <DemoWallForm key={active.id} cfg={active.hsForm} fontFamily={t.fontSerif} />
            ) : (
            /* White behind the frame: HubSpot's page is always light, and on a dark
                theme the gap before it paints reads as a broken box. */
            <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0, height: 640, maxHeight: '100%', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
              {!loaded && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: 13, fontFamily: t.fontSerif }}>
                  {tr('demo.wall.loading')}
                </div>
              )}
              <iframe
                key={active.id}
                title={active.frameTitle}
                src={active.embed}
                onLoad={() => setLoaded(true)}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            </div>
            )}
            {dismissLink}
          </>
        ) : (
          <>
            <div style={{ color: t.textPrimary, fontSize: 16, lineHeight: 1.45, marginBottom: 18, fontFamily: t.fontSerif }}>
              {tr('demo.wall.intro')}
            </div>
            {options.map(o => o.href ? (
              <a key={o.id} href={o.href} target="_blank" rel="noopener noreferrer" style={cardStyle}>{cardInner(o)}</a>
            ) : hasView(o) ? (
              <button key={o.id} onClick={() => { setLoaded(false); setView(o.id); }} style={{ ...cardStyle, border: `1px solid ${t.accentBorder}` }}>{cardInner(o)}</button>
            ) : (
              <div key={o.id} style={{ ...cardStyle, cursor: 'default' }}>{cardInner(o)}</div>
            ))}
            {dismissLink}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Connection watch ────────────────────────────────────────────────────────
// A tablet that drops off wifi keeps showing whatever was on screen, so it LOOKS
// fine - but every list, pairing and chat loads from the server, and Start Over
// waits on the network (Fully's waitInternetOnReload). Oct 2026: a demo tablet
// sat like that after an Android update, and the only symptom was "Start Over
// does nothing". Staff need to be told plainly, and the tablet should recover by
// itself when the connection returns.
//
// navigator.onLine alone is not trusted: an Android tablet still joined to wifi
// that has lost the internet behind it usually reports itself ONLINE. So a failed
// request (rfetch rejects only on a network failure, never on an HTTP error) and
// the browser's 'offline' event are both treated as SUSPICION, confirmed by
// fetching a small same-origin file. Only a failed probe shows the notice; probing
// then repeats until one succeeds.
const CONNECTION_PROBE_MS = 5000;
const CONNECTION_PROBE_TIMEOUT_MS = 4000;
// Recovery must hold before we trust it. Oct 2026 test: the notice cleared, Start
// Over was tapped, and the reload died on net::ERR_NAME_NOT_RESOLVED - wifi had
// rejoined, and one probe got through (an open connection or a cached address),
// but DNS wasn't answering yet, so the full page load failed and the guest got
// Android's "Webpage not available". A second probe a few seconds later rides
// out that reconnect window.
const CONNECTION_CONFIRM_MS = 4000;
let connectionLost = false;
let connectionProbing = false;
const connectionSubscribers = new Set();

function setConnectionLost(lost) {
  if (connectionLost === lost) return;
  connectionLost = lost;
  connectionSubscribers.forEach(fn => fn(lost));
}

function useConnectionLost() {
  const [lost, setLost] = useState(connectionLost);
  useEffect(() => {
    setLost(connectionLost);
    connectionSubscribers.add(setLost);
    return () => { connectionSubscribers.delete(setLost); };
  }, []);
  return lost;
}

// A kiosk tablet in Fully (or the demo's own kiosk) - where an automatic reload
// on recovery is wanted. NOT a guest's phone: a blip on a train must not reload
// their page and throw away what they had starred.
function isKioskRuntime() {
  try {
    if (typeof window !== 'undefined' && window.fully) return true;
  } catch (e) {}
  return isOwnKioskDevice();
}

async function probeConnection() {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), CONNECTION_PROBE_TIMEOUT_MS) : null;
  try {
    // index.html is static and tiny; no-store plus a unique query so neither
    // the webview's cache nor a CDN edge can answer for the server.
    const res = await fetch(`${window.location.origin}/index.html?_ping=${Date.now()}`, {
      cache: 'no-store', ...(ctrl ? { signal: ctrl.signal } : {}),
    });
    return res.ok;
  } catch (e) {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function connectionRecovered() {
  const wasLost = connectionLost;
  setConnectionLost(false);
  // Screens that failed to load while offline are still showing their failure.
  // On a kiosk, start clean; on a phone, the guest just tries again.
  if (wasLost && isKioskRuntime()) hardReload();
}

function checkConnection() {
  if (connectionProbing) return;
  connectionProbing = true;
  const tick = async () => {
    let ok = await probeConnection();
    if (ok && connectionLost) {
      // Coming back from lost: confirm before clearing the notice or reloading.
      await new Promise(r => setTimeout(r, CONNECTION_CONFIRM_MS));
      ok = await probeConnection();
    }
    if (ok) {
      connectionProbing = false;
      connectionRecovered();
    } else {
      setConnectionLost(true);
      setTimeout(tick, CONNECTION_PROBE_MS);
    }
  };
  tick();
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', checkConnection);
  window.addEventListener('online', checkConnection);
}

function rfetch(url, options = {}) {
  const sep = url.includes('?') ? '&' : '?';
  // `lang` on every request: the language the guest is reading. Guest list reads
  // and chat cards come back with each description in it (localizeDescriptions on
  // the server); pairing and chat write in it. Ignored by everything else.
  const lang = encodeURIComponent(getRequestedLanguage());
  const fullUrl = `${url}${sep}restaurantId=${RESTAURANT_ID}&lang=${lang}`;
  if (options.body && typeof options.body === 'string') {
    try {
      const body = JSON.parse(options.body);
      body.restaurantId = RESTAURANT_ID;
      if (!body.lang) body.lang = getRequestedLanguage();
      // Injected here rather than at each call site so every guest request
      // carries it, including ones added later.
      if (!body.sessionId) body.sessionId = GUEST_SESSION_ID;
      if (!body.demoToken) body.demoToken = GUEST_DEMO_TOKEN;
      options = { ...options, body: JSON.stringify(body) };
    } catch (e) {}
  }
  return authFetch(fullUrl, options).then(res => {
    // A public-demo turn/daily cap is a cross-cutting concern - pairing and
    // chat are two separate screens deep in the tree, and neither should have
    // to know about the other's wall. Catching it here means whichever screen
    // tripped it gets an ordinary-looking empty response, and a single
    // top-level listener renders the wall regardless of which one fired it.
    if (res.status === 429) {
      res.clone().json().then(data => {
        if (data && (data.error === 'demo_turn_limit' || data.error === 'demo_daily_limit')) {
          // Only the per-visitor limit is remembered. The tenant-wide daily
          // ceiling is a traffic spike that can clear, so it must not block
          // the chat from opening later.
          if (data.error === 'demo_turn_limit') markDemoTurnsExhausted();
          window.dispatchEvent(new CustomEvent('ss-demo-wall', { detail: data }));
        }
      }).catch(() => {});
    }
    // A response while marked lost means the network is probably back - let the
    // probe confirm it rather than reloading on a single answer.
    if (connectionLost) checkConnection();
    return res;
  }, err => {
    // fetch only rejects on a network failure (or an abort); an HTTP error
    // still resolves above. Check, then let the caller's own error path run.
    checkConnection();
    throw err;
  });
}

// Tiers and subgroups are derived dynamically from Toast data in arrival order.
// TIER_LABELS just controls the short display name in the filter buttons — add entries as needed.
// Tier display labels — short names shown in filter buttons.
// Add entries here for any custom tier names used in a restaurant's Toast menu.
const TIER_LABELS = { "House Wines": "House", "Cellar Wines": "Cellar" };

const VARIETAL_GROUPS = {
  "Sparkling": ["Prosecco", "Champagne", "Sparkling", "Cava"],
  "Rosé": ["Rosé"],
  "Port": ["Port"],
};

// Text for searching: lower-cased with accents removed, so "rose" finds "Rosé",
// "chateau" finds "Château", "cote" finds "Côte", "gruner" finds "Grüner" and
// "albarino" finds "Albariño". Both the typed query and the text being searched
// go through this, so it does not matter which side has the accent. A guest on a
// phone keyboard almost never types them, and a wine list is full of them.
// NFD splits "é" into "e" plus a combining mark, which the regex then drops; the
// handful of letters NFD cannot split (ß, æ, œ, ø, ł, đ) are mapped by hand.
function foldText(v) {
  return String(v == null ? "" : v)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss").replace(/æ/g, "ae").replace(/œ/g, "oe")
    .replace(/ø/g, "o").replace(/ł/g, "l").replace(/đ/g, "d")
    .toLowerCase();
}

// Does `text` contain the typed query, starting at the beginning of a word?
// `q` must already be run through foldText(...).trim() (every search box does
// that once, where it reads the input). "rose" matches "Rosé", "Roses" and
// "Whispering Angel Rose", but NOT "Prosecco" - the letters r-o-s-e are inside
// that word, not at its start, and a guest typing "rose" wants the rosés. A
// word starts after anything that is not a letter or digit, so a space, a
// hyphen ("Crozes-Hermitage" is found by "hermitage") and an apostrophe
// ("d'Esclans" is found by "esclans") all count. Several words must appear as
// that exact phrase, in order, which is how search has always behaved.
function searchHit(text, q) {
  if (!q) return true;
  const needle = q.replace(/\s+/g, " ");
  const hay = foldText(text).replace(/\s+/g, " ");
  let i = hay.indexOf(needle);
  while (i !== -1) {
    if (i === 0 || !/[a-z0-9]/.test(hay[i - 1])) return true;
    i = hay.indexOf(needle, i + 1);
  }
  return false;
}

function consolidateVarietal(v) {
  if (!v) return null;
  for (const [group, members] of Object.entries(VARIETAL_GROUPS)) {
    if (members.includes(v)) return group;
  }
  return v;
}

// ─── Prices in the restaurant's currency (Oct 29, 2026) ──────────────────────
// appSettings.currency is the RESTAURANT's (absent = USD, so nothing changed for
// existing restaurants); the guest's language only decides how it is written:
// "$8.50" (en), "8,50 $" (fr), "$8.50" (es), "8,50 €". Never rounded - a rounded
// price invites an argument at the table - and ".00" is dropped: $14, $8.50.
// appSettings.showCurrencyCode appends the code ("$350 MXN"), for tourist areas
// where "$" could be read as US dollars. Read into module variables by
// applySettings(), like the language. Same rules as formatMoney() in
// functions/index.js (menu email, printed list) - keep them in step.
const CURRENCIES = [
  { code: "USD", label: "US dollar" },
  { code: "CAD", label: "Canadian dollar" },
  { code: "MXN", label: "Mexican peso" },
  { code: "EUR", label: "Euro" },
  { code: "GBP", label: "British pound" },
];
const PRICE_LOCALE = { en: "en-US", fr: "fr-CA", es: "es-MX", de: "de-DE", it: "it-IT", pl: "pl-PL", cs: "cs-CZ", sk: "sk-SK", hu: "hu-HU", ro: "ro-RO", ru: "ru-RU", uk: "uk-UA", el: "el-GR", ja: "ja-JP", ko: "ko-KR", zh: "zh-CN" };
let restaurantCurrency = "USD";
let showCurrencyCode = false;
// appSettings.hideCurrencySymbol (Oct 30, 2026): prices as bare numbers ("14").
let hideCurrencySymbol = false;
function setCurrencySettings(settings) {
  const c = settings && settings.currency;
  restaurantCurrency = CURRENCIES.some(x => x.code === c) ? c : "USD";
  showCurrencyCode = !!(settings && settings.showCurrencyCode === true);
  hideCurrencySymbol = !!(settings && settings.hideCurrencySymbol === true);
}
function formatMoney(v, { currency, code, lang, hideSymbol } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const cur = currency || restaurantCurrency;
  const loc = PRICE_LOCALE[String(lang || getLanguage()).split("-")[0]] || "en-US";
  const whole = Math.abs(n - Math.round(n)) < 0.005;
  const bare = hideSymbol === undefined ? hideCurrencySymbol : hideSymbol;
  const digits = { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 };
  let out;
  try {
    out = new Intl.NumberFormat(loc, bare ? digits : { style: "currency", currency: cur, currencyDisplay: "narrowSymbol", ...digits })
      .format(whole ? Math.round(n) : n);
  } catch (e) {
    out = bare ? String(whole ? Math.round(n) : n.toFixed(2)) : `$${whole ? Math.round(n) : n.toFixed(2)}`;
  }
  const withCode = code === undefined ? showCurrencyCode : code;
  return withCode && !bare ? `${out} ${cur}` : out;
}
// The currency an address suggests, for a restaurant that has not chosen one.
const EURO_COUNTRIES = /\b(france|germany|deutschland|italy|italia|spain|españa|portugal|ireland|netherlands|belgium|belgique|austria|österreich|greece|finland|luxembourg|slovenia|slovakia|estonia|latvia|lithuania|malta|cyprus|croatia)\b/i;
function suggestedCurrency(country) {
  const c = String(country || "").trim();
  if (/^(ca|can|canada)$/i.test(c)) return "CAD";
  if (/^(mx|mex|m[eé]xico)$/i.test(c)) return "MXN";
  if (/^(gb|uk|united kingdom|great britain|england|scotland|wales)$/i.test(c)) return "GBP";
  if (EURO_COUNTRIES.test(c)) return "EUR";
  return "USD";
}

function formatPrice(p) { return p ? formatMoney(p) : null; }

// Which pairing course a food menu group locks to, from its guest-facing name:
// "dessert" (DES only), "entree" (MAIN only) or "open" (guest picks 1ST or MAIN).
// Group names come verbatim from Toast or a CSV ("Dessert", "Desserts", "Entrées",
// "Mains"), so the guess matches loosely — an exact-string check once silently gave
// "Desserts" the starter layout on the demo. Unrecognized names stay "open", the
// safe default for starters, soups and salads.
//
// An owner can override the guess per section (Edit Menu Items → Food, the
// "Pairs as" dropdown beside each heading). Overrides live server-side in
// restaurants/{id}/courseOverrides and arrive with getSettings and getFoodItems;
// both feed setCourseOverrides(). The override always wins over the guess.
//
// normCourseName() MUST match normalizeCourseName() in functions/index.js.
const COURSE_KIND_LABELS = { open: "First or Main", entree: "Main only", dessert: "Dessert" };
let COURSE_OVERRIDES = {};
function setCourseOverrides(map) { if (map && typeof map === "object") COURSE_OVERRIDES = map; }
function normCourseName(course) {
  return String(course || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
}
function guessCourseKind(course) {
  const c = normCourseName(course);
  if (/\bdesserts?\b|\bsweets?\b|\bpastry\b/.test(c)) return "dessert";
  if (/\bentrees?\b|^mains?$|\bmain courses?\b/.test(c)) return "entree";
  return "open";
}
function courseKind(course, overrides = COURSE_OVERRIDES) {
  const o = overrides && overrides[normCourseName(course)];
  return o === "open" || o === "entree" || o === "dessert" ? o : guessCourseKind(course);
}

// Size-priced items (Potato Leek Soup: Cup $10 / Bowl $12) must show BOTH, the
// same way wines show glass and bottle. Showing only the cheaper one invites the
// complaint this exists to avoid - a guest ordering a bowl and finding $12 on a
// bill after reading $10 on the tablet.
//
// Falls back to the plain price, so call sites use it unconditionally.
function formatItemPrice(item) {
  const sizes = Array.isArray(item?.sizePrices) ? item.sizePrices.filter(sp => sp && sp.price > 0) : [];
  if (sizes.length === 0) return formatPrice(item?.price);
  return sizes.map(sp => `${formatPrice(sp.price)} ${String(sp.size).toLowerCase()}`).join(' · ');
}

// The model writes wine names in **bold** because that is ordinary prose style.
// The chat bubble rendered the asterisks literally, so every recommendation came
// with visible markup. Deliberately handles bold only - not a markdown parser, and
// nothing here should be able to inject markup, since it renders React elements
// rather than HTML.
function renderChatText(text) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    /^\*\*[^*]+\*\*$/.test(part)
      ? <strong key={i} style={{ fontWeight: 600 }}>{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>
  );
}
function timeAgo(ts) {
  if (!ts) return "";
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  return `${Math.round(mins / 60)}hr ago`;
}

function FilterBtn({ label, active, onClick, small }) {
  const t = useTheme();
  // Held at arm's length on a mounted tablet these read fine; held in the hand
  // at a dim table they do not. Bumped only on the narrow band - deliberately
  // by one point, because these pills wrap and a larger jump costs a whole extra
  // row of sticky header on a phone, which is worse than small type.
  const narrow = useIsNarrow();
  const chipScale = useChipScale();
  const base = narrow ? (small ? 11 : 12) : (small ? 10 : 11);
  return (
    <button onClick={onClick} style={{
      background: active ? t.accentSecondary : t.white08,
      border: `0.5px solid ${active ? t.accentSecondary : t.accentSecondaryBorder}`,
      color: active ? t.accentText : t.accentSecondary,
      fontSize: Math.round(base * chipScale * 2) / 2,
      padding: small ? "4px 11px" : "6px 15px",
      borderRadius: 20, cursor: "pointer",
      letterSpacing: "0.5px", fontFamily: t.fontSerif,
      fontWeight: active ? 600 : 400, transition: "all 0.15s",
      whiteSpace: "nowrap"
    }}>{label}</button>
  );
}

// ─── Copy Button ─────────────────────────────────────────────────────────────

function CopyButton({ text }) {
  const t = useTheme();
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button onClick={handleCopy} style={{
      background: copied ? t.successDim : t.accentDim,
      border: `0.5px solid ${copied ? t.success : t.accent}`,
      color: copied ? t.success : t.accent,
      fontSize: 11, padding: "4px 12px", borderRadius: 5,
      cursor: "pointer", fontFamily: t.fontSerif,
      transition: "all 0.2s", whiteSpace: "nowrap"
    }}>
      {copied ? "✓ Copied" : "Copy Name"}
    </button>
  );
}

// ─── Manager Screen ───────────────────────────────────────────────────────────

// The "shown at two prices" list on Menus & hours, collapsed to one line until
// tapped (Oct 28, 2026; a 15-drink happy hour filled the screen with notices).
function MmOverlapPanel({ overlaps, s }) {
  const t = useTheme();
  const [open, setOpen] = useState(false);
  if (!overlaps.length) return null;
  const n = overlaps.length;
  return (
    <section style={{ marginBottom: 22 }}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", boxSizing: "border-box", textAlign: "left", cursor: "pointer", background: t.warningDim, border: `0.5px solid ${t.warningBorder}`, borderRadius: 8, padding: "10px 12px", color: t.textPrimary, fontSize: 13, fontFamily: "inherit" }}>
        <span>{n} {n === 1 ? "item shows" : "items show"} at two prices at the same time</span>
        <span style={{ color: t.textDim, fontSize: 12, whiteSpace: "nowrap" }}>{open ? "Hide ▴" : "Show ▾"}</span>
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          <div style={{ ...(s && s.sectionNote), marginBottom: 8 }}>Guests see both prices, each with its menu's name, like two printed menus side by side. Fine for a happy hour. If it isn't one, fix the price on one of the menus.</div>
          {overlaps.map((o, i) => (
            <div key={i} style={{ borderBottom: `0.5px solid ${t.borderSubtle}`, padding: "8px 2px", color: t.textPrimary, fontSize: 12.5, lineHeight: 1.5 }}>
              {o.message}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function normalizeName(name) {
  return (name || "")
    .toLowerCase()
    .replace(/\b(glass|bottle|btl|gls)\b/g, "")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function findDuplicates(wines) {
  const groups = {};
  wines.forEach(w => {
    const key = normalizeName(w.name);
    if (!groups[key]) groups[key] = [];
    groups[key].push(w);
  });
  // Return groups with more than one wine, excluding valid glass/bottle pairs
  // Same wine on DIFFERENT menus (the happy hour glass next to the Wine List
  // one) is two items on purpose, one per menu, each at its own price. Not a
  // duplicate: Menus & hours reports those as "shown at two prices" instead.
  // Oct 28, 2026. Only when every copy is on a menu and no two share one.
  const separateMenus = group => {
    const sets = group.map(w => (Array.isArray(w.menuGuids) ? w.menuGuids : []));
    if (sets.some(m => m.length === 0)) return false;
    const seen = new Set();
    for (const m of sets) { for (const g of m) { if (seen.has(g)) return false; } m.forEach(g => seen.add(g)); }
    return true;
  };
  const exact = Object.values(groups).filter(group => {
    if (group.length < 2) return false;
    if (separateMenus(group)) return false;
    // If exactly 2 wines and one has glassPrice and the other has bottlePrice — valid pair, not a duplicate
    if (group.length === 2) {
      const hasGlass = group.some(w => w.glassPrice && !w.bottlePrice);
      const hasBottle = group.some(w => w.bottlePrice && !w.glassPrice);
      if (hasGlass && hasBottle) return false;
    }
    return true;
  });

  // Near-miss glass/bottle pairs. The backend merges "X Glass" with "X Bottle" only
  // on an exact base-name match, so a pair named slightly differently in Toast —
  // "Stella Pinot Grigio Glass" against "Stella Pinot Grigio Delle Venezie Bottle" —
  // silently stays two menu entries and is invisible to the exact-match check above.
  // Flagged rather than auto-merged: guessing wrong puts the wrong glass price on a
  // menu, and the real fix is one rename in Toast.
  const glassOnly  = wines.filter(w => w.glassPrice && !w.bottlePrice);
  const bottleOnly = wines.filter(w => w.bottlePrice && !w.glassPrice);
  const alreadyGrouped = new Set(exact.flat().map(w => w.id));
  const nearMiss = [];
  for (const g of glassOnly) {
    if (alreadyGrouped.has(g.id)) continue;
    const gk = normalizeName(g.name);
    if (gk.length < 8) continue;   // too short to match confidently
    const candidates = bottleOnly.filter(b => {
      if (alreadyGrouped.has(b.id)) return false;
      const bk = normalizeName(b.name);
      return bk !== gk && (bk.startsWith(gk) || gk.startsWith(bk));
    });
    // Only when there is exactly one plausible partner — several means guessing.
    if (candidates.length === 1) {
      nearMiss.push([g, candidates[0]]);
      alreadyGrouped.add(g.id);
      alreadyGrouped.add(candidates[0].id);
    }
  }
  return [...exact, ...nearMiss];
}

// ─── Polling Schedule ─────────────────────────────────────────────────────────
// Simple 2-minute poll during service hours, 30 minutes overnight.
// The stock-only sync runs every 5 minutes on the backend anyway.
function getPollingInterval() {
  const hour = new Date().getHours();
  if (hour >= 10 || hour < 2) return 2 * 60 * 1000;   // 2 min — likely open
  return 30 * 60 * 1000;                                 // 30 min — overnight
}

// ─── Pairing Loading Messages ─────────────────────────────────────────────────

// Keys, not text: these are module-level, so a tr() here would run before the
// language is set. LoadingMessages translates each one as it is shown.
const SOMMELIER_MESSAGES = [
  "loading.sommelier.cellar",
  "loading.sommelier.swirling",
  "loading.sommelier.evaluating",
  "loading.sommelier.tannins",
  "loading.sommelier.cellarMaster",
  "loading.sommelier.nose",
  "loading.sommelier.decanting",
];

const KITCHEN_MESSAGES = [
  "loading.kitchen.stepping",
  "loading.kitchen.chef",
  "loading.kitchen.weighing",
  "loading.kitchen.plated",
  "loading.kitchen.terroir",
];

// These messages exist so a guest is not staring at a blank screen while the model
// works. They were never meant to set the pace.
//
// The original fired `onAllShown` only after EVERY phrase had been displayed, and
// the parent held the finished answer until then - a hard floor of one full cycle,
// roughly 15-20 seconds, and if the response landed just after a cycle boundary the
// guest waited through the entire list a second time. The app was slower than the
// model.
//
// Now the callback fires once, after MIN_LOADING_MS. The answer appears the moment
// it is ready, unless it is ready so fast the screen would flicker - which happens
// on a cached pairing that makes no model call at all. Messages keep cycling if
// the wait runs long; they simply no longer gate anything.
const MIN_LOADING_MS = 2000;

// This app was built for mounted tablets. QR guests arrive on a phone, where a
// bottom sheet capped at 70vh leaves a ~30% strip of the list above it - usually
// one and a half items, still scrolling behind the sheet. It reads as broken.
// Below this width the detail view takes the whole screen instead.
const NARROW_BREAKPOINT = 640;

// Fallback icons for a chat suggestion card with no label image. A fork-and-knife
// under a single malt reads as a missing asset rather than a deliberate choice.
// Keys are the `type` values sommelierChatLean returns.
const SUGGESTION_ICONS = {
  wine: "🍷", beer: "🍺", cocktail: "🍸", spirit: "🥃", nab: "🥤", food: "🍽",
};

// The wide band is what a mounted tablet gets. It is deliberately NOT keyed to
// deviceSetup.deploymentMode: layout should fill whatever space actually exists,
// and a QR guest who scanned on an iPad deserves the same treatment as a kiosk.
// Behaviour (the walk-away reset) is the case where guessing the device wrong
// has a real cost; filling the screen is not.
//
// HEIGHT is part of the test on purpose. A phone in landscape reports ~844px
// wide and would otherwise pass a width-only check - but it is only ~390px tall,
// where a 460px logo is absurd. Requiring both excludes phone-landscape and
// still admits tablet portrait (800x1280) and tablet landscape (1280x800).
const WIDE_MIN_WIDTH = 768;
const WIDE_MIN_HEIGHT = 600;

// The mid band is a small kiosk tablet in portrait. An 8.7" Samsung reports
// ~600x1000, which misses the wide band on width alone and used to fall all the
// way through to phone sizing: a 340px column of 15px buttons with a third of
// the screen as margin on each side, while the wine list beside it filled the
// width. Same reasoning as the wide band for the height test - phone landscape
// is under ~450px tall and must not qualify, and no phone is 560px wide in
// portrait. Only HomeScreen reads it; detail views still switch on
// NARROW_BREAKPOINT.
const MID_MIN_WIDTH = 560;
const MID_MIN_HEIGHT = 700;

function readViewport() {
  if (typeof window === 'undefined') {
    return { width: 1024, height: 768, narrow: false, wide: false, mid: false };
  }
  const width = window.innerWidth;
  const height = window.innerHeight;
  return {
    width,
    height,
    narrow: width < NARROW_BREAKPOINT,
    wide: width >= WIDE_MIN_WIDTH && height >= WIDE_MIN_HEIGHT,
    mid: !(width >= WIDE_MIN_WIDTH && height >= WIDE_MIN_HEIGHT)
      && width >= MID_MIN_WIDTH && height >= MID_MIN_HEIGHT,
  };
}

// One listener for the whole app, not one per component. FilterBtn and WineCard
// render dozens of instances each on a long list; a hook that attached its own
// resize handler would put ~250 listeners on the window for a 248-wine list.
// Components subscribe to a module-level store instead.
let viewportState = readViewport();
const viewportSubscribers = new Set();

if (typeof window !== 'undefined') {
  const publishViewport = () => {
    const next = readViewport();
    if (
      next.width === viewportState.width &&
      next.height === viewportState.height
    ) return;
    viewportState = next;
    viewportSubscribers.forEach(fn => fn(next));
  };
  window.addEventListener('resize', publishViewport);
  // Android fires orientationchange BEFORE innerWidth/innerHeight have settled,
  // so reading synchronously here returns the pre-rotation dimensions. resize
  // normally follows and covers it; the delayed re-read is the belt to that
  // braces, and matters on a tablet rotating in its mount.
  window.addEventListener('orientationchange', () => setTimeout(publishViewport, 150));
}

function useViewport() {
  const [vp, setVp] = useState(viewportState);
  useEffect(() => {
    // The store may have moved between module evaluation and this mount.
    setVp(viewportState);
    viewportSubscribers.add(setVp);
    return () => { viewportSubscribers.delete(setVp); };
  }, []);
  return vp;
}

// Kept as-is so every existing call site is untouched.
function useIsNarrow() {
  return useViewport().narrow;
}

// ─── Guest text size ─────────────────────────────────────────────────────────
// The "Aa" button in the list header steps guest-facing reading text through
// three sizes. Feedback from the floor: item descriptions are hard to read
// without glasses.
//
// Applied as a multiplier on the reading text only - item names, descriptions,
// prices, detail-panel notes and pairing reasons - not on headers, chips or
// controls, so layouts don't break at the largest step.
//
// Deliberately NOT persisted. It lives in a module-level variable, so the
// walk-away reload and Start Over both put it back to Normal and the next
// table never inherits the last guest's setting. Same store pattern as
// useViewport: one value, many subscribers (a WineCard per wine).
const TEXT_SCALES = [
  { scale: 1,    labelKey: "textSize.normal" },
  { scale: 1.15, labelKey: "textSize.large" },
  { scale: 1.3,  labelKey: "textSize.larger" },
];
let textScaleIndex = 0;
const textScaleSubscribers = new Set();

function cycleTextScale() {
  textScaleIndex = (textScaleIndex + 1) % TEXT_SCALES.length;
  textScaleSubscribers.forEach(fn => fn(textScaleIndex));
}

function useTextScaleIndex() {
  const [i, setI] = useState(textScaleIndex);
  useEffect(() => {
    setI(textScaleIndex);
    textScaleSubscribers.add(setI);
    return () => { textScaleSubscribers.delete(setI); };
  }, []);
  return i;
}

function useTextScale() {
  return TEXT_SCALES[useTextScaleIndex()].scale;
}

// Scales every value in a size map, rounded to the half pixel.
function scaleFonts(map, scale) {
  if (scale === 1) return map;
  const out = {};
  for (const k in map) out[k] = Math.round(map[k] * scale * 2) / 2;
  return out;
}

// Filter and course chips grow one step at most. They wrap inside sticky
// headers, so every extra row of chips is a row of list the guest can't see;
// at the full 1.3x the Wine List filters alone could fill half a phone screen.
function useChipScale() {
  return Math.min(useTextScale(), 1.15);
}

function TextSizeButton() {
  const t = useTheme();
  const i = useTextScaleIndex();
  const active = i > 0;
  const size = tr(TEXT_SCALES[i].labelKey);
  const next = tr(TEXT_SCALES[(i + 1) % TEXT_SCALES.length].labelKey);
  return (
    <button
      onClick={cycleTextScale}
      aria-label={tr("textSize.aria", { size, next })}
      title={tr("textSize.title", { size })}
      style={{
        background: active ? t.accentDim : "none",
        border: `0.5px solid ${active ? t.accent : t.accentBorder}`,
        color: t.accent, borderRadius: 12, cursor: "pointer",
        fontFamily: t.fontSerif, padding: "2px 9px", lineHeight: 1,
        display: "inline-flex", alignItems: "baseline", gap: 1, flexShrink: 0,
        minHeight: 26,
      }}
    >
      <span style={{ fontSize: 11 }}>A</span>
      <span style={{ fontSize: 16 }}>a</span>
      {/* No step indicator. There were three dots here showing the current size;
          removed as clutter - a guest finds the steps by tapping, and the text
          itself changing is the feedback. The filled background (active) still
          marks that a larger size is on. */}
    </button>
  );
}

// ─── Guest overlay stacking ──────────────────────────────────────────────────
// The full-screen detail view on a phone is a SCREEN, not an overlay. Anything
// a guest can open from inside it - the label zoom, the sommelier chat, the
// toast, My Menu - has to sit above it.
//
// It was at 9000 while every one of those sat between 500 and 1100, so on a
// phone they all opened silently underneath an opaque panel. The label zoom was
// the visible symptom: tap the label and nothing happens, close the panel and
// the zoom is revealed, already open behind it. On a tablet the same detail view
// is `position: sticky` with no z-index at all, so the overlays landed above it
// and none of this showed.
//
// Ordered lowest to highest. Extend this ladder rather than picking a number:
// the reason this broke is that each overlay chose its own in isolation.
const Z = {
  listHeader: 100,
  detailPanel: 700,
  labelModal: 800,
  chat: 900,
  chatLabelZoom: 1000,
  toast: 1050,
  chatPrompt: 1100,
  shortlist: 1200,
  welcome: 1300,
  demoThemeSwitcher: 1400,
};

function LoadingMessages({ messages, onAllShown, minMs = MIN_LOADING_MS }) {
  const t = useTheme();
  const [displayIdx, setDisplayIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const idxRef = useRef(0);
  const callbackRef = useRef(onAllShown);
  useEffect(() => { callbackRef.current = onAllShown; }, [onAllShown]);

  // Fires once. Named onAllShown for compatibility with the three call sites; it
  // now means "the minimum dwell has passed".
  useEffect(() => {
    const t0 = setTimeout(() => callbackRef.current?.(), minMs);
    return () => clearTimeout(t0);
  }, [minMs]);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        idxRef.current = (idxRef.current + 1) % messages.length;
        setDisplayIdx(idxRef.current);
        setVisible(true);
      }, 450);
    }, 2500);
    return () => clearInterval(timer);
  }, [messages.length]);

  return (
    <div style={{ textAlign: "center", padding: "32px 16px" }}>
      <div style={{ fontSize: 28, marginBottom: 16 }}>✦</div>
      <div style={{ opacity: visible ? 1 : 0, transition: "opacity 0.45s ease", color: t.accent, fontSize: 15, fontStyle: "italic", fontFamily: t.fontSerif, letterSpacing: "0.5px", lineHeight: 1.6 }}>
        {tr(messages[displayIdx])}
      </div>
    </div>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────

const MENU_TYPES = [
  { value: "wine",         label: "Wine List",           description: "AI enrichment · glass/bottle merge · bidirectional pairing" },
  { value: "food",         label: "Food Menu",           description: "Course-grouped · pairing source · no enrichment" },
  { value: "beer_pours",   label: "Beer & Premium Pours", description: "AI enrichment · labels · food→drink pairing" },
  { value: "cocktails_na", label: "Cocktails & NA Beverages", description: "No enrichment · labels · food→drink pairing" },
];

// What a POS (Toast) menu feeds, as the manager picks it in Settings. Six
// choices since Oct 28, 2026. Beer & Premium Pours and Cocktails & NA are still
// the menu's `menuType` underneath (everything that filters by type keeps
// working); `category` says which list it feeds. Before this the sync guessed
// from the menu's name, so "Happy Hour Drafts" was synced as nothing. Configs
// saved before then have no category and keep the name rules; they show as the
// old combined choice until someone picks. See posMenusForCategory in
// functions/index.js.
const MENU_CHOICES = [
  { value: "wine",      menuType: "wine",         category: null,        label: "Wine List",            description: "AI enrichment · glass/bottle merge · bidirectional pairing" },
  { value: "food",      menuType: "food",         category: null,        label: "Food Menu",            description: "Course-grouped · pairing source · no enrichment" },
  { value: "beers",     menuType: "beer_pours",   category: "beers",     label: "Beer",                 description: "AI enrichment as beer · labels · food→drink pairing" },
  { value: "pours",     menuType: "beer_pours",   category: "pours",     label: "Premium Pours",        description: "AI enrichment as spirits · labels · food→drink pairing" },
  { value: "cocktails", menuType: "cocktails_na", category: "cocktails", label: "Cocktails",            description: "No enrichment · food→drink pairing" },
  { value: "nab",       menuType: "cocktails_na", category: "nab",       label: "NA Beverages",         description: "No enrichment · food→drink pairing" },
];
// The choice a saved menu config represents. Old combined configs map to a
// "legacy:" value so the dropdown can show them honestly.
function menuChoiceOf(menu) {
  if (!menu) return "wine";
  if (menu.category && MENU_CHOICES.some(c => c.value === menu.category)) return menu.category;
  if (menu.menuType === "wine" || menu.menuType === "food") return menu.menuType;
  return `legacy:${menu.menuType}`;
}
function menuChoiceLabel(menu) {
  const v = menuChoiceOf(menu);
  const c = MENU_CHOICES.find(x => x.value === v);
  if (c) return c.label;
  return MENU_TYPES.find(x => x.value === (menu && menu.menuType))?.label || "Menu";
}

const EMPTY_MENU = {
  id: null, label: "", guid: "", menuType: "wine", locations: [],
};

// ─── Logo upload: shrink in the browser before it ever leaves ────────────────
// A logo is shown at most ~460px wide (the tablet home screen), which a 2x
// tablet draws with ~920 real pixels. Anything bigger is downloaded by every
// guest device and thrown away. That is exactly what happened to the demo: a
// ChatGPT "high resolution" redo made the home screen load slowly.
//
// So every logo is redrawn here to at most LOGO_MAX_PX on its longest side and
// sent as PNG (keeps transparency) - or JPEG when the artwork has no
// transparent pixels and the PNG would still be heavy. Owners are never asked
// to resize anything; a file from their designer just works.
//
// Output is ONLY PNG or JPEG. uploadRestaurantLogo accepts nothing else,
// because the print-menu PDF and the guest email both need one of those two
// (Outlook shows no WebP). WebP, GIF and SVG from the picker are converted.
//
// A file that is already small enough (within LOGO_MAX_PX and LOGO_KEEP_BYTES,
// and already PNG/JPEG) is sent untouched, so a carefully exported logo is
// never re-encoded for nothing.
//
// LOGO_MAX_BYTES must match LOGO_MAX_BYTES in functions/index.js.
const LOGO_MAX_PX = 1200;
const LOGO_KEEP_BYTES = 400 * 1024;
const LOGO_TARGET_BYTES = 700 * 1024;
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

function loadImageFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("unreadable")); };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error("encode failed"))), type, quality));
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = () => reject(new Error("read failed"));
    r.readAsDataURL(blob);
  });
}

function hasTransparency(ctx, w, h) {
  const data = ctx.getImageData(0, 0, w, h).data;
  for (let i = 3; i < data.length; i += 4) if (data[i] < 255) return true;
  return false;
}

// Returns { base64, contentType } ready for uploadRestaurantLogo, or throws an
// Error whose message is safe to show the owner.
async function prepareLogoForUpload(file) {
  let img;
  try { img = await loadImageFile(file); }
  catch (e) {
    throw new Error("That file couldn't be opened as an image. Use a PNG or JPG — an iPhone HEIC photo won't open here.");
  }
  // SVGs can report 0x0 when they carry no width/height; give them a size.
  let w = img.naturalWidth || 0, h = img.naturalHeight || 0;
  if (!w || !h) { w = LOGO_MAX_PX; h = LOGO_MAX_PX; }

  const isPngOrJpeg = file.type === "image/png" || file.type === "image/jpeg";
  if (isPngOrJpeg && Math.max(w, h) <= LOGO_MAX_PX && file.size <= LOGO_KEEP_BYTES) {
    return { base64: await blobToBase64(file), contentType: file.type };
  }

  // Try the full cap first, then step down only if the result is still heavy.
  // Some browsers refuse to read pixels back from a drawn SVG (a "tainted"
  // canvas); that surfaces here as a SecurityError, which no owner should see.
  try {
    for (const maxPx of [LOGO_MAX_PX, 900, 700]) {
      const scale = Math.min(1, maxPx / Math.max(w, h));
      const cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
      const canvas = document.createElement("canvas");
      canvas.width = cw; canvas.height = ch;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, cw, ch);

      const png = await canvasToBlob(canvas, "image/png");
      if (png.size <= LOGO_TARGET_BYTES) return { base64: await blobToBase64(png), contentType: "image/png" };

      // Heavy PNG with no transparent pixels (a photo-like logo on a solid
      // background): JPEG is far smaller and loses nothing that matters.
      if (!hasTransparency(ctx, cw, ch)) {
        const jpg = await canvasToBlob(canvas, "image/jpeg", 0.9);
        if (jpg.size <= LOGO_TARGET_BYTES) return { base64: await blobToBase64(jpg), contentType: "image/jpeg" };
      }
      if (maxPx === 700 && png.size <= LOGO_MAX_BYTES) {
        return { base64: await blobToBase64(png), contentType: "image/png" };
      }
    }
  } catch (e) {
    throw new Error("This browser couldn't convert that file. Save the logo as a PNG and upload that instead.");
  }
  throw new Error("This logo is too detailed to make small enough. Try a simpler version, or one with a plain background.");
}

// onDeviceSetupSaved lets the app above re-read theme and logos immediately.
// Without it those live in two separate states — this one and AppContent's —
// and a theme change only appeared after a page reload.
function SettingsTab({ onDeviceSetupSaved, onLocationNamesSaved, openLanguages = false }) {
  const t = useTheme();
  // openLanguages: arrived from Account & billing's "Choose languages" - open
  // Restaurant Settings with the Language card in edit mode and scrolled to.
  const [settingsView, setSettingsView] = useState(openLanguages ? "restaurant" : "home"); // 'home' | 'restaurant' | 'menus'
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [addingMenu, setAddingMenu] = useState(false);
  const [newMenu, setNewMenu] = useState({ ...EMPTY_MENU });
  const [syncingMenu, setSyncingMenu] = useState(null);
  const [toastAvailability, setToastAvailability] = useState({});

  // Floor manager PIN
  const [pinStatus, setPinStatus] = useState(null);
  const [editingPin, setEditingPin] = useState(false);
  const [pinDraft, setPinDraft] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [pinSaving, setPinSaving] = useState(false);
  const [pinError, setPinError] = useState("");
  const [pinSaved, setPinSaved] = useState(false);

  // Location names — editable
  const [locationNames, setLocationNames] = useState({ bar: "Bar", dining: "Dining Room" });
  const [editingLocations, setEditingLocations] = useState(false);
  // Language card and per-language room names (multi-language, Oct 29, 2026).
  // Saved through the same whole-settings write as everything here, starting
  // from the full loaded `settings`, so language / showLanguagePicker /
  // locationNamesI18n are carried by every other save on this screen.
  const [editingLanguage, setEditingLanguage] = useState(false);
  const languagesOpenedRef = useRef(false);
  const [languageDraft, setLanguageDraft] = useState({ language: "en", showLanguagePicker: true, languages: DEFAULT_LANGUAGE_CODES });
  const [roomI18nDraft, setRoomI18nDraft] = useState({});
  const [editingCurrency, setEditingCurrency] = useState(false);
  const [translationStatus, setTranslationStatus] = useState(null);
  // Menu item approval card (Nov 5, 2026). null until loaded; stays null for a
  // floor manager (the endpoint is owner-level), which hides the card.
  const [autoApprove, setAutoApprove] = useState(null);
  const [autoApproveBusy, setAutoApproveBusy] = useState(false);
  const [autoApproveMsg, setAutoApproveMsg] = useState(null);
  // Pairings card (automatic matrix upkeep, Oct 30, 2026): getMatrixStatus?auto=1.
  const [pairingAuto, setPairingAuto] = useState(null);
  const [pairingMsg, setPairingMsg] = useState(null);
  const [pairingBusy, setPairingBusy] = useState(false);
  // Menu section names (Oct 29, 2026): the restaurant's headings and their
  // translations, through managerUpdateEnrichment getSections / saveSections.
  const [sectionsData, setSectionsData] = useState(null);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [sectionsDraft, setSectionsDraft] = useState({});
  const [sectionsTouched, setSectionsTouched] = useState({});
  const [sectionsSaving, setSectionsSaving] = useState(false);
  const [currencyDraft, setCurrencyDraft] = useState({ currency: "USD", showCurrencyCode: false, hideCurrencySymbol: false });
  const [locationDraft, setLocationDraft] = useState({ bar: "Bar", dining: "Dining Room" });

  // About Us — for PDF filler feature
  const [websiteUrl, setWebsiteUrl]     = useState('');
  const [aboutUsDraft, setAboutUsDraft] = useState('');
  const [scraping, setScraping]         = useState(false);
  const [aboutUsSaving, setAboutUsSaving] = useState(false);
  const [aboutUsSaved, setAboutUsSaved]   = useState(false);
  const [scrapeError, setScrapeError]     = useState(null);
  const [awardsDraft, setAwardsDraft]     = useState('');
  const [extraNotesDraft, setExtraNotesDraft] = useState('');
  const [instagramDraft, setInstagramDraft]   = useState('');
  const [facebookDraft, setFacebookDraft]     = useState('');

  // Restaurant location + what "local wine" means here (Oct 2026). Lets the chat
  // answer "can you recommend a local wine?" from the house's own list instead of
  // guessing. Named addr* because locationDraft is already the Bar / Dining Room
  // names. localPlaces / localSummary come back from the server after a save -
  // the owner sees exactly which places the chat will call local.
  // localRemoved / localAdded: the owner's edits to the generated list - tap a
  // place to take it out, type one to put it in. Applied on top of the list, so a
  // rebuild for a new address or scope keeps them.
  const [addrDraft, setAddrDraft] = useState({ street: "", city: "", region: "", postalCode: "", country: "United States", localScope: "neighbors", localRemoved: [], localAdded: [] });
  const [addPlaceDraft, setAddPlaceDraft] = useState("");
  const [addrSaved, setAddrSaved] = useState(null);      // the stored location, with localPlaces
  const [addrSaving, setAddrSaving] = useState(false);
  const [addrMsg, setAddrMsg] = useState(null);          // { ok, text }
  const [addrShowAll, setAddrShowAll] = useState(false);

  // Device setup — default location and logos
  const [deviceSetup, setDeviceSetup] = useState({ defaultLocation: "bar", barLogo: "", diningLogo: "", barLogoUrl: "", diningLogoUrl: "", barAltLogoUrl: "", diningAltLogoUrl: "", printLogoUrl: "", barTheme: DEFAULT_THEME.name, diningTheme: DEFAULT_THEME.name });
  const [editingDeviceSetup, setEditingDeviceSetup] = useState(false);
  const [deviceSetupDraft, setDeviceSetupDraft] = useState({ defaultLocation: "bar", deploymentMode: "kiosk", barLogo: "", diningLogo: "", barLogoUrl: "", diningLogoUrl: "", barAltLogoUrl: "", diningAltLogoUrl: "", printLogoUrl: "", barTheme: DEFAULT_THEME.name, diningTheme: DEFAULT_THEME.name });
  const [logoUploading, setLogoUploading] = useState({ bar: false, dining: false, print: false, barAlt: false, diningAlt: false });
  // Per-slot message under the upload button. Uploads used to fail into the
  // console only, so an owner whose file was refused saw the spinner stop and
  // nothing change.
  const [logoError, setLogoError] = useState({});

  // The file is shrunk in the browser first - see prepareLogoForUpload().
  async function handleLogoUpload(logoType, file) {
    if (!file) return;
    setLogoUploading(prev => ({ ...prev, [logoType]: true }));
    setLogoError(prev => ({ ...prev, [logoType]: null }));
    try {
      const { base64, contentType } = await prepareLogoForUpload(file);
      const res = await rfetch(UPLOAD_RESTAURANT_LOGO_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logoType, imageBase64: base64, contentType }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        const urlKey = `${logoType}LogoUrl`;
        setDeviceSetupDraft(prev => ({ ...prev, [urlKey]: data.url }));
        setDeviceSetup(prev => ({ ...prev, [urlKey]: data.url }));
      } else {
        setLogoError(prev => ({ ...prev, [logoType]: data.error || "The logo didn't upload. Try again." }));
      }
    } catch (err) {
      console.error('Logo upload failed:', err);
      setLogoError(prev => ({ ...prev, [logoType]: err.message || "The logo didn't upload. Try again." }));
    }
    setLogoUploading(prev => ({ ...prev, [logoType]: false }));
  }
  const [availableLogos, setAvailableLogos] = useState([{ value: "", label: "— Default logo —" }]);

  useEffect(() => {
    fetch("/logos/index.json")
      .then(r => r.json())
      .then(list => {
        setAvailableLogos([
          { value: "", label: "— Default logo —" },
          ...list
        ]);
      })
      .catch(() => {}); // silently ignore if index.json not present yet
  }, []);

  useEffect(() => {
    rfetch(SETTINGS_URL)
      .then(r => r.json())
      .then(data => {
        setSettings(data.settings || { menus: [] });
        if (data.settings?.locationNames) {
          setLocationNames(data.settings.locationNames);
          setLocationDraft(data.settings.locationNames);
        }
        if (data.settings?.deviceSetup) {
          setDeviceSetup(data.settings.deviceSetup);
          setDeviceSetupDraft(data.settings.deviceSetup);
        }
        // Load any cached Toast availability data
        if (data.settings?.toastAvailability) {
          setToastAvailability(data.settings.toastAvailability);
        }
        if (data.websiteUrl) setWebsiteUrl(data.websiteUrl);
        setTranslationStatus(data.translationStatus || null);
        if (data.aboutUs)    setAboutUsDraft(data.aboutUs);
        else if (data.aboutUsRaw) setAboutUsDraft(data.aboutUsRaw);
        if (data.awards)     setAwardsDraft(data.awards);
        if (data.extraNotes) setExtraNotesDraft(data.extraNotes);
        if (data.instagram)  setInstagramDraft(data.instagram);
        if (data.facebook)   setFacebookDraft(data.facebook);
        if (data.location) {
          setAddrSaved(data.location);
          setAddrDraft(d => ({ ...d, ...Object.fromEntries(["street", "city", "region", "postalCode", "country", "localScope"].map(k => [k, data.location[k] || d[k]])),
            localRemoved: Array.isArray(data.location.localRemoved) ? data.location.localRemoved : [],
            localAdded: Array.isArray(data.location.localAdded) ? data.location.localAdded : [] }));
        }
        setLoading(false);
      })
      .catch(() => { setSettings({ menus: [] }); setLoading(false); });
  }, []);

  async function saveSettings(updated) {
    setSaving(true);
    setSaveResult(null);
    try {
      const res = await rfetch(SAVE_SETTINGS_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ settings: updated })
      });
      const data = await res.json();
      setSaveResult({ ok: data.ok, message: data.ok ? "Settings saved." : (data.error || "Save failed.") });
      if (data.ok) setSettings(updated);
    } catch (e) {
      setSaveResult({ ok: false, message: e.message });
    }
    setSaving(false);
    setTimeout(() => setSaveResult(null), 4000);
  }

  function updateMenu(idx, fields) {
    const updated = { ...settings, menus: settings.menus.map((m, i) => i === idx ? { ...m, ...fields } : m) };
    setSettings(updated);
  }

  function deleteMenu(idx) {
    if (!window.confirm("Remove this menu?")) return;
    const updated = { ...settings, menus: settings.menus.filter((_, i) => i !== idx) };
    saveSettings(updated);
  }

  function addMenu() {
    if (!newMenu.guid.trim()) return;
    const ta = toastAvailability[newMenu.guid.trim()];
    const menuTypeLabel = menuChoiceLabel(newMenu);
    const resolvedLabel = newMenu.label.trim() || ta?.name || menuTypeLabel;
    const entry = { ...newMenu, label: resolvedLabel, id: Date.now().toString(), guid: newMenu.guid.trim() };
    const updated = { ...settings, menus: [...(settings.menus || []), entry] };
    setAddingMenu(false);
    setNewMenu({ ...EMPTY_MENU });
    saveSettings(updated);
  }

  function saveEdited() {
    saveSettings(settings);
    setExpandedMenu(null);
  }

  async function handleScrapeAboutUs() {
    if (!websiteUrl.trim()) return;
    setScraping(true); setScrapeError(null);
    try {
      const res = await rfetch(SCRAPE_ABOUT_US_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ websiteUrl: websiteUrl.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not read that site (${res.status}). Check the URL, or enter your About Us text below manually.`);
      if (!data.aboutUsRaw) throw new Error("Nothing readable was found on that page. Many restaurant sites load their text with JavaScript, which we can't read — enter your About Us text below manually.");
      if (data.websiteUrl) setWebsiteUrl(data.websiteUrl);
      setAboutUsDraft(data.aboutUsRaw);
    } catch (e) { setScrapeError(e.message); }
    setScraping(false);
  }

  // ── Floor manager PIN ──────────────────────────────────────────────────────

  async function loadPinStatus() {
    try {
      const res = await rfetch(FLOOR_PIN_STATUS_URL);
      if (res.ok) setPinStatus(await res.json());
    } catch (e) { /* panel just shows unknown state */ }
  }

  useEffect(() => { loadPinStatus(); }, []);

  // Mirrors the server check so an owner is told before a round trip. The server
  // is still the authority — this is only to avoid a pointless failed save.
  function localPinProblem(pin) {
    const len = pinStatus?.pinLength || 6;
    if (!new RegExp(`^\\d{${len}}$`).test(pin)) return `PIN must be exactly ${len} digits`;
    if (/^(\d)\1+$/.test(pin)) return "Not all the same digit";
    const d = pin.split("").map(Number);
    const asc = d.every((x, i) => i === 0 || x === (d[i-1] + 1) % 10);
    const desc = d.every((x, i) => i === 0 || x === (d[i-1] + 9) % 10);
    if (asc || desc) return "Not a straight run of digits";
    if (/^(\d\d)\1+$/.test(pin) || /^(\d\d\d)\1+$/.test(pin)) return "Not a repeating pattern";
    return null;
  }

  async function handleSavePin() {
    const problem = localPinProblem(pinDraft);
    if (problem) { setPinError(problem); return; }
    if (pinDraft !== pinConfirm) { setPinError("The two PINs don't match"); return; }
    setPinSaving(true);
    setPinError("");
    try {
      const res = await rfetch(SET_FLOOR_PIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pinDraft }),
      });
      const data = await res.json();
      if (!res.ok) { setPinError(data.error || "Could not save PIN"); }
      else {
        setPinSaved(true);
        setEditingPin(false);
        setPinDraft(""); setPinConfirm("");
        await loadPinStatus();
        setTimeout(() => setPinSaved(false), 2500);
      }
    } catch (e) {
      setPinError("Could not save PIN");
    }
    setPinSaving(false);
  }

  async function handleClearLockout() {
    try {
      await rfetch(CLEAR_PIN_LOCKOUT_URL, { method: "POST" });
      await loadPinStatus();
    } catch (e) { /* status refresh will show if it failed */ }
  }

  async function handleSaveAboutUs() {
    setAboutUsSaving(true); setScrapeError(null);
    try {
      await rfetch(SAVE_ABOUT_US_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          aboutUs: aboutUsDraft, websiteUrl,
          awards: awardsDraft, extraNotes: extraNotesDraft,
          instagram: instagramDraft, facebook: facebookDraft,
        }),
      });
      setAboutUsSaved(true);
      setTimeout(() => setAboutUsSaved(false), 3000);
    } catch (e) { setScrapeError("Save failed: " + e.message); }
    setAboutUsSaving(false);
  }

  async function handleSaveAddress() {
    if (!addrDraft.city.trim() || !addrDraft.region.trim() || !addrDraft.country.trim()) {
      setAddrMsg({ ok: false, text: "Enter at least the city, state or province, and country." });
      return;
    }
    setAddrSaving(true); setAddrMsg(null);
    try {
      const res = await rfetch(SAVE_ABOUT_US_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ location: addrDraft }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Save failed (${res.status})`);
      if (data.location) setAddrSaved(data.location);
      setAddrShowAll(false);
      setAddrMsg(data.localError ? { ok: false, text: data.localError } : { ok: true, text: "Saved." });
      if (!data.localError) setTimeout(() => setAddrMsg(m => (m && m.ok ? null : m)), 3000);
    } catch (e) { setAddrMsg({ ok: false, text: e.message }); }
    setAddrSaving(false);
  }

  function saveLocationNames() {
    // Per-language room names: blanks dropped, so an empty field means "the name
    // as entered" (see roomName() for how guests see them).
    const i18n = {};
    Object.entries(roomI18nDraft || {}).forEach(([room, byLang]) => {
      Object.entries(byLang || {}).forEach(([lang, v]) => {
        const name = String(v || "").trim();
        if (name) { i18n[room] = i18n[room] || {}; i18n[room][lang] = name; }
      });
    });
    const updated = { ...settings, locationNames: locationDraft, locationNamesI18n: Object.keys(i18n).length ? i18n : null };
    setRoomNameOverrides(i18n);
    setLocationNames(locationDraft);
    setEditingLocations(false);
    saveSettings(updated);
    if (onLocationNamesSaved) onLocationNamesSaved(locationDraft);
  }

  async function loadSections(action = "getSections", sections = undefined) {
    const res = await rfetch(MANAGER_UPDATE_URL, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, ...(sections ? { sections } : {}) }),
    });
    const d = await res.json();
    if (!d || !d.ok) throw new Error((d && d.error) || "Could not load section names");
    setSectionsData(d.sections || []);
    setSectionsDraft(Object.fromEntries((d.sections || []).map(x => [x.key,
      Object.fromEntries(Object.entries(x.translations || {}).map(([l, v]) => [l, v ? v.text : ""]))])));
    setSectionsTouched({});
  }
  async function saveSections() {
    const payload = {};
    Object.keys(sectionsTouched).forEach(k => {
      const [key, lang] = k.split("|");
      const sec = (sectionsData || []).find(x => x.key === key);
      payload[key] = payload[key] || { name: sec ? sec.name : "" };
      payload[key][lang] = (sectionsDraft[key] || {})[lang] || "";
    });
    setSectionsSaving(true);
    try { await loadSections("saveSections", payload); setSaveResult({ ok: true, message: "Section names saved." }); }
    catch (e) { setSaveResult({ ok: false, message: e.message }); }
    setSectionsSaving(false);
    setTimeout(() => setSaveResult(null), 4000);
  }

  async function loadPairingAuto() {
    try {
      const r = await rfetch(`${MATRIX_STATUS_URL}?auto=1`);
      const d = await r.json();
      if (r.ok) setPairingAuto(d);
    } catch (e) { /* the card just stays empty */ }
  }
  async function requestPairingRebuild() {
    setPairingBusy(true); setPairingMsg(null);
    try {
      const r = await rfetch(MATRIX_STATUS_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "requestRebuild" }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) setPairingMsg(d.message || d.error || "Could not schedule the rebuild.");
    } catch (e) { setPairingMsg(e.message); }
    await loadPairingAuto();
    setPairingBusy(false);
  }
  useEffect(() => { loadPairingAuto(); }, []);

  // "Auto-Approve AI verified menu items". Lives outside appSettings on the
  // server (saveSettings replaces appSettings wholesale), so it has its own
  // read and write actions rather than riding along with the settings save.
  useEffect(() => {
    rfetch(SAVE_SETTINGS_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "getApprovalSettings" }) })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.ok) setAutoApprove(d.autoApproveAiVerified === true); })
      .catch(() => {});
  }, []);
  async function toggleAutoApprove(enabled) {
    setAutoApproveBusy(true); setAutoApproveMsg(null);
    try {
      const r = await rfetch(SAVE_SETTINGS_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "setAutoApproveAiVerified", enabled }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) setAutoApproveMsg(d.error || "Could not save. Please try again.");
      else setAutoApprove(d.autoApproveAiVerified === true);
    } catch (e) { setAutoApproveMsg(e.message); }
    setAutoApproveBusy(false);
  }
  // While a rebuild is scheduled or running, refresh once a minute.
  useEffect(() => {
    const st = pairingAuto && pairingAuto.rebuild && pairingAuto.rebuild.state;
    if (st !== "scheduled" && st !== "building" && !(pairingAuto && pairingAuto.dishesPending)) return;
    const id = setInterval(loadPairingAuto, 60000);
    return () => clearInterval(id);
  }, [pairingAuto]);

  function saveCurrency() {
    const updated = { ...settings, currency: currencyDraft.currency || "USD",
      hideCurrencySymbol: currencyDraft.hideCurrencySymbol === true,
      showCurrencyCode: currencyDraft.hideCurrencySymbol === true ? false : currencyDraft.showCurrencyCode === true };
    setCurrencySettings(updated);
    setEditingCurrency(false);
    saveSettings(updated);
  }

  function saveLanguage() {
    const language = languageDraft.language || "en";
    // The default is always offered; catalogue order.
    const languages = restaurantLanguageCodes({ language, languages: languageDraft.languages });
    // Adding a language starts its translation at once (translateOnLanguagesAdded);
    // the card says so for a few hours.
    const before = new Set(restaurantLanguageCodes(settings));
    const added = languages.some(c => !before.has(c));
    const updated = { ...settings, language, languages, showLanguagePicker: languageDraft.showLanguagePicker !== false,
      ...(added ? { languagesAddedAt: Date.now() } : {}) };
    setEnabledLanguages(updated);
    setEditingLanguage(false);
    saveSettings(updated);
  }

  function saveDeviceSetup() {
    const updated = { ...settings, deviceSetup: deviceSetupDraft };
    setDeviceSetup(deviceSetupDraft);
    setEditingDeviceSetup(false);
    saveSettings(updated);
    if (onDeviceSetupSaved) onDeviceSetupSaved(deviceSetupDraft);
  }

  async function syncMenuPreview(menu, idx) {
    setSyncingMenu(idx);
    try {
      const res = await rfetch(SAVE_SETTINGS_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "fetchAvailability", guid: menu.guid })
      });
      const data = await res.json();
      if (data.availability) {
        setToastAvailability(prev => ({ ...prev, [menu.guid]: data.availability }));
        // Persist it back into settings
        const updated = {
          ...settings,
          toastAvailability: { ...(settings.toastAvailability || {}), [menu.guid]: data.availability }
        };
        setSettings(updated);
        saveSettings(updated);
      }
    } catch (e) {}
    setSyncingMenu(null);
  }

  function toggleLocation(menuDraft, setDraft, loc) {
    const locs = menuDraft.locations || [];
    setDraft(prev => ({
      ...prev,
      locations: locs.includes(loc) ? locs.filter(l => l !== loc) : [...locs, loc]
    }));
  }

  // Shared shapes from styles.js, with this screen's heavier field treatment on top.
  const inputStyle = { ...input(t), background: t.white08, border: `0.5px solid ${t.borderStrong}`, padding: "8px 12px", marginBottom: 0 };
  const labelStyle = { ...labelStyleFor(t), color: t.textPrimary };
  const selectStyle = { ...inputStyle, cursor: "pointer" };
  const optionStyle = { background: t.bgBase, color: t.textPrimary };
  const bodyText = { color: t.accent, fontSize: 12, lineHeight: 1.5 };
  const hintText = { color: t.textMuted, fontSize: 10, marginTop: 4, fontStyle: "italic" };

  function MenuTypeTag({ menu }) {
    // Colour by family, label by the category it feeds (Beer, Premium Pours...).
    // A config saved before categories existed shows its old combined name.
    const colors = { wine: t.accent, food: t.success, beer_pours: t.accentSecondary, cocktails_na: t.info };
    const color = colors[menu.menuType] || t.textDim;
    return (
      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, border: `0.5px solid ${color}`, color, letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
        {menuChoiceLabel(menu)}
      </span>
    );
  }

  function AvailabilitySummary({ menu }) {
    const ta = toastAvailability[menu.guid];
    const locs = menu.locations || [];
    const locStr = locs.length === 0 ? "All locations" :
      locs.map(l => l === "bar" ? locationNames.bar : l === "dining" ? locationNames.dining : l).join(" & ");

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
        {ta ? (
          <>
            {ta.toastDays && ta.toastDays.length > 0 && (
              <span style={{ fontSize: 10, color: t.success, background: t.successDim, border: `0.5px solid ${t.successBorder}`, borderRadius: 10, padding: "2px 8px" }}>
                <LineIcon name="calendar" inline />{ta.toastDays.length === 7 ? "Every day" : ta.toastDays.join(", ")}
              </span>
            )}
            {ta.toastHours && (
              <span style={{ fontSize: 10, color: t.success, background: t.successDim, border: `0.5px solid ${t.successBorder}`, borderRadius: 10, padding: "2px 8px" }}>
                <LineIcon name="clock" inline />{ta.toastHours.open} – {ta.toastHours.close}
              </span>
            )}
            {!ta.toastDays && !ta.toastHours && (
              <span style={{ fontSize: 10, color: t.success, background: t.successDim, border: `0.5px solid ${t.successBorder}`, borderRadius: 10, padding: "2px 8px" }}>
                <LineIcon name="calendar" inline />Always available (no schedule in Toast)
              </span>
            )}
            <span style={{ fontSize: 10, color: t.success, background: t.successDim, border: `0.5px solid ${t.successBorder}`, borderRadius: 10, padding: "2px 8px" }}>
              {ta.itemCount || "?"} items in Toast · checked {ta.checkedAt ? timeAgo(ta.checkedAt) : ""}
            </span>
          </>
        ) : (
          <span style={{ fontSize: 10, color: t.textMuted, fontStyle: "italic" }}>
            Tap ⟳ Check to pull availability from Toast
          </span>
        )}
        <span style={{ fontSize: 10, color: t.textMuted, background: t.accentDimSm, border: `0.5px solid ${t.accentBorderSm}`, borderRadius: 10, padding: "2px 8px" }}>
          <LineIcon name="pin" inline />{locStr}
        </span>
      </div>
    );
  }

  function MenuForm({ draft, setDraft, onSave, onCancel, saveLabel = "Save Menu" }) {
  const t = useTheme();
    const ta = toastAvailability[draft.guid];
    const [guidLookingUp, setGuidLookingUp] = useState(false);
    const [guidError, setGuidError] = useState(null);
    const guidDebounceRef = useRef(null);

    async function lookupGuid(guid) {
      if (!guid || guid.length < 10) return;
      setGuidLookingUp(true);
      setGuidError(null);
      try {
        const res = await rfetch(SAVE_SETTINGS_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "fetchAvailability", guid })
        });
        const data = await res.json();
        if (data.availability) {
          setToastAvailability(prev => ({ ...prev, [guid]: data.availability }));
          // Auto-populate label from Toast if not already set
          if (data.availability.name && !draft.label.trim()) {
            setDraft(p => ({ ...p, label: data.availability.name }));
          }
        } else {
          setGuidError("GUID not found in Toast — double-check it");
        }
      } catch (e) {
        setGuidError("Could not reach Toast — check your connection");
      }
      setGuidLookingUp(false);
    }

    function handleGuidChange(val) {
      const guid = val.trim();
      setDraft(p => ({ ...p, guid }));
      setGuidError(null);
      if (guidDebounceRef.current) clearTimeout(guidDebounceRef.current);
      if (guid.length > 10) {
        guidDebounceRef.current = setTimeout(() => lookupGuid(guid), 800);
      }
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

        {/* GUID — first, since it drives the name */}
        <div>
          <label style={labelStyle}>Toast Menu / Group GUID</label>
          <div style={{ position: "relative" }}>
            <input style={inputStyle} placeholder="e.g. 2d490bef-759b-447f-9af4-5bf0971948ba"
              value={draft.guid} onChange={e => handleGuidChange(e.target.value)}
              onBlur={() => { if (draft.guid && !ta) lookupGuid(draft.guid); }} />
            {guidLookingUp && (
              <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: t.accent, fontSize: 11 }}>Looking up…</div>
            )}
          </div>
          <div style={{ ...hintText }}>
            Found in Toast → Menus → select menu → the GUID appears in the URL or menu details
          </div>
          {guidError && <div style={{ color: t.error, fontSize: 11, marginTop: 4 }}><LineIcon name="alert" inline />{guidError}</div>}
        </div>

        {/* Label — auto-populated from Toast if available, otherwise from menu type */}
        {(() => {
          const menuTypeLabel = menuChoiceLabel(draft);
          const effectiveLabel = draft.label.trim() || ta?.name || menuTypeLabel;
          const isAutoLabel = !draft.label.trim() && ta;
          return (
            <div>
              <label style={labelStyle}>Display Name
                {isAutoLabel && <span style={{ color: t.success, fontStyle: "italic", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}> — using "{effectiveLabel}"</span>}
              </label>
              <input style={inputStyle}
                placeholder={guidLookingUp ? "Looking up…" : ta?.name || menuTypeLabel}
                value={draft.label}
                onChange={e => setDraft(p => ({ ...p, label: e.target.value }))} />
              {isAutoLabel && (
                <div style={{ ...hintText }}>Leave blank to use the name above, or type to override.</div>
              )}
              {ta?.name && draft.label && draft.label !== ta.name && (
                <div style={{ ...hintText }}>Toast name: {ta.name} · <span style={{ color: t.accent, cursor: "pointer" }} onClick={() => setDraft(p => ({ ...p, label: ta.name }))}>Use Toast name</span></div>
              )}
            </div>
          );
        })()}

        {/* Menu Type */}
        <div>
          <label style={labelStyle}>Menu Type</label>
          {(() => {
            const choice = menuChoiceOf(draft);
            const legacy = choice.startsWith("legacy:");
            return (
              <>
                <select style={selectStyle} value={choice}
                  onChange={e => {
                    const c = MENU_CHOICES.find(x => x.value === e.target.value);
                    if (!c) return;
                    setDraft(p => ({ ...p, menuType: c.menuType, category: c.category }));
                  }}>
                  {legacy && (
                    <option value={choice} style={optionStyle}>
                      {MENU_TYPES.find(x => x.value === draft.menuType)?.label || draft.menuType} (old setting: pick one)
                    </option>
                  )}
                  {MENU_CHOICES.map(c => (
                    <option key={c.value} value={c.value} style={optionStyle}>{c.label}</option>
                  ))}
                </select>
                <div style={{ ...hintText }}>
                  {legacy
                    ? "Saved before each drink list had its own choice. It is matched by its name for now; pick what it actually holds."
                    : MENU_CHOICES.find(c => c.value === choice)?.description}
                </div>
              </>
            );
          })()}
        </div>

        {/* Sort Order */}
        <div>
          <label style={labelStyle}>Sort Order</label>
          <input type="number" style={{ ...inputStyle, marginBottom: 0 }} placeholder="0"
            value={draft.sortOrder ?? 0}
            onChange={e => setDraft(p => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))} />
          <div style={{ ...hintText }}>Lower numbers appear first in the pairing screen. Use 0, 10, 20... to leave room for reordering.</div>
        </div>

        {/* Toast Availability — read-only display */}
        <div>
          <label style={labelStyle}>Availability (from Toast)</label>
          {ta ? (
            <div style={{ background: t.successDim, border: `0.5px solid ${t.successBorder}`, borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                {ta.toastDays && ta.toastDays.length > 0 ? (
                  <span style={{ fontSize: 11, color: t.success }}><LineIcon name="calendar" inline />{ta.toastDays.length === 7 ? "Every day" : ta.toastDays.join(", ")}</span>
                ) : (
                  <span style={{ fontSize: 11, color: t.success }}><LineIcon name="calendar" inline />No day restriction in Toast</span>
                )}
              </div>
              {ta.toastHours ? (
                <div style={{ fontSize: 11, color: t.success, marginBottom: 4 }}><LineIcon name="clock" inline />{ta.toastHours.open} – {ta.toastHours.close}</div>
              ) : (
                <div style={{ fontSize: 11, color: t.success, marginBottom: 4 }}><LineIcon name="clock" inline />No time restriction in Toast</div>
              )}
              <div style={{ fontSize: 10, color: t.textMuted, marginTop: 4 }}>
                {ta.itemCount || "?"} items found in Toast · last checked {ta.checkedAt ? timeAgo(ta.checkedAt) : "—"}
              </div>
              <div style={{ ...hintText, marginTop: 6 }}>
                Availability is set in Toast → Menus → select menu → Availability. Tap ⟳ Check to refresh.
              </div>
            </div>
          ) : (
            <div style={{ background: t.white04, border: `0.5px dashed ${t.borderStrong}`, borderRadius: 8, padding: "10px 12px", color: t.textMuted, fontSize: 11, fontStyle: "italic" }}>
              {draft.guid ? "Tap ⟳ Check on the menu card to pull availability from Toast." : "Enter a GUID first, then use ⟳ Check to fetch availability."}
            </div>
          )}
        </div>

        {/* Locations */}
        <div>
          <label style={labelStyle}>Available Locations</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[{ id: "bar", name: locationNames.bar }, { id: "dining", name: locationNames.dining }].map(loc => {
              const active = (draft.locations || []).includes(loc.id);
              return (
                <button key={loc.id} onClick={() => toggleLocation(draft, setDraft, loc.id)} style={{
                  flex: 1, padding: "10px 8px", borderRadius: 8,
                  border: `0.5px solid ${active ? t.accent : t.borderStrong}`,
                  background: active ? t.accentDim : t.white04,
                  color: active ? t.textPrimary : t.textMuted, fontSize: 12, cursor: "pointer",
                  fontFamily: t.fontSerif, textAlign: "center", transition: "all 0.12s"
                }}>
                  <div style={{ fontSize: 16, marginBottom: 3 }}><LineIcon name={loc.id === "bar" ? "cocktail" : "utensils"} size={18} /></div>
                  <div style={{ fontSize: 11 }}>{loc.name}</div>
                  {active && <div style={{ fontSize: 9, color: t.accent, marginTop: 2 }}>✓ included</div>}
                </button>
              );
            })}
          </div>
          <div style={{ ...hintText }}>
            Leave both unchecked to show in all locations. Check one or both to restrict by location.
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, paddingTop: 4 }}>
          <button onClick={onSave}
            disabled={!draft.guid.trim() || (!draft.label.trim() && !toastAvailability[draft.guid]) || saving || guidLookingUp}
            style={{ flex: 1, background: (!draft.guid.trim() || (!draft.label.trim() && !toastAvailability[draft.guid]) || guidLookingUp) ? t.accentDimSm : t.accent, color: (!draft.guid.trim() || (!draft.label.trim() && !toastAvailability[draft.guid]) || guidLookingUp) ? t.textDim : t.accentText, border: "none", padding: "11px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 13, fontWeight: 600 }}>
            {saving ? "Saving…" : guidLookingUp ? "Looking up…" : saveLabel}
          </button>
          <button onClick={onCancel}
            style={{ background: "none", border: `0.5px solid ${t.borderMid}`, color: t.textDim, padding: "11px 16px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 13 }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Coming from Account & billing: once settings are loaded, open the language
  // editor (same draft as its Edit button) and bring the card into view. Once.
  useEffect(() => {
    if (!openLanguages || loading || !settings || languagesOpenedRef.current) return;
    languagesOpenedRef.current = true;
    const match = languageEntry(settings.language);
    setLanguageDraft({
      language: match ? match.code : "en",
      showLanguagePicker: settings.showLanguagePicker !== false,
      languages: restaurantLanguageCodes(settings),
    });
    setEditingLanguage(true);
    setTimeout(() => {
      const el = document.getElementById("settings-language-card");
      if (el && el.scrollIntoView) el.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 80);
  }, [openLanguages, loading, settings]);

  if (loading) return <div style={{ color: t.textDim, textAlign: "center", padding: 40 }}>Loading settings…</div>;

  const menus = settings?.menus || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Settings home — two entry points */}
      {settingsView === "home" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            { id: "restaurant", label: "Restaurant Settings", sub: "Location names, color themes, logos" },
            { id: "menus",      label: "Menu Management",     sub: `${menus.length} Toast menus configured` },
          ].map(btn => (
            <button key={btn.id} onClick={() => setSettingsView(btn.id)}
              style={{ display: "flex", alignItems: "center", gap: 16, background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 12, padding: "16px 18px", cursor: "pointer", textAlign: "left", width: "100%", boxSizing: "border-box" }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: t.textPrimary, fontSize: 15, fontFamily: t.fontSerif, marginBottom: 3 }}>{btn.label}</div>
                <div style={{ color: t.textSecondary, fontSize: 11 }}>{btn.sub}</div>
              </div>
              <div style={{ color: t.textDim, fontSize: 18 }}>›</div>
            </button>
          ))}
        </div>
      )}

      {/* Back button for Restaurant Settings and Menu Management sub-screens */}
      {settingsView !== "home" && (
        <button onClick={() => setSettingsView("home")}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: t.accent, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 13, padding: "0 0 4px 0", alignSelf: "flex-start" }}>
          ← Back to Settings
        </button>
      )}

      {/* ── Restaurant Settings: Location Names + Device Setup ── */}
      {settingsView === "restaurant" && (<>

      {/* ── Location Names ── */}
      <div style={{ background: t.white06, border: `0.5px solid ${t.borderStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editingLocations ? 14 : 0 }}>
          <div>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 3 }}>Location Names</div>
            {!editingLocations && (
              <div style={{ color: t.accent, fontSize: 12 }}>
                {locationNames.bar} &nbsp;·&nbsp; {locationNames.dining}
              </div>
            )}
          </div>
          {!editingLocations && (
            <button onClick={() => { setLocationDraft({ ...locationNames }); setRoomI18nDraft(JSON.parse(JSON.stringify((settings && settings.locationNamesI18n) || {}))); setEditingLocations(true); }}
              style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
              Edit
            </button>
          )}
        </div>
        {editingLocations && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}><LineIcon name="cocktail" inline />Location 1 Name</label>
                <input style={inputStyle} value={locationDraft.bar}
                  onChange={e => setLocationDraft(p => ({ ...p, bar: e.target.value }))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}><LineIcon name="utensils" inline />Location 2 Name</label>
                <input style={inputStyle} value={locationDraft.dining}
                  onChange={e => setLocationDraft(p => ({ ...p, dining: e.target.value }))} />
              </div>
            </div>
            <div style={{ ...hintText }}>
              These names appear on menu location checkboxes throughout settings.
            </div>
            {/* Optional per-language names. Blank = the name above, in every
                language - except our own defaults ("Bar", "Dining Room"), which
                guests always see translated. */}
            <div style={{ borderTop: `0.5px solid ${t.borderStrong}`, paddingTop: 10 }}>
              <div style={{ color: t.accent, fontSize: 10, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>Names in other languages (optional)</div>
              {[["bar", "cocktail"], ["dining", "utensils"]].map(([room, icon]) => (
                <div key={room} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-end" }}>
                  <div style={{ width: 22, paddingBottom: 9, flexShrink: 0 }}><LineIcon name={icon} size={18} /></div>
                  {enabledLanguages().map(l => (
                    <div key={l.code} style={{ flex: 1, minWidth: 0 }}>
                      <label style={labelStyle}>{l.label}</label>
                      <input style={inputStyle} lang={l.code} placeholder={locationDraft[room] || ""}
                        value={((roomI18nDraft[room] || {})[l.code]) || ""}
                        onChange={e => { const v = e.target.value; setRoomI18nDraft(p => ({ ...p, [room]: { ...(p[room] || {}), [l.code]: v } })); }} />
                    </div>
                  ))}
                </div>
              ))}
              <div style={{ ...hintText }}>
                Leave blank to show the name above in every language. Guests always see "Bar" and "Dining Room" translated.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveLocationNames}
                style={{ flex: 1, background: t.accent, color: t.accentText, border: "none", padding: "9px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600 }}>
                Save Names
              </button>
              <button onClick={() => setEditingLocations(false)}
                style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "9px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Language (multi-language, Oct 29, 2026) ── */}
      <div id="settings-language-card" style={{ background: t.white06, border: `0.5px solid ${t.borderStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editingLanguage ? 14 : 0 }}>
          <div>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 3 }}>Language</div>
            {!editingLanguage && (() => {
              const lang = languageEntry(settings && settings.language) || GUEST_LANGUAGES[0];
              const offered = restaurantLanguageCodes(settings).map(c => languageEntry(c).english);
              const shown = !settings || settings.showLanguagePicker !== false;
              const extra = Math.max(0, offered.length - INCLUDED_LANGUAGES);
              return (
                <div style={{ color: t.accent, fontSize: 12, lineHeight: 1.5 }}>
                  Guests start in <span style={{ color: t.textPrimary }}>{lang.english}</span>
                  <span style={{ color: t.textDim }}> · {shown ? "language choice shown" : "language choice hidden"}</span>
                  <div style={{ color: t.textDim }}>
                    Offered: {offered.join(", ")}{extra > 0 ? ` · ${extra} extra, $${extraLanguagePrice(extra)}/month` : ""}
                  </div>
                  {(() => {
                    const p = translationProgress(translationStatus);
                    if (!p) {
                      return settings && settings.languagesAddedAt && Date.now() - settings.languagesAddedAt < 3 * 3600 * 1000 ? (
                        <div style={{ color: t.textDim, fontStyle: "italic" }}>
                          Translating newly added languages — usually ready within a couple of hours.
                        </div>
                      ) : null;
                    }
                    return (
                      <div style={{ marginTop: 8, borderTop: `0.5px solid ${t.borderStrong}`, paddingTop: 8 }}>
                        <div style={{ color: t.textDim, fontSize: 11, marginBottom: 4 }}>
                          Translations {p.pending === 0 ? "complete" : `in progress (${p.pct}%)`} · updated {minutesAgoLabel(p.ageMin)}
                        </div>
                        {p.rows.map(r => (
                          <div key={r.code} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: t.textPrimary, lineHeight: 1.6 }}>
                            <span>{r.entry ? r.entry.english : r.code}</span>
                            <span style={{ color: r.complete ? t.textDim : t.accent }}>
                              {r.done} of {r.total} items{r.sections !== null ? ` · ${r.sections}/${p.sectionsTotal} headings` : ""} {r.complete ? "✓" : "· translating"}
                            </span>
                          </div>
                        ))}
                        {p.review > 0 && (
                          <div style={{ color: t.warning || t.accent, fontSize: 11, marginTop: 4 }}>
                            <LineIcon name="alert" inline />{p.review} of your own translation{p.review === 1 ? "" : "s"} may need review: the original changed since you wrote {p.review === 1 ? "it" : "them"}.
                          </div>
                        )}
                        {p.stuck > 0 && (
                          <div style={{ color: t.warning || t.accent, fontSize: 11, marginTop: 4 }}>
                            {p.stuck} item{p.stuck === 1 ? "" : "s"} could not be translated automatically. Add your own translation in the item editor, or edit the description to try again.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
          </div>
          {!editingLanguage && (
            <button onClick={() => {
                const match = languageEntry(settings && settings.language);
                setLanguageDraft({
                  language: match ? match.code : "en",
                  showLanguagePicker: !settings || settings.showLanguagePicker !== false,
                  languages: restaurantLanguageCodes(settings),
                });
                setEditingLanguage(true);
              }}
              style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
              Edit
            </button>
          )}
        </div>
        {editingLanguage && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={labelStyle}>Default language</label>
              <select style={inputStyle} value={languageDraft.language}
                onChange={e => { const v = e.target.value; setLanguageDraft(d => ({ ...d, language: v, languages: [...new Set([...(d.languages || []), v])] })); }}>
                {GUEST_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.english}{l.label !== l.english ? ` — ${l.label}` : ""}</option>)}
              </select>
              <div style={{ ...hintText, marginTop: 6 }}>
                What guests see when a tablet starts or resets. New tasting notes are written in this language, and every
                note is also translated into the other two automatically. Changing it later does not rewrite existing notes.
              </div>
            </div>
            {/* Languages offered (Oct 30, 2026): the default plus two are included;
                each further one is an add-on (billing not wired yet). */}
            <div>
              <label style={labelStyle}>Languages offered to guests</label>
              {(() => {
                const chosen = new Set(languageDraft.languages || []);
                chosen.add(languageDraft.language);
                const count = chosen.size;
                return (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 6, marginTop: 4 }}>
                      {GUEST_LANGUAGES.map(l => {
                        const isDefault = l.code === languageDraft.language;
                        const on = chosen.has(l.code);
                        const full = !on && count >= languageLimit;
                        return (
                          <label key={l.code} style={{ display: "flex", gap: 8, alignItems: "center", cursor: isDefault || full ? "default" : "pointer", color: full ? t.textDim : t.textPrimary, fontSize: 13 }}>
                            <input type="checkbox" checked={on} disabled={isDefault || full}
                              onChange={e => {
                                const v = e.target.checked;
                                setLanguageDraft(d => {
                                  const set = new Set(d.languages || []);
                                  if (v) set.add(l.code); else set.delete(l.code);
                                  return { ...d, languages: [...set] };
                                });
                              }} />
                            <span>{l.english}{isDefault ? <span style={{ color: t.textDim, fontSize: 11 }}> (default)</span> : null}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ ...hintText, marginTop: 6 }}>
                      {count <= INCLUDED_LANGUAGES
                        ? `Your plan includes ${INCLUDED_LANGUAGES} languages: your default and two more. Extra languages are $3/month each, never more than $10/month.`
                        : `${INCLUDED_LANGUAGES} included + ${count - INCLUDED_LANGUAGES} extra = $${extraLanguagePrice(count - INCLUDED_LANGUAGES)}/month.`}
                      {count >= languageLimit ? ` That is the maximum of ${languageLimit} languages.` : ` Up to ${languageLimit} languages in all.`}
                      {" "}New languages start translating as soon as you save, and are usually ready within a couple of hours.
                    </div>
                  </>
                );
              })()}
            </div>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
              <input type="checkbox" checked={languageDraft.showLanguagePicker}
                onChange={e => setLanguageDraft(d => ({ ...d, showLanguagePicker: e.target.checked }))}
                style={{ marginTop: 3 }} />
              <span style={{ color: t.textPrimary, fontSize: 13, lineHeight: 1.4 }}>
                Let guests choose their language
                <span style={{ display: "block", color: t.textDim, fontSize: 11, marginTop: 2 }}>
                  A language choice on the home screen. Their choice lasts until Start Over.
                </span>
              </span>
            </label>
            <div style={{ ...hintText }}>
              Tablets pick up a change the next time they reset (Start Over, or after 10 idle minutes).
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveLanguage}
                style={{ flex: 1, background: t.accent, color: t.accentText, border: "none", padding: "9px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600 }}>
                Save Language
              </button>
              <button onClick={() => setEditingLanguage(false)}
                style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "9px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Currency (Oct 29, 2026) ── */}
      <div style={{ background: t.white06, border: `0.5px solid ${t.borderStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editingCurrency ? 14 : 0 }}>
          <div>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 3 }}>Currency</div>
            {!editingCurrency && (() => {
              const set = settings && settings.currency;
              const cur = CURRENCIES.find(c => c.code === set);
              const suggestion = suggestedCurrency(addrSaved && addrSaved.country);
              return (
                <div style={{ color: t.accent, fontSize: 12 }}>
                  {cur
                    ? <>Prices in <span style={{ color: t.textPrimary }}>{cur.label}</span> · {formatMoney(14, { currency: cur.code, lang: "en" })} / {formatMoney(8.5, { currency: cur.code, lang: "en" })}{settings.hideCurrencySymbol ? " · no symbol" : settings.showCurrencyCode ? " · code shown" : ""}</>
                    : <>US dollar (not set{suggestion !== "USD" ? ` — your address suggests ${CURRENCIES.find(c => c.code === suggestion).label}` : ""})</>}
                </div>
              );
            })()}
          </div>
          {!editingCurrency && (
            <button onClick={() => {
                const set = settings && settings.currency;
                setCurrencyDraft({
                  currency: CURRENCIES.some(c => c.code === set) ? set : suggestedCurrency(addrSaved && addrSaved.country),
                  showCurrencyCode: !!(settings && settings.showCurrencyCode === true),
                  hideCurrencySymbol: !!(settings && settings.hideCurrencySymbol === true),
                });
                setEditingCurrency(true);
              }}
              style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
              Edit
            </button>
          )}
        </div>
        {editingCurrency && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={labelStyle}>Currency your prices are in</label>
              <select style={inputStyle} value={currencyDraft.currency}
                onChange={e => setCurrencyDraft(d => ({ ...d, currency: e.target.value }))}>
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label} ({c.code})</option>)}
              </select>
            </div>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
              <input type="checkbox" checked={currencyDraft.hideCurrencySymbol}
                onChange={e => setCurrencyDraft(d => ({ ...d, hideCurrencySymbol: e.target.checked }))}
                style={{ marginTop: 3 }} />
              <span style={{ color: t.textPrimary, fontSize: 13, lineHeight: 1.4 }}>
                Show prices without a currency symbol
                <span style={{ display: "block", color: t.textDim, fontSize: 11, marginTop: 2 }}>
                  14 instead of $14, as many menus print them.
                </span>
              </span>
            </label>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: currencyDraft.hideCurrencySymbol ? "default" : "pointer", opacity: currencyDraft.hideCurrencySymbol ? 0.5 : 1 }}>
              <input type="checkbox" checked={currencyDraft.showCurrencyCode && !currencyDraft.hideCurrencySymbol} disabled={currencyDraft.hideCurrencySymbol}
                onChange={e => setCurrencyDraft(d => ({ ...d, showCurrencyCode: e.target.checked }))}
                style={{ marginTop: 3 }} />
              <span style={{ color: t.textPrimary, fontSize: 13, lineHeight: 1.4 }}>
                Show the currency code
                <span style={{ display: "block", color: t.textDim, fontSize: 11, marginTop: 2 }}>
                  For areas with many visitors, where "$" could be read as US dollars.
                </span>
              </span>
            </label>
            <div style={{ ...hintText }}>
              How guests will see it — English: {formatMoney(14, { currency: currencyDraft.currency, code: currencyDraft.showCurrencyCode, hideSymbol: currencyDraft.hideCurrencySymbol, lang: "en" })} · {formatMoney(8.5, { currency: currencyDraft.currency, code: currencyDraft.showCurrencyCode, hideSymbol: currencyDraft.hideCurrencySymbol, lang: "en" })}
              {"  "}· Français: {formatMoney(14, { currency: currencyDraft.currency, code: currencyDraft.showCurrencyCode, hideSymbol: currencyDraft.hideCurrencySymbol, lang: "fr" })} · {formatMoney(8.5, { currency: currencyDraft.currency, code: currencyDraft.showCurrencyCode, hideSymbol: currencyDraft.hideCurrencySymbol, lang: "fr" })}
              {"  "}· Español: {formatMoney(14, { currency: currencyDraft.currency, code: currencyDraft.showCurrencyCode, hideSymbol: currencyDraft.hideCurrencySymbol, lang: "es" })} · {formatMoney(8.5, { currency: currencyDraft.currency, code: currencyDraft.showCurrencyCode, hideSymbol: currencyDraft.hideCurrencySymbol, lang: "es" })}.
              {" "}This changes how prices are written, not the amounts.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveCurrency}
                style={{ flex: 1, background: t.accent, color: t.accentText, border: "none", padding: "9px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600 }}>
                Save Currency
              </button>
              <button onClick={() => setEditingCurrency(false)}
                style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "9px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Menu section names (Oct 29, 2026) ── */}
      <div style={{ background: t.white06, border: `0.5px solid ${t.borderStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: sectionsOpen ? 14 : 0 }}>
          <div>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 3 }}>Menu Section Names</div>
            {!sectionsOpen && <div style={{ color: t.accent, fontSize: 12 }}>Headings like "Starters" or "Signature", in every language</div>}
          </div>
          {!sectionsOpen && (
            <button onClick={() => { setSectionsOpen(true); setSectionsData(null); loadSections().catch(e => setSaveResult({ ok: false, message: e.message })); }}
              style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
              Edit
            </button>
          )}
        </div>
        {sectionsOpen && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ ...hintText, marginTop: 0 }}>
              Every heading on your menus is translated automatically for guests reading another language. Change any of
              them to use your own wording; clear one to go back to the automatic translation. New headings are translated within the hour.
            </div>
            {!sectionsData ? (
              <div style={{ color: t.textDim, fontSize: 12 }}>Loading…</div>
            ) : sectionsData.length === 0 ? (
              <div style={{ color: t.textDim, fontSize: 12 }}>No menu sections yet.</div>
            ) : sectionsData.map(sec => (
              <div key={sec.key} style={{ borderTop: `0.5px solid ${t.borderStrong}`, paddingTop: 8 }}>
                <div style={{ color: t.textPrimary, fontSize: 13, marginBottom: 6 }}>{sec.name}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {enabledLanguages().filter(l => l.code !== sec.srcLang).map(l => {
                    const cur = (sec.translations || {})[l.code];
                    const touched = !!sectionsTouched[`${sec.key}|${l.code}`];
                    return (
                      <div key={l.code} style={{ flex: "1 1 140px", minWidth: 0 }}>
                        <label style={labelStyle} lang={l.code}>
                          {l.label}
                          <span style={{ color: t.textDim, fontSize: 10, marginLeft: 6 }}>
                            {touched ? "edited" : !cur ? "on its way" : cur.by === "manager" ? "yours" : "automatic"}
                          </span>
                        </label>
                        <input style={inputStyle} lang={l.code} placeholder={sec.name}
                          value={(sectionsDraft[sec.key] || {})[l.code] || ""}
                          onChange={e => {
                            const v = e.target.value;
                            setSectionsDraft(d => ({ ...d, [sec.key]: { ...(d[sec.key] || {}), [l.code]: v } }));
                            setSectionsTouched(x => ({ ...x, [`${sec.key}|${l.code}`]: true }));
                          }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveSections} disabled={sectionsSaving || Object.keys(sectionsTouched).length === 0}
                style={{ flex: 1, background: t.accent, color: t.accentText, border: "none", padding: "9px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600, opacity: Object.keys(sectionsTouched).length === 0 ? 0.5 : 1 }}>
                {sectionsSaving ? "Saving…" : "Save Section Names"}
              </button>
              <button onClick={() => setSectionsOpen(false)}
                style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "9px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Menu item approval: auto-approve AI verified items (Nov 5, 2026) ── */}
      {autoApprove !== null && (
        <div style={{ background: t.white06, border: `0.5px solid ${t.borderStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
          <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 6 }}>Menu item approval</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ color: t.textPrimary, fontSize: 13 }}>Auto-Approve AI verified menu items</div>
            <div role="group" aria-label="Auto-approve AI verified menu items" style={{ display: "flex", border: `0.5px solid ${t.borderStrong}`, borderRadius: 6, overflow: "hidden", flex: "none" }}>
              {[[true, "Yes"], [false, "No"]].map(([val, label]) => (
                <button key={label} onClick={() => autoApprove !== val && toggleAutoApprove(val)} disabled={autoApproveBusy}
                  aria-pressed={autoApprove === val}
                  style={{ background: autoApprove === val ? t.accent : "transparent", color: autoApprove === val ? t.accentText : t.textMuted,
                    border: "none", padding: "6px 16px", cursor: autoApproveBusy ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ color: t.textDim, fontSize: 12, lineHeight: 1.5, marginTop: 8 }}>
            {autoApprove
              ? "New items that SommSync identifies with confidence go live to guests as soon as they are researched. Anything it is not sure of still waits in Needs review for you."
              : "Every new item waits in Needs review until you approve it."}
          </div>
          <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginTop: 4, fontStyle: "italic" }}>
            Applies to items researched from now on. Items already waiting can be approved from Menu items › Needs review.
          </div>
          {autoApproveMsg && <div style={{ marginTop: 6, color: t.warning || t.accent, fontSize: 11 }}>{autoApproveMsg}</div>}
        </div>
      )}

      {/* ── Pairings: automatic matrix upkeep (Oct 30, 2026) ── */}
      {pairingAuto && pairingAuto.exists && (() => {
        const a = pairingAuto;
        const tz = a.timezone || "America/New_York";
        const fmtTime = ms => new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz }).format(new Date(ms));
        const nightly = `${a.nightlyHour || 3}:00 AM`;
        const rb = a.rebuild || {};
        const now = Date.now();
        const startAt = rb.state === "scheduled"
          ? Math.max(rb.dueAt || now, (a.wineEditedAt || 0) + (a.quietMinutes || 15) * 60000)
          : null;
        const buildMin = a.lastDurationMin || 10;
        const doneToday = rb.state === "done" && rb.finishedAt && now - rb.finishedAt < 12 * 3600 * 1000;
        const line = (text, opts = {}) => <div style={{ color: opts.dim ? t.textDim : t.textPrimary, fontSize: 12, lineHeight: 1.5, fontStyle: opts.italic ? "italic" : "normal" }}>{text}</div>;
        return (
          <div style={{ background: t.white06, border: `0.5px solid ${t.borderStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 6 }}>Pairings</div>
            {rb.state === "building" ? (
              line(`Rebuilding pairings now… usually done in about ${buildMin} minutes.`)
            ) : rb.state === "scheduled" ? (
              <>
                {line(`Rebuild scheduled. Pairings will update in about ${Math.max(1, Math.round((startAt - now) / 60000)) + buildMin} minutes.`)}
                {line("You can keep adding or editing wines; they will be included.", { dim: true })}
              </>
            ) : a.winesChanged > 0 ? (
              <>
                {line(`${a.winesChanged} wine${a.winesChanged === 1 ? "" : "s"} changed since the last update.`)}
                {line(`Pairings will update automatically tonight at ${nightly}. Nothing to do.`, { dim: true })}
                <div style={{ marginTop: 10 }}>
                  {a.manualLeft > 0 ? (
                    <button onClick={requestPairingRebuild} disabled={pairingBusy}
                      style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "6px 14px", borderRadius: 6, cursor: pairingBusy ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
                      {pairingBusy ? "Scheduling…" : "Rebuild now"}
                    </button>
                  ) : line(`Next update tonight at ${nightly}.`, { dim: true, italic: true })}
                </div>
              </>
            ) : (
              <>
                {line(doneToday ? `Pairings updated at ${fmtTime(rb.finishedAt)} ✓` : "Pairings are up to date ✓")}
                {line(`If wines change, pairings update automatically at ${nightly}.`, { dim: true })}
              </>
            )}
            {a.dishesPending > 0 && rb.state !== "building" && (
              <div style={{ marginTop: 6 }}>{line(`${a.dishesPending} dish change${a.dishesPending === 1 ? " is" : "s are"} being added to pairings — usually within a few minutes.`, { dim: true, italic: true })}</div>
            )}
            {rb.state === "error" && (
              <div style={{ marginTop: 6 }}>{line("The last automatic update did not finish. It will try again tonight.", { dim: true })}</div>
            )}
            {pairingMsg && <div style={{ marginTop: 6, color: t.warning || t.accent, fontSize: 11 }}>{pairingMsg}</div>}
          </div>
        );
      })()}

      {/* ── Device Setup ── */}
      <div style={{ background: t.white06, border: `0.5px solid ${t.borderStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editingDeviceSetup ? 14 : 0 }}>
          <div>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 3 }}>Device Setup</div>
            {!editingDeviceSetup && (
              <div style={{ color: t.accent, fontSize: 12 }}>
                Default: <span style={{ color: t.textPrimary }}>{deviceSetup.defaultLocation === "bar" ? locationNames.bar : locationNames.dining}</span>
                {(deviceSetup.barLogo || deviceSetup.diningLogo) && <span style={{ color: t.textDim }}> · logos configured</span>}
              </div>
            )}
          </div>
          {!editingDeviceSetup && (
            <button onClick={() => { setDeviceSetupDraft({ ...deviceSetup }); setEditingDeviceSetup(true); }}
              style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
              Edit
            </button>
          )}
        </div>
        {editingDeviceSetup && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* How guests reach the app. Not inferred from screen size: a phone in
                landscape and a small kiosk tablet look identical to the browser, and
                the difference matters for more than copy. A shared device will need
                to reset itself when a guest walks away; a guest's own phone must
                never do that. */}
            <div>
              <label style={labelStyle}>How Guests Use the App</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { id: "kiosk", icon: "tablet", label: "House tablets", hint: "Shared devices at the table or bar" },
                  { id: "qr", icon: "link", label: "QR on their phone", hint: "Guests scan and use their own device" },
                ].map(mode => {
                  const active = (deviceSetupDraft.deploymentMode || "kiosk") === mode.id;
                  return (
                    <button key={mode.id} onClick={() => setDeviceSetupDraft(p => ({ ...p, deploymentMode: mode.id }))} style={{
                      flex: 1, padding: "10px 8px", borderRadius: 8,
                      border: `0.5px solid ${active ? t.accent : t.borderStrong}`,
                      background: active ? t.accentDim : t.white04,
                      color: active ? t.textPrimary : t.textMuted,
                      fontFamily: t.fontSerif, fontSize: 12, cursor: "pointer", textAlign: "center"
                    }}>
                      <div style={{ marginBottom: 3, display: "flex", justifyContent: "center" }}><LineIcon name={mode.icon} size={20} /></div>
                      <div>{mode.label}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{ ...hintText }}>
                Changes the wording guests see on the welcome screen, and will control
                whether the app resets itself between guests once that is available.
                Restaurants doing both can leave this on house tablets.
              </div>
            </div>

            {/* Default Location */}
            <div>
              <label style={labelStyle}>Default Location for New Devices</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[{ id: "bar", icon: "cocktail" }, { id: "dining", icon: "utensils" }].map(loc => {
                  const active = deviceSetupDraft.defaultLocation === loc.id;
                  return (
                    <button key={loc.id} onClick={() => setDeviceSetupDraft(p => ({ ...p, defaultLocation: loc.id }))} style={{
                      flex: 1, padding: "10px 8px", borderRadius: 8,
                      border: `0.5px solid ${active ? t.accent : t.borderStrong}`,
                      background: active ? t.accentDim : t.white04,
                      color: active ? t.textPrimary : t.textMuted,
                      fontFamily: t.fontSerif, fontSize: 12, cursor: "pointer", textAlign: "center"
                    }}>
                      <div style={{ marginBottom: 3, display: "flex", justifyContent: "center" }}><LineIcon name={loc.icon} size={20} /></div>
                      <div>{loc.id === "bar" ? locationNames.bar : locationNames.dining}</div>
                      {active && <div style={{ fontSize: 9, color: t.accent, marginTop: 2, letterSpacing: "1px" }}>DEFAULT</div>}
                    </button>
                  );
                })}
              </div>
              <div style={{ ...hintText }}>
                Used on any device that hasn't been assigned a location yet. Set it to your most common room.
              </div>
            </div>
            {/* Logo uploads — two slots per guest-facing location so a
                restaurant on a light colour scheme isn't stuck with a
                white-on-transparent file made for a dark one, or vice versa.
                Bar/Dining pick between them automatically at render time based
                on which theme is active (see resolveLocationLogo below); the
                "dark version" is optional and simply falls back to the light
                one if never uploaded, so nothing breaks for a restaurant that
                only ever had one file. Print stays single — a printed page is
                always white paper, so there's only one background to design for. */}
            {[
              { type: 'bar',      label: `${locationNames.bar} Logo — light version`,     hint: `Used when ${locationNames.bar} is set to a dark-themed color scheme. White/light artwork on transparent or dark background.`,    darkBg: true  },
              { type: 'barAlt',   label: `${locationNames.bar} Logo — dark version`,      hint: `Used when ${locationNames.bar} is set to a light-themed color scheme. Dark artwork on transparent or light background. Optional — falls back to the light version above if not uploaded.`, darkBg: false },
              { type: 'dining',     label: `${locationNames.dining} Logo — light version`,  hint: `Used when ${locationNames.dining} is set to a dark-themed color scheme. White/light artwork on transparent or dark background.`, darkBg: true  },
              { type: 'diningAlt',  label: `${locationNames.dining} Logo — dark version`,   hint: `Used when ${locationNames.dining} is set to a light-themed color scheme. Dark artwork on transparent or light background. Optional — falls back to the light version above if not uploaded.`, darkBg: false },
              { type: 'print',  label: 'Print Menu Logo',              hint: 'Used on printed beverage menus. Must be dark artwork on white or transparent background.',  darkBg: false },
            ].map(({ type, label, hint, darkBg }) => {
              const urlKey = `${type}LogoUrl`;
              const currentUrl = deviceSetupDraft[urlKey];
              const uploading = logoUploading[type];
              const inputId = `logo-upload-${type}`;
              return (
                <div key={type}>
                  <label style={labelStyle}>{label}</label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{
                      width: 90, height: 56, borderRadius: 6, flexShrink: 0,
                      // Literal light/dark on purpose: this swatch exists to show the
                      // logo against a light and a dark field, so it must not follow
                      // the active scheme.
                      background: darkBg ? SEMANTIC.previewDark : SEMANTIC.previewLight,
                      border: `0.5px solid ${t.borderMid}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                    }}>
                      {currentUrl
                        ? <img src={currentUrl} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                        : <span style={{ color: darkBg ? SEMANTIC.previewDarkText : SEMANTIC.previewLightText, fontSize: 9 }}>No logo</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      {/* Anything the browser can draw is accepted; it is
                          converted to PNG/JPEG before upload. The value is
                          cleared so picking the same file again re-fires. */}
                      <input type="file" id={inputId} accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                        style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; handleLogoUpload(type, f); }} />
                      <button
                        onClick={() => document.getElementById(inputId).click()}
                        disabled={uploading}
                        style={{ ...selectStyle, cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1, marginBottom: 5 }}>
                        {uploading ? <><LineIcon name="hourglass" inline />Uploading...</> : currentUrl ? <><LineIcon name="upload" inline />Replace Logo</> : <><LineIcon name="upload" inline />Upload Logo</>}
                      </button>
                      {logoError[type] && <div role="alert" style={{ color: t.error, fontSize: 11, lineHeight: 1.4, marginBottom: 4 }}>{logoError[type]}</div>}
                      <div style={{ color: t.textSecondary, fontSize: 10, lineHeight: 1.4 }}>{hint}</div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Theme per location */}
            <div>
              <label style={labelStyle}>{locationNames.bar} Theme</label>
              <select style={selectStyle} value={deviceSetupDraft.barTheme || DEFAULT_THEME.name}
                onChange={e => setDeviceSetupDraft(p => ({ ...p, barTheme: e.target.value }))}>
                {THEME_CATALOGUE.map(th => (
                  <option key={th.value} value={th.value} style={optionStyle}>{th.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>{locationNames.dining} Theme</label>
              <select style={selectStyle} value={deviceSetupDraft.diningTheme || DEFAULT_THEME.name}
                onChange={e => setDeviceSetupDraft(p => ({ ...p, diningTheme: e.target.value }))}>
                {THEME_CATALOGUE.map(th => (
                  <option key={th.value} value={th.value} style={optionStyle}>{th.label}</option>
                ))}
              </select>
            </div>
            {/* Preview */}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveDeviceSetup}
                style={{ flex: 1, background: t.accent, color: t.accentText, border: "none", padding: "9px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600 }}>
                Save Device Setup
              </button>
              <button onClick={() => setEditingDeviceSetup(false)}
                style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "9px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Restaurant Location & Local Wines ── */}
      {/* Its own card, directly above About Us, rather than inside it: that card
          is the printed-menu filler and says so, and an address there would read
          as something that prints. Saved through the same endpoint. */}
      <div style={{ background: t.white06, border: `0.5px solid ${t.borderStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 6 }}>Location · Local Wines</div>
        <div style={{ color: t.textSecondary, fontSize: 10, lineHeight: 1.5, marginBottom: 12 }}>
          Guests ask the sommelier for "something local." Tell us where you are and what local means to you, and it will answer from your own list.
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Street Address</label>
          <input style={inputStyle} value={addrDraft.street} placeholder="123 Main Street"
            onChange={e => setAddrDraft(d => ({ ...d, street: e.target.value }))} />
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>City</label>
            <input style={inputStyle} value={addrDraft.city} placeholder="Phoenix"
              onChange={e => setAddrDraft(d => ({ ...d, city: e.target.value }))} />
          </div>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>State / Province</label>
            <input style={inputStyle} value={addrDraft.region} placeholder="Arizona"
              onChange={e => setAddrDraft(d => ({ ...d, region: e.target.value }))} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Postal Code</label>
            <input style={inputStyle} value={addrDraft.postalCode} placeholder="85004"
              onChange={e => setAddrDraft(d => ({ ...d, postalCode: e.target.value }))} />
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Country</label>
          <input style={inputStyle} value={addrDraft.country} placeholder="United States"
            onChange={e => setAddrDraft(d => ({ ...d, country: e.target.value }))} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Local wines in your area refer to</label>
          <select style={selectStyle} value={addrDraft.localScope}
            onChange={e => setAddrDraft(d => ({ ...d, localScope: e.target.value }))}>
            <option value="area" style={optionStyle}>My local area only</option>
            <option value="state" style={optionStyle}>My state / province only</option>
            <option value="neighbors" style={optionStyle}>My state / province and its neighbors</option>
            <option value="country" style={optionStyle}>My country</option>
          </select>
        </div>
        {/* What the chat will call local, from the last save - and editable. Tap
            a place to take it out (it stays visible, struck through, so it can be
            put back); type a place to add one the list missed. Edits are part of
            the draft and apply on Save Location. */}
        {addrSaved && ((Array.isArray(addrSaved.localPlaces) && addrSaved.localPlaces.length > 0) || addrDraft.localAdded.length > 0) && (() => {
          const generated = Array.isArray(addrSaved.localPlaces) ? addrSaved.localPlaces : [];
          const removed = new Set(addrDraft.localRemoved);
          const shown = addrShowAll ? generated : generated.slice(0, 30);
          // Title case for display only (places are stored lowercase), keeping the
          // small words of place names lowercase: "Oak Knoll District of Napa
          // Valley", "Côtes du Rhône", "Ribera del Duero". First word always capped.
          const SMALL = new Set(["of", "the", "and", "de", "du", "des", "la", "le", "les", "del", "della", "di", "da", "y", "sur", "en", "am", "an", "der"]);
          const titled = x => x.replace(/(^|[\s-])([a-z\u00e0-\u00ff][a-z\u00e0-\u00ff']*)/g,
            (m, a, w, i) => a + (i > 0 && a === " " && SMALL.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)));
          const sameList = (a, b) => [...(a || [])].sort().join("|") === [...(b || [])].sort().join("|");
          const addressStale = ["city", "region", "postalCode", "country", "localScope"].some(k => (addrDraft[k] || "").trim().toLowerCase() !== (addrSaved[k] || "").trim().toLowerCase());
          const editsStale = !sameList(addrDraft.localRemoved, addrSaved.localRemoved) || !sameList(addrDraft.localAdded, addrSaved.localAdded);
          const activeCount = generated.filter(x => !removed.has(x)).length + addrDraft.localAdded.filter(x => !generated.includes(x)).length;
          const toggle = x => setAddrDraft(d => ({ ...d, localRemoved: d.localRemoved.includes(x) ? d.localRemoved.filter(y => y !== x) : [...d.localRemoved, x] }));
          const addPlace = () => {
            const x = addPlaceDraft.replace(/\s+/g, " ").trim().toLowerCase();
            if (x.length < 3) return;
            setAddrDraft(d => generated.includes(x)
              ? { ...d, localRemoved: d.localRemoved.filter(y => y !== x) }          // already listed: just turn it back on
              : { ...d, localAdded: d.localAdded.includes(x) ? d.localAdded : [...d.localAdded, x] });
            setAddPlaceDraft("");
          };
          const chip = (on) => ({
            display: "inline-flex", alignItems: "center", gap: 4, margin: "0 6px 6px 0", padding: "4px 10px", borderRadius: 14, cursor: "pointer",
            fontFamily: t.fontSerif, fontSize: 11, lineHeight: 1.3,
            background: on ? t.bgSurface : "transparent",
            border: on ? `0.5px solid ${t.accentBorder}` : `0.5px dashed ${t.borderStrong}`,
            color: on ? t.textPrimary : t.textSecondary, textDecoration: on ? "none" : "line-through",
          });
          return (
            <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorder}`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
              <div style={{ color: t.textPrimary, fontSize: 11, marginBottom: 4 }}>
                The sommelier will treat wines from these places as local ({activeCount}):
              </div>
              {addrSaved.localSummary && <div style={{ color: t.accent, fontSize: 11, fontStyle: "italic", marginBottom: 6 }}>{addrSaved.localSummary}</div>}
              <div style={{ color: t.textSecondary, fontSize: 10, marginBottom: 8 }}>Tap a place to leave it out. Tap again to put it back.</div>
              <div>
                {shown.map(x => (
                  <button key={x} type="button" onClick={() => toggle(x)} style={chip(!removed.has(x))}
                    title={removed.has(x) ? "Left out. Tap to count it as local again" : "Tap to leave this out"}>
                    {titled(x)}
                  </button>
                ))}
                {generated.length > shown.length && (
                  <button type="button" onClick={() => setAddrShowAll(true)} style={{ background: "none", border: "none", color: t.accent, fontSize: 11, cursor: "pointer", padding: "4px 6px", textDecoration: "underline", fontFamily: t.fontSerif }}>
                    and {generated.length - shown.length} more
                  </button>
                )}
                {addrDraft.localAdded.filter(x => !generated.includes(x)).map(x => (
                  <span key={`added-${x}`} style={{ ...chip(true), cursor: "default", border: `1px solid ${t.accent}` }}>
                    {titled(x)}
                    <button type="button" onClick={() => setAddrDraft(d => ({ ...d, localAdded: d.localAdded.filter(y => y !== x) }))}
                      aria-label={`Remove ${titled(x)}`}
                      style={{ background: "none", border: "none", color: t.textSecondary, cursor: "pointer", fontSize: 13, lineHeight: 1, padding: 0 }}>{"\u00d7"}</button>
                  </span>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <input style={{ ...inputStyle, flex: 1 }} value={addPlaceDraft} placeholder="Add a place, e.g. Suisun Valley"
                  onChange={e => setAddPlaceDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addPlace(); } }} />
                <button type="button" onClick={addPlace} disabled={addPlaceDraft.trim().length < 3}
                  style={{ background: t.accentDim, border: `0.5px solid ${t.accent}`, color: t.accent, padding: "8px 12px", borderRadius: 6, fontSize: 11, cursor: addPlaceDraft.trim().length < 3 ? "default" : "pointer", opacity: addPlaceDraft.trim().length < 3 ? 0.5 : 1, fontFamily: t.fontSerif }}>
                  Add
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 8 }}>
                <div style={{ color: (addressStale || editsStale) ? t.warning : t.textSecondary, fontSize: 10, lineHeight: 1.4 }}>
                  {addressStale ? "Save Location to rebuild this list for your new address or choice. Your edits will carry over."
                    : editsStale ? "Save Location to apply your changes." : ""}
                </div>
                {(addrDraft.localRemoved.length > 0 || addrDraft.localAdded.length > 0) && (
                  <button type="button" onClick={() => setAddrDraft(d => ({ ...d, localRemoved: [], localAdded: [] }))}
                    style={{ background: "none", border: "none", color: t.accent, fontSize: 10, cursor: "pointer", padding: 0, textDecoration: "underline", fontFamily: t.fontSerif, whiteSpace: "nowrap" }}>
                    Reset to suggested
                  </button>
                )}
              </div>
            </div>
          );
        })()}
        <button onClick={handleSaveAddress} disabled={addrSaving} style={{
          width: "100%", background: addrMsg && addrMsg.ok ? t.successDim : t.accent,
          color: addrMsg && addrMsg.ok ? t.success : t.accentText, border: addrMsg && addrMsg.ok ? `0.5px solid ${t.success}` : "none",
          padding: "9px", borderRadius: 6, cursor: addrSaving ? "default" : "pointer",
          fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600,
        }}>
          {addrSaving ? "Saving\u2026 working out your local wine regions" : addrMsg && addrMsg.ok ? "\u2713 Saved" : "Save Location"}
        </button>
        {addrMsg && !addrMsg.ok && (
          <div style={{ color: t.warning, fontSize: 10, marginTop: 8, lineHeight: 1.5 }}>{addrMsg.text}</div>
        )}
      </div>

      {/* ── About Us · PDF Filler ── */}
      <div style={{ background: t.white06, border: `0.5px solid ${t.borderStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 6 }}>About Us · PDF Filler</div>
        <div style={{ color: t.textSecondary, fontSize: 10, lineHeight: 1.5, marginBottom: 12 }}>
          When a printed beverage menu has blank space on the last page, SommSync can fill it with an About Us section. Enter your website URL to generate a draft, then edit as needed.
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Website URL</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input style={{ ...inputStyle, flex: 1 }} value={websiteUrl} placeholder="https://yourrestaurant.com"
              onChange={e => setWebsiteUrl(e.target.value)} />
            <button onClick={handleScrapeAboutUs} disabled={scraping || !websiteUrl.trim()} style={{
              background: t.accentDim, border: `0.5px solid ${t.accent}`, color: t.accent,
              padding: "8px 12px", borderRadius: 6, fontSize: 11, whiteSpace: "nowrap",
              cursor: scraping || !websiteUrl.trim() ? "default" : "pointer",
              opacity: scraping || !websiteUrl.trim() ? 0.5 : 1,
            }}>
              {scraping ? "Fetching…" : "Fetch from site"}
            </button>
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>About Us Text</label>
          <textarea style={{ ...inputStyle, minHeight: 90, resize: "vertical", lineHeight: 1.5 }}
            value={aboutUsDraft}
            placeholder="Write 2-4 sentences about your restaurant's story, atmosphere, and what makes it special…"
            onChange={e => setAboutUsDraft(e.target.value)} />
          <div style={{ color: t.textSecondary, fontSize: 10, marginTop: 4, lineHeight: 1.4 }}>
            Claude condenses this to fit the available blank space on the last page of your printed menu.
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Awards & Recognition</label>
          <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical", lineHeight: 1.5 }}
            value={awardsDraft}
            placeholder="James Beard semifinalist 2023 · DIRONA award · Wine Spectator Award of Excellence · best-of lists, press mentions…"
            onChange={e => setAwardsDraft(e.target.value)} />
          <div style={{ color: t.textSecondary, fontSize: 10, marginTop: 4, lineHeight: 1.4 }}>
            Anything you'd be happy to see printed on a menu. Include the year where it matters.
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Anything Else</label>
          <textarea style={{ ...inputStyle, minHeight: 70, resize: "vertical", lineHeight: 1.5 }}
            value={extraNotesDraft}
            placeholder="Paste anything here — a press clipping, a social post, notes about your chef, sourcing, history, the room…"
            onChange={e => setExtraNotesDraft(e.target.value)} />
          <div style={{ color: t.textSecondary, fontSize: 10, marginTop: 4, lineHeight: 1.4 }}>
            Rough notes are fine. This is source material, not finished copy — nothing here prints as written.
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Instagram</label>
            <input style={inputStyle} value={instagramDraft} placeholder="@yourrestaurant"
              onChange={e => setInstagramDraft(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Facebook</label>
            <input style={inputStyle} value={facebookDraft} placeholder="yourrestaurant"
              onChange={e => setFacebookDraft(e.target.value)} />
          </div>
        </div>

        <button onClick={handleSaveAboutUs} disabled={aboutUsSaving} style={{
          width: "100%", background: aboutUsSaved ? t.successDim : t.accent,
          color: aboutUsSaved ? t.success : t.accentText, border: aboutUsSaved ? `0.5px solid ${t.success}` : "none",
          padding: "9px", borderRadius: 6, cursor: aboutUsSaving ? "default" : "pointer",
          fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600,
        }}>
          {aboutUsSaved ? "✓ Saved" : aboutUsSaving ? "Saving…" : "Save About Us"}
        </button>
        {scrapeError && (
          <div style={{ color: t.warning, fontSize: 10, marginTop: 8, lineHeight: 1.5 }}>{scrapeError}</div>
        )}
      </div>

      {/* ── Floor Manager PIN ── */}
      <div style={{ background: t.white06, border: `0.5px solid ${t.borderStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editingPin ? 14 : 0 }}>
          <div>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 3 }}>Floor Manager PIN</div>
            {!editingPin && (
              <div style={{ color: pinStatus?.isSet ? t.accent : t.warning, fontSize: 12 }}>
                {pinStatus === null ? "Checking…"
                  : pinStatus.isSet
                    ? `${pinStatus.pinLength}-digit PIN set${pinStatus.setAt ? ` · ${new Date(pinStatus.setAt).toLocaleDateString()}` : ""}`
                    : "No PIN set — floor managers cannot sign in"}
              </div>
            )}
          </div>
          {!editingPin && (
            <button onClick={() => { setEditingPin(true); setPinDraft(""); setPinConfirm(""); setPinError(""); }}
              style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
              {pinStatus?.isSet ? "Change" : "Set PIN"}
            </button>
          )}
        </div>

        {!editingPin && (
          <div style={{ color: t.textDim, fontSize: 10, lineHeight: 1.6, marginTop: 8 }}>
            Lets floor managers review items, print menus and sync — without an email account.
            It does not reach settings or billing. Share it with staff who need it; change it when they leave.
          </div>
        )}

        {!editingPin && pinStatus?.lockedUntil && (
          <div style={{ marginTop: 10, padding: "8px 10px", background: t.warningDim || t.white06, border: `0.5px solid ${t.warning}`, borderRadius: 6 }}>
            <div style={{ color: t.warning, fontSize: 11, marginBottom: 6 }}>
              Sign-in is paused after {pinStatus.failedAttempts} incorrect attempts — until {new Date(pinStatus.lockedUntil).toLocaleTimeString()}.
            </div>
            <button onClick={handleClearLockout}
              style={{ background: "none", border: `0.5px solid ${t.warning}`, color: t.warning, padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
              Clear the wait
            </button>
          </div>
        )}

        {editingPin && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>New {pinStatus?.pinLength || 6}-digit PIN</label>
                <input style={inputStyle} type="password" inputMode="numeric" autoComplete="new-password"
                  maxLength={pinStatus?.pinLength || 6} value={pinDraft}
                  onChange={e => { setPinDraft(e.target.value.replace(/\D/g, "")); setPinError(""); }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Confirm</label>
                <input style={inputStyle} type="password" inputMode="numeric" autoComplete="new-password"
                  maxLength={pinStatus?.pinLength || 6} value={pinConfirm}
                  onChange={e => { setPinConfirm(e.target.value.replace(/\D/g, "")); setPinError(""); }} />
              </div>
            </div>

            <div style={{ color: t.textDim, fontSize: 10, lineHeight: 1.6 }}>
              Avoid anything a stranger would try first — repeated digits, or a straight run like 123456.
              A birthday is fine.
            </div>

            {pinError && <div style={{ color: t.warning, fontSize: 11 }}>{pinError}</div>}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleSavePin} disabled={pinSaving}
                style={{ flex: 1, background: t.accent, color: t.accentText, border: "none", padding: "9px", borderRadius: 6, cursor: pinSaving ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600, opacity: pinSaving ? 0.6 : 1 }}>
                {pinSaving ? "Saving…" : "Save PIN"}
              </button>
              <button onClick={() => { setEditingPin(false); setPinDraft(""); setPinConfirm(""); setPinError(""); }}
                style={{ background: "none", border: `0.5px solid ${t.borderMid}`, color: t.textDim, padding: "9px 16px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {pinSaved && <div style={{ color: t.success, fontSize: 11, marginTop: 10 }}>✓ PIN updated</div>}
      </div>

      </>)}

      {/* ── Menu Management: Toast Menu GUIDs ── */}
      {settingsView === "menus" && (<>

      {/* ── Menu List ── */}
      <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 10 }}>
        Toast Menus ({menus.length})
      </div>

      {menus.length === 0 && !addingMenu && (
        <div style={{ color: t.textMuted, fontSize: 12, fontStyle: "italic", textAlign: "center", padding: "24px 0", marginBottom: 16 }}>
          No menus configured yet. Add your first menu below.
        </div>
      )}

      {menus.map((menu, idx) => {
        const isExpanded = expandedMenu === idx;
        const ta = toastAvailability[menu.guid];
        return (
          <div key={menu.id || idx} style={{ background: t.white06, border: `0.5px solid ${isExpanded ? t.accent : t.borderStrong}`, borderRadius: 10, marginBottom: 10, overflow: "hidden" }}>
            {/* Collapsed row */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", cursor: "pointer" }}
              onClick={() => setExpandedMenu(isExpanded ? null : idx)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ color: t.textPrimary, fontSize: 13, fontWeight: 500 }}>{menu.label || "Unnamed Menu"}</span>
                  <MenuTypeTag menu={menu} />
                </div>
                <div style={{ color: t.textMuted, fontSize: 10, fontFamily: t.fontMono, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{menu.guid}</div>
                <AvailabilitySummary menu={menu} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                <button onClick={e => { e.stopPropagation(); syncMenuPreview(menu, idx); }}
                  disabled={syncingMenu === idx}
                  style={{ background: t.successDim, border: `0.5px solid ${t.successBorder}`, color: t.success, padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 10, whiteSpace: "nowrap" }}>
                  {syncingMenu === idx ? "…" : "⟳ Check"}
                </button>
                <button onClick={e => { e.stopPropagation(); deleteMenu(idx); }}
                  style={{ background: "none", border: "none", color: t.textDim, fontSize: 16, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
              </div>
            </div>

            {/* Expanded edit form */}
            {isExpanded && (
              <div style={{ borderTop: `0.5px solid ${t.borderStrong}`, padding: "16px 14px", background: t.white04 }}>
                <MenuForm
                  draft={menus[idx]}
                  setDraft={(updater) => {
                    const updated = typeof updater === "function" ? updater(menus[idx]) : updater;
                    updateMenu(idx, updated);
                  }}
                  onSave={saveEdited}
                  onCancel={() => setExpandedMenu(null)}
                  saveLabel="Save Changes"
                  onCheckAvailability={() => syncMenuPreview(menus[idx], idx)}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* ── Add New Menu ── */}
      {addingMenu ? (
        <div style={{ background: t.white06, border: `0.5px solid ${t.accent}`, borderRadius: 10, padding: "16px 14px", marginBottom: 16 }}>
          <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 14 }}>Add New Menu</div>
          <MenuForm
            draft={newMenu}
            setDraft={setNewMenu}
            onSave={addMenu}
            onCancel={() => { setAddingMenu(false); setNewMenu({ ...EMPTY_MENU }); }}
            saveLabel="Add Menu"
            onCheckAvailability={newMenu.guid ? () => syncMenuPreview(newMenu, -1) : null}
          />
        </div>
      ) : (
        <button onClick={() => setAddingMenu(true)}
          style={{ width: "100%", background: t.accentDimSm, border: `0.5px dashed ${t.accentBorder}`, color: t.accent, padding: "13px", borderRadius: 10, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 13, letterSpacing: "0.5px" }}>
          + Add Menu
        </button>
      )}

      {/* ── Save feedback ── */}
      {saveResult && (
        <div style={{ marginTop: 14, background: saveResult.ok ? t.successDim : t.errorDim, border: `0.5px solid ${saveResult.ok ? t.success : t.error}`, borderRadius: 8, padding: "10px 14px" }}>
          <div style={{ color: saveResult.ok ? t.success : t.error, fontSize: 12, fontFamily: t.fontSerif }}>{saveResult.message}</div>
        </div>
      )}

      {/* ── Legend ── */}
      <div style={{ marginTop: 24, borderTop: `0.5px solid ${t.borderStrong}`, paddingTop: 16 }}>
        <div style={{ color: t.textMuted, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 10 }}>Menu Type Reference</div>
        {MENU_CHOICES.map(mt => {
          const colors = { wine: t.accent, food: t.success, beer_pours: t.accentSecondary, cocktails_na: t.info };
          return (
            <div key={mt.value} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "flex-start" }}>
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, border: `0.5px solid ${colors[mt.menuType]}`, color: colors[mt.menuType], whiteSpace: "nowrap", marginTop: 1, flexShrink: 0 }}>{mt.label}</span>
              <span style={{ color: t.textSecondary, fontSize: 11 }}>{mt.description}</span>
            </div>
          );
        })}
      </div>
      </>)}
    </div>
  );
}

// csvOnly (Oct 27, 2026): opened from Your menus → "Import a CSV export". Shows
// only the CSV form; uploading a PDF or photos lives on Your menus.
function SyncTab({ locationNames, csvOnly = false }) {
  const t = useTheme();
  const categories = [
    { id: "wine",      label: "Wine List" },
    { id: "beer",      label: "Beer List" },
    { id: "pours",     label: "Premium Pours" },
    { id: "food",      label: "Food Menu" },
    { id: "cocktails", label: "Specialty Cocktails" },
    { id: "nab",       label: "Non-Alcoholic Beverages" },
  ];
  const [syncingId, setSyncingId] = useState(null);
  const [results, setResults] = useState({});
  const [pipelineStatus, setPipelineStatus] = useState({ phase: 'idle' });
  const pipelineIntervalRef = useRef(null);
  // Menu re-upload. A restaurant onboarded from a file has no POS to pull from,
  // so this is how its menu gets refreshed - the same job the sync buttons above
  // do for a connected restaurant.
  const [reimport, setReimport] = useState({ files: {}, removeMissing: false, running: false, msg: '', warnings: [] });
  const reimportInputsRef = useRef({});
  // Where this restaurant's menu actually comes from. A CSV upload into a
  // category a live POS owns is not merely redundant — the next sync does
  // .remove() then .set() on that node, so the upload is erased hours later
  // with nothing on screen to explain it. null while unknown: the panel stays
  // hidden until the answer arrives rather than flashing a control that may
  // not apply.
  const [menuSource, setMenuSource] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await rfetch(MENU_SOURCE_URL);
        const data = await res.json();
        if (!cancelled && !data.error) {
          console.log('[menuSource]', data.posType, data.liveSync ? 'live' : 'file', '-', data.reason);
          setMenuSource(data);
        }
      } catch (e) {
        // Fail OPEN. If this call fails we do not know the source, and hiding
        // the upload from a file-import restaurant would strip the only way it
        // has to update its menu. The server-side guard refuses a bad upload
        // anyway, with an error that says why.
        if (!cancelled) setMenuSource({ liveSync: false, categories: {}, unknown: true, reason: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Categories the POS does not own — a restaurant can be on Toast for wine and
  // on a file for cocktails, and the sync leaves an unconfigured category alone.
  const uploadableCats = IMPORT_CATEGORIES.filter(c => !(menuSource?.categories || {})[c.key]);
  const showUpload = !!menuSource && uploadableCats.length > 0;

  async function runReimport() {
    if (reimport.running) return;
    const attached = IMPORT_CATEGORIES.filter(c => reimport.files[c.key]);
    if (attached.length === 0) { setReimport(p => ({ ...p, msg: 'Attach a CSV first.' })); return; }
    setReimport(p => ({ ...p, running: true, msg: 'Reading files…', warnings: [] }));
    try {
      const body = { restaurantId: RESTAURANT_ID, replace: reimport.removeMissing };
      for (const cat of attached) {
        const rows = parseCsv(await readFileText(reimport.files[cat.key]));
        // An empty file is almost always a wrong-file or wrong-export mistake, and
        // sending it through would wipe the category on a replace upload.
        if (rows.length === 0) {
          setReimport(p => ({ ...p, running: false, msg: `${cat.label} CSV had no data rows.` }));
          return;
        }
        body[cat.key] = rows;
      }
      setReimport(p => ({ ...p, msg: 'Importing…' }));
      const res = await authFetch(IMPORT_MENU_DATA_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.ok) {
        const counts = data.counts || {};
        const summary = IMPORT_CATEGORIES
          .filter(c => counts[c.key] != null)
          .map(c => `${counts[c.key]} ${c.label.toLowerCase()}`)
          .join(', ');
        // The file inputs are uncontrolled, so clearing state is not enough -
        // without this the filename stays on screen and invites a double upload.
        Object.values(reimportInputsRef.current).forEach(el => { if (el) el.value = ''; });
        setReimport(p => ({ ...p, running: false, files: {},
          msg: `Imported ${summary}. Reload to see changes.`,
          warnings: data.warnings || [] }));
      } else {
        setReimport(p => ({ ...p, running: false, msg: `Failed: ${data.error || 'unknown error'}` }));
      }
    } catch (e) {
      setReimport(p => ({ ...p, running: false, msg: `Failed: ${e.message}` }));
    }
  }

  useEffect(() => {
    async function checkPipeline() {
      try {
        const res = await rfetch(PIPELINE_STATUS_URL);
        const data = await res.json();
        setPipelineStatus(data || { phase: 'idle' });
        if (['idle', 'complete', 'error', 'pendingLargeListApproval'].includes(data?.phase)) {
          clearInterval(pipelineIntervalRef.current);
        }
      } catch (e) {}
    }
    checkPipeline();
    pipelineIntervalRef.current = setInterval(checkPipeline, 8000);
    return () => clearInterval(pipelineIntervalRef.current);
  }, []);

  const pipelineRunning = pipelineStatus.phase && !['idle', 'complete', 'error', 'pendingLargeListApproval'].includes(pipelineStatus.phase);

  const phaseLabels = {
    starting: <><LineIcon name="hourglass" inline />Starting pipeline...</>,
    toast: <><LineIcon name="radio" inline />Phase 1: Syncing from Toast{pipelineStatus.currentCategory ? ` (${pipelineStatus.currentCategory})` : ''}...</>,
    enrichment: <><LineIcon name="chip" inline />Phase 2: AI Enrichment{pipelineStatus.currentCategory ? ` (${pipelineStatus.currentCategory})` : ''}...</>,
    images: <><LineIcon name="image" inline />Phase 3: Queuing label images...</>,
    complete: <><LineIcon name="checkCircle" inline />Onboarding complete</>,
    error: <><LineIcon name="xCircle" inline />Pipeline error: {pipelineStatus.error}</>,
    pendingLargeListApproval: <><LineIcon name="alert" inline />Large list detected ({pipelineStatus.largeListTotal || '?'} beverages) — awaiting SommSync approval</>,
  };

  async function handleSyncOne(id) {
    setSyncingId(id);
    setResults(prev => ({ ...prev, [id]: null }));
    try {
      const res = await rfetch(FORCE_SYNC_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categories: [id] })
      });
      const data = await res.json();
      setResults(prev => ({ ...prev, [id]: { ok: data.ok, message: data.ok ? "✓ Syncing…" : (data.error || "Sync failed") } }));
      // The sync itself runs in a background trigger, so poll for the outcome.
      // Worth doing only to report restored items — a manager who removed wines
      // from a Toast menu months ago has no reason to expect their descriptions,
      // images and edits to come back, and would otherwise assume it was redone.
      if (data.ok) pollSyncOutcome(id);
    } catch (e) {
      setResults(prev => ({ ...prev, [id]: { ok: false, message: e.message } }));
    }
    setSyncingId(null);
  }

  function pollSyncOutcome(id) {
    let tries = 0;
    const iv = setInterval(async () => {
      tries++;
      try {
        const r = await rfetch(SYNC_STATUS_URL);
        const st = await r.json();
        const done = st[id] === 'done' || st[id] === 'error';
        if (done || tries > 40) {
          clearInterval(iv);
          const n = st.restored && st.restored.count;
          setResults(prev => ({
            ...prev,
            [id]: st[id] === 'error'
              ? { ok: false, message: "Sync failed — check with SommSync" }
              : {
                  ok: true,
                  message: n
                    ? `✓ Synced · restored ${n} item${n === 1 ? '' : 's'} from your archive — descriptions, images and your edits came back automatically`
                    : "✓ Synced",
                },
          }));
        }
      } catch (e) { if (tries > 40) clearInterval(iv); }
    }, 3000);
  }

  return (
    <div>
      {/* Menu re-upload — the refresh path for categories with no live POS behind
          them. Hidden entirely for a restaurant whose menus all come from a POS:
          the upload would be replaced by the next sync, which is a worse outcome
          than not offering it. */}
      {/* PDF / photo / Word upload, read by Claude and reviewed before publishing. */}
      {showUpload && !csvOnly && <MenuUploadPanel locationNames={locationNames} />}
      {showUpload && (
      <div style={{ background: t.white06, border: `0.5px solid ${t.borderMid}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
        <div style={{ color: t.textPrimary, fontSize: 13, marginBottom: 6 }}>Update from a CSV export</div>
        <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
          Upload a fresh export to refresh prices and add new items. Items keep their
          ids, so approved descriptions and out-of-stock flags carry over. Attach only
          the lists that have changed — anything you leave blank is left alone.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {uploadableCats.map(cat => (
            <div key={cat.key}>
              <div style={{ color: t.textSecondary, fontSize: 11, marginBottom: 4 }}>
                {cat.label} CSV <span style={{ color: t.textDim }}>· optional</span>
              </div>
              <input type="file" accept=".csv,text/csv"
                ref={el => { reimportInputsRef.current[cat.key] = el; }}
                onChange={e => {
                  const f = e.target.files?.[0] || null;
                  setReimport(p => ({ ...p, files: { ...p.files, [cat.key]: f }, msg: '' }));
                }}
                style={{ color: t.textSecondary, fontSize: 12, width: '100%' }} />
              <div style={{ color: t.textDim, fontSize: 10, marginTop: 2 }}>{cat.columns}</div>
            </div>
          ))}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={reimport.removeMissing} style={{ marginTop: 2 }}
              onChange={e => setReimport(p => ({ ...p, removeMissing: e.target.checked }))} />
            <span style={{ color: t.textSecondary, fontSize: 11, lineHeight: 1.4 }}>
              Remove items not in this file. Leave unchecked to add and update only —
              anything you have 86'd or edited stays as it is.
            </span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={runReimport} disabled={reimport.running}
              style={{ background: reimport.running ? t.white06 : t.accent, border: 'none', color: reimport.running ? t.textSecondary : t.bgBase, padding: '8px 16px', borderRadius: 6, cursor: reimport.running ? 'default' : 'pointer', fontFamily: t.fontSerif, fontSize: 12 }}>
              {reimport.running ? 'Importing…' : 'Upload'}
            </button>
            {reimport.msg && <div style={{ color: t.textSecondary, fontSize: 11 }}>{reimport.msg}</div>}
          </div>
          {reimport.warnings.length > 0 && (
            <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorder}`, borderRadius: 6, padding: 10 }}>
              <div style={{ color: t.accent, fontSize: 11, marginBottom: 4 }}>{reimport.warnings.length} warning(s)</div>
              {reimport.warnings.map((w, i) => (
                <div key={i} style={{ color: t.textSecondary, fontSize: 11, lineHeight: 1.5 }}>· {w}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      )}

      {/* Pipeline status banner */}
      {pipelineStatus.phase && pipelineStatus.phase !== 'idle' && (
        <div style={{ background: pipelineStatus.phase === 'complete' ? t.successDim : pipelineStatus.phase === 'error' ? t.errorDim : t.accentDim, border: `0.5px solid ${pipelineStatus.phase === 'complete' ? t.success : pipelineStatus.phase === 'error' ? t.errorBorder : t.accentBorder}`, borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ color: pipelineStatus.phase === 'complete' ? t.success : pipelineStatus.phase === 'error' ? t.error : t.accent, fontSize: 13 }}>
            {phaseLabels[pipelineStatus.phase] || pipelineStatus.phase}
          </div>
          {pipelineRunning && <div style={{ color: t.textDim, fontSize: 11, marginTop: 4 }}>Individual sync buttons disabled while onboarding pipeline is running</div>}
        </div>
      )}

      {/* A manual/file-import tenant has no POS to pull from at all - the
          "tap Sync" instructions and the per-category Sync buttons below both
          describe a live POS connection this tenant doesn't have. Showing them
          anyway is confusing (and, for a restaurant on a real POS elsewhere, a
          wrong-tenant footgun waiting to happen), so both are gated on the same
          menuSource.posType check that already decides whether the upload panel
          shows. Manual tenants get ONLY the "Update Menu from File" panel above -
          it is not a fallback here, it is the whole story. */}
      {menuSource?.posType !== 'manual' && (<>
      {!pipelineRunning && (
        <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 16, lineHeight: 1.6 }}>
          {menuSource?.liveSync
            ? `Tap \u27F3 Sync next to a menu to pull the latest data from ${menuSource.posName || 'your POS'}. Full syncs run automatically at 5:15 PM daily — change an item there and it appears here, not the other way round.`
            : 'Tap \u27F3 Sync next to a menu to pull the latest data from your POS. Full syncs run automatically at 5:15 PM daily.'}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24 }}>
        {categories.map(c => {
          const isSyncing = syncingId === c.id;
          const result = results[c.id];
          const disabled = isSyncing || syncingId !== null || pipelineRunning;
          return (
            <div key={c.id} style={{ background: t.white04, border: `0.5px solid ${t.borderMid}`, borderRadius: 8, padding: "10px 12px", opacity: pipelineRunning ? 0.5 : 1 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: t.textPrimary, fontSize: 13, fontFamily: t.fontSerif }}>{c.label}</span>
                </div>
                <button onClick={() => handleSyncOne(c.id)} disabled={disabled}
                  style={{ background: isSyncing ? t.accentDimSm : t.accentDim, border: `0.5px solid ${t.accentBorder}`, color: isSyncing ? t.textDim : t.accent, padding: "5px 12px", borderRadius: 6, cursor: disabled ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 11, whiteSpace: "nowrap", opacity: disabled && !isSyncing ? 0.4 : 1 }}>
                  {isSyncing ? "…" : "⟳ Sync"}
                </button>
              </div>
              {result && (
                <div style={{ marginTop: 6, fontSize: 11, color: result.ok ? t.success : t.error, fontStyle: "italic" }}>
                  {result.message}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>)}

    </div>
  );
}

// Does an item carry a price a guest will see? Wine: a glass or bottle price
// (a price tier is not a price - all restaurants must give real prices now).
// Everything else: a price, a Toast size price (soup cup/bowl, pour sizes), or
// the menu group's price. Used by Needs review → No price and its count, so the
// two always agree.
function itemHasPrice(item, kind) {
  const pos = v => Number(v) > 0;
  if (!item) return false;
  if (kind === "wine") return pos(item.glassPrice) || pos(item.bottlePrice);
  if (pos(item.price) || pos(item.groupPrice)) return true;
  return Array.isArray(item.sizePrices) && item.sizePrices.some(sp => sp && pos(sp.price));
}

// ─── All Items Tab ────────────────────────────────────────────────────────────
//
// The one place an item is managed (Oct 16, 2026). This used to be two tabs:
// All Items (what guests read, hide/show, re-enrich, and the "Your menu" half
// with prices and sections) and Add / Remove Items (the items half of
// MenusScreen: add, remove, rename a section, restore). Both talked to the same
// items, so they are now one list:
//
//   - Grouped by section (course for food), with Rename on a section heading.
//   - ＋ Add item, for a category the restaurant manages here (not one a live POS
//     feeds). The new item opens in the editor straight away so it can be
//     enriched and checked without hunting for it.
//   - Remove from menu inside the editor. It ARCHIVES (menuManager deleteItem),
//     and Removed items at the foot of the list restores it.
//   - Everything else is the editor that was already here.
//
// Which categories are editable comes from menuManager listMenus, the same
// source YourMenuSection and MenusScreen use, and the server still refuses
// writes to a POS-owned category with 409. Nothing here relaxes a rule.
//
// Add, remove, restore and rename all finish with onDataChanged() (the parent's
// refreshMenuData), which also bumps refreshKey so the lists here reload. One
// refresh path, so the review tiles' counts move with the list.

//
// statusView: the same list and editor, narrowed to what guests cannot see right
// now - out of stock (available === false, from the POS or a manual 86) and/or
// hidden (excluded). Used by Menu items → Out of stock & hidden. Adding, removing,
// renaming sections and the removed-items archive are left out there: that view
// answers "what's off and how do I bring it back", nothing else.
//
// view="noprice": the same list narrowed to items with no price (itemHasPrice),
// every category, for Needs review → No price. Tap one to set the price.
//
// "All" is the default pill everywhere (Oct 16, 2026): searching for a beer with
// Wines selected used to show nothing. Each list item carries `_cat`, its own
// category, and the editor, Remove and the "Your menu" half use that rather than
// the selected pill, so an item opened from All behaves exactly as from its own pill.
// Featuring strengths in words. The server takes a boost of 1 to FEATURE_BOOST_MAX
// (8) and clamps it; these are the only values the editor sends. A boost set by
// hand to something else shows as the nearest level and is left alone unless the
// owner picks one.
const FEATURE_LEVELS = [
  { id: "light",  label: "Light",  boost: 3, help: "Breaks a tie between two equally good matches." },
  { id: "medium", label: "Medium", boost: 5, help: "Wins most close calls in its price range." },
  { id: "strong", label: "Strong", boost: 8, help: "The most allowed. Wins any close call in its price range, but never beats a clearly better match." },
];

// <input type="date"> speaks "YYYY-MM-DD" in local time. An end date means the
// promotion runs through that whole day in the restaurant, so it is stored as the
// last millisecond of the day rather than midnight at its start.
function msToDateInput(ms) {
  const d = new Date(Number(ms));
  if (isNaN(d.getTime())) return "";
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function dateInputToEndOfDayMs(value) {
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

// ─── Vintage vs. name mismatch ─────────────────────────────────────────────────
// The master key is built from the Vintage field, not the name. Changing only the
// year in the name (Qupé Viognier 2020 -> 2017, Oct 2026) left the item linked to
// a 2020 master record and showing a 2020 label for a 2017 wine, with nothing on
// screen to say so. Shown under the Vintage select in both wine editors. Only
// fires when the name holds exactly one plausible year that disagrees with
// Vintage - a name with no year is normal (vintage tracked separately).
function nameYearMismatch(name, vintage) {
  const years = [...new Set((String(name || "").match(/\b(19|20)\d{2}\b/g) || []).map(Number))]
    .filter(y => y >= 1900 && y <= new Date().getFullYear());
  if (years.length !== 1) return null;
  const y = years[0];
  return String(vintage ?? "") === String(y) ? null : y;
}
function VintageMismatchNote({ name, vintage, onUseYear }) {
  const t = useTheme();
  const y = nameYearMismatch(name, vintage);
  if (!y) return null;
  const current = vintage === "NV" ? "NV" : (vintage ? String(vintage) : "not specified");
  return (
    <div style={{ background: t.white04, border: `0.5px solid ${t.warning}`, borderRadius: 6, padding: "8px 10px", margin: "6px 0 10px", fontSize: 11.5, lineHeight: 1.5, color: t.textSecondary }}>
      The name says <strong>{y}</strong> but Vintage is <strong>{current}</strong>. Vintage decides which
      bottle SommSync researches and which label image guests see.
      <button onClick={() => onUseYear(y)} style={{ display: "block", marginTop: 6, background: "none", border: `0.5px solid ${t.borderMid}`, borderRadius: 5, padding: "4px 10px", color: t.accent, fontSize: 11.5, cursor: "pointer" }}>
        Set Vintage to {y}
      </button>
    </div>
  );
}

function AllItemsTab({ wines, onWineUpdate, managerSearch, onRequestUpcScan, isAdmin = false, refreshKey = 0, onDataChanged, view = null }) {
  const t = useTheme();
  const mm = useMmStyles();
  const statusView = view === "status";
  const priceView = view === "noprice";
  const [statusFilter, setStatusFilter] = useState("all"); // statusView only: all | oos | hidden
  const [beers, setBeers] = useState([]);
  const [pours, setPours] = useState([]);
  const [food, setFood] = useState([]);
  const [cocktails, setCocktails] = useState([]);
  const [nab, setNab] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [addPicker, setAddPicker] = useState(false); // All selected + Add: which category?
  const [editingItem, setEditingItem] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [editBaseline, setEditBaseline] = useState({});
  // Translations of the item being edited (step 3a). trData is the server's view
  // (getTranslations); trDraft holds the text fields; trTouched marks which
  // fields to send. Only touched fields are saved, so an untouched AI translation
  // stays AI.
  const [trData, setTrData] = useState(null);
  const [trDraft, setTrDraft] = useState({ description: {}, name: {} });
  const [trTouched, setTrTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [reEnriching, setReEnriching] = useState(false);
  const [reEnrichResult, setReEnrichResult] = useState(null);
  const menuRef = useRef(null); // the "Your menu" half of the editor (YourMenuSection)

  // Menu-management half (formerly Add / Remove Items)
  const [mmInfo, setMmInfo] = useState(null);         // listMenus response, null until loaded or if it failed
  const [adding, setAdding] = useState(false);
  const [pendingOpenId, setPendingOpenId] = useState(null); // a just-added item to open once the list has it
  const [removing, setRemoving] = useState(false);
  const [notice, setNotice] = useState(null);         // { kind, text }
  const [renamingGroup, setRenamingGroup] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  // Food course overrides (see courseKind). Held in state so the headings
  // re-render on save; mirrored into the module copy the guest screens read.
  const [courseOverrides, setCourseOverridesState] = useState({});
  const [courseBusy, setCourseBusy] = useState(null); // section name being saved
  // Featured wines (readFeaturedWines on the server): { wineId: { boost, until } }.
  // null until it loads - and it only loads for an owner, since saveSettings is
  // owner-gated. A floor manager signed in by PIN never sees the control.
  const [featuredWines, setFeaturedWines] = useState(null);
  // The open wine's featuring, as the editor shows it: { on, boost, until } with
  // until as "YYYY-MM-DD" or "". Saved with Save Changes like every other field.
  const [featureDraft, setFeatureDraft] = useState(null);
  const [featureBaseline, setFeatureBaseline] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [archive, setArchive] = useState(null);
  // Removed this session. Filtered out at once, so a removed wine (which lives
  // in the parent's list) does not linger until the refresh lands.
  const [removedIds, setRemovedIds] = useState(() => new Set());

  // Silent after the first load: a refresh after a save must not blank the list
  // the manager is working in.
  const loadLists = useCallback(async () => {
    try {
      // admin=1 so excluded items stay visible here and can be re-included, and
      // (for food) so 86'd dishes are listed rather than vanishing from the editor.
      const [bData, pData, fData, cData, nData] = await Promise.all([
        rfetch(BEER_URL + "?admin=1").then(r => r.json()),
        rfetch(POURS_URL + "?admin=1").then(r => r.json()),
        rfetch(FOOD_URL + "?admin=1").then(r => r.json()),
        rfetch(COCKTAILS_URL + "?admin=1").then(r => r.json()),
        rfetch(NAB_URL + "?admin=1").then(r => r.json()),
      ]);
      let info = null;
      try { info = await mmCall("listMenus"); } catch (e) { info = null; }
      let foodList = fData.foodItems || [];
      // getFoodItems still applies menu schedules and drops dishes the pairing
      // matrix marked vague, so a lunch dish disappears from this list at 5pm.
      // Where food is managed here, the raw item list is the truth: add anything
      // the guest-shaped read left out. For food, "hidden" and "leave out of
      // pairings" are the same flag (foodExclusions), so it maps straight across.
      if (info && info.editable && info.editable.foodItems) {
        try {
          const raw = (await mmCall("listItems", { category: "foodItems" })).items || [];
          const have = new Set(foodList.map(f => f.id));
          foodList = [...foodList, ...raw.filter(r => !have.has(r.id)).map(r => ({ ...r, excluded: !!r.excludeFromPairing }))];
        } catch (e) {}
      }
      setBeers(bData.beers || []);
      setPours(pData.pours || []);
      setFood(foodList);
      if (fData.courseOverrides) { setCourseOverridesState(fData.courseOverrides); setCourseOverrides(fData.courseOverrides); }
      setCocktails(cData.cocktails || []);
      setNab(nData.nab || []);
      setMmInfo(info);
    } catch (e) {}
    setLoading(false);
  }, []);
  useEffect(() => { loadLists(); }, [loadLists, refreshKey]);

  const CATEGORY_TO_TYPE = { wines: "wine", beer: "beer", pours: "pour", cocktails: "cocktail", nab: "nab", food: "food" };
  const keep = list => removedIds.size ? list.filter(x => !removedIds.has(x.id)) : list;
  const isOos = it => it.available === false;
  const isHidden = it => !!it.excluded;
  const statusMatch = it => statusFilter === "oos" ? isOos(it) : statusFilter === "hidden" ? isHidden(it) : (isOos(it) || isHidden(it));
  const KIND_OF = { wines: "wine", food: "food", beer: "bev", pours: "bev", cocktails: "bev", nab: "bev" };
  const narrow = (list, cat) => keep(list)
    .filter(it => statusView ? statusMatch(it) : priceView ? !itemHasPrice(it, KIND_OF[cat]) : true)
    .map(it => ({ ...it, _cat: cat }));
  const fullLists = { wines: narrow(wines, "wines"), beer: narrow(beers, "beer"), pours: narrow(pours, "pours"), cocktails: narrow(cocktails, "cocktails"), nab: narrow(nab, "nab"), food: narrow(food, "food") };
  const CAT_ORDER = ["wines", "beer", "pours", "cocktails", "nab", "food"];
  const CAT_LABEL = { wines: "Wines", beer: "Beer", pours: "Pours", cocktails: "Cocktails", nab: "N/A Bev", food: "Food" };
  fullLists.all = CAT_ORDER.flatMap(c => fullLists[c]);
  // Totals for the status filter chips, across every category, ignoring search.
  const statusTotals = (() => {
    if (!statusView) return null;
    const all = [wines, beers, pours, cocktails, nab, food].flatMap(keep);
    return { oos: all.filter(isOos).length, hidden: all.filter(isHidden).length, all: all.filter(it => isOos(it) || isHidden(it)).length };
  })();
  // Category-level controls (Add into, Rename a section, Removed items, the POS
  // note) need one category, so they act on the selected pill and are off on All.
  const mmCatId = category === "all" ? null : MM_CAT_FOR_ITEMS_TAB[category];
  const mmCat = MM_CATEGORIES.find(c => c.id === mmCatId) || null;
  // Per-item: may this item's category be changed here (add/remove/86/price)?
  const catEditable = c => !!(mmInfo && mmInfo.editable && mmInfo.editable[MM_CAT_FOR_ITEMS_TAB[c]]);
  const editableCats = CAT_ORDER.filter(catEditable);
  // In the status view nothing is added, removed or renamed, so the
  // menu-management controls key off this rather than raw editability. The
  // editor's own "Your menu" half (86, price) still follows the server's rules.
  const editableRaw = !!(mmInfo && mmInfo.editable && mmInfo.editable[mmCatId]);
  const editable = editableRaw && !statusView;
  const activeMenus = ((mmInfo && mmInfo.menus) || []).filter(m => !m.retired);

  // Removed items: loaded only when asked for, and reloaded after any change.
  useEffect(() => {
    if (!showArchive || !editable) return;
    let alive = true;
    setArchive(null);
    mmCall("listArchive", { category: mmCatId })
      .then(d => { if (alive) setArchive(d.items || []); })
      .catch(() => { if (alive) setArchive([]); });
    return () => { alive = false; };
  }, [showArchive, mmCatId, editable, refreshKey]);

  function flash(kind, text) {
    setNotice({ kind, text });
    setTimeout(() => setNotice(n => (n && n.text === text ? null : n)), 8000);
  }

  function switchCategory(id) {
    setCategory(id); setEditingItem(null); setReEnrichResult(null); setAdding(false); setAddPicker(false);
    setRenamingGroup(null); setShowArchive(false); setArchive(null);
  }

  useEffect(() => {
    let cancelled = false;
    rfetch(SAVE_SETTINGS_URL, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "listFeaturedWines" }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d && d.ok) setFeaturedWines(d.featured || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [refreshKey]);

  // A wine opened before the list arrived showed featuring as off. Correct it
  // once the list lands - unless the owner has already started changing it.
  useEffect(() => {
    if (!editingItem || editingItem._type !== "wine" || !featuredWines) return;
    if (JSON.stringify(featureDraft) !== JSON.stringify(featureBaseline)) return;
    const f = featuredWines[editingItem.id];
    const feat = { on: !!f, boost: f ? Number(f.boost) || FEATURE_LEVELS[2].boost : FEATURE_LEVELS[1].boost, until: f && f.until ? msToDateInput(f.until) : "" };
    if (JSON.stringify(feat) === JSON.stringify(featureBaseline)) return;
    setFeatureBaseline(feat);
    setFeatureDraft(feat);
  }, [featuredWines]);

  function openEdit(item, type) {
    setAdding(false);
    setReEnrichResult(null);
    setEditingItem({ ...item, _type: type });
    const initial =
      type === "wine" ? { correctedName: item.name || "", varietal: item.varietal || "", region: item.region || "", description: item.description || "", reviews: item.reviews || "", vintage: item.vintage !== undefined ? item.vintage : null, excluded: item.excluded || false }
      : type === "beer" ? { correctedName: item.name || "", style: item.style || "", brewery: item.brewery || "", abv: item.abv || "", description: item.description || "", excluded: item.excluded || false }
      : type === "pour" ? { correctedName: item.name || "", category: item.category || "", producer: item.producer || "", abv: item.abv || "", description: item.description || "", excluded: item.excluded || false }
      : type === "food" ? { description: item.description || "", excluded: item.excluded || false }
      : { excluded: item.excluded || false }; // cocktail / nab: name and description are in the Your menu half
    setEditBaseline(initial);
    setEditFields(initial);
    const f = type === "wine" && featuredWines ? featuredWines[item.id] : null;
    const feat = { on: !!f, boost: f ? Number(f.boost) || FEATURE_LEVELS[2].boost : FEATURE_LEVELS[1].boost, until: f && f.until ? msToDateInput(f.until) : "" };
    setFeatureBaseline(feat);
    setFeatureDraft(feat);
    loadTranslations(item.id, type);
  }

  function loadTranslations(itemId, type) {
    setTrData(null); setTrTouched({}); setTrDraft({ description: {}, name: {} });
    rfetch(MANAGER_UPDATE_URL, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "getTranslations", itemId, itemType: type }),
    }).then(r => r.json()).then(d => {
      if (!d || !d.ok) return;
      setTrData(d);
      setTrDraft({
        description: Object.fromEntries(Object.entries(d.description || {}).map(([l, v]) => [l, v ? v.text : ""])),
        name: Object.fromEntries(Object.entries(d.name || {}).map(([l, v]) => [l, v || ""])),
      });
    }).catch(() => {});
  }

  // In a narrowed view, a pill whose last item was just fixed falls back to All
  // rather than leaving the manager on an empty list.
  useEffect(() => {
    if (!view || loading || category === "all") return;
    if ((fullLists[category] || []).length === 0) switchCategory("all");
  }, [view, loading, statusFilter, wines, beers, pours, cocktails, nab, food]);

  // A just-added item is opened as soon as the refreshed list contains it.
  useEffect(() => {
    if (!pendingOpenId) return;
    const it = fullLists.all.find(x => x.id === pendingOpenId);
    if (it) { openEdit(it, CATEGORY_TO_TYPE[it._cat]); setPendingOpenId(null); }
  }, [pendingOpenId, wines, beers, pours, cocktails, nab, food]);

  async function handleSave() {
    if (!editingItem) return;
    setSaving(true);
    try {
      const t = editingItem._type;
      const menuDirty = !!(menuRef.current && menuRef.current.isDirty());
      const guestDirty = Object.keys(editFields).some(k => String(editFields[k] ?? "") !== String(editBaseline[k] ?? ""));
      const featureDirty = t === "wine" && featuredWines !== null && !!featureDraft
        && JSON.stringify(featureDraft) !== JSON.stringify(featureBaseline);
      // Translations are saved LAST, after any change to the original, so a
      // translation is stamped against the original as it now stands.
      const trDirty = Object.keys(trTouched).length > 0;

      // Featuring first: it is the one part with a check that can fail on the
      // owner's input (an end date already past), and failing before anything
      // else is written keeps the save all-or-nothing from where they sit.
      if (featureDirty) {
        const res = await rfetch(SAVE_SETTINGS_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "setFeaturedWine", wineId: editingItem.id,
            featured: featureDraft.on, boost: featureDraft.boost,
            until: featureDraft.on && featureDraft.until ? dateInputToEndOfDayMs(featureDraft.until) : null,
          }),
        });
        let data = {};
        try { data = await res.json(); } catch (e) {}
        if (!res.ok || !data.ok) {
          const msg = data.error || `Could not save featuring (${res.status})`;
          flash("error", msg);
          throw new Error(msg);
        }
        setFeaturedWines(data.featured || {});
      }

      // Menu half first. If it is refused (bad price, a POS-owned item) it shows
      // its own error inside the editor and the throw stops everything here, so
      // nothing is half-saved silently.
      let menuSaved = null;
      if (menuDirty) menuSaved = await menuRef.current.save();

      // Guest half. Posted exactly as before in every case that existed before
      // this editor had a menu half (no menu change). It is skipped ONLY when the
      // sole change was a menu field: posting it marks the item manager-edited,
      // which the server treats as approval, and fixing a price must not publish
      // an item nobody has reviewed.
      // Not when featuring was the only change either: same reason - turning on a
      // promotion must not quietly approve an unreviewed wine.
      // Nor when translations were the only change: a translation is not a review.
      if (guestDirty || (!menuDirty && !featureDirty && !trDirty)) {
        await rfetch(MANAGER_UPDATE_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ itemId: editingItem.id, itemType: editingItem._type, updates: editFields })
        });
        if (t === "wine") onWineUpdate(editingItem.id, { ...editFields, name: editFields.correctedName });
        else if (t === "beer") setBeers(prev => prev.map(b => b.id === editingItem.id ? { ...b, ...editFields, name: editFields.correctedName } : b));
        else if (t === "pour") setPours(prev => prev.map(p => p.id === editingItem.id ? { ...p, ...editFields, name: editFields.correctedName } : p));
        else if (t === "food") setFood(prev => prev.map(f => f.id === editingItem.id ? { ...f, ...editFields } : f));
        else if (t === "cocktail") setCocktails(prev => prev.map(i => i.id === editingItem.id ? { ...i, ...editFields } : i));
        else if (t === "nab") setNab(prev => prev.map(i => i.id === editingItem.id ? { ...i, ...editFields } : i));
      }

      // Reflect the menu changes in the list rows. Only the menu fields are
      // copied across - the saved record's own `name` is the menu's name, and
      // must not overwrite the AI-corrected name shown to guests.
      if (menuSaved && menuSaved.item) {
        const picked = {};
        ["glassPrice", "bottlePrice", "priceTier", "price", "groupPrice", "subgroup", "course", "available", "menuGuids"]
          .forEach(k => { if (menuSaved.item[k] !== undefined) picked[k] = menuSaved.item[k]; });
        // For wine, beer and spirits the saved record's name is the MENU name and must
        // not replace the AI-corrected one. For food, cocktails and non-alcoholic it is
        // the only name there is, and for the latter two the description is too.
        const ownName = t === "food" || t === "cocktail" || t === "nab";
        if (ownName && menuSaved.item.name !== undefined) picked.name = menuSaved.item.name;
        if ((t === "cocktail" || t === "nab") && menuSaved.item.description !== undefined) picked.description = menuSaved.item.description;
        if (t === "wine") onWineUpdate(editingItem.id, picked);
        else if (t === "beer") setBeers(prev => prev.map(b => b.id === editingItem.id ? { ...b, ...picked } : b));
        else if (t === "pour") setPours(prev => prev.map(p => p.id === editingItem.id ? { ...p, ...picked } : p));
        else if (t === "food") setFood(prev => prev.map(f => f.id === editingItem.id ? { ...f, ...picked } : f));
        else if (t === "cocktail") setCocktails(prev => prev.map(i => i.id === editingItem.id ? { ...i, ...picked } : i));
        else if (t === "nab") setNab(prev => prev.map(i => i.id === editingItem.id ? { ...i, ...picked } : i));
        if (menuSaved.warnings && menuSaved.warnings.length) flash("warning", menuSaved.warnings.join(" "));
      }
      if (trDirty) {
        const payload = { description: {}, name: {} };
        Object.keys(trTouched).forEach(k => {
          const [field, lang] = k.split(":");
          payload[field][lang] = (trDraft[field] || {})[lang] || "";
        });
        const res = await rfetch(MANAGER_UPDATE_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "saveTranslations", itemId: editingItem.id, itemType: editingItem._type, ...payload }),
        });
        let data = {};
        try { data = await res.json(); } catch (e) {}
        if (!res.ok || !data.ok) {
          const msg = data.error || `Could not save translations (${res.status})`;
          flash("error", msg);
          throw new Error(msg);
        }
      }
      setEditingItem(null);
      // The narrowed views exist to be worked down to zero; keep the hub's
      // counts honest as they are.
      if (view && onDataChanged) onDataChanged();
    } catch (e) { console.error(e); }
    setSaving(false);
  }

  async function handleReEnrich() {
    if (!editingItem || !["wine", "beer", "pour"].includes(editingItem._type)) return;
    setReEnriching(true);
    setReEnrichResult(null);
    // Pass current edit fields as hints so Claude respects manager corrections
    const hints = {};
    if (editingItem._type === "wine") {
      if (editFields.region)        hints.region = editFields.region;
      Object.assign(hints, changedFields(editFields, editBaseline, ['region', 'varietal', 'correctedName']));
      // Vintage always travels: it is a real three-state value (year / NV / not
      // tracked) and enrichment needs to know which, not infer it from silence.
      hints.vintage = editFields.vintage !== undefined ? editFields.vintage : null;
    } else if (editingItem._type === "beer") {
      Object.assign(hints, changedFields(editFields, editBaseline, ['brewery', 'style', 'abv']));
    } else if (editingItem._type === "pour") {
      Object.assign(hints, changedFields(editFields, editBaseline, ['producer', 'category', 'abv']));
    }
    try {
      const res = await rfetch(REENRICH_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId: editingItem.id, itemType: editingItem._type, itemName: editingItem.name, hints })
      });
      const data = await res.json();
      if (data.ok) {
        setReEnrichResult({ ok: true, warn: !!data.uncertain, message: data.uncertain ? `Re-enriched — flagged for review: ${data.uncertainReason}` : '✓ Re-enriched successfully' });
        const e = data.enrichment;
        if (editingItem._type === "wine") {
          const updates = { correctedName: e.correctedName, varietal: e.varietal, region: e.region, description: e.description, reviews: e.reviews, vintage: e.vintage !== undefined ? e.vintage : null };
          setEditFields(p => ({ ...p, ...updates }));
          // Re-baseline: the model's fresh answer is now the starting point. Without
          // this, a second re-enrich in the same session would diff against the
          // ORIGINAL values and hand the first re-enrichment's output back to Claude
          // as manager-confirmed fact.
          setEditBaseline(p => ({ ...p, ...updates }));
          onWineUpdate(editingItem.id, { ...updates, name: e.correctedName, ...enrichmentStateFields(e) });
        } else if (editingItem._type === "beer") {
          const updates = { correctedName: e.correctedName, style: e.style, brewery: e.brewery, abv: e.abv, description: e.description };
          setEditFields(p => ({ ...p, ...updates }));
          setEditBaseline(p => ({ ...p, ...updates }));
          setBeers(prev => prev.map(b => b.id === editingItem.id ? { ...b, ...updates, name: e.correctedName, ...enrichmentStateFields(e) } : b));
        } else if (editingItem._type === "pour") {
          const updates = { correctedName: e.correctedName, category: e.category, producer: e.producer, abv: e.abv, description: e.description };
          setEditFields(p => ({ ...p, ...updates }));
          setEditBaseline(p => ({ ...p, ...updates }));
          setPours(prev => prev.map(p => p.id === editingItem.id ? { ...p, ...updates, name: e.correctedName, ...enrichmentStateFields(e) } : p));
        }
        // Re-enrichment sends the item back for approval and hides it from
        // guests until then. Without this, Needs review kept its sign-in count
        // and the item looked like it had vanished rather than moved into the
        // queue (Oct 28, 2026).
        if (onDataChanged) onDataChanged();
      } else {
        setReEnrichResult({ ok: false, message: data.error || "Re-enrichment failed" });
      }
    } catch (e) {
      setReEnrichResult({ ok: false, message: e.message });
    }
    setReEnriching(false);
  }

  // ── Add / remove / restore / rename (formerly the Add / Remove Items tab) ──

  function handleAdded(it, warnings) {
    setAdding(false);
    const enrichable = mmCat && (mmCat.kind === "wine" || mmCat.enrichable);
    const lead = enrichable
      ? `${it.name} added. Guests won't see it until SommSync has researched it and it is approved - tap ✦ Enrich below to do that now.`
      : `${it.name} added.`;
    flash(warnings && warnings.length ? "warning" : "success", [lead, ...(warnings || [])].join(" "));
    setPendingOpenId(it.id);
    if (onDataChanged) onDataChanged(); else loadLists();
  }

  async function handleRemove() {
    if (!editingItem || view || !catEditable(editingItem._cat) || removing) return;
    const it = editingItem;
    const label = it.name || it.sourceName;
    if (!window.confirm(`Remove "${label}" from the menu?\n\nGuests stop seeing it right away. You can bring it back from Removed items at the bottom of this list.`)) return;
    setRemoving(true);
    try {
      await mmCall("deleteItem", { category: MM_CAT_FOR_ITEMS_TAB[it._cat], id: it.id });
      // Gone from the list at once; the refresh below brings the parent's wine
      // list and the review counts into line.
      setRemovedIds(prev => new Set(prev).add(it.id));
      setEditingItem(null); setReEnrichResult(null);
      flash("success", `${label} removed. Restore it from Removed items if that was a mistake.`);
      if (onDataChanged) onDataChanged(); else loadLists();
    } catch (e) { flash("error", e.message); }
    setRemoving(false);
  }

  async function restore(a) {
    try {
      const data = await mmCall("restoreItem", { category: mmCatId, id: a.id });
      setArchive(x => (x || []).filter(y => y.id !== a.id));
      setRemovedIds(prev => { const n = new Set(prev); n.delete(a.id); return n; });
      flash(data.warnings && data.warnings.length ? "warning" : "success", [`${a.name} is back on the menu.`, ...(data.warnings || [])].join(" "));
      if (onDataChanged) onDataChanged(); else loadLists();
    } catch (e) { flash("error", e.message); }
  }

  // Rename a section heading ("Red Wine" -> "Reds") without opening every item.
  // A heading is not stored on its own: it is the Section value its items share,
  // so renaming it saves that value onto each of them through the same saveItem
  // call the editor uses (same rules: floor managers and owners, never a category
  // fed by a live POS). Works on ALL items under the heading, not just the ones
  // the search box shows. Four saves at a time; a part-way failure reports how
  // many went through rather than pretending nothing happened.
  async function renameSection(oldName, rawNew) {
    let newName = String(rawNew || "").trim();
    if (!mmCat || !editable || !newName || newName === oldName) { setRenamingGroup(null); return; }
    const field = mmCat.groupField;
    const all = fullLists[category] || [];
    const labelOf = it => (it[field] && String(it[field]).trim()) || "Other";
    const inGroup = all.filter(it => labelOf(it) === oldName);
    if (inGroup.length === 0) { setRenamingGroup(null); return; }
    // Typing an existing section's name, in any capitalisation, means "merge
    // into it". Adopt its exact spelling so the merge leaves one heading.
    const existingLabel = all.map(labelOf).find(l => l !== oldName && l.toLowerCase() === newName.toLowerCase());
    if (existingLabel) {
      newName = existingLabel;
      if (!window.confirm(`There is already a section called "${newName}".\n\nMove these ${inGroup.length} item${inGroup.length === 1 ? "" : "s"} into it?`)) return;
    }
    setRenameBusy(true);
    let done = 0;
    let failure = null;
    const queue = [...inGroup];
    async function worker() {
      while (queue.length && !failure) {
        const it = queue.shift();
        try { await mmCall("saveItem", { category: mmCatId, id: it.id, item: { [field]: newName } }); done++; }
        catch (e) { failure = e; }
      }
    }
    await Promise.all([worker(), worker(), worker(), worker()]);
    setRenameBusy(false);
    if (failure) {
      flash("error", `Renamed ${done} of ${inGroup.length} to "${newName}", then it stopped: ${failure.message} Try again to finish the rest.`);
    } else {
      setRenamingGroup(null);
      flash("success", `"${oldName}" is now "${newName}" (${inGroup.length} item${inGroup.length === 1 ? "" : "s"}).`);
    }
    if (onDataChanged) onDataChanged(); else loadLists();
  }

  // Override how the pairing screen treats a food section. Owner-only (the
  // server gates saveSettings at owner). "auto" clears the override so the
  // name-based guess applies again.
  async function saveCourseKind(sectionName, kind) {
    setCourseBusy(sectionName);
    try {
      const res = await rfetch(SAVE_SETTINGS_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "setCourseOverride", course: sectionName, kind }),
      });
      let data = {};
      try { data = await res.json(); } catch (e) {}
      if (!res.ok || !data.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setCourseOverridesState(data.courseOverrides || {});
      setCourseOverrides(data.courseOverrides || {});
      flash("success", kind === "auto"
        ? `"${sectionName}" is back to automatic: ${COURSE_KIND_LABELS[guessCourseKind(sectionName)]}.`
        : `Guests now pair "${sectionName}" as ${COURSE_KIND_LABELS[kind]}.`);
    } catch (e) { flash("error", e.message); }
    setCourseBusy(null);
  }

  const q = foldText(managerSearch).trim();
  const filterList = list => q ? list.filter(i => searchHit(i.name, q) || searchHit(i.sourceName, q)) : list;

  const categories = [{ id: "all", label: "All" }, ...CAT_ORDER.map(id => ({ id, label: CAT_LABEL[id] }))]
    .map(c => ({ ...c, list: filterList(fullLists[c.id] || []) }))
    // In a narrowed view a category with nothing in it is noise: leave it out.
    .filter(c => !view || c.id === "all" || (fullLists[c.id] || []).length > 0 || c.id === category);
  const activeList = categories.find(c => c.id === category)?.list || [];
  const isEnrichable = !!editingItem && ["wine", "beer", "pour"].includes(editingItem._type);
  const neverEnriched = !!editingItem && !editingItem.enrichedAt && !editingItem.description;

  // Group by section (course for food), keeping the list's own order. On All the
  // headings are the categories instead, and the section shows on each row.
  const onAll = category === "all";
  const groupField = mmCat ? mmCat.groupField : (category === "food" ? "course" : "subgroup");
  const groups = [];
  const groupIndex = {};
  activeList.forEach(it => {
    const g = onAll ? CAT_LABEL[it._cat] : ((it[groupField] && String(it[groupField]).trim()) || "Other");
    if (!(g in groupIndex)) { groupIndex[g] = groups.length; groups.push({ name: g, items: [] }); }
    groups[groupIndex[g]].items.push(it);
  });
  const showHeadings = onAll || groups.length > 1 || (groups.length === 1 && groups[0].name !== "Other");
  // Food headings are courses, which the pairing screen treats specially
  // (see courseKind), so free-text rename is offered only where the heading is
  // a plain section name. Food headings get the "Pairs as" control instead.
  const canRename = editable && !onAll && groupField === "subgroup";
  const showCourseKind = !view && !onAll && category === "food";
  const courseKindControl = name => {
    if (!showCourseKind || name === "Other") return null;
    const override = courseOverrides[normCourseName(name)];
    const guess = guessCourseKind(name);
    const effective = courseKind(name, courseOverrides);
    if (!isAdmin) return (
      <span title="How the pairing screen treats this section. An owner can change it."
        style={{ marginLeft: "auto", color: t.textDim, fontSize: 10 }}>
        Pairs as {COURSE_KIND_LABELS[effective]}
      </span>
    );
    return (
      <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, color: t.textDim, fontSize: 10 }}>
        Pairs as
        <select value={override || "auto"} disabled={courseBusy === name}
          onChange={e => saveCourseKind(name, e.target.value)}
          aria-label={`How guests pair ${name}`}
          style={{ ...mm.input, width: "auto", padding: "3px 6px", color: override ? t.accent : t.textSecondary }}>
          <option value="auto">Auto ({COURSE_KIND_LABELS[guess]})</option>
          <option value="open">First or Main</option>
          <option value="entree">Main only</option>
          <option value="dessert">Dessert</option>
        </select>
      </label>
    );
  };

  const priceText = it => {
    const kind = KIND_OF[it._cat] || "bev";
    if (kind === "wine") return (it.glassPrice || it.bottlePrice || it.priceTier) ? mmPriceText(it, "wine") : null;
    if (Array.isArray(it.sizePrices) && it.sizePrices.some(sp => sp && Number(sp.price) > 0)) return formatItemPrice(it);
    if (!(it.price || it.groupPrice)) return null;
    return mmPriceText({ ...it, price: it.price || it.groupPrice }, kind);
  };

  const inputStyle = input(t);
  const labelStyle = { ...labelStyleFor(t), color: t.textSecondary, letterSpacing: "1px", marginBottom: 3 };

  if (loading) return <div style={{ color: t.textSecondary, textAlign: "center", padding: 40 }}>Loading…</div>;

  const renderRow = item => (
    <div key={`${item._cat}:${item.id}`} onClick={() => openEdit(item, CATEGORY_TO_TYPE[item._cat] || "wine")}
      style={{ background: editingItem?.id === item.id ? t.accentDimSm : t.white04, border: `0.5px solid ${editingItem?.id === item.id ? t.accentBorder : item.available === false ? t.errorBorder : t.borderMid}`, borderRadius: 8, padding: "10px 12px", marginBottom: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, opacity: item.available === false ? 0.75 : 1 }}>
      {item.imageUrl && <div style={{ width: 32, height: 44, borderRadius: 3, overflow: "hidden", flexShrink: 0 }}><img src={item.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 1 }}>
          <div style={{ color: t.textPrimary, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.name || item.sourceName}
          </div>
          {item.sourceName && item.name && item.sourceName !== item.name && (
            <span style={{ flexShrink: 0, fontSize: 9, background: t.accentDim, border: `0.5px solid ${t.accentBorder}`, color: t.accent, borderRadius: 4, padding: "1px 5px", letterSpacing: "0.5px" }}>AI renamed</span>
          )}
        </div>
        {/* The headline is the name GUESTS see (the AI-corrected name, or the
            menu name if nothing was corrected). The menu's own name sits
            underneath, labelled "Original:" in words (no hover note: hovers do
            not exist on a touch screen, and the label says it already). */}
        {item.sourceName && item.name && item.sourceName !== item.name && (
          <div style={{ color: t.textSecondary, fontSize: 10, fontStyle: "italic", marginBottom: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Original: {item.sourceName}
          </div>
        )}
        <div style={{ color: t.textSecondary, fontSize: 10.5 }}>
          {item._cat === "wines" && [item.varietal, item.region].filter(Boolean).join(" · ")}
          {item._cat === "beer" && [item.style, item.brewery].filter(Boolean).join(" · ")}
          {item._cat === "pours" && [item.category, item.producer].filter(Boolean).join(" · ")}
          {(item._cat === "cocktails" || item._cat === "nab") && (onAll || !showHeadings) && (item.subgroup || "")}
          {item._cat === "food" && (onAll || !showHeadings) && item.course}
          {item.manuallyEdited && <span style={{ color: t.success, marginLeft: 6 }}>✎ edited</span>}
          {item._cat === "wines" && featuredWines && featuredWines[item.id] && <span style={{ color: t.accent, marginLeft: 6 }}>★ featured</span>}
          {item.excluded && <span style={{ color: t.error, marginLeft: 6 }}>hidden</span>}
          {item.available === false && <span style={{ color: t.error, marginLeft: 6 }}>86'd</span>}
        </div>
        <div style={{ color: t.textSecondary, fontSize: 10, fontFamily: t.fontMono, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.id}</div>
      </div>
      {priceText(item)
        ? <div style={{ color: t.textSecondary, fontSize: 11, whiteSpace: "nowrap", flexShrink: 0 }}>{priceText(item)}</div>
        : priceView && <div style={{ color: t.warning, fontSize: 11, whiteSpace: "nowrap", flexShrink: 0 }}>No price</div>}
      <div style={{ color: t.borderStrong, fontSize: 18 }}>›</div>
    </div>
  );

  return (
    <div style={{ position: "relative", height: "100%" }}>
      {statusView && (
        <>
          <div role="radiogroup" aria-label="Show" style={{ display: "flex", background: t.white04, borderRadius: 8, padding: 4, marginBottom: 10 }}>
            {[
              { id: "all", label: "All", n: statusTotals.all },
              { id: "oos", label: "Out of stock", n: statusTotals.oos },
              { id: "hidden", label: "Hidden", n: statusTotals.hidden },
            ].map(f => (
              <button key={f.id} type="button" role="radio" aria-checked={statusFilter === f.id}
                onClick={() => { setStatusFilter(f.id); setEditingItem(null); setReEnrichResult(null); }}
                style={{ flex: 1, padding: "9px 6px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: t.fontSerif, fontSize: 13,
                  background: statusFilter === f.id ? t.accentDim : "transparent", color: statusFilter === f.id ? t.accent : t.textSecondary, fontWeight: statusFilter === f.id ? 600 : 400 }}>
                {f.label} <span style={{ opacity: 0.8 }}>({f.n})</span>
              </button>
            ))}
          </div>
          <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
            Tap an item to bring it back: turn on Show to guests, or untick 86'd. Items marked out of stock in your POS come back on their own when the POS shows them in stock.
          </div>
        </>
      )}
      {priceView && (
        <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
          Guests see no price for these. Tap one to add it. Items that come from your POS are priced there, and appear here until the next sync after you fix them.
        </div>
      )}
      {priceView && fullLists.all.length === 0 ? (
        <div style={{ color: t.success, textAlign: "center", padding: "32px 16px" }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
          <div style={{ fontSize: 14 }}>Every item has a price.</div>
        </div>
      ) : statusView && statusTotals.all === 0 ? (
        <div style={{ color: t.success, textAlign: "center", padding: "32px 16px" }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
          <div style={{ fontSize: 14 }}>Nothing is out of stock or hidden. Guests can see your whole menu.</div>
        </div>
      ) : (<>
      {/* Category pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {categories.map(c => (
          <button key={c.id} onClick={() => switchCategory(c.id)} style={{
            background: category === c.id ? t.accentDim : t.white04,
            border: `0.5px solid ${category === c.id ? t.accent : t.borderMid}`,
            color: category === c.id ? t.accent : t.textSecondary,
            padding: "5px 14px", borderRadius: 20, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11
          }}>{c.label} <span style={{ opacity: 0.85 }}>({c.list.length})</span></button>
        ))}
      </div>

      {notice && <MmNotice kind={notice.kind}>{notice.text}</MmNotice>}

      {/* Add, or where items come from when this category is fed by a POS. On All,
          Add asks which category first (or just uses the only one there is). */}
      {(editable || (onAll && !view && editableCats.length > 0)) ? (
        !adding && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: addPicker ? 8 : 12 }}>
              <button type="button" onClick={() => {
                setEditingItem(null); setReEnrichResult(null);
                if (!onAll) { setAdding(true); return; }
                if (editableCats.length === 1) { switchCategory(editableCats[0]); setAdding(true); return; }
                setAddPicker(v => !v);
              }} style={mm.primaryBtn(false)}>＋ Add item</button>
              <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5 }}>Tap an item to edit it, hide it, or remove it. To add a whole menu at once, upload it from Update Menu.</div>
            </div>
            {addPicker && onAll && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                <span style={{ color: t.textSecondary, fontSize: 12, marginRight: 2 }}>Add to:</span>
                {editableCats.map(c => (
                  <button key={c} type="button" style={mm.chip(false)} onClick={() => { switchCategory(c); setAdding(true); }}>{CAT_LABEL[c]}</button>
                ))}
              </div>
            )}
          </>
        )
      ) : !view && !onAll && mmInfo && mmInfo.posName ? (
        <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
          {categories.find(c => c.id === category)?.label} come from {mmInfo.posName}, so add, remove and reprice them there. You can still edit what guests read and hide items here.
        </div>
      ) : null}

      {adding && mmCat && (
        <MenuItemEditor key={`new-${mmCatId}`} cat={mmCat} item={null} menus={activeMenus} isAdmin={isAdmin}
          onCancel={() => setAdding(false)}
          onSaved={(it, warnings) => handleAdded(it, warnings)}
          onRemoved={() => {}} />
      )}

      {/* Item list, grouped by section */}
      {activeList.length === 0 ? (
        <div style={{ color: t.textSecondary, textAlign: "center", padding: 32 }}>{q ? `No results for "${managerSearch}"` : statusView ? (statusFilter === "oos" ? "Nothing is out of stock." : statusFilter === "hidden" ? "Nothing is hidden." : "Nothing here.") : priceView ? "Nothing here." : editable ? "No items yet. Add one, or upload a file from Update Menu." : "No items"}</div>
      ) : groups.map(g => (
        <div key={g.name} style={{ marginBottom: showHeadings ? 12 : 0 }}>
          {showHeadings && (canRename && renamingGroup === g.name ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, paddingBottom: 6, borderBottom: `0.5px solid ${t.borderSubtle}` }}>
              <input autoFocus value={renameValue} disabled={renameBusy}
                onChange={e => setRenameValue(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") renameSection(g.name, renameValue); if (e.key === "Escape") setRenamingGroup(null); }}
                aria-label={`New name for ${g.name}`} placeholder="Section name"
                style={{ ...mm.input, padding: "6px 9px", flex: 1, minWidth: 0 }} />
              <button type="button" disabled={renameBusy || !renameValue.trim()} onClick={() => renameSection(g.name, renameValue)} style={mm.primaryBtn(renameBusy || !renameValue.trim())}>{renameBusy ? "Saving…" : "Save"}</button>
              <button type="button" disabled={renameBusy} onClick={() => setRenamingGroup(null)} style={mm.quietBtn}>Cancel</button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: t.accent, fontSize: 12, marginBottom: 6, paddingBottom: 5, borderBottom: `0.5px solid ${t.borderSubtle}` }}>
              <span>{g.name}</span>
              <span style={{ color: t.textDim, fontSize: 10 }}>{g.items.length}</span>
              {canRename && (
                <button type="button" onClick={() => { setRenamingGroup(g.name); setRenameValue(g.name === "Other" ? "" : g.name); }}
                  aria-label={`Rename section ${g.name}`}
                  style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11, padding: "2px 4px", textDecoration: "underline" }}>
                  Rename
                </button>
              )}
              {courseKindControl(g.name)}
            </div>
          ))}
          {g.items.map(renderRow)}
        </div>
      ))}

      {/* Removed items (archived by Remove from menu, or by a menu being deleted) */}
      {editable && (
        <div style={{ marginTop: 18, marginBottom: 12 }}>
          <button type="button" style={mm.quietBtn} onClick={() => setShowArchive(v => !v)}>
            {showArchive ? "Hide removed items" : "Show removed items"}
          </button>
          {showArchive && archive === null && <div style={{ color: t.textDim, fontSize: 12, marginTop: 10 }}>Loading…</div>}
          {showArchive && archive && archive.length === 0 && <div style={{ color: t.textDim, fontSize: 12, marginTop: 10 }}>Nothing removed.</div>}
          {showArchive && archive && archive.map(a => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, background: t.white04, borderRadius: 8, padding: "9px 12px", marginTop: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: t.textSecondary, fontSize: 13 }}>{a.name}</div>
                <div style={{ color: t.textDim, fontSize: 11 }}>{a.archivedAt ? `Removed ${new Date(a.archivedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : "Removed"}{a.archivedReason && a.archivedReason !== "deleted by manager" ? ` (${a.archivedReason})` : ""}</div>
              </div>
              <button type="button" style={mm.quietBtn} onClick={() => restore(a)}>Restore</button>
            </div>
          ))}
        </div>
      )}

      </>)}

      {/* Edit panel */}
      {editingItem && (
        <div style={{ position: "sticky", bottom: 0, background: t.bgSurface, border: `0.5px solid ${t.borderMid}`, borderRadius: "12px 12px 0 0", padding: "16px 16px 20px", marginTop: 8, maxHeight: "85vh", overflowY: "auto", boxShadow: `0 -8px 32px ${t.shadow}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
            <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
              <div style={{ color: t.textSecondary, fontSize: 9, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 2 }}>Menu name</div>
              <div style={{ color: t.textPrimary, fontSize: 13, marginBottom: editingItem.sourceName && editingItem.name !== editingItem.sourceName ? 4 : 0 }}>
                {editingItem.sourceName || editingItem.name}
              </div>
              {editingItem.sourceName && editingItem.name !== editingItem.sourceName && (
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 9, background: t.accentDim, border: `0.5px solid ${t.accentBorder}`, color: t.accent, borderRadius: 4, padding: "1px 5px" }}>AI renamed to</span>
                  <span style={{ color: t.accent, fontSize: 12 }}>{editingItem.name}</span>
                </div>
              )}
              <div style={{ color: t.textSecondary, fontSize: 10, fontFamily: t.fontMono, marginTop: 4 }}>{editingItem.id}</div>
            </div>
            <button onClick={() => { setEditingItem(null); setReEnrichResult(null); }} style={{ background: "none", border: "none", color: t.textSecondary, fontSize: 20, cursor: "pointer", padding: 0, flexShrink: 0 }}>×</button>
          </div>

          <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 8 }}>What guests see</div>
          {isEnrichable && neverEnriched && (
            <div style={{ background: t.white06, border: `0.5px solid ${t.borderMid}`, borderRadius: 6, padding: "8px 10px", marginBottom: 10, color: t.textSecondary, fontSize: 11.5, lineHeight: 1.5 }}>
              SommSync hasn't researched this item yet. Tap ✦ Enrich to fill in the details below, then approve it in AI Verified.
            </div>
          )}
          {isEnrichable && (
            <>
              <label style={labelStyle}>Name</label>
              <input style={inputStyle} value={editFields.correctedName || ""} onChange={e => setEditFields(p => ({ ...p, correctedName: e.target.value }))} />
            </>
          )}
          {editingItem._type === "wine" && (
            <>
              <label style={labelStyle}>Varietal</label>
              <input style={inputStyle} value={editFields.varietal || ""} onChange={e => setEditFields(p => ({ ...p, varietal: e.target.value }))} />
              <label style={labelStyle}>Region</label>
              <input style={inputStyle} value={editFields.region || ""} onChange={e => setEditFields(p => ({ ...p, region: e.target.value }))} />
              <label style={labelStyle}>Reviews & Ratings</label>
              <input style={inputStyle} value={editFields.reviews || ""} onChange={e => setEditFields(p => ({ ...p, reviews: e.target.value }))} />
              <label style={labelStyle}>Vintage</label>
              <select style={inputStyle}
                value={editFields.vintage === null || editFields.vintage === undefined ? "" : String(editFields.vintage)}
                onChange={e => setEditFields(p => ({ ...p, vintage: e.target.value === "" ? null : e.target.value === "NV" ? "NV" : parseInt(e.target.value) }))}>
                <option value="">Not specified</option>
                <option value="NV">NV (Non-Vintage)</option>
                {Array.from({ length: new Date().getFullYear() - 1989 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
              <VintageMismatchNote name={editFields.correctedName} vintage={editFields.vintage}
                onUseYear={y => setEditFields(p => ({ ...p, vintage: y }))} />
            </>
          )}
          {editingItem._type === "beer" && (
            <>
              <label style={labelStyle}>Style</label>
              <input style={inputStyle} value={editFields.style || ""} onChange={e => setEditFields(p => ({ ...p, style: e.target.value }))} />
              <label style={labelStyle}>Brewery</label>
              <input style={inputStyle} value={editFields.brewery || ""} onChange={e => setEditFields(p => ({ ...p, brewery: e.target.value }))} />
              <label style={labelStyle}>ABV</label>
              <input style={inputStyle} value={editFields.abv || ""} onChange={e => setEditFields(p => ({ ...p, abv: e.target.value }))} />
            </>
          )}
          {editingItem._type === "pour" && (
            <>
              <label style={labelStyle}>Category</label>
              <input style={inputStyle} value={editFields.category || ""} onChange={e => setEditFields(p => ({ ...p, category: e.target.value }))} />
              <label style={labelStyle}>Producer</label>
              <input style={inputStyle} value={editFields.producer || ""} onChange={e => setEditFields(p => ({ ...p, producer: e.target.value }))} />
              <label style={labelStyle}>ABV</label>
              <input style={inputStyle} value={editFields.abv || ""} onChange={e => setEditFields(p => ({ ...p, abv: e.target.value }))} />
            </>
          )}
          {isEnrichable && (
            <>
              <label style={labelStyle}>Tasting Notes</label>
              <textarea style={{ ...inputStyle, height: 72, resize: "vertical" }} value={editFields.description || ""} onChange={e => setEditFields(p => ({ ...p, description: e.target.value }))} />
            </>
          )}
          {editingItem._type === "food" && (
            <>
              <label style={labelStyle}>Description</label>
              <textarea style={{ ...inputStyle, height: 72, resize: "vertical" }} value={editFields.description || ""} onChange={e => setEditFields(p => ({ ...p, description: e.target.value }))} />
            </>
          )}
          {/* ── Translations (step 3a) ── Every description exists in all three
              languages: the original plus AI translations the owner can replace.
              Names are never AI-translated; an owner may add one per language. */}
          {trData && (() => {
            const src = trData.srcLang && trData.srcLang !== "other" ? trData.srcLang : null;
            const descLangs = enabledLanguages().filter(l => l.code !== src);
            const needsReview = descLangs.filter(l => { const v = (trData.description || {})[l.code]; return v && v.by === "manager" && v.stale; }).length;
            const touch = (field, lang, value) => {
              setTrDraft(d => ({ ...d, [field]: { ...(d[field] || {}), [lang]: value } }));
              setTrTouched(x => ({ ...x, [`${field}:${lang}`]: true }));
            };
            const srcLabel = src ? (languageEntry(src) || {}).english : null;
            const warnColour = t.warning || t.accent;
            return (
              <details style={{ background: t.white04, border: `0.5px solid ${needsReview ? warnColour : t.borderMid}`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}
                open={needsReview > 0 || undefined}>
                <summary style={{ cursor: "pointer", color: t.textPrimary, fontSize: 13 }}>
                  Translations
                  {needsReview > 0 && <span style={{ color: warnColour, fontSize: 11 }}> · {needsReview} to review</span>}
                </summary>
                <div style={{ marginTop: 10 }}>
                  {trData.original ? (
                    <>
                      <div style={{ color: t.textDim, fontSize: 11, marginBottom: 8, lineHeight: 1.4 }}>
                        {srcLabel ? `The description above is in ${srcLabel}. ` : ""}
                        Guests reading another language see these. Edit any of them to use your own wording; clear one to go back to an automatic translation.
                        {trData.pending ? " The description changed recently — automatic translations update within the hour." : ""}
                      </div>
                      {descLangs.map(l => {
                        const cur = (trData.description || {})[l.code];
                        const draft = (trDraft.description || {})[l.code] || "";
                        const touched = !!trTouched[`description:${l.code}`];
                        const status = touched
                          ? (draft.trim() ? "Your version (unsaved)" : "Automatic translation (after saving)")
                          : !cur ? "Automatic translation on its way"
                          : cur.by === "manager" ? (cur.stale ? "The original changed — check this still matches" : "Your version")
                          : "Automatic translation";
                        const warn = !touched && cur && cur.by === "manager" && cur.stale;
                        return (
                          <div key={l.code} style={{ marginBottom: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                              <label style={labelStyle} lang={l.code}>{l.label}</label>
                              <span style={{ fontSize: 10, color: warn ? warnColour : t.textDim }}>{warn && <LineIcon name="alert" inline />}{status}</span>
                            </div>
                            <textarea lang={l.code} style={{ ...inputStyle, height: 64, resize: "vertical" }}
                              value={draft} placeholder={!cur ? "Will be filled in automatically" : ""}
                              onChange={e => touch("description", l.code, e.target.value)} />
                            <div style={{ display: "flex", gap: 12 }}>
                              {warn && (
                                <button type="button" onClick={() => touch("description", l.code, cur.text)}
                                  style={{ background: "none", border: "none", color: t.accent, fontSize: 11, cursor: "pointer", padding: 0, fontFamily: t.fontSerif, textDecoration: "underline" }}>
                                  Keep my version
                                </button>
                              )}
                              {cur && cur.by === "manager" && !(touched && !draft.trim()) && (
                                <button type="button" onClick={() => touch("description", l.code, "")}
                                  style={{ background: "none", border: "none", color: t.textSecondary, fontSize: 11, cursor: "pointer", padding: 0, fontFamily: t.fontSerif, textDecoration: "underline" }}>
                                  Use an automatic translation instead
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div style={{ color: t.textDim, fontSize: 11, marginBottom: 8 }}>
                      No description yet. Once there is one, it is translated automatically.
                    </div>
                  )}
                  <div style={{ borderTop: `0.5px solid ${t.borderMid}`, paddingTop: 8, marginTop: 4 }}>
                    <div style={{ color: t.textDim, fontSize: 11, marginBottom: 6, lineHeight: 1.4 }}>
                      Name in other languages (optional). Leave blank to show the name as it is in every language.
                    </div>
                    {enabledLanguages().map(l => (
                      <div key={l.code} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ width: 70, flexShrink: 0, color: t.textSecondary, fontSize: 12 }} lang={l.code}>{l.label}</span>
                        <input lang={l.code} style={{ ...inputStyle, marginBottom: 0 }}
                          placeholder={editingItem.name || ""}
                          value={(trDraft.name || {})[l.code] || ""}
                          onChange={e => touch("name", l.code, e.target.value)} />
                      </div>
                    ))}
                  </div>
                </div>
              </details>
            );
          })()}
          {/* Show-to-guests switch, every menu type. A labelled on/off switch rather
              than a button whose text was the current state: managers could not tell
              whether "Visible to guests" described the item or was the thing to tap.
              ON = guests see it. Still only takes effect on Save Changes, like every
              other field here, so the line under it says so while it differs. */}
          {(() => {
            const shown = !editFields.excluded;
            const pending = !!editFields.excluded !== !!editBaseline.excluded;
            const isFood = editingItem._type === "food";
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 12, background: t.white04, border: `0.5px solid ${t.borderMid}`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div id="show-to-guests-label" style={{ color: t.textPrimary, fontSize: 13 }}>{isFood ? "Use in wine pairings" : "Show to guests"}</div>
                  <div style={{ color: shown ? t.textSecondary : t.error, fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>
                    {shown
                      ? (isFood ? "Guests can be offered pairings for this dish." : "Guests see this on the menu and the sommelier can suggest it.")
                      : (isFood ? "Left out of wine pairings (sides, sauces, add-ons)." : "Hidden from the guest menu and the sommelier.")}
                    {pending && <span style={{ color: t.textDim, fontStyle: "italic" }}> Save to apply.</span>}
                  </div>
                </div>
                <button type="button" role="switch" aria-checked={shown} aria-labelledby="show-to-guests-label"
                  onClick={() => setEditFields(p => ({ ...p, excluded: !p.excluded }))}
                  style={{ position: "relative", width: 52, height: 30, flexShrink: 0, borderRadius: 15, border: `0.5px solid ${shown ? t.success : t.borderStrong}`, background: shown ? t.success : t.white08, cursor: "pointer", padding: 0, transition: "background 0.15s, border-color 0.15s" }}>
                  <span style={{ position: "absolute", top: 3, left: shown ? 25 : 3, width: 24, height: 24, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.35)", transition: "left 0.15s" }} />
                </button>
              </div>
            );
          })()}
          {/* Feature in pairings. Wine only, owner only (featuredWines stays null
              for anyone else). Plain-language strengths rather than the 1-8 boost:
              nobody can reason about "5", and all three stay inside FEATURE_BOOST_MAX
              on the server, so no setting here can push an unsuitable wine onto a
              table - the line under the switch says exactly that. */}
          {editingItem._type === "wine" && featuredWines !== null && featureDraft && (() => {
            const on = featureDraft.on;
            const pending = JSON.stringify(featureDraft) !== JSON.stringify(featureBaseline);
            const level = FEATURE_LEVELS.reduce((best, l) => Math.abs(l.boost - featureDraft.boost) < Math.abs(best.boost - featureDraft.boost) ? l : best, FEATURE_LEVELS[0]);
            const today = msToDateInput(Date.now());
            return (
              <div style={{ background: on ? t.accentDimSm : t.white04, border: `0.5px solid ${on ? t.accentBorder : t.borderMid}`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div id="feature-wine-label" style={{ color: t.textPrimary, fontSize: 13 }}>Feature in wine pairings</div>
                    <div style={{ color: t.textSecondary, fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>
                      {on
                        ? "Recommended more often when it suits the dish. Never recommended for a dish it doesn't suit."
                        : "Off. The sommelier recommends on fit and price alone."}
                      {pending && <span style={{ color: t.textDim, fontStyle: "italic" }}> Save to apply.</span>}
                    </div>
                  </div>
                  <button type="button" role="switch" aria-checked={on} aria-labelledby="feature-wine-label"
                    onClick={() => setFeatureDraft(p => ({ ...p, on: !p.on }))}
                    style={{ position: "relative", width: 52, height: 30, flexShrink: 0, borderRadius: 15, border: `0.5px solid ${on ? t.accent : t.borderStrong}`, background: on ? t.accent : t.white08, cursor: "pointer", padding: 0, transition: "background 0.15s, border-color 0.15s" }}>
                    <span style={{ position: "absolute", top: 3, left: on ? 25 : 3, width: 24, height: 24, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.35)", transition: "left 0.15s" }} />
                  </button>
                </div>
                {on && (
                  <div style={{ marginTop: 12, paddingTop: 10, borderTop: `0.5px solid ${t.borderSubtle}` }}>
                    <div style={{ color: t.textSecondary, fontSize: 11, marginBottom: 6 }}>How strongly</div>
                    <div role="radiogroup" aria-label="How strongly to feature this wine" style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      {FEATURE_LEVELS.map(l => {
                        const sel = l.id === level.id;
                        return (
                          <button key={l.id} type="button" role="radio" aria-checked={sel}
                            onClick={() => setFeatureDraft(p => ({ ...p, boost: l.boost }))}
                            style={{ flex: 1, padding: "8px 6px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, background: sel ? t.accent : "none", color: sel ? t.accentText : t.textSecondary, border: `0.5px solid ${sel ? t.accent : t.borderMid}` }}>
                            {l.label}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.4, marginBottom: 12 }}>{level.help}</div>
                    <label style={{ ...labelStyle, marginTop: 0 }} htmlFor="feature-until">Feature until</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input id="feature-until" type="date" min={today} value={featureDraft.until}
                        onChange={e => setFeatureDraft(p => ({ ...p, until: e.target.value }))}
                        style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
                      {featureDraft.until && (
                        <button type="button" onClick={() => setFeatureDraft(p => ({ ...p, until: "" }))}
                          style={{ background: "none", border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, padding: "8px 10px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11, whiteSpace: "nowrap" }}>
                          No end date
                        </button>
                      )}
                    </div>
                    <div style={{ color: t.textDim, fontSize: 11, marginTop: 4 }}>
                      {featureDraft.until ? "Stops on its own after this day." : "Stays featured until you turn it off."}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {/* Prices, sections, 86 and menu assignment. For a category fed by a live
              POS this shows the values locked, with a note that they are changed there. */}
          <YourMenuSection ref={menuRef} key={`${editingItem._cat}:${editingItem.id}`} catKey={editingItem._cat} itemId={editingItem.id} item={editingItem} isAdmin={isAdmin} />
          {/* Re-enrich result message */}
          {reEnrichResult && (
            <div style={{ marginBottom: 10, background: reEnrichResult.ok ? t.successDim : t.errorDim, border: `0.5px solid ${reEnrichResult.ok ? t.success : t.error}`, borderRadius: 6, padding: "8px 12px" }}>
              <div style={{ color: reEnrichResult.ok ? t.success : t.error, fontSize: 11, fontFamily: t.fontSerif }}>{!(reEnrichResult.ok && !reEnrichResult.warn) && <LineIcon name={reEnrichResult.warn ? 'alert' : 'xCircle'} inline />}{reEnrichResult.message}</div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={handleSave} disabled={saving || removing} style={{ flex: 1, background: saving ? t.accentDimSm : t.accent, color: saving ? t.textDim : t.accentText, border: "none", padding: "10px", borderRadius: 6, cursor: saving ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 13, fontWeight: 600 }}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
            {isEnrichable && (
              <button onClick={handleReEnrich} disabled={reEnriching || saving || removing}
                title={neverEnriched ? "Research this item with AI" : "Clear existing AI enrichment and re-run from scratch through Claude"}
                style={{ background: reEnriching ? t.white06 : t.infoDim, border: `0.5px solid ${reEnriching ? t.textMuted : t.info}`, color: reEnriching ? t.textMuted : t.info, padding: "10px 12px", borderRadius: 6, cursor: reEnriching ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 11, whiteSpace: "nowrap" }}>
                {reEnriching ? (neverEnriched ? "Enriching…" : "Re-enriching…") : (neverEnriched ? "✦ Enrich" : "✦ Re-enrich")}
              </button>
            )}
            {editingItem && editingItem._type === "wine" && (
              <button onClick={() => onRequestUpcScan && onRequestUpcScan(editingItem)}
                title="Scan wine barcode to identify and re-enrich"
                style={{ background: t.infoDim, border: `0.5px solid ${t.infoBorder}`, color: t.info, padding: "10px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11, whiteSpace: "nowrap" }}>
                <LineIcon name="camera" inline />Scan
              </button>
            )}
            <button onClick={() => { setEditingItem(null); setReEnrichResult(null); }} style={{ background: "none", border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, padding: "10px 16px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 13 }}>Cancel</button>
          </div>
          {/* Remove archives the item (menuManager deleteItem). Only where the
              restaurant manages this category here; a POS item is removed in the POS. */}
          {!view && catEditable(editingItem._cat) && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, paddingTop: 12, borderTop: `0.5px solid ${t.borderSubtle}` }}>
              <button type="button" onClick={handleRemove} disabled={removing || saving} style={mm.dangerBtn}>{removing ? "Removing…" : "Remove from menu"}</button>
              <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5 }}>Takes it off the menu entirely. Use Hidden instead to keep it here but out of sight; use 86'd for out of stock.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Print Menu Tab ───────────────────────────────────────────────────────────
function PrintMenuTab({ itemCounts = {}, itemHeights = {}, isAdmin = false, wineSubgroups = [] }) {
  const t = useTheme();
  const [menuFormat, setMenuFormat] = useState("full"); // 'full' | 'condensed'
  const [selected, setSelected] = useState({ wine: true, beer: false, pours: false, cocktails: false, nab: false });
  const [pageSize, setPageSize] = useState("letter");
  const [wineMode, setWineMode]           = useState('full');   // 'full' | 'btg_only' | 'both'
  const [wineGrouping, setWineGrouping]   = useState('varietal'); // 'varietal' | 'region' | 'toast'
  // Managers print the same thing most weeks. Open on a one-line summary of the
  // last configuration that actually produced a PDF; expand only to change it.
  const [showOptions, setShowOptions] = useState(null); // null = profile not loaded yet
  const [configLoaded, setConfigLoaded] = useState(false);
  const [lastRun, setLastRun] = useState(null); // { pages, lastPageFillPct } from the previous print
  const [savedAboutUs, setSavedAboutUs] = useState('');  // copy approved on a previous print

  const [catOrder, setCatOrder]           = useState(['wine', 'beer', 'pours', 'cocktails', 'nab']);
  const [includeOutOfStock, setIncludeOutOfStock] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [pdfResult, setPdfResult] = useState(null); // {pdfBase64, pages, lastPageFillPct, pageSize}
  const [downloadedAt, setDownloadedAt] = useState(null); // timestamp of last download for confirmation flash
  const [pdfPhase, setPdfPhase]           = useState(null); // current generation phase for progress display
  const [restaurantAboutUs, setRestaurantAboutUs] = useState(null); // null=loading, ''=none, 'text'=has content
  const [restaurantPlan, setRestaurantPlan] = useState(null); // 'print' | 'base' | 'premium'
  const [restaurantName, setRestaurantName] = useState('');
  const [includeQr, setIncludeQr] = useState(true);

  // Human-readable one-liner for the collapsed view. Reads as a sentence rather
  // than a settings dump — the manager is confirming, not configuring.
  const configSummary = React.useMemo(() => {
    const CAT_LABEL = { wine: 'Wine', beer: 'Beer', pours: 'Spirits', cocktails: 'Cocktails', nab: 'Non-alcoholic' };
    const cats = catOrder.filter(id => selected[id]).map(id => CAT_LABEL[id] || id);
    const parts = [];
    parts.push(menuFormat === 'full' ? 'Full menu' : 'Condensed');
    parts.push(cats.length ? cats.join(', ') : 'no sections selected');
    if (selected.wine) {
      if (wineMode === 'btg_only') parts.push('by the glass only');
      else {
        parts.push(wineGrouping === 'region' ? 'wine by region'
                 : wineGrouping === 'toast'  ? 'wine by menu sections'
                 : 'wine by varietal');
        if (wineMode === 'both') parts.push('with glass list');
      }
    }
    parts.push(pageSize === 'legal' ? 'Legal' : 'Letter');
    if (!includeOutOfStock) parts.push('in-stock only');
    if (includeQr && restaurantPlan !== 'print') parts.push('QR code');
    return parts.join(' · ');
  }, [menuFormat, selected, wineMode, wineGrouping, pageSize, includeOutOfStock, includeQr, restaurantPlan, catOrder]);


  // Toast group names print exactly as written, so show them before the manager commits.
  const toastGroupPreview = React.useMemo(() => {
    if (!wineSubgroups.length) return 'The sections on your wine list';
    const shown = wineSubgroups.slice(0, 4).join(' · ');
    return wineSubgroups.length > 4 ? `${shown} +${wineSubgroups.length - 4} more` : shown;
  }, [wineSubgroups]);

  const [aboutUsPristine, setAboutUsPristine] = useState('');  // last AI draft, for edit detection
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [activeTier, setActiveTier] = useState('0');
  const [aboutUsAdded, setAboutUsAdded]   = useState(false); // true after About Us second pass completes
  const [addingAboutUs, setAddingAboutUs] = useState(false);
  const [aboutUsDraftText, setAboutUsDraftText] = useState(null); // null = not drafted yet
  const [aboutUsCharLimit, setAboutUsCharLimit] = useState(0);
  const [draftingAboutUs, setDraftingAboutUs]   = useState(false);
  const [aboutUsError, setAboutUsError]         = useState(null);

  // Load restaurant profile on mount to check if About Us exists
  useEffect(() => {
    rfetch(GET_PROFILE_URL)
      .then(r => r.json())
      .then(data => {
        setRestaurantAboutUs(data.aboutUs || '');
        setRestaurantPlan(data.plan || 'premium');
        setRestaurantName(data.name || '');
        const c = data.pdfConfig;
        if (c) {
          if (c.format) setMenuFormat(c.format);
          if (Array.isArray(c.categories) && c.categories.length) {
            setSelected({ wine: false, beer: false, pours: false, cocktails: false, nab: false,
              ...Object.fromEntries(c.categories.map(k => [k, true])) });
          }
          if (c.pageSize) setPageSize(String(c.pageSize).toLowerCase() === 'legal' ? 'legal' : 'letter');
          if (c.wineGrouping) setWineGrouping(c.wineGrouping);
          setWineMode(c.btgOnly ? 'btg_only' : c.includeBtg ? 'both' : 'full');
          setIncludeOutOfStock(c.includeOutOfStock !== false);
          setIncludeQr(c.includeQr !== false);
          if (c.pages != null) setLastRun({ pages: c.pages, lastPageFillPct: c.lastPageFillPct ?? null });
          if (c.aboutUsText) setSavedAboutUs(c.aboutUsText);
          setShowOptions(false);   // collapse — they've printed before
        } else {
          setShowOptions(true);    // first print for this restaurant
        }
        setConfigLoaded(true);
      })
      .catch(() => { setRestaurantAboutUs(''); setRestaurantPlan('premium'); setShowOptions(true); setConfigLoaded(true); });
  }, []);

  // Poll pdfProgress while generating so the manager sees live phase labels
  useEffect(() => {
    if (!generating && !addingAboutUs) { setPdfPhase(null); return; }
    const iv = setInterval(() => {
      rfetch(GET_PDF_PROGRESS_URL).then(r => r.json()).then(d => setPdfPhase(d.phase || null)).catch(() => {});
    }, 1500);
    return () => clearInterval(iv);
  }, [generating, addingAboutUs]);

  const CATS = [
    { id: "wine",      label: "Wine List"                },
    { id: "beer",      label: "Beer List"                },
    { id: "pours",     label: "Premium Pours"            },
    { id: "cocktails", label: "Specialty Cocktails"      },
    { id: "nab",       label: "Non-Alcoholic Beverages"  },
  ];

  function moveCat(id, dir) {
    setCatOrder(prev => {
      const i = prev.indexOf(id), j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  // Fixed keys: selected uses 'pours'/'cocktails', itemCounts uses 'pour'/'cocktail'
  const wineCount      = selected.wine      ? (itemCounts.wine     || 0) : 0;
  const beerCount      = selected.beer      ? (itemCounts.beer     || 0) : 0;
  const poursCount     = selected.pours     ? (itemCounts.pour     || 0) : 0;
  const cocktailsCount = selected.cocktails ? (itemCounts.cocktail || 0) : 0;
  const nabCount       = selected.nab       ? (itemCounts.nab      || 0) : 0;
  // BTG adds an extra listing for each glass-priced wine (exact count from itemCounts)
  const btgCount       = (menuFormat === 'condensed' && selected.wine && wineMode === 'both')
                          ? (itemCounts.wineGlass || 0) : 0;
  const btgOnlyCount   = (menuFormat === 'condensed' && selected.wine && wineMode === 'btg_only')
                          ? (itemCounts.wineGlass || 0) : 0;
  const selectedItemCount = wineCount + beerCount + poursCount + cocktailsCount + nabCount;

  // ── Page estimation for condensed format ────────────────────────────────
  // Content height per page (two columns combined, after header + footer margins)
  const colH   = pageSize === 'legal' ? 882 : 666; // pt per column
  const pageH  = colH * 2;                          // pt of content per full page

  // Per-item height estimates (average, accounting for wrapping + description length)
  // Per-section heading overhead (section title + expected subgroup headings)
  function estimateCondensedPages() {
    let h = 0;
    if (selected.wine) {
      if (wineMode === 'btg_only') {
        h += 18 + btgOnlyCount * 14; // flat list, no varietal-group overhead
      } else if (wineMode === 'both') {
        h += itemHeights.wine || (22 + 36 + 10*15 + wineCount * 16); // full varietal list
        h += itemHeights.wineGlass || (18 + btgCount * 14);           // BTG section at top
      } else {
        h += itemHeights.wine || (22 + 36 + 10*15 + wineCount * 16); // fallback: 10 varietals avg
      }
    }
    if (selected.beer)      h += itemHeights.beer      || (82  + beerCount      * 22);
    if (selected.pours)     h += itemHeights.pours     || (157 + poursCount     * 26);
    if (selected.cocktails) h += itemHeights.cocktails || (52  + cocktailsCount * 22);
    if (selected.nab)       h += itemHeights.nab       || (37  + nabCount       * 16);
    // Inter-section spacing
    const sectionCount = [selected.wine, selected.beer, selected.pours, selected.cocktails, selected.nab].filter(Boolean).length;
    if (sectionCount > 1) h += (sectionCount - 1) * 16;
    const totalPages  = h / pageH;
    const fullPages   = Math.floor(totalPages);
    const lastPagePct = Math.round((totalPages - fullPages) * 100);
    return {
      pages:       Math.ceil(totalPages) || 1,
      lastPagePct: lastPagePct === 0 ? 100 : lastPagePct,
    };
  }
  const pageEst = menuFormat === 'condensed' ? estimateCondensedPages() : null;

  const PHASE_LABELS = {
    syncing:      'Syncing with Toast…',
    generating:   'Generating PDF…',
    analyzing:    'Analyzing layout…',
    condensing:   'Writing About Us…',
    regenerating: 'Regenerating PDF…',
    fitting:      'Trying to fit two pages…',
  };
  const progressLabel = pdfPhase ? (PHASE_LABELS[pdfPhase] || 'Working…') : 'Syncing & Generating PDF…';

  // reuse=true loads the copy approved on a previous print and asks only for the
  // space budget, so a manager who hand-edited last time is not made to redo it.
  async function handleDraftAboutUs(reuse = false) {
    setDraftingAboutUs(true); setAboutUsError(null);
    try {
      const res = await rfetch(DRAFT_ABOUT_US_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prevFillPct: pdfResult.lastPageFillPct, pageSize, format: menuFormat, budgetOnly: reuse }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not draft the About Us section.");
      const text = reuse ? savedAboutUs : data.text;
      if (!text) throw new Error("Could not draft the About Us section.");
      setAboutUsDraftText(text);
      setAboutUsPristine(text);
      setAboutUsCharLimit(data.charLimit || 0);
    } catch (e) { setAboutUsError(e.message); }
    setDraftingAboutUs(false);
  }

  async function handleAddAboutUs() {
    const categories = catOrder.filter(id => selected[id]);
    setAddingAboutUs(true); setPdfPhase('regenerating'); setAboutUsError(null);
    try {
      const res = await rfetch(GENERATE_PDF_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          categories, pageSize, format: menuFormat,
          includeBtg: selected.wine && wineMode === 'both',
          btgOnly:    selected.wine && wineMode === 'btg_only',
          includeOutOfStock,
          includeQr,
          addAboutUs: true,
          wineGrouping,
          prevFillPct: pdfResult.lastPageFillPct,
          aboutUsText: aboutUsDraftText,
          format: menuFormat,
          tier: activeTier,
          pageSize: pdfResult.pageSize === 'Legal' ? 'legal' : 'letter',
          prevPages: pdfResult.pages,
        }),
      });
      if (!res.ok) throw new Error("PDF regeneration failed.");
      const data = await res.json();

      // The server measured the result. If the block pushed the menu onto another
      // page, that PDF is worse than no About Us at all — so it is discarded, the
      // previous result stands, and the editor reopens with a tighter limit and the
      // reason. Nothing spilled is ever presented as finished.
      if (data.spilled) {
        setAboutUsCharLimit(data.suggestedCharLimit || Math.round(aboutUsDraftText.length * 0.85));
        // Trimming is one remedy; more room is the other, and on a menu that was
        // already close to a page boundary it is usually the better one.
        setAboutUsError(
          `That copy ran onto a ${data.pages}-page menu — about ${data.spilledChars} characters more than the space holds. ` +
          `Your previous PDF is unchanged. Either trim to roughly ${data.suggestedCharLimit} characters, ` +
          `or use Change & Regenerate below to make room${pdfResult.pageSize !== 'Legal' ? ' — switching to Legal paper is usually enough' : ''}.`
        );
        setAddingAboutUs(false);
        setPdfPhase(null);
        return;
      }

      setPdfResult(prev => ({ ...prev, pdfBase64: data.pdf, pages: data.pages, lastPageFillPct: data.lastPageFillPct, pageSize: data.pageSize }));
      setAboutUsAdded(true);
      setAboutUsDraftText(null);
    } catch (e) { setAboutUsError(e.message); }
    setAddingAboutUs(false); setPdfPhase(null);
  }

  async function handleDownload() {
    const categories = catOrder.filter(id => selected[id]);
    if (!categories.length) { setError("Select at least one section."); return; }
    setGenerating(true); setError(null); setSuccess(false);
    try {
      const res = await rfetch(GENERATE_PDF_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          categories,
          pageSize,
          format: menuFormat,
          includeBtg: selected.wine && wineMode === 'both',
          btgOnly:    selected.wine && wineMode === 'btg_only',
          includeOutOfStock,
          includeQr,
          wineGrouping,
          tier: '0',
        }),
      });
      if (!res.ok) {
        let errMsg = "PDF generation failed";
        try { const d = await res.json(); errMsg = d.error || errMsg; } catch (_) { try { errMsg = await res.text() || errMsg; } catch (__) {} }
        throw new Error(errMsg);
      }
      // Both formats now return JSON {pdf, pages, lastPageFillPct, pageSize}.
      // Manager sees measured page count and fill before deciding to download,
      // or to close the menu with an About Us block.
      const data = await res.json();
      setActiveTier('0');
      // Capture the delta before overwriting, then treat this run as the new baseline.
      setLastRun(prev => (data.pages != null ? { pages: data.pages, lastPageFillPct: data.lastPageFillPct ?? null, prev } : prev));
      setPdfResult({
        pdfBase64:      data.pdf,
        pages:          data.pages,
        lastPageFillPct: data.lastPageFillPct,
        pageSize:       data.pageSize,
        targetPages:    data.targetPages || 2,
      });
    } catch (e) { setError(e.message); }
    setGenerating(false);
  }

  return (
    <div>
      {/* Configuration panel — locked while a result is showing to prevent confusion.
          Manager must click "Change & Regenerate" to unlock and make new selections. */}
      <div style={{ position: "relative" }}>
        {pdfResult && (
          <div style={{ position: "absolute", inset: 0, zIndex: 10, borderRadius: 8, cursor: "not-allowed" }}
               onClick={e => e.stopPropagation()} />
        )}
        <div style={{ opacity: pdfResult ? 0.4 : 1, pointerEvents: pdfResult ? "none" : "auto", transition: "opacity 0.2s" }}>

      {/* One-line summary of the current configuration. Most weeks the manager
          prints exactly what they printed last week, so the default view is this
          plus a Generate button — everything else stays behind "Change options". */}
      {showOptions === null && (
        <div style={{ background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 8, padding: "12px 14px", marginBottom: 14, minHeight: 58 }}>
          <div style={{ color: t.textDim, fontSize: 12, fontStyle: "italic" }}>Loading your last setup…</div>
        </div>
      )}

      {showOptions === false && (
        <div style={{ background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ color: t.textPrimary, fontSize: 12.5, lineHeight: 1.6 }}>{configSummary}</div>
          <button onClick={() => setShowOptions(true)}
            style={{ marginTop: 8, background: "transparent", border: "none", padding: 0, cursor: "pointer",
                     color: t.accent, fontFamily: t.fontSerif, fontSize: 11, textDecoration: "underline" }}>
            Change options
          </button>
        </div>
      )}

      {showOptions === true && (<>
      {/* Format toggle */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ color: t.textMuted, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>Menu Format</div>
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { id: "full",      label: "Full Beverage Menu",    sub: "Full listing with region notes · ideal for menu binders" },
            { id: "condensed", label: "Condensed Format",      sub: "2-column, compact, choose sections" },
          ].map(f => (
            <button key={f.id} onClick={() => setMenuFormat(f.id)} style={{
              flex: 1, padding: "10px 10px", borderRadius: 8, textAlign: "center",
              border: `1.5px solid ${menuFormat === f.id ? t.accent : t.borderStrong}`,
              background: menuFormat === f.id ? t.accentDim : t.white04,
              color: t.textPrimary, fontFamily: t.fontSerif, fontSize: 12, cursor: "pointer",
            }}>
              <div style={{ fontWeight: menuFormat === f.id ? 600 : 400, marginBottom: 2 }}>{f.label}</div>
              <div style={{ fontSize: 10, color: t.textMuted }}>{f.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Section selection with print-order control */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ color: t.textMuted, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4 }}>Include Sections</div>
        <div style={{ color: t.textDim, fontSize: 10, marginBottom: 8 }}>Use ▲▼ to set print order. Check to include.</div>
        {catOrder.map((catId, idx) => {
          const cat = CATS.find(c => c.id === catId);
          const isFirst = idx === 0, isLast = idx === catOrder.length - 1;
          return (
            <div key={catId}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0", borderBottom: `0.5px solid ${t.borderSubtle}` }}>
                {/* Order buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 0, flexShrink: 0, width: 18 }}>
                  <button onClick={() => moveCat(catId, -1)} disabled={isFirst}
                    style={{ background: "none", border: "none", cursor: isFirst ? "default" : "pointer", color: isFirst ? t.borderMid : t.textMuted, fontSize: 8, padding: "1px 0", lineHeight: 1, textAlign: "center" }}>▲</button>
                  <button onClick={() => moveCat(catId, 1)} disabled={isLast}
                    style={{ background: "none", border: "none", cursor: isLast ? "default" : "pointer", color: isLast ? t.borderMid : t.textMuted, fontSize: 8, padding: "1px 0", lineHeight: 1, textAlign: "center" }}>▼</button>
                </div>
                {/* Checkbox + label */}
                <div onClick={() => setSelected(p => ({ ...p, [catId]: !p[catId] }))}
                  style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, cursor: "pointer" }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${selected[catId] ? t.success : t.borderMid}`, background: selected[catId] ? t.successDim : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {selected[catId] && <span style={{ color: t.success, fontSize: 11, lineHeight: 1 }}>✓</span>}
                  </div>
                  <div style={{ color: selected[catId] ? t.textPrimary : t.textMuted, fontSize: 13 }}>{cat.label}</div>
                </div>
              </div>
              {/* Wine mode radios — inline under Wine row. Both formats support all
                  three modes; the full menu renders them with its own layout. */}
              {catId === 'wine' && selected.wine && (
                <div style={{ marginLeft: 28, paddingTop: 6, paddingBottom: 4 }}>
                  {[
                    { id: 'full',     label: 'Full wine list by bottle', sub: 'Organized by varietal group · no by-the-glass section' },
                    { id: 'btg_only', label: 'By the glass only',        sub: 'Flat list · shows glass & bottle prices' },
                    { id: 'both',     label: 'Both',                     sub: 'By the glass list at top, then full varietal groups' },
                  ].map(mode => (
                    <div key={mode.id} onClick={() => setWineMode(mode.id)}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer" }}>
                      <div style={{ width: 14, height: 14, borderRadius: "50%", border: `1.5px solid ${wineMode === mode.id ? t.accent : t.borderMid}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {wineMode === mode.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: t.accent }} />}
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: t.textPrimary }}>{mode.label}</div>
                        <div style={{ fontSize: 10, color: t.textDim, marginTop: 1 }}>{mode.sub}</div>
                      </div>
                    </div>
                  ))}

                  {/* Wine grouping. Beer, spirits, cocktails and NAB already print
                      their Toast subgroup headings, so "Toast structure" makes wine
                      consistent with the rest of the menu rather than special. */}
                  {wineMode !== 'btg_only' && (
                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `0.5px solid ${t.borderSubtle}` }}>
                      <div style={{ color: t.textMuted, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 }}>
                        Group wines by
                      </div>
                      {[
                        { id: 'varietal', label: 'Varietal',        sub: 'Sparkling, whites by grape, rosé, reds by grape, dessert' },
                        { id: 'region',   label: 'Region',          sub: 'By state or country · small regions grouped together' },
                        { id: 'toast',    label: 'Your menu sections', sub: toastGroupPreview },
                      ].map(g => (
                        <div key={g.id} onClick={() => setWineGrouping(g.id)}
                          style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer" }}>
                          <div style={{ width: 14, height: 14, borderRadius: "50%", border: `1.5px solid ${wineGrouping === g.id ? t.accent : t.borderMid}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {wineGrouping === g.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: t.accent }} />}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, color: t.textPrimary }}>{g.label}</div>
                            <div style={{ fontSize: 10, color: t.textDim, marginTop: 1, lineHeight: 1.4 }}>{g.sub}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {menuFormat === "condensed" && (
        <div style={{ background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 8, padding: "10px 14px", marginBottom: 18 }}>
          <div style={{ color: t.textSecondary, fontSize: 11, lineHeight: 1.6, marginBottom: 10 }}>
            2-column compact format. Wine is organized by varietal group. Beer, Spirits, Cocktails, and NAB use subgroup headings.
          </div>

          {/* Item counts */}
          {selectedItemCount > 0 && (
            <div style={{ color: t.textMuted, fontSize: 10, lineHeight: 1.8, marginBottom: 8, borderTop: `0.5px solid ${t.borderSubtle}`, paddingTop: 8 }}>
              {[
                selected.wine && wineMode === 'btg_only'
                  ? (btgOnlyCount > 0 && `${btgOnlyCount} wine${btgOnlyCount !== 1 ? "s" : ""} by the glass`)
                  : (wineCount > 0 && `${wineCount} wine${wineCount !== 1 ? "s" : ""}`),
                btgCount > 0 && `${btgCount} by-the-glass`,
                beerCount      > 0 && `${beerCount} beer${beerCount !== 1 ? "s" : ""}`,
                poursCount     > 0 && `${poursCount} spirit${poursCount !== 1 ? "s" : ""}`,
                cocktailsCount > 0 && `${cocktailsCount} cocktail${cocktailsCount !== 1 ? "s" : ""}`,
                nabCount       > 0 && `${nabCount} non-alcoholic`,
              ].filter(Boolean).join(" · ")}
            </div>
          )}



          {/* Manager guidance */}
          <div style={{ color: t.textDim, fontSize: 10, lineHeight: 1.6, borderTop: `0.5px solid ${t.borderSubtle}`, paddingTop: 8 }}>
            Generate a first pass to see how many pages your menu produces and how much blank space remains on the last page. Adjust which sections to include and switch between Letter and Legal paper sizes, then regenerate until you get a layout that meets your needs. To start a new section at the top of a page, generate it as its own separate PDF.
          </div>
        </div>
      )}

      {/* Out of stock option */}
      <div style={{ marginBottom: 18 }}>
        <div onClick={() => setIncludeOutOfStock(p => !p)}
          style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <div style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${includeOutOfStock ? t.success : t.borderMid}`, background: includeOutOfStock ? t.successDim : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {includeOutOfStock && <span style={{ color: t.success, fontSize: 11, lineHeight: 1 }}>✓</span>}
          </div>
          <div>
            <div style={{ color: t.textPrimary, fontSize: 13 }}>Show full menu including temporarily unavailable items</div>
            <div style={{ color: t.textDim, fontSize: 10, marginTop: 1 }}>Guests see everything normally on offer — unavailable items are noted so guests know to ask or come back</div>
          </div>
        </div>
      </div>

      {/* QR code option — hidden entirely on the print-only plan, where there is
          no guest-facing digital menu for the code to point at. */}
      {restaurantPlan && restaurantPlan !== 'print' && (
        <div style={{ marginBottom: 18 }}>
          <div onClick={() => setIncludeQr(p => !p)}
            style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <div style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${includeQr ? t.success : t.borderMid}`, background: includeQr ? t.successDim : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {includeQr && <span style={{ color: t.success, fontSize: 11, lineHeight: 1 }}>✓</span>}
            </div>
            <div>
              <div style={{ color: t.textPrimary, fontSize: 13 }}>Include QR code in the header</div>
              <div style={{ color: t.textDim, fontSize: 10, marginTop: 1 }}>Links to your live digital menu. Turn off for a plain printed list with no digital prompt.</div>
            </div>
          </div>
        </div>
      )}

      {/* Page size */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ color: t.textMuted, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>Page Size</div>
        <div style={{ display: "flex", gap: 8 }}>
          {[{ id: "letter", label: "Letter", sub: '8.5" × 11"' }, { id: "legal", label: "Legal", sub: '8.5" × 14"' }].map(s => (
            <button key={s.id} onClick={() => setPageSize(s.id)} style={{
              flex: 1, padding: "10px 8px", borderRadius: 8,
              border: `1.5px solid ${pageSize === s.id ? t.accent : t.borderStrong}`,
              background: pageSize === s.id ? t.accentDim : t.white04,
              color: t.textPrimary, fontFamily: t.fontSerif, fontSize: 12, cursor: "pointer", textAlign: "center",
            }}>
              <div style={{ fontWeight: pageSize === s.id ? 600 : 400 }}>{s.label}</div>
              <div style={{ fontSize: 10, color: t.textMuted, marginTop: 2 }}>{s.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {configLoaded && (
        <button onClick={() => setShowOptions(false)}
          style={{ width: "100%", background: "transparent", border: `0.5px solid ${t.borderSubtle}`,
                   color: t.textDim, padding: "8px", borderRadius: 8, cursor: "pointer",
                   fontFamily: t.fontSerif, fontSize: 11, marginBottom: 18 }}>
          Hide options
        </button>
      )}
      </>)}

      {error && (
        <div style={{ background: t.errorDim, border: `0.5px solid ${t.errorBorder}`, borderRadius: 6, padding: "7px 12px", marginBottom: 12 }}>
          <div style={{ color: t.error, fontSize: 11 }}>{error}</div>
        </div>
      )}
      </div>{/* end opacity wrapper */}
      </div>{/* end position:relative wrapper */}

      {/* Result panel — shown after generation, manager sees stats before downloading */}
      {pdfResult && (
        <div style={{ background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: t.success, marginBottom: 6 }}>
            ✓ PDF ready · {pdfResult.pages ? `${pdfResult.pages} page${pdfResult.pages !== 1 ? "s" : ""} on ${(pdfResult.pageSize||"").toUpperCase() === "LEGAL" ? "Legal" : "Letter"}` : "page count unavailable"}
            {pdfResult.lastPageFillPct != null && <span style={{ color: t.textMuted, fontWeight: 400 }}> · last page {pdfResult.lastPageFillPct}% full</span>}
          </div>

          {/* About Us prompt — shown when last page has significant blank space and About Us text exists */}
          {!aboutUsAdded && !addingAboutUs && pdfResult.lastPageFillPct != null && pdfResult.lastPageFillPct < 75 && restaurantAboutUs && (
            <div style={{ background: t.accentDim, border: `0.5px solid ${t.accent}`, borderRadius: 6, padding: "10px 12px", marginBottom: 10 }}>

              {aboutUsDraftText === null ? (<>
                <div style={{ fontSize: 11, color: t.textPrimary, marginBottom: 8, lineHeight: 1.5 }}>
                  The last page is <strong>{100 - pdfResult.lastPageFillPct}% blank</strong>. Close the menu with an About Us section?
                </div>
                {/* Copy approved on a previous print leads. It may have been rewritten
                    by hand, and regenerating it by default would discard that work. */}
                {savedAboutUs ? (<>
                  <button onClick={() => handleDraftAboutUs(true)} disabled={draftingAboutUs} style={{
                    width: "100%", background: t.accent, color: t.accentText, border: "none",
                    padding: "8px", borderRadius: 6, cursor: draftingAboutUs ? "default" : "pointer",
                    fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600, opacity: draftingAboutUs ? 0.6 : 1,
                  }}>
                    {draftingAboutUs ? "Loading…" : "✓ Use last time's About Us"}
                  </button>
                  <button onClick={() => handleDraftAboutUs(false)} disabled={draftingAboutUs} style={{
                    width: "100%", marginTop: 6, background: "transparent",
                    border: `0.5px solid ${t.borderMid}`, color: t.textDim,
                    padding: "7px", borderRadius: 6, cursor: "pointer",
                    fontFamily: t.fontSerif, fontSize: 11,
                  }}>
                    <LineIcon name="sparkle" inline />Write a fresh one instead
                  </button>
                </>) : (
                  <button onClick={() => handleDraftAboutUs(false)} disabled={draftingAboutUs} style={{
                    width: "100%", background: t.accent, color: t.accentText, border: "none",
                    padding: "8px", borderRadius: 6, cursor: draftingAboutUs ? "default" : "pointer",
                    fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600, opacity: draftingAboutUs ? 0.6 : 1,
                  }}>
                    {draftingAboutUs ? "Writing…" : <><LineIcon name="sparkle" inline />Draft About Us section</>}
                  </button>
                )}
              </>) : (<>
                <div style={{ fontSize: 11, color: t.textPrimary, marginBottom: 6, lineHeight: 1.5 }}>
                  Review before it goes to print — edit anything you like.
                </div>
                <textarea
                  value={aboutUsDraftText}
                  onChange={e => setAboutUsDraftText(e.target.value)}
                  style={{
                    width: "100%", minHeight: 110, resize: "vertical", lineHeight: 1.5,
                    background: t.white04, border: `0.5px solid ${t.borderStrong}`, borderRadius: 6,
                    color: t.textPrimary, fontFamily: t.fontSerif, fontSize: 12, padding: "8px 10px",
                  }} />
                <div style={{
                  fontSize: 10, marginTop: 4, marginBottom: 8,
                  color: aboutUsDraftText.length > aboutUsCharLimit ? t.warning : t.textDim,
                }}>
                  {aboutUsDraftText.length} / {aboutUsCharLimit} characters
                  {aboutUsDraftText.length > aboutUsCharLimit && " · longer than the available space — the box may push to a new page"}
                </div>
                {aboutUsDraftText.length > aboutUsCharLimit && aboutUsCharLimit > 0 && (
                  <button
                    onClick={() => {
                      // Cut at the last sentence boundary inside the limit — never
                      // mid-sentence, which is worse than being slightly long.
                      const slice = aboutUsDraftText.slice(0, aboutUsCharLimit);
                      const cut = Math.max(slice.lastIndexOf('. '), slice.lastIndexOf('! '), slice.lastIndexOf('? '));
                      setAboutUsDraftText(cut > 80 ? slice.slice(0, cut + 1) : slice.trim());
                    }}
                    style={{
                      width: "100%", marginBottom: 8, background: t.white04,
                      border: `0.5px solid ${t.borderStrong}`, color: t.textSecondary,
                      padding: "7px", borderRadius: 6, cursor: "pointer",
                      fontFamily: t.fontSerif, fontSize: 11,
                    }}>
                    ✂ Trim to fit ({aboutUsCharLimit} characters, cut at a sentence end)
                  </button>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={handleAddAboutUs} disabled={!aboutUsDraftText.trim()} style={{
                    flex: 1, background: t.accent, color: t.accentText, border: "none",
                    padding: "8px", borderRadius: 6, cursor: aboutUsDraftText.trim() ? "pointer" : "default",
                    fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600, opacity: aboutUsDraftText.trim() ? 1 : 0.5,
                  }}>
                    ✓ Add to Menu
                  </button>
                  <button
                    onClick={() => {
                      if (aboutUsDraftText !== aboutUsPristine &&
                          !window.confirm("Rewrite from scratch? Your edits to this text will be lost.")) return;
                      handleDraftAboutUs(false);
                    }}
                    disabled={draftingAboutUs} style={{
                    background: t.white04, color: t.textMuted, border: `0.5px solid ${t.borderStrong}`,
                    padding: "8px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11,
                  }}>
                    {draftingAboutUs ? "…" : "↻ Rewrite"}
                  </button>
                  <button onClick={() => { setAboutUsDraftText(null); setAboutUsError(null); }} style={{
                    background: "transparent", color: t.textDim, border: `0.5px solid ${t.borderSubtle}`,
                    padding: "8px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11,
                  }}>
                    Cancel
                  </button>
                </div>
              </>)}

              {aboutUsError && (
                <div style={{ color: t.warning, fontSize: 10, marginTop: 8, lineHeight: 1.4 }}>{aboutUsError}</div>
              )}
            </div>
          )}
          {addingAboutUs && (
            <div style={{ color: t.textMuted, fontSize: 11, marginBottom: 10, padding: "8px 0", fontStyle: "italic" }}>
              {PHASE_LABELS[pdfPhase] || 'Working…'}
            </div>
          )}

          {/* Settings are remembered, so a manager can print for weeks without opening
              the options. The menu underneath changes even when the settings do not —
              this is the only signal that the list itself moved. */}
          {lastRun && lastRun.prev && lastRun.prev.pages != null && pdfResult.pages != null &&
           pdfResult.pages !== lastRun.prev.pages && !aboutUsAdded && (
            <div style={{ background: t.accentDim, border: `0.5px solid ${t.accent}`, borderRadius: 6, padding: "9px 11px", marginBottom: 10 }}>
              <div style={{ color: t.textPrimary, fontSize: 11, lineHeight: 1.5 }}>
                This is <strong>{pdfResult.pages} page{pdfResult.pages === 1 ? "" : "s"}</strong> — last time these
                settings produced {lastRun.prev.pages}. Your menu has changed since then.
              </div>
            </div>
          )}

          {/* ── Two-page fitting ────────────────────────────────────────────────
              A condensed menu is meant to be one duplex sheet. Every option below
              maps to a control the manager already has, so nothing is pre-rendered:
              they unlock the configuration and generate again. Page counts cannot
              be predicted anyway - only measured. */}
          {menuFormat === 'condensed' && !aboutUsAdded && !addingAboutUs &&
           pdfResult.pages > (pdfResult.targetPages || 2) && (
            <div style={{ background: t.white04, border: `0.5px solid ${t.borderMid}`, borderRadius: 6, padding: "10px 12px", marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: t.textPrimary, lineHeight: 1.5, marginBottom: 8 }}>
                This runs to <strong>{pdfResult.pages} pages</strong>. A condensed menu usually works best as 2 pages &mdash; a single sheet printed front and back.
              </div>
              <div style={{ color: t.textDim, fontSize: 10, lineHeight: 1.7 }}>
                Click &ldquo;Change &amp; Regenerate&rdquo; below to try any of these:
                {pdfResult.pageSize !== 'Legal' && (
                  <div>· Switch to <strong>Legal paper</strong> — about a third more room per page, and nothing else changes</div>
                )}
                {includeOutOfStock && (
                  <div>· Uncheck <strong>temporarily unavailable items</strong> to drop everything currently 86'd</div>
                )}
                {selected.wine && wineMode !== 'btg_only' && (
                  <div>· Switch wine to <strong>by the glass only</strong></div>
                )}
                <div>· Leave out a section, or print wine and the rest of the bar as <strong>two separate menus</strong></div>
              </div>
            </div>
          )}

          {aboutUsAdded && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ color: t.success, fontSize: 11 }}>✓ About Us section added</div>
              <div style={{ color: t.textSecondary, fontSize: 10, marginTop: 3, lineHeight: 1.4 }}>
                Download before changing anything — regenerating writes a fresh About Us and any edits you made here are lost.
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            {/* Download is blocked while the About Us pass is running. pdfResult still
                holds the PRE-About-Us PDF until the server responds, so an early click
                would silently hand the manager the version they just asked to change. */}
            <button disabled={addingAboutUs} onClick={() => {
              if (addingAboutUs) return;
              const binary = atob(pdfResult.pdfBase64);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
              const blob = new Blob([bytes], { type: "application/pdf" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              // Both formats share this download path now, so the name has to follow
              // menuFormat. The restaurant slug came free when the full menu streamed
              // its own response; it is rebuilt here so downloads stay identifiable.
              const slug = (restaurantName || 'beverage').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
              const kind = menuFormat === 'full' ? 'full-menu' : 'condensed-menu';
              a.download = `${slug}-${kind}-${new Date().toISOString().slice(0,10)}.pdf`;
              document.body.appendChild(a); a.click();
              document.body.removeChild(a); URL.revokeObjectURL(url);
              setDownloadedAt(Date.now());
              setTimeout(() => setDownloadedAt(null), 3000);
            }} style={{
              flex: 1,
              background: addingAboutUs ? t.white04 : t.accentDim,
              border: `0.5px solid ${addingAboutUs ? t.borderMid : t.accent}`,
              color: addingAboutUs ? t.textDim : t.accent,
              padding: "10px", borderRadius: 8,
              cursor: addingAboutUs ? "default" : "pointer",
              fontFamily: t.fontSerif, fontSize: 13, fontWeight: 600,
              opacity: addingAboutUs ? 0.55 : 1,
            }}>
              {addingAboutUs ? "Adding About Us…" : <><LineIcon name="download" inline />Download PDF</>}
            </button>
            {downloadedAt && (
              <div style={{ width: "100%", marginTop: 6, padding: "6px 10px", background: t.successDim, border: `0.5px solid ${t.success}`, borderRadius: 6, color: t.success, fontSize: 11, textAlign: "center" }}>
                ✓ Saved to your downloads
              </div>
            )}
            <button
              onClick={() => {
                // Regenerating discards the About Us section and any edits made to it.
                // Only worth confirming when there is actually work to lose.
                if (addingAboutUs) return;
                const hasAboutUsWork = aboutUsAdded || aboutUsDraftText !== null;
                if (hasAboutUsWork && !confirmRegen) { setConfirmRegen(true); return; }
                setPdfResult(null); setError(null); setSuccess(false); setDownloadedAt(null);
                setAboutUsAdded(false); setAboutUsDraftText(null); setAboutUsPristine('');
                setAboutUsError(null); setConfirmRegen(false);
              }}
              disabled={addingAboutUs}
              style={{
                padding: "10px 14px",
                background: t.white04,
                border: `0.5px solid ${confirmRegen ? t.warning : t.borderMid}`,
                color: confirmRegen ? t.warning : t.textSecondary,
                borderRadius: 8,
                cursor: addingAboutUs ? "default" : "pointer",
                fontSize: 12,
                opacity: addingAboutUs ? 0.55 : 1,
              }}>
              {confirmRegen ? "Discard About Us and start over?" : <><LineIcon name="undo" inline />Change & Regenerate</>}
            </button>
            {confirmRegen && (
              <button onClick={() => setConfirmRegen(false)}
                style={{ padding: "10px 14px", background: "transparent", border: `0.5px solid ${t.borderSubtle}`, color: t.textDim, borderRadius: 8, cursor: "pointer", fontSize: 12 }}>
                Keep it
              </button>
            )}
          </div>
        </div>
      )}

      {!pdfResult && (
        // Disabled until the saved configuration resolves — generating a second
        // earlier would silently print the defaults instead of their setup.
        <button onClick={handleDownload} disabled={generating || showOptions === null}
          style={{ width: "100%", background: generating ? t.accentDimSm : t.accentDim, border: `0.5px solid ${t.accent}`, color: t.accent, padding: "12px", borderRadius: 8, cursor: (generating || showOptions === null) ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 13, fontWeight: 600, opacity: showOptions === null ? 0.5 : 1 }}>
          {generating ? progressLabel : "⟳ Generate PDF"}
        </button>
      )}

      <div style={{ color: t.textSecondary, fontSize: 10, marginTop: 10, fontStyle: "italic", lineHeight: 1.5 }}>
        {includeQr && restaurantPlan !== 'print' && (
          <>The QR code always points to your live digital menu, so it stays current after you print — price and vintage changes show up there without a reprint. </>
        )}
        {includeOutOfStock
          ? <>This PDF prints every item you've approved in Review, with anything currently out of stock marked as temporarily unavailable.</>
          : <>This PDF prints only items you've approved in Review that are currently in stock.</>}
      </div>
    </div>
  );
}

// ─── UPC Scan Modal ───────────────────────────────────────────────────────────

// ─── GUEST-FACING GROUP NAME CHECK ────────────────────────────────────────────
// Toast tier and subgroup names are rendered verbatim as the guest's navigation
// tabs and as printed menu headings. A group called "Red2" or "WINE BTG" is a
// button a guest taps at the table. The app deliberately does not rewrite these -
// guessing that "Red2" means "Bold Reds" is exactly the kind of inference that
// goes wrong - so instead it flags the likely offenders for the owner to fix in
// Toast. Heuristic and advisory: false positives are expected and harmless.
const GROUP_NAME_STOPWORDS = ['test', 'misc', 'temp', 'other', 'tbd', 'new', 'old', 'dnu', 'do not use', 'xxx', 'zz'];
function auditGroupName(name) {
  const raw = String(name || '').trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (GROUP_NAME_STOPWORDS.some(w => lower === w || lower.startsWith(w + ' ') || lower.endsWith(' ' + w))) {
    return 'looks like a working name';
  }
  if (/\d\s*$/.test(raw) && !/\b(19|20)\d{2}\s*$/.test(raw)) return 'ends in a number';
  // No vowels in a word of 3+ letters — abbreviations like "BTG", "CHRD", "SPKLG"
  const words = raw.split(/[\s\-_/]+/).filter(Boolean);
  if (words.some(w => w.length >= 3 && /^[a-z]+$/i.test(w) && !/[aeiouy]/i.test(w))) return 'looks abbreviated';
  if (raw.length <= 3 && raw === raw.toUpperCase() && /^[a-z]+$/i.test(raw)) return 'very short abbreviation';
  if (/[_]/.test(raw)) return 'contains an underscore';
  return null;
}

function LabelScanModal({ wine, onClose, onEnrichComplete }) {
  const t = useTheme();
  const videoRef  = React.useRef(null);
  const streamRef = React.useRef(null);
  const fileRef   = React.useRef(null);

  const [phase, setPhase] = React.useState("camera"); // camera|identifying|confirm|unreadable|enriching|done|error
  const [cameraError, setCameraError] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [ident, setIdent] = React.useState(null);
  const [confirmedName, setConfirmedName] = React.useState("");
  const [enrichResult, setEnrichResult] = React.useState(null);

  const itemType = wine._type || "wine";

  React.useEffect(() => { startCamera(); return () => stopCamera(); }, []);

  async function startCamera() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1600 } },
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
    } catch (e) {
      setCameraError("Camera unavailable — you can choose a photo instead.");
    }
  }

  function stopCamera() {
    if (streamRef.current) { streamRef.current.getTracks().forEach(tr => tr.stop()); streamRef.current = null; }
  }

  // Downscale before upload. A 1200px-wide frame is more than enough for Claude to
  // read label text, and keeps the request small enough to be quick on restaurant wifi.
  function frameToDataUrl(source, sw, sh) {
    const maxW = 1200;
    const scale = Math.min(1, maxW / sw);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(sw * scale);
    canvas.height = Math.round(sh * scale);
    canvas.getContext("2d").drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.82);
  }

  function capture() {
    const v = videoRef.current;
    if (!v || v.readyState < 2) return;
    const dataUrl = frameToDataUrl(v, v.videoWidth, v.videoHeight);
    stopCamera();
    identify(dataUrl);
  }

  function onFilePicked(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => { stopCamera(); identify(frameToDataUrl(img, img.width, img.height)); };
    img.src = URL.createObjectURL(file);
  }

  async function identify(dataUrl) {
    setPreview(dataUrl);
    setPhase("identifying");
    try {
      const res = await rfetch(IDENTIFY_LABEL_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: dataUrl, itemType }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Couldn't read that photo.");
      if (!data.readable || !data.fullName) { setIdent(data); setPhase("unreadable"); return; }
      setIdent(data);
      setConfirmedName(data.fullName);
      setPhase("confirm");
    } catch (err) {
      setEnrichResult({ ok: false, message: err.message });
      setPhase("error");
    }
  }

  function retake() {
    setPreview(null); setIdent(null); setEnrichResult(null);
    setPhase("camera"); startCamera();
  }

  async function handleEnrich() {
    if (!confirmedName.trim()) return;
    setPhase("enriching");
    try {
      const res = await rfetch(REENRICH_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          itemId: wine.id, itemType,
          itemName: confirmedName,
          hints: { correctedName: confirmedName },
          // Name confirmed from the label; the enrichment itself still needs review.
          identityOnly: true,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        const e = data.enrichment;
        // Every field the review UI keys off must be replaced, not just the visible
        // ones. uncertainReason in particular: without it the previous warning text
        // survives in local state and keeps rendering after the item is resolved.
        onEnrichComplete(wine.id, {
          correctedName: e.correctedName, varietal: e.varietal, region: e.region,
          description: e.description, reviews: e.reviews,
          vintage: wine.vintage, name: e.correctedName,
          uncertain: e.uncertain || false,
          uncertainReason: e.uncertain ? (e.uncertainReason || null) : null,
          identityConfirmed: e.identityConfirmed || false,
          manuallyEdited: e.manuallyEdited || false,
          pendingApproval: e.pendingApproval !== false,
          approved: e.approved || false,
        });
        setEnrichResult({ ok: true, warn: !!e.uncertain, message: e.uncertain ? "Enriched — still flagged for review" : "✓ Enriched successfully" });
        setPhase("done");
      } else {
        setEnrichResult({ ok: false, message: data.error || "Enrichment failed" });
        setPhase("error");
      }
    } catch (e) { setEnrichResult({ ok: false, message: e.message }); setPhase("error"); }
  }

  const modalLabel = { display: "block", color: t.textMuted, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6, marginTop: 12 };
  const modalInput = { width: "100%", background: t.white04, border: `0.5px solid ${t.borderStrong}`, borderRadius: 6, color: t.textPrimary, fontFamily: t.fontSerif, fontSize: 14, padding: "10px 12px", marginBottom: 4 };
  const primaryBtn = { width: "100%", background: t.accent, color: t.accentText, border: "none", padding: "12px", borderRadius: 8, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 14, fontWeight: 600, marginTop: 14 };
  const ghostBtn   = { width: "100%", background: "transparent", border: `0.5px solid ${t.borderMid}`, color: t.textDim, padding: "10px", borderRadius: 8, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, marginTop: 8 };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1200, background: t.bgOverlay, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgSurface, border: `0.5px solid ${t.borderMid}`, borderRadius: 14, width: "100%", maxWidth: 460, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ padding: "16px 18px", borderBottom: `0.5px solid ${t.borderSubtle}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ color: t.textPrimary, fontFamily: t.fontSerif, fontSize: 16 }}><LineIcon name="camera" inline />Identify from Label</div>
            <div style={{ color: t.textDim, fontSize: 12, marginTop: 2 }}>{wine.name}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.textDim, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 18 }}>

          {phase === "camera" && (<>
            <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
              Fill the frame with the front label. Everything printed on it — producer, bottling, appellation — is what gets read.
            </div>
            <div style={{ position: "relative", background: t.bgDeep, borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
              <video ref={videoRef} playsInline muted style={{ width: "100%", display: "block", maxHeight: "46vh", objectFit: "cover" }} />
            </div>
            {cameraError && <div style={{ color: t.warning, fontSize: 11, marginBottom: 10 }}>{cameraError}</div>}
            <button onClick={capture} style={{ ...primaryBtn, marginTop: 0 }}>◎ Capture Label</button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFilePicked} style={{ display: "none" }} />
            <button onClick={() => fileRef.current && fileRef.current.click()} style={ghostBtn}>Choose a photo instead</button>
          </>)}

          {phase === "identifying" && (
            <div style={{ textAlign: "center", padding: "26px 0" }}>
              {preview && <img alt="" src={preview} style={{ width: "100%", borderRadius: 10, marginBottom: 14, opacity: 0.5 }} />}
              <div style={{ color: t.textMuted, fontSize: 13, fontStyle: "italic" }}>Reading the label…</div>
            </div>
          )}

          {phase === "unreadable" && (<>
            <div style={{ background: t.errorDim, border: `0.5px solid ${t.errorBorder}`, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ color: t.error, fontSize: 12, marginBottom: 4 }}>Couldn't read that label</div>
              <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5 }}>
                {(ident && ident.notes) || "Try again with more light, the label flat to the camera, and the bottle filling the frame."}
              </div>
            </div>
            <label style={modalLabel}>Or type the name</label>
            <input style={modalInput} value={confirmedName} onChange={e => setConfirmedName(e.target.value)} placeholder="e.g. Raventos i Blanc De Nit" />
            <button onClick={handleEnrich} disabled={!confirmedName.trim()} style={{ ...primaryBtn, opacity: confirmedName.trim() ? 1 : 0.4 }}>✦ Enrich with AI</button>
            <button onClick={retake} style={ghostBtn}>← Retake photo</button>
          </>)}

          {phase === "confirm" && ident && (<>
            {preview && <img alt="" src={preview} style={{ width: "100%", borderRadius: 10, marginBottom: 12 }} />}
            {ident.confidence === "low" && (
              <div style={{ background: t.warningDim || t.white04, border: `0.5px solid ${t.warning}`, borderRadius: 6, padding: "8px 10px", marginBottom: 10 }}>
                <div style={{ color: t.warning, fontSize: 11, lineHeight: 1.5 }}>
                  Parts of this label were hard to read — check the name before enriching.
                </div>
              </div>
            )}
            <label style={modalLabel}>Wine Name</label>
            <input style={modalInput} value={confirmedName} onChange={e => setConfirmedName(e.target.value)} />
            <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.6, marginTop: 8 }}>
              {ident.producer && <div>Producer · {ident.producer}</div>}
              {ident.region && <div>Region · {ident.region}</div>}
              {ident.varietal && <div>Varietal · {ident.varietal}</div>}
            </div>

            {/* The label often shows a year, but vintages live in the Toast item name so
                they go visibly stale when the next shipment lands. Surface it as an
                instruction, never as something this screen writes. */}
            {ident.vintage && !wine.vintage && (
              <div style={{ background: t.white04, border: `0.5px solid ${t.borderMid}`, borderRadius: 6, padding: "9px 11px", marginTop: 12 }}>
                <div style={{ color: t.textSecondary, fontSize: 11, lineHeight: 1.5 }}>
                  The label reads <strong>{String(ident.vintage)}</strong>. To track the vintage for
                  this wine, add {String(ident.vintage)} to the item name in Toast. Leave it off if
                  you'd rather show this wine without a vintage.
                </div>
              </div>
            )}

            <button onClick={handleEnrich} disabled={!confirmedName.trim()} style={{ ...primaryBtn, opacity: confirmedName.trim() ? 1 : 0.4 }}>✦ Enrich with AI</button>
            <button onClick={retake} style={ghostBtn}>← Retake photo</button>
          </>)}

          {phase === "enriching" && (
            <div style={{ textAlign: "center", padding: "30px 0", color: t.textMuted, fontSize: 13, fontStyle: "italic" }}>Researching this bottle…</div>
          )}

          {(phase === "done" || phase === "error") && enrichResult && (<>
            <div style={{
              background: enrichResult.ok ? t.successDim : t.errorDim,
              border: `0.5px solid ${enrichResult.ok ? t.success : t.errorBorder}`,
              borderRadius: 8, padding: "14px", textAlign: "center",
              color: enrichResult.ok ? t.success : t.error, fontSize: 13,
            }}>{!(enrichResult.ok && !enrichResult.warn) && <LineIcon name={enrichResult.warn ? 'alert' : 'xCircle'} inline />}{enrichResult.message}</div>
            <button onClick={onClose} style={primaryBtn}>Done</button>
            {!enrichResult.ok && <button onClick={retake} style={ghostBtn}>← Try again</button>}
          </>)}

        </div>
      </div>
    </div>
  );
}

// ─── Manager Screen ───────────────────────────────────────────────────────────

// ─── Add a Tablet ────────────────────────────────────────────────────────────
//
// Owner-level. Shows the Fully Kiosk provisioning QR for this restaurant plus
// the four steps the device side actually needs.
//
// The QR is rendered server-side by getProvisioningQr and arrives as a data
// URI, so the payload itself never reaches this component. Nothing here is a
// credential: the code carries an APK URL, a checksum and a short Fully Cloud
// reference. The kiosk PIN, Remote Admin password and volume licence key are
// pulled from Fully Cloud after the device enrols.
//
// Step 2 is in bold for a reason. A tablet that has been tapped through setup
// cannot be provisioned and has to be factory reset first — that is Android's
// rule, not Fully's, and it is the one mistake that costs someone half an hour.
function AddTabletScreen({ t }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let alive = true;
    rfetch(`${BASE}/getProvisioningQr`)
      .then(r => r.json())
      .then(d => { if (alive) setState({ loading: false, ...d }); })
      .catch(e => { if (alive) setState({ loading: false, error: e.message }); });
    return () => { alive = false; };
  }, []);

  // Seven steps, not four. The first run-through showed that scanning the code
  // is the halfway point, not the end: Samsung and Google both insist on their
  // own terms screens, and then Fully asks for a set of "special app access"
  // permissions that Android will NOT let a device owner grant automatically.
  //
  // The App Runtime Permission Policy in the Fully Cloud configuration is set
  // to Grant, and it works — but it only covers runtime permissions (camera,
  // location, storage). Appear on top, Usage data access, Alarms & reminders
  // and the battery exemption are special app access, which Android requires a
  // human to toggle. There is no configuration that removes step 6.
  //
  // Step 6 is where someone gets stuck. Each permission opens a separate
  // Android settings page with no Done button, and the way back is the Back
  // arrow. Without being told, people assume they have broken something.
  const STEPS = [
    { n: 1, title: 'Unbox the tablet, plug it in and power it on', body: 'It must be brand new, or factory reset. Do not tap through the setup screens.' },
    { n: 2, title: 'Tap the first screen six times', body: 'The welcome screen, usually the one asking you to choose a language. Six taps anywhere on it opens a QR scanner.' },
    { n: 3, title: 'Join your wifi', body: 'Pick your network and enter the password, the same as you would on a phone. Use a network without a sign-in page \u2014 a staff or office network, not guest wifi. If the scanner opened before you joined wifi, it will ask for the code again afterwards. That is normal.' },
    { n: 4, title: 'Scan the code below', body: 'The tablet downloads its software. This takes a few minutes \u2014 leave it plugged in.' },
    { n: 5, title: 'Accept the setup screens', body: 'Several pages from Google and the tablet\u2019s maker, one after another: Continue, Accept & continue, Agree. One says the tablet \"isn\u2019t private\" and is managed by your organization \u2014 that is expected, it means the setup is working. On \"Google services\", leave the switches as they are and tap More until the button says Accept. Some tablets also offer apps and games along the way \u2014 install none of them. See \"Other screens you may see\" below the code.' },
    { n: 6, title: 'Tap GET PERMISSIONS, then use the Back arrow', body: 'A screen headed \"Device Provisioning\" appears with a GET PERMISSIONS button. Tap it. The tablet asks for several permissions one at a time. Turn each switch on for Fully Kiosk Browser, then press the Back arrow at the top left to return. One page, \"Display over other apps\", lists several apps: turn Fully Kiosk Browser on, and turn Google off if it says Allowed. Repeat until it stops asking. There is no Done button; Back is how you move forward.' },
    { n: 7, title: 'Your menu opens', body: 'The tablet restarts into your beverage menu and locks itself down. For the first few minutes a bar of app icons may still appear at the bottom \u2014 it goes away on its own. Each day: press the power button to turn the screen on when you open, and again at close. Screens turn themselves off at 1:55am if anyone forgets. Keep tablets on their chargers overnight \u2014 updates install between 2 and 4am.' },
  ];

  // The screens that are NOT the same on every tablet. Written from setting up a
  // Lenovo next to a Samsung: the Samsung went straight through, the Lenovo stopped
  // for a navigation choice and then a chain of app-install offers with games and a
  // sportsbook ALREADY TICKED. Tapping through those puts them on a kiosk tablet.
  // Keyed by the heading the owner sees, since that is what they will search for.
  const OTHER_SCREENS = [
    { title: 'System navigation', body: 'Choose 3-button navigation, then Next. The buttons are locked once setup finishes; gesture navigation can bring up a PIN screen when a guest swipes from the edge.' },
    { title: 'Congrats on your new device!', body: 'An app-offer screen from the tablet\u2019s maker. Tap Continue. On \"Hi there! Introduce yourself\" tap Skip, as many times as it asks.' },
    { title: 'Recommended', body: 'A grid of apps, some already ticked. Untick every one \u2014 nothing should be selected \u2014 then tap Next.' },
    { title: 'Game Spotlight, or any other sign-up offer', body: 'Tap Later. Never Sign up.' },
    { title: 'You\u2019re all set up!', body: 'Tap Get started.' },
    { title: 'Display over other apps', body: 'Part of step 6. Two changes on this list: tap Fully Kiosk Browser and turn it ON, then Back. Tap Google and turn it OFF, then Back. Fully Kiosk Browser should end up Allowed and Google Not allowed. Leave any other apps as they are.' },
    { title: 'System update \u2014 Restart now / Later', body: 'Tap Later. The tablet installs updates overnight on its own while it is on the charger.' },
    { title: 'Something went wrong \u2014 contact your IT admin', body: 'The setup download did not finish, usually because the wifi signal dropped. Tap Reset and start again at step 2, somewhere the signal is strong. Nothing is lost.' },
  ];

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "20px 20px" }}>
      <div style={{ color: t.textSecondary, fontSize: 13, lineHeight: 1.6, marginBottom: 22 }}>
        Adding another tablet, or replacing one that stopped working? Scanning the
        code is the middle of the process, not the end &mdash; Google and the tablet&rsquo;s
        maker each ask you to accept their terms, and the tablet needs a few permissions
        turned on by hand. Follow all seven steps. Allow about 15 minutes.
      </div>

      {/* Hardware block sits ABOVE the steps. Someone on this screen is either
          about to buy a tablet or has just bought one — a spec placed after the
          instructions is too late to be useful.
          A named model rather than a spec sheet: a spec sheet invites
          interpretation and ends with us supporting hardware nobody here has
          held. Fire tablets are called out by name because they cannot do this
          at all (no Play Services, no Android Enterprise provisioning) and are
          exactly what gets bought at $60. */}
      <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ color: t.textPrimary, fontSize: 13, marginBottom: 7, fontFamily: t.fontSerif }}>Which tablet to buy</div>
        <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.65 }}>
          We recommend the <strong style={{ color: t.textPrimary }}>Samsung Galaxy Tab A11+</strong> &mdash;
          it&rsquo;s what the steps below were written for, so the screens will match what
          you see. Any recent Android tablet works, though the setup screens may look
          a little different along the way. Some brands, Lenovo among them, add screens
          offering apps and games during setup &mdash; the list below the code covers them.
          <br /><br />
          <span style={{ color: t.textSecondary }}>
            Any tablet can open your menu in a browser. Android is what makes setup
            easy &mdash; scan a code and the tablet configures itself and locks to your
            menu. If one ever goes wrong, we can get you back up by rescanning the
            same code.
            <br /><br />
            On an iPad or anything else, guests see the same menu, but you&rsquo;d be
            setting each device up by hand. iPads can be locked to a single app using
            Guided Access in Apple&rsquo;s own settings, which is a reasonable option if
            you already own them &mdash; it&rsquo;s just a manual job on every tablet, and
            one we can&rsquo;t help you with.
            <br /><br />
            Two things to avoid: <strong>Amazon Fire tablets</strong>, which cannot run this
            setup at all, and <strong>used or refurbished</strong> units, which often arrive
            already set up or account-locked and won't accept the code below.
          </span>
        </div>
      </div>

      <div style={{ background: t.warningDim, border: `1px solid ${t.warningBorder}`, borderRadius: 10, padding: "13px 16px", marginBottom: 22 }}>
        <div style={{ color: t.warning, fontSize: 12, lineHeight: 1.6 }}>
          <strong>Do not set the tablet up first.</strong> If anyone taps through
          the welcome screens, the tablet has to be factory reset before this will
          work. Open the box and go straight to step 2.
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 26 }}>
        {STEPS.map(s => (
          <div key={s.n} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 14, background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, color: t.accent, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: t.fontSerif }}>{s.n}</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: t.textPrimary, fontSize: 14, marginBottom: 3, fontFamily: t.fontSerif }}>{s.title}</div>
              <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.55 }}>{s.body}</div>
            </div>
          </div>
        ))}
      </div>

      {state.loading && (
        <div style={{ color: t.textSecondary, fontSize: 13, textAlign: "center", padding: "40px 0" }}>Loading your code…</div>
      )}

      {!state.loading && state.error && (
        <div style={{ color: t.warning, fontSize: 13, textAlign: "center", padding: "30px 0" }}>
          Couldn't load the code. Please try again, or contact SommSync.
        </div>
      )}

      {/* Deliberately not an error state. A restaurant whose profile has not been
          built yet should get a plain instruction, not a failure message. */}
      {!state.loading && !state.error && !state.configured && (
        <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 10, padding: "22px 20px", textAlign: "center" }}>
          <div style={{ color: t.textSecondary, fontSize: 13, lineHeight: 1.6 }}>
            Tablet setup hasn't been enabled for your restaurant yet. Contact
            SommSync and we'll turn it on.
          </div>
        </div>
      )}

      {!state.loading && state.configured && (
        <>
          {/* White plate under the code. A QR on a dark themed background will
              not scan — the scanner needs the quiet zone light. */}
          <div style={{ background: "#FFFFFF", borderRadius: 12, padding: 16, maxWidth: 340, margin: "0 auto" }}>
            <img src={state.qrDataUri} alt="Tablet setup code" style={{ width: "100%", display: "block" }} />
          </div>
          <div style={{ color: t.textSecondary, fontSize: 11, textAlign: "center", marginTop: 14, lineHeight: 1.6 }}>
            Hold the new tablet 6–10 inches away. If it won't read, tilt this
            screen to cut the glare or turn its brightness up.
          </div>

          {/* After the code, not among the steps: most tablets never show most of
              these, and seven steps already read as a lot. Step 5 points here. */}
          <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 10, padding: "14px 16px", marginTop: 22 }}>
            <div style={{ color: t.textPrimary, fontSize: 13, marginBottom: 4, fontFamily: t.fontSerif }}>Other screens you may see</div>
            <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.55, marginBottom: 12 }}>
              Depending on the tablet, some of these appear between the steps above. Find the
              heading on the tablet&rsquo;s screen and do what it says here.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              {OTHER_SCREENS.map(o => (
                <div key={o.title}>
                  <div style={{ color: t.textPrimary, fontSize: 12.5, fontWeight: 600, marginBottom: 2 }}>&ldquo;{o.title}&rdquo;</div>
                  <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.55 }}>{o.body}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Billing notice sits BELOW the code, not above it. A charge shown
              before the instructions reads as a paywall; shown after, it reads
              as a receipt. There is no device counter behind this — it is a
              reminder only, and reconciliation happens against the Fully Cloud
              device group at month end. */}
          <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 10, padding: "14px 16px", marginTop: 22 }}>
            <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.65 }}>
              Each tablet you set up here is billed at <strong style={{ color: t.textPrimary }}>$20, one time</strong>,
              on your next invoice. That covers the kiosk licence and the setup that
              lets the tablet configure itself.
              <br /><br />
              <span style={{ color: t.textSecondary }}>
                Replacing a tablet that failed? Let us know and we won't charge for it.
                A scan that doesn't finish isn't billed either — only tablets that
                complete setup and reach your menu.
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Menus & Items (manual / file-managed menus) ─────────────────────────────
//
// Talks only to the `menuManager` endpoint. Categories fed by a live POS are
// never offered for editing here: the server refuses them with 409 because the
// next sync would erase the change, and this screen hides them so a manager is
// not shown a form that cannot work. Toast items are fixed in Toast.
//
// Owners create menus and set their hours and locations. Floor managers can fix
// items (price, description, 86, which menus an item is on) during service.

const MENU_MANAGER_URL = `${BASE}/menuManager`;

const MM_CATEGORIES = [
  { id: "foodItems", label: "Food",          menuType: "food",         kind: "food", groupField: "course" },
  { id: "wines",     label: "Wine",          menuType: "wine",         kind: "wine", groupField: "subgroup" },
  { id: "beers",     label: "Beer",          menuType: "beer_pours",   kind: "bev",  groupField: "subgroup", enrichable: true },
  { id: "pours",     label: "Spirits",       menuType: "beer_pours",   kind: "bev",  groupField: "subgroup", enrichable: true },
  { id: "cocktails", label: "Cocktails",     menuType: "cocktails_na", kind: "bev",  groupField: "subgroup" },
  { id: "nab",       label: "Non-alcoholic", menuType: "cocktails_na", kind: "bev",  groupField: "subgroup" },
];

// Monday first for display; the server stores whatever order, as day codes.
const MM_WEEK = [
  { code: "MON", short: "M", name: "Mon" }, { code: "TUE", short: "T", name: "Tue" },
  { code: "WED", short: "W", name: "Wed" }, { code: "THU", short: "T", name: "Thu" },
  { code: "FRI", short: "F", name: "Fri" }, { code: "SAT", short: "S", name: "Sat" },
  { code: "SUN", short: "S", name: "Sun" },
];

function mmFormatTime(hhmm) {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (!Number.isFinite(h)) return hhmm || "";
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, "0")} ${suffix}` : `${h12} ${suffix}`;
}

function mmDescribeDays(days) {
  const set = new Set(days || []);
  if (set.size === 0 || set.size === 7) return "Every day";
  // Collapse consecutive runs of three or more into a range: Mon–Fri.
  const idx = MM_WEEK.map((d, i) => (set.has(d.code) ? i : -1)).filter(i => i >= 0);
  const parts = [];
  for (let i = 0; i < idx.length; ) {
    let j = i;
    while (j + 1 < idx.length && idx[j + 1] === idx[j] + 1) j++;
    if (j - i >= 2) parts.push(`${MM_WEEK[idx[i]].name}–${MM_WEEK[idx[j]].name}`);
    else for (let k = i; k <= j; k++) parts.push(MM_WEEK[idx[k]].name);
    i = j + 1;
  }
  return parts.join(", ");
}

function mmFormatDate(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function mmDescribeSchedule(s) {
  if (!s) return "Always available";
  const windows = (s.windows || []).map(w => `${mmDescribeDays(w.days)}, ${mmFormatTime(w.start)} to ${mmFormatTime(w.end)}`);
  const hours = windows.length ? windows.join("; ") : "All day";
  if (s.startDate && s.endDate) return `${hours} (${mmFormatDate(s.startDate)} to ${mmFormatDate(s.endDate)})`;
  if (s.startDate) return `${hours} (from ${mmFormatDate(s.startDate)})`;
  if (s.endDate) return `${hours} (until ${mmFormatDate(s.endDate)})`;
  return hours;
}

function mmPriceText(item, kind) {
  const p = v => (v === null || v === undefined || v === "") ? null : formatMoney(v);
  if (kind === "wine") {
    const parts = [p(item.glassPrice) && `${p(item.glassPrice)} glass`, p(item.bottlePrice) && `${p(item.bottlePrice)} bottle`].filter(Boolean);
    return parts.length ? parts.join(", ") : (item.priceTier ? `${item.priceTier} tier` : "No price");
  }
  return p(item.price) || "No price";
}

async function mmCall(action, payload = {}) {
  const res = await rfetch(MENU_MANAGER_URL, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, ...payload }),
  });
  let data = {};
  try { data = await res.json(); } catch (e) {}
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

function useMmStyles() {
  const t = useTheme();
  return {
    t,
    // 16px minimum on every input: iOS Safari zooms the page on focus below that.
    input: { width: "100%", boxSizing: "border-box", background: t.bgInput, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: "9px 11px", borderRadius: 6, fontFamily: t.fontSerif, fontSize: 16, outline: "none" },
    label: { color: t.textSecondary, fontSize: 11, marginBottom: 5, display: "block" },
    primaryBtn: (disabled) => ({ background: disabled ? t.accentDimSm : t.accent, color: disabled ? t.textDim : t.accentText, border: "none", padding: "10px 16px", borderRadius: 6, cursor: disabled ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 13, fontWeight: 600 }),
    quietBtn: { background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "9px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 },
    dangerBtn: { background: "none", border: `0.5px solid ${t.errorBorder}`, color: t.error, padding: "9px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 },
    chip: (active) => ({ background: active ? t.accentDim : t.white04, border: `0.5px solid ${active ? t.accent : t.borderMid}`, color: active ? t.accent : t.textSecondary, padding: "7px 12px", borderRadius: 16, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }),
    sectionTitle: { color: t.textPrimary, fontSize: 16, fontFamily: t.fontSerif, margin: "0 0 4px" },
    sectionNote: { color: t.textSecondary, fontSize: 13, lineHeight: 1.5, marginBottom: 12 },
  };
}

function MmNotice({ kind = "info", children }) {
  const t = useTheme();
  const c = kind === "error" ? [t.errorDim, t.errorBorder, t.error] : kind === "warning" ? [t.warningDim, t.warningBorder, t.warning] : [t.successDim, t.successBorder, t.success];
  return <div style={{ background: c[0], border: `0.5px solid ${c[1]}`, color: c[2], borderRadius: 8, padding: "9px 12px", fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>{children}</div>;
}

// Hours form shared by the menu editor and the upload review. `form` is
// { scheduled, windows, dated, startDate, endDate }; onChange(patch) merges.
function scheduleToForm(schedule) {
  const w = (schedule && schedule.windows) || [];
  return {
    scheduled: w.length > 0,
    windows: w.length ? w.map(x => ({ ...x, days: [...(x.days || [])] })) : [{ days: [], start: "11:00", end: "22:00" }],
    dated: !!(schedule && (schedule.startDate || schedule.endDate)),
    startDate: (schedule && schedule.startDate) || "",
    endDate: (schedule && schedule.endDate) || "",
  };
}

function formToSchedule(f) {
  return (f.scheduled || f.dated) ? {
    windows: f.scheduled ? f.windows : [],
    startDate: f.dated ? (f.startDate || null) : null,
    endDate: f.dated ? (f.endDate || null) : null,
  } : null;
}

function RoomChoice({ locations, onChange, locationNames }) {
  const s = useMmStyles();
  // "Both" is stored as an empty list, which is how Toast menus mean "everywhere".
  const locChoice = (locations || []).length === 1 ? locations[0] : "both";
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
      {[{ id: "both", name: `${locationNames.bar} and ${locationNames.dining}` }, { id: "bar", name: locationNames.bar }, { id: "dining", name: locationNames.dining }].map(o => (
        <button key={o.id} type="button" style={s.chip(locChoice === o.id)} onClick={() => onChange(o.id === "both" ? [] : [o.id])}>{o.name}</button>
      ))}
    </div>
  );
}

function ScheduleFields({ form, onChange }) {
  const s = useMmStyles(); const t = s.t;
  const draft = form;
  const set = onChange;
  const setWindow = (i, patch) => onChange({ windows: form.windows.map((w, j) => (j === i ? { ...w, ...patch } : w)) });
  const toggleDay = (i, code) => onChange({ windows: form.windows.map((w, j) => {
    if (j !== i) return w;
    return { ...w, days: w.days.includes(code) ? w.days.filter(x => x !== code) : [...w.days, code] };
  }) });
  return (
    <>
      <label style={s.label}>Hours</label>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <button type="button" style={s.chip(!draft.scheduled)} onClick={() => set({ scheduled: false })}>All day, every day</button>
        <button type="button" style={s.chip(draft.scheduled)} onClick={() => set({ scheduled: true })}>Set hours</button>
      </div>

      {draft.scheduled && draft.windows.map((w, i) => {
        const overnight = w.start && w.end && w.end <= w.start;
        return (
          <div key={i} style={{ background: t.white04, borderRadius: 8, padding: 12, marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 5, marginBottom: 10, flexWrap: "wrap" }} role="group" aria-label="Days">
              {MM_WEEK.map(d => {
                const on = w.days.includes(d.code);
                return (
                  <button key={d.code} type="button" title={d.name} aria-pressed={on} onClick={() => toggleDay(i, d.code)}
                    style={{ width: 36, height: 36, borderRadius: "50%", border: `0.5px solid ${on ? t.accent : t.borderMid}`, background: on ? t.accent : "transparent", color: on ? t.accentText : t.textSecondary, fontFamily: t.fontSerif, fontSize: 13, cursor: "pointer" }}>
                    {d.short}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="time" aria-label="Opens" style={{ ...s.input, flex: 1 }} value={w.start} onChange={e => setWindow(i, { start: e.target.value })} />
              <span style={{ color: t.textDim, fontSize: 12 }}>to</span>
              <input type="time" aria-label="Closes" style={{ ...s.input, flex: 1 }} value={w.end} onChange={e => setWindow(i, { end: e.target.value })} />
              {draft.windows.length > 1 && (
                <button type="button" aria-label="Remove these hours" onClick={() => set({ windows: draft.windows.filter((_, j) => j !== i) })}
                  style={{ background: "none", border: "none", color: t.textDim, fontSize: 20, cursor: "pointer", padding: "0 4px" }}>×</button>
              )}
            </div>
            <div style={{ color: t.textDim, fontSize: 11, marginTop: 7 }}>
              {w.days.length === 0 ? "No days picked, so these hours apply every day." : mmDescribeDays(w.days)}
              {overnight && " Runs past midnight into the next day."}
            </div>
          </div>
        );
      })}
      {draft.scheduled && (
        <button type="button" style={{ ...s.quietBtn, marginBottom: 14 }} onClick={() => set({ windows: [...draft.windows, { days: [], start: "17:00", end: "22:00" }] })}>
          Add more hours
        </button>
      )}

      <div style={{ marginTop: draft.scheduled ? 0 : 6, marginBottom: 16 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, color: t.textSecondary, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={draft.dated} onChange={e => set({ dated: e.target.checked })} style={{ width: 18, height: 18 }} />
          Only run between certain dates (seasonal or holiday)
        </label>
        {draft.dated && (
          <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
            <input type="date" aria-label="First day" style={{ ...s.input, flex: 1 }} value={draft.startDate} onChange={e => set({ startDate: e.target.value })} />
            <span style={{ color: t.textDim, fontSize: 12 }}>to</span>
            <input type="date" aria-label="Last day" style={{ ...s.input, flex: 1 }} value={draft.endDate} onChange={e => set({ endDate: e.target.value })} />
          </div>
        )}
      </div>
    </>
  );
}

function MenuEditor({ menu, existingTypes, locationNames, onSaved, onCancel, onDeleted }) {
  const s = useMmStyles(); const t = s.t;
  const isNew = !menu.id;
  const [draft, setDraft] = useState(() => ({
    label: menu.label || "",
    menuType: menu.menuType || existingTypes[0] || "food",
    locations: menu.locations || [],
    ...scheduleToForm(menu.schedule),
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = patch => setDraft(d => ({ ...d, ...patch }));

  async function save() {
    setBusy(true); setError(null);
    try {
      const schedule = formToSchedule(draft);
      const data = await mmCall("saveMenu", { menu: { id: menu.id, label: draft.label, menuType: draft.menuType, locations: draft.locations, schedule } });
      onSaved(data.menu);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function remove() {
    if (!window.confirm(`Delete "${menu.label}"?\n\nItems that are only on this menu will be removed from the guest menu too. You can bring them back from Removed items.`)) return;
    setBusy(true); setError(null);
    try {
      const data = await mmCall("deleteMenu", { id: menu.id });
      onDeleted(data);
    } catch (e) { setError(e.message); setBusy(false); }
  }

  const typeOptions = MENU_TYPES.filter(o => existingTypes.includes(o.value));

  return (
    <div style={{ background: t.bgCard, border: `0.5px solid ${t.accentBorder}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      {error && <MmNotice kind="error">{error}</MmNotice>}

      <label style={s.label}>Menu name</label>
      <input style={{ ...s.input, marginBottom: 14 }} value={draft.label} placeholder="Lunch, Happy Hour, Brunch…" onChange={e => set({ label: e.target.value })} />

      <label style={s.label}>What's on it</label>
      {isNew ? (
        <select style={{ ...s.input, marginBottom: 14 }} value={draft.menuType} onChange={e => set({ menuType: e.target.value })}>
          {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <div style={{ color: t.textPrimary, fontSize: 14, marginBottom: 14 }}>
          {MENU_TYPES.find(o => o.value === draft.menuType)?.label || draft.menuType}
          <span style={{ color: t.textDim, fontSize: 11 }}> (can't be changed once created)</span>
        </div>
      )}

      <label style={s.label}>Shown in</label>
      <RoomChoice locations={draft.locations} locationNames={locationNames} onChange={locations => set({ locations })} />

      <ScheduleFields form={draft} onChange={set} />

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" style={s.primaryBtn(busy || !draft.label.trim())} disabled={busy || !draft.label.trim()} onClick={save}>
          {busy ? "Saving…" : isNew ? "Create menu" : "Save menu"}
        </button>
        <button type="button" style={s.quietBtn} onClick={onCancel} disabled={busy}>Cancel</button>
        <div style={{ flex: 1 }} />
        {!isNew && <button type="button" style={s.dangerBtn} onClick={remove} disabled={busy}>Delete menu</button>}
      </div>
    </div>
  );
}

// presetMenuGuids: for a NEW item, the menus it starts on. Your menus → Edit
// items passes the menu being edited, so "Add a dish" on Dinner lands on Dinner
// instead of on no menu at all.
function MenuItemEditor({ cat, item, menus, isAdmin, onSaved, onCancel, onRemoved, presetMenuGuids = null }) {
  const s = useMmStyles(); const t = s.t;
  const isNew = !item;
  const str = v => (v === null || v === undefined ? "" : String(v));
  const baseline = useMemo(() => ({
    name: str(item?.name), description: str(item?.description), course: str(item?.course),
    subgroup: str(item?.subgroup), price: str(item?.price), glassPrice: str(item?.glassPrice),
    bottlePrice: str(item?.bottlePrice), priceTier: str(item?.priceTier),
    available: item ? item.available !== false : true,
    excludeFromPairing: !!item?.excludeFromPairing,
    menuGuids: [...(item?.menuGuids || [])].sort(),
  }), [item]);
  const [form, setForm] = useState(() => (isNew && presetMenuGuids ? { ...baseline, menuGuids: [...presetMenuGuids] } : baseline));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = patch => setForm(f => ({ ...f, ...patch }));
  const menusOfType = menus.filter(m => m.menuType === cat.menuType);
  const showDescription = cat.kind === "food" || (cat.kind === "bev" && !cat.enrichable);

  const fields = cat.kind === "wine" ? ["name", "glassPrice", "bottlePrice", "priceTier", "subgroup"]
    : cat.kind === "food" ? ["name", "description", "course", "price"]
    : ["name", ...(showDescription ? ["description"] : []), "price", "subgroup"];

  function buildChanges() {
    const out = {};
    fields.forEach(f => { if (isNew ? form[f] !== "" : form[f] !== baseline[f]) out[f] = form[f]; });
    const guids = [...form.menuGuids].sort();
    if (isNew ? guids.length : JSON.stringify(guids) !== JSON.stringify(baseline.menuGuids)) out.menuGuids = guids;
    if (!isNew && form.available !== baseline.available) out.available = form.available;
    if (cat.kind === "food" && form.excludeFromPairing !== baseline.excludeFromPairing) out.excludeFromPairing = form.excludeFromPairing;
    return out;
  }
  const changes = buildChanges();
  const dirty = Object.keys(changes).length > 0;

  async function save() {
    setBusy(true); setError(null);
    try {
      const data = await mmCall("saveItem", { category: cat.id, id: item?.id, item: changes });
      onSaved({ ...data.item, excludeFromPairing: form.excludeFromPairing }, data.warnings || [], data.created, !!data.needsEnrichment);
    } catch (e) { setError(e.message); setBusy(false); }
  }

  async function remove() {
    if (!window.confirm(`Remove "${item.name}" from the menu?\n\nYou can bring it back from Removed items.`)) return;
    setBusy(true); setError(null);
    try { await mmCall("deleteItem", { category: cat.id, id: item.id }); onRemoved(item); }
    catch (e) { setError(e.message); setBusy(false); }
  }

  const field = (key, label, props = {}) => (
    <div style={{ marginBottom: 12, ...(props.half ? { flex: 1, minWidth: 0 } : {}) }}>
      <label style={s.label} htmlFor={`mm-${key}`}>{label}</label>
      {props.multiline
        ? <textarea id={`mm-${key}`} rows={3} style={{ ...s.input, resize: "vertical", lineHeight: 1.45 }} value={form[key]} onChange={e => set({ [key]: e.target.value })} />
        : <input id={`mm-${key}`} style={s.input} value={form[key]} inputMode={props.money ? "decimal" : undefined} placeholder={props.placeholder} onChange={e => set({ [key]: e.target.value })} />}
    </div>
  );

  return (
    <div style={{ background: t.bgCard, border: `0.5px solid ${t.accentBorder}`, borderRadius: 10, padding: 14, margin: "4px 0 10px" }}>
      {error && <MmNotice kind="error">{error}</MmNotice>}
      {/* For wine, beer and spirits the name guests see is the AI-corrected one,
          edited in Edit Menu Items. This field is the menu's own name, and changing it
          does not reach guests - say so instead of leaving it to be discovered. */}
      {field("name", (cat.kind === "wine" || cat.enrichable) ? "Menu name" : "Name")}
      {(cat.kind === "wine" || cat.enrichable) && !isNew && (
        <div style={{ color: t.textDim, fontSize: 11, marginTop: -6, marginBottom: 12 }}>This is the name from your menu. The name guests see is set in Edit Menu Items.</div>
      )}
      {showDescription && field("description", "Description", { multiline: true })}
      {cat.kind === "wine" ? (
        <>
          <div style={{ display: "flex", gap: 10 }}>
            {field("glassPrice", "Glass price", { money: true, half: true, placeholder: "—" })}
            {field("bottlePrice", "Bottle price", { money: true, half: true, placeholder: "—" })}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ marginBottom: 12, flex: 1 }}>
              <label style={s.label} htmlFor="mm-priceTier">Price tier (if no price)</label>
              <select id="mm-priceTier" style={s.input} value={form.priceTier} onChange={e => set({ priceTier: e.target.value })}>
                <option value="">None</option><option>Value</option><option>Mid-Range</option><option>Premium</option>
              </select>
            </div>
            {field("subgroup", "Section", { half: true, placeholder: "Reds, Sparkling…" })}
          </div>
          <div style={{ color: t.textDim, fontSize: 11, marginBottom: 12 }}>{isNew ? "SommSync researches the wine's description after you add it." : "The wine's description comes from SommSync's research. Edit it in Manage Items → Edit menu items."}</div>
        </>
      ) : (
        <div style={{ display: "flex", gap: 10 }}>
          {field("price", "Price", { money: true, half: true, placeholder: "—" })}
          {cat.kind === "food" ? field("course", "Course", { half: true, placeholder: "Starters, Mains…" }) : field("subgroup", "Section", { half: true })}
        </div>
      )}
      {cat.enrichable && cat.kind === "bev" && <div style={{ color: t.textDim, fontSize: 11, marginBottom: 12 }}>{isNew ? "SommSync researches the description after you add it." : "The description comes from SommSync's research. Edit it in Manage Items → Edit menu items."}</div>}

      <div style={{ marginBottom: 12 }}>
        <div style={s.label}>On these menus</div>
        {menusOfType.length === 0 ? (
          <div style={{ color: t.textDim, fontSize: 12 }}>
            {isAdmin ? "No menus of this type exist yet, so this shows all day in both rooms. To control when and where it shows, create one in Menus & hours." : "No menus of this type exist yet, so this shows all day in both rooms. An owner can create one to control when and where it shows."}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {menusOfType.map(m => {
              const on = form.menuGuids.includes(m.id);
              return <button key={m.id} type="button" aria-pressed={on} style={s.chip(on)}
                onClick={() => set({ menuGuids: on ? form.menuGuids.filter(g => g !== m.id) : [...form.menuGuids, m.id] })}>{on ? "✓ " : ""}{m.label}</button>;
            })}
          </div>
        )}
      </div>

      {!isNew && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, color: t.textSecondary, fontSize: 13, marginBottom: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={!form.available} onChange={e => set({ available: !e.target.checked })} style={{ width: 18, height: 18 }} />
          86'd (out of stock, hidden from guests)
        </label>
      )}
      {cat.kind === "food" && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, color: t.textSecondary, fontSize: 13, marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={form.excludeFromPairing} onChange={e => set({ excludeFromPairing: e.target.checked })} style={{ width: 18, height: 18 }} />
          Leave out of wine pairings (sides, sauces, add-ons)
        </label>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" style={s.primaryBtn(busy || !dirty || !form.name.trim())} disabled={busy || !dirty || !form.name.trim()} onClick={save}>
          {busy ? "Saving…" : isNew ? "Add item" : "Save changes"}
        </button>
        <button type="button" style={s.quietBtn} onClick={onCancel} disabled={busy}>Cancel</button>
        <div style={{ flex: 1 }} />
        {!isNew && <button type="button" style={s.dangerBtn} onClick={remove} disabled={busy}>Remove</button>}
      </div>
    </div>
  );
}

// ─── "Your menu" half of the unified item editor ─────────────────────────────
//
// The All Items editor used to cover only what guests read (name, grape, region,
// description), while prices, sections, 86 and menu assignment lived in a second
// editor on another screen - the same item, two editors, and a Name field in each
// that changed different things. This is that second half, placed inside the
// first, so fixing a wine is one place and one Save.
//
// It talks to the same menuManager endpoint the old editor does (listMenus,
// listItems, saveItem), so every rule on the server still applies: a category fed
// by a live POS is refused with 409, so for those this shows a note instead of a
// form, and floor managers and owners get exactly the permissions they had.
//
// It deliberately has no Name field. The name guests see is the AI-corrected one
// edited in the guest half; the menu's own name is the one that used to confuse.
//
// The parent drives Save through the ref: isDirty() says whether anything here
// changed, save() sends only the changed fields. The parent calls save() BEFORE it
// posts the guest half, and skips the guest post when only menu fields changed -
// posting it would mark the item as manager-edited, which the server treats as
// approval, so a price tweak must never publish an unreviewed item.
const MM_CAT_FOR_ITEMS_TAB = { wines: "wines", beer: "beers", pours: "pours", cocktails: "cocktails", nab: "nab", food: "foodItems" };

const YourMenuSection = React.forwardRef(function YourMenuSection({ catKey, itemId, item, isAdmin = false }, ref) {
  const s = useMmStyles(); const t = s.t;
  const cat = MM_CATEGORIES.find(c => c.id === MM_CAT_FOR_ITEMS_TAB[catKey]) || null;
  const [load, setLoad] = useState({ status: "loading" }); // loading | ready | readonly | missing | error
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [menus, setMenus] = useState([]);
  const [error, setError] = useState(null);
  const str = v => (v === null || v === undefined ? "" : String(v));
  // A dish has no separate AI-corrected name the way wine, beer and spirits do:
  // the menu's name IS what guests see, so for food the name is edited here. It
  // goes through saveItem, which records the original name so a later file upload
  // still matches a renamed dish instead of adding it a second time.
  //
  // Cocktails and non-alcoholic drinks are the same: nothing enriches them, so
  // their menu name and description ARE what guests read, and with the Add /
  // Remove tab folded into All Items this is the only place left to edit them.
  const ownCopy = !!cat && cat.kind === "bev" && !cat.enrichable;
  const fieldsFor = cat ? (cat.kind === "wine" ? ["glassPrice", "bottlePrice", "priceTier", "subgroup"]
    : cat.kind === "food" ? ["name", "price", "course"]
    : ownCopy ? ["name", "description", "price", "subgroup"] : ["price", "subgroup"]) : [];

  useEffect(() => {
    let alive = true;
    setLoad({ status: "loading" }); setForm(null); setBaseline(null); setError(null);
    if (!cat) { setLoad({ status: "missing" }); return () => { alive = false; }; }
    (async () => {
      try {
        const info = await mmCall("listMenus");
        if (!alive) return;
        if (!info.editable || !info.editable[cat.id]) { setLoad({ status: "readonly", posName: info.posName }); return; }
        const items = (await mmCall("listItems", { category: cat.id })).items || [];
        if (!alive) return;
        const rec = items.find(x => x.id === itemId);
        if (!rec) { setLoad({ status: "missing" }); return; }
        const f = {};
        fieldsFor.forEach(k => { f[k] = str(rec[k]); });
        f.available = rec.available !== false;
        f.excludeFromPairing = !!rec.excludeFromPairing;
        f.menuGuids = [...(rec.menuGuids || [])].sort();
        setBaseline(f); setForm(f);
        setMenus((info.menus || []).filter(m => !m.retired && m.menuType === cat.menuType));
        setLoad({ status: "ready" });
      } catch (e) { if (alive) setLoad({ status: "error", message: e.message }); }
    })();
    return () => { alive = false; };
  }, [cat ? cat.id : null, itemId]);

  function buildChanges() {
    if (!form || !baseline || !cat) return {};
    const out = {};
    fieldsFor.forEach(k => { if (form[k] !== baseline[k]) out[k] = form[k]; });
    const guids = [...form.menuGuids].sort();
    if (JSON.stringify(guids) !== JSON.stringify(baseline.menuGuids)) out.menuGuids = guids;
    if (form.available !== baseline.available) out.available = form.available;
    // No excludeFromPairing here for food: it is the same foodExclusions flag the
    // Hidden toggle above sets, and two controls for one flag in one Save meant
    // whichever was posted last silently won.
    return out;
  }

  React.useImperativeHandle(ref, () => ({
    isDirty: () => load.status === "ready" && Object.keys(buildChanges()).length > 0,
    save: async () => {
      const changes = buildChanges();
      if (!Object.keys(changes).length) return null;
      setError(null);
      try {
        const data = await mmCall("saveItem", { category: cat.id, id: itemId, item: changes });
        setBaseline(form);
        return data;
      } catch (e) { setError(e.message); throw e; }
    },
  }), [load.status, form, baseline]);

  if (!cat) return null;
  const heading = (
    <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", margin: "14px 0 8px", paddingTop: 10, borderTop: `0.5px solid ${t.borderSubtle}` }}>Your menu</div>
  );
  if (load.status === "loading") return <>{heading}<div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 12 }}>Loading menu details…</div></>;
  if (load.status === "readonly") {
    // A category fed by a live POS. The server refuses edits to it (409), so there
    // is nothing to edit here - but showing the current values, greyed out and
    // locked, is more useful than a bare note: the manager can see what guests
    // will be charged without leaving this screen, and the layout matches what a
    // file-managed restaurant sees, only locked. Values come from the item the
    // editor was opened on (the POS sync already put them there).
    const pos = load.posName || "your POS";
    const it = item || {};
    const money = v => (v === null || v === undefined || v === "") ? "—" : (formatMoney(v) || "—");
    const txt = v => (v === null || v === undefined || v === "") ? "—" : String(v);
    // Greyed by its background, but the text stays readable: a locked field the
    // manager cannot read defeats the point of showing the value.
    const roStyle = { ...s.input, background: t.white06, color: t.textSecondary, borderColor: t.borderSubtle, cursor: "default" };
    const ro = (label, value) => (
      <div style={{ marginBottom: 12, flex: 1, minWidth: 0 }}>
        <label style={s.label}>{label}</label>
        <input style={roStyle} value={value} disabled readOnly aria-label={`${label}, from ${pos}, read-only`} />
      </div>
    );
    return (
      <>
        <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", margin: "14px 0 8px", paddingTop: 10, borderTop: `0.5px solid ${t.borderSubtle}`, display: "flex", alignItems: "center", gap: 8 }}>
          Your menu <span style={{ color: t.textSecondary, letterSpacing: 0, textTransform: "none", fontSize: 11 }}><LineIcon name="lock" inline />from {pos}</span>
        </div>
        {cat.kind === "wine" ? (
          <>
            <div style={{ display: "flex", gap: 10 }}>{ro("Glass price", money(it.glassPrice))}{ro("Bottle price", money(it.bottlePrice))}</div>
            <div style={{ display: "flex", gap: 10 }}>{ro(`${pos} menu`, txt(it.tier))}{ro("Section", txt(it.subgroup))}</div>
          </>
        ) : cat.kind === "food" ? (
          <div style={{ display: "flex", gap: 10 }}>{ro("Price", money(it.price))}{ro("Course", txt(it.course))}</div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 10 }}>{ro("Price", money(it.price !== undefined && it.price !== null ? it.price : it.groupPrice))}{ro("Section", txt(it.subgroup))}</div>
            {it.tier ? <div style={{ display: "flex", gap: 10 }}>{ro(`${pos} menu`, txt(it.tier))}</div> : null}
          </>
        )}
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>Stock</label>
          <div style={{ ...roStyle, display: "inline-block", width: "auto", padding: "6px 12px", borderRadius: 16, fontSize: 13 }}>{it.available === false ? "86'd (out of stock)" : "In stock"}</div>
        </div>
        <div style={{ color: t.textSecondary, fontSize: 11.5, marginBottom: 12, fontStyle: "italic", lineHeight: 1.5 }}>
          These fields are greyed out because they come from {pos}. Please edit them in {pos} directly. Changes appear here after the next sync, or right away from Update menu.
        </div>
      </>
    );
  }
  if (load.status === "error") return <>{heading}<div style={{ color: t.textSecondary, fontSize: 11.5, marginBottom: 12, fontStyle: "italic", lineHeight: 1.5 }}>Menu details could not be loaded ({load.message}). Price and availability are changed in your POS or in Menus & hours.</div></>;
  if (load.status === "missing" || !form) return null;

  const set = patch => setForm(f => ({ ...f, ...patch }));
  const field = (key, label, props = {}) => (
    <div style={{ marginBottom: 12, ...(props.half ? { flex: 1, minWidth: 0 } : {}) }}>
      <label style={s.label} htmlFor={`ym-${key}`}>{label}</label>
      <input id={`ym-${key}`} style={s.input} value={form[key]} inputMode={props.money ? "decimal" : undefined} placeholder={props.placeholder} onChange={e => set({ [key]: e.target.value })} />
    </div>
  );
  return (
    <>
      {heading}
      {error && <MmNotice kind="error">{error}</MmNotice>}
      {cat.kind === "wine" ? (
        <>
          <div style={{ display: "flex", gap: 10 }}>
            {field("glassPrice", "Glass price", { money: true, half: true, placeholder: "—" })}
            {field("bottlePrice", "Bottle price", { money: true, half: true, placeholder: "—" })}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ marginBottom: 12, flex: 1 }}>
              <label style={s.label} htmlFor="ym-priceTier">Price tier (if no price)</label>
              <select id="ym-priceTier" style={s.input} value={form.priceTier} onChange={e => set({ priceTier: e.target.value })}>
                <option value="">None</option><option>Value</option><option>Mid-Range</option><option>Premium</option>
              </select>
            </div>
            {field("subgroup", "Section", { half: true, placeholder: "Reds, Sparkling…" })}
          </div>
        </>
      ) : (
        <>
          {cat.kind === "food" && (
            <>
              {field("name", "Name (what guests see)")}
              <div style={{ color: t.textSecondary, fontSize: 11, marginTop: -6, marginBottom: 12, lineHeight: 1.5 }}>
                Changing a dish's name or description means its pairings use the live sommelier until the pairing matrix is next regenerated.
              </div>
            </>
          )}
          {ownCopy && (
            <>
              {field("name", "Name (what guests see)")}
              <div style={{ marginBottom: 12 }}>
                <label style={s.label} htmlFor="ym-description">Description</label>
                <textarea id="ym-description" rows={3} style={{ ...s.input, resize: "vertical", lineHeight: 1.45 }} value={form.description} onChange={e => set({ description: e.target.value })} />
              </div>
            </>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            {field("price", "Price", { money: true, half: true, placeholder: "—" })}
            {cat.kind === "food" ? field("course", "Course", { half: true, placeholder: "Starters, Mains…" }) : field("subgroup", "Section", { half: true })}
          </div>
        </>
      )}
      <div style={{ marginBottom: 12 }}>
        <div style={s.label}>On these menus</div>
        {menus.length === 0 ? (
          <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.5 }}>{isAdmin
            ? "No menus of this type exist yet, so this shows all day in both rooms. To control when and where it shows, create a menu in Menus & hours, then come back and tick it here."
            : "No menus of this type exist yet, so this shows all day in both rooms. An owner can create one to control when and where it shows."}</div>
        ) : (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {menus.map(m => {
              const on = form.menuGuids.includes(m.id);
              return <button key={m.id} type="button" aria-pressed={on} style={s.chip(on)}
                onClick={() => set({ menuGuids: on ? form.menuGuids.filter(g => g !== m.id) : [...form.menuGuids, m.id] })}>{on ? "✓ " : ""}{m.label}</button>;
            })}
          </div>
        )}
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, color: t.textSecondary, fontSize: 13, marginBottom: 10, cursor: "pointer" }}>
        <input type="checkbox" checked={!form.available} onChange={e => set({ available: !e.target.checked })} style={{ width: 18, height: 18 }} />
        86'd (out of stock, hidden from guests)
      </label>
    </>
  );
});

// ⚠️ No longer rendered anywhere (Oct 27, 2026): Your menus (MenusHub) replaced
// both places it was mounted. Kept for one season in case a path back to it is
// needed; delete once the hub has run through a live restaurant's menu changes.
//
// `only` lets the Menu items screen show one half: "menus" is the Menus & Hours
// tab. "items" was the Add / Remove Items tab until Oct 16, 2026, when adding,
// removing, section rename and restore moved into All Items (AllItemsTab); it
// still works but nothing in Menu items opens it. Left null it renders both, as
// the old Menus & Items screen did (still used for the held-review fallback and
// for admin setup links that open ?manager=menus).
function MenusScreen({ isAdmin, locationNames = { bar: "Bar", dining: "Dining Room" }, only = null }) {
  const s = useMmStyles(); const t = s.t;
  const [info, setInfo] = useState(null);           // listMenus response
  const [loadError, setLoadError] = useState(null);
  const [editingMenu, setEditingMenu] = useState(null); // menu object, or {} for new
  const [notice, setNotice] = useState(null);       // { kind, text }
  const [catId, setCatId] = useState(null);
  const [items, setItems] = useState(null);
  const [itemsError, setItemsError] = useState(null);
  const [search, setSearch] = useState("");
  const [editingItem, setEditingItem] = useState(null); // item id, or "new"
  // Renaming a section heading in place (see renameSection). `renamingGroup` is
  // the heading being edited, `renameValue` what is typed so far.
  const [renamingGroup, setRenamingGroup] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archive, setArchive] = useState(null);

  const loadMenus = useCallback(async () => {
    try { setInfo(await mmCall("listMenus")); setLoadError(null); }
    catch (e) { setLoadError(e.message); }
  }, []);
  useEffect(() => { loadMenus(); }, [loadMenus]);

  const editableCats = info ? MM_CATEGORIES.filter(c => info.editable?.[c.id]) : [];
  const editableTypes = [...new Set(editableCats.map(c => c.menuType))];
  const cat = MM_CATEGORIES.find(c => c.id === catId) || null;

  // Default to the first editable category once permissions are known.
  useEffect(() => { if (!catId && editableCats.length) setCatId(editableCats[0].id); }, [info]);

  const loadItems = useCallback(async () => {
    if (!catId) return;
    setItems(null); setItemsError(null); setEditingItem(null); setRenamingGroup(null);
    try { setItems((await mmCall("listItems", { category: catId })).items || []); }
    catch (e) { setItemsError(e.message); }
  }, [catId]);
  useEffect(() => { loadItems(); }, [loadItems]);

  // Loaded only when asked for, and separately, so opening it does not blank
  // the item list above it.
  useEffect(() => {
    if (!showArchive || !catId) return;
    let alive = true;
    setArchive(null);
    mmCall("listArchive", { category: catId })
      .then(d => { if (alive) setArchive(d.items || []); })
      .catch(() => { if (alive) setArchive([]); });
    return () => { alive = false; };
  }, [showArchive, catId]);

  function flash(kind, text) {
    setNotice({ kind, text });
    setTimeout(() => setNotice(n => (n && n.text === text ? null : n)), 6000);
  }

  async function restore(it) {
    try {
      const data = await mmCall("restoreItem", { category: catId, id: it.id });
      setArchive(a => (a || []).filter(x => x.id !== it.id));
      setItems(list => [...(list || []), data.item]);
      flash(data.warnings?.length ? "warning" : "success", [`${it.name} is back on the menu.`, ...(data.warnings || [])].join(" "));
    } catch (e) { flash("error", e.message); }
  }

  // Rename a section heading ("Red Wine" -> "White Wine") without opening every
  // item. A heading is not stored anywhere on its own: it is just the Section
  // value its items share, so renaming it means saving that value onto each of
  // them. That reuses the same saveItem call the item editor makes, so it
  // follows the same rules - floor managers and owners alike, never a category
  // fed by a live POS (the server refuses those with 409), and each wine is
  // marked as manager-edited so a later file upload shows a conflict instead of
  // quietly putting the old heading back.
  //
  // Works on ALL items under the heading, not just the ones the search box
  // currently shows. Four saves run at a time. If one fails part-way the ones
  // that went through stay renamed and the message says how many, rather than
  // pretending nothing happened.
  async function renameSection(oldName, rawNew) {
    let newName = String(rawNew || "").trim();
    if (!cat || !items || !newName || newName === oldName) { setRenamingGroup(null); return; }
    const field = cat.groupField;
    const labelOf = it => it[field] || "Other";
    const inGroup = items.filter(it => labelOf(it) === oldName);
    if (inGroup.length === 0) { setRenamingGroup(null); return; }
    // Typing the name of a section that already exists, in any capitalisation,
    // means "merge into it". Adopt that section's exact spelling so the merge
    // does not leave "Sparkling" and "sparkling" as two headings.
    const existingLabel = items.map(labelOf).find(l => l !== oldName && l.toLowerCase() === newName.toLowerCase());
    if (existingLabel) {
      newName = existingLabel;
      if (!window.confirm(`There is already a section called "${newName}".\n\nMove these ${inGroup.length} item${inGroup.length === 1 ? "" : "s"} into it?`)) return;
    }
    setRenameBusy(true);
    const saved = [];
    let failure = null;
    const queue = [...inGroup];
    async function worker() {
      while (queue.length && !failure) {
        const it = queue.shift();
        try {
          const data = await mmCall("saveItem", { category: cat.id, id: it.id, item: { [field]: newName } });
          saved.push(data.item);
        } catch (e) { failure = e; }
      }
    }
    await Promise.all([worker(), worker(), worker(), worker()]);
    // Merge rather than replace: the server's copy does not carry the
    // pairing-exclusion flag the list adds to food items.
    const byId = new Map(saved.map(x => [x.id, x]));
    setItems(list => (list || []).map(x => (byId.has(x.id) ? { ...x, ...byId.get(x.id) } : x)));
    setRenameBusy(false);
    if (failure) {
      flash("error", `Renamed ${saved.length} of ${inGroup.length} to "${newName}", then it stopped: ${failure.message} Try again to finish the rest.`);
    } else {
      setRenamingGroup(null);
      flash("success", `"${oldName}" is now "${newName}" (${inGroup.length} item${inGroup.length === 1 ? "" : "s"}).`);
    }
  }

  if (loadError) return <div style={{ flex: 1, overflowY: "auto", padding: 16 }}><MmNotice kind="error">{loadError}</MmNotice></div>;
  if (!info) return <div style={{ flex: 1, color: t.textDim, textAlign: "center", padding: 40 }}>Loading menus…</div>;

  if (editableCats.length === 0) {
    return (
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        <div style={{ color: t.textPrimary, fontSize: 15, marginBottom: 8 }}>Your whole menu comes from {info.posName}.</div>
        <div style={{ color: t.textSecondary, fontSize: 13, lineHeight: 1.6 }}>
          Make price, description and menu changes in {info.posName}. They appear here after the next sync, or right away from Update Menu.
        </div>
      </div>
    );
  }

  const activeMenus = info.menus.filter(m => !m.retired);
  const retiredMenus = info.menus.filter(m => m.retired);
  const menuCountByType = type => activeMenus.filter(m => m.menuType === type).length;
  const q = foldText(search).trim();
  const visible = (items || []).filter(it => !q || [it.name, it.description, it.course, it.subgroup].some(v => searchHit(v, q)));
  const groups = [];
  const groupIndex = {};
  visible.forEach(it => {
    const g = (cat && it[cat.groupField]) || "Other";
    if (!(g in groupIndex)) { groupIndex[g] = groups.length; groups.push({ name: g, items: [] }); }
    groups[groupIndex[g]].items.push(it);
  });
  const menuName = id => info.menus.find(m => m.id === id)?.label;
  const posOwnedLabels = MM_CATEGORIES.filter(c => !info.editable?.[c.id]).map(c => c.label);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 40px" }}>
      {notice && <MmNotice kind={notice.kind}>{notice.text}</MmNotice>}
      {info.liveSync && posOwnedLabels.length > 0 && (
        <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 14 }}>
          {posOwnedLabels.join(", ")} come{posOwnedLabels.length === 1 ? "s" : ""} from {info.posName}; change {posOwnedLabels.length === 1 ? "it" : "those"} there.
        </div>
      )}

      {/* ── Menus ───────────────────────────────────────────── */}
      {only !== "items" && (
      <section style={{ marginBottom: 28 }}>
        <h2 style={s.sectionTitle}>Menus</h2>
        <div style={s.sectionNote}>
          A menu sets when and where its items show to guests. Items not on any menu show whenever their category does.
        </div>

        {/* Creating and editing menus is owner-only (the server enforces it). A
            manager signed in with the staff PIN used to get disabled cards and no
            explanation, which read as broken. Say what is going on and how to get
            the access. */}
        {!isAdmin && (
          <div style={{ background: t.white06, border: `0.5px solid ${t.borderMid}`, borderRadius: 8, padding: "10px 12px", marginBottom: 12, color: t.textSecondary, fontSize: 12, lineHeight: 1.55 }}>
            <b style={{ color: t.textPrimary }}>View only.</b> Only the restaurant owner can create or change menus, so you can see them here but not edit them. To make changes, close Manager, tap the logo 5 times, and choose “Owner? Sign in with email.”
          </div>
        )}

        {activeMenus.length === 0 && !editingMenu && (
          <div style={{ color: t.textSecondary, fontSize: 13, marginBottom: 12 }}>
            {isAdmin ? "No menus yet. Create one to show lunch and dinner at different times, or the bar menu only in the bar." : "No menus yet. An owner can create them."}
          </div>
        )}

        {activeMenus.map(m => editingMenu && editingMenu.id === m.id ? (
          <MenuEditor key={m.id} menu={m} existingTypes={editableTypes} locationNames={locationNames}
            onCancel={() => setEditingMenu(null)}
            onSaved={() => { setEditingMenu(null); loadMenus(); flash("success", `${m.label} saved.`); }}
            onDeleted={d => { setEditingMenu(null); loadMenus(); loadItems(); flash("success", `${m.label} deleted.${d.archived ? ` ${d.archived} item(s) that were only on it moved to Removed items.` : ""}`); }} />
        ) : (
          <button key={m.id} type="button" disabled={!isAdmin} onClick={() => isAdmin && setEditingMenu(m)}
            style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", boxSizing: "border-box", textAlign: "left", background: t.white04, border: `0.5px solid ${t.borderMid}`, borderRadius: 8, padding: "11px 13px", marginBottom: 6, cursor: isAdmin ? "pointer" : "default", fontFamily: t.fontSerif }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: t.textPrimary, fontSize: 14 }}>{m.label}</div>
              <div style={{ color: t.textDim, fontSize: 11, marginTop: 2 }}>
                {MENU_TYPES.find(o => o.value === m.menuType)?.label}, {m.locations.length === 1 ? (locationNames[m.locations[0]] || m.locations[0]) : "both rooms"}
              </div>
              <div style={{ color: t.textSecondary, fontSize: 12, marginTop: 3 }}>{mmDescribeSchedule(m.schedule)}</div>
            </div>
            <div style={{ color: m.openNow ? t.success : t.textDim, fontSize: 11, whiteSpace: "nowrap" }}>{m.openNow ? "● Showing now" : "○ Not showing now"}</div>
          </button>
        ))}

        {isAdmin && (editingMenu && !editingMenu.id ? (
          <MenuEditor menu={{}} existingTypes={editableTypes} locationNames={locationNames}
            onCancel={() => setEditingMenu(null)}
            onSaved={saved => { setEditingMenu(null); loadMenus(); flash("success", `${saved.label} created. Add items to it below.`); }}
            onDeleted={() => {}} />
        ) : (
          <button type="button" style={{ ...s.quietBtn, marginTop: 4 }} onClick={() => setEditingMenu({})}>New menu</button>
        ))}
        {info.timezone && <div style={{ color: t.textVeryDim || t.textDim, fontSize: 10, marginTop: 10 }}>Times are in {info.timezone.replace(/_/g, " ")}.</div>}

        {retiredMenus.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 6 }}>No longer used</div>
            <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 8 }}>
              These categories now come from {info.posName}, so its menus and hours apply instead.
            </div>
            {retiredMenus.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, background: t.white04, borderRadius: 8, padding: "9px 12px", marginBottom: 6 }}>
                <div style={{ flex: 1, color: t.textDim, fontSize: 13 }}>{m.label}</div>
                {isAdmin && (
                  <button type="button" style={s.quietBtn} onClick={async () => {
                    try { await mmCall("deleteMenu", { id: m.id }); loadMenus(); flash("success", `${m.label} deleted.`); }
                    catch (e) { flash("error", e.message); }
                  }}>Delete</button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
      )}

      {/* ── Items ───────────────────────────────────────────── */}
      {only !== "menus" && (
      <section>
        <h2 style={s.sectionTitle}>{only === "items" ? "Add and remove items" : "Items"}</h2>
        <div style={s.sectionNote}>{only === "items"
          ? "Add an item, remove one, rename a section, or bring back a removed item. To change an item's name, description, prices or sections, open it in Edit Menu Items."
          : "Fix a price or description, 86 an item, or move it between menus. Changes show to guests right away. To add a whole menu at once, upload it from Update Menu."}</div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {editableCats.map(c => (
            <button key={c.id} type="button" style={s.chip(c.id === catId)} onClick={() => { setCatId(c.id); setSearch(""); setShowArchive(false); setArchive(null); }}>{c.label}</button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input type="search" placeholder={`Search ${cat ? cat.label.toLowerCase() : "items"}…`} value={search} onChange={e => setSearch(e.target.value)} style={{ ...s.input, borderRadius: 20, flex: 1 }} />
          <button type="button" style={s.primaryBtn(false)} onClick={() => setEditingItem("new")}>Add</button>
        </div>

        {editingItem === "new" && cat && (
          <MenuItemEditor key={`new-${catId}`} cat={cat} item={null} menus={activeMenus} isAdmin={isAdmin}
            onCancel={() => setEditingItem(null)}
            onSaved={(it, warnings) => { setItems(list => [...(list || []), it]); setEditingItem(null); flash(warnings.length ? "warning" : "success", [`${it.name} added.`, ...warnings].join(" ")); }}
            onRemoved={() => {}} />
        )}

        {itemsError && <MmNotice kind="error">{itemsError}</MmNotice>}
        {items === null && !itemsError && <div style={{ color: t.textDim, textAlign: "center", padding: 24 }}>Loading…</div>}
        {items && items.length === 0 && editingItem !== "new" && (
          <div style={{ color: t.textSecondary, fontSize: 13, padding: "8px 0 16px" }}>No {cat?.label.toLowerCase()} items yet. Add one, or upload a file from Update Menu.</div>
        )}
        {items && items.length > 0 && visible.length === 0 && (
          <div style={{ color: t.textSecondary, fontSize: 13, padding: "8px 0 16px" }}>Nothing matches "{search}".</div>
        )}

        {groups.map(g => (
          <div key={g.name} style={{ marginBottom: 14 }}>
            {/* Food headings are courses, which the pairing screen treats specially
                (Entrees and Dessert behave differently), so a free-text rename is
                offered only where the heading is a plain section name. */}
            {cat.groupField === "subgroup" && renamingGroup === g.name ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, paddingBottom: 6, borderBottom: `0.5px solid ${t.borderSubtle}` }}>
                <input autoFocus value={renameValue} disabled={renameBusy}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") renameSection(g.name, renameValue); if (e.key === "Escape") setRenamingGroup(null); }}
                  aria-label={`New name for ${g.name}`} placeholder="Section name"
                  style={{ ...s.input, padding: "6px 9px", flex: 1, minWidth: 0 }} />
                <button type="button" disabled={renameBusy || !renameValue.trim()} onClick={() => renameSection(g.name, renameValue)} style={s.primaryBtn(renameBusy || !renameValue.trim())}>{renameBusy ? "Saving…" : "Save"}</button>
                <button type="button" disabled={renameBusy} onClick={() => setRenamingGroup(null)} style={s.quietBtn}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10, color: t.accent, fontSize: 12, marginBottom: 6, paddingBottom: 5, borderBottom: `0.5px solid ${t.borderSubtle}` }}>
                <span>{g.name}</span>
                {cat.groupField === "subgroup" && (
                  <button type="button" onClick={() => { setRenamingGroup(g.name); setRenameValue(g.name === "Other" ? "" : g.name); }}
                    aria-label={`Rename section ${g.name}`}
                    style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11, padding: "2px 4px", textDecoration: "underline" }}>
                    Rename
                  </button>
                )}
              </div>
            )}
            {g.items.map(it => {
              const out = it.available === false;
              const onMenus = (it.menuGuids || []).map(menuName).filter(Boolean);
              const unassigned = cat.kind === "food" && onMenus.length === 0 && menuCountByType(cat.menuType) > 0;
              if (editingItem === it.id) {
                return <MenuItemEditor key={it.id} cat={cat} item={it} menus={activeMenus} isAdmin={isAdmin}
                  onCancel={() => setEditingItem(null)}
                  onSaved={(saved, warnings) => { setItems(list => list.map(x => (x.id === saved.id ? saved : x))); setEditingItem(null); flash(warnings.length ? "warning" : "success", [`${saved.name} saved.`, ...warnings].join(" ")); }}
                  onRemoved={removed => { setItems(list => list.filter(x => x.id !== removed.id)); setEditingItem(null); if (archive) setArchive(a => [removed, ...a]); flash("success", `${removed.name} removed. Restore it from Removed items if that was a mistake.`); }} />;
              }
              return (
                <button key={it.id} type="button" onClick={() => setEditingItem(it.id)}
                  style={{ display: "flex", gap: 12, width: "100%", boxSizing: "border-box", textAlign: "left", background: t.white04, border: `0.5px solid ${out ? t.errorBorder : t.borderMid}`, borderRadius: 8, padding: "10px 12px", marginBottom: 6, cursor: "pointer", fontFamily: t.fontSerif, opacity: out ? 0.7 : 1 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: t.textPrimary, fontSize: 14 }}>{it.name}</div>
                    {it.description && <div style={{ color: t.textDim, fontSize: 12, fontStyle: "italic", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.description}</div>}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4, fontSize: 11 }}>
                      {out && <span style={{ color: t.error }}>86'd</span>}
                      {onMenus.length > 0 && <span style={{ color: t.textSecondary }}>{onMenus.join(", ")}</span>}
                      {unassigned && <span style={{ color: t.warning }}>Not on a menu</span>}
                      {it.excludeFromPairing && <span style={{ color: t.textDim }}>No pairings</span>}
                      {it.managerEdits && <span style={{ color: t.textDim }}>Edited</span>}
                    </div>
                  </div>
                  <div style={{ color: t.textSecondary, fontSize: 13, whiteSpace: "nowrap" }}>{mmPriceText(it, cat.kind)}</div>
                </button>
              );
            })}
          </div>
        ))}

        {items && (
          <div style={{ marginTop: 18 }}>
            <button type="button" style={s.quietBtn} onClick={() => setShowArchive(v => !v)}>
              {showArchive ? "Hide removed items" : "Show removed items"}
            </button>
            {showArchive && archive === null && <div style={{ color: t.textDim, fontSize: 12, marginTop: 10 }}>Loading…</div>}
            {showArchive && archive && archive.length === 0 && <div style={{ color: t.textDim, fontSize: 12, marginTop: 10 }}>Nothing removed.</div>}
            {showArchive && archive && archive.map(a => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, background: t.white04, borderRadius: 8, padding: "9px 12px", marginTop: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: t.textSecondary, fontSize: 13 }}>{a.name}</div>
                  <div style={{ color: t.textDim, fontSize: 11 }}>{a.archivedAt ? `Removed ${new Date(a.archivedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : "Removed"}{a.archivedReason && a.archivedReason !== "deleted by manager" ? ` (${a.archivedReason})` : ""}</div>
                </div>
                <button type="button" style={s.quietBtn} onClick={() => restore(a)}>Restore</button>
              </div>
            ))}
          </div>
        )}
      </section>
      )}
    </div>
  );
}

// ─── Menu upload: pick files → Claude reads them → review → approve ─────────
//
// Nothing reaches guests until Approve. The review compares every item with
// what is already on the menu (matching runs on the server, see
// matchMenuItems): exact matches are automatic, similar names must be
// confirmed, and a field a manager edited by hand is kept unless the reviewer
// chooses the file's version.

const UPLOAD_ACCEPT = ".pdf,.ai,.jpg,.jpeg,.png,.webp,.heic,.heif,.docx,.txt,.csv,application/pdf,image/*";
const UPLOAD_MAX_B64 = 9.3 * 1024 * 1024; // the function's request limit is 10 MB
const UPLOAD_CAT_LABEL = { wines: "Wine", foodItems: "Food", beers: "Beer", pours: "Spirits", cocktails: "Cocktails", nab: "Non-alcoholic" };
const UPLOAD_CAT_KIND = { wines: "wine", foodItems: "food", beers: "bev", pours: "bev", cocktails: "bev", nab: "bev" };
// menuManager category -> the triggerEnrichment category that researches it.
const MM_RESEARCH_CAT = { wines: "wine", beers: "beer", pours: "pours" };

// Researches every unresearched item in one category, in passes, until a pass
// finds nothing left. triggerEnrichment does up to 25 per call and only picks up
// items with no research yet, so wines already on the list are never re-done:
// this is what makes "replace the wine list" cost only the NEW wines.
// onProgress({ running, done, msg }). Runs while the app is open; a pass that
// is cut off is simply picked up by the next.
async function runResearchPasses(cat, onProgress = () => {}) {
  onProgress({ running: true, done: 0, msg: "Starting…" });
  let total = 0;
  for (let pass = 1; pass <= 12; pass++) {
    try {
      const res = await rfetch(TRIGGER_ENRICHMENT_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ category: cat }) });
      const d = await res.json();
      if (d.error) { onProgress({ running: false, done: total, msg: `Stopped: ${d.error}` }); return total; }
      const n = Number(d.enriched ?? d.enrichedCount ?? 0); total += n;
      onProgress({ running: true, done: total, msg: `${total} researched…` });
      if (n === 0) break;
    } catch (e) {
      onProgress({ running: true, done: total, msg: `Pass ${pass} interrupted, continuing…` });
    }
  }
  onProgress({ running: false, done: total, msg: `Done: ${total} researched. Approve them in Manage Items → Needs review.` });
  return total;
}

function fileToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = () => reject(new Error("Could not read the file."));
    r.readAsDataURL(blob);
  });
}

// Photos are shrunk in the browser: phone photos are 3–8 MB and the model
// reads at roughly 1500px anyway. Safari also converts iPhone HEIC here; other
// browsers cannot decode HEIC, which the server explains.
async function prepareUploadFile(file) {
  const name = file.name || "file";
  const isImage = /^image\//.test(file.type) || /\.(jpe?g|png|webp|heic|heif|gif)$/i.test(name);
  if (isImage) {
    try {
      const bmp = await createImageBitmap(file);
      const scale = Math.min(1, 2200 / Math.max(bmp.width, bmp.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(bmp.width * scale); canvas.height = Math.round(bmp.height * scale);
      canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.86));
      if (blob) return { name: name.replace(/\.(heic|heif|png|webp|gif)$/i, ".jpg"), type: "image/jpeg", data: await fileToBase64(blob) };
    } catch (e) { /* fall through: send as-is and let the server explain */ }
  }
  return { name, type: file.type || "", data: await fileToBase64(file) };
}

const UP_FIELDS = {
  wine: ["name", "section", "glassPrice", "bottlePrice"],
  food: ["name", "description", "section", "price"],
  bev: ["name", "description", "section", "price"],
};
const UP_LABEL = { name: "Name", description: "Description", section: "Section", price: "Price", glassPrice: "Glass", bottlePrice: "Bottle" };
const upMoney = v => (v === null || v === undefined || v === "" ? "—" : (formatMoney(v) || "—"));
const upStr = v => (v === null || v === undefined ? "" : String(v));
const upNum = v => { const s = String(v ?? "").replace(/[$,\s]/g, ""); if (s === "") return null; const n = Number(s); return Number.isFinite(n) && n >= 0 ? n : null; };
const upSame = (a, b, field) => (["price", "glassPrice", "bottlePrice"].includes(field)
  ? upNum(a) === upNum(b)
  : upStr(a).trim() === upStr(b).trim());

// Build the per-item review state from a draft and its match result.
function buildUploadRows(draft, matches, prev = {}) {
  return (draft.items || []).map(it => {
    const m = matches[it.key] || null;
    const pool = m && m.kind === "restore" ? draft.archived : draft.existing;
    const existing = m ? (((pool || {})[it.category] || {})[m.id] || null) : null;
    const before = prev[it.key];
    const sameMatch = before && before.matchId === (m ? m.id : null);
    const fields = { name: it.name, description: upStr(it.description), section: upStr(it.section), price: upStr(it.price), glassPrice: upStr(it.glassPrice), bottlePrice: upStr(it.bottlePrice) };
    // An exact match keeps the name already on the menu: the file's
    // capitalisation or punctuation is not a reason to rename it.
    if (existing && m.kind === "exact") fields.name = existing.name;
    return {
      key: it.key, category: it.category, item: it, match: m, existing,
      matchId: m ? m.id : null,
      include: before ? before.include : true,
      fields: before && before.category === it.category ? before.fields : fields,
      edited: before ? before.edited : [],
      choice: sameMatch ? before.choice : (!m ? "new" : m.kind === "fuzzy" ? null : "same"),
      keep: sameMatch ? before.keep : {},
      // "Looks right" on a flagged item (Nov 3, 2026): the manager checked it.
      checked: before ? !!before.checked : false,
    };
  });
}

// What approving this row would do, and which fields differ from the menu.
function uploadRowState(row) {
  const kind = UPLOAD_CAT_KIND[row.category];
  const fields = UP_FIELDS[kind];
  const asNew = !row.existing || row.choice === "different";
  const diffs = [], conflicts = [];
  if (!asNew) {
    fields.forEach(f => {
      if (f === "name" && row.match.kind === "exact") return;
      if (upSame(row.fields[f], row.existing[f], f)) return;
      diffs.push(f);
      if (row.existing.managerEdits && row.existing.managerEdits[f === "section" ? (kind === "food" ? "course" : "subgroup") : f]) conflicts.push(f);
    });
  }
  const status = !row.include ? "skip"
    : row.choice === null ? "confirm"
    : asNew ? "new"
    : row.match.kind === "restore" ? "restore"
    : diffs.length ? "changed" : "unchanged";
  return { kind, fields, asNew, diffs, conflicts, status };
}

// The review's per-menu plan. The server's shape is { mode, label, schedule,
// locations }; the form keeps the hours as editable fields.
function plansFromServer(serverPlans) {
  const out = {};
  Object.entries(serverPlans || {}).forEach(([k, p]) => {
    out[k] = {
      mode: (p && p.mode) || "none",
      label: p && p.mode === "create" ? (p.label || "") : "",
      existingLabel: p && p.mode === "existing" ? p.label : "",
      sched: scheduleToForm(p && p.mode === "create" ? p.schedule : null),
      locations: (p && p.locations) || [],
    };
  });
  return out;
}
function plansToServer(plans) {
  const out = {};
  Object.entries(plans || {}).forEach(([k, p]) => {
    out[k] = p.mode === "create" ? { mode: "create", label: p.label.trim(), schedule: formToSchedule(p.sched), locations: p.locations }
      : p.mode === "existing" ? { mode: "existing", label: p.existingLabel }
      : { mode: "none" };
  });
  return out;
}

// The review's "Preview the menu" view: the upload laid out the way guests read
// it, menu by menu, then by category and printed section, in printed order.
// Read-only - every fix happens in "Check items". Skipped rows are left out and
// anything coming off is listed at the end, struck through. Oct 27, 2026.
function UploadPreview({ states, removing = [], planLabel, multiMenu, complete }) {
  const s = useMmStyles(); const t = s.t;
  const tree = [];                       // [{ menu, cats: [{ cat, sections: [{ name, rows }] }] }]
  const find = (arr, key, make) => { let x = arr.find(a => a.key === key); if (!x) { x = make(); arr.push(x); } return x; };
  states.filter(x => x.st.status !== "skip").forEach(({ row, st }) => {
    const appears = (row.item.appearsOn && row.item.appearsOn.length) ? row.item.appearsOn : [{ menu: null, section: row.fields.section, order: 0 }];
    appears.forEach(a => {
      const menuName = (a.menu && planLabel(a.menu)) || (multiMenu ? "Not on a specific menu" : "");
      const m = find(tree, menuName, () => ({ key: menuName, menu: menuName, cats: [] }));
      const c = find(m.cats, row.category, () => ({ key: row.category, cat: row.category, sections: [] }));
      const secName = (row.edited.includes("section") ? row.fields.section : (a.section || row.fields.section)) || "";
      const sec = find(c.sections, secName, () => ({ key: secName, name: secName, rows: [] }));
      sec.rows.push({ row, st, order: a.order || 0 });
    });
  });
  const price = (row, kind) => kind === "wine"
    ? [upNum(row.fields.glassPrice) !== null && `${upMoney(row.fields.glassPrice)} glass`, upNum(row.fields.bottlePrice) !== null && `${upMoney(row.fields.bottlePrice)} bottle`].filter(Boolean).join(" · ")
    : (upNum(row.fields.price) !== null ? upMoney(row.fields.price) : "");
  const tag = { new: ["New", t.success], changed: ["Changed", t.accent], restore: ["Back", t.accent], confirm: ["Same item?", t.warning] };

  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
        {complete
          ? "The whole menu as guests will see it, in printed order."
          : "What's in this upload, in printed order. Items already on your menus that aren't in it stay as they are."}
        {" "}Wine, beer and spirit descriptions come from SommSync's research after you publish.
      </div>
      {tree.map(m => (
        <div key={m.key || "_"} style={{ background: t.bgCard, border: `0.5px solid ${t.borderMid}`, borderRadius: 10, padding: "14px 16px", marginBottom: 12 }}>
          {m.menu && <div style={{ color: t.textPrimary, fontSize: 17, fontFamily: t.fontSerif, marginBottom: 10 }}>{m.menu}</div>}
          {m.cats.map(c => (
            <div key={c.key} style={{ marginBottom: 10 }}>
              {m.cats.length > 1 && <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 6 }}>{UPLOAD_CAT_LABEL[c.cat]}</div>}
              {c.sections.map(sec => (
                <div key={sec.key || "_"} style={{ marginBottom: 10 }}>
                  {sec.name && <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", margin: "6px 0" }}>{sec.name}</div>}
                  {sec.rows.sort((a, b) => a.order - b.order).map(({ row, st }) => {
                    const p = price(row, st.kind);
                    const tg = tag[st.status];
                    return (
                      <div key={row.key} style={{ padding: "5px 0", borderBottom: `0.5px solid ${t.borderSubtle}` }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                          <div style={{ flex: 1, minWidth: 0, color: t.textPrimary, fontSize: 14, fontFamily: t.fontSerif }}>
                            {row.fields.name}
                            {tg && <span style={{ color: tg[1], fontSize: 10, marginLeft: 8 }}>{tg[0]}</span>}
                          </div>
                          <div style={{ color: p ? t.textSecondary : t.warning, fontSize: 12, whiteSpace: "nowrap" }}>{p || "No price"}</div>
                        </div>
                        {st.kind !== "wine" && upStr(row.fields.description).trim() && (
                          <div style={{ color: t.textDim, fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>{row.fields.description}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
      {tree.length === 0 && <div style={{ color: t.textDim, fontSize: 12, marginBottom: 12 }}>Every item is set to not publish.</div>}
      {removing.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 6 }}>Coming off</div>
          {removing.map(r => (
            <div key={`${r.category}:${r.id}:${r.menuId}`} style={{ color: t.textDim, fontSize: 13, padding: "2px 0" }}>
              <span style={{ textDecoration: "line-through" }}>{r.name}</span> <span style={{ fontSize: 11 }}>({r.menuLabel})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Props added Oct 27, 2026 for Your menus (MenusHub):
//   target     { label } - "Replace this menu". The review opens already set to
//              add to that menu and treat the upload as the whole menu, so what
//              is missing from the file is listed as coming off.
//   resumeId   open this waiting draft straight away.
//   showDrafts the "Waiting for review" list on the pick screen; the hub lists
//              drafts itself.
//   onDone     shows "Back to your menus" on the published screen.
//   footer     shown under the published summary (the POS sync nudge).
// Left out, it behaves as it always has on the Update menu screen.
function MenuUploadPanel({ onApplied, onDone = null, locationNames = { bar: "Bar", dining: "Dining Room" }, target = null, resumeId = null, showDrafts = true, footer = null, canRemoveMenus = false }) {
  const s = useMmStyles(); const t = s.t;
  const [stage, setStage] = useState("pick");        // pick | reading | review | done
  // The pages to read, in order (Nov 1, 2026): a new file, or a page already
  // kept on the server ({ sourceId | draftId, index }) - so one page can be
  // swapped, or missing pages added, without re-photographing the rest.
  //   { id, kind: "new", file } | { id, kind: "kept", ref, name, contentType, url }
  const [pages, setPages] = useState([]);
  const [replacingDraftId, setReplacingDraftId] = useState(null);   // re-read of a draft: discard it once the new read succeeds
  const [thumbs, setThumbs] = useState({});                         // page id -> object URL for new photos
  const files = pages;   // older code paths count `files`
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [review, setReview] = useState(null);         // { draft, menus, assignments, complete, removed }
  const [rows, setRows] = useState([]);
  const [removeIds, setRemoveIds] = useState({});
  const [filter, setFilter] = useState("attention");
  const [openKey, setOpenKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [research, setResearch] = useState({});
  const [view, setView] = useState("review");         // review | preview (how guests will see it)
  const [targetNote, setTargetNote] = useState(null);
  // Replace-by-file: menus the old file produced that the new one doesn't
  // map to, and which of them the owner chose to remove on publish.
  const [removeMenus, setRemoveMenus] = useState({});
  // Headings step (Nov 3, 2026): renames the manager chose, per drinks
  // category: { pours: { "Premium Pours – Tequila": "Tequila" } }.
  const [lastSkipped, setLastSkipped] = useState(null);         // row key just set to "don't publish", for Undo
  const [headingRenames, setHeadingRenames] = useState({});
  // Undo steps back one change per heading (a rename after "Use the short
  // headings" undoes to the short heading, not to the printed one).
  const [headingHistory, setHeadingHistory] = useState({});     // "cat|heading" -> earlier values
  const [editingHeading, setEditingHeading] = useState(null);   // "cat|heading"
  const [headingDraft, setHeadingDraft] = useState("");
  const autoResearched = useRef(null);
  const inputRef = useRef(null);

  const loadDrafts = useCallback(() => {
    mmCall("listDrafts").then(d => setDrafts((d.drafts || []).filter(x => x.status === "ready"))).catch(() => {});
  }, []);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);
  useEffect(() => { if (resumeId) resume(resumeId); }, [resumeId]);
  useEffect(() => {
    if (stage !== "reading") return;
    const start = Date.now(); setElapsed(0);
    const id = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [stage]);

  function openReview(data) {
    setHeadingRenames({}); setHeadingHistory({}); setEditingHeading(null);
    setReview({ draft: data.draft, menus: data.menus || [], plans: plansFromServer(data.plans), complete: !!data.complete, removed: data.removed || [], removalStats: data.removalStats || [] });
    const built = buildUploadRows(data.draft, data.matches || {});
    setRows(built);
    setRemoveIds(Object.fromEntries((data.removed || []).map(r => [`${r.category}:${r.id}:${r.menuId}`, true])));
    const needs = built.filter(r => { const st = uploadRowState(r); return st.status === "confirm" || r.item.uncertain || st.conflicts.length || (r.match && r.match.kind === "vintage"); }).length;
    setFilter(needs ? "attention" : "all");
    setOpenKey(null);
    setView("review");
    setStage("review");
  }

  // "Replace Dinner": point the menu in the file at Dinner and make it the
  // whole menu, before the review opens. One rematch round trip; the server
  // re-derives everything from the plan, so nothing here is trusted.
  async function retarget(data) {
    const dms = (data.draft && data.draft.menus) || [];
    if (!target || !dms.length) return data;
    const norm = x => String(x || "").trim().toLowerCase().replace(/\s+/g, " ");
    // "Replace the whole file": every menu the old file produced, matched by
    // name to the menus in the new one. Anything left over on either side is
    // shown: new menus to create, old ones not in the new file.
    if (target.source) {
      const labels = target.source.labels || [];
      const plans = { ...(data.plans || {}) };
      const used = new Set();
      dms.forEach(dm => {
        const hit = dm.title && labels.find(l => norm(l) === norm(dm.title) && !used.has(norm(l)));
        if (hit) { plans[dm.key] = { mode: "existing", label: hit }; used.add(norm(hit)); }
      });
      if (labels.length === 1 && dms.length === 1 && !used.size) { plans[dms[0].key] = { mode: "existing", label: labels[0] }; used.add(norm(labels[0])); }
      setTargetNote(`This replaces ${(target.source.fileNames || []).join(", ") || "the old file"} (${labels.join(", ")}). Check how each menu below lines up.`);
      setRemoveMenus({});
      const r = await mmCall("rematchDraft", { id: data.draft.id, plans, complete: true });
      return { ...data, plans: r.plans || plans, complete: true, matches: r.matches || {}, removed: r.removed || [], removalStats: r.removalStats || [] };
    }
    const titled = dms.find(m => m.title && norm(m.title) === norm(target.label));
    const pick = dms.length === 1 ? dms[0] : (titled || dms[0]);
    if (dms.length > 1) {
      setTargetNote(titled
        ? `This upload has ${dms.length} menus. "${pick.title}" replaces ${target.label}; check what the others should be below.`
        : `This upload has ${dms.length} menus. The first is set to replace ${target.label}; check that's right below.`);
    } else setTargetNote(null);
    const plans = { ...(data.plans || {}), [pick.key]: { mode: "existing", label: target.label } };
    const r = await mmCall("rematchDraft", { id: data.draft.id, plans, complete: true });
    return { ...data, plans: r.plans || plans, complete: true, matches: r.matches || {}, removed: r.removed || [], removalStats: r.removalStats || [] };
  }

  async function read() {
    if (!pages.length) return;
    setError(null); setStage("reading");
    try {
      const payload = [];
      let total = 0;
      for (const pg of pages) {
        if (pg.kind === "kept") { payload.push({ keep: pg.ref }); continue; }
        const f = await prepareUploadFile(pg.file);
        total += f.data.length;
        payload.push({ file: f });
      }
      if (total > UPLOAD_MAX_B64) throw new Error(`Those files add up to ${(total / 1.37 / 1048576).toFixed(1)} MB. Upload up to about 7 MB at a time, for example the wine list and the food menu separately.`);
      const extracted = await mmCall("extractUpload", { pages: payload, ...(target && target.source ? { sourceId: target.source.id } : {}) });
      if (replacingDraftId) { mmCall("discardDraft", { id: replacingDraftId }).catch(() => {}); setReplacingDraftId(null); }
      const data = await retarget(extracted);
      setPages([]);
      openReview(data);
    } catch (e) { setError(e.message); setStage("pick"); }
  }

  // Kept pages of a source (the menu's current file) or of a draft (the upload
  // being reviewed), loaded into the page list as "kept" pages.
  async function loadKeptPages(ref) {
    setError(null); setBusy(true);
    try {
      const r = await mmCall("getSourcePages", ref);
      const only = ref.sourceId && target && target.source && Array.isArray(target.source.fileIndexes) ? target.source.fileIndexes : null;
      setPages((r.pages || []).filter(pg => !only || only.includes(pg.index)).map(pg => ({ id: `k${pg.index}-${Math.random().toString(36).slice(2, 7)}`, kind: "kept",
        ref: { ...ref, index: pg.index }, name: pg.name, contentType: pg.contentType, url: pg.url })));
    } catch (e) { setError(e.message); }
    setBusy(false);
  }
  // "Add the missing pages" from the review: back to the page list with this
  // upload's pages kept; the old upload is discarded once the new read succeeds.
  async function addMissingPages() {
    const draftId = review.draft.id;
    setReplacingDraftId(draftId);
    setReview(null); setStage("pick");
    await loadKeptPages({ draftId });
  }
  const movePage = (i, d) => setPages(ps => { const a = [...ps]; const j = i + d; if (j < 0 || j >= a.length) return ps; [a[i], a[j]] = [a[j], a[i]]; return a; });

  // Thumbnails for new photos (object URLs, released when the list changes).
  useEffect(() => {
    if (typeof URL === "undefined" || !URL.createObjectURL) return;
    const made = {};
    // A thumbnail is a nicety: if the browser can't make one, show the tile.
    pages.forEach(pg => { if (pg.kind === "new" && /^image\//.test(pg.file.type || "")) { try { made[pg.id] = URL.createObjectURL(pg.file); } catch (e) {} } });
    setThumbs(made);
    return () => Object.values(made).forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} });
  }, [pages]);

  async function resume(id) {
    setError(null); setBusy(true);
    try { openReview(await mmCall("getDraft", { id })); }
    catch (e) { setError(e.message); loadDrafts(); }
    setBusy(false);
  }

  async function discard(id) {
    if (!window.confirm("Discard this upload? Nothing from it has been published.")) return;
    try { await mmCall("discardDraft", { id }); } catch (e) {}
    if (review && review.draft.id === id) { setReview(null); setStage("pick"); }
    loadDrafts();
  }

  // Only a change of WHICH menus are targeted (or "whole menu") can change what
  // comes off, so names and hours are edited locally without a round trip.
  const setPlan = (key, patch) => setReview(r => ({ ...r, plans: { ...r.plans, [key]: { ...r.plans[key], ...patch } } }));
  async function rematch(plans, complete) {
    setBusy(true);
    setReview(r => ({ ...r, plans, complete }));
    try {
      const data = await mmCall("rematchDraft", { id: review.draft.id, plans: plansToServer(plans), complete });
      setReview(r => ({ ...r, removed: data.removed || [], removalStats: data.removalStats || [] }));
      setRows(prevRows => buildUploadRows(review.draft, data.matches || {}, Object.fromEntries(prevRows.map(r => [r.key, r]))));
      setRemoveIds(Object.fromEntries((data.removed || []).map(r => [`${r.category}:${r.id}:${r.menuId}`, true])));
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  // ── Headings after publishing ─────────────────────────────────────────────
  // For each drinks category this upload touches: every heading the category
  // will have (existing items not in the upload keep theirs; uploaded items
  // bring the file's), with counts. Shown when the upload brings a heading the
  // category doesn't have yet. Food headings are courses per menu: not here.
  // Our best guess is offered, never forced: headings differing only in
  // capitalisation are merged automatically; "Premium Pours – Tequila" style
  // headings (page title + heading) can be shortened in one tap; a heading
  // close to an existing one ("Brandy" / "Cognac") gets a one-tap suggestion.
  const HEADING_CATS = ["wines", "beers", "pours", "cocktails", "nab"];
  const headingTail = h => { const parts = String(h).split(/\s+[–—-]\s+/); return parts[parts.length - 1].trim(); };
  const HEADING_KIN = { brandy: "cognac", cognac: "brandy", whisky: "whiskey", whiskey: "whisky", liqueurs: "cordials", cordials: "liqueurs" };
  function headingPlans() {
    if (!review) return [];
    const out = [];
    HEADING_CATS.forEach(cat => {
      const catRows = rows.filter(r => r.category === cat);
      if (!catRows.length) return;
      const live = (review.draft.existing || {})[cat] || {};
      const before = [...new Set(Object.values(live).map(it => String((it && it.subgroup) || "").trim()).filter(Boolean))];
      const counts = new Map(), touched = new Set();
      const add = h => { const k = String(h || "").trim(); if (k) counts.set(k, (counts.get(k) || 0) + 1); };
      catRows.forEach(r => {
        const st = uploadRowState(r);
        if (st.status === "skip") return;
        if (!st.asNew && r.existing) touched.add(r.existing.id);
        const keepMine = !st.asNew && st.conflicts.includes("section") && r.keep.section !== "file";
        add(keepMine ? r.existing.subgroup : r.fields.section);
      });
      Object.values(live).forEach(it => { if (it && !touched.has(it.id)) add(it.subgroup); });
      const names = [...counts.keys()];
      const fresh = names.filter(h => !before.includes(h));
      if (!fresh.length) return;
      // Automatic: same heading in another capitalisation -> the existing spelling.
      const auto = {};
      fresh.forEach(h => { const e = before.find(b => b.toLowerCase() === h.toLowerCase()); if (e) auto[h] = e; });
      const ren = { ...auto, ...(headingRenames[cat] || {}) };
      const finalOf = h => ren[h] || h;
      // One tap: drop the page title ("Premium Pours – Tequila" -> "Tequila")
      // when headings would then line up with each other or an existing one.
      const shortable = names.filter(h => headingTail(h) !== h);
      const tails = shortable.map(headingTail);
      const shortUseful = shortable.length > 0 && (new Set(tails).size < tails.length || shortable.some(h => names.includes(headingTail(h)))
        || shortable.some(h => names.some(o => o !== h && headingTail(o) === headingTail(h))));
      // A heading next to a kindred existing one (Brandy / Cognac).
      const kin = {};
      fresh.forEach(h => {
        const tl = headingTail(finalOf(h)).toLowerCase();
        const mate = names.find(o => o !== h && !fresh.includes(o) && HEADING_KIN[tl] === headingTail(finalOf(o)).toLowerCase());
        if (mate) kin[h] = finalOf(mate);
      });
      out.push({ cat, names, counts, before, fresh, ren, auto, finalOf, shortable, shortUseful, kin });
    });
    return out;
  }
  const applyRename = (r, cat, from, to) => {
    const m = { ...(r[cat] || {}) };
    if (!to || to === from) delete m[from]; else m[from] = to;
    return { ...r, [cat]: m };
  };
  // A change: remember what it was before (for Undo), then apply.
  const setRename = (cat, from, to) => {
    const before = (headingRenames[cat] || {})[from] || null;
    if ((to || null) === before) return;
    setHeadingHistory(hs => ({ ...hs, [`${cat}|${from}`]: [...(hs[`${cat}|${from}`] || []), before] }));
    setHeadingRenames(r => applyRename(r, cat, from, to));
  };
  const renameMany = (cat, pairs) => pairs.forEach(([from, to]) => setRename(cat, from, to));
  const undoRename = (cat, from) => {
    const key = `${cat}|${from}`;
    const stack = headingHistory[key] || [];
    if (!stack.length) return;
    setHeadingHistory(hs => ({ ...hs, [key]: stack.slice(0, -1) }));
    setHeadingRenames(r => applyRename(r, cat, from, stack[stack.length - 1]));
  };

  function missingMenuLabels() {
    if (!target || !target.source || !review) return [];
    const mapped = new Set(Object.values(review.plans || {}).filter(p => p && p.mode === "existing").map(p => mmNormLabel(p.label)));
    return (target.source.labels || []).filter(l => !mapped.has(mmNormLabel(l)));
  }
  const updateRow = (key, patch) => setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const setField = (row, f, v) => updateRow(row.key, { fields: { ...row.fields, [f]: v }, edited: row.edited.includes(f) ? row.edited : [...row.edited, f] });

  async function approve() {
    setBusy(true); setError(null);
    try {
      const items = rows.map(row => {
        const st = uploadRowState(row);
        if (st.status === "skip") return { key: row.key, category: row.category, action: "skip" };
        const f = {};
        st.fields.forEach(k => {
          // "Keep mine" sends the menu's current value, which the server sees
          // as no change, so the manager's edit and its record both survive.
          const keepMine = !st.asNew && st.conflicts.includes(k) && row.keep[k] !== "file";
          const v = keepMine ? row.existing[k] : row.fields[k];
          f[k] = ["price", "glassPrice", "bottlePrice"].includes(k) ? upNum(v) : (upStr(v).trim() || null);
        });
        return {
          key: row.key, category: row.category,
          action: st.asNew ? "create" : row.match.kind === "restore" ? "restore" : "update",
          targetId: st.asNew ? null : row.match.id,
          vintageChange: !st.asNew && row.match.kind === "vintage",
          extractedName: row.item.name,
          fields: f,
          reviewEdited: row.edited.filter(k => !(!st.asNew && st.conflicts.includes(k) && row.keep[k] !== "file")),
          sideOrAddOn: row.item.sideOrAddOn === true,
        };
      });
      const removals = review.complete ? review.removed.filter(r => removeIds[`${r.category}:${r.id}:${r.menuId}`]).map(r => ({ category: r.category, id: r.id, menuId: r.menuId })) : [];
      // Headings: uploaded items go with the new heading; the same renames are
      // sent so items already on the app under that heading move with it.
      const headingRenamesOut = {};
      headingPlans().forEach(hp => {
        const m = {};
        hp.names.forEach(h => { const f = hp.finalOf(h); if (f && f !== h) m[h] = f; });
        if (Object.keys(m).length) headingRenamesOut[hp.cat] = m;
      });
      items.forEach(it => {
        const m = headingRenamesOut[it.category];
        if (m && it.fields && it.fields.section && m[it.fields.section]) it.fields.section = m[it.fields.section];
      });
      const data = await mmCall("approveDraft", { id: review.draft.id, complete: review.complete, plans: plansToServer(review.plans), items, removals, headingRenames: headingRenamesOut });
      // Menus from the old file that aren't in the new one, which the owner
      // chose to remove. Done after publishing, with the ordinary deleteMenu
      // (items only on that menu are archived, never lost).
      const removedMenus = [];
      for (const label of missingMenuLabels().filter(l => removeMenus[l])) {
        const ids = (review.menus || []).filter(m => !m.retired && mmNormLabel(m.label) === mmNormLabel(label)).map(m => m.id);
        try { for (const id of ids) await mmCall("deleteMenu", { id }); removedMenus.push(label); }
        catch (e) { (data.warnings = data.warnings || []).push(`Couldn't remove the ${label} menu: ${e.message}`); }
      }
      setResult({ ...data, removedMenus }); setStage("done"); loadDrafts();
      if (onApplied) onApplied();
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function runResearch(cat) {
    return runResearchPasses(cat, p => setResearch(r => ({ ...r, [cat]: p })));
  }

  // Research starts on its own once a menu is published. It used to wait for a
  // "Research now" tap on this screen, which was easy to miss - and until it
  // runs, new wines, beers and spirits are invisible to guests. Only items with
  // no research yet are picked up, so this costs only what is actually new.
  // One category at a time, so the passes do not race each other.
  useEffect(() => {
    if (stage !== "done" || !result || autoResearched.current === result) return;
    autoResearched.current = result;
    const ne = result.needsEnrichment || {};
    const cats = ["wine", "beer", "pours"].filter(c => Number(ne[c]) > 0);
    if (!cats.length) return;
    (async () => { for (const c of cats) await runResearch(c); })();
  }, [stage, result]);

  // ── Render ──────────────────────────────────────────────────────────────
  const box = { background: t.white06, border: `0.5px solid ${t.borderMid}`, borderRadius: 8, padding: 14, marginBottom: 16 };

  if (stage === "reading") {
    return (
      <div style={box}>
        <div style={{ color: t.textPrimary, fontSize: 14, marginBottom: 6 }}>Reading your menu… {elapsed}s</div>
        <div style={{ color: t.textDim, fontSize: 12, lineHeight: 1.5 }}>
          A one-page menu takes about 30 seconds; a long wine list can take three minutes. Keep this screen open.
        </div>
      </div>
    );
  }

  if (stage === "done" && result) {
    const sm = result.summary || {};
    const parts = [
      sm.created && `${sm.created} added`, sm.updated && `${sm.updated} updated`, sm.restored && `${sm.restored} brought back`,
      sm.removedFromMenu && `${sm.removedFromMenu} taken off this menu`, sm.archived && `${sm.archived} removed`,
      sm.unchanged && `${sm.unchanged} unchanged`,
    ].filter(Boolean);
    const ne = result.needsEnrichment || {};
    const researchCats = [["wine", ne.wine, "wines"], ["beer", ne.beer, "beers"], ["pours", ne.pours, "spirits"]].filter(([, n]) => n > 0);
    return (
      <div style={box}>
        <MmNotice kind="success">Published. {parts.join(", ")}.</MmNotice>
        {(result.removedMenus || []).length > 0 && (
          <MmNotice kind="success">Removed the {result.removedMenus.join(" and ")} menu{result.removedMenus.length === 1 ? "" : "s"}. Anything only on {result.removedMenus.length === 1 ? "it" : "them"} is in Removed items.</MmNotice>
        )}
        {(result.createdMenus || []).length > 0 && (() => {
          const labels = [...new Set(result.createdMenus.map(m => m.label))];
          const unscheduled = [...new Set(result.createdMenus.filter(m => !m.scheduled).map(m => m.label))];
          return (
            <MmNotice kind={unscheduled.length ? "warning" : "success"}>
              Created the {labels.join(" and ")} menu{labels.length === 1 ? "" : "s"}.
              {unscheduled.length > 0 && ` ${unscheduled.join(" and ")} ${unscheduled.length === 1 ? "shows" : "show"} all day until you set hours in Menus & hours → Hours & rooms.`}
            </MmNotice>
          );
        })()}
        {(result.warnings || []).map((w, i) => <MmNotice key={i} kind="warning">{w}</MmNotice>)}
        {researchCats.map(([cat, n, label]) => (
          <div key={cat} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1, color: t.textSecondary, fontSize: 12 }}>
              {research[cat]?.msg || `${n} ${label} need SommSync's research (description, region, label) before guests see them.`}
            </div>
            {research[cat] && !research[cat].running && research[cat].msg && research[cat].msg.startsWith("Stopped") && (
              <button type="button" style={s.quietBtn} onClick={() => runResearch(cat)}>Try again</button>
            )}
          </div>
        ))}
        {researchCats.some(([cat]) => research[cat]?.running) && (
          <div style={{ color: t.textDim, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
            Only items new to your list are researched; everything you already had keeps its description and label. Keep the app open until this finishes; you can leave this screen.
          </div>
        )}
        {footer}
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          {onDone && <button type="button" style={s.primaryBtn(false)} onClick={onDone}>Back to your menus</button>}
          {/* A replace is one job: "upload another" would replace the same menu again. */}
          {!target && <button type="button" style={s.quietBtn} onClick={() => { setResult(null); setReview(null); setResearch({}); setTargetNote(null); setStage("pick"); }}>Upload another</button>}
        </div>
      </div>
    );
  }

  if (stage === "review" && review) {
    const states = rows.map(r => ({ row: r, st: uploadRowState(r) }));
    const count = st => states.filter(x => x.st.status === st).length;
    const attention = x => x.st.status === "confirm" || (x.st.status !== "skip" && ((x.row.item.uncertain && !x.row.checked) || x.st.conflicts.length || (x.row.match && x.row.match.kind === "vintage")));
    const needN = states.filter(attention).length;
    const undecided = count("confirm");
    const shown = states.filter(x => filter === "all" ? true
      : filter === "attention" ? attention(x)
      : filter === "changed" ? (x.st.status === "changed" || x.st.status === "restore")
      : x.st.status === filter);
    const removing = review.complete ? review.removed.filter(r => removeIds[`${r.category}:${r.id}:${r.menuId}`]) : [];
    const publishN = states.filter(x => x.st.status !== "skip").length;
    const unnamed = Object.values(review.plans).some(p => p.mode === "create" && !p.label.trim());
    const planLabel = key => { const p = review.plans[key] || {}; return p.mode === "create" ? (p.label.trim() || "new menu") : p.mode === "existing" ? p.existingLabel : null; };

    return (
      <div style={box}>
        {error && <MmNotice kind="error">{error}</MmNotice>}
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
          <div style={{ color: t.textPrimary, fontSize: 15, flex: 1 }}>{target ? `Check the new ${target.label}` : "Check what we read"}</div>
          <button type="button" style={{ ...s.quietBtn, padding: "5px 10px" }} onClick={() => discard(review.draft.id)}>Discard</button>
        </div>
        <div style={{ color: t.textDim, fontSize: 11, marginBottom: 8 }}>
          {(review.draft.fileNames || []).join(", ")}
        </div>
        <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
          Nothing has changed for guests yet. Answer anything marked, fix what we misread, then publish.
          {" "}Items you already have keep their descriptions, photos and edits.
        </div>
        {targetNote && <MmNotice kind="warning">{targetNote}</MmNotice>}
        {review.complete && (review.removalStats || []).filter(st => st.removing >= 3 && st.removing / st.total > 0.4).map(st => (
          <div key={st.menuId} style={{ background: t.warningDim, border: `0.5px solid ${t.warningBorder}`, borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
            <div style={{ color: t.textPrimary, fontSize: 13, marginBottom: 6, lineHeight: 1.5 }}>
              This would take off {st.removing} of {st.total} items on {st.label}. Is a page missing?
            </div>
            {(review.draft.files || []).length > 0 && (
              <button type="button" style={{ ...s.quietBtn, padding: "6px 12px" }} onClick={addMissingPages}>Add the missing pages</button>
            )}
          </div>
        ))}
        {missingMenuLabels().map(label => (
          <div key={label} style={{ background: t.warningDim, border: `0.5px solid ${t.warningBorder}`, borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
            <div style={{ color: t.textPrimary, fontSize: 13, marginBottom: 4 }}>{label} isn't in the new file.</div>
            {canRemoveMenus ? (
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", color: t.textSecondary, fontSize: 12, lineHeight: 1.5, cursor: "pointer" }}>
                <input type="checkbox" checked={!!removeMenus[label]} onChange={e => setRemoveMenus(m => ({ ...m, [label]: e.target.checked }))} style={{ marginTop: 2 }} />
                <span>Remove the {label} menu when I publish. Leave this off if you now print it separately; it stays as it is.</span>
              </label>
            ) : (
              <div style={{ color: t.textDim, fontSize: 12, lineHeight: 1.5 }}>It stays as it is. An owner can remove it in Menus &amp; hours if it's no longer served.</div>
            )}
          </div>
        ))}
        {(review.draft.skippedPosOwned || []).length > 0 && (
          <MmNotice kind="warning">Left out: {review.draft.skippedPosOwned.map(c => UPLOAD_CAT_LABEL[c]).join(", ")} come from your POS.</MmNotice>
        )}

        {/* The menus in this upload: name them, set their hours, or add to an
            existing menu. Pre-filled only where the files made it obvious. */}
        <div style={{ color: t.textPrimary, fontSize: 13, margin: "4px 0 8px" }}>
          {review.draft.menus.length > 1 ? `${review.draft.menus.length} menus in this upload` : "Menu"}
        </div>
        {review.draft.menus.map((dm, i) => {
          const p = review.plans[dm.key] || { mode: "none" };
          const count = rows.filter(r => (r.item.appearsOn || []).some(a => a.menu === dm.key)).length;
          const existingLabels = [...new Set(review.menus.map(m => m.label))];
          const existing = review.menus.find(m => m.label === p.existingLabel);
          const choose = mode => rematch({ ...review.plans, [dm.key]: { ...p, mode, existingLabel: mode === "existing" ? (p.existingLabel || existingLabels[0] || "") : p.existingLabel } },
            review.complete || (mode === "existing" && !Object.values(review.plans).some(x => x.mode === "existing")));
          return (
            <div key={dm.key} style={{ background: t.white04, border: `0.5px solid ${t.borderMid}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 8 }}>
                {review.draft.menus.length > 1 ? `Menu ${i + 1}: ` : ""}{count} item{count === 1 ? "" : "s"}
                {dm.servedHours ? `. The menu says: ${dm.servedHours}` : ""}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                <button type="button" style={s.chip(p.mode === "create")} disabled={busy} onClick={() => choose("create")}>New menu</button>
                {existingLabels.length > 0 && <button type="button" style={s.chip(p.mode === "existing")} disabled={busy} onClick={() => choose("existing")}>Add to an existing menu</button>}
                <button type="button" style={s.chip(p.mode === "none")} disabled={busy} onClick={() => choose("none")}>No specific menu</button>
              </div>
              {p.mode === "create" && (
                <>
                  <label style={s.label} htmlFor={`up-menu-${dm.key}`}>Menu name</label>
                  <input id={`up-menu-${dm.key}`} style={{ ...s.input, marginBottom: 12 }} value={p.label} placeholder="Lunch, Dinner, Brunch…"
                    onChange={e => setPlan(dm.key, { label: e.target.value })} />
                  <label style={s.label}>Shown in</label>
                  <RoomChoice locations={p.locations} locationNames={locationNames} onChange={locations => setPlan(dm.key, { locations })} />
                  <ScheduleFields form={p.sched} onChange={patch => setPlan(dm.key, { sched: { ...p.sched, ...patch } })} />
                </>
              )}
              {p.mode === "existing" && (
                <>
                  <select style={s.input} value={p.existingLabel} disabled={busy}
                    onChange={e => rematch({ ...review.plans, [dm.key]: { ...p, existingLabel: e.target.value } }, review.complete)}>
                    {existingLabels.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                  {existing && <div style={{ color: t.textDim, fontSize: 11, marginTop: 6 }}>Keeps its hours: {mmDescribeSchedule(existing.schedule)}. Change them in Menus &amp; hours → Hours &amp; rooms.</div>}
                </>
              )}
              {p.mode === "none" && <div style={{ color: t.textDim, fontSize: 11 }}>These items show whenever their category does.</div>}
            </div>
          );
        })}
        {Object.values(review.plans).some(p => p.mode === "existing") && (
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", color: t.textSecondary, fontSize: 13, margin: "4px 0 12px", cursor: "pointer" }}>
            <input type="checkbox" checked={review.complete} disabled={busy} style={{ width: 18, height: 18, marginTop: 1 }}
              onChange={e => rematch(review.plans, e.target.checked)} />
            <span>Replace the whole menu. Items on {[...new Set(Object.values(review.plans).filter(p => p.mode === "existing").map(p => p.existingLabel))].join(" and ")} that aren't in the upload come off. Leave unchecked to only add and update.</span>
          </label>
        )}

        <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>
          {rows.length} items: {[count("unchanged") && `${count("unchanged")} unchanged`, (count("changed") + count("restore")) && `${count("changed") + count("restore")} changed`, count("new") && `${count("new")} new`, undecided && `${undecided} to confirm`, count("skip") && `${count("skip")} skipped`].filter(Boolean).join(", ")}
          {removing.length > 0 && `. ${removing.length} coming off.`}
        </div>

        <div style={{ display: "flex", background: t.white04, borderRadius: 8, padding: 4, marginBottom: 12 }}>
          {[["review", "Check items"], ["preview", "Preview the menu"]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setView(id)}
              style={{ flex: 1, padding: "8px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, background: view === id ? t.accentDim : "transparent", color: view === id ? t.accent : t.textDim, fontWeight: view === id ? 600 : 400 }}>
              {label}
            </button>
          ))}
        </div>

        {view === "preview" && (
          <UploadPreview states={states} removing={removing} planLabel={planLabel} multiMenu={review.draft.menus.length > 1}
            complete={review.complete} />
        )}

        {view === "review" && (<>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {[["attention", `Needs a look (${needN})`], ["new", "New"], ["changed", "Changed"], ["unchanged", "Unchanged"], ["all", "All"],
            ...(count("skip") ? [["skip", `Not publishing (${count("skip")})`]] : [])].map(([id, label]) => (
            <button key={id} type="button" style={s.chip(filter === id)} onClick={() => setFilter(id)}>{label}</button>
          ))}
        </div>
        {/* Setting an item aside is never a dead end: say so, with Undo. */}
        {lastSkipped && (() => {
          const r = rows.find(x => x.key === lastSkipped);
          if (!r || r.include) return null;
          return (
            <div style={{ display: "flex", gap: 10, alignItems: "center", background: t.white04, borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: t.textSecondary }}>
              <span style={{ flex: 1 }}>"{r.fields.name}" won't be published. It's under Not publishing if you change your mind.</span>
              <button type="button" style={{ ...s.quietBtn, padding: "4px 10px" }} onClick={() => { updateRow(r.key, { include: true }); setLastSkipped(null); }}>Undo</button>
            </div>
          );
        })()}

        {shown.length === 0 && <div style={{ color: t.textDim, fontSize: 12, padding: "6px 0 12px" }}>{filter === "attention" ? "Nothing needs a look." : filter === "skip" ? "Everything is being published." : "None."}</div>}

        {shown.map(({ row, st }) => {
          const open = openKey === row.key;
          const badge = { new: ["New", t.success], changed: ["Changed", t.accent], unchanged: ["No change", t.textDim], restore: ["Back on the menu", t.accent], confirm: ["Same item?", t.warning], skip: ["Skipped", t.textDim] }[st.status];
          const priceText = st.kind === "wine"
            ? [upNum(row.fields.glassPrice) !== null && `${upMoney(row.fields.glassPrice)} gl`, upNum(row.fields.bottlePrice) !== null && `${upMoney(row.fields.bottlePrice)} btl`].filter(Boolean).join(", ") || "No price"
            : upMoney(row.fields.price);
          return (
            <div key={row.key} style={{ background: t.bgCard, border: `0.5px solid ${st.status === "confirm" || row.item.uncertain ? t.warningBorder : t.borderMid}`, borderRadius: 8, marginBottom: 6, opacity: st.status === "skip" ? 0.6 : 1 }}>
              <button type="button" onClick={() => setOpenKey(open ? null : row.key)}
                style={{ display: "flex", gap: 10, width: "100%", textAlign: "left", background: "none", border: "none", padding: "10px 12px", cursor: "pointer", fontFamily: t.fontSerif }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: t.textPrimary, fontSize: 14 }}>{row.fields.name}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 11, marginTop: 3 }}>
                    <span style={{ color: badge[1] }}>{badge[0]}</span>
                    <span style={{ color: t.textDim }}>{UPLOAD_CAT_LABEL[row.category]}{row.fields.section ? `, ${row.fields.section}` : ""}</span>
                    {review.draft.menus.length > 1 && (() => {
                      const on = [...new Set((row.item.appearsOn || []).map(a => planLabel(a.menu)).filter(Boolean))];
                      return on.length ? <span style={{ color: t.textSecondary }}>{on.join(" + ")}</span> : null;
                    })()}
                    {/* "Check:" rather than "Hard to read": the reader's notes cover more than
                        legibility - a missing vintage, a price printed twice. */}
                    {row.item.uncertain && st.status !== "skip" && !row.checked && <span style={{ color: t.warning }}>{row.item.note ? `Check: ${row.item.note}` : "Check this item"}</span>}
                    {row.item.uncertain && st.status !== "skip" && row.checked && <span style={{ color: t.textDim }}>Checked{row.item.note ? `: ${row.item.note}` : ""}</span>}
                    {!row.item.uncertain && row.item.note && <span style={{ color: t.textDim }}>{row.item.note}</span>}
                    {st.conflicts.length > 0 && <span style={{ color: t.warning }}>Your edit differs from the file</span>}
                    {st.status === "changed" && <span style={{ color: t.textDim }}>{st.diffs.map(f => UP_LABEL[f].toLowerCase()).join(", ")}</span>}
                  </div>
                </div>
                <div style={{ color: t.textSecondary, fontSize: 13, whiteSpace: "nowrap" }}>{priceText}</div>
              </button>
              {/* One tap for the two everyday answers, without opening the item:
                  a flag the manager has checked, and an item set aside by mistake. */}
              {((row.item.uncertain && st.status !== "skip" && st.status !== "confirm") || st.status === "skip") && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "0 12px 10px" }}>
                  {st.status === "skip" ? (
                    <button type="button" style={{ ...s.quietBtn, padding: "5px 12px" }} onClick={() => { updateRow(row.key, { include: true }); if (lastSkipped === row.key) setLastSkipped(null); }}>Publish it after all</button>
                  ) : row.checked ? (
                    <button type="button" style={{ ...s.quietBtn, padding: "5px 12px", border: "none" }} onClick={() => updateRow(row.key, { checked: false })}>Undo "looks right"</button>
                  ) : (
                    <button type="button" style={{ ...s.quietBtn, padding: "5px 12px" }} onClick={() => updateRow(row.key, { checked: true })}>Looks right</button>
                  )}
                </div>
              )}

              {(open || st.status === "confirm") && (
                <div style={{ padding: "0 12px 12px" }}>
                  {row.existing && row.match.kind !== "exact" && row.match.kind !== "restore" && (
                    <div style={{ background: t.white04, borderRadius: 6, padding: 10, marginBottom: 10 }}>
                      <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 8 }}>
                        {row.match.kind === "vintage"
                          ? <>Replaces <b style={{ fontWeight: 600 }}>{row.existing.name}</b>, which isn't on the new list?</>
                          : <>Is this the same item as <b style={{ fontWeight: 600 }}>{row.existing.name}</b> already on the menu?</>}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button type="button" style={s.chip(row.choice === "same")} onClick={() => updateRow(row.key, { choice: "same" })}>
                          {row.match.kind === "vintage" ? "Yes, new vintage" : "Same item"}
                        </button>
                        <button type="button" style={s.chip(row.choice === "different")} onClick={() => updateRow(row.key, { choice: "different" })}>
                          {row.match.kind === "vintage" ? "No, list both" : "Different item"}
                        </button>
                      </div>
                      <div style={{ color: t.textDim, fontSize: 11, marginTop: 6 }}>
                        {row.match.kind === "vintage" ? "Yes keeps its place and history and researches the new vintage." : "Same item keeps its description, photo and history."}
                      </div>
                    </div>
                  )}

                  {open && (
                    <>
                      {st.fields.map(f => {
                        const conflict = st.conflicts.includes(f);
                        const money = ["price", "glassPrice", "bottlePrice"].includes(f);
                        return (
                          <div key={f} style={{ marginBottom: 10 }}>
                            <label style={s.label} htmlFor={`up-${row.key}-${f}`}>
                              {UP_LABEL[f]}
                              {!st.asNew && st.diffs.includes(f) && !conflict && <span style={{ color: t.textDim }}> (was {money ? upMoney(row.existing[f]) : `"${upStr(row.existing[f]) || "blank"}"`})</span>}
                            </label>
                            {conflict && (
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                                <button type="button" style={s.chip(row.keep[f] !== "file")} onClick={() => updateRow(row.key, { keep: { ...row.keep, [f]: "mine" } })}>
                                  Keep yours: {money ? upMoney(row.existing[f]) : `"${upStr(row.existing[f]).slice(0, 60)}"`}
                                </button>
                                <button type="button" style={s.chip(row.keep[f] === "file")} onClick={() => updateRow(row.key, { keep: { ...row.keep, [f]: "file" } })}>
                                  Use the file's
                                </button>
                              </div>
                            )}
                            {f === "description"
                              ? <textarea id={`up-${row.key}-${f}`} rows={2} style={{ ...s.input, resize: "vertical", opacity: conflict && row.keep[f] !== "file" ? 0.5 : 1 }} value={row.fields[f]} onChange={e => setField(row, f, e.target.value)} />
                              : <input id={`up-${row.key}-${f}`} style={{ ...s.input, opacity: conflict && row.keep[f] !== "file" ? 0.5 : 1 }} inputMode={money ? "decimal" : undefined} value={row.fields[f]} onChange={e => setField(row, f, e.target.value)} />}
                          </div>
                        );
                      })}
                      {st.kind === "wine" && <div style={{ color: t.textDim, fontSize: 11, marginBottom: 10 }}>Wine descriptions come from SommSync's research, not the menu.</div>}
                      <label style={{ display: "flex", gap: 8, alignItems: "center", color: t.textSecondary, fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" checked={!row.include} onChange={e => { updateRow(row.key, { include: !e.target.checked }); setLastSkipped(e.target.checked ? row.key : null); }} style={{ width: 18, height: 18 }} />
                        Don't publish this item
                      </label>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {review.complete && review.removed.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ color: t.textSecondary, fontSize: 13, marginBottom: 6 }}>Not in the file, so coming off</div>
            {review.removed.map(r => {
              const k = `${r.category}:${r.id}:${r.menuId}`;
              return (
                <label key={k} style={{ display: "flex", gap: 8, alignItems: "center", background: t.white04, borderRadius: 6, padding: "8px 10px", marginBottom: 4, color: t.textPrimary, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!removeIds[k]} onChange={e => setRemoveIds(m => ({ ...m, [k]: e.target.checked }))} style={{ width: 18, height: 18 }} />
                  <span style={{ flex: 1 }}>{r.name}</span>
                  <span style={{ color: t.textDim, fontSize: 11 }}>{removeIds[k] ? `comes off ${r.menuLabel}` : `stays on ${r.menuLabel}`}</span>
                </label>
              );
            })}
            <div style={{ color: t.textDim, fontSize: 11, marginTop: 4 }}>Items on other menus too just leave this one. Anything removed keeps its research and can be restored from Manage Items → Edit menu items → Show removed items.</div>
          </div>
        )}
        </>)}

        {headingPlans().map(hp => {
          const label = UPLOAD_CAT_LABEL[hp.cat] || hp.cat;
          const finals = [...new Set(hp.names.map(hp.finalOf))];
          const linkBtn = { background: "none", border: "none", padding: 0, color: t.accent, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, textDecoration: "underline" };
          return (
            <div key={hp.cat} style={{ marginTop: 16, background: t.white04, borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ color: t.textPrimary, fontSize: 14, marginBottom: 4 }}>{label} headings after publishing</div>
              <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
                How {label.toLowerCase()} will be grouped for guests. Rename any heading to tidy them up; giving two the same name puts them together. Nothing changes unless you choose to.
              </div>
              {hp.shortUseful && (
                <div style={{ background: t.accentDim, border: `0.5px solid ${t.accentBorder}`, borderRadius: 8, padding: "8px 10px", marginBottom: 10, fontSize: 12, color: t.textSecondary, lineHeight: 1.5 }}>
                  Some headings include the page title, like "{hp.shortable[0]}".{" "}
                  <button type="button" style={linkBtn} onClick={() => renameMany(hp.cat, hp.shortable.map(h => [h, headingTail(h)]))}>
                    Use the short headings ({[...new Set(hp.shortable.map(headingTail))].slice(0, 3).join(", ")}{new Set(hp.shortable.map(headingTail)).size > 3 ? ", …" : ""})
                  </button>
                </div>
              )}
              {hp.names.map(h => {
                const fin = hp.finalOf(h);
                const key = `${hp.cat}|${h}`;
                const editing = editingHeading === key;
                const renamed = fin !== h;
                return (
                  <div key={h} style={{ padding: "6px 0", borderBottom: `0.5px solid ${t.borderSubtle}` }}>
                    {editing ? (
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <input autoFocus value={headingDraft} onChange={e => setHeadingDraft(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") { setRename(hp.cat, h, headingDraft.trim()); setEditingHeading(null); } if (e.key === "Escape") setEditingHeading(null); }}
                          aria-label={`New name for ${h}`}
                          style={{ flex: 1, minWidth: 160, background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, borderRadius: 6, padding: "6px 8px", fontFamily: t.fontSerif, fontSize: 16 }} />
                        <button type="button" style={{ ...s.quietBtn, padding: "6px 12px" }} onClick={() => { setRename(hp.cat, h, headingDraft.trim()); setEditingHeading(null); }}>Save</button>
                        <button type="button" style={{ ...s.quietBtn, padding: "6px 12px", border: "none" }} onClick={() => setEditingHeading(null)}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                        <span style={{ color: t.textPrimary, fontSize: 13 }}>
                          {renamed ? <><span style={{ color: t.textDim, textDecoration: "line-through" }}>{h}</span> → {fin}</> : h}
                        </span>
                        <span style={{ color: t.textDim, fontSize: 11 }}>{hp.counts.get(h)} item{hp.counts.get(h) === 1 ? "" : "s"}{hp.fresh.includes(h) ? " · new" : ""}{hp.auto[h] && !(headingRenames[hp.cat] || {})[h] ? " · same as an existing heading" : ""}</span>
                        <span style={{ flex: 1 }} />
                        {hp.kin[h] && fin !== hp.kin[h] && <button type="button" style={linkBtn} onClick={() => setRename(hp.cat, h, hp.kin[h])}>Use {hp.kin[h]}?</button>}
                        <button type="button" style={linkBtn} onClick={() => { setEditingHeading(key); setHeadingDraft(fin); }}>Rename</button>
                        {(headingHistory[key] || []).length > 0 && <button type="button" style={linkBtn} onClick={() => undoRename(hp.cat, h)}>Undo</button>}
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ color: t.textDim, fontSize: 11, marginTop: 8 }}>
                {finals.length} heading{finals.length === 1 ? "" : "s"} after publishing. Renaming also moves the items already on the app under that heading.
              </div>
            </div>
          );
        })}

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16 }}>
          <button type="button" style={s.primaryBtn(busy || undecided > 0 || publishN === 0 || unnamed)} disabled={busy || undecided > 0 || publishN === 0 || unnamed} onClick={approve}>
            {busy ? "Working…" : `Approve and publish ${publishN} item${publishN === 1 ? "" : "s"}`}
          </button>
          {undecided > 0 && <span style={{ color: t.warning, fontSize: 12 }}>Answer the {undecided} "Same item?" question{undecided === 1 ? "" : "s"} first.</span>}
          {!undecided && unnamed && <span style={{ color: t.warning, fontSize: 12 }}>Name each new menu first.</span>}
        </div>
      </div>
    );
  }

  // ── Pick ────────────────────────────────────────────────────────────────
  return (
    <div style={box}>
      <div style={{ color: t.textPrimary, fontSize: 13, marginBottom: 6 }}>{target ? `Replace ${target.label}` : "Upload a menu"}</div>
      <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
        {target
          ? `Upload the new version of ${target.label}: a PDF, photos of the pages, a Word file or an Illustrator file. You'll see what's new, what changed and what comes off before anything changes for guests. Items you already have keep their descriptions and photos; only new ones are researched.`
          : "Upload one menu at a time: a PDF, photos of its pages, a Word file or an Illustrator file. Several files are read as pages of the same menu, so upload a different menu (lunch, dinner, the wine list) on its own. Beer and cocktails on the same page are fine: SommSync sorts them out. You'll see everything before anything changes for guests."}
      </div>
      {error && <MmNotice kind="error">{error}</MmNotice>}
      {showDrafts && drafts.map(d => (
        <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, background: t.white04, borderRadius: 6, padding: "8px 10px", marginBottom: 8 }}>
          <div style={{ flex: 1, minWidth: 0, color: t.textSecondary, fontSize: 12 }}>
            Waiting for review: {d.menuTitle || (d.fileNames || []).join(", ")} ({d.itemCount} items)
          </div>
          <button type="button" style={s.quietBtn} disabled={busy} onClick={() => resume(d.id)}>Review</button>
          <button type="button" style={{ ...s.quietBtn, border: "none" }} onClick={() => discard(d.id)}>Discard</button>
        </div>
      ))}
      <input ref={inputRef} type="file" multiple accept={UPLOAD_ACCEPT} style={{ display: "none" }}
        onChange={e => {
          // Read the files NOW. Clearing the input (so the same file can be picked
          // again) empties e.target.files in a real browser, and a state updater
          // may run after that: reading the list inside it added nothing whenever
          // React deferred the update, which is what "nothing happens" was.
          const picked = Array.from(e.target.files || []);
          e.target.value = "";
          if (!picked.length) return;
          setPages(prev => [...prev, ...picked.map(f => ({ id: `n${Math.random().toString(36).slice(2, 9)}`, kind: "new", file: f }))].slice(0, 10));
          setError(null);
        }} />
      {/* Replacing a menu whose file is kept: start from its pages and swap,
          add or remove only what changed. */}
      {pages.length === 0 && target && target.source && target.source.fileCount > 0 && (
        <div style={{ background: t.white04, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
          <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>
            Only some pages changed? Start from the current {target.source.fileCount === 1 ? "file" : `${target.source.fileCount} pages`} and swap just those.
          </div>
          <button type="button" style={{ ...s.quietBtn, padding: "6px 12px" }} disabled={busy} onClick={() => loadKeptPages({ sourceId: target.source.id })}>
            {busy ? "Loading…" : "Start from the current pages"}
          </button>
        </div>
      )}
      {pages.map((pg, i) => {
        const isPhoto = /^image\//.test((pg.kind === "kept" ? pg.contentType : pg.file.type) || "");
        const thumb = pg.kind === "kept" ? (isPhoto ? pg.url : null) : thumbs[pg.id];
        const name = pg.kind === "kept" ? pg.name : pg.file.name;
        const arrow = { background: "none", border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, borderRadius: 6, width: 30, height: 30, cursor: "pointer", fontSize: 14 };
        return (
          <div key={pg.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `0.5px solid ${t.borderSubtle}` }}>
            <div style={{ width: 44, height: 56, flexShrink: 0, borderRadius: 4, overflow: "hidden", background: t.white06, display: "flex", alignItems: "center", justifyContent: "center", color: t.textDim, fontSize: 10 }}>
              {thumb ? <img src={thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (/\.pdf$/i.test(name) ? "PDF" : "FILE")}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* "Page N" only for a photo, where one file IS one page. A PDF or
                  Word file can hold any number of pages, all of them read. */}
              <div style={{ color: t.textPrimary, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {isPhoto ? `Page ${i + 1}` : name}
                {pg.kind === "kept" && <span style={{ color: t.textDim, fontSize: 11 }}> · {isPhoto ? "current page" : "current file"}</span>}
              </div>
              <div style={{ color: t.textDim, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {isPhoto ? name : "Every page in this file is read"}{pg.kind === "new" ? ` · ${(pg.file.size / 1048576).toFixed(1)} MB` : ""}
              </div>
            </div>
            <button type="button" aria-label={`Move page ${i + 1} up`} disabled={i === 0} onClick={() => movePage(i, -1)} style={{ ...arrow, opacity: i === 0 ? 0.35 : 1 }}>↑</button>
            <button type="button" aria-label={`Move page ${i + 1} down`} disabled={i === pages.length - 1} onClick={() => movePage(i, 1)} style={{ ...arrow, opacity: i === pages.length - 1 ? 0.35 : 1 }}>↓</button>
            <button type="button" aria-label={`Remove page ${i + 1}`} onClick={() => setPages(ps => ps.filter(x => x.id !== pg.id))}
              style={{ background: "none", border: "none", color: t.textDim, fontSize: 20, cursor: "pointer", width: 30 }}>×</button>
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button type="button" style={s.quietBtn} onClick={() => inputRef.current && inputRef.current.click()}>{pages.length ? "+ Add another page" : "Choose files"}</button>
        {pages.length > 0 && (() => {
          const photos = pages.every(pg => /^image\//.test((pg.kind === "kept" ? pg.contentType : pg.file.type) || ""));
          return <button type="button" style={s.primaryBtn(false)} onClick={read}>Read {pages.length === 1 ? "menu" : `${pages.length} ${photos ? "pages" : "files"}`}</button>;
        })()}
        {replacingDraftId && <button type="button" style={{ ...s.quietBtn, border: "none" }} onClick={() => { setReplacingDraftId(null); setPages([]); resume(replacingDraftId); }}>Back to the review</button>}
      </div>
      <div style={{ color: t.textDim, fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>
        One menu per upload; its pages can be several files. For photos, one page per photo, flat and in good light. Put the pages in order (↑ ↓): items appear in the order they're read.
      </div>
    </div>
  );
}

// ─── Your menus (manager hub for file-managed menus) ─────────────────────────
//
// Oct 27, 2026. Before this, a file-managed restaurant's menu work was spread
// over three screens: uploading lived under Update menu (a POS sync screen),
// hours under Menu items → Menus & Hours, and item edits under Edit menu items,
// which is organised by category (all wines, all beers), never by menu. Whether
// an upload replaced a menu or added to it was decided AFTER the file was read.
//
// Here a restaurant's menus are cards, one per menu NAME. The server keeps one
// manual menu per type (food, wine, beer & pours, cocktails & NA), so a dinner
// menu with cocktails on it is two records called "Dinner"; the manager only
// ever sees "Dinner". Each card offers the three jobs:
//   Edit items       - the menu as it reads, by section; add, change, 86, remove
//   Replace          - upload the new version; the review opens set to "whole
//                      menu", so what's missing from the file comes off
//   Hours & rooms    - saved to every record of that name, so they can't drift
// "Add a menu" uploads something new. Same menuManager endpoint and the same
// server rules as everywhere else: POS-fed categories are never offered.

const POS_NUDGE_KEY = "sommsync.posNudgeSnoozedUntil";
const POS_NUDGE_SNOOZE_DAYS = 30;
// Sales scheduler (the same HubSpot meetings page the demo wall embeds), opened
// as a plain page rather than the embed.
const POS_NUDGE_URL = DEMO_MEETINGS_URL.replace(/\?embed=true$/, "");

function posNudgeSnoozed() {
  try { return Number(localStorage.getItem(POS_NUDGE_KEY) || 0) > Date.now(); } catch (e) { return false; }
}

// The upgrade nudge: file-managed restaurants on Toast can move to live sync.
// Full banner on the hub (dismissible for 30 days on this device); a one-line
// version under a published upload, where the effort of uploading is fresh.
function PosSyncNudge({ compact = false, onDismiss = null }) {
  const t = useTheme();
  const link = (
    <a href={POS_NUDGE_URL} target="_blank" rel="noopener noreferrer" style={{ color: t.accent, fontSize: compact ? 11 : 12, textDecoration: "none", whiteSpace: "nowrap" }}>
      Talk to us about Toast sync →
    </a>
  );
  if (compact) {
    return (
      <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, margin: "4px 0 12px" }}>
        On Toast? With live sync, changes like these show up on their own. {link}
      </div>
    );
  }
  return (
    <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorder}`, borderRadius: 10, padding: "12px 14px", marginBottom: 18 }}>
      <div style={{ color: t.accent, fontSize: 13, marginBottom: 4 }}>Tired of uploading menu changes?</div>
      <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.55, marginBottom: 10 }}>
        If you're on Toast, SommSync can sync with it instead. New items, price changes and 86s show up on their own, and stock stays current through service. Everything you've set up here carries over.
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        {link}
        {onDismiss && (
          <button type="button" onClick={onDismiss}
            style={{ background: "none", border: "none", color: t.textDim, fontSize: 11, cursor: "pointer", fontFamily: t.fontSerif, padding: 0 }}>
            Not now
          </button>
        )}
      </div>
    </div>
  );
}

const mmNormLabel = x => String(x || "").trim().toLowerCase().replace(/\s+/g, " ");

// Active menus, one group per name, in the order the menus were set up.
function groupMenusByName(menus) {
  const groups = [], idx = {};
  (menus || []).filter(m => !m.retired)
    .slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .forEach(m => {
      const k = mmNormLabel(m.label);
      if (!(k in idx)) { idx[k] = groups.length; groups.push({ key: k, label: m.label, menus: [] }); }
      groups[idx[k]].menus.push(m);
    });
  return groups;
}

// For a no-menu pseudo group, `unassigned: true` and `menuType`.
function itemIsInGroup(item, group) {
  const g = item.menuGuids || [];
  if (group.unassigned) return g.length === 0;
  return group.menus.some(m => g.includes(m.id));
}

const MM_COUNT_NOUN = {
  foodItems: ["dish", "dishes"], wines: ["wine", "wines"], beers: ["beer", "beers"],
  pours: ["spirit", "spirits"], cocktails: ["cocktail", "cocktails"], nab: ["non-alcoholic drink", "non-alcoholic drinks"],
};
function groupCountsText(itemsByCat, group, cats) {
  const parts = cats.map(c => {
    const n = (itemsByCat[c.id] || []).filter(it => itemIsInGroup(it, group)).length;
    return n ? `${n} ${MM_COUNT_NOUN[c.id][n === 1 ? 0 : 1]}` : null;
  }).filter(Boolean);
  return parts.length ? parts.join(" · ") : "Nothing on it yet";
}

function MenusHub({ isAdmin, locationNames = { bar: "Bar", dining: "Dining Room" }, onOpenCsvImport = null }) {
  const s = useMmStyles(); const t = s.t;
  const [info, setInfo] = useState(null);            // listMenus response
  const [loadError, setLoadError] = useState(null);
  const [itemsByCat, setItemsByCat] = useState(null); // { [category]: items }
  const [drafts, setDrafts] = useState([]);
  // list | upload { target, resumeId } | items { groupKey | unassignedType } |
  // hours { groupKey } | newMenu | setup { menuType } (name a menu before replacing)
  const [view, setView] = useState({ kind: "list" });
  const [notice, setNotice] = useState(null);
  const [nudgeHidden, setNudgeHidden] = useState(posNudgeSnoozed);
  const [setupName, setSetupName] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);

  const load = useCallback(async () => {
    let i;
    try { i = await mmCall("listMenus"); setInfo(i); setLoadError(null); }
    catch (e) { setLoadError(e.message); return; }
    const cats = MM_CATEGORIES.filter(c => i.editable?.[c.id]);
    const out = {};
    await Promise.all(cats.map(async c => {
      try { out[c.id] = (await mmCall("listItems", { category: c.id })).items || []; }
      catch (e) { out[c.id] = []; }
    }));
    setItemsByCat(out);
    mmCall("listDrafts").then(d => setDrafts((d.drafts || []).filter(x => x.status === "ready"))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const goList = (n = null) => { setNotice(n); setView({ kind: "list" }); load(); };
  const dismissNudge = () => {
    try { localStorage.setItem(POS_NUDGE_KEY, String(Date.now() + POS_NUDGE_SNOOZE_DAYS * 86400000)); } catch (e) {}
    setNudgeHidden(true);
  };
  async function discardDraft(id) {
    if (!window.confirm("Discard this upload? Nothing from it has been published.")) return;
    try { await mmCall("discardDraft", { id }); } catch (e) {}
    load();
  }

  const wrap = children => <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 40px" }}>{children}</div>;
  const backLink = (
    <button type="button" onClick={() => goList()}
      style={{ background: "none", border: "none", color: t.accent, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif, padding: "0 0 12px" }}>
      ← All menus
    </button>
  );

  if (loadError && !info) return wrap(
    <>
      <MmNotice kind="error">Couldn't load your menus: {loadError}</MmNotice>
      <button type="button" style={s.quietBtn} onClick={load}>Try again</button>
    </>
  );
  if (!info || !itemsByCat) return wrap(<div style={{ color: t.textDim, fontSize: 13 }}>Loading your menus…</div>);

  const editableCats = MM_CATEGORIES.filter(c => info.editable?.[c.id]);
  const editableTypes = [...new Set(editableCats.map(c => c.menuType))];
  const groups = groupMenusByName(info.menus);
  const groupByKey = k => groups.find(g => g.key === k) || null;
  const catsOfTypes = types => editableCats.filter(c => types.includes(c.menuType));
  const isManual = info.posType === "manual";
  const typeLabel = type => MENU_TYPES.find(o => o.value === type)?.label || type;
  // Items on no menu, per menu type: lists that predate menus (a CSV onboarding)
  // or items whose menu was deleted. Shown so they are never invisible here.
  const unassigned = editableTypes.map(type => ({
    unassigned: true, menuType: type, key: `none:${type}`, label: typeLabel(type),
    count: catsOfTypes([type]).reduce((n, c) => n + (itemsByCat[c.id] || []).filter(it => !(it.menuGuids || []).length).length, 0),
    hasMenu: groups.some(g => g.menus.some(m => m.menuType === type)),
  })).filter(u => u.count > 0);
  const nudge = isManual ? <PosSyncNudge compact /> : null;

  // ── Nothing to manage here ──
  if (!editableCats.length) return wrap(
    <div style={{ color: t.textSecondary, fontSize: 13, lineHeight: 1.6 }}>
      Every menu on this restaurant comes from {info.posName || "your POS"}. Change items there and they appear here after the next sync.
    </div>
  );

  // ── Upload: add a menu, replace one, or finish a waiting draft ──
  if (view.kind === "upload") return wrap(
    <>
      {backLink}
      <MenuUploadPanel locationNames={locationNames} target={view.target || null} resumeId={view.resumeId || null}
        showDrafts={false} onApplied={load} onDone={() => goList()} footer={nudge} canRemoveMenus={!!isAdmin} />
    </>
  );

  // ── Edit items ──
  if (view.kind === "items") {
    const group = view.unassignedType
      ? (unassigned.find(u => u.menuType === view.unassignedType) || { unassigned: true, menuType: view.unassignedType, label: typeLabel(view.unassignedType), key: `none:${view.unassignedType}` })
      : groupByKey(view.groupKey);
    if (!group) return wrap(<>{backLink}<div style={{ color: t.textDim, fontSize: 13 }}>That menu isn't there any more.</div></>);
    return wrap(
      <>
        {backLink}
        <MenuItemsView group={group} info={info} itemsByCat={itemsByCat} isAdmin={isAdmin} onChanged={load} />
      </>
    );
  }

  // ── Hours & rooms ──
  if (view.kind === "hours") {
    const group = groupByKey(view.groupKey);
    if (!group) return wrap(<>{backLink}<div style={{ color: t.textDim, fontSize: 13 }}>That menu isn't there any more.</div></>);
    return wrap(
      <>
        {backLink}
        <GroupMenuEditor group={group} locationNames={locationNames}
          onCancel={() => goList()}
          onSaved={label => goList({ kind: "success", text: `${label} saved.` })}
          onDeleted={(label, archived) => goList({ kind: "success", text: `${label} deleted.${archived ? ` ${archived} item(s) that were only on it moved to Removed items.` : ""}` })} />
      </>
    );
  }

  // ── An empty menu, built by hand ──
  if (view.kind === "newMenu") return wrap(
    <>
      {backLink}
      <MenuEditor menu={{}} existingTypes={editableTypes} locationNames={locationNames}
        onCancel={() => goList()}
        onSaved={saved => { setNotice({ kind: "success", text: `${saved.label} created. Add items to it with Edit items.` }); setView({ kind: "list" }); load(); }}
        onDeleted={() => goList()} />
    </>
  );

  // ── Name a menu for a list that has none, then replace it ──
  // Replacing needs a menu to replace. A list that predates menus gets one here
  // (all day, both rooms, until hours are set), and the replace targets it. The
  // server treats items on no menu as on it, because it is the only menu of its
  // type, so items missing from the new file can come off (soleMenuIdByType).
  if (view.kind === "setup") {
    const type = view.menuType;
    const create = async () => {
      setSetupBusy(true); setNotice(null);
      try {
        const label = setupName.trim();
        await mmCall("saveMenu", { menu: { label, menuType: type, locations: [], schedule: null } });
        await load();
        setView({ kind: "upload", target: { label } });
      } catch (e) { setNotice({ kind: "error", text: e.message }); }
      setSetupBusy(false);
    };
    return wrap(
      <>
        {backLink}
        {notice && <MmNotice kind={notice.kind}>{notice.text}</MmNotice>}
        <div style={{ background: t.bgCard, border: `0.5px solid ${t.accentBorder}`, borderRadius: 10, padding: 16 }}>
          <div style={{ color: t.textPrimary, fontSize: 15, marginBottom: 6 }}>Name this menu first</div>
          <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.55, marginBottom: 14 }}>
            Your {typeLabel(type).toLowerCase()} isn't set up as a menu yet, so there's nothing to replace. Give it a name and the upload will replace it. It shows all day in both rooms until you set hours.
          </div>
          <label style={s.label} htmlFor="mm-setup-name">Menu name</label>
          <input id="mm-setup-name" style={{ ...s.input, marginBottom: 14 }} value={setupName} placeholder="Wine List, Dinner, Bar Menu…" onChange={e => setSetupName(e.target.value)} />
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={s.primaryBtn(setupBusy || !setupName.trim())} disabled={setupBusy || !setupName.trim()} onClick={create}>
              {setupBusy ? "Setting up…" : "Continue to upload"}
            </button>
            <button type="button" style={s.quietBtn} onClick={() => goList()} disabled={setupBusy}>Cancel</button>
          </div>
        </div>
      </>
    );
  }

  // ── The list ──
  const roomsText = m => ((m.locations || []).length === 1 ? (locationNames[m.locations[0]] || m.locations[0]) : "Both rooms");
  // Source documents: the file each menu came from, its previous version, and
  // the other menus the same file produced (for "Replace the whole file").
  const sources = info.sources || [];
  const sourceOf = g => { const id = g.menus.map(m => m.sourceDocId).find(Boolean); return id ? sources.find(x => x.id === id) || null : null; };
  // Which of a source's files a menu card came from (Nov 3, 2026): recorded per
  // menu at upload time; for older uploads, a file whose name carries a word of
  // the menu's name (beachcomber-lunch.pdf -> Lunch); else all of them.
  const filesOf = (src, g) => {
    const n = src.fileCount || 0;
    const rec = [...new Set(g.menus.flatMap(m => (src.menuFiles && src.menuFiles[m.id]) || []))].filter(i => i < n).sort((a, b) => a - b);
    if (rec.length) return rec;
    if (n > 1) {
      const words = String(g.label).toLowerCase().split(/[^a-z0-9]+/).filter(w => w.length >= 4);
      const hit = (src.fileNames || []).map((f, i) => (words.some(w => String(f).toLowerCase().includes(w)) ? i : -1)).filter(i => i >= 0);
      if (hit.length) return hit;
    }
    return Array.from({ length: n }, (_, i) => i);
  };
  // Menus that came from the same file(s): those are replaced together.
  const labelsFrom = (src, idxs) => groups.filter(gr => gr.menus.some(m => m.sourceDocId === src.id)
    && (!idxs || filesOf(src, gr).some(i => idxs.includes(i)))).map(gr => gr.label);
  const previousOf = (src, label) => sources.find(x => x.status === "previous" && x.supersededBy === src.id && (x.labels || []).some(l => mmNormLabel(l) === mmNormLabel(label))) || null;
  const shortDate = ms => (ms ? new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
  async function viewOriginal(src, i = 0) {
    // Open the tab first (a tab opened after a network wait is often blocked),
    // then point it at the short-lived link.
    const w = window.open("", "_blank");
    try {
      const r = await mmCall("getSourceFile", { sourceId: src.id, index: i });
      if (w) w.location.href = r.url; else window.location.href = r.url;
    } catch (e) { if (w) w.close(); setNotice({ kind: "error", text: e.message }); }
  }
  // Photos are named by the phone ("IMG_4417.jpg"), so photo uploads read as
  // pages; other files keep their names.
  const allPhotos = src => Array.isArray(src.photos) && src.photos.length > 0 && src.photos.every(Boolean);
  const fileLinks = (src, idxs) => (idxs && idxs.length > 1
    ? idxs.map((i, n) => (
        <button key={i} type="button" onClick={() => viewOriginal(src, i)} style={{ background: "none", border: "none", padding: 0, color: t.accent, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11, textDecoration: "underline", marginRight: 8 }}>
          {allPhotos(src) ? `Page ${n + 1}` : (src.fileNames[i] || `File ${i + 1}`)}
        </button>))
    : idxs && idxs.length === 1
      ? <button type="button" onClick={() => viewOriginal(src, idxs[0])} style={{ background: "none", border: "none", padding: 0, color: t.accent, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11, textDecoration: "underline" }}>View original</button>
      : src.fileCount === 1
      ? <button type="button" onClick={() => viewOriginal(src, 0)} style={{ background: "none", border: "none", padding: 0, color: t.accent, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11, textDecoration: "underline" }}>View original</button>
      : <span style={{ color: t.textDim }}>original not kept</span>);
  const actionBtn = { ...s.quietBtn, padding: "8px 12px" };

  return wrap(
    <>
      {notice && <MmNotice kind={notice.kind}>{notice.text}</MmNotice>}
      {isManual && !nudgeHidden && <PosSyncNudge onDismiss={dismissNudge} />}
      {info.liveSync && editableCats.length < MM_CATEGORIES.length && (
        <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 14 }}>
          {MM_CATEGORIES.filter(c => !info.editable?.[c.id]).map(c => c.label).join(", ")} come from {info.posName}; change those there.
        </div>
      )}

      {drafts.length > 0 && (
        <section style={{ marginBottom: 22 }}>
          <h2 style={s.sectionTitle}>Waiting for your review</h2>
          <div style={s.sectionNote}>Read but not published. Guests don't see any of it yet.</div>
          {drafts.map(d => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, background: t.warningDim, border: `0.5px solid ${t.warningBorder}`, borderRadius: 8, padding: "10px 12px", marginBottom: 6 }}>
              <div style={{ flex: 1, minWidth: 0, color: t.textPrimary, fontSize: 13 }}>
                {d.menuTitle || (d.fileNames || []).join(", ")}
                <div style={{ color: t.textDim, fontSize: 11, marginTop: 2 }}>{d.itemCount} items</div>
              </div>
              <button type="button" style={s.primaryBtn(false)} onClick={() => setView({ kind: "upload", resumeId: d.id })}>Review</button>
              <button type="button" style={{ ...s.quietBtn, border: "none" }} onClick={() => discardDraft(d.id)}>Discard</button>
            </div>
          ))}
        </section>
      )}

      {/* Same drink, same room, same time, two prices. Information, not an
          error: a happy hour does it on purpose. Computed by listMenus
          (findMenuPriceOverlaps in functions/index.js). One line, tap to see
          the list: a happy hour card makes this true of every drink on it. */}
      <MmOverlapPanel overlaps={info.overlaps || []} s={s} />

      <section style={{ marginBottom: 24 }}>
        <h2 style={s.sectionTitle}>Your menus</h2>
        <div style={s.sectionNote}>Tap Edit items to change a menu, or Replace when you have a new version.</div>

        {groups.length === 0 && unassigned.length === 0 && (
          <div style={{ color: t.textSecondary, fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
            No menus yet. Upload one: a PDF, photos of the pages or a Word file. SommSync reads it and sets it up, and you check everything before guests see it.
          </div>
        )}

        {groups.map(g => {
          const first = g.menus[0];
          const hoursDiffer = g.menus.some(m => JSON.stringify(m.schedule || null) !== JSON.stringify(first.schedule || null)
            || JSON.stringify([...(m.locations || [])].sort()) !== JSON.stringify([...(first.locations || [])].sort()));
          const openNow = g.menus.some(m => m.openNow);
          const cats = catsOfTypes(g.menus.map(m => m.menuType));
          return (
            <div key={g.key} style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: t.textPrimary, fontSize: 16, fontFamily: t.fontSerif }}>{g.label}</div>
                  <div style={{ color: t.textDim, fontSize: 11, marginTop: 3 }}>{groupCountsText(itemsByCat, g, cats)}</div>
                  <div style={{ color: t.textSecondary, fontSize: 12, marginTop: 4 }}>
                    {mmDescribeSchedule(first.schedule)} · {roomsText(first)}
                  </div>
                  {(() => {
                    const src = sourceOf(g);
                    if (!src) return null;
                    const prev = previousOf(src, g.label);
                    const idxs = filesOf(src, g);
                    const what = (x, ix) => (ix.length > 1 ? `${ix.length} ${allPhotos(x) ? "photos" : "files"}` : (allPhotos(x) ? "a photo" : (x.fileNames[ix[0]] || "an upload")));
                    const prevIdxs = prev ? filesOf(prev, g) : [];
                    return (
                      <div style={{ color: t.textDim, fontSize: 11, marginTop: 4, lineHeight: 1.6 }}>
                        From {what(src, idxs)}, {shortDate(src.uploadedAt)} · {fileLinks(src, idxs)}
                        {prev && <><br />Previous version, {shortDate(prev.uploadedAt)} · {fileLinks(prev, prevIdxs)}</>}
                      </div>
                    );
                  })()}
                  {hoursDiffer && (
                    <div style={{ color: t.warning, fontSize: 11, marginTop: 4 }}>
                      Parts of this menu have different hours or rooms.{isAdmin ? " Save Hours & rooms to line them up." : ""}
                    </div>
                  )}
                </div>
                <div style={{ color: openNow ? t.success : t.textDim, fontSize: 11, whiteSpace: "nowrap" }}>{openNow ? "● Showing now" : "○ Not showing now"}</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" style={s.primaryBtn(false)} onClick={() => { setNotice(null); setView({ kind: "items", groupKey: g.key }); }}>Edit items</button>
                {(() => {
                  // A file that produced several menus is replaced as a whole
                  // (one upload, one review); a single menu can still be
                  // replaced on its own.
                  const src = sourceOf(g);
                  const idxs = src ? filesOf(src, g) : [];
                  const siblings = src ? labelsFrom(src, idxs) : [];
                  const whole = src && siblings.length > 1;
                  const go = tg => { setNotice(null); setView({ kind: "upload", target: tg }); };
                  return whole ? (
                    <>
                      <button type="button" style={actionBtn} onClick={() => go({ label: siblings.join(" & "), source: { id: src.id, labels: siblings, fileNames: idxs.map(i => src.fileNames[i]), fileCount: idxs.length, fileIndexes: idxs } })}>
                        Replace the whole file ({siblings.join(", ")})
                      </button>
                      <button type="button" style={actionBtn} onClick={() => go({ label: g.label })}>Replace just {g.label}</button>
                    </>
                  ) : (
                    <button type="button" style={actionBtn} onClick={() => go(src ? { label: g.label, source: { id: src.id, labels: [g.label], fileNames: idxs.map(i => src.fileNames[i]), fileCount: idxs.length, fileIndexes: idxs } } : { label: g.label })}>Replace with a new file</button>
                  );
                })()}
                {isAdmin && <button type="button" style={actionBtn} onClick={() => { setNotice(null); setView({ kind: "hours", groupKey: g.key }); }}>Hours &amp; rooms</button>}
              </div>
            </div>
          );
        })}

        {unassigned.map(u => (
          <div key={u.key} style={{ background: t.white04, border: `0.5px dashed ${t.borderMid}`, borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
            <div style={{ color: t.textPrimary, fontSize: 15, fontFamily: t.fontSerif }}>{u.label}: not on a menu</div>
            <div style={{ color: t.textDim, fontSize: 11, marginTop: 3, lineHeight: 1.5 }}>
              {u.count} item{u.count === 1 ? "" : "s"} with no menu of {u.count === 1 ? "its" : "their"} own, shown whenever {u.label.toLowerCase()} is.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <button type="button" style={s.primaryBtn(false)} onClick={() => { setNotice(null); setView({ kind: "items", unassignedType: u.menuType }); }}>Edit items</button>
              {!u.hasMenu && isAdmin && (
                <button type="button" style={actionBtn} onClick={() => { setNotice(null); setSetupName(u.label); setView({ kind: "setup", menuType: u.menuType }); }}>Replace with a new file</button>
              )}
            </div>
            {!u.hasMenu && !isAdmin && (
              <div style={{ color: t.textDim, fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>To replace these with a new file, an owner first sets them up as a menu.</div>
            )}
          </div>
        ))}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
          <button type="button" style={s.primaryBtn(false)} onClick={() => { setNotice(null); setView({ kind: "upload" }); }}>+ Add a menu</button>
          {isAdmin && <button type="button" style={s.quietBtn} onClick={() => { setNotice(null); setView({ kind: "newMenu" }); }}>Start an empty menu</button>}
        </div>
        {info.timezone && <div style={{ color: t.textVeryDim || t.textDim, fontSize: 10, marginTop: 12 }}>Times are in {info.timezone.replace(/_/g, " ")}.</div>}
      </section>

      {onOpenCsvImport && (
        <section style={{ borderTop: `1px solid ${t.borderSubtle}`, paddingTop: 16 }}>
          <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 4 }}>Have a CSV export from your POS?</div>
          <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 10 }}>
            A Toast items export carries Toast's own item IDs, which makes a later switch to live sync seamless.
          </div>
          <button type="button" style={s.quietBtn} onClick={onOpenCsvImport}>Import a CSV export</button>
        </section>
      )}
    </>
  );
}

// One menu's items, read the way the menu reads: by category, then section, in
// menu order. Tap an item to edit it in place; "Add" puts a new item on this
// menu. New or renamed wines, beers and spirits are researched straight away,
// only those - the rest of the list keeps its research.
function MenuItemsView({ group, info, itemsByCat, isAdmin, onChanged }) {
  const s = useMmStyles(); const t = s.t;
  const [editing, setEditing] = useState(null);       // `${catId}:${itemId}` or `${catId}:new`
  const [msgs, setMsgs] = useState([]);               // [{ kind, text }]
  const [research, setResearch] = useState({});       // { [researchCat]: progress }
  const liveMenus = (info.menus || []).filter(m => !m.retired);
  const types = group.unassigned ? [group.menuType] : [...new Set(group.menus.map(m => m.menuType))];
  const cats = MM_CATEGORIES.filter(c => info.editable?.[c.id] && types.includes(c.menuType));
  const menuIdFor = type => (group.unassigned ? null : (group.menus.find(m => m.menuType === type) || {}).id || null);

  async function research1(rc) {
    if (research[rc]?.running) return;
    await runResearchPasses(rc, p => setResearch(r => ({ ...r, [rc]: p })));
    onChanged();
  }

  // Section and order on THIS menu: a dish can sit under different courses at
  // lunch and dinner (courseNames / menuSortOrders, written by uploads).
  const sectionsFor = cat => {
    const mid = menuIdFor(cat.menuType);
    const list = (itemsByCat[cat.id] || []).filter(it => itemIsInGroup(it, group));
    const ordered = list.map((it, i) => ({ it, i, o: mid && it.menuSortOrders && Number.isFinite(it.menuSortOrders[mid]) ? it.menuSortOrders[mid] : null }));
    if (ordered.some(x => x.o !== null)) ordered.sort((a, b) => (a.o ?? 1e9) - (b.o ?? 1e9) || a.i - b.i);
    const secs = [], idx = {};
    ordered.forEach(({ it }) => {
      const name = (cat.kind === "food" ? ((mid && it.courseNames && it.courseNames[mid]) || it.course) : it.subgroup) || "";
      if (!(name in idx)) { idx[name] = secs.length; secs.push({ name, items: [] }); }
      secs[idx[name]].items.push(it);
    });
    return { secs, count: list.length };
  };

  const afterSave = (cat, saved, warnings, created) => {
    setEditing(null);
    const out = [{ kind: "success", text: `${saved.name} ${created ? "added" : "saved"}.` }];
    (warnings || []).forEach(w => out.push({ kind: "warning", text: w }));
    setMsgs(out);
    onChanged();
    // A new wine, beer or spirit has no research yet; start it now rather than
    // leave it invisible to guests until someone finds the Research button. A
    // RENAME is not picked up here: the research pass skips items that already
    // have a record (see the saveItem warning the manager is shown).
    const rc = MM_RESEARCH_CAT[cat.id];
    if (rc && created) research1(rc);
  };

  const researchRunning = Object.entries(research).filter(([, p]) => p && (p.running || p.msg));
  return (
    <>
      <div style={{ color: t.textPrimary, fontSize: 18, fontFamily: t.fontSerif, marginBottom: 4 }}>
        {group.unassigned ? `${group.label}: not on a menu` : group.label}
      </div>
      <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 14 }}>
        Tap an item to change its price, section or description, 86 it, or remove it. Changes reach guests right away; new wines, beers and spirits appear once SommSync has researched them and they're approved in Manage Items → Needs review.
      </div>
      {msgs.map((m, i) => <MmNotice key={i} kind={m.kind}>{m.text}</MmNotice>)}
      {researchRunning.map(([rc, p]) => (
        <div key={rc} style={{ color: t.textSecondary, fontSize: 12, marginBottom: 10 }}>
          Researching new {rc === "pours" ? "spirits" : rc === "beer" ? "beers" : "wines"}: {p.msg}
        </div>
      ))}

      {cats.map(cat => {
        const { secs, count } = sectionsFor(cat);
        const mid = menuIdFor(cat.menuType);
        const addKey = `${cat.id}:new`;
        return (
          <section key={cat.id} style={{ marginBottom: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <h2 style={{ ...s.sectionTitle, margin: 0, flex: 1 }}>{cat.label} <span style={{ color: t.textDim, fontSize: 12 }}>({count})</span></h2>
              {editing !== addKey && (
                <button type="button" style={{ ...s.quietBtn, padding: "6px 12px" }} onClick={() => { setMsgs([]); setEditing(addKey); }}>
                  + Add {MM_COUNT_NOUN[cat.id][0]}
                </button>
              )}
            </div>
            {editing === addKey && (
              <MenuItemEditor cat={cat} item={null} menus={liveMenus} isAdmin={isAdmin} presetMenuGuids={mid ? [mid] : null}
                onCancel={() => setEditing(null)}
                onSaved={(saved, w, created) => afterSave(cat, saved, w, created)}
                onRemoved={() => {}} />
            )}
            {count === 0 && editing !== addKey && <div style={{ color: t.textDim, fontSize: 12 }}>None on this menu.</div>}
            {secs.map(sec => (
              <div key={sec.name || "_"} style={{ marginBottom: 10 }}>
                {sec.name && <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", margin: "8px 0 6px" }}>{sec.name}</div>}
                {sec.items.map(it => {
                  const key = `${cat.id}:${it.id}`;
                  if (editing === key) {
                    return (
                      <MenuItemEditor key={key} cat={cat} item={it} menus={liveMenus} isAdmin={isAdmin}
                        onCancel={() => setEditing(null)}
                        onSaved={(saved, w, created) => afterSave(cat, saved, w, created)}
                        onRemoved={removed => { setEditing(null); setMsgs([{ kind: "success", text: `${removed.name} removed. It keeps its research and can be restored from Manage Items → Edit menu items → Show removed items.` }]); onChanged(); }} />
                    );
                  }
                  const out = it.available === false;
                  const showDesc = (cat.kind === "food" || (cat.kind === "bev" && !cat.enrichable)) && it.description;
                  return (
                    <button key={key} type="button" onClick={() => { setMsgs([]); setEditing(key); }}
                      style={{ display: "block", width: "100%", boxSizing: "border-box", textAlign: "left", background: "none", border: "none", borderBottom: `0.5px solid ${t.borderSubtle}`, padding: "8px 2px", cursor: "pointer", fontFamily: t.fontSerif, opacity: out ? 0.55 : 1 }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                        <div style={{ flex: 1, minWidth: 0, color: t.textPrimary, fontSize: 14 }}>
                          {it.name}
                          {out && <span style={{ color: t.warning, fontSize: 10, marginLeft: 8 }}>86'd</span>}
                        </div>
                        <div style={{ color: t.textSecondary, fontSize: 12, whiteSpace: "nowrap" }}>{mmPriceText(it, cat.kind)}</div>
                      </div>
                      {showDesc && <div style={{ color: t.textDim, fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>{it.description}</div>}
                    </button>
                  );
                })}
              </div>
            ))}
          </section>
        );
      })}
    </>
  );
}

// Hours, rooms and name for one menu NAME, saved to every record behind it
// (Dinner food and Dinner cocktails), so the parts of a menu cannot drift apart.
// Owner-only, as saveMenu is on the server.
function GroupMenuEditor({ group, locationNames, onSaved, onCancel, onDeleted }) {
  const s = useMmStyles(); const t = s.t;
  const first = group.menus[0];
  const [draft, setDraft] = useState(() => ({ label: group.label, locations: first.locations || [], ...scheduleToForm(first.schedule) }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = patch => setDraft(d => ({ ...d, ...patch }));
  const typeNames = group.menus.map(m => (MENU_TYPES.find(o => o.value === m.menuType)?.label || m.menuType));
  const differ = group.menus.some(m => JSON.stringify(m.schedule || null) !== JSON.stringify(first.schedule || null)
    || JSON.stringify([...(m.locations || [])].sort()) !== JSON.stringify([...(first.locations || [])].sort()));

  async function save() {
    setBusy(true); setError(null);
    try {
      const schedule = formToSchedule(draft);
      for (const m of group.menus) {
        await mmCall("saveMenu", { menu: { id: m.id, label: draft.label.trim(), menuType: m.menuType, locations: draft.locations, schedule } });
      }
      onSaved(draft.label.trim());
    } catch (e) { setError(e.message); setBusy(false); }
  }

  async function remove() {
    if (!window.confirm(`Delete "${group.label}"?\n\nItems that are only on this menu are removed from the guest menu too. They keep their research, and you can bring them back from Removed items.`)) return;
    setBusy(true); setError(null);
    let archived = 0;
    try {
      for (const m of group.menus) archived += Number((await mmCall("deleteMenu", { id: m.id })).archived || 0);
      onDeleted(group.label, archived);
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div style={{ background: t.bgCard, border: `0.5px solid ${t.accentBorder}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      {error && <MmNotice kind="error">{error}</MmNotice>}
      {differ && <MmNotice kind="warning">The parts of this menu ({typeNames.join(", ")}) have different hours or rooms. Saving gives them all the ones below.</MmNotice>}
      <label style={s.label} htmlFor="mm-group-label">Menu name</label>
      <input id="mm-group-label" style={{ ...s.input, marginBottom: 6 }} value={draft.label} onChange={e => set({ label: e.target.value })} />
      <div style={{ color: t.textDim, fontSize: 11, marginBottom: 14 }}>Has {typeNames.join(" and ").toLowerCase()} on it.</div>
      <label style={s.label}>Shown in</label>
      <RoomChoice locations={draft.locations} locationNames={locationNames} onChange={locations => set({ locations })} />
      <ScheduleFields form={draft} onChange={set} />
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" style={s.primaryBtn(busy || !draft.label.trim())} disabled={busy || !draft.label.trim()} onClick={save}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" style={s.quietBtn} onClick={onCancel} disabled={busy}>Cancel</button>
        <div style={{ flex: 1 }} />
        <button type="button" style={s.dangerBtn} onClick={remove} disabled={busy}>Delete menu</button>
      </div>
    </div>
  );
}

// Screens a link may open Manager on: ?manager=<view>. Used by the admin setup
// checklist so "Upload the menu" lands on Update Menu rather than the guest home.
const MANAGER_LINK_VIEWS = ['review', 'sync', 'print', 'settings', 'devices', 'menus', 'account'];

// Shrinks a photo before upload. A phone camera shot is 3-12 MB, and as base64 in
// a JSON body that is past what a Cloud Function will accept - and far more than
// a 50px list thumbnail or a kiosk label view needs. 1200px on the long side,
// JPEG at 0.85, lands around 150-400 KB.
function downscaleImageFile(file, maxDim = 1200) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        // White behind any transparency - a PNG cut-out would otherwise turn
        // black when flattened to JPEG.
        ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        URL.revokeObjectURL(url);
        resolve({ base64: dataUrl.split(",")[1], contentType: "image/jpeg" });
      } catch (e) { URL.revokeObjectURL(url); reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("That file could not be read as an image.")); };
    img.src = url;
  });
}

// ─── Account & billing (Oct 31, 2026) ────────────────────────────────────────
// Owner screen over the `billing` endpoint. Card, invoices and cancelling are
// Stripe's hosted portal ("Manage billing"); plan, chat and languages are here,
// because the portal can't change a subscription with more than one product.
// Starting a subscription goes to Stripe Checkout and comes back to
// ?manager=account&billing=success, which reopens this screen; the webhook may
// land a second or two later, so the screen polls until the subscription shows.
// Manager-portal text: English for now (i18n step 5 covers the manager screens).
const BILLING_URL = `${BASE}/billing`;
const BILLING_TIER_NAMES = { select: 'Select', reserve: 'Reserve', estate: 'Estate' };
const BILLING_TIER_ORDER = ['select', 'reserve', 'estate'];
// Wines each tier covers (beer and spirits unlimited). Mirrors WINE_LIMITS in
// functions/index.js; getAccount also sends the server's copy as wines.limits.
const BILLING_WINE_LIMITS = { select: 50, reserve: 150, estate: 300 };

async function billingCall(action, body = {}) {
  const res = await rfetch(BILLING_URL, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...body }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || `Request failed (${res.status})`), { status: res.status, data });
  return data;
}

function formatBillingMoney(cents, currency = 'usd') {
  if (cents === null || cents === undefined) return '';
  const whole = cents % 100 === 0;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase(),
      minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100);
  } catch (e) { return `$${(cents / 100).toFixed(whole ? 0 : 2)}`; }
}
// Epoch ms -> "Nov 14, 2026" in the RESTAURANT's timezone. Trials end and
// renewals fall at noon restaurant time (Oct 31, 2026), which is the same date
// in UTC and every US zone - so this matches Stripe's checkout, portal and
// emails. (The browser's own zone could show a different date.)
function formatBillingDate(ms, tz = 'UTC') {
  if (!ms) return '';
  try { return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz }); }
  catch (e) { return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }); }
}
// 'YYYY-MM-DD' -> "Jan 30, 2027" (a date already in the restaurant's timezone).
function formatYmdLong(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
// 'YYYY-MM-DD' (already in the restaurant's timezone) -> "Nov 14". Built from
// the parts so the browser's own timezone can't move it a day.
function formatAllowanceDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
// Total for `qty` extra languages on a volume-priced language price: every unit
// is priced by the tier the total falls in, plus that tier's flat fee.
function languagePriceTotal(price, qty) {
  if (!price || !qty) return 0;
  if (!Array.isArray(price.tiers) || !price.tiers.length) return (price.amount || 0) * qty;
  const step = price.tiers.find(st => st.upTo === null || qty <= st.upTo) || price.tiers[price.tiers.length - 1];
  return (step.unit || 0) * qty + (step.flat || 0);
}

function AccountScreen({ t, onOpenLanguages = null }) {
  const [acct, setAcct] = useState(null);        // getAccount response
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState('');           // which action is running
  const [notice, setNotice] = useState(null);     // { tone: 'ok'|'warn'|'error', text }
  const [showHow, setShowHow] = useState(false);  // plan picker's "How it works"
  // Start-subscription choices
  const [tier, setTier] = useState('reserve');
  const [interval, setIntervalChoice] = useState('monthly');
  const [chat, setChat] = useState(true);
  // Change-plan choices (seeded from the subscription once loaded)
  const [newTier, setNewTier] = useState(null);
  const [newInterval, setNewInterval] = useState(null);
  const [extra, setExtra] = useState(null);
  const pollRef = useRef(null);
  // Billing dates in the restaurant's timezone (sent by getAccount).
  const fmtDate = ms => formatBillingDate(ms, (acct && acct.timezone) || 'UTC');

  const load = useCallback(async () => {
    try {
      const d = await billingCall('getAccount');
      setAcct(d); setLoadError('');
      const s = d.subscription;
      // Selections start from what renewal will bring (a scheduled change, if any).
      if (s) { const pl = s.pending || s; setNewTier(pl.tier); setNewInterval(pl.interval); setExtra(pl.extraLanguages || 0); }
      else {
        setExtra(d.languages ? d.languages.extraNeeded : 0);
        // Start the picker on the cheapest plan for the wines already uploaded.
        if (d.wines && d.wines.count > 0 && d.wines.recommended) setTier(d.wines.recommended.monthly);
      }
      return d;
    } catch (e) { setLoadError(e.message); return null; }
  }, []);

  useEffect(() => {
    // Back from Stripe Checkout. The webhook usually lands within a few seconds;
    // poll until the subscription appears (up to ~30s), then stop.
    const params = new URLSearchParams(window.location.search);
    const result = params.get('billing');
    if (result) {
      params.delete('billing');
      try { window.history.replaceState({}, '', window.location.pathname + (params.toString() ? `?${params}` : '')); } catch (e) {}
    }
    if (result === 'cancelled') setNotice({ tone: 'warn', text: 'Checkout was cancelled. Nothing was charged.' });
    load().then(d => {
      if (result !== 'success') return;
      if (d && d.subscription) { setNotice({ tone: 'ok', text: 'Your subscription is set up. Thank you!' }); return; }
      setNotice({ tone: 'ok', text: 'Thanks! Finishing setting up your subscription…' });
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries += 1;
        const again = await load();
        if ((again && again.subscription) || tries >= 15) {
          clearInterval(pollRef.current);
          setNotice(again && again.subscription
            ? { tone: 'ok', text: 'Your subscription is set up. Thank you!' }
            : { tone: 'warn', text: 'Payment went through, but your subscription is taking longer than usual to appear. Refresh in a minute; if it still isn’t here, contact SyncBev.' });
        }
      }, 2000);
    });
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  // Where Stripe should send the owner back: this page, reopened on Account.
  const returnUrl = () => {
    const u = new URL(window.location.href);
    u.searchParams.set('manager', 'account');
    u.searchParams.delete('billing');
    return u.toString();
  };

  const run = async (name, fn, okText) => {
    setBusy(name); setNotice(null);
    try {
      const out = await fn();
      const msg = typeof okText === 'function' ? okText(out || {}) : okText;
      if (msg) setNotice(typeof msg === 'string' ? { tone: 'ok', text: msg } : { tone: 'ok', ...msg });
      await load();
      return out;
    } catch (e) {
      setNotice({ tone: 'error', text: e.message });
      return null;
    } finally { setBusy(''); }
  };

  const go = url => { window.location.href = url; };

  // Asks the server what a change would do - and, for an upgrade after the
  // trial, the exact amount charged today - then confirms in plain words.
  const confirmChange = async (action, fields, what, thenText) => {
    const st = acct && acct.subscription ? acct.subscription.status : null;
    if (st === 'trialing') return window.confirm(`${what}?\n\nYou’re in your free trial, so nothing is charged now. The new price applies when billing starts.`);
    let pv;
    try { pv = await billingCall(action, { ...fields, preview: true }); }
    catch (e) { setNotice({ tone: 'error', text: e.message }); return false; }
    if (pv.preview === 'now') {
      const cur = (Object.values((acct && acct.catalog) || {})[0] || {}).currency || 'usd';
      const today = pv.amountDue === 0 ? 'Nothing is charged today: your account credit covers it.'
        : (pv.amountDue !== null && pv.amountDue !== undefined)
          ? `${formatBillingMoney(pv.amountDue, cur)} will be charged to your card today for the rest of this billing period.`
          : 'The difference for the rest of this billing period will be charged to your card today.';
      return window.confirm(`${what} now?\n\n${today}${thenText ? ` ${thenText}` : ''}`);
    }
    if (pv.preview === 'renewal') return window.confirm(`${what} at renewal on ${fmtDate(pv.effectiveAt)}?\n\nYou keep your current plan until then. Nothing is charged or credited today.`);
    if (pv.preview === 'kept') return window.confirm('Keep your current plan and cancel the scheduled change?\n\nNothing changes on your bill.');
    return true;
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const card = { background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 12, padding: '16px 18px', marginBottom: 14 };
  const heading = { color: t.accent, fontSize: 10, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 10 };
  const body = { color: t.textSecondary, fontSize: 13, lineHeight: 1.55 };
  const strong = { color: t.textPrimary, fontSize: 15 };
  const primaryBtn = disabled => ({ background: disabled ? t.white06 : t.accent, border: 'none', color: disabled ? t.textSecondary : t.bgBase,
    padding: '10px 16px', borderRadius: 8, cursor: disabled ? 'default' : 'pointer', fontFamily: t.fontSerif, fontSize: 14 });
  const secondaryBtn = disabled => ({ background: 'none', border: `0.5px solid ${t.borderStrong}`, color: disabled ? t.textDim : t.textPrimary,
    padding: '9px 14px', borderRadius: 8, cursor: disabled ? 'default' : 'pointer', fontFamily: t.fontSerif, fontSize: 13 });
  const pill = active => ({ flex: 1, padding: '10px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13, textAlign: 'center',
    border: `1.5px solid ${active ? t.accent : t.borderStrong}`, background: active ? t.accentBorderSm : t.white04, color: active ? t.textPrimary : t.textSecondary });
  const toneStyle = tone => tone === 'error'
    ? { background: t.errorDim, border: `0.5px solid ${t.errorBorder}`, color: t.error }
    : tone === 'warn'
      ? { background: t.warningDim, border: `0.5px solid ${t.warningBorder}`, color: t.warning }
      : { background: t.successDim, border: `0.5px solid ${t.success}`, color: t.success };

  const Notice = () => notice ? (
    <div style={{ ...toneStyle(notice.tone), borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, lineHeight: 1.5,
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ flex: 1, minWidth: 180 }}>{notice.text}</span>
      {notice.action && (
        <button onClick={notice.action.onClick} style={{ ...secondaryBtn(false), padding: '6px 12px', fontSize: 12 }}>{notice.action.label}</button>
      )}
    </div>
  ) : null;

  if (loadError && !acct) {
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div style={{ ...toneStyle('error'), borderRadius: 8, padding: '12px 14px', fontSize: 13 }}>Couldn’t load your account: {loadError}</div>
        <button onClick={load} style={{ ...secondaryBtn(false), marginTop: 12 }}>Try again</button>
      </div>
    );
  }
  if (!acct) return <div style={{ flex: 1, padding: 24, ...body }}>Loading your account…</div>;

  const cat = acct.catalog || {};
  const currency = (Object.values(cat)[0] || {}).currency || 'usd';
  const priceOf = key => (cat[key] ? cat[key].amount : null);
  const perLabel = iv => (iv === 'annual' ? '/year' : '/month');
  const sub = acct.subscription;
  const status = sub ? sub.status : null;
  const ended = !sub || ['canceled', 'incomplete_expired'].includes(status);
  const langs = acct.languages || { included: 3, selected: [], extraNeeded: 0, extraPaid: 0, maxExtra: 5 };
  const winesInfo = acct.wines || null;
  const wineLimits = (winesInfo && winesInfo.limits) || BILLING_WINE_LIMITS;
  const wineCount = winesInfo ? winesInfo.count || 0 : 0;
  const overageUnit = (winesInfo && winesInfo.unitAmount) || priceOf('wine_overage_monthly') || 200;

  // ── Not billed ─────────────────────────────────────────────────────────────
  if (acct.exempt) {
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div style={card}>
          <div style={heading}>Account</div>
          <div style={body}>This restaurant isn’t billed through SommSync. If that’s unexpected, contact SyncBev.</div>
        </div>
      </div>
    );
  }

  // ── Start a subscription ──────────────────────────────────────────────────
  // Kept short on purpose (Oct 31, 2026): each plan card shows what THIS list
  // would cost on it, the cheapest is tagged, and the per-wine detail sits
  // behind "How it works".
  if (ended) {
    const trialMonths = acct.trialUsed ? 0 : (acct.trialMonthsForNewSignups || 0);
    const trialEndsOn = trialMonths > 0 ? formatYmdLong(acct.trialEndsOnForNewSignups) : null;
    const annual = interval === 'annual';
    const perPeriod = annual ? '/year' : '/month';
    const extraFor = k => Math.max(0, wineCount - wineLimits[k]);
    // Plan + extra wines for this list, in the chosen interval. Annual plans pay
    // extra wines yearly too ($24 a wine, no "2 months free" on overage).
    const overageFor = iv => priceOf(`wine_overage_${iv}`) || overageUnit * (iv === 'annual' ? 12 : 1);
    const planCost = k => {
      const b = priceOf(`${k}_${interval}`);
      return b === null ? null : b + extraFor(k) * overageFor(interval);
    };
    const chatCost = k => priceOf(`chat_${k}_${interval}`);
    const fullCost = k => (planCost(k) === null ? null : planCost(k) + (chat ? (chatCost(k) || 0) : 0));
    const best = wineCount > 0
      ? BILLING_TIER_ORDER.filter(k => fullCost(k) !== null).sort((a, b) => fullCost(a) - fullCost(b))[0]
      : null;
    const total = fullCost(tier) || 0;
    const priceLabel = k => (planCost(k) === null ? '—' : `${formatBillingMoney(planCost(k), currency)}${perPeriod}`);
    const totalLabel = `${formatBillingMoney(total, currency)}${perPeriod}`;
    const pricesMissing = priceOf(`${tier}_${interval}`) === null;
    const small = { fontSize: 11, color: t.textSecondary };
    const linkBtn = { background: 'none', border: 'none', padding: 0, color: t.accent, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12, textDecoration: 'underline' };
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <Notice />
        {sub && (
          <div style={{ ...toneStyle('warn'), borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, lineHeight: 1.5 }}>
            Your subscription ended{sub.endedAt ? ` on ${fmtDate(sub.endedAt)}` : ''}. Start a new one to switch your guest app back on.
          </div>
        )}
        <div style={card}>
          <div style={heading}>{wineCount > 0 ? `Prices for your ${wineCount} wines` : 'Choose your plan'}</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {['monthly', 'annual'].map(iv => (
              <button key={iv} onClick={() => setIntervalChoice(iv)} aria-pressed={interval === iv} style={pill(interval === iv)}>
                {iv === 'monthly' ? 'Monthly' : 'Annual · 2 months free'}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
            {BILLING_TIER_ORDER.map(k => {
              const cost = planCost(k);
              const extra = extraFor(k);
              return (
                <button key={k} onClick={() => setTier(k)} aria-pressed={tier === k}
                  style={{ ...pill(tier === k), display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left', padding: '12px 14px', gap: 10 }}>
                  <span>
                    <span style={{ fontSize: 15 }}>{BILLING_TIER_NAMES[k]}</span>
                    {best === k && <span style={{ marginLeft: 8, fontSize: 10, letterSpacing: '1px', textTransform: 'uppercase', color: t.success, border: `0.5px solid ${t.success}`, borderRadius: 10, padding: '1px 7px' }}>Best value</span>}
                    <br /><span style={small}>Up to {wineLimits[k]} wines</span>
                  </span>
                  <span style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 16, color: t.textPrimary }}>{cost !== null ? priceLabel(k) : '—'}</span>
                    {extra > 0 && cost !== null && <><br /><span style={small}>{formatBillingMoney(priceOf(`${k}_${interval}`), currency)} + {extra} extra wines</span></>}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ ...body, fontSize: 12, marginBottom: 14 }}>
            Between plans? {formatBillingMoney(overageUnit, currency)} a month per extra wine. Only wines count.{' '}
            <button onClick={() => setShowHow(v => !v)} style={linkBtn}>{showHow ? 'Hide' : 'How it works'}</button>
            {showHow && (
              <div style={{ marginTop: 8, padding: '10px 12px', background: t.white04, borderRadius: 8, lineHeight: 1.55 }}>
                Each plan covers a number of wines. Over that, each extra wine is {formatBillingMoney(overageUnit, currency)} a month ({formatBillingMoney(overageFor('annual'), currency)} a year on annual plans).
                {/* The example shows only the extra, in the interval being viewed -
                    a plan total here read as "+$99", or as a monthly figure on Annual. */}
                {' '}For example, {wineLimits.select + 10} wines on Select is 10 extra wines × {formatBillingMoney(overageFor(interval), currency)} = {formatBillingMoney(10 * overageFor(interval), currency)} {annual ? 'a year' : 'a month'} extra.
                {' '}Beer and spirits are unlimited. Hidden wines don’t count, and a wine sold by the glass and the bottle counts once.
                {' '}If a bigger plan would ever cost you less, we’ll tell you.
              </div>
            )}
          </div>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={chat} onChange={e => setChat(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} />
            <span style={body}>
              <span style={{ color: t.textPrimary }}>Add Sommelier Chat</span>
              {chatCost(tier) !== null && <> · +{formatBillingMoney(chatCost(tier), currency)}{perPeriod}</>}
              <br /><span style={small}>Guests ask the AI sommelier anything. 2,000 questions a month.</span>
            </span>
          </label>
          <div style={{ borderTop: `1px solid ${t.borderSubtle}`, paddingTop: 12, marginBottom: 14 }}>
            <div style={{ ...strong, fontSize: 18 }}>{totalLabel}</div>
            <div style={{ ...body, fontSize: 12 }}>
              {trialMonths > 0
                ? <>Free until <span style={{ color: t.textPrimary }}>{trialEndsOn}</span>. We’ll remind you a week before; cancel anytime before then.</>
                : <>Billing starts today.</>}
            </div>
          </div>
          {pricesMissing && <div style={{ ...toneStyle('warn'), borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12 }}>Prices couldn’t be loaded. Try again in a moment.</div>}
          <button disabled={!!busy || pricesMissing}
            onClick={() => run('checkout', async () => {
              const d = await billingCall('startCheckout', { tier, interval, chat, returnUrl: returnUrl() });
              go(d.url);
            })}
            style={primaryBtn(!!busy || pricesMissing)}>
            {busy === 'checkout' ? 'Opening secure checkout…' : 'Continue to secure checkout'}
          </button>
          <div style={{ ...small, marginTop: 8 }}>Payment is handled by Stripe. Your card details never reach SommSync.</div>
        </div>
      </div>
    );
  }

  // ── Existing subscription ─────────────────────────────────────────────────
  const tierName = BILLING_TIER_NAMES[sub.tier] || sub.tier || 'Plan';
  const changeable = ['trialing', 'active', 'past_due'].includes(status);
  const statusLine = (() => {
    if (status === 'trialing') return { tone: 'ok', text: `Free trial · ends ${fmtDate(sub.trialEnd)}${sub.cancelAtPeriodEnd ? '' : ', then billing starts'}` };
    if (status === 'active') return { tone: 'ok', text: `Active · renews ${fmtDate(sub.currentPeriodEnd)}` };
    if (status === 'past_due') return { tone: 'warn', text: 'The last payment failed. Your guest app stays on while we retry the card for about two weeks — update it under Manage billing to settle it now.' };
    if (status === 'unpaid') return { tone: 'error', text: 'Payment failed and all retries are used up, so your guest app is off. Update your card under Manage billing and pay the open invoice to switch it back on.' };
    return { tone: 'warn', text: `Status: ${status}. Contact SyncBev if this looks wrong.` };
  })();
  // Shown up front in the cards that change the price, so the owner knows before
  // choosing anything that the trial makes changes free.
  const trialNote = status === 'trialing' ? (
    <div style={{ ...toneStyle('ok'), borderRadius: 8, padding: '8px 12px', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
      You’re in your free trial until {fmtDate(sub.trialEnd)}. Plan changes cost nothing until then; the new price starts when billing begins. Extra chat turns are charged when you buy them.
    </div>
  ) : null;
  const cancelLine = sub.cancelAtPeriodEnd
    ? `Set to cancel on ${fmtDate(sub.cancelAt || (status === 'trialing' ? sub.trialEnd : sub.currentPeriodEnd))}. Your guest app keeps working until then, and changes you make here apply until then. Changed your mind? Use Manage billing to renew.`
    : null;
  const basePrice = priceOf(`${sub.tier}_${sub.interval}`);
  const chatPrice = priceOf(`chat_${sub.tier}_${sub.interval}`);
  const langPrice = cat[`language_${sub.interval}`];
  const usage = acct.chatUsage;
  const turnsLeft = usage ? Math.max(0, usage.allowance + usage.bundleTurns - usage.used) : null;
  // Upgrades apply now; downgrades wait for renewal (decided Oct 31, 2026).
  // `pending` is the change already scheduled; `planned` what renewal brings.
  const renewDate = fmtDate(sub.currentPeriodEnd);
  const pending = status === 'trialing' ? null : (sub.pending || null);
  const planned = pending || sub;
  const intervalName = iv => (iv === 'annual' ? 'Annual' : 'Monthly');
  const pendingLines = pending ? [
    (pending.tier !== sub.tier || pending.interval !== sub.interval) && `Plan changes to SommSync ${BILLING_TIER_NAMES[pending.tier]} · ${intervalName(pending.interval)}`,
    (sub.chat && !pending.chat) && 'Sommelier Chat turns off',
    ((pending.extraLanguages || 0) < (sub.extraLanguages || 0)) && `Extra languages drop to ${pending.extraLanguages || 0}`,
  ].filter(Boolean) : [];
  const pendingDate = pending ? fmtDate(pending.effectiveAt) : '';
  const isDowngrade = (tier, iv) => BILLING_TIER_ORDER.indexOf(tier) < BILLING_TIER_ORDER.indexOf(sub.tier) || (sub.interval === 'annual' && iv === 'monthly');
  const planChanged = newTier && newInterval && (newTier !== planned.tier || newInterval !== planned.interval);
  const selectionIsCurrent = newTier === sub.tier && newInterval === sub.interval;
  const selectionIsDown = status !== 'trialing' && !selectionIsCurrent && isDowngrade(newTier, newInterval);
  const extraChanged = extra !== null && extra !== (planned.extraLanguages || 0);
  const chatOffPending = !!(pending && sub.chat && !pending.chat);
  const bundlePrice = priceOf('chat_bundle_500');

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
      <Notice />

      {/* Plan and status */}
      <div style={card}>
        <div style={heading}>Your plan</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
          <div style={strong}>SommSync {tierName} · {sub.interval === 'annual' ? 'Annual' : 'Monthly'}</div>
          {basePrice !== null && <div style={body}>{formatBillingMoney(basePrice, currency)}{perLabel(sub.interval)}</div>}
        </div>
        <div style={{ ...toneStyle(statusLine.tone), borderRadius: 8, padding: '8px 12px', fontSize: 12, lineHeight: 1.5, marginBottom: cancelLine ? 8 : 12 }}>{statusLine.text}</div>
        {cancelLine && <div style={{ ...toneStyle('warn'), borderRadius: 8, padding: '8px 12px', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>{cancelLine}</div>}
        {pendingLines.length > 0 && (
          <div style={{ ...toneStyle('warn'), borderRadius: 8, padding: '8px 12px', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
            <div>Scheduled for renewal on {pendingDate}:</div>
            <ul style={{ margin: '4px 0 4px 18px', padding: 0 }}>{pendingLines.map(l => <li key={l}>{l}</li>)}</ul>
            <div>You keep your current plan until then.</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button disabled={!!busy}
            onClick={() => run('portal', async () => { const d = await billingCall('openPortal', { returnUrl: returnUrl() }); go(d.url); })}
            style={primaryBtn(!!busy)}>
            {busy === 'portal' ? 'Opening…' : 'Manage billing'}
          </button>
          {pendingLines.length > 0 && (
            <button disabled={!!busy}
              onClick={() => run('keep', () => billingCall('keepCurrent'), 'Kept your current plan. The scheduled change is cancelled.')}
              style={secondaryBtn(!!busy)}>
              {busy === 'keep' ? 'Keeping…' : 'Keep my current plan'}
            </button>
          )}
          {pendingLines.length > 0 && (
            <button disabled={!!busy}
              onClick={() => {
                if (!window.confirm(`Cancel your subscription at renewal on ${renewDate}?\n\nYour guest app keeps working until then. The scheduled change is dropped. Nothing more is charged after that date.`)) return;
                run('cancel', () => billingCall('cancelAtRenewal'), `Your subscription will end on ${renewDate}.`);
              }}
              style={secondaryBtn(!!busy)}>
              {busy === 'cancel' ? 'Cancelling…' : 'Cancel at renewal'}
            </button>
          )}
        </div>
        <div style={{ ...body, fontSize: 11, marginTop: 6 }}>
          {pendingLines.length > 0
            ? 'Update your card or download invoices in Manage billing. While a change is scheduled, cancel here.'
            : 'Update your card, download invoices, or cancel. Opens Stripe’s secure billing page. Cancelling takes effect at the end of the period you’ve paid for.'}
        </div>
      </div>

      {/* Change plan */}
      {changeable && (
        <div style={card}>
          <div style={heading}>Change plan</div>
          {trialNote}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {['monthly', 'annual'].map(iv => (
              <button key={iv} onClick={() => setNewInterval(iv)} aria-pressed={newInterval === iv} style={pill(newInterval === iv)}>
                {iv === 'monthly' ? 'Monthly' : 'Annual · 2 months free'}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {BILLING_TIER_ORDER.map(k => (
              <button key={k} onClick={() => setNewTier(k)} aria-pressed={newTier === k} style={{ ...pill(newTier === k), padding: '10px 4px' }}>
                <div>{BILLING_TIER_NAMES[k]}</div>
                <div style={{ fontSize: 11, color: t.textSecondary, marginTop: 2 }}>
                  {priceOf(`${k}_${newInterval}`) !== null ? `${formatBillingMoney(priceOf(`${k}_${newInterval}`), currency)}${perLabel(newInterval)}` : ''}
                </div>
                {sub.chat && priceOf(`chat_${k}_${newInterval}`) !== null && (
                  <div style={{ fontSize: 11, color: t.textSecondary }}>+ {formatBillingMoney(priceOf(`chat_${k}_${newInterval}`), currency)} chat</div>
                )}
                <div style={{ fontSize: 11, color: t.textSecondary }}>up to {wineLimits[k]} wines, then {formatBillingMoney(overageUnit, currency)} each</div>
              </button>
            ))}
          </div>
          {planChanged && status !== 'trialing' && (
            <div style={{ ...body, fontSize: 12, marginBottom: 12 }}>
              {selectionIsCurrent
                ? 'Keeps your current plan and cancels the scheduled plan change.'
                : selectionIsDown
                  ? `Takes effect at renewal on ${renewDate}. You keep SommSync ${tierName} · ${intervalName(sub.interval)} until then.`
                  : newInterval !== sub.interval
                    ? 'Switching to annual applies now. The difference is charged to your card today.'
                    : 'The change applies now. The difference for the rest of this billing period is charged to your card today.'}
              {sub.chat && !selectionIsCurrent ? ' Sommelier Chat moves to the matching price.' : ''}
            </div>
          )}
          <button disabled={!planChanged || !!busy}
            onClick={async () => {
              const target = `SommSync ${BILLING_TIER_NAMES[newTier]} · ${intervalName(newInterval)}`;
              const newPrice = priceOf(`${newTier}_${newInterval}`);
              const thenText = newInterval === sub.interval && newPrice !== null
                ? `Then ${formatBillingMoney(newPrice + (sub.chat ? (priceOf(`chat_${newTier}_${newInterval}`) || 0) : 0), currency)}${perLabel(newInterval)}${sub.chat ? ' with chat' : ''} from ${renewDate}.` : '';
              if (!(await confirmChange('changePlan', { tier: newTier, interval: newInterval },
                selectionIsCurrent ? `Keep ${target}` : `Change to ${target}`, thenText))) return;
              run('plan', () => billingCall('changePlan', { tier: newTier, interval: newInterval }),
                out => out.applied === 'renewal' ? `Scheduled: ${target} from ${fmtDate(out.effectiveAt)}.`
                  : out.applied === 'kept' ? 'Kept your current plan. The scheduled change is cancelled.'
                  : 'Your plan has been changed.');
            }}
            style={primaryBtn(!planChanged || !!busy)}>
            {busy === 'plan' ? 'Changing…' : !planChanged ? 'Change plan' : (selectionIsCurrent && status !== 'trialing') ? 'Keep current plan' : selectionIsDown ? 'Schedule change' : 'Change plan'}
          </button>
        </div>
      )}

      {/* Wine list against the plan. Says what is billed and when the bill next
          updates - not how the count is worked out. */}
      {winesInfo && (
        <div style={card}>
          <div style={heading}>Wine list</div>
          {(() => {
            const live = winesInfo.liveCount !== undefined ? winesInfo.liveCount : winesInfo.count;
            const liveOver = winesInfo.liveOver !== undefined ? winesInfo.liveOver : winesInfo.over;
            const billed = winesInfo.billedExtra || 0;
            const per = sub.interval === 'annual' ? '/year' : '/month';
            const nextOn = winesInfo.nextUpdate ? formatYmdLong(winesInfo.nextUpdate) : null;
            return (
              <>
                <div style={{ ...strong, marginBottom: 4 }}>{live} of {winesInfo.limit} wines</div>
                <div style={{ height: 8, borderRadius: 4, background: t.white06, overflow: 'hidden', marginBottom: 6 }}>
                  <div style={{ height: '100%', width: `${Math.min(100, (live / Math.max(1, winesInfo.limit)) * 100)}%`, background: liveOver ? t.warning : t.accent }} />
                </div>
                <div style={{ ...body, fontSize: 12, marginBottom: 10 }}>
                  {billed > 0
                    ? (status === 'trialing'
                        ? <>{billed} extra wines on your plan, free during your trial.</>
                        : <>Billed for {billed} extra wines: {formatBillingMoney(billed * (winesInfo.billedUnit || overageUnit), currency)}{per}.</>)
                    : (liveOver === 0 && <>Hidden wines don’t count, and a wine sold by the glass and the bottle counts once. Beer and spirits are unlimited.</>)}
                  {liveOver !== billed && nextOn && <> Your list is now {liveOver > 0 ? `${liveOver} over your plan` : 'within your plan'}; your bill updates on {nextOn}.</>}
                </div>
                {acct.creditCents > 0 && (
                  <div style={{ ...body, fontSize: 12, marginBottom: 10 }}>Credit on your account: <span style={{ color: t.textPrimary }}>{formatBillingMoney(acct.creditCents, currency)}</span>, used toward your next charge.</div>
                )}
              </>
            );
          })()}
          {winesInfo.suggestion && (() => {
            const sg = winesInfo.suggestion;
            const already = planned && BILLING_TIER_ORDER.indexOf(planned.tier) >= BILLING_TIER_ORDER.indexOf(sg.tier);
            return (
              <div style={{ ...toneStyle('ok'), borderRadius: 8, padding: '10px 12px', fontSize: 12, lineHeight: 1.5 }}>
                <div style={{ marginBottom: already || !changeable ? 0 : 8 }}>
                  {BILLING_TIER_NAMES[sg.tier]} would save you about {formatBillingMoney(sg.saving, currency)}/month: {formatBillingMoney(sg.monthlyThere, currency)} instead of {formatBillingMoney(sg.monthlyNow, currency)}, with room for {wineLimits[sg.tier]} wines.
                  {already && ` You’re switching to ${BILLING_TIER_NAMES[planned.tier]} on ${pendingDate}.`}
                </div>
                {!already && changeable && (
                  <button disabled={!!busy}
                    onClick={() => {
                      const when = status === 'trialing' ? 'now (you’re in your free trial, so nothing is charged)' : `at renewal on ${renewDate}`;
                      if (!window.confirm(`Switch to SommSync ${BILLING_TIER_NAMES[sg.tier]} ${when}?`)) return;
                      run('switch', () => billingCall('switchAtRenewal', { tier: sg.tier }),
                        out => out.applied === 'renewal' ? `Switching to ${BILLING_TIER_NAMES[sg.tier]} on ${fmtDate(out.effectiveAt)}.` : `You’re now on ${BILLING_TIER_NAMES[sg.tier]}.`);
                    }}
                    style={secondaryBtn(!!busy)}>
                    {busy === 'switch' ? 'Switching…' : status === 'trialing' ? `Switch to ${BILLING_TIER_NAMES[sg.tier]}` : `Switch to ${BILLING_TIER_NAMES[sg.tier]} at renewal (${renewDate})`}
                  </button>
                )}
              </div>
            );
          })()}
          {winesInfo.customSuggested && (
            <div style={{ ...body, fontSize: 12, marginTop: 8 }}>More than 300 wines? A Custom plan may suit you better. Contact SyncBev and we’ll work it out with you.</div>
          )}
        </div>
      )}

      {/* Sommelier Chat */}
      <div style={card}>
        <div style={heading}>Sommelier Chat</div>
        {sub.chat ? (
          <>
            {usage && (
              <>
                <div style={{ ...strong, marginBottom: 4 }}>{usage.used.toLocaleString('en-US')} of {(usage.allowance + usage.bundleTurns).toLocaleString('en-US')} turns used</div>
                <div style={{ height: 8, borderRadius: 4, background: t.white06, overflow: 'hidden', marginBottom: 6 }}>
                  <div style={{ height: '100%', width: `${Math.min(100, (usage.used / Math.max(1, usage.allowance + usage.bundleTurns)) * 100)}%`, background: turnsLeft === 0 ? t.error : t.accent }} />
                </div>
                <div style={{ ...body, fontSize: 12, marginBottom: 12 }}>
                  {usage.bundleTurns > 0 ? `Includes ${usage.bundleTurns.toLocaleString('en-US')} extra turns bought this month. ` : ''}
                  Resets {formatAllowanceDate(usage.resetsOn)}. Unused turns don’t carry over.
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button disabled={!!busy || (status !== 'trialing' && status !== 'active')}
                onClick={() => {
                  if (!window.confirm(`Buy 500 more chat turns${bundlePrice !== null ? ` for ${formatBillingMoney(bundlePrice, currency)}` : ''}?\n\nYour card on file is charged now. Extra turns last until ${formatAllowanceDate(usage && usage.resetsOn) || 'your next reset'}.`)) return;
                  run('bundle', () => billingCall('buyBundle'), '500 extra turns added.');
                }}
                style={secondaryBtn(!!busy || (status !== 'trialing' && status !== 'active'))}>
                {busy === 'bundle' ? 'Buying…' : `Buy 500 more turns${bundlePrice !== null ? ` · ${formatBillingMoney(bundlePrice, currency)}` : ''}`}
              </button>
              {changeable && chatOffPending && (
                <button disabled={!!busy}
                  onClick={() => run('chat', () => billingCall('setChat', { on: true }), 'Sommelier Chat stays on.')}
                  style={secondaryBtn(!!busy)}>
                  {busy === 'chat' ? 'Keeping…' : 'Keep chat'}
                </button>
              )}
              {changeable && !chatOffPending && (
                <button disabled={!!busy}
                  onClick={() => {
                    const msg = status === 'trialing'
                      ? 'Turn off Sommelier Chat now?\n\nGuests will no longer see the chat. Instant wine pairing stays on.'
                      : `Turn off Sommelier Chat at renewal on ${renewDate}?\n\nChat stays on until then. Guests won’t see it after that, and it isn’t charged again. Instant wine pairing stays on.`;
                    if (!window.confirm(msg)) return;
                    run('chat', () => billingCall('setChat', { on: false }),
                      out => out.applied === 'renewal' ? `Sommelier Chat turns off on ${fmtDate(out.effectiveAt)}.` : 'Sommelier Chat is off.');
                  }}
                  style={secondaryBtn(!!busy)}>
                  {busy === 'chat' ? 'Turning off…' : 'Turn off chat'}
                </button>
              )}
            </div>
            {chatOffPending && <div style={{ ...body, fontSize: 12, marginTop: 8, color: t.warning }}>Sommelier Chat turns off on {pendingDate}.</div>}
            {/* Auto top-up: buys a 500-turn bundle when the month's turns run low,
                up to the owner's monthly limit, so chat never stops mid-service. */}
            {changeable && acct.autoTopUp && (() => {
              const at = acct.autoTopUp;
              const save = (enabled, maxPerMonth) => run('auto', () => billingCall('setAutoTopUp', { enabled, maxPerMonth }),
                enabled ? `Auto top-up is on: up to ${maxPerMonth} ${maxPerMonth === 1 ? 'time' : 'times'} a month.` : 'Auto top-up is off.');
              return (
                <div style={{ marginTop: 12, padding: '10px 12px', background: t.white04, borderRadius: 8 }}>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: busy ? 'default' : 'pointer' }}>
                    <input type="checkbox" checked={at.enabled} disabled={!!busy} onChange={e => save(e.target.checked, at.maxPerMonth)} style={{ marginTop: 3, width: 18, height: 18 }} />
                    <span style={body}>
                      <span style={{ color: t.textPrimary }}>Auto top-up</span>
                      <br /><span style={{ fontSize: 12 }}>When you’re running low, add 500 turns automatically{bundlePrice !== null ? ` (${formatBillingMoney(bundlePrice, currency)} each)` : ''}, so guests never lose the chat mid-service.</span>
                    </span>
                  </label>
                  {at.enabled && (
                    <div style={{ ...body, fontSize: 12, marginTop: 8, marginLeft: 28 }}>
                      Up to{' '}
                      <select value={at.maxPerMonth} disabled={!!busy} onChange={e => save(true, Number(e.target.value))}
                        style={{ background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, borderRadius: 6, padding: '4px 6px', fontFamily: t.fontSerif, fontSize: 16 }}>
                        {(at.choices || [1, 2, 3, 5, 10]).map(n => (
                          <option key={n} value={n}>{n} {n === 1 ? 'time' : 'times'} a month{bundlePrice !== null ? ` (${formatBillingMoney(bundlePrice * n, currency)} at most)` : ''}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div style={{ ...body, fontSize: 11, marginTop: 6, marginLeft: 28 }}>
                    Charged to your card when added{status === 'trialing' ? ', even during your free trial' : ''}. Extra turns don’t carry over to the next month.
                  </div>
                </div>
              );
            })()}
          </>
        ) : (
          <>
            <div style={{ ...body, marginBottom: 12 }}>
              Let guests ask the AI sommelier anything about your list: pairings, styles, a bottle for a birthday. Includes 2,000 turns a month.
              {chatPrice !== null && <> <span style={{ color: t.textPrimary }}>{formatBillingMoney(chatPrice, currency)}{perLabel(sub.interval)}</span>{status === 'trialing' ? ', free during your trial' : ''}.</>}
            </div>
            {changeable && (
              <button disabled={!!busy}
                onClick={async () => {
                  if (!(await confirmChange('setChat', { on: true }, 'Add Sommelier Chat',
                    chatPrice !== null ? `Then ${formatBillingMoney(chatPrice, currency)}${perLabel(sub.interval)} from ${renewDate}.` : ''))) return;
                  run('chat', () => billingCall('setChat', { on: true }), 'Sommelier Chat is on.');
                }}
                style={primaryBtn(!!busy)}>
                {busy === 'chat' ? 'Turning on…' : 'Add Sommelier Chat'}
              </button>
            )}
          </>
        )}
      </div>

      {/* Guest languages */}
      <div style={card}>
        <div style={heading}>Guest languages</div>
        <div style={{ ...body, marginBottom: 10 }}>
          Your plan includes {langs.included} languages. Your guest app currently offers {langs.selected.length}
          {langs.selected.length ? `: ${langs.selected.map(code => (GUEST_LANGUAGES.find(g => g.code === code) || {}).english || code).join(', ')}` : ''}.
          {onOpenLanguages && <> <button onClick={onOpenLanguages} style={{ background: 'none', border: 'none', padding: 0, color: t.accent, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13, textDecoration: 'underline' }}>Choose languages</button></>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ color: t.textPrimary, fontSize: 14, flex: 1 }}>Extra languages</div>
          <button aria-label="Fewer" disabled={!changeable || extra <= 0 || !!busy} onClick={() => setExtra(v => Math.max(0, v - 1))} style={{ ...secondaryBtn(!changeable || extra <= 0), width: 40, padding: '8px 0' }}>−</button>
          <div style={{ ...strong, minWidth: 20, textAlign: 'center' }}>{extra}</div>
          <button aria-label="More" disabled={!changeable || extra >= langs.maxExtra || !!busy} onClick={() => setExtra(v => Math.min(langs.maxExtra, v + 1))} style={{ ...secondaryBtn(!changeable || extra >= langs.maxExtra), width: 40, padding: '8px 0' }}>+</button>
        </div>
        <div style={{ ...body, fontSize: 12, marginBottom: 12 }}>
          {extra > 0 && langPrice ? `${formatBillingMoney(languagePriceTotal(langPrice, extra), currency)}${perLabel(sub.interval)} for ${extra} extra${status === 'trialing' ? ', free during your trial' : ''}. ` : 'No extra languages. '}
          {status !== 'trialing' && extra < (sub.extraLanguages || 0) && extraChanged && `Fewer languages take effect at renewal on ${renewDate}; you keep ${sub.extraLanguages} extra until then. `}
          {pending && (pending.extraLanguages || 0) < (sub.extraLanguages || 0) && !extraChanged && `Drops to ${pending.extraLanguages || 0} extra on ${pendingDate}. `}
          Up to {langs.maxExtra} extra ({langs.included + langs.maxExtra} languages in all). Need more? Contact SyncBev.
          {langs.extraNeeded > 0 && extra < langs.extraNeeded && <><br /><span style={{ color: t.warning }}>Your guest app offers {langs.selected.length} languages, which needs at least {langs.extraNeeded} extra. Remove languages in Settings first.</span></>}
        </div>
        {changeable && (
          <button disabled={!extraChanged || extra < langs.extraNeeded || !!busy}
            onClick={async () => {
              if (extra > (sub.extraLanguages || 0) && !(await confirmChange('setLanguages', { extra }, `Add ${extra - (sub.extraLanguages || 0)} extra language${extra - (sub.extraLanguages || 0) === 1 ? '' : 's'}`,
                langPrice ? `Then ${formatBillingMoney(languagePriceTotal(langPrice, extra), currency)}${perLabel(sub.interval)} for languages from ${renewDate}.` : ''))) return;
              run('langs', () => billingCall('setLanguages', { extra }), out =>
              out.applied === 'renewal' ? `Extra languages drop to ${extra} on ${fmtDate(out.effectiveAt)}.`
              : out.applied === 'kept' ? 'Kept your current languages. The scheduled change is cancelled.'
              : extra > langs.extraNeeded
                ? { text: `You now have ${extra} extra language${extra === 1 ? '' : 's'}. Next, choose which ${extra - langs.extraNeeded === 1 ? 'one' : 'ones'} your guests can pick.`,
                    action: onOpenLanguages ? { label: 'Choose languages', onClick: onOpenLanguages } : null }
                : (extra > 0 ? `You now have ${extra} extra language${extra === 1 ? '' : 's'}.` : 'Extra languages removed.'));
            }}
            style={primaryBtn(!extraChanged || extra < langs.extraNeeded || !!busy)}>
            {busy === 'langs' ? 'Updating…' : 'Update languages'}
          </button>
        )}
      </div>
    </div>
  );
}

function ManagerScreen({ wines, onClose, isAdmin, tabletLocation = "all", onSetLocation, locationNames = { bar: "Bar", dining: "Dining Room" }, plan = "premium", showWelcome, onSetShowWelcome, onDeviceSetupSaved, onLocationNamesSaved, deviceSetup = {}, isPublicDemoTenant = false, initialView = null }) {
  const t = useTheme();
  // initialView comes from a setup link (see MANAGER_LINK_VIEWS). Settings, Add
  // a Tablet and Account are owner screens; anyone else lands on the home screen.
  const [managerView, setManagerView] = useState(() =>
    initialView && MANAGER_LINK_VIEWS.includes(initialView) && (isAdmin || !['settings', 'devices', 'account'].includes(initialView)) ? initialView : "home");
  // Release gate. Held means SommSync is still doing its accuracy pass and the
  // review screen stays shut. Defaults to released so restaurants onboarded
  // before this existed are never locked out by a flag they never set.
  const [releaseState, setReleaseState] = useState(null);
  useEffect(() => {
    rfetch(GET_RELEASE_STATE_URL)
      .then(r => r.json())
      .then(d => setReleaseState(d || { status: 'released' }))
      .catch(() => setReleaseState({ status: 'released' }));
  }, []);
  const reviewHeld = releaseState?.status === 'held';
  // Whether any category is file-managed, so the Menus & Items tile only shows
  // where it can do something. A fully Toast-synced restaurant never sees it.
  const [hasEditableMenu, setHasEditableMenu] = useState(false);
  // posType 'manual' = every menu comes from files. Those restaurants get Your
  // menus INSTEAD of Update menu: Update menu is a POS sync screen, and for
  // them it only ever held the upload, which now lives on Your menus.
  const [isManualTenant, setIsManualTenant] = useState(false);
  // Update menu opened from Your menus → "Import a CSV export": CSV form only,
  // and Back returns to Your menus.
  const [syncCsvOnly, setSyncCsvOnly] = useState(false);
  // Settings opened from Account & billing's "Choose languages": land on the
  // language editor. Cleared on leaving Settings.
  const [settingsLanguages, setSettingsLanguages] = useState(false);
  // Billing notices for owners on the dashboard: a failed payment, or a wine
  // list over the plan (with the saving, when a higher tier is cheaper).
  const [billingNotice, setBillingNotice] = useState(null);
  // Re-read every time the dashboard is shown (not just when Manager opens),
  // so a change made on another screen - hiding wines, a plan change - is
  // reflected on the way back. Uses today's count, like the Account page.
  const onDashboard = managerView === 'home';
  useEffect(() => {
    if (!isAdmin || isPublicDemoTenant || !onDashboard) return;
    let alive = true;
    billingCall('getAccount').then(d => {
      if (!alive || !d || d.exempt) return;
      const s = d.subscription, w = d.wines;
      const live = w ? (w.liveCount !== undefined ? w.liveCount : w.count) : 0;
      const liveOver = w ? (w.liveOver !== undefined ? w.liveOver : w.over) : 0;
      if (s && s.status === 'past_due') setBillingNotice({ tone: 'warn', text: 'Your last payment failed. Your guest app stays on while we retry; update your card to settle it.' });
      else if (s && s.status === 'unpaid') setBillingNotice({ tone: 'error', text: 'Payment failed and your guest app is off. Update your card to switch it back on.' });
      else if (s && w && liveOver > 0) setBillingNotice({ tone: 'warn', text: w.suggestion
        ? `Your wine list (${live}) is over your plan's ${w.limit}. ${BILLING_TIER_NAMES[w.suggestion.tier]} would save you about $${Math.round(w.suggestion.saving / 100)}/month.`
        : `Your wine list (${live}) is over your plan's ${w.limit}: ${liveOver} extra at $${Math.round(w.unitAmount / 100)} a wine per month.` });
      else setBillingNotice(null);
    }).catch(() => {});
    return () => { alive = false; };
  }, [isAdmin, isPublicDemoTenant, onDashboard]);
  useEffect(() => { if (managerView !== 'settings') setSettingsLanguages(false); }, [managerView]);
  useEffect(() => {
    rfetch(MENU_SOURCE_URL)
      .then(r => r.json())
      .then(d => {
        setHasEditableMenu(!!d && !!d.categories && Object.values(d.categories).some(v => v === false));
        setIsManualTenant(!!d && d.posType === 'manual');
      })
      .catch(() => {});
  }, []);
  const [activeTab, setActiveTab] = useState("uncertain");
  const [search, setSearch] = useState("");
  // Bulk enrichment. Scheduled syncs cover POS-connected restaurants, but a
  // tenant onboarded from a file has no sync to ride on - without a manual
  // trigger its items stay unenriched forever, which is why the old copy
  // promising the "next sync" was wrong for them.
  //
  // Keyed by category (wine/beer/pours) rather than a single object - this
  // used to be wine-only, which silently left beer and pours with no
  // enrichment path at all for a file-import tenant: triggerEnrichment
  // supports category on the backend, but nothing here ever sent it, so a
  // CSV-imported beer or pours list sat enriched-never with no error and no
  // control on screen to fix it. See getEnrichmentProgress / triggerEnrichment
  // in functions/index.js for the backend side of this.
  const [enrichRun, setEnrichRun] = useState({
    wine:  { running: false, done: 0, msg: '' },
    beer:  { running: false, done: 0, msg: '' },
    pours: { running: false, done: 0, msg: '' },
  });
  const [localWines, setLocalWines] = useState(wines);
  const [allItems, setAllItems] = useState([]);
  const [localCocktails, setLocalCocktails] = useState([]);
  const [localNab, setLocalNab] = useState([]);
  const [itemCounts, setItemCounts] = useState({ wine: 0, wineGlass: 0, beer: 0, pour: 0, cocktail: 0, nab: 0 });
  const [itemHeights, setItemHeights] = useState({});
  const [syncTimes, setSyncTimes] = useState({ lastUpdated: null, stockLastChecked: null });
  const [menuRefreshing, setMenuRefreshing] = useState(false);
  // Bumped at the end of every refreshMenuData, so All Items (which loads its own
  // beer/spirits/cocktail/food lists) reloads with everything else.
  const [itemsRefreshKey, setItemsRefreshKey] = useState(0);
  // Items guests cannot see right now (86'd or hidden), across every category,
  // for the Menu items hub. null until the first load.
  const [statusCount, setStatusCount] = useState(null);
  // Ids of items guests see no price for, every category (itemHasPrice). Feeds
  // the No price count and the Needs review total; the list itself is AllItemsTab.
  const [noPriceIds, setNoPriceIds] = useState([]);

  // Pulled out of the mount effect so enrichment, reimport, and a manual
  // refresh button can all re-run the same fetch instead of asking the
  // manager to close and reopen the screen to see updated counts. Previously
  // this only ran once on mount, which is why "Done — reload to see them"
  // meant a real reload (or a full close/re-login) rather than anything on
  // this screen actually updating.
  async function refreshMenuData() {
    setMenuRefreshing(true);
    try {
      const [wData, bData, pData, cData, nData, fData] = await Promise.all([
        rfetch(FIREBASE_URL + "?admin=1").then(r => r.json()),
        rfetch(BEER_URL + "?admin=1").then(r => r.json()),
        rfetch(POURS_URL + "?admin=1").then(r => r.json()),
        rfetch(COCKTAILS_URL + "?admin=1").then(r => r.json()),
        rfetch(NAB_URL + "?admin=1").then(r => r.json()),
        // Only for the hub's out-of-stock & hidden count. A failure here must not
        // sink the beverage refresh, so it resolves to an empty list instead.
        rfetch(FOOD_URL + "?admin=1").then(r => r.json()).catch(() => ({})),
      ]);
      const wineArr = Array.isArray(wData.wines) ? wData.wines : Object.values(wData.wines || {});
      setLocalWines(wineArr);

      // itemHeights computed in useEffect on canonical data below
      setSyncTimes({ lastUpdated: wData.lastUpdated, stockLastChecked: wData.stockLastChecked });
      const beers = (bData.beers || []).map(i => ({ ...i, _type: "beer" }));
      const pours = (pData.pours || []).map(i => ({ ...i, _type: "pour" }));
      setAllItems([...beers, ...pours]);
      setLocalCocktails((cData.cocktails || []).filter(i => !i.excluded));
      setLocalNab((nData.nab || []).filter(i => !i.excluded));
      {
        // Same test the Out of stock & hidden screen applies. Food here is the
        // guest-shaped read (admin=1), which can miss a dish on a menu that is
        // closed right now; that screen also unions the raw list, so its own
        // count can be a little higher outside service hours.
        const off = i => i.available === false || i.excluded === true;
        setStatusCount([wineArr, bData.beers, pData.pours, cData.cocktails, nData.nab, fData.foodItems]
          .reduce((n, list) => n + (list || []).filter(off).length, 0));
        // Ids can repeat across categories (different nodes), so prefix them.
        setNoPriceIds([
          ...wineArr.filter(i => !itemHasPrice(i, "wine")).map(i => `wines:${i.id}`),
          ...[["beer", bData.beers], ["pours", pData.pours], ["cocktails", cData.cocktails], ["nab", nData.nab]]
            .flatMap(([c, list]) => (list || []).filter(i => !itemHasPrice(i, "bev")).map(i => `${c}:${i.id}`)),
          ...(fData.foodItems || []).filter(i => !itemHasPrice(i, "food")).map(i => `food:${i.id}`),
        ]);
      }
      setItemCounts({
        wine:      wineArr.filter(w => !w.excluded).length,
        wineGlass: wineArr.filter(w => !w.excluded && w.glassPrice).length,
        beer:      (bData.beers     || []).filter(i => !i.excluded).length,
        pour:      (pData.pours     || []).filter(i => !i.excluded).length,
        cocktail:  (cData.cocktails || []).filter(i => !i.excluded).length,
        nab:       (nData.nab       || []).filter(i => !i.excluded).length,
      });
    } catch (e) {
      // Non-fatal - whatever screen triggered this keeps its own error
      // handling; a failed refresh just means counts stay as they were.
    } finally {
      setMenuRefreshing(false);
      setItemsRefreshKey(k => k + 1);
    }
  }

  useEffect(() => { refreshMenuData(); }, []);
  // Re-read on the way into Menu items (and back to the dashboard). Your menus
  // is its own screen with its own research run: an upload researched there
  // left this screen's counts as they were at sign-in, so Needs review said
  // "None" for 12 drinks waiting for approval until a reload and re-login
  // (Oct 28, 2026). The mount effect above covers the first visit.
  const viewSeenRef = useRef(false);
  useEffect(() => {
    if (!viewSeenRef.current) { viewSeenRef.current = true; return; }
    if (managerView === 'review' || managerView === 'home') refreshMenuData();
  }, [managerView]);

  // ── Compute exact print page heights from actual item data ──────────────
  // Runs whenever any category's canonical data changes.
  // Uses same 3-bucket character-length logic as the PDF generator:
  //   ≤54 chars → 9.5pt (1 line)   55-108 chars → 18pt (2 lines)   109+ → 27pt (3 lines)
  useEffect(() => {
    function itmH(name, sub) {
      const l = (name||'').length + (sub ? sub.length + 3 : 0);
      return l > 108 ? 27 : l > 54 ? 18 : 9.5;
    }
    function sgPt(arr) {
      const n = new Set(arr.map(i => i.subgroup || '_default')).size;
      return n > 1 ? n * 15 : 0; // 15pt per subgroup heading
    }
    function bucket(v) {
      const vl = (v||'').toLowerCase();
      if (/sparkling|prosecco|champagne|cava|crémant|brut/.test(vl)) return 'SPARKLING';
      if (/chardonnay/.test(vl)) return 'CHARDONNAY';
      if (/sauvignon blanc/.test(vl)) return 'SAV_BLANC';
      if (/pinot gri/.test(vl)) return 'PINOT_GRIGIO';
      if (/ros[eé]/.test(vl)) return 'ROSE';
      if (/cabernet sauvignon/.test(vl)) return 'CAB_SAV';
      if (/pinot noir/.test(vl)) return 'PINOT_NOIR';
      if (/merlot/.test(vl)) return 'MERLOT';
      if (/malbec/.test(vl)) return 'MALBEC';
      if (/zinfandel/.test(vl)) return 'ZINFANDEL';
      if (/dessert|port|sherry|tawny|sauternes|madeira|fortified/.test(vl)) return 'DESSERT';
      if (/albar|viognier|moscato|muscat|riesling|white blend|chenin|vinho verde|gewurz|marsanne|roussanne|godello|assyrtiko|picpoul|piquepoul|furmint|verdicchio|garganega|fiano|cortese|trebbiano|palomino|colombard|muscadet|melon de bourgogne|alvarinho|loureiro|arinto|xarel|macabeo|parellada|pecorino|arneis|grillo|vernaccia|falanghina|friulano|verdelho|viura|s[eé]millon|sylvaner|silvaner|scheurebe|kerner|soave|chablis/.test(vl)) return '_other_white';
      return '_other_red';
    }

    const wines_a = localWines.filter(w => !w.excluded);
    const beers_a = allItems.filter(i => i._type === 'beer');
    const pours_a = allItems.filter(i => i._type === 'pour');

    // Diagnostic: log first wine item structure so we can verify property names
    if (wines_a.length > 0) {
      const sample = wines_a[0];
      console.log('[pageEst] Sample wine keys:', Object.keys(sample).join(', '));
      console.log('[pageEst] varietal:', sample.varietal, '| correctedName:', sample.correctedName, '| region:', sample.region);
    }

    // Wine: exact varietal heading count + per-item heights using correctedName + origin
    const varietalCount = new Set(wines_a.map(w => bucket(w.varietal))).size || 1;
    const wineHeadPt    = 22 + 2 * 18 + varietalCount * 15; // WINE + WHITE/RED WINES + varietals
    const wineItemsPt   = wines_a.reduce((s, w) => {
      const name   = w.correctedName || w.name || '';
      const origin = (w.region || '').split(',').pop().trim();
      return s + itmH(name, origin);
    }, 0);
    const wineGlassPt = 18 + wines_a.filter(w => w.glassPrice).reduce((s, w) => {
      const name   = w.correctedName || w.name || '';
      const origin = (w.region || '').split(',').pop().trim();
      return s + itmH(name, origin);
    }, 0);

    // Beer: section heading + subgroup headings + per-item heights
    const beerPt = beers_a.length
      ? 22 + sgPt(beers_a) + beers_a.reduce((s, i) =>
          s + itmH(i.correctedName||i.name||'', [i.style, i.brewery].filter(Boolean).join(' · ')), 0)
      : 0;

    // Pours: section heading + subgroup headings + per-item heights
    const poursPt = pours_a.length
      ? 22 + sgPt(pours_a) + pours_a.reduce((s, i) =>
          s + itmH(i.correctedName||i.name||'', [i.category, i.producer].filter(Boolean).join(' · ')), 0)
      : 0;

    // Cocktails
    const cktPt = localCocktails.length
      ? 22 + sgPt(localCocktails) + localCocktails.reduce((s, i) =>
          s + itmH(i.correctedName||i.name||'', (i.description||'').slice(0, 100)), 0)
      : 0;

    // NAB
    const nabPt = localNab.length
      ? 22 + sgPt(localNab) + localNab.reduce((s, i) =>
          s + itmH(i.correctedName||i.name||'', (i.description||'').slice(0, 100)), 0)
      : 0;

    setItemHeights({
      wine:      wineHeadPt + wineItemsPt,
      wineGlass: wineGlassPt,
      beer:      beerPt,
      pours:     poursPt,
      cocktails: cktPt,
      nab:       nabPt,
    });
  }, [localWines, allItems, localCocktails, localNab]);

  // Sync managerView to activeTab so existing content sections render correctly
  useEffect(() => {
    if (managerView === 'print') setActiveTab('print');
    else if (managerView === 'sync') setActiveTab('sync');
    else if (managerView === 'settings') setActiveTab('settings');
    // Menu items opens on its hub of three or four big choices (Oct 16, 2026),
    // not on a review list: a new or busy floor manager should not have to
    // decode a grid of nine tiles to find the one they need.
    else if (managerView === 'review') { setActiveTab('hub'); setSearch(''); }
  }, [managerView]);

  // A file-managed restaurant has no Update menu tile. A setup link
  // (?manager=sync, "Upload the menu") still lands somewhere useful.
  useEffect(() => {
    if (managerView === 'sync' && isManualTenant && !syncCsvOnly) setManagerView('menus');
    if (managerView === 'home' && syncCsvOnly) setSyncCsvOnly(false);
  }, [managerView, isManualTenant, syncCsvOnly]);

  function formatSyncTime(ts) {
    if (!ts) return 'Never';
    const d = new Date(ts);
    const now = new Date();
    const mins = Math.floor((now - d) / 60000);
    if (mins < 2) return 'Just now';
    if (mins < 60) return `${mins} minutes ago`;
    if (mins < 120) return '1 hour ago';
    if (d.toDateString() === now.toDateString()) return `Today at ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  function handleWineUpdate(id, fields) {
    setLocalWines(prev => prev.map(w => w.id === id ? { ...w, ...fields } : w));
  }

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [approving, setApproving] = useState(false);
  const [zoomedManagerItem, setZoomedManagerItem] = useState(null);
  // The restaurant's own photo (uploadItemImage). Always wins over the POS image
  // and any searched label, and shows to guests at once - no search, no approval.
  // The backend has existed since Aug 2026; nothing in the app called it, so a
  // wine with no searchable label had no way to get a picture at all.
  const photoInputRef = useRef(null);
  const photoTargetRef = useRef(null);
  const [photoBusyId, setPhotoBusyId] = useState(null);
  const PHOTO_TYPES = { wine: "wines", beer: "beers", pour: "spirits" };

  function choosePhotoFor(item, itemType) {
    photoTargetRef.current = { id: item.id, itemType };
    if (photoInputRef.current) { photoInputRef.current.value = ""; photoInputRef.current.click(); }
  }

  async function handlePhotoChosen(e) {
    const file = e.target.files && e.target.files[0];
    const target = photoTargetRef.current;
    if (!file || !target) return;
    setPhotoBusyId(target.id);
    try {
      const { base64, contentType } = await downscaleImageFile(file);
      const res = await rfetch(UPLOAD_ITEM_IMAGE_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: PHOTO_TYPES[target.itemType], itemId: target.id, imageBase64: base64, contentType }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || "Upload failed");
      const patch = { imageUrl: data.url };
      setLocalWines(prev => prev.map(w => w.id === target.id ? { ...w, ...patch } : w));
      setAllItems(prev => prev.map(i => i.id === target.id ? { ...i, ...patch } : i));
    } catch (err) {
      window.alert(`Photo not saved: ${err.message}`);
    }
    setPhotoBusyId(null);
  }
  const [upcScanWine, setUpcScanWine] = useState(null);

  // Distinct Toast menu groups on the wine list, in arrival order. Shown in the
  // Print tab so the manager can see what "Toast structure" would actually print.
  const wineSubgroups = React.useMemo(
    () => [...new Set((localWines || []).map(w => (w.subgroup || '').trim()).filter(Boolean))],
    [localWines]
  );

  function handleUpcEnrichComplete(id, fields) {
    handleWineUpdate(id, fields);
    setUpcScanWine(null);
  }

  function toggleSelect(id) {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function handleApproveItems(itemsToApprove) {
    if (itemsToApprove.length === 0) return;
    setApproving(true);
    try {
      await rfetch(APPROVE_ITEMS_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: itemsToApprove })
      });
      const approvedIds = new Set(itemsToApprove.map(i => i.itemId));
      setLocalWines(prev => prev.map(w => approvedIds.has(w.id) ? { ...w, pendingApproval: false, approved: true } : w));
      setAllItems(prev => prev.map(i => approvedIds.has(i.id) ? { ...i, pendingApproval: false, approved: true } : i));
      setSelectedIds(new Set());
    } catch (e) { console.error(e); }
    setApproving(false);
  }

  // Undo an approval without re-enriching. A misclick should not cost an API call
  // or overwrite a description the manager may have already corrected.
  //
  // ⚠️ This button sits in the exact screen position "✓ Approve" occupies for a
  // not-yet-approved item - both the collapsed list row and the expanded detail
  // view. Scrolling and tapping quickly down a list that mixes approved and
  // unapproved items (e.g. batch-approving a fresh pours rebuild alongside
  // already-approved beers) can hit this on an already-approved item with no
  // visible sign anything happened, since the patch below deliberately leaves
  // the description/image/enrichedAt untouched. Confirming here is cheap
  // insurance against exactly that - found after this happened for real.
  async function handleUnapprove(item) {
    if (!window.confirm(`Undo approval for "${item.name || item.correctedName || 'this item'}"? It will go back to pending review.`)) return;
    const entry = { itemId: item.id, itemType: item._type || "wine" };
    setApproving(true);
    try {
      await rfetch(APPROVE_ITEMS_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [entry], approve: false })
      });
      const patch = { pendingApproval: true, approved: false, manuallyEdited: false };
      setLocalWines(prev => prev.map(w => w.id === item.id ? { ...w, ...patch } : w));
      setAllItems(prev => prev.map(i => i.id === item.id ? { ...i, ...patch } : i));
    } catch (e) { console.error(e); }
    setApproving(false);
  }

  async function handleApproveAll() {
    const allPending = [...pendingWines, ...pendingOther].filter(i => !i.uncertain);
    const items = allPending.map(i => ({ itemId: i.id, itemType: i._type || "wine" }));
    await handleApproveItems(items);
  }

  async function handleApproveSelected() {
    const allPending = [...pendingWines, ...pendingOther].filter(i => !i.uncertain);
    const items = allPending.filter(i => selectedIds.has(i.id)).map(i => ({ itemId: i.id, itemType: i._type || "wine" }));
    await handleApproveItems(items);
  }

  async function handleApprove(item) {
    await handleApproveItems([{ itemId: item.id, itemType: item._type || "wine" }]);
  }

  async function handleExclude(item) {
    const itemType = item._type || "wine";
    try {
      await rfetch(SET_EXCLUSION_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId: item.id, itemType, excluded: true })
      });
      if (itemType === "wine") setLocalWines(prev => prev.map(w => w.id === item.id ? { ...w, excluded: true } : w));
      else setAllItems(prev => prev.map(i => i.id === item.id ? { ...i, excluded: true } : i));
    } catch (e) { console.error(e); }
  }

  async function handleUnexclude(item) {
    const itemType = item._type || "wine";
    try {
      await rfetch(SET_EXCLUSION_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId: item.id, itemType, excluded: false })
      });
      if (itemType === "wine") setLocalWines(prev => prev.map(w => w.id === item.id ? { ...w, excluded: false } : w));
      else setAllItems(prev => prev.map(i => i.id === item.id ? { ...i, excluded: false } : i));
    } catch (e) { console.error(e); }
  }

  const q = foldText(search).trim();
  const filterBySearch = list => q ? list.filter(w => searchHit(w.name, q)) : list;

  const [reviewSubTab, setReviewSubTab] = useState("pending");
  const [reviewEditingId, setReviewEditingId] = useState(null);
  const [reviewEditFields, setReviewEditFields] = useState({});
  const [reviewEditBaseline, setReviewEditBaseline] = useState({});
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewReEnriching, setReviewReEnriching] = useState(false);
  const [reviewEditResult, setReviewEditResult] = useState(null);

  function openReviewEdit(wine) {
    if (reviewEditingId === wine.id) { setReviewEditingId(null); return; }
    setReviewEditingId(wine.id);
    setReviewEditResult(null);
    const t = wine._type || "wine";
    const initial =
      t === "wine" ? { correctedName: wine.name || "", varietal: wine.varietal || "", region: wine.region || "", description: wine.description || "", reviews: wine.reviews || "", vintage: wine.vintage !== undefined ? wine.vintage : null, vineyardConfirmedNone: false }
      : t === "beer" ? { correctedName: wine.name || "", style: wine.style || "", brewery: wine.brewery || "", abv: wine.abv || "", description: wine.description || "" }
      : { correctedName: wine.name || "", category: wine.category || "", producer: wine.producer || "", abv: wine.abv || "", description: wine.description || "" };
    setReviewEditBaseline(initial);
    setReviewEditFields(initial);
  }

  async function handleReviewSave(wine) {
    setReviewSaving(true);
    const itemType = wine._type || "wine";
    try {
      await rfetch(MANAGER_UPDATE_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId: wine.id, itemType, updates: reviewEditFields })
      });
      if (itemType === "wine") handleWineUpdate(wine.id, { ...reviewEditFields, name: reviewEditFields.correctedName });
      else setAllItems(prev => prev.map(i => i.id === wine.id ? { ...i, ...reviewEditFields, name: reviewEditFields.correctedName } : i));
      setReviewEditResult({ ok: true, message: "✓ Saved" });
    } catch (e) { setReviewEditResult({ ok: false, message: e.message }); }
    setReviewSaving(false);
  }

  async function handleReviewReEnrich(wine) {
    setReviewReEnriching(true);
    setReviewEditResult(null);
    const itemType = wine._type || "wine";
    const hints = {};
    if (itemType === "wine") {
      Object.assign(hints, changedFields(reviewEditFields, reviewEditBaseline, ['region', 'varietal', 'correctedName']));
      hints.vintage = reviewEditFields.vintage !== undefined ? reviewEditFields.vintage : null;
      // Only sent when true - never an explicit false. reenrichItem's
      // substantiveHints check treats any non-empty value as the manager
      // vouching for something, and String(false) is not empty, so sending
      // false here would wrongly set identityConfirmed on every re-enrichment
      // regardless of whether the checkbox was ever touched.
      if (reviewEditFields.vineyardConfirmedNone === true) hints.vineyardConfirmedNone = true;
    } else if (itemType === "beer") {
      Object.assign(hints, changedFields(reviewEditFields, reviewEditBaseline, ['brewery', 'style']));
    } else if (itemType === "pour") {
      Object.assign(hints, changedFields(reviewEditFields, reviewEditBaseline, ['producer', 'category']));
    }
    try {
      const res = await rfetch(REENRICH_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId: wine.id, itemType, itemName: wine.name, hints })
      });
      const data = await res.json();
      if (data.ok) {
        const e = data.enrichment;
        if (itemType === "wine") {
          const updates = { correctedName: e.correctedName, varietal: e.varietal, region: e.region, description: e.description, reviews: e.reviews, vintage: e.vintage !== undefined ? e.vintage : null };
          setReviewEditFields(p => ({ ...p, ...updates }));
          setReviewEditBaseline(p => ({ ...p, ...updates }));
          handleWineUpdate(wine.id, { ...updates, name: e.correctedName, ...enrichmentStateFields(e) });
        } else {
          const updates = itemType === "beer"
            ? { correctedName: e.correctedName, style: e.style, brewery: e.brewery, abv: e.abv, description: e.description }
            : { correctedName: e.correctedName, category: e.category, producer: e.producer, abv: e.abv, description: e.description };
          setReviewEditFields(p => ({ ...p, ...updates }));
          setReviewEditBaseline(p => ({ ...p, ...updates }));
          setAllItems(prev => prev.map(i => i.id === wine.id ? { ...i, ...updates, name: e.correctedName, ...enrichmentStateFields(e) } : i));
        }
        setReviewEditResult({ ok: true, warn: !!e.uncertain, message: e.uncertain ? `Still uncertain: ${e.uncertainReason || "needs further review"}` : "✓ Re-enriched — no longer flagged" });
      } else {
        setReviewEditResult({ ok: false, message: data.error || "Re-enrichment failed" });
      }
    } catch (e) { setReviewEditResult({ ok: false, message: e.message }); }
    setReviewReEnriching(false);
  }

  // "Needs Approval" — items awaiting manager sign-off, not yet excluded
  const pendingWines = filterBySearch(localWines.filter(w => w.pendingApproval && !w.approved && !w.manuallyEdited && !w.excluded));
  const pendingOther = q ? allItems.filter(i => i.pendingApproval && !i.approved && !i.manuallyEdited && !i.excluded && searchHit(i.name, q))
    : allItems.filter(i => i.pendingApproval && !i.approved && !i.manuallyEdited && !i.excluded);
  const pending = [...pendingWines, ...pendingOther];
  const pendingClean = pending.filter(i => !i.uncertain); // passed enrichment — safe to bulk approve
  const uncertain = pendingClean; // Tab 1: "AI Verified, Ready for Approval" — excludes uncertain items

  const reviewedWines = filterBySearch(localWines.filter(w => w.approved || w.manuallyEdited));
  const reviewedOther = q ? allItems.filter(i => (i.approved || i.manuallyEdited) && searchHit(i.name, q)) : allItems.filter(i => i.approved || i.manuallyEdited);
  const reviewed = [...reviewedWines, ...reviewedOther];

  // "Unknown Beverages" — AI flagged as ambiguous, not yet approved or excluded
  const uncertainWines = filterBySearch(localWines.filter(w => w.uncertain && !w.approved && !w.excluded));
  const uncertainOther = q ? allItems.filter(i => i.uncertain && !i.approved && !i.excluded && searchHit(i.name, q)) : allItems.filter(i => i.uncertain && !i.approved && !i.excluded);
  const trueUncertain = [...uncertainWines, ...uncertainOther];
  const noImageWines = filterBySearch(localWines.filter(w => !w.imageUrl));
  const noImageOther = q ? allItems.filter(i => !i.imageUrl && searchHit(i.name, q)) : allItems.filter(i => !i.imageUrl);
  const noImage = [...noImageWines, ...noImageOther];
  const noPrice = filterBySearch(localWines.filter(w => !w.glassPrice && !w.bottlePrice));
  // !uncertain matters here for the same reason pendingClean excludes it above:
  // an item Claude correctly flagged uncertain is DELIBERATELY left without a
  // description (see enrichBeerWithClaude/enrichPourWithClaude's prompts - an
  // unidentifiable name gets no invented style/brewery/description rather than
  // a guessed one). Without this exclusion, every uncertain item satisfies
  // "no description" forever and shows up here as if never attempted, even
  // though it was correctly processed and is already sitting in Needs Further
  // Review waiting on a human. Found via a beer ("Old Spruce Garage Beer
  // Lime") that Claude correctly couldn't identify - it showed in both tabs
  // instead of just the one it belonged in.
  const unenriched = filterBySearch(localWines.filter(w => !w.description && !w.varietal && !w.uncertain));
  // Same "no description, no identifying field" test as wine's, adapted to
  // what beer/pours enrichment actually fills in (style/brewery for beer,
  // category/producer for pours - see enrichBeerWithClaude/enrichPourWithClaude
  // in functions/index.js). Not filtered by search, matching how the wine
  // button's count above isn't gated on it being visible in the current tab -
  // both buttons live outside the per-tab item list.
  // Matches the server's canonical isEnriched() exactly (functions/index.js):
  // `!!(e.description || e.varietal)`, which for beer/pours reduces to just
  // `!!description` since varietal is wine-only. Previously checked
  // style/brewery/category/producer too, which is stricter than the real
  // definition - an item with a style or brewery guess from an uncertain
  // Claude call but no description would pass this stricter test (look
  // "done") while getEnrichmentProgress's poll and triggerEnrichment's own
  // before/after count (both using the real isEnriched()) correctly still
  // counted it as pending. That mismatch is what caused the badge to say a
  // number the button's own item list didn't agree with.
  const unenrichedBeer = allItems.filter(i => i._type === 'beer' && !i.description && !i.uncertain);
  const unenrichedPours = allItems.filter(i => i._type === 'pour' && !i.description && !i.uncertain);
  const duplicateGroups = findDuplicates(q ? localWines.filter(w => searchHit(w.name, q)) : localWines);

  const excludedWines = filterBySearch(localWines.filter(w => w.excluded));
  const excludedOther = q ? allItems.filter(i => i.excluded && searchHit(i.name, q)) : allItems.filter(i => i.excluded);
  const excluded = [...excludedWines, ...excludedOther];

  async function runBulkEnrichment(category = 'wine') {
    if (enrichRun[category].running) return;
    setEnrichRun(p => ({ ...p, [category]: { running: true, done: 0, msg: 'Starting…' } }));
    let total = 0, masterHits = 0;
    // The enrichment request does not return until the whole pass is finished,
    // which can be many minutes. Poll the server-side counter alongside it so the
    // manager sees per-item movement rather than a stalled screen.
    let stop = false;
    const poll = setInterval(async () => {
      if (stop) return;
      try {
        const r = await rfetch(`${ENRICH_PROGRESS_URL}?category=${category}`);
        const p = await r.json();
        if (p && p.total) {
          setEnrichRun(prev => prev[category].running
            ? { ...prev, [category]: { ...prev[category], msg: `${p.done} of ${p.total} enriched` } }
            : prev);
        }
      } catch (e) {}
    }, 3000);
    // triggerEnrichment skips items that already have a record and is bounded
    // by its own 540s timeout, so a large list needs several passes. Looping
    // here rather than asking the manager to press the button repeatedly.
    for (let pass = 1; pass <= 12; pass++) {
      try {
        setEnrichRun(p => ({ ...p, [category]: { ...p[category], msg: p[category].msg && p[category].msg !== 'Starting…' ? p[category].msg : `Pass ${pass} — enriching…` } }));
        const res = await authFetch(TRIGGER_ENRICHMENT_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ restaurantId: RESTAURANT_ID, category })
        });
        const data = await res.json();
        if (data.error) { stop = true; clearInterval(poll); setEnrichRun(p => ({ ...p, [category]: { running: false, done: total, msg: `Stopped: ${data.error}` } })); return; }
        const n = Number(data.enriched ?? data.enrichedCount ?? 0);
        total += n;
        masterHits += Number(data.masterHits || 0);
        setEnrichRun(p => ({ ...p, [category]: { ...p[category], done: total } }));
        if (n === 0) break;
      } catch (e) {
        // A timeout mid-pass is expected on long lists; the work already done is
        // saved, so try the next pass rather than abandoning the run.
        setEnrichRun(p => ({ ...p, [category]: { ...p[category], msg: `Pass ${pass} interrupted, continuing…` } }));
      }
    }
    stop = true; clearInterval(poll);
    setEnrichRun(p => ({ ...p, [category]: { running: false, done: total, msg: `Done — ${total} enriched${masterHits ? `, ${masterHits} from the shared database` : ''}. Refreshing…` } }));
    // Previously this told the manager to reload the page (or, in practice,
    // close and re-login) to see updated counts. refreshMenuData() re-pulls
    // the same data the mount effect does, so the screen updates itself.
    await refreshMenuData();
    setEnrichRun(p => ({ ...p, [category]: { ...p[category], msg: `Done — ${total} enriched${masterHits ? `, ${masterHits} from the shared database` : ''}.` } }));
  }

  // The old nine-tile grid (and its `tabs` array) was replaced Oct 16, 2026 by the
  // Menu items hub below: Edit menu items, Out of stock & hidden, Needs review
  // (its six lists one level down), and Menus & hours for owners. Food Menu and
  // Hidden Items are covered by Edit menu items and Out of stock & hidden.

  const lists = { uncertain, unknown: trueUncertain, noimage: noImage, noprice: noPrice, unenriched: [...unenriched, ...unenrichedBeer, ...unenrichedPours], excluded };
  const current = lists[activeTab] || [];

  // ── Menu items: hub → (Edit / Out of stock & hidden / Needs review / Menus & hours)
  //    Needs review → its six lists. Back goes up one level, then to the dashboard.
  const REVIEW_TABS = [
    { id: "uncertain",  label: "AI verified, ready for approval", sub: "SommSync has researched these. Check and approve them so guests can see them.", count: uncertain.length },
    { id: "unenriched", label: "No description", sub: "Items SommSync hasn't researched yet.", count: unenriched.length + unenrichedBeer.length + unenrichedPours.length },
    { id: "noprice",    label: "No price", sub: "Items guests see no price for, on every part of the menu.", count: noPriceIds.length },
    { id: "duplicates", label: "Duplicates", sub: "The same wine listed more than once.", count: duplicateGroups.length },
    { id: "unknown",    label: "Needs further review", sub: "SommSync couldn't identify these. Add details, research again, or hide them.", count: trueUncertain.length },
    { id: "noimage",    label: "Missing images", sub: "No label or bottle photo yet.", count: noImage.length },
  ];
  // One number on the hub: how many ITEMS need a look, each counted once even if
  // it is on several lists (a new wine is often both unresearched and imageless).
  // Search is cleared on every move between screens, so these lists are unfiltered here.
  const reviewIds = new Set([
    ...[...uncertain, ...trueUncertain, ...noImage, ...unenriched, ...unenrichedBeer, ...unenrichedPours, ...duplicateGroups.flat()]
      .map(i => `${i._type === "beer" ? "beer" : i._type === "pour" ? "pours" : "wines"}:${i.id}`),
    ...noPriceIds,
  ]);
  const reviewTotal = reviewIds.size;
  const LIST_TABS = ["all", "unavailable", ...REVIEW_TABS.map(r => r.id), "excluded"];
  const parentTab = id => (REVIEW_TABS.some(r => r.id === id) || id === "excluded") ? "reviewhub" : "hub";
  function goTab(id) {
    setActiveTab(id); setSearch(''); setSelectedIds(new Set());
    if (id === "uncertain") setReviewSubTab("pending");
  }
  const reviewTitle = activeTab === "hub" ? "Manage Items"
    : activeTab === "all" ? "Edit menu items"
    : activeTab === "unavailable" ? "Out of stock & hidden"
    : activeTab === "reviewhub" ? "Needs review"
    : activeTab === "menus" ? "Menus & hours"
    : activeTab === "excluded" ? "Hidden items"
    : (REVIEW_TABS.find(r => r.id === activeTab) || {}).label || "Manage Items";
  const reviewCrumb = activeTab === "hub" ? "Manager"
    : parentTab(activeTab) === "reviewhub" ? "Manage Items › Needs review" : "Manage Items";

  // Big-card list, same look as the dashboard's own buttons.
  const hubCard = ({ id, icon, label, sub, count, countTone, onClick }) => (
    <button key={id} type="button" onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 16, background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 12, padding: "16px 18px", cursor: "pointer", textAlign: "left", width: "100%", boxSizing: "border-box", fontFamily: t.fontSerif }}>
      {icon && <div style={{ width: 34, display: "flex", justifyContent: "center", flexShrink: 0, color: t.accent }}><LineIcon name={icon} size={24} strokeWidth={1.5} /></div>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: t.textPrimary, fontSize: 15, marginBottom: 3 }}>{label}</div>
        <div style={{ color: t.textSecondary, fontSize: 11.5, lineHeight: 1.45 }}>{sub}</div>
      </div>
      {count !== undefined && count !== null && (
        count > 0
          ? <div style={{ minWidth: 34, padding: "4px 10px", borderRadius: 14, background: countTone === "warn" ? t.warningDim : t.white08, border: `0.5px solid ${countTone === "warn" ? t.warningBorder : t.borderMid}`, color: countTone === "warn" ? t.warning : t.textPrimary, fontSize: 15, fontWeight: 600, textAlign: "center", flexShrink: 0 }}>{count}</div>
          : <div style={{ color: t.success, fontSize: 13, flexShrink: 0, whiteSpace: "nowrap" }}><LineIcon name="check" inline />None</div>
      )}
      <div style={{ color: t.textDim, fontSize: 18, flexShrink: 0 }}>›</div>
    </button>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: t.bgOverlay, zIndex: 1000, display: "flex", alignItems: "stretch", justifyContent: "center", fontFamily: t.fontSerif }}>
      <div style={{ background: t.bgBase, display: "flex", flexDirection: "column", width: "100%", maxWidth: 780, height: "100%", boxShadow: `0 0 80px ${t.shadowStrong}`, borderLeft: `1px solid ${t.borderStrong}`, borderRight: `1px solid ${t.borderStrong}` }}>
      {/* Header */}
      <div style={{ background: t.bgSurface, borderBottom: `1px solid ${t.borderSubtle}`, padding: "16px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "3px", textTransform: "uppercase", marginBottom: 2 }}>{managerView === 'review' ? reviewCrumb : 'Manager'}</div>
            <div style={{ color: t.textPrimary, fontSize: 18 }}>
              {managerView === 'home' ? 'Wine List Dashboard' :
               managerView === 'review' ? reviewTitle :
               managerView === 'print' ? 'Print Beverage Menu' :
               managerView === 'sync' ? (syncCsvOnly ? 'Import a CSV' : 'Update Menu') :
               managerView === 'devices' ? 'Add a Tablet' :
               managerView === 'account' ? 'Account & billing' :
               managerView === 'menus' ? 'Menus & hours' : 'Settings'}
            </div>
            {/* RESTAURANT_ID is a module-level const set once at page load
                (detectRestaurantId()) and never re-evaluated - editing the URL
                bar in an already-open tab does nothing without a full reload.
                A tab left open from before a tenant switch silently keeps
                writing to the old one with no visual difference otherwise.
                This line exists so that mismatch is visible at a glance
                instead of discovered after a week of uploads land somewhere
                unexpected. */}
            <div style={{ color: t.textDim, fontSize: 10, letterSpacing: "1px", marginTop: 1 }}>
              editing: {RESTAURANT_ID}
            </div>
          </div>
          {managerView === 'home'
            ? <button onClick={onClose} style={{ background: t.accentDim, border: `0.5px solid ${t.accent}`, color: t.accent, padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>Close</button>
            : <button onClick={() => (managerView === 'review' && activeTab !== 'hub') ? goTab(parentTab(activeTab))
                : (managerView === 'sync' && syncCsvOnly) ? (setSyncCsvOnly(false), setManagerView('menus'))
                : setManagerView('home')} style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textMuted, padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>← Back</button>
          }
        </div>

        {/* Tablet Location Toggle — home only */}
        {managerView === 'home' && (
          <div style={{ marginTop: 14, background: t.white08, borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ color: t.textSecondary, fontSize: 9, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 8 }}>This Tablet Is In</div>
            <div style={{ display: "flex", gap: 6 }}>
              {/* Names, not logos (Oct 27, 2026). The logos only told the rooms apart
                  where each has its own brand (Corduroy's Tuque's and Appalachia
                  Kitchen); most restaurants have one logo, so both buttons looked
                  the same. The names are the ones set in Settings - "Bar" and
                  "Dining Room" by default, Tuques and AK at Corduroy. */}
              {[
                { id: "bar", label: locationNames.bar || "Bar" },
                { id: "dining", label: locationNames.dining || "Dining Room" },
              ].map(loc => {
                const active = tabletLocation === loc.id;
                return (
                  <button key={loc.id} onClick={() => onSetLocation && onSetLocation(loc.id)} aria-pressed={active} style={{
                    flex: 1, padding: "12px 8px", borderRadius: 8,
                    border: `1.5px solid ${active ? t.accent : t.borderStrong}`,
                    background: active ? t.accentBorderSm : t.white04,
                    color: active ? t.textPrimary : t.textSecondary, fontFamily: t.fontSerif, cursor: "pointer",
                    transition: "all 0.15s", textAlign: "center"
                  }}>
                    <div style={{ fontSize: 15, lineHeight: 1.25, overflowWrap: "anywhere" }}>{loc.label}</div>
                    <div style={{ fontSize: 9, color: t.accent, marginTop: 5, letterSpacing: "1px", visibility: active ? "visible" : "hidden" }}>✓ THIS DEVICE</div>
                  </button>
                );
              })}
            </div>
            <div style={{ color: t.textSecondary, fontSize: 12, marginTop: 8, fontStyle: "italic" }}>
              Sets which menus and food items the AI uses for pairing on this device. Persists across restarts.
            </div>
          </div>
        )}

      </div>

      {/* Search — only on screens that are a list of items (not the hubs or Menus & hours) */}
      {managerView === 'review' && LIST_TABS.includes(activeTab) && (
      <div style={{ padding: "8px 20px 4px", background: t.bgSurface }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <input type="text" placeholder="Search menu items…" value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: "7px 30px 7px 12px", borderRadius: 20, fontFamily: t.fontSerif, fontSize: 16, outline: "none" }}
            />
            {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: t.textDim, cursor: "pointer", fontSize: 16, padding: 0 }}>×</button>}
          </div>
          {/* Counts/enrichment status can change from something other than this
              screen (a background enrichment pass, another manager, a sync) -
              this covers that without needing a full page reload. */}
          <button onClick={refreshMenuData} disabled={menuRefreshing} title="Refresh menu data"
            style={{ background: "none", border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, borderRadius: 20, width: 28, height: 28, flexShrink: 0, cursor: menuRefreshing ? "default" : "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {menuRefreshing ? "…" : "↻"}
          </button>
        </div>
        {activeTab === "unenriched" && (unenriched.length > 0 || unenrichedBeer.length > 0 || unenrichedPours.length > 0) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {unenriched.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => runBulkEnrichment('wine')} disabled={enrichRun.wine.running}
                  style={{ background: enrichRun.wine.running ? t.white06 : t.accent, border: "none", color: enrichRun.wine.running ? t.textSecondary : t.bgBase, padding: "7px 14px", borderRadius: 6, cursor: enrichRun.wine.running ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                  {enrichRun.wine.running ? "Enriching…" : `Enrich ${unenriched.length} wine(s)`}
                </button>
                {enrichRun.wine.msg && <div style={{ color: t.textSecondary, fontSize: 11 }}>{enrichRun.wine.msg}</div>}
              </div>
            )}
            {/* Beer and pours have no path to enrichment on a file-import
                tenant besides this button - see the enrichRun state comment
                above. A CSV-imported restaurant with beer or pours items and
                nobody ever clicking these sits enriched-never indefinitely. */}
            {unenrichedBeer.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => runBulkEnrichment('beer')} disabled={enrichRun.beer.running}
                  style={{ background: enrichRun.beer.running ? t.white06 : t.accent, border: "none", color: enrichRun.beer.running ? t.textSecondary : t.bgBase, padding: "7px 14px", borderRadius: 6, cursor: enrichRun.beer.running ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                  {enrichRun.beer.running ? "Enriching…" : `Enrich ${unenrichedBeer.length} beer(s)`}
                </button>
                {enrichRun.beer.msg && <div style={{ color: t.textSecondary, fontSize: 11 }}>{enrichRun.beer.msg}</div>}
              </div>
            )}
            {unenrichedPours.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => runBulkEnrichment('pours')} disabled={enrichRun.pours.running}
                  style={{ background: enrichRun.pours.running ? t.white06 : t.accent, border: "none", color: enrichRun.pours.running ? t.textSecondary : t.bgBase, padding: "7px 14px", borderRadius: 6, cursor: enrichRun.pours.running ? "default" : "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                  {enrichRun.pours.running ? "Enriching…" : `Enrich ${unenrichedPours.length} pour(s)`}
                </button>
                {enrichRun.pours.msg && <div style={{ color: t.textSecondary, fontSize: 11 }}>{enrichRun.pours.msg}</div>}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Home landing page */}
      {managerView === 'home' && (
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 20px" }}>
          {reviewHeld && (
            <div style={{ background: t.bgSurface, border: `1px solid ${t.accent}`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ color: t.accent, fontSize: 13, marginBottom: 5 }}>Menu review in progress</div>
              <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.55 }}>
                {releaseState?.message || "We're checking your beverage data for accuracy before handing it over. You'll be able to review and publish shortly — usually within 24 hours of onboarding."}
              </div>
            </div>
          )}
          {isPublicDemoTenant && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 10, padding: "10px 14px", marginBottom: 16 }}>
              <div style={{ flex: 1, color: t.textSecondary, fontSize: 12, lineHeight: 1.5 }}>
                Testing the public demo: this device counts as one visitor against the chat limit.
              </div>
              <button type="button"
                onClick={() => { if (window.confirm("Reset this device's demo chat limit?\n\nThis device becomes a new visitor with a fresh allowance. The limit itself is unchanged. The page will reload.")) resetDemoVisitor(); }}
                style={{ background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textSecondary, padding: "7px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 13, whiteSpace: "nowrap" }}>
                Reset demo limit
              </button>
            </div>
          )}
          {billingNotice && (
            <button onClick={() => setManagerView('account')}
              style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", boxSizing: "border-box", textAlign: "left", cursor: "pointer", marginBottom: 16, fontFamily: t.fontSerif,
                background: billingNotice.tone === 'error' ? t.errorDim : t.warningDim, border: `0.5px solid ${billingNotice.tone === 'error' ? t.errorBorder : t.warningBorder}`,
                color: billingNotice.tone === 'error' ? t.error : t.warning, borderRadius: 10, padding: "12px 14px", fontSize: 13, lineHeight: 1.5 }}>
              <span style={{ flex: 1 }}>{billingNotice.text}</span>
              <span style={{ whiteSpace: "nowrap" }}>Account ›</span>
            </button>
          )}
          {/* Action buttons */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
            {[
              // Review Menu Items and Menus & Items were two doors to the same items.
              // They are one tile now: Menu items. Update Menu stays its own - bringing
              // in a whole new menu is a different job from fixing one item.
              // Oct 28, 2026: "Menu items" and "Your menus" read as the same thing in
              // two words. Renamed for the job each does: Manage Items is day-to-day
              // (86, hide, a price, approve research; floor managers use it), Menus &
              // hours is occasional setup (a new version, hours, rooms; managers and
              // owners). Kept separate on purpose: merging would put "Replace with a
              // new file" next to the 86 button.
              { id: 'review',   label: 'Manage Items',
                sub: reviewHeld ? 'SommSync is checking your menu — available shortly'
                  : (reviewTotal > 0 ? `${reviewTotal} need review · Approve, 86/hide, or change prices` : 'Approve, 86/hide, or change prices'),
                icon: 'checkbox',
                color: reviewHeld ? t.textDim : (reviewTotal > 0 ? t.warning : t.success),
                disabled: reviewHeld },
              // Your menus (Oct 27, 2026): upload, replace, edit by menu, hours. Shown
              // wherever any category is file-managed, held review included (it used
              // to appear only then, as the fallback for the hours tools).
              ...(hasEditableMenu ? [{ id: 'menus', label: 'Menus & hours', sub: 'Upload a new version, set hours and rooms', icon: 'pencil', color: t.accent }] : []),
              // Update menu is the POS sync screen. A restaurant with no POS behind
              // any menu does all of that on Your menus.
              ...(!isManualTenant ? [{ id: 'sync', label: 'Update menu', sub: 'Upload a new file or sync from your POS', icon: 'refresh', color: t.success }] : []),
              { id: 'print',    label: 'Print Beverage Menu', sub: 'Download a print-ready PDF',                                       icon: 'document', color: t.accent },
              ...(isAdmin ? [{ id: 'settings', label: 'Settings', sub: 'Device setup, logos, menus', icon: 'sliders', color: t.accent }] : []),
              ...(isAdmin ? [{ id: 'devices', label: 'Add a Tablet', sub: 'Set up a new device with a QR code', icon: 'tablet', color: t.accent }] : []),
              ...(isAdmin && !isPublicDemoTenant ? [{ id: 'account', label: 'Account & billing', sub: 'Plan, card and invoices, chat usage, languages', icon: 'card', color: t.accent }] : []),
            ].map(btn => (
              <button key={btn.id} onClick={() => !btn.disabled && setManagerView(btn.id)} disabled={!!btn.disabled}
                style={{ display: "flex", alignItems: "center", gap: 16, background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 12, padding: "16px 18px", cursor: btn.disabled ? "default" : "pointer", textAlign: "left", width: "100%", boxSizing: "border-box", opacity: btn.disabled ? 0.55 : 1 }}>
                <div style={{ width: 36, display: "flex", justifyContent: "center", flexShrink: 0, color: btn.color }}><LineIcon name={btn.icon} size={26} strokeWidth={1.5} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: t.textPrimary, fontSize: 15, fontFamily: t.fontSerif, marginBottom: 3 }}>{btn.label}</div>
                  <div style={{ color: t.textSecondary, fontSize: 11 }}>{btn.sub}</div>
                </div>
                <div style={{ color: t.textDim, fontSize: 18 }}>›</div>
              </button>
            ))}
          </div>

          {/* Stats */}
          <div style={{ borderTop: `1px solid ${t.borderSubtle}`, paddingTop: 20 }}>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 14 }}>Menu Contents</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Wines',     count: itemCounts.wine },
                { label: 'Beers',     count: itemCounts.beer },
                { label: 'Spirits',   count: itemCounts.pour },
                { label: 'Cocktails', count: itemCounts.cocktail },
                { label: 'NAB',       count: itemCounts.nab },
              ].map(({ label, count }) => (
                <div key={label} style={{ background: t.white04, borderRadius: 8, padding: "12px 10px", textAlign: "center" }}>
                  <div style={{ color: t.textPrimary, fontSize: 22, fontWeight: 600 }}>{count}</div>
                  <div style={{ color: t.textDim, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: t.white04, borderRadius: 8 }}>
                <div style={{ color: t.textMuted, fontSize: 12 }}>⟳ Last Full Sync</div>
                <div style={{ color: t.textPrimary, fontSize: 12 }}>{formatSyncTime(syncTimes.lastUpdated)}</div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: t.white04, borderRadius: 8 }}>
                <div style={{ color: t.textMuted, fontSize: 12 }}>◉ Last Stock Check</div>
                <div style={{ color: t.textPrimary, fontSize: 12 }}>{formatSyncTime(syncTimes.stockLastChecked)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add a Tablet — own screen, not driven by activeTab. Excluded from the
          generic sub-screen block below, which renders whatever review/print/
          sync tab happens to be active. */}
      {managerView === 'devices' && <AddTabletScreen t={t} />}
      {managerView === 'account' && <AccountScreen t={t} onOpenLanguages={() => { setSettingsLanguages(true); setManagerView('settings'); }} />}
      {managerView === 'menus' && <MenusHub isAdmin={isAdmin} locationNames={locationNames}
        onOpenCsvImport={() => { setSyncCsvOnly(true); setManagerView('sync'); }} />}
      {managerView === 'review' && activeTab === 'menus' && <MenusHub isAdmin={isAdmin} locationNames={locationNames} />}

      {/* Sub-screen content (review / print / sync / settings) */}
      {managerView !== 'home' && managerView !== 'devices' && managerView !== 'account' && managerView !== 'menus' && !(managerView === 'review' && activeTab === 'menus') && (
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
        {/* Review sub-tab switcher — rendered outside ternary to avoid chain break */}
        {activeTab === "unknown" && trueUncertain.length > 0 && (
          <div style={{ background: t.warningDim, border: `0.5px solid ${t.warningBorder}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
            <div style={{ color: t.warning, fontSize: 11, lineHeight: 1.5 }}>
              <LineIcon name="alert" inline />These beverages couldn't be confidently identified by AI. Edit each one with any known details, then use Research with AI or approve/exclude.
            </div>
          </div>
        )}
        {activeTab === "uncertain" && (
          <div>
            <div style={{ display: "flex", background: t.white04, borderRadius: 8, padding: 4, marginBottom: 12 }}>
              <button onClick={() => { setReviewSubTab("pending"); setSelectedIds(new Set()); }} style={{ flex: 1, padding: "8px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, background: reviewSubTab === "pending" ? t.warningDim : "transparent", color: reviewSubTab === "pending" ? t.warning : t.textDim, fontWeight: reviewSubTab === "pending" ? 600 : 400 }}>
                <LineIcon name="hourglass" inline />Pending ({uncertain.length})
              </button>
              <button onClick={() => setReviewSubTab("reviewed")} style={{ flex: 1, padding: "8px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, background: reviewSubTab === "reviewed" ? t.successDim : "transparent", color: reviewSubTab === "reviewed" ? t.success : t.textDim, fontWeight: reviewSubTab === "reviewed" ? 600 : 400 }}>
                ✓ Approved ({reviewed.length})
              </button>
            </div>
            {/* Bulk approve controls — only for items that passed enrichment cleanly */}
            {reviewSubTab === "pending" && pendingClean.length > 0 && (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <button onClick={handleApproveAll} disabled={approving}
                  style={{ flex: 1, background: t.successDim, border: `0.5px solid ${t.success}`, color: t.success, padding: "10px", borderRadius: 8, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600 }}>
                  {approving ? "Approving…" : `✓ Approve All (${pendingClean.length})`}
                </button>
                {selectedIds.size > 0 && (
                  <button onClick={handleApproveSelected} disabled={approving}
                    style={{ background: t.accentDim, border: `0.5px solid ${t.accent}`, color: t.accent, padding: "10px 14px", borderRadius: 8, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                    ✓ Approve ({selectedIds.size})
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {activeTab === "sync" ? (
          <SyncTab locationNames={locationNames} csvOnly={syncCsvOnly} />
        ) : activeTab === "settings" ? (
          <SettingsTab onDeviceSetupSaved={onDeviceSetupSaved} onLocationNamesSaved={onLocationNamesSaved} openLanguages={settingsLanguages} />
        ) : activeTab === "print" ? (
          <PrintMenuTab itemCounts={itemCounts} itemHeights={itemHeights} isAdmin={isAdmin} wineSubgroups={wineSubgroups} />
        ) : activeTab === "hub" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "6px 4px 20px" }}>
            {hubCard({ id: "reviewhub", icon: "checkbox", label: "Needs review", sub: reviewTotal > 0 ? "New and changed items waiting for a manager to check them." : "Nothing is waiting on you. New items show up here after a menu update.", count: reviewTotal, countTone: "warn", onClick: () => goTab("reviewhub") })}
            {hubCard({ id: "all", icon: "pencil", label: "Edit menu items", sub: "Change names, prices, sections and descriptions. Add or remove items.", onClick: () => goTab("all") })}
            {hubCard({ id: "unavailable", icon: "ban", label: "Out of stock & hidden", sub: "What guests can't see or order right now, and how to bring it back.", count: statusCount, onClick: () => goTab("unavailable") })}
            {/* Menus & hours is reached from the dashboard only (one way in). */}
          </div>
        ) : activeTab === "reviewhub" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "6px 4px 20px" }}>
            {REVIEW_TABS.map(r => hubCard({ id: r.id, label: r.label, sub: r.sub, count: r.count, countTone: "warn", onClick: () => goTab(r.id) }))}
          </div>
        ) : activeTab === "noprice" ? (
          <AllItemsTab view="noprice" wines={localWines} onWineUpdate={handleWineUpdate} managerSearch={search} onRequestUpcScan={wine => setUpcScanWine(wine)} isAdmin={isAdmin} refreshKey={itemsRefreshKey} onDataChanged={refreshMenuData} />
        ) : activeTab === "unavailable" ? (
          <AllItemsTab view="status" wines={localWines} onWineUpdate={handleWineUpdate} managerSearch={search} onRequestUpcScan={wine => setUpcScanWine(wine)} isAdmin={isAdmin} refreshKey={itemsRefreshKey} onDataChanged={refreshMenuData} />
        ) : activeTab === "all" ? (
          <AllItemsTab wines={localWines} onWineUpdate={handleWineUpdate} managerSearch={search} onRequestUpcScan={wine => setUpcScanWine(wine)} isAdmin={isAdmin} refreshKey={itemsRefreshKey} onDataChanged={refreshMenuData} />
        ) : activeTab === "duplicates" ? (
          duplicateGroups.length === 0 ? (
            <div style={{ textAlign: "center", color: t.success, padding: 40 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
              <div style={{ fontSize: 14 }}>No duplicates found</div>
            </div>
          ) : duplicateGroups.map((group, gi) => {
            const hasPriceDiff = group.some(w => {
              const price = w.glassPrice || w.bottlePrice;
              return group.some(w2 => (w2.glassPrice || w2.bottlePrice) !== price);
            });
            // A glass-priced entry beside a bottle-priced one is the unmerged-pair
            // case, not a duplicate — different problem, different fix.
            const unmergedPair = group.length === 2
              && group.some(w => w.glassPrice && !w.bottlePrice)
              && group.some(w => w.bottlePrice && !w.glassPrice);
            return (
              <div key={gi} style={{ background: t.white04, border: `0.5px solid ${unmergedPair ? t.accent : hasPriceDiff ? t.errorBorder : t.borderMid}`, borderRadius: 8, padding: "12px 14px", marginBottom: 10 }}>
                <div style={{ color: unmergedPair ? t.accent : hasPriceDiff ? t.error : t.warning, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>
                  {unmergedPair ? "Glass & bottle not linked" : hasPriceDiff ? <><LineIcon name="alert" inline />Price conflict</> : "Duplicate entry"} · {group.length} entries
                </div>
                {unmergedPair && (
                  <div style={{ color: t.textDim, fontSize: 10.5, lineHeight: 1.55, marginBottom: 10 }}>
                    These look like the same wine, but the names before &ldquo;Glass&rdquo; and
                    &ldquo;Bottle&rdquo; don't match, so they print as two separate items. Make the
                    names identical in Toast and they'll show as one wine with both prices.
                  </div>
                )}
                {group.map((wine, i) => (
                  <div key={wine.id} style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: i > 0 ? 8 : 0, borderTop: i > 0 ? `0.5px solid ${t.accentText}` : "none" }}>
                    <div style={{ width: 30, height: 42, borderRadius: 3, background: t.bgCardHover, border: `0.5px solid ${t.borderSubtle}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, overflow: "hidden" }}>
                      {wine.imageUrl ? <img src={wine.imageUrl} alt={wine.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🍷"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: t.textPrimary, fontSize: 13 }}>{wine.name}</div>
                      <div style={{ color: t.textDim, fontSize: 10 }}>
                        {wine._type === "beer" ? ["Beer", wine.style, wine.brewery].filter(Boolean).join(" · ")
                          : wine._type === "pour" ? ["Pour", wine.category, wine.producer].filter(Boolean).join(" · ")
                          : [wine.subgroup, wine.tier].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      {wine.glassPrice && <div style={{ color: t.accent, fontSize: 12 }}>{formatPrice(wine.glassPrice)} glass</div>}
                      {wine.bottlePrice && <div style={{ color: t.accent, fontSize: 12 }}>{formatPrice(wine.bottlePrice)} btl</div>}
                      {!wine.glassPrice && !wine.bottlePrice && <div style={{ color: t.textDim, fontSize: 11, fontStyle: "italic" }}>No price</div>}
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 10, color: t.textDim, fontSize: 11, fontStyle: "italic", borderTop: `0.5px solid ${t.accentText}`, paddingTop: 8 }}>
                  Remove the duplicate{hasPriceDiff ? " and correct the price" : ""} at the source, then re-sync or re-import
                </div>
              </div>
            );
          })
        ) : (activeTab === "uncertain" && reviewSubTab === "pending" && uncertain.length === 0) ? (
          <div style={{ textAlign: "center", color: t.success, padding: 40 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 14 }}>All items approved — nothing pending</div>
          </div>
        ) : (activeTab === "uncertain" && reviewSubTab === "reviewed" && reviewed.length === 0) ? (
          <div style={{ textAlign: "center", color: t.textDim, padding: 40 }}>
            <div style={{ fontSize: 14 }}>No reviewed items yet</div>
          </div>
        ) : (activeTab !== "uncertain" && current.length === 0) ? (
          <div style={{ textAlign: "center", color: t.success, padding: 40 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 14 }}>All clear</div>
          </div>
        ) : (
          (activeTab === "uncertain" ? (reviewSubTab === "pending" ? uncertain : reviewed) : current).map(wine => {
            const isEditingThis = reviewEditingId === wine.id;
            const itemType = wine._type || "wine";
            const riInputStyle = { width: "100%", boxSizing: "border-box", background: t.white08, border: `0.5px solid ${t.borderStrong}`, color: t.textPrimary, padding: "7px 10px", borderRadius: 6, fontFamily: t.fontSerif, fontSize: 12, outline: "none", marginBottom: 8 };
            const riLabelStyle = { color: t.textMuted, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 3, display: "block" };
            return (
            <div key={wine.id} style={{ background: isEditingThis ? t.accentDimSm : t.white04, border: `0.5px solid ${isEditingThis ? t.accent : t.borderSubtle}`, borderRadius: 8, marginBottom: 8, overflow: "hidden" }}>
              {/* Main row */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", cursor: (activeTab === "uncertain" || activeTab === "unknown") ? "pointer" : "default" }}
                onClick={() => (activeTab === "uncertain" || activeTab === "unknown") && openReviewEdit(wine)}>
                {/* Checkbox — only on clean (non-uncertain) items in pending sub-tab */}
                {activeTab === "uncertain" && reviewSubTab === "pending" && !wine.uncertain && (
                  <div onClick={e => { e.stopPropagation(); toggleSelect(wine.id); }}
                    style={{ width: 20, height: 20, borderRadius: 4, border: `1.5px solid ${selectedIds.has(wine.id) ? t.success : t.borderMid}`, background: selectedIds.has(wine.id) ? t.successDim : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer", marginTop: 2 }}>
                    {selectedIds.has(wine.id) && <span style={{ color: t.success, fontSize: 12, lineHeight: 1 }}>✓</span>}
                  </div>
                )}
                <div onClick={wine.imageUrl ? (e => { e.stopPropagation(); setZoomedManagerItem(wine); }) : undefined}
                  style={{ width: 36, height: 50, borderRadius: 3, background: t.bgCardHover, border: `0.5px solid ${wine.imageUrl ? t.accentBorderSm : t.borderSubtle}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, overflow: "hidden", cursor: wine.imageUrl ? "zoom-in" : "default" }}>
                  {wine.imageUrl ? <img src={wine.imageUrl} alt={wine.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🍷"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 1 }}>
                    <div style={{ color: t.textPrimary, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {wine.name || wine.sourceName}
                    </div>
                    {wine.sourceName && wine.name && wine.sourceName !== wine.name && (
                      <span style={{ flexShrink: 0, fontSize: 9, background: t.accentDim, border: `0.5px solid ${t.accentBorder}`, color: t.accent, borderRadius: 4, padding: "1px 5px" }}>AI renamed</span>
                    )}
                  </div>
                  {wine.sourceName && wine.name && wine.sourceName !== wine.name && (
                    <div style={{ color: t.textMuted, fontSize: 10, fontStyle: "italic", marginBottom: 2 }}>Original: {wine.sourceName}</div>
                  )}
                  <div style={{ color: t.textDim, fontSize: 9, fontFamily: t.fontMono, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{wine.id}</div>
                  <div style={{ color: t.textDim, fontSize: 10, letterSpacing: "0.5px" }}>
                    {itemType === "beer" ? [wine.style, wine.brewery].filter(Boolean).join(" · ")
                      : itemType === "pour" ? [wine.category, wine.producer].filter(Boolean).join(" · ")
                      : [wine.varietal, wine.region].filter(Boolean).join(" · ") || [wine.subgroup, wine.tier].filter(Boolean).join(" · ")}
                  </div>
                  {wine.description && (
                    <div style={{ color: t.textSecondary, fontSize: 10, marginTop: 4, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {wine.description}
                    </div>
                  )}
                  {PHOTO_TYPES[itemType] && (
                    <button type="button"
                      onClick={e => { e.stopPropagation(); choosePhotoFor(wine, itemType); }}
                      disabled={photoBusyId === wine.id}
                      style={{ marginTop: 6, background: "none", border: `0.5px solid ${t.accentBorder}`, color: t.accent,
                        fontSize: 11, padding: "4px 10px", borderRadius: 6, cursor: photoBusyId === wine.id ? "default" : "pointer", fontFamily: t.fontSerif }}>
                      {photoBusyId === wine.id ? "Uploading…" : wine.imageUrl ? "Use my own photo" : "Add photo"}
                    </button>
                  )}
                  {activeTab === "uncertain" && reviewSubTab === "reviewed" && (
                    <div style={{ marginTop: 4 }}>
                      <span style={{ color: t.success, fontSize: 10 }}>✓ {wine.manuallyEdited ? "Manually edited" : wine.approvedBy === "auto" ? "Approved automatically" : "Approved"}</span>
                    </div>
                  )}
                  {(activeTab === "unknown" || (activeTab === "uncertain" && reviewSubTab === "pending")) && wine.uncertain && wine.uncertainReason && (
                    <div style={{ background: t.warningDim || t.white04, border: `0.5px solid ${t.warning}`, borderRadius: 6, padding: "8px 10px", marginTop: 6 }}>
                      <div style={{ color: t.warning, fontSize: 11, fontStyle: "italic", marginBottom: 4 }}><LineIcon name="alert" inline />{wine.uncertainReason}</div>
                      {/* Publishing is a legitimate choice, not a shortcut. On a large list,
                          forcing every ambiguous wine to be corrected before launch is what
                          stalls an onboarding — so make the trade-off explicit and let the
                          owner decide how precise they want to be. */}
                      <div style={{ color: t.textDim, fontSize: 10.5, lineHeight: 1.55 }}>
                        We couldn't tell which of this producer's wines this is. The description below
                        is about the winery &mdash; accurate, but not specific to this bottle. You can
                        approve it as is, or put the full wine name in Toast and re-enrich for
                        bottle-specific notes and critic scores.
                      </div>
                    </div>
                  )}
                  {/* Approving does not erase the flag. A restaurant that onboards fast
                      should still be able to find these later and tidy them up. */}
                  {activeTab === "uncertain" && reviewSubTab === "approved" && wine.uncertain && wine.uncertainReason && (
                    <div style={{ color: t.textDim, fontSize: 10, marginTop: 4, fontStyle: "italic" }}>
                      Published with a general producer description &mdash; not specific to this bottle.
                    </div>
                  )}
                  {activeTab === "noprice" && <div style={{ color: t.warning, fontSize: 11, marginTop: 4 }}>No price in Toast — update menu item</div>}
                  {activeTab === "noimage" && <div style={{ color: t.textMuted, fontSize: 11, marginTop: 4 }}>Tap Add photo to photograph the bottle or choose a picture. It shows to guests right away.</div>}
                  {activeTab === "unenriched" && <div style={{ color: t.textMuted, fontSize: 11, marginTop: 4 }}>Pending AI enrichment</div>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                  <div style={{ textAlign: "right" }}>
                    {wine.glassPrice && <div style={{ color: t.accent, fontSize: 12 }}>{formatPrice(wine.glassPrice)} glass</div>}
                    {wine.bottlePrice && <div style={{ color: t.accent, fontSize: 12 }}>{formatPrice(wine.bottlePrice)} btl</div>}
                  </div>
                  {(activeTab === "uncertain" || activeTab === "unknown") && (
                    <div style={{ color: t.textDim, fontSize: 10 }}>{isEditingThis ? "▲ close" : "✎ edit"}</div>
                  )}
                </div>
              </div>

              {/* Exclude button — Missing Images tab */}
              {activeTab === "noimage" && (
                <div style={{ padding: "0 14px 12px" }}>
                  <button onClick={e => { e.stopPropagation(); handleExclude(wine); }}
                    style={{ background: t.errorDim, border: `0.5px solid ${t.errorBorder}`, color: t.error, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
                    ✕ Exclude from List
                  </button>
                </div>
              )}

              {/* Inline edit panel — Review and Unknown tabs */}
              {(activeTab === "uncertain" || activeTab === "unknown") && isEditingThis && (
                <div style={{ borderTop: `0.5px solid ${t.borderStrong}`, padding: "14px 14px 16px", background: t.white06 }}>
                  {/* Fields by type */}
                  {itemType !== "food" && (
                    <>
                      <label style={riLabelStyle}>Name</label>
                      <input style={riInputStyle} value={reviewEditFields.correctedName || ""} onChange={e => setReviewEditFields(p => ({ ...p, correctedName: e.target.value }))} />
                    </>
                  )}
                  {itemType === "wine" && (
                    <>
                      <label style={riLabelStyle}>Varietal</label>
                      <input style={riInputStyle} value={reviewEditFields.varietal || ""} onChange={e => setReviewEditFields(p => ({ ...p, varietal: e.target.value }))} />
                      <label style={riLabelStyle}>Region</label>
                      <input style={riInputStyle} value={reviewEditFields.region || ""} onChange={e => setReviewEditFields(p => ({ ...p, region: e.target.value }))} />
                      <label style={riLabelStyle}>Reviews & Ratings</label>
                      <input style={riInputStyle} value={reviewEditFields.reviews || ""} onChange={e => setReviewEditFields(p => ({ ...p, reviews: e.target.value }))} />
                      <label style={riLabelStyle}>Vintage</label>
                      <select style={riInputStyle}
                        value={reviewEditFields.vintage === null || reviewEditFields.vintage === undefined ? "" : String(reviewEditFields.vintage)}
                        onChange={e => setReviewEditFields(p => ({ ...p, vintage: e.target.value === "" ? null : e.target.value === "NV" ? "NV" : parseInt(e.target.value) }))}>
                        <option value="">Not specified</option>
                        <option value="NV">NV (Non-Vintage)</option>
                        {Array.from({ length: new Date().getFullYear() - 1989 }, (_, i) => new Date().getFullYear() - i).map(y => (
                          <option key={y} value={String(y)}>{y}</option>
                        ))}
                      </select>
                      <VintageMismatchNote name={reviewEditFields.correctedName} vintage={reviewEditFields.vintage}
                        onUseYear={y => setReviewEditFields(p => ({ ...p, vintage: y }))} />
                      {/* A confirmation flag, not a text field - travels to Research
                          Further as hints.vineyardConfirmedNone and is treated as an
                          authoritative fact by the prompt (see enrichWineWithClaude),
                          never as a displayed value. Solves the case where a producer
                          has several vineyard-designated bottlings and the plain name
                          alone is correctly flagged uncertain, but the manager knows
                          this is the standard/base one - without needing to write
                          disambiguating language into Region or Name, which would
                          otherwise be the only fields available to signal it. */}
                      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 4, marginBottom: 10, cursor: "pointer" }}>
                        <input type="checkbox" style={{ marginTop: 2 }}
                          checked={reviewEditFields.vineyardConfirmedNone === true}
                          onChange={e => setReviewEditFields(p => ({ ...p, vineyardConfirmedNone: e.target.checked }))} />
                        <span style={{ color: t.textSecondary, fontSize: 11, lineHeight: 1.4 }}>
                          Confirm: no specific vineyard/cuvée — this is the producer's standard bottling, not a single-vineyard, estate, or reserve selection
                        </span>
                      </label>
                    </>
                  )}
                  {itemType === "beer" && (
                    <>
                      <label style={riLabelStyle}>Style</label>
                      <input style={riInputStyle} value={reviewEditFields.style || ""} onChange={e => setReviewEditFields(p => ({ ...p, style: e.target.value }))} />
                      <label style={riLabelStyle}>Brewery</label>
                      <input style={riInputStyle} value={reviewEditFields.brewery || ""} onChange={e => setReviewEditFields(p => ({ ...p, brewery: e.target.value }))} />
                      <label style={riLabelStyle}>ABV</label>
                      <input style={riInputStyle} value={reviewEditFields.abv || ""} onChange={e => setReviewEditFields(p => ({ ...p, abv: e.target.value }))} />
                    </>
                  )}
                  {itemType === "pour" && (
                    <>
                      <label style={riLabelStyle}>Category</label>
                      <input style={riInputStyle} value={reviewEditFields.category || ""} onChange={e => setReviewEditFields(p => ({ ...p, category: e.target.value }))} />
                      <label style={riLabelStyle}>Producer</label>
                      <input style={riInputStyle} value={reviewEditFields.producer || ""} onChange={e => setReviewEditFields(p => ({ ...p, producer: e.target.value }))} />
                      <label style={riLabelStyle}>ABV</label>
                      <input style={riInputStyle} value={reviewEditFields.abv || ""} onChange={e => setReviewEditFields(p => ({ ...p, abv: e.target.value }))} />
                    </>
                  )}
                  <label style={riLabelStyle}>Tasting Notes</label>
                  <textarea style={{ ...riInputStyle, height: 64, resize: "vertical", marginBottom: 10 }} value={reviewEditFields.description || ""} onChange={e => setReviewEditFields(p => ({ ...p, description: e.target.value }))} />

                  {/* Research hint */}
                  <div style={{ color: t.textMuted, fontSize: 10, fontStyle: "italic", marginBottom: 10, lineHeight: 1.5 }}>
                    Fill in any known fields above, then tap ✦ Research Further — Claude will use your corrections as a starting point and write a fresh description.
                  </div>

                  {/* Result message */}
                  {reviewEditResult && (
                    <div style={{ marginBottom: 10, background: reviewEditResult.ok ? t.successDim : t.errorDim, border: `0.5px solid ${reviewEditResult.ok ? t.success : t.error}`, borderRadius: 6, padding: "7px 12px" }}>
                      <div style={{ color: reviewEditResult.ok ? t.success : t.error, fontSize: 11 }}>{!(reviewEditResult.ok && !reviewEditResult.warn) && <LineIcon name={reviewEditResult.warn ? 'alert' : 'xCircle'} inline />}{reviewEditResult.message}</div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleReviewSave(wine)} disabled={reviewSaving || reviewReEnriching}
                      style={{ flex: 1, background: t.accent, color: t.accentText, border: "none", padding: "9px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600 }}>
                      {reviewSaving ? "Saving…" : "Save"}
                    </button>
                    <button onClick={() => handleReviewReEnrich(wine)} disabled={reviewReEnriching || reviewSaving}
                      style={{ background: reviewReEnriching ? t.white06 : t.infoDim, border: `0.5px solid ${reviewReEnriching ? t.textMuted : t.info}`, color: t.info, padding: "9px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11, whiteSpace: "nowrap" }}>
                      {reviewReEnriching ? "Researching…" : "✦ Research Further"}
                    </button>
                    {/* Same swap as the collapsed row: an already-approved item offers
                        the reverse action instead of a second approve. */}
                    {(wine.approved || wine.manuallyEdited) ? (
                      <button onClick={e => { e.stopPropagation(); handleUnapprove(wine); }} disabled={approving}
                        style={{ background: t.white04, border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, padding: "9px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11, whiteSpace: "nowrap" }}>
                        <LineIcon name="undo" inline />Undo approval
                      </button>
                    ) : (
                      <button onClick={e => { e.stopPropagation(); handleApprove(wine); }}
                        style={{ background: t.successDim, border: `0.5px solid ${t.success}`, color: t.success, padding: "9px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11, whiteSpace: "nowrap" }}>
                        ✓ Approve
                      </button>
                    )}
                    <button onClick={() => { setReviewEditingId(null); setReviewEditResult(null); }}
                      style={{ background: "none", border: `0.5px solid ${t.borderMid}`, color: t.textDim, padding: "9px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12 }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Restore button — shown on the Hidden Items tab */}
              {activeTab === "excluded" && !isEditingThis && (
                <div style={{ padding: "0 14px 12px" }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={e => { e.stopPropagation(); handleUnexclude(wine); }}
                      style={{ background: t.successDim, border: `0.5px solid ${t.successBorder}`, color: t.success, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
                      <LineIcon name="undo" inline />Restore to Menu
                    </button>
                  </div>
                </div>
              )}

              {/* Action buttons on collapsed items — shown on pending and unknown tabs */}
              {(activeTab === "unknown" || (activeTab === "uncertain" && reviewSubTab === "pending")) && !isEditingThis && (
                <div style={{ padding: "0 14px 12px" }}>
                  {wine.correctedName && wine.correctedName !== wine.name && (
                    <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorder}`, borderRadius: 6, padding: "8px 10px", marginBottom: 8 }}>
                      <div style={{ color: t.textSecondary, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4 }}>Suggested name</div>
                      <div style={{ color: t.textPrimary, fontSize: 13, marginBottom: 6 }}>{wine.correctedName}</div>
                      <CopyButton text={wine.correctedName} />
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button onClick={() => openReviewEdit(wine)}
                      style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorder}`, color: t.accent, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
                      ✎ Edit & Research with AI
                    </button>
                    {(
                      <button onClick={e => { e.stopPropagation(); setUpcScanWine(wine); }}
                        style={{ background: t.infoDim, border: `0.5px solid ${t.infoBorder}`, color: t.info, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
                        <LineIcon name="camera" inline />Identify from Label
                      </button>
                    )}
                    {/* An approved item shows the reverse action instead. Approving is
                        one tap, so undoing it should be too — not a re-enrichment. */}
                    {(wine.approved || wine.manuallyEdited) ? (
                      <button onClick={e => { e.stopPropagation(); handleUnapprove(wine); }} disabled={approving}
                        style={{ background: t.white04, border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
                        <LineIcon name="undo" inline />Undo approval
                      </button>
                    ) : (
                      <button onClick={e => { e.stopPropagation(); handleApprove(wine); }} disabled={approving}
                        style={{ background: t.successDim, border: `0.5px solid ${t.successBorder}`, color: t.success, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
                        ✓ Approve
                      </button>
                    )}
                    <button onClick={e => { e.stopPropagation(); handleExclude(wine); }}
                      style={{ background: t.errorDim, border: `0.5px solid ${t.errorBorder}`, color: t.error, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
                      ✕ Exclude
                    </button>
                  </div>
                </div>
              )}
            </div>
            );
          })
        )}
      </div>
      )}

      {/* Footer */}
      <div style={{ background: t.bgSurface, borderTop: `1px solid ${t.borderSubtle}`, padding: "12px 20px", textAlign: "center" }}>
        <div style={{ color: t.textSecondary, fontSize: 12, letterSpacing: "0.5px" }}>
          {wines.length} total wines · {isAdmin ? "Admin access" : "Manager access"} · Tap the restaurant's logo 5× on the home screen to open this screen
        </div>
      </div>
      </div>

      {/* Label identification modal */}
      {upcScanWine && (
        <LabelScanModal
          wine={upcScanWine}
          onClose={() => setUpcScanWine(null)}
          onEnrichComplete={handleUpcEnrichComplete}
        />
      )}

      {/* One hidden picker for every row. accept="image/*" offers the camera on a
          tablet or phone, so a manager can photograph the bottle in hand. */}
      <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoChosen} style={{ display: "none" }} />
      {/* Image zoom overlay */}
      {zoomedManagerItem && (
        <div onClick={() => setZoomedManagerItem(null)} style={{
          position: "fixed", inset: 0, zIndex: 1100,
          background: t.bgOverlay,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          padding: 32, cursor: "pointer"
        }}>
          <img
            src={zoomedManagerItem.imageUrl}
            alt={zoomedManagerItem.name}
            style={{ maxHeight: "72vh", maxWidth: "80vw", objectFit: "contain", borderRadius: 8, boxShadow: `0 8px 48px ${t.shadowStrong}` }}
            onClick={e => e.stopPropagation()}
          />
          <div style={{ marginTop: 20, textAlign: "center" }}>
            <div style={{ color: t.textPrimary, fontSize: 16, fontFamily: t.fontSerif, marginBottom: 4 }}>{zoomedManagerItem.name}</div>
            {(zoomedManagerItem.varietal || zoomedManagerItem.region) && (
              <div style={{ color: t.accent, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", fontFamily: t.fontSerif }}>
                {[zoomedManagerItem.varietal, zoomedManagerItem.region].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
          <div style={{ marginTop: 24, color: t.textDim, fontSize: 11, fontFamily: t.fontSerif, letterSpacing: "1px" }}>TAP ANYWHERE TO CLOSE</div>
        </div>
      )}
      </div>
  );
}

// ─── PIN Screen ───────────────────────────────────────────────────────────────

function PinScreen({ onSuccess, onCancel }) {
  const t = useTheme();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Password managers write straight into the DOM without firing React's change
  // event, so controlled state stays empty while the field looks filled. Read the
  // element at submit and fall back to state for hand-typed input.
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // The PIN is checked on the server, so this is async and can fail slowly.
  // Digits are locked while it is in flight to stop a double submit.
  async function submitPin(value) {
    setBusy(true);
    setError("");
    try {
      await signInFloorPin(RESTAURANT_ID, value);
      onSuccess("manager");
    } catch (e) {
      // A wait is not the same as a wrong PIN — say which, and how long.
      setError(e.retryAfterSeconds ? e.message : (e.message || "Incorrect PIN"));
      setTimeout(() => setPin(""), 300);
    }
    setBusy(false);
  }

  function handleDigit(d) {
    if (busy || pin.length >= PIN_LENGTH) return;
    const next = pin + d;
    setPin(next);
    setError("");
    if (next.length === PIN_LENGTH) submitPin(next);
  }

  async function handleEmailLogin() {
    setBusy(true);
    setError("");
    try {
      const claims = await signInEmail(
        emailRef.current?.value || email,
        passwordRef.current?.value || password,
      );
      onSuccess(can(claims, 'owner', RESTAURANT_ID) ? "admin" : "manager");
    } catch (e) {
      setError(friendlyAuthError(e));
    }
    setBusy(false);
  }

  // Self-service reset. The response is deliberately identical whether or not
  // the address has an account — see requestPasswordReset — so the confirmation
  // below is worded to be true either way rather than promising an email that
  // may never arrive.
  async function handleForgotPassword() {
    const addr = (emailRef.current?.value || email || '').trim();
    if (!addr) { setError('Enter your email address first, then tap Forgot password.'); return; }
    setBusy(true); setError('');
    try {
      const r = await fetch(`${BASE}/requestPasswordReset?restaurantId=${RESTAURANT_ID}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addr }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not send the reset email');
      setResetSent(true);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }

  function handleDelete() { setPin(p => p.slice(0, -1)); setError(""); }

  if (showEmail) {
    const field = { ...inputLarge(t), borderRadius: 8, marginBottom: 10 };
    return (
      <div style={{ position: "fixed", inset: 0, background: t.bgOverlay, zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: t.fontSerif }}>
        <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 16, padding: "32px 28px", width: 320 }}>
          <div style={{ color: t.accent, fontSize: 10, letterSpacing: "3px", textTransform: "uppercase", marginBottom: 8, textAlign: "center" }}>Owner Access</div>
          <div style={{ color: t.textPrimary, fontSize: 16, marginBottom: 20, textAlign: "center" }}>Sign In</div>
          <input style={field} type="email" placeholder="Email" defaultValue={email} autoComplete="username"
            ref={emailRef} onChange={e => setEmail(e.target.value)} />
          <input style={field} type="password" placeholder="Password" defaultValue={password} autoComplete="current-password"
            ref={passwordRef} onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !busy && handleEmailLogin()} />
          {error && <div style={{ color: t.error, fontSize: 12, marginBottom: 12 }}>{error}</div>}
          {/* Worded to be true whether or not the address has an account. The
              endpoint cannot say, and a confident "check your inbox" would send
              someone hunting for an email that is never coming. */}
          {resetSent && (
            <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.55, marginBottom: 12 }}>
              If that address has an account, a reset link is on its way. It expires in an hour.
            </div>
          )}
          <button onClick={handleEmailLogin} disabled={busy}
            style={{ width: "100%", padding: "11px", borderRadius: 8, border: "none", background: t.accent, color: t.accentText, fontFamily: t.fontSerif, fontSize: 14, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Signing in..." : "Sign In"}
          </button>
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <button onClick={handleForgotPassword} disabled={busy}
              style={{ background: "none", border: "none", color: t.textDim, fontSize: 12, cursor: busy ? "default" : "pointer", fontFamily: t.fontSerif, textDecoration: "underline" }}>
              Forgot password?
            </button>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
            <button onClick={() => { setShowEmail(false); setError(""); }}
              style={{ background: "none", border: "none", color: t.textDim, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif }}>← Use PIN</button>
            <button onClick={onCancel}
              style={{ background: "none", border: "none", color: t.textDim, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: t.bgOverlay, zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: t.fontSerif }}>
      <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 16, padding: "32px 28px", width: 280, textAlign: "center" }}>
        <div style={{ color: t.accent, fontSize: 10, letterSpacing: "3px", textTransform: "uppercase", marginBottom: 8 }}>Staff Access</div>
        <div style={{ color: t.textPrimary, fontSize: 16, marginBottom: 24 }}>Enter PIN</div>

        {/* PIN dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginBottom: 24 }}>
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <div key={i} style={{
              width: 13, height: 13, borderRadius: "50%",
              background: i < pin.length ? (error ? t.error : t.accent) : "transparent",
              border: `2px solid ${error ? t.error : i < pin.length ? t.accent : t.borderStrong}`,
              transition: "all 0.15s"
            }} />
          ))}
        </div>

        {busy && <div style={{ color: t.textDim, fontSize: 12, marginBottom: 16 }}>Checking...</div>}
        {error && !busy && <div style={{ color: t.error, fontSize: 12, marginBottom: 16, lineHeight: 1.4 }}>{error}</div>}

        {/* Keypad */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 12 }}>
          {[1,2,3,4,5,6,7,8,9].map(d => (
            <button key={d} onClick={() => handleDigit(String(d))} style={{
              background: t.white06, border: `0.5px solid ${t.borderSubtle}`,
              color: t.textPrimary, fontSize: 20, padding: "14px", borderRadius: 10,
              cursor: "pointer", fontFamily: t.fontSerif
            }}>{d}</button>
          ))}
          <button onClick={onCancel} style={{ background: "transparent", border: `0.5px solid ${t.borderSubtle}`, color: t.textDim, fontSize: 12, padding: "14px", borderRadius: 10, cursor: "pointer", fontFamily: t.fontSerif }}>Cancel</button>
          <button onClick={() => handleDigit("0")} style={{ background: t.white06, border: `0.5px solid ${t.borderSubtle}`, color: t.textPrimary, fontSize: 20, padding: "14px", borderRadius: 10, cursor: "pointer", fontFamily: t.fontSerif }}>0</button>
          <button onClick={handleDelete} style={{ background: "transparent", border: `0.5px solid ${t.borderSubtle}`, color: t.textDim, fontSize: 18, padding: "14px", borderRadius: 10, cursor: "pointer" }}>⌫</button>
        </div>

        <button onClick={() => { setShowEmail(true); setPin(""); setError(""); }}
          style={{ background: "none", border: "none", color: t.textDim, fontSize: 11, cursor: "pointer", fontFamily: t.fontSerif, marginTop: 6, letterSpacing: "0.5px" }}>
          Owner? Sign in with email
        </button>
      </div>
    </div>
  );
}

// ─── Shared List Sub-Components ──────────────────────────────────────────────
// Edit these once and changes apply to Wine, Beer, Cocktails, Pours, NAB

function ListScreenHeader({ title, onBack, favorites, onShowShortlist, children }) {
  const t = useTheme();
  const narrow = useIsNarrow();
  return (
    <div style={{ background: t.bgBase, padding: "0 20px" }}>
      <div style={{ padding: "10px 0 6px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: t.textPrimary, cursor: "pointer", fontFamily: t.fontSerif, fontSize: narrow ? 13 : 12, letterSpacing: "1px", display: "flex", alignItems: "center", gap: 4, padding: 0 }}>
          ‹ <span style={{ textTransform: "uppercase", letterSpacing: "2px" }}>{tr("common.mainMenu")}</span>
        </button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ color: t.accent, fontSize: narrow ? 15 : 14, letterSpacing: "4px", textTransform: "uppercase" }}>{title}</div>
        </div>
        <div style={{ minWidth: 80, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
          <TextSizeButton />
          {favorites.length > 0 && (
            <button onClick={onShowShortlist} style={{ background: t.accentDim, border: `0.5px solid ${t.accentBorder}`, color: t.accent, padding: "4px 10px", borderRadius: 12, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
              ★ {favorites.length}
            </button>
          )}
        </div>
      </div>
      <div style={{ height: "0.5px", background: `linear-gradient(90deg, transparent, ${t.accent}44, transparent)`, marginBottom: 10 }} />
      {children}
    </div>
  );
}

function ListSectionHeading({ label, borderTop }) {
  const t = useTheme();
  const narrow = useIsNarrow();
  const textScale = useTextScale();
  return (
    <div style={{ padding: "18px 20px 6px", borderTop: borderTop ? `0.5px solid ${t.borderSubtle}` : "none" }}>
      <div style={{ color: t.accent, fontSize: Math.round((narrow ? 10.5 : 9) * textScale * 2) / 2, letterSpacing: "3px", textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

function ListCountBar({ left, right }) {
  const t = useTheme();
  const narrow = useIsNarrow();
  return (
    <div style={{ background: t.bgSurface, padding: "5px 20px 8px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
      <span style={{ color: t.textDim, fontSize: narrow ? 12 : 11, letterSpacing: "1px" }}>{left}</span>
      {right && <span style={{ color: t.textDim, fontSize: narrow ? 11 : 10, fontStyle: "italic", textAlign: "right" }}>{right}</span>}
    </div>
  );
}

// ─── Generic Item List Screen (Beer, Pours, Cocktails, NAB) ──────────────────

function ItemListScreen({ title, allLabel, searchPlaceholder, endpoint, dataKey, onBack, favorites = [], onToggleFavorite = () => {}, onShowShortlist = () => {}, tabletLocation = "all", locationNames = { bar: "Bar", dining: "Dining Room" }, plan = "premium" }) {
  // Shared by beers, spirits and cocktails - the same bottom sheet, and the same
  // problem on a phone.
  const narrow = useIsNarrow();

  const t = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeGroup, setActiveGroup] = useState("All");
  const [selectedItem, setSelectedItem] = useState(null);
  // Stop the list scrolling behind the full-screen detail view. Was gated on
  // `narrow` (useIsNarrow()) - since the detail panel is now always
  // full-screen regardless of device width (see the detail block below),
  // this needs to engage whenever an item is expanded, not just on an
  // actually-narrow screen.
  useEffect(() => {
    if (!selectedItem) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [selectedItem]);
  const [visible, setVisible] = useState(false);
  const [itemSearch, setItemSearch] = useState("");
  const [zoomedLabel, setZoomedLabel] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatContext, setChatContext] = useState(null);
  const [toast, setToast] = useState(null);

  function handleOpenChat(ctx) { setChatContext(ctx); setChatOpen(true); }

  // The tablet's room goes to the server, which only returns drinks whose menu
  // is open in this room right now (menu hours and rooms, Oct 28, 2026).
  const listUrl = tabletLocation && tabletLocation !== "all"
    ? `${endpoint}${endpoint.includes("?") ? "&" : "?"}location=${encodeURIComponent(tabletLocation)}`
    : endpoint;
  useEffect(() => {
    rfetch(listUrl)
      .then(r => r.json())
      .then(data => {
        registerSectionLabels(data.sectionLabels);
        setItems(data[dataKey] || []);
        setLoading(false);
        setTimeout(() => setVisible(true), 50);
      })
      .catch(e => { setError(e.message || tr("lists.loadFailed")); setLoading(false); });

    // Silent background poll — interval adjusts to season/time
    let pollTimer;
    function scheduleNext() {
      pollTimer = setTimeout(() => {
        rfetch(listUrl).then(r => r.json())
          .then(data => { registerSectionLabels(data.sectionLabels); setItems(data[dataKey] || []); })
          .catch(() => {});
        scheduleNext();
      }, getPollingInterval());
    }
    scheduleNext();
    return () => clearTimeout(pollTimer);
  }, [listUrl, dataKey]);

  const available = items.filter(i => i.available !== false);
  const groupOrder = [...new Map(available.map(i => [i.subgroup || i.tier || "Menu", true])).keys()];
  const groups = ["All", ...groupOrder];
  const filtered = activeGroup === "All" ? available : available.filter(i => i.subgroup === activeGroup || i.tier === activeGroup);

  const searchFiltered = itemSearch.trim() === ""
    ? filtered
    : filtered.filter(i => {
        const q = foldText(itemSearch).trim();
        return searchHit(i.name, q)
          || searchHit(i.description, q)
          || searchHit(i.style, q)
          || searchHit(i.category, q);
      });

  const grouped = {};
  searchFiltered.forEach(item => {
    const key = item.subgroup || item.tier || "Menu";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  const filteredGroupOrder = [...new Map(searchFiltered.map(i => [i.subgroup || i.tier || "Menu", true])).keys()];

  // Type scale. The tablet column is the original sizing and is not touched; the
  // phone column is one to one-and-a-half points larger throughout. An explicit
  // map rather than a multiplier so the two sets can be read side by side and a
  // single value adjusted without moving everything else.
  // Guest text size multiplies the reading text; search stays at 16 (iOS
  // auto-zooms an input under 16px, and it needn't grow past it).
  const textScale = useTextScale();
  const F = { ...scaleFonts(narrow
    ? { name: 17.5, meta: 13,   desc: 14, more: 12, price: 15, unit: 11,   ask: 12, empty: 15 }
    : { name: 16,   meta: 12,   desc: 13, more: 11, price: 14, unit: 10,   ask: 11, empty: 14 }, textScale), search: 16 };
  const sz = n => Math.round(n * textScale * 2) / 2;

  if (loading) return (
    <div style={{ background: t.bgBase, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ color: t.accent, fontSize: 13, letterSpacing: "3px", textTransform: "uppercase", fontFamily: t.fontSerif }}>{tr("lists.loading", { title })}</div>
    </div>
  );

  if (error) return (
    <div style={{ background: t.bgBase, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: t.fontSerif }}>
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <div style={{ color: t.accent, fontSize: 13, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 12 }}>{tr("lists.unableToLoad", { title })}</div>
        <div style={{ color: t.textDim, fontSize: 12, marginBottom: 8 }}>{tr("lists.endpoint", { endpoint })}</div>
        <div style={{ background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 6, padding: "10px 14px", marginBottom: 16, textAlign: "left" }}>
          <div style={{ color: t.textDim, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4 }}>{tr("lists.error")}</div>
          <div style={{ color: t.textSecondary, fontSize: 12, fontFamily: t.fontMono, wordBreak: "break-all" }}>{error}</div>
        </div>
        <button onClick={() => window.location.reload()} style={{ background: t.accent, color: t.accentText, border: "none", padding: "8px 20px", borderRadius: 6, fontFamily: t.fontSerif, cursor: "pointer" }}>{tr("lists.tryAgain")}</button>
      </div>
    </div>
  );

  return (
    <div style={{ background: t.bgBase, minHeight: "100vh", fontFamily: t.fontSerif, maxWidth: 680, margin: "0 auto", opacity: visible ? 1 : 0, transition: "opacity 0.5s ease" }}>
      {/* Shared header — edit ListScreenHeader to change all list screens */}
      <div style={{ position: "sticky", top: "var(--somm-banner-h, 0px)", zIndex: 10 }}>
        <ListScreenHeader title={title} onBack={onBack} favorites={favorites} onShowShortlist={onShowShortlist}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {groups.map(g => (
              <FilterBtn key={g}
                label={g === "All" ? (allLabel || tr("lists.allFallback", { title })) : sectionLabel(g)}
                active={activeGroup === g}
                onClick={() => { setActiveGroup(g); setSelectedItem(null); }} />
            ))}
          </div>
          <div style={{ padding: "4px 0 10px", position: "relative" }}>
            <input type="text" placeholder={searchPlaceholder || tr("lists.searchFallback", { list: title.toLowerCase() })} value={itemSearch}
              onChange={e => setItemSearch(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", background: t.white08, border: `0.5px solid ${t.accentBorderSm}`, color: t.textPrimary, padding: narrow ? "9px 32px 9px 12px" : "8px 32px 8px 12px", borderRadius: 20, fontFamily: t.fontSerif, fontSize: F.search, outline: "none", letterSpacing: "0.3px" }}
            />
            {itemSearch && <button onClick={() => setItemSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: t.textDim, cursor: "pointer", fontSize: 18, padding: 0 }}>×</button>}
          </div>
        </ListScreenHeader>
      </div>

      <ListCountBar
        left={`${tr("lists.itemCount", { count: searchFiltered.length })}${itemSearch ? ` · ${tr("common.searchQuery", { query: itemSearch })}` : ""}`}
        right={tr("common.starToSave")}
      />

      <div style={{ background: t.bgSurface }}>
        {filteredGroupOrder.map((group, gi) => (
          <div key={group}>
            <ListSectionHeading label={group === "Menu" ? tr("lists.defaultGroup") : sectionLabel(group)} borderTop={gi > 0} />
            <div style={{ padding: "0 14px 8px", display: "flex", flexDirection: "column", gap: 1 }}>
              {grouped[group].map(item => (
                <div key={item.id} onClick={() => setSelectedItem(selectedItem === item.id ? null : item.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    background: selectedItem === item.id ? t.white08 : "transparent",
                    borderLeft: selectedItem === item.id ? `2px solid ${t.accent}` : "2px solid transparent",
                    borderRadius: 8, padding: "11px 8px", cursor: "pointer", transition: "all 0.15s"
                  }}>
                  <div
                    onClick={item.imageUrl ? e => { e.stopPropagation(); setZoomedLabel({ name: item.name, varietal: item.style || item.category, region: item.brewery || item.producer, imageUrl: item.imageUrl }); } : undefined}
                    style={{ width: 40, height: 56, borderRadius: 3, background: item.imageUrl ? t.labelBackdrop : t.white06, border: `0.5px solid ${item.imageUrl ? t.accent : t.borderMid}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, overflow: "hidden", cursor: item.imageUrl ? "zoom-in" : "default" }}>
                    {item.imageUrl ? <img src={item.imageUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : itemGlyph(dataKey, item.subgroup)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: t.textPrimary, fontSize: F.name, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</div>
                    <OrderAs item={item} size={sz(11)} />
                    {(item.style || item.category) && (
                      <div style={{ color: t.accentSecondary, fontSize: F.meta, letterSpacing: "0.3px", marginBottom: 2 }}>
                        {item.style || item.category}{(item.brewery || item.producer) ? ` · ${item.brewery || item.producer}` : ""}{item.abv ? ` · ${item.abv}` : ""}
                      </div>
                    )}
                    {item.description ? (
                      <div style={{ color: t.textSecondary, fontSize: F.desc, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.description} <span style={{ color: t.accent, fontSize: F.more }}>{tr("common.details")}</span></div>
                    ) : (
                      <div style={{ color: t.accent, fontSize: F.more, fontStyle: "italic" }}>{tr("common.tapForDetails")}</div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <button onClick={e => { e.stopPropagation(); onToggleFavorite(item); }} style={{ background: "none", border: "none", fontSize: narrow ? 20 : 18, cursor: "pointer", color: favorites.some(f => f.id === item.id) ? t.accent : t.textSecondary, padding: "2px 0", lineHeight: 1 }}>
                      {favorites.some(f => f.id === item.id) ? "★" : "☆"}
                    </button>
                    <div style={{ textAlign: "right", flexShrink: 0, minWidth: narrow ? 48 : 44 }}>
                    {item.price ? (
                      <>
                        <div style={{ color: t.textPrimary, fontSize: F.price, fontWeight: 500 }}>${Math.round(item.price)}</div>
                        {/* menuLabel is set only when the same drink is also showing
                            from another menu right now (a happy hour), so the two
                            prices read like the two cards a guest would hold. */}
                        <div style={{ color: item.menuLabel ? t.accent : t.textSecondary, fontSize: F.unit, marginTop: 1 }}>{item.menuLabel || tr("lists.each")}</div>
                      </>
                    ) : (
                      <span style={{ color: t.textSecondary, fontSize: F.ask, fontStyle: "italic" }}>{tr("common.ask")}</span>
                    )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ color: t.textSecondary, textAlign: "center", padding: 40, fontSize: F.empty }}>
            {items.length === 0 ? tr("lists.comingSoon") : tr("lists.noItems")}
          </div>
        )}
      </div>

      {/* Expanded detail panel */}
      {selectedItem && (() => {
        const item = items.find(i => i.id === selectedItem);
        if (!item) return null;
        // Scoped to this block only - was `narrow` (useIsNarrow()), which is
        // still used correctly elsewhere in this component for actual
        // responsive sizing (list cards, search bar). Forced to true here on
        // request: leaving the list visible behind the expanded item left
        // guests unsure what to do next or how to close it, so every device
        // now gets the same full-screen "own new screen" treatment a phone
        // already had.
        const narrow = true;
        return (
          <div style={narrow ? {
            // maxWidth/margin match the tablet-width column every other screen
            // in this app already uses - without it this stretched edge-to-edge
            // on a wide desktop monitor once forced full-width on every device.
            position: "fixed", inset: 0, zIndex: Z.detailPanel,
            maxWidth: 680, margin: "0 auto",
            background: t.bgBase,
            overflowY: "auto", WebkitOverflowScrolling: "touch",
            padding: "0 20px calc(28px + env(safe-area-inset-bottom))",
          } : {
            position: "sticky", bottom: 0, background: t.bgSurface,
            borderTop: `1px solid ${t.borderMid}`, padding: "18px 20px",
            boxShadow: `0 -8px 32px ${t.shadow}`,
          }}>
            {narrow && (
              <div style={{
                position: "sticky", top: 0, zIndex: 1, background: t.bgBase,
                margin: "0 -20px", padding: "calc(10px + env(safe-area-inset-top)) 20px 10px",
                borderBottom: `0.5px solid ${t.borderSubtle}`,
              }}>
                <button onClick={() => setSelectedItem(null)} style={{ background: "none", border: "none", color: t.accent, fontSize: 13, fontFamily: t.fontSerif, cursor: "pointer", padding: 0, letterSpacing: "0.5px" }}>
                  ‹ {title}
                </button>
              </div>
            )}
            <div style={{ display: "flex", gap: 14, marginBottom: 12, marginTop: narrow ? 14 : 0 }}>
              <div
                onClick={item.imageUrl ? () => setZoomedLabel({ name: item.name, varietal: item.style || item.category, region: item.brewery || item.producer, imageUrl: item.imageUrl }) : undefined}
                style={{ width: 52, height: 72, borderRadius: 4, background: item.imageUrl ? t.labelBackdrop : t.white06, border: `0.5px solid ${item.imageUrl ? t.accent : t.borderMid}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, overflow: "hidden", cursor: item.imageUrl ? "zoom-in" : "default" }}>
                {item.imageUrl ? <img src={item.imageUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 4 }} /> : itemGlyph(dataKey, item.subgroup)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: t.textPrimary, fontSize: sz(narrow ? 19 : 16), fontWeight: 500, marginBottom: 3 }}>{item.name}</div>
                <OrderAs item={item} size={sz(narrow ? 13 : 12)} />
                {(item.style || item.category) && <div style={{ color: t.accentSecondary, fontSize: sz(narrow ? 12.5 : 11), marginBottom: 2 }}>{item.style || item.category}</div>}
                {(item.brewery || item.producer) && <div style={{ color: t.textMuted, fontSize: sz(narrow ? 12.5 : 11) }}>{item.brewery || item.producer}</div>}
                <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
                  {item.abv && <span style={{ color: t.textMuted, fontSize: sz(narrow ? 12 : 11) }}>{tr("lists.abv", { abv: item.abv })}</span>}
                  {item.age && <span style={{ color: t.textMuted, fontSize: sz(narrow ? 12 : 11) }}>{item.age}</span>}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0, display: "flex", alignItems: "flex-start", gap: 10 }}>
                {item.price && (
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: t.textPrimary, fontSize: narrow ? 20 : 18, fontWeight: 500 }}>${Math.round(item.price)}</div>
                    {item.menuLabel && <div style={{ color: t.accent, fontSize: narrow ? 12 : 11, marginTop: 2 }}>{item.menuLabel}</div>}
                  </div>
                )}
                {/* Full screen, the list card that opened this is no longer on
                    screen to toggle it shut. Without this the guest is stuck. */}
                {narrow && (
                  <button onClick={() => setSelectedItem(null)} aria-label={tr("lists.backToList")}
                    style={{ background: t.white06, border: `1px solid ${t.borderMid}`, color: t.textPrimary,
                      fontSize: 20, cursor: "pointer", lineHeight: 1, width: 38, height: 38,
                      borderRadius: 19, flexShrink: 0 }}>×</button>
                )}
              </div>
            </div>
            {item.imageDisclaimer && (
              <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorderSm}`, borderRadius: 6, padding: "6px 10px", marginBottom: 10, fontSize: 11, color: t.textSecondary, fontStyle: "italic", lineHeight: 1.4 }}>
                {disclaimerText(item.imageDisclaimer)}
              </div>
            )}
            {item.description && (
              <div style={{ color: t.textSecondary, fontSize: sz(narrow ? 14.5 : 13), lineHeight: 1.6, fontStyle: "italic", borderTop: `0.5px solid ${t.borderSubtle}`, paddingTop: 10, marginBottom: 12 }}>
                {item.description}
              </div>
            )}
            {planHasPairing(plan) && <ItemPairingButton item={item} onOpenChat={planHasChat(plan) ? handleOpenChat : undefined} favorites={favorites} onToggleFavorite={onToggleFavorite} tabletLocation={tabletLocation} />}
          </div>
        );
      })()}

      <LabelModal wine={zoomedLabel} onClose={() => setZoomedLabel(null)} />
      {planHasChat(plan) && <SommelierChat isOpen={chatOpen} onClose={(added) => { setChatOpen(false); if (added) setToast(added); }} contextItem={chatContext} favorites={favorites} onToggleFavorite={onToggleFavorite} tabletLocation={tabletLocation} locationNames={locationNames} />}
      {toast && <SommelierToast items={toast} onViewMenu={() => { setToast(null); onShowShortlist(); }} onDismiss={() => setToast(null)} />}
      <div style={{ height: 32 }} />
    </div>
  );
}

// The course a paired wine belongs to, as stored on the wine (courseLabel) by
// SommelierChat and SommelierScreen and saved with an emailed menu (cl). It is
// stored English and must stay so: My Menu and the saved-menu page group wines
// under a course by matching it, and a saved menu outlives any language change.
// The heading a guest sees is tr(`pairing.course.${role}`), at display time only.
const STORED_COURSE_LABELS = { first: "First Course", main: "Main Course", dessert: "Dessert" };

// ─── Server-written text, shown in the guest's language (step 4c-2) ──────────
// The server sends a locale key beside its English text (splitNoteKey,
// colourNoteKey, messageKey, replyKey). Shown through tr(); the English text is
// the fallback for a key this build does not know.
function keyedText(k, fallback) {
  if (k && typeof k.key === "string") {
    const out = tr(k.key, k.params || undefined);
    if (out !== k.key) return out;
  }
  return fallback || null;
}

// "Your red picks are below. If you're open to a white, ..." - one sentence per
// colour group, with the suggested style filled in.
function colourNoteText(k, fallback) {
  const p = k && k.params;
  if (!k || !k.key || !p || !p.group) return fallback || null;
  const key = `${k.key}.${p.group}`;
  const out = tr(key, { noun: tr(`pairing.note.noun.${p.colour || "other"}`) });
  return out === key ? (fallback || null) : out;
}

// A pairing's `level` stays English in data: the client keys "Get Different
// Pairings" exclusions on it. Translated only where shown, like courseLabel.
const LEVEL_KEYS = { "Value": "value", "Mid-Range": "mid", "Premium": "premium" };
function levelLabel(level) {
  const k = LEVEL_KEYS[level];
  return k ? tr(`pairing.level.${k}`) : (level || "");
}

// A byCourse heading is either one of ours ("First Course") or a dish name from a
// per-dish split. Ours translate; the restaurant's dish names never do.
function courseHeading(course) {
  const role = Object.keys(STORED_COURSE_LABELS).find(r => STORED_COURSE_LABELS[r] === course);
  return role ? tr(`pairing.course.${role}`) : course;
}

// The two standard label disclaimers are server constants (LABEL_DISCLAIMER /
// GENERIC_DISCLAIMER in functions/index.js), stored on items. Matched exactly and
// shown translated; anything else (a manager's own wording) is shown as written.
const LABEL_DISCLAIMER_KEYS = {
  "Label shown for reference \u2014 the actual label may differ by vintage. Ask your server to see the bottle.": "labelZoom.disclaimerWine",
  "Label shown for reference \u2014 actual packaging may vary.": "labelZoom.disclaimerOther",
};
function disclaimerText(d) {
  const k = LABEL_DISCLAIMER_KEYS[d];
  return k ? tr(k) : d;
}

// ─── Room names in the guest's language ──────────────────────────────────────
// Treated like dish names. Our defaults ("Bar", "Dining Room") are our words and
// translate (rooms.*). A name the owner chose ("Tuques", "AK") is the
// restaurant's own and shows as written in every language, unless the owner has
// given a per-language name: appSettings.locationNamesI18n
// ({ dining: { "fr-CA": "Salle principale" } }), written by the Settings tab in
// step 5. Kept OUTSIDE appSettings.locationNames on purpose: the room-name editor
// copies that object into its draft. Read into a module variable by
// applySettings(), like the language, so no component needs a new prop.
const DEFAULT_ROOM_NAMES = { bar: "Bar", dining: "Dining Room" };
let roomNameOverrides = {};
function setRoomNameOverrides(o) {
  roomNameOverrides = o && typeof o === "object" ? o : {};
}
function roomName(id, locationNames) {
  const ov = roomNameOverrides[id] || {};
  const requested = getRequestedLanguage(), shown = getLanguage();
  const base = shown.split("-")[0];
  const sameBase = Object.keys(ov).find(k => k.split("-")[0] === base);
  const override = ov[requested] || ov[shown] || (sameBase ? ov[sameBase] : null);
  if (override) return override;
  const own = (locationNames || {})[id];
  if (!own || own === DEFAULT_ROOM_NAMES[id]) return DEFAULT_ROOM_NAMES[id] ? tr(`rooms.${id}`) : (own || "");
  return own;
}

// ─── Menu section headings in the guest's language (Oct 29, 2026) ───────────
// "Starters", "Bar Bites", "Signature": the restaurant's own headings. Each guest
// list arrives with sectionLabels ({ heading: translation }) for the guest's
// language; it REPLACES the map, so switching back to the original language clears
// it. Display only - the original heading stays the group and filter key.
let sectionLabels = {};
// How the guest Wine List orders its sections, from getWines' sectionOrder:
// 'colour' for an uploaded list (sparkling, white, rosé, red, dessert), anything
// else keeps the list's own order - a POS restaurant arranged its menu groups
// on purpose. See WineListScreen and orderWineSections in functions/index.js.
let WINE_SECTIONS_BY_COLOUR = false;
function registerSectionLabels(map) {
  if (map && typeof map === "object") sectionLabels = map;
}
function sectionLabel(name) {
  return (name && sectionLabels[name]) || name;
}

// ─── The restaurant's own name under a translated name (Oct 29, 2026) ──────
// A dish or drink shown under a manager's translated name also shows the name the
// restaurant uses, so a guest can order it from a server who only knows that one.
// nameOriginal comes from localizeDescriptions() on the server and is kept in
// starred items and the saved-menu link (no). Nothing shows when they match.
function OrderAs({ item, size = 11 }) {
  const t = useTheme();
  const orig = item && item.nameOriginal;
  if (!orig || orig === item.name) return null;
  return (
    <div style={{ color: t.textSecondary, fontSize: size, fontStyle: "italic", lineHeight: 1.3, marginBottom: 2 }}>
      {orig}
    </div>
  );
}

// The app makes no allergen or dietary claims - guests rely on the printed menu
// and their server (decided Oct 29, 2026). Shown wherever dishes are listed.
function DietaryNote({ text, size = 11.5 }) {
  const t = useTheme();
  return (
    <div style={{ color: t.textSecondary, fontSize: size, fontStyle: "italic", lineHeight: 1.5, textAlign: "center", padding: "14px 20px 6px" }}>
      {text}
    </div>
  );
}

// ─── My Menu QR Code Helpers ─────────────────────────────────────────────────

function encodeFavorites(favorites) {
  const compact = favorites.map(f => {
    if (f.favoriteType === 'food') return { t: 'food', n: f.name, no: f.nameOriginal || undefined, cr: f.courseRole, p: f.price, d: clipAtWord(f.description, 80) };
    if (f.favoriteType === 'wine') return { t: 'wine', n: f.name, no: f.nameOriginal || undefined, v: f.varietal, r: f.region, gp: f.glassPrice, bp: f.bottlePrice, rs: clipAtWord(f.reason, 140), cl: f.courseLabel, fp: f.fromPairing, img: f.imageUrl || null };
    return { t: f.favoriteType, n: f.name, no: f.nameOriginal || undefined, p: f.price };
  });
  try { return btoa(unescape(encodeURIComponent(JSON.stringify({ v: 1, dt: Date.now(), items: compact })))); } catch(e) { return null; }
}

function decodeFavorites(encoded) {
  const parsed = JSON.parse(decodeURIComponent(escape(atob(encoded))));
  // Handle both new format { v, dt, items } and old format (plain array)
  const compact = Array.isArray(parsed) ? parsed : (parsed.items || []);
  const savedAt = Array.isArray(parsed) ? null : (parsed.dt || null);
  const favorites = compact.map((f, i) => ({
    id: `shared-${i}`,
    favoriteType: f.t,
    name: f.n,
    nameOriginal: f.no,
    courseRole: f.cr,
    price: f.p,
    description: f.d,
    varietal: f.v,
    region: f.r,
    glassPrice: f.gp,
    bottlePrice: f.bp,
    reason: f.rs,
    courseLabel: f.cl,
    fromPairing: f.fp,
  }));
  return Object.assign(favorites, { _savedAt: savedAt });
}

// ─── Guest Menu Loader (fetches saved menu from DB by short code) ─────────────

// Saved menus store a shortened description. A hard slice cut mid-word
// ("tossed in a bright orange-sha") on the saved page and in the email; this cuts
// at the last whole word and marks the cut. Saved-menu records are small and live
// in the database, so 160 characters costs nothing. The legacy encoded-URL path
// keeps 80, since that text travels inside the URL itself.
// My Menu can go to a whole table at once: addresses separated by commas,
// semicolons or spaces, deduplicated. The server applies the same split, the
// same cap, and sends each guest their own copy.
const MENU_EMAIL_MAX = 10;
function parseEmailList(raw) {
  return [...new Set(String(raw || "").split(/[,;\s]+/).map(a => a.trim().toLowerCase()).filter(Boolean))];
}

function clipAtWord(text, max) {
  const str = String(text || "").trim();
  if (str.length <= max) return str;
  const cut = str.slice(0, max);
  const space = cut.lastIndexOf(" ");
  return (space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:.\-–—]+$/, "") + "…";
}

// The saved-menu page is rendered from the App root, OUTSIDE AppContent - which
// is where the restaurant's theme is resolved and provided. So it always painted
// in the platform default colours, whatever the restaurant had configured.
// Resolve the theme here the same way the tablet does: the configured default
// location's theme, seeded from this device's cache, with the server value
// winning when it lands. Held for at most 2.5s so a guest never sees the default
// palette flash first.
function GuestMenuLoader({ menuCode }) {
  const [themeKey, setThemeKey] = useState(() => {
    const cached = readCachedThemes();
    return cached ? (cached.barTheme || cached.diningTheme || null) : null;
  });
  const [themeReady, setThemeReady] = useState(() => !!themeKey);
  // The restaurant's own identity on the page: this is a guest-facing page carrying
  // their brand, often opened days later from an email. Logo resolution matches the
  // tablet's location toggle - uploaded URL, then the legacy /logos/ file, and the
  // light-background variant when the theme is light.
  const [brand, setBrand] = useState({ logoSet: null, websiteUrl: null });
  useEffect(() => {
    let done = false;
    const gate = setTimeout(() => { if (!done) setThemeReady(true); }, 2500);
    rfetch(SETTINGS_URL)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        // The page renders outside AppContent, so it sets the restaurant's
        // language itself - before the state updates below trigger the render.
        if (data && data.settings) setLanguage(URL_LANG || data.settings.language);
        if (data && data.settings) setCurrencySettings(data.settings);
        const ds = (data && data.settings && data.settings.deviceSetup) || {};
        const dining = ds.defaultLocation === "dining";
        const key = (dining ? ds.diningTheme : ds.barTheme) || ds.barTheme || ds.diningTheme;
        if (key) setThemeKey(key);
        const pick = (url, legacy, alt) => ({ url: url || (legacy ? `/logos/${legacy}` : null), alt: alt || null });
        const primary = dining ? pick(ds.diningLogoUrl, ds.diningLogo, ds.diningAltLogoUrl) : pick(ds.barLogoUrl, ds.barLogo, ds.barAltLogoUrl);
        const other   = dining ? pick(ds.barLogoUrl, ds.barLogo, ds.barAltLogoUrl) : pick(ds.diningLogoUrl, ds.diningLogo, ds.diningAltLogoUrl);
        const website = data && typeof data.websiteUrl === "string" && /^https?:\/\//i.test(data.websiteUrl) ? data.websiteUrl : null;
        setBrand({ logoSet: primary.url ? primary : (other.url ? other : null), websiteUrl: website });
      })
      .catch(() => {})
      .finally(() => { done = true; clearTimeout(gate); setThemeReady(true); });
    return () => { done = true; clearTimeout(gate); };
  }, []);
  const theme = getTheme(themeKey) || resolveTheme(themeKey);
  if (!themeReady) return <div style={{ minHeight: "100vh", background: theme.bgBase }} />;
  const logoUrl = brand.logoSet ? ((theme.isLight && brand.logoSet.alt) ? brand.logoSet.alt : brand.logoSet.url) : null;
  return (
    <ThemeContext.Provider value={theme}>
      <GuestMenuLoaderInner menuCode={menuCode} logoUrl={logoUrl} websiteUrl={brand.websiteUrl} />
    </ThemeContext.Provider>
  );
}

function GuestMenuLoaderInner({ menuCode, logoUrl = null, websiteUrl = null }) {
  const t = useTheme();
  // Re-render when the page's language file arrives (loaded on demand).
  const [, setI18nTick] = useState(0);
  useEffect(() => onLanguageLoaded(() => setI18nTick(n => n + 1)), []);
  const [state, setState] = useState("loading"); // loading | ready | expired | error
  const [favorites, setFavorites] = useState([]);
  const [savedAt, setSavedAt] = useState(null);
  // The venue name is fetched rather than hardcoded: this screen is shown to a
  // guest of whichever restaurant the link belongs to, not always our own.
  const [venueName, setVenueName] = useState("");

  useEffect(() => {
    rfetch(GET_PROFILE_URL).then(r => r.json()).then(d => setVenueName(d.name || "")).catch(() => {});
  }, []);

  useEffect(() => {
    // restaurantId is REQUIRED: getMenu reads restaurants/{id}/savedMenus and,
    // without it, looks under a placeholder tenant and returns not_found. It was
    // missing here, so every emailed "View with labels" link and every My Menu QR
    // code showed "Menu not found". RESTAURANT_ID comes from the link's
    // ?restaurant= param or the subdomain.
    fetch(`${GET_MENU_URL}?id=${encodeURIComponent(menuCode)}&restaurantId=${encodeURIComponent(RESTAURANT_ID || "")}`)
      .then(r => r.json())
      .then(data => {
        if (data.ok && data.favorites) { setFavorites(data.favorites); setSavedAt(data.createdAt || null); setState("ready"); }
        else if (data.error === "expired") setState("expired");
        else setState("error");
      })
      .catch(() => setState("error"));
  }, [menuCode]);

  if (state === "loading") return (
    <div style={{ background: t.bgBase, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: t.fontSerif }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ color: t.accent, fontSize: 22, marginBottom: 12 }}>✦</div>
        <div style={{ color: t.accent, fontSize: 12, letterSpacing: "3px", textTransform: "uppercase" }}>{tr("guestMenu.loading")}</div>
      </div>
    </div>
  );

  if (state === "expired") return (
    <div style={{ background: t.bgBase, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 32, fontFamily: t.fontSerif }}>
      <div style={{ textAlign: "center", maxWidth: 360 }}>
        <div style={{ color: t.accent, fontSize: 28, marginBottom: 16 }}>✦</div>
        {venueName && <div style={{ color: t.accent, fontSize: 12, letterSpacing: "3px", textTransform: "uppercase", marginBottom: 12 }}>{venueName}</div>}
        <div style={{ color: t.textPrimary, fontSize: 18, marginBottom: 12 }}>{tr("guestMenu.expiredTitle")}</div>
        <div style={{ color: t.textSecondary, fontSize: 13, lineHeight: 1.6 }}>{tr("guestMenu.expiredBody")}</div>
      </div>
    </div>
  );

  if (state === "error") return (
    <div style={{ background: t.bgBase, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 32, fontFamily: t.fontSerif }}>
      <div style={{ textAlign: "center", color: t.textDim, fontSize: 14 }}>{tr("guestMenu.notFound")}</div>
    </div>
  );

  return <GuestMenuScreen favorites={favorites} savedAt={savedAt} venueName={venueName} logoUrl={logoUrl} websiteUrl={websiteUrl} />;
}

// ─── Guest Menu Screen (read-only, opened via QR code on guest's phone) ───────

function GuestMenuScreen({ favorites, savedAt, venueName = "", logoUrl = null, websiteUrl = null }) {
  const t = useTheme();
  // Guest text size, same "Aa" control and scale as the tablet screens. This page
  // is read on a phone, often days later, by someone without the tablet's larger
  // type - the base sizes here are also a step up from what they were.
  const textScale = useTextScale();
  const sz = n => Math.round(n * textScale * 2) / 2;
  // A logo that fails to load falls back to the name, never a broken-image icon.
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = !!logoUrl && !logoFailed;
  // Tapping a label opens it large. The thumbnail already called setZoomedLabel,
  // but this screen never declared it - so the tap threw and nothing happened,
  // on the one page whose button promises "View with labels".
  const [zoomedLabel, setZoomedLabel] = useState(null);
  // Shown without the scheme: "gildedfork.com", not "https://www.gildedfork.com/".
  const websiteLabel = websiteUrl ? websiteUrl.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/+$/, "") : "";
  const courseOrder  = ["first", "main", "dessert"];

  const foodItems  = favorites.filter(f => f.favoriteType === "food");
  const wineItems  = favorites.filter(f => f.favoriteType === "wine");
  const drinkItems = favorites.filter(f => f.favoriteType !== "food" && f.favoriteType !== "wine");

  const foodByCourse = {};
  foodItems.forEach(f => { const r = f.courseRole || "main"; if (!foodByCourse[r]) foodByCourse[r] = []; foodByCourse[r].push(f); });
  const foodCourses = courseOrder.filter(r => foodByCourse[r]);

  const winesByCourseLabel = {};
  const standaloneWines = [];
  wineItems.forEach(w => {
    if (w.fromPairing && w.courseLabel) {
      if (!winesByCourseLabel[w.courseLabel]) winesByCourseLabel[w.courseLabel] = [];
      winesByCourseLabel[w.courseLabel].push(w);
    } else { standaloneWines.push(w); }
  });

  const SectionHeader = ({ label }) => (
    <div style={{ background: t.bgCardHover, padding: "8px 16px", marginBottom: 10, marginTop: 6, borderRadius: 6 }}>
      <div style={{ color: t.accent, fontSize: sz(11), letterSpacing: "2px", textTransform: "uppercase", fontWeight: 600 }}>✦ {label}</div>
    </div>
  );

  const WineCard = ({ item }) => (
    <div style={{ background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        {item.imageUrl
          ? <div onClick={() => setZoomedLabel(item)} style={{ width: 36, height: 50, borderRadius: 3, overflow: "hidden", flexShrink: 0, cursor: "zoom-in", border: `0.5px solid ${t.accent}` }}><img src={item.imageUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div>
          : <div style={{ fontSize: 22, flexShrink: 0 }}>🍷</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: t.textPrimary, fontSize: sz(15.5), marginBottom: 2 }}>{item.name}</div>
          <OrderAs item={item} size={sz(12)} />
          {(item.varietal || item.region) && <div style={{ color: t.accentSecondary, fontSize: sz(10.5), letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4 }}>{[item.varietal, item.region].filter(Boolean).join(" · ")}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            {item.glassPrice && <div style={{ color: t.textSecondary, fontSize: sz(12.5) }}>{formatPrice(item.glassPrice)} <span style={{ color: t.textSecondary }}>{tr("common.glass")}</span></div>}
            {item.bottlePrice && <div style={{ color: t.textSecondary, fontSize: sz(12.5) }}>{formatPrice(item.bottlePrice)} <span style={{ color: t.textSecondary }}>{tr("common.bottle")}</span></div>}
          </div>
          {item.imageUrl && <div onClick={() => setZoomedLabel(item)} style={{ color: t.accent, fontSize: sz(11), letterSpacing: "0.5px", marginTop: 4, cursor: "pointer" }}>{tr("chat.tapLabel")}</div>}
        </div>
      </div>
      {item.reason && <div style={{ color: t.textSecondary, fontSize: sz(14), fontStyle: "italic", lineHeight: 1.6, marginTop: 10, paddingTop: 10, borderTop: `0.5px solid ${t.accentBorderSm}` }}>"{item.reason}"</div>}
    </div>
  );

  return (
    <div style={{ background: t.bgBase, minHeight: "100vh", fontFamily: t.fontSerif, maxWidth: 480, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ position: "relative", background: t.bgSurface, borderBottom: `1px solid ${t.borderSubtle}`, padding: "24px 20px 20px", textAlign: "center" }}>
        <div style={{ position: "absolute", top: 14, right: 14 }}><TextSizeButton /></div>
        {/* The restaurant's logo leads, then its name as a fallback. This page was
            headed "MY MENU / My Menu" - the same words twice and nothing saying
            whose menu it was, on a page a guest opens days later from an email. */}
        {showLogo ? (
          <div style={{ marginBottom: 14 }}>
            <img src={logoUrl} alt={venueName || tr("guestMenu.logoAlt")} onError={() => setLogoFailed(true)}
              style={{ maxHeight: 64, maxWidth: "70%", objectFit: "contain", display: "inline-block" }} />
          </div>
        ) : venueName ? (
          <div style={{ color: t.accent, fontSize: 12, letterSpacing: "3px", textTransform: "uppercase", marginBottom: 8 }}>{venueName}</div>
        ) : null}
        <div style={{ color: t.textPrimary, fontSize: 20, marginBottom: 4 }}>{tr("myMenu.title")}</div>
        {savedAt && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `0.5px solid ${t.accentBorderSm}` }}>
            <div style={{ color: t.accent, fontSize: 12, letterSpacing: "0.5px" }}>
              {new Date(savedAt).toLocaleDateString(dateLocale(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: "16px 16px 48px" }}>
        {foodCourses.map(role => {
          const courseWines = winesByCourseLabel[STORED_COURSE_LABELS[role]] || [];
          return (
            <div key={role}>
              <SectionHeader label={tr(`pairing.course.${role}`)} />
              {foodByCourse[role].map(item => (
                <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ fontSize: 18, flexShrink: 0, marginTop: 2 }}>🍽️</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: t.textPrimary, fontSize: sz(15.5) }}>{item.name}</div>
                    <OrderAs item={item} size={sz(12)} />
                    {item.description && <div style={{ color: t.textSecondary, fontSize: sz(13), lineHeight: 1.45, fontStyle: "italic", marginTop: 2 }}>{item.description}</div>}
                    {/* A dish the chat suggested FOR a drink carries why. */}
                    {item.reason && <div style={{ color: t.accent, fontSize: sz(12.5), lineHeight: 1.45, fontStyle: "italic", marginTop: 4 }}>{item.reason}</div>}
                    {item.price && <div style={{ color: t.textSecondary, fontSize: sz(12.5), marginTop: 3 }}>{formatItemPrice(item)}</div>}
                  </div>
                </div>
              ))}
              {courseWines.map(item => <WineCard key={item.id} item={item} />)}
            </div>
          );
        })}

        {foodItems.length > 0 && <DietaryNote text={tr("myMenu.dietaryNote")} size={sz(12)} />}

        {standaloneWines.length > 0 && (
          <div>
            <SectionHeader label={tr("myMenu.wines")} />
            {standaloneWines.map(item => <WineCard key={item.id} item={item} />)}
          </div>
        )}

        {drinkItems.length > 0 && (
          <div>
            <SectionHeader label={tr("myMenu.drinks")} />
            {drinkItems.map(item => (
              <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 20, flexShrink: 0 }}>
                  {itemGlyph(item.favoriteType, item.subgroup)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: t.textPrimary, fontSize: sz(15.5) }}>{item.name}</div>
                  <OrderAs item={item} size={sz(12)} />
                </div>
                {item.price && <div style={{ color: t.textSecondary, fontSize: sz(12.5) }}>{formatItemPrice(item)}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ textAlign: "center", padding: "24px 20px 40px", margin: "8px 16px 0", borderTop: `0.5px solid ${t.accentBorderSm}` }}>
        {/* The way back to the restaurant. Only when a website is set in Settings. */}
        {websiteUrl && (
          <div style={{ marginBottom: 22 }}>
            {venueName && <div style={{ color: t.textSecondary, fontSize: sz(14), marginBottom: 10 }}>{tr("guestMenu.welcomeBack", { venue: venueName })}</div>}
            <a href={websiteUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-block", border: `0.5px solid ${t.accent}`, color: t.accent, textDecoration: "none",
                padding: "10px 22px", borderRadius: 8, fontSize: 14, fontFamily: t.fontSerif }}>
              {tr("guestMenu.visit", { site: websiteLabel })}
            </a>
          </div>
        )}
        <div style={{ color: t.textDim, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase" }}>
          {tr("home.poweredBy")}
        </div>
      </div>

      <LabelModal wine={zoomedLabel} onClose={() => setZoomedLabel(null)} zIndex={1000} />
    </div>
  );
}

// ─── Shortlist Screen ─────────────────────────────────────────────────────────

function ShortlistScreen({ favorites, onRemove, onClose, onNewPairing = null }) {
  const t = useTheme();
  // Guest text size - see TEXT_SCALES. The title and Back stay fixed.
  const textScale = useTextScale();
  const sz = n => Math.round(n * textScale * 2) / 2;
  const courseOrder  = ["first", "main", "dessert"];

  const foodItems  = favorites.filter(f => f.favoriteType === "food");
  const wineItems  = favorites.filter(f => f.favoriteType === "wine");
  const drinkItems = favorites.filter(f => f.favoriteType !== "food" && f.favoriteType !== "wine");

  const foodByCourse = {};
  foodItems.forEach(f => {
    const r = f.courseRole || "main";
    if (!foodByCourse[r]) foodByCourse[r] = [];
    foodByCourse[r].push(f);
  });
  const foodCourses = courseOrder.filter(r => foodByCourse[r]);

  // Wines from AI pairing embed in their course; wines from wine list sit standalone
  const winesByCourseLabel = {};
  const standaloneWines = [];
  wineItems.forEach(w => {
    if (w.fromPairing && w.courseLabel) {
      if (!winesByCourseLabel[w.courseLabel]) winesByCourseLabel[w.courseLabel] = [];
      winesByCourseLabel[w.courseLabel].push(w);
    } else {
      standaloneWines.push(w);
    }
  });

  const hasCourses = foodCourses.length > 0;

  const [zoomedLabel, setZoomedLabel] = useState(null);
  const [menuCode, setMenuCode] = useState(null);
  // The saved code is a snapshot of the picks at the moment it was made. A guest
  // who emails, then adds a dessert and emails again, must get a new snapshot -
  // reusing the code sent the old list. Keyed on what the snapshot contains.
  const favoritesKey = favorites.map(f => `${f.favoriteType}:${f.id}:${f.courseRole || ""}`).join("|");
  useEffect(() => { setMenuCode(null); }, [favoritesKey]);
  const [emailInput, setEmailInput] = useState("");
  const [emailState, setEmailState] = useState("idle"); // idle | sending | sent | error
  // Unchecked by default, and deliberately so. Every tenant sends from the same
  // getsommsync.com domain, so deliverability reputation is shared: pre-ticking
  // produces recipients who do not remember subscribing, they press "spam"
  // rather than unsubscribe, and one careless tenant degrades inbox placement
  // for every restaurant on the platform. It is also the only form of consent
  // that is valid under CASL, which matters for the Vancouver market.
  const [joinList, setJoinList] = useState(false);
  // Remembers what was sent, because joinList itself is cleared on success.
  const [sentWithList, setSentWithList] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [sentCount, setSentCount] = useState(0);
  const [venueName, setVenueName] = useState("");
  // Gates the mailing-list checkbox only — never the email itself. Defaults to
  // false so a tenant whose jurisdiction has not been reviewed shows no checkbox
  // at all, including if this fetch fails.
  const [listOptIn, setListOptIn] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // Fetched here rather than threaded through props — same pattern GuestMenuLoader
  // already uses, and it keeps this change inside one component.
  React.useEffect(() => {
    rfetch(GET_PROFILE_URL).then(r => r.json()).then(d => {
      setVenueName(d.name || "");
      setListOptIn(d.guestListOptIn === true);
    }).catch(() => {});
  }, []);

  // Saves the picks and returns the 6-char code the emailed link points at.
  // The QR path that used to share this was removed — a code a guest can only
  // reach by finding the tab again is not worth the screen it sits on — so this
  // now exists solely for the email.
  async function ensureMenuCode() {
    if (menuCode) return menuCode;
    const compact = favorites.map(f => ({
      t: f.favoriteType, n: f.name, cr: f.courseRole, p: f.price,
      d: clipAtWord(f.description, 160), v: f.varietal, r: f.region,
      gp: f.glassPrice, bp: f.bottlePrice, rs: clipAtWord(f.reason, 600),
      cl: f.courseLabel, fp: f.fromPairing, img: f.imageUrl || null,
    }));
    const res = await rfetch(SAVE_MENU_URL, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ favorites: compact })
    });
    const data = await res.json();
    if (data.ok) { setMenuCode(data.menuId); return data.menuId; }
    return null;
  }


  async function handleSendEmail() {
    const addresses = parseEmailList(emailInput);
    const trimmed = addresses.join(", ");
    // Loose check only. Rejecting valid-but-unusual addresses is a worse failure
    // than letting Resend bounce one — the guest is standing at a table.
    const bad = addresses.find(a => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a));
    if (addresses.length === 0 || bad) {
      setEmailError(bad ? tr("myMenu.email.badAddress", { address: bad }) : "");
      setEmailState("error"); return;
    }
    if (addresses.length > MENU_EMAIL_MAX) {
      setEmailError(tr("myMenu.email.tooMany", { max: MENU_EMAIL_MAX }));
      setEmailState("error"); return;
    }
    setEmailError("");
    setEmailState("sending");
    try {
      const code = await ensureMenuCode();
      if (!code) { setEmailState("error"); return; }
      const res = await rfetch(SEND_EMAIL_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        // policyVersion and consentText are recorded with the consent. Neither
        // can be reconstructed after the fact, which is why they are sent even
        // though the server could guess at them.
        body: JSON.stringify({
          // Consent is one person's - never sent with several addresses. The
          // server enforces the same rule.
          email: trimmed, menuId: code, marketingConsent: joinList && addresses.length === 1,
          policyVersion: POLICY_VERSION,
          consentText: (joinList && addresses.length === 1) ? consentText(venueName) : null,
        })
      });
      const data = await res.json();
      if (data.ok) {
        // Wipe the address as soon as it has been sent. Tablets are shared and
        // there is no walk-away reset — Android kiosk constraints ruled that out —
        // so the only guarantee that the next guest cannot recover the previous
        // one's address is that the app stops holding it. Clearing on success
        // rather than on unmount also covers the guest who backs out of the
        // confirmation and lands on the form again.
        setSentWithList(joinList && addresses.length === 1);
        setSentCount(data.sent || addresses.length);
        if (Array.isArray(data.failed) && data.failed.length) {
          // Partial: keep only the addresses that failed in the box to retry.
          setEmailInput(data.failed.join(", "));
          setEmailError(tr("myMenu.email.partial", { sent: data.sent, failed: data.failed.join(", ") }));
          setEmailState("error");
          return;
        }
        setEmailInput("");
        setJoinList(false);
        setEmailState("sent");
      } else {
        // Keep the address on a failure — the guest needs to retry, and making
        // them retype it is a worse outcome than the brief exposure.
        setEmailState("error");
      }
    } catch(e) { setEmailState("error"); }
  }

  const SectionHeader = ({ label }) => (
    <div style={{ background: t.bgCardHover, padding: "8px 16px", marginBottom: 10, marginTop: 6, borderRadius: 6 }}>
      <div style={{ color: t.accent, fontSize: sz(10), letterSpacing: "2px", textTransform: "uppercase", fontWeight: 600 }}>✦ {label}</div>
    </div>
  );

  const RemoveBtn = ({ item }) => (
    <button onClick={() => onRemove(item.id)} style={{ background: "none", border: "none", color: t.borderStrong, cursor: "pointer", fontSize: 20, padding: "2px 4px", lineHeight: 1, flexShrink: 0 }}>×</button>
  );

  const WineCard = ({ item }) => {
    const [expanded, setExpanded] = React.useState(false);
    const desc = item.description || item.reason || null;
    const truncated = desc && desc.length > 100 ? desc.slice(0, 100) + "…" : desc;
    return (
    <div style={{ background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        {item.imageUrl
          ? <div onClick={() => setZoomedLabel(item)} style={{ width: 36, height: 50, borderRadius: 3, overflow: "hidden", flexShrink: 0, cursor: "zoom-in", border: `0.5px solid ${t.accent}` }}><img src={item.imageUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div>
          : <div style={{ fontSize: 22, flexShrink: 0 }}>🍷</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: t.textPrimary, fontSize: sz(14), marginBottom: 2 }}>{item.name}</div>
          <OrderAs item={item} size={sz(11)} />
          {(item.varietal || item.region) && <div style={{ color: t.accentSecondary, fontSize: sz(10), letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4 }}>{[item.varietal, item.region].filter(Boolean).join(" · ")}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            {item.glassPrice && <div style={{ color: t.textSecondary, fontSize: sz(11) }}>{formatPrice(item.glassPrice)} <span style={{ color: t.textSecondary }}>{tr("common.glass")}</span></div>}
            {item.bottlePrice && <div style={{ color: t.textSecondary, fontSize: sz(11) }}>{formatPrice(item.bottlePrice)} <span style={{ color: t.textSecondary }}>{tr("common.bottle")}</span></div>}
          </div>
          {desc && (
            <div style={{ marginTop: 6 }}>
              <span style={{ color: t.textDim, fontSize: sz(11), lineHeight: 1.5, fontStyle: "italic" }}>{expanded ? desc : truncated}</span>
              {desc.length > 100 && (
                <span onClick={() => setExpanded(e => !e)} style={{ color: t.accent, fontSize: sz(11), cursor: "pointer", marginLeft: 4 }}>{" "}{expanded ? tr("chat.less") : tr("chat.more")}</span>
              )}
            </div>
          )}
        </div>
        <RemoveBtn item={item} />
      </div>
    </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: Z.shortlist, display: "flex", justifyContent: "center" }}>
    <div style={{ width: "100%", maxWidth: 680, background: t.bgBase, display: "flex", flexDirection: "column", fontFamily: t.fontSerif, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ background: t.bgSurface, borderBottom: `1px solid ${t.borderSubtle}`, padding: "16px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: t.accent, fontSize: 12, letterSpacing: "3px", textTransform: "uppercase" }}>{tr("myMenu.title")}</div>
            <div style={{ color: t.textSecondary, fontSize: 11, marginTop: 2 }}>{tr("myMenu.subtitle")}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <TextSizeButton />
          {/* The Save button opened a take-home QR. Removed for now: it only helps a
              guest who bookmarks the link, which nobody does, and it promised
              permanence the product does not yet deliver. My Menu's real job is
              showing a server what the table wants, and that works within the
              session. The handler and the QR modal are left in place for when the
              email send is built - that is the version of this worth having. */}
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.accent, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 13, letterSpacing: "1px", padding: "4px 0" }}>{tr("myMenu.back")}</button>
          </div>
        </div>
        <div style={{ borderTop: `0.5px solid ${t.accentBorderSm}`, paddingTop: 8, color: t.accent, fontSize: sz(11), fontStyle: "italic", textAlign: "center" }}>
          {tr("myMenu.readyToOrder")}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 32px" }}>
        {favorites.length === 0 ? (
          <div style={{ color: t.textSecondary, textAlign: "center", padding: "60px 20px" }}>
            <div style={{ fontSize: 36, marginBottom: 16 }}>☆</div>
            <div style={{ fontSize: sz(14) }}>{tr("myMenu.empty")}</div>
          </div>
        ) : (
          <>
            {/* Food by course, with paired wines embedded */}
            {hasCourses && foodCourses.map(role => {
              const courseWines = winesByCourseLabel[STORED_COURSE_LABELS[role]] || [];
              return (
                <div key={role}>
                  <SectionHeader label={tr(`pairing.course.${role}`)} />
                  {foodByCourse[role].map(item => (
                    <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 8, marginBottom: 8 }}>
                      <div style={{ fontSize: 18, flexShrink: 0, marginTop: 2 }}>🍽️</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: t.textPrimary, fontSize: sz(14) }}>{item.name}</div>
                        <OrderAs item={item} size={sz(11)} />
                        {item.description && <div style={{ color: t.textSecondary, fontSize: sz(11), fontStyle: "italic", marginTop: 2, lineHeight: 1.5 }}>{item.description}</div>}
                        {item.price && <div style={{ color: t.textSecondary, fontSize: sz(11), marginTop: 3 }}>{formatItemPrice(item)}</div>}
                      </div>
                      <RemoveBtn item={item} />
                    </div>
                  ))}
                  {courseWines.map(item => <WineCard key={item.id} item={item} />)}
                </div>
              );
            })}

            {foodItems.length > 0 && <DietaryNote text={tr("myMenu.dietaryNote")} size={sz(11)} />}

            {/* Standalone wines — added from wine list, not from a course pairing */}
            {standaloneWines.length > 0 && (
              <div>
                <SectionHeader label={tr("myMenu.wines")} />
                {standaloneWines.map(item => <WineCard key={item.id} item={item} />)}
              </div>
            )}

            {/* Other drinks */}
            {drinkItems.length > 0 && (
              <div>
                <SectionHeader label={tr("myMenu.drinks")} />
                {drinkItems.map(item => (
                  <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: t.white04, border: `0.5px solid ${t.borderSubtle}`, borderRadius: 8, marginBottom: 8 }}>
                    <div style={{ fontSize: 20, flexShrink: 0 }}>
                      {itemGlyph(item.favoriteType, item.subgroup)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: t.textPrimary, fontSize: sz(14) }}>{item.name}</div>
                      <OrderAs item={item} size={sz(11)} />
                      <div style={{ color: t.accent, fontSize: sz(10), letterSpacing: "1px", textTransform: "uppercase", marginTop: 2 }}>
                        {item.favoriteType === "beer" ? (item.style || tr("myMenu.kind.beer")) : item.favoriteType === "cocktail" ? tr("myMenu.kind.cocktail") : item.favoriteType === "nab" ? tr("myMenu.kind.nab") : tr("myMenu.kind.pour")}
                      </div>
                      {/* A drink the chat paired with a dish carries why. */}
                      {item.reason && <div style={{ color: t.textSecondary, fontSize: sz(12.5), lineHeight: 1.45, fontStyle: "italic", marginTop: 4 }}>{item.reason}</div>}
                    </div>
                    {item.price && <div style={{ color: t.textSecondary, fontSize: sz(12), flexShrink: 0 }}>{formatItemPrice(item)}</div>}
                    <RemoveBtn item={item} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Back to the pairing screen, cleared, for the next course. Guests land
            here after pairing their mains and then want dessert, and the only
            way back was Back plus Back plus un-ticking every dish (David's
            test, Oct 25, 2026). Same action and name as the link on the result
            screen; My Menu is kept either way. Absent where pairing is (no
            onNewPairing - non-premium plans). */}
        {onNewPairing && (
          <button onClick={onNewPairing}
            style={{ width: "100%", marginTop: 16, background: t.accentDimSm, border: `1px solid ${t.accent}`, color: t.accent, padding: "12px 8px", borderRadius: 8, cursor: "pointer", fontFamily: t.fontSerif, fontSize: sz(14), lineHeight: 1.3, textAlign: "center" }}>
            {tr("pairing.startNew")}
            <span style={{ display: "block", color: t.textSecondary, fontSize: sz(11), fontStyle: "italic", marginTop: 2 }}>
              {tr("myMenu.newPairingHint")}
            </span>
          </button>
        )}

        {/* Email capture — the take-home action, and now the only one. This
            previously lived inside the QR modal, which became unreachable when
            the QR button was removed from the UI, so nothing ever rendered it.
            Gated on favorites: offering to email an empty menu is noise. */}
        {favorites.length > 0 && (
        <div style={{ borderTop: `0.5px solid ${t.borderSubtle}`, paddingTop: 18, marginTop: 20, textAlign: "left" }}>
              {emailState === "sent" ? (
                <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
                  <div style={{ color: t.accent, fontSize: sz(13), marginBottom: 4 }}>{sentCount > 1 ? tr("myMenu.email.sentMany", { count: sentCount }) : tr("myMenu.email.sentOne")}</div>
                  <div style={{ color: t.textSecondary, fontSize: 11, lineHeight: 1.5 }}>
                    {sentCount > 1 ? tr("myMenu.email.sentManyBody") : sentWithList ? tr("myMenu.email.sentOneBodyList") : tr("myMenu.email.sentOneBody")}
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ color: t.textPrimary, fontSize: sz(13), marginBottom: 4 }}>{tr("myMenu.email.title")}</div>
                  <div style={{ color: t.textSecondary, fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
                    {tr("myMenu.email.body")}
                  </div>
                  <input
                    type="email" multiple inputMode="email" autoComplete="email"
                    autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    placeholder={tr("myMenu.email.placeholder")}
                    aria-describedby="menu-email-hint"
                    value={emailInput}
                    onChange={e => { setEmailInput(e.target.value); if (emailState === "error") setEmailState("idle"); }}
                    style={{
                      width: "100%", boxSizing: "border-box", background: t.white08,
                      border: `0.5px solid ${emailState === "error" ? t.accent : t.borderSubtle}`,
                      borderRadius: 8, padding: "11px 12px", color: t.textPrimary,
                      fontSize: 16, fontFamily: t.fontSerif, outline: "none", marginBottom: 12,
                    }}
                  />
                  {/* Unchecked. See the note on joinList — shared sending domain
                      means one tenant's spam complaints degrade everyone's inbox
                      placement, and the transactional email is the product.
                      Hidden entirely where the tenant is not cleared to collect;
                      the server re-checks, so this is presentation only. */}
                  <div id="menu-email-hint" style={{ color: t.textSecondary, fontSize: 10.5, lineHeight: 1.4, marginTop: -6, marginBottom: 12 }}>
                    {tr("myMenu.email.hint")}
                  </div>
                  {/* The list checkbox is one person's consent, so it only appears for a
                      single address. With several, nobody is added to the list. */}
                  {listOptIn && parseEmailList(emailInput).length <= 1 && (
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", marginBottom: 14 }}>
                    <input
                      type="checkbox" checked={joinList}
                      onChange={e => setJoinList(e.target.checked)}
                      style={{ marginTop: 2, width: 16, height: 16, accentColor: t.accent, flexShrink: 0, cursor: "pointer" }}
                    />
                    <span style={{ color: t.textSecondary, fontSize: 11, lineHeight: 1.45 }}>
                      {consentText(venueName)}
                    </span>
                  </label>
                  )}
                  <button
                    onClick={handleSendEmail}
                    disabled={emailState === "sending"}
                    style={{
                      width: "100%", background: emailState === "sending" ? t.white08 : t.accent,
                      color: emailState === "sending" ? t.textSecondary : t.accentText,
                      border: "none", padding: "11px 0", borderRadius: 8,
                      cursor: emailState === "sending" ? "default" : "pointer",
                      fontFamily: t.fontSerif, fontSize: 13, fontWeight: 600,
                    }}>
                    {emailState === "sending" ? tr("myMenu.email.sending") : tr("myMenu.email.send")}
                  </button>
                  {emailState === "error" && (
                    <div style={{ color: t.accent, fontSize: 11, marginTop: 8, textAlign: "center" }}>
                      {emailError || tr("myMenu.email.failed")}
                    </div>
                  )}
                  {/* At the point of collection, which is the disclosure a guest
                      actually reads. A modal rather than a route so they do not
                      lose their picks to navigate away and back. */}
                  <div style={{ textAlign: "center", marginTop: 10 }}>
                    <button onClick={() => setShowPrivacy(true)} style={{ background: "none", border: "none", padding: 0, color: t.textSecondary, fontSize: 10, textDecoration: "underline", cursor: "pointer", fontFamily: t.fontSerif }}>
                      {tr("myMenu.email.privacyLink")}
                    </button>
                  </div>
                </>
              )}
        </div>
        )}
      </div>
      <LabelModal wine={zoomedLabel} onClose={() => setZoomedLabel(null)} />
      {showPrivacy && <PrivacyNotice venueName={venueName} listOptIn={listOptIn} onClose={() => setShowPrivacy(false)} />}
    </div>
    </div>
  );
}

// ─── Sommelier Chat ───────────────────────────────────────────────────────────
// A slide-up chat drawer that opens from any pairing entry point.
// The backend system prompt hard-boxes the AI to F&B at Appalachia Kitchen only.
//
// Props:
//   isOpen       — controls visibility
//   onClose      — called when user taps X or backdrop
//   contextItem  — { name, type } of the wine/beer/pour/food that opened the chat
//                  used to personalise the opening message; null for generic open

// ─── Chat "working on it" indicator ──────────────────────────────────────────
// A guest reported the "• • •" bubble looked hung on a slow answer. It is now
// the SyncBev logo glass, swirling, with no words.
//
// History, so nobody re-adds what was tried and dropped:
//   - Timed status lines ("Thinking that over…", "Gathering my thoughts…") were
//     tried and removed in favour of the image alone. Any wording has to fit
//     every question - "Checking what we have…" read as an inventory search on
//     "what does tannin mean" - so if text ever comes back, keep it neutral.
//   - Real server-side progress ("Found 3 Australian reds…") was tried and
//     dropped: its constraints are read from the whole conversation, so a guest
//     who asked for Australian reds and then about the salmon would still see
//     "Australian reds" and feel unheard.
//
// Past this, stop waiting and offer a retry. Covers both chat calls (lean, then
// the fallback) together, measured from the moment the guest hit send. Typical
// turns land around 15s. Aborting only stops the tablet waiting - the function
// may still finish server-side, which on a public-demo tenant can use a turn.
const CHAT_TIMEOUT_MS = 45000;

// The SyncBev logo glass, traced from syncbevverticallogo.png (glass only, no
// wordmark): red left side, chartreuse right side, stem and foot, red wine.
// The glass rocks on its foot and the wine leans the other way with a slow
// rolling surface, so it reads as liquid lagging behind the glass. Pure SVG +
// CSS, no image files, so nothing extra loads on the tablets.
//
// The sides, stem and foot are the traced shapes. The wine's BOTTOM is traced
// too (it is the clip below); only its surface is a drawn wave, since it has
// to move. The stem is black in the logo but uses the theme's primary text
// colour here, because black would vanish on a dark theme. The same glass,
// static and with a black stem, is kept separately as syncbev-glass.svg.
const LOGO_RED = "#B41C15";
const LOGO_GREEN = "#B0B618";
const LOGO_GLASS = {
  viewBox: "272 63 260 456",
  wineClip: "M384.3 313.5 C364.5 308.1 340.3 286.3 328.8 263.5 C327.1 260.2 325.1 256.8 324.3 255.8 C318.9 249.7 316.1 213.1 319.5 191.4 C323.4 166.1 331.3 142.7 344.1 118.9 C354.0 100.4 352.6 101.0 382.8 101.0 L401.2 101.0 L409.1 93.3 C413.5 89.1 417.0 85.1 417.0 84.3 C417.0 83.3 420.6 83.0 434.5 83.0 C449.5 83.0 452.0 83.2 452.0 84.5 C452.0 85.4 453.3 88.6 454.9 91.8 C462.6 107.1 473.4 137.5 478.9 159.5 C489.5 201.5 489.3 240.5 478.3 262.5 C460.3 298.4 416.5 322.1 384.3 313.5 Z",
  wave: "M-445 218 C-408.6 218 -381.4 244 -345 244 C-308.6 244 -281.4 218 -245 218 C-208.6 218 -181.4 244 -145 244 C-108.6 244 -81.4 218 -45 218 C-8.6 218 18.6 244 55 244 C91.4 244 118.6 218 155 218 C191.4 218 218.6 244 255 244 C291.4 244 318.6 218 355 218 C391.4 218 418.6 244 455 244 C491.4 244 518.6 218 555 218 C591.4 218 618.6 244 655 244 C691.4 244 718.6 218 755 218 C791.4 218 818.6 244 855 244 C891.4 244 918.6 218 955 218 C991.4 218 1018.6 244 1055 244 L1055 420 L-445 420 Z",
  redSide: "M330.3 318.2 C308.8 301.1 291.1 270.4 285.9 241.0 C284.2 231.5 284.2 201.7 285.9 189.0 C289.9 160.0 304.1 122.0 319.9 98.0 C333.8 76.9 338.5 75.0 378.1 75.0 C400.4 75.0 400.7 75.0 402.8 77.3 C405.7 80.4 405.7 85.4 402.8 87.4 C401.0 88.7 397.2 89.0 382.4 89.0 C349.8 89.0 347.4 90.4 332.0 118.9 C313.3 153.6 305.3 185.5 306.3 220.9 C307.2 252.7 316.3 274.8 337.2 296.3 C346.3 305.6 346.3 305.8 340.0 316.8 C336.4 323.0 336.5 323.0 330.3 318.2 Z",
  greenSide: "M415.1 343.4 C410.7 336.2 410.1 334.6 411.2 333.3 C411.9 332.4 419.7 328.2 428.6 323.8 C467.9 304.7 488.1 281.1 496.2 244.9 C504.3 208.5 492.8 147.1 467.8 93.8 C463.1 83.9 463.2 81.0 468.1 78.4 C476.9 73.9 483.9 81.9 495.8 110.0 C523.9 176.5 528.0 234.5 507.6 277.6 C494.1 306.0 476.3 323.2 440.8 342.1 C418.6 353.8 421.2 353.7 415.1 343.4 Z",
  stem: "M351.5 507.2 C322.8 507.0 319.4 506.8 318.2 505.3 C316.2 502.6 316.8 497.4 319.4 494.4 C321.7 491.6 321.7 491.6 345.6 489.9 C358.7 488.9 371.3 487.6 373.5 486.9 C378.6 485.5 385.4 478.7 387.1 473.5 C388.8 468.3 389.6 385.0 388.1 375.1 C385.9 360.8 376.6 348.3 361.5 339.3 C351.5 333.3 339.2 325.1 338.5 324.0 C338.1 323.4 339.4 319.8 341.4 316.0 C343.4 312.3 345.0 308.5 345.0 307.7 C345.0 304.5 347.0 305.2 357.7 312.3 C363.7 316.2 373.4 322.2 379.1 325.5 C395.9 335.1 407.2 348.1 412.1 363.5 C413.0 366.4 413.5 381.2 413.9 419.0 C414.6 475.3 414.5 474.7 420.7 481.2 C426.4 487.2 431.9 488.4 462.7 490.4 C483.6 491.7 485.9 492.5 487.4 498.6 C488.2 501.5 485.7 506.6 483.3 507.4 C481.6 507.9 428.5 507.9 351.5 507.2 Z",
};
let chatGlassSeq = 0;
function SwirlingGlass({ stemColor, size = 48, still = false }) {
  // Unique per instance: two glasses on screen must not share a clipPath id.
  const [clipId] = useState(() => `ssGlassBowl${++chatGlassSeq}`);
  // The thin same-colour stroke closes hairline gaps where traced shapes meet.
  return (
    <svg viewBox={LOGO_GLASS.viewBox} width={Math.round(size * 260 / 456)} height={size} aria-hidden="true" style={{ flexShrink: 0, overflow: "visible" }}>
      <defs><clipPath id={clipId}><path d={LOGO_GLASS.wineClip} /></clipPath></defs>
      {/* still = the logo at rest (the demo frame's header): same shapes, no
          animation classes, so it works without the chat's keyframes. */}
      <g className={still ? undefined : "ss-glass-swirl"}>
        <g clipPath={`url(#${clipId})`}>
          <g className={still ? undefined : "ss-glass-slosh"}>
            <path className={still ? undefined : "ss-glass-wave"} fill={LOGO_RED} d={LOGO_GLASS.wave} />
          </g>
        </g>
        <path fill={LOGO_RED} stroke={LOGO_RED} strokeWidth="2" d={LOGO_GLASS.redSide} />
        <path fill={LOGO_GREEN} stroke={LOGO_GREEN} strokeWidth="2" d={LOGO_GLASS.greenSide} />
        <path fill={stemColor} stroke={stemColor} strokeWidth="2" d={LOGO_GLASS.stem} />
      </g>
    </svg>
  );
}

// Shown beside the swirling glass while a reply is on its way. A reply usually
// lands in a few seconds but can take 15-20 when the sommelier is weighing a
// long list, and a silent animation that long reads as frozen. The line changes
// as time passes, so a guest can see the app is still working; the last one
// stays up until the reply arrives. Timers live in this component, so each new
// question starts again from the first line.
const CHAT_STATUS_STEPS = [
  { at: 0,     textKey: "loading.chat.thinking" },
  { at: 4000,  textKey: "loading.chat.looking" },
  { at: 9000,  textKey: "loading.chat.working" },
  { at: 18000, textKey: "loading.chat.almost" },
];

function ChatStatusIndicator() {
  const t = useTheme();
  const textScale = useTextScale();
  const [step, setStep] = useState(0);
  useEffect(() => {
    const timers = CHAT_STATUS_STEPS.slice(1).map((s, i) =>
      setTimeout(() => setStep(i + 1), s.at));
    return () => timers.forEach(clearTimeout);
  }, []);
  return (
    <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }}>
      <style>{`
        .ss-glass-swirl { transform-box: view-box; transform-origin: 402px 501px; animation: ssGlassSwirl 1.6s ease-in-out infinite alternate; }
        .ss-glass-slosh { transform-box: view-box; transform-origin: 402px 250px; animation: ssGlassSlosh 1.6s ease-in-out infinite alternate; }
        .ss-glass-wave  { animation: ssGlassWave 2.4s linear infinite; }
        .ss-status-text { animation: ssStatusIn 0.4s ease-out; }
        @keyframes ssGlassSwirl { from { transform: rotate(-6deg); } to { transform: rotate(6deg); } }
        @keyframes ssGlassSlosh { from { transform: rotate(12deg); } to { transform: rotate(-12deg); } }
        @keyframes ssGlassWave  { from { transform: translateX(0); } to { transform: translateX(200px); } }
        @keyframes ssStatusIn   { from { opacity: 0; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .ss-glass-swirl, .ss-glass-slosh, .ss-glass-wave, .ss-status-text { animation: none; } }
      `}</style>
      {/* No bubble around it: the glass sits on the chat background by itself.
          Indented in from the edge where replies start, but still on the left -
          it stands in for the reply that is coming, so it sits where that will.
          role="status" lets a screen reader announce each new line. */}
      <div role="status" style={{ padding: "6px 0 6px 28px", display: "flex", alignItems: "center", gap: 12 }}>
        <SwirlingGlass stemColor={t.textPrimary} size={60} />
        {/* key restarts the fade so each new line visibly arrives. */}
        <div key={step} className="ss-status-text" style={{ color: t.textSecondary, fontSize: Math.round(12.5 * textScale * 2) / 2, fontStyle: "italic", lineHeight: 1.4, maxWidth: 220 }}>
          {tr(CHAT_STATUS_STEPS[step].textKey)}
        </div>
      </div>
    </div>
  );
}

// Chat card type -> the favoriteType the drink list screens store. The chat
// calls a pour a "spirit"; App's Premium Pours screen stores "pour".
const CHAT_DRINK_FAVORITE_TYPE = { spirit: "pour", cocktail: "cocktail", beer: "beer", nab: "nab" };

function SommelierChat({ isOpen, onClose, contextItem, selectedFoods = [], entry = null, shownWines = [], favorites = [], onToggleFavorite, onShowShortlist, tabletLocation = null, locationNames = { bar: "Bar", dining: "Dining Room" } }) {
  const t = useTheme();
  // Guest text size - see TEXT_SCALES. Above the !isOpen early return so the
  // hook order never changes. Scales the messages, suggested wines and hints;
  // the header, Add buttons and the input (16px, iOS zoom) stay fixed.
  const textScale = useTextScale();
  const sz = n => Math.round(n * textScale * 2) / 2;
  // Out of free demo turns: send the visitor straight to the wall instead of
  // opening a chat that can only fail after they have typed into it.
  const demoBlocked = isOpen && demoTurnsExhausted();
  useEffect(() => {
    if (demoBlocked) { showDemoWall(); onClose(); }
  }, [demoBlocked]);
  const restaurantName = roomName(tabletLocation === "dining" ? "dining" : "bar", locationNames);
  function buildOpener() {
    if (selectedFoods.length > 0) {
      const names = selectedFoods.map(f => f.name).join(", ");
      return tr("chat.openerFoods", { names });
    }
    if (contextItem) {
      // Says what the chat can do from here. The old opener asked about
      // "preferences", and a guest answering "prefer meaty fish" meant a dish to
      // go with this bottle - the opener should invite exactly that.
      return tr("chat.openerItem", { name: contextItem.name });
    }
    return tr("chat.opener");
  }

  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState("");
  const [sending, setSending]         = useState(false);
  const [addedThisSession, setAddedThisSession] = useState([]);
  // Which suggestion cards have had their tasting note expanded. Keyed by
  // message index + suggestion index, so two cards for the same wine in different
  // turns expand independently.
  const [expandedSug, setExpandedSug] = useState({});
  const [zoomedLabel, setZoomedLabel] = useState(null);
  const bottomRef                     = useRef(null);
  const inputRef                      = useRef(null);
  // Keyed by message index, so a new assistant reply can be scrolled to its
  // own top rather than the bottom of the whole thread (see the scroll
  // effect below).
  const messageRefs                   = useRef({});
  const messagesListRef                = useRef(null);
  const [viewportH, setViewportH]     = useState(null);
  // The in-flight chat request, so it can be timed out, and dropped if the
  // drawer closes or re-seeds before it answers. Without this a late reply
  // would land in the next guest's fresh conversation.
  const pendingRef                    = useRef(null);
  function cancelPending() {
    const c = pendingRef.current;
    pendingRef.current = null;
    if (c) c.abort();
    setSending(false);
  }
  useEffect(() => { if (!isOpen) cancelPending(); }, [isOpen]);
  useEffect(() => () => { const c = pendingRef.current; pendingRef.current = null; if (c) c.abort(); }, []);

  // maxHeight: "80vh" reflects the LAYOUT viewport on most mobile browsers,
  // which does not shrink when the on-screen keyboard opens - so the drawer
  // stayed sized for the full screen while the actually-visible area above
  // the keyboard shrank, pushing the input bar and send button below what
  // the guest could see. window.visualViewport tracks the real visible
  // area and does update on keyboard open/close; falls back to the CSS
  // vh-based sizing wherever visualViewport isn't supported.
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined' || !window.visualViewport) return;
    const vv = window.visualViewport;
    const onResize = () => setViewportH(vv.height);
    vv.addEventListener('resize', onResize);
    onResize();
    return () => vv.removeEventListener('resize', onResize);
  }, [isOpen]);
  // Only override the resting 80vh look once the keyboard has actually
  // shrunk the visible area by a meaningful amount - otherwise the normal
  // bottom-sheet proportions (backdrop visible above the drawer) stay as
  // designed when no keyboard is open.
  const keyboardOpen = viewportH != null && typeof window !== 'undefined' && viewportH < window.innerHeight * 0.85;
  const drawerMaxHeight = keyboardOpen ? `${Math.max(viewportH - 8, 200)}px` : "80vh";

  // Reset session state when drawer opens
  useEffect(() => {
    if (isOpen) {
      cancelPending();
      setMessages([{ role: "assistant", text: buildOpener(), suggestions: [] }]);
      setInput("");
      setAddedThisSession([]);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, contextItem?.name, selectedFoods.map(f=>f.id).join(',')]); // intentional: re-seed when item/foods change

  useEffect(() => {
    if (!isOpen) return;
    const lastIdx = messages.length - 1;
    const last = messages[lastIdx];
    // A fresh assistant reply (not the opener at index 0, and not mid-send,
    // where the last message is still the guest's own) scrolls to ITS OWN
    // top - a long pairing writeup previously landed scrolled to its own
    // end, so the guest had to scroll back up to read it from the start.
    // Anything else (the guest's own message just sent, the "..." typing
    // indicator) keeps the old bottom-anchor behavior, since seeing that
    // land at the bottom is exactly the reassurance wanted there.
    //
    // Using getBoundingClientRect deltas rather than
    // scrollIntoView({block:'start'}) - the latter reportedly still landed
    // partway into the message (its bottom, not its top) on a real device,
    // likely because "start" alignment through a nested overflow:auto
    // container doesn't resolve consistently on every mobile browser. This
    // computes the exact scroll offset directly, independent of any
    // offsetParent/ancestor-chain ambiguity.
    const container = messagesListRef.current;
    const target = last && last.role === "assistant" && lastIdx > 0 ? messageRefs.current[lastIdx] : null;
    if (container && target) {
      const targetRect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const delta = targetRect.top - containerRect.top;
      container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
    } else {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  async function handleSend() {
    const text = input.trim();
    if (!text || sending) return;
    // Already out of free turns (for example the wall was dismissed with the
    // chat still open): show it again rather than send a request that can only
    // fail. The typed text stays in the box.
    if (demoTurnsExhausted()) { showDemoWall(); return; }
    const newMessages = [...messages, { role: "user", text, suggestions: [] }];
    setMessages(newMessages);
    setInput("");
    requestReply(newMessages);
  }

  // "Try again" on a timed-out turn. Drops the timeout notice and resends the
  // same conversation, so the guest does not have to retype their question.
  function handleRetry() {
    if (sending) return;
    if (demoTurnsExhausted()) { showDemoWall(); return; }
    const thread = messages.filter(m => !m.timedOut);
    setMessages(thread);
    requestReply(thread);
  }

  // What goes to the server. Timeout notices are UI only and never sent. If a
  // guest typed something new instead of tapping "Try again", the unanswered
  // question and the new one would sit back to back as two user turns, so
  // they are merged into one.
  function toApiMessages(thread) {
    const out = [];
    for (const m of thread) {
      if (m.timedOut) continue;
      const prev = out[out.length - 1];
      if (prev && prev.role === "user" && m.role === "user") prev.content = `${prev.content}\n\n${m.text}`;
      else out.push({ role: m.role, content: m.text });
    }
    return out;
  }

  async function requestReply(thread) {
    const controller = new AbortController();
    pendingRef.current = controller;
    const timer = setTimeout(() => controller.abort(), CHAT_TIMEOUT_MS);
    // Superseded = the drawer closed or re-seeded while this was in flight.
    // Its result, success or failure, belongs to a conversation that is gone.
    const superseded = () => pendingRef.current !== controller;
    setSending(true);
    try {
      // Lean chat: the model gets a ~200-token inventory summary and a search
      // tool rather than the whole enriched list, and looks up only the shelf it
      // needs. Roughly $0.01 a turn against ~$0.086 for the full-list path.
      // Falls back to the previous endpoint on failure, so a bad turn degrades
      // instead of leaving a guest with nothing.
      const payload = {
        messages: toApiMessages(thread),
        // The language the guest is reading - see guestLanguage() on the server.
        lang: getRequestedLanguage(),
        contextItem: contextItem || null,
        selectedFoods: selectedFoods.length > 0 ? selectedFoods.map(f => ({ id: f.id, name: f.name, courseRole: f.courseRole })) : [],
        // The wines already on screen. Without these the chat has no idea what was
        // recommended, and a guest saying "I prefer old world reds" is talking
        // about wines the model has never been told about.
        seedWineIds: [
          // A wine's detail panel passes `wineId`, not `id` - reading only `id`
          // meant the chat was never told which bottle the guest was looking at.
          ...(contextItem && (contextItem.wineId || contextItem.id) ? [contextItem.wineId || contextItem.id] : []),
          ...shownWines.map(w => w && w.id).filter(Boolean),
        ].filter((v, i, a) => a.indexOf(v) === i).slice(0, 6),
        location: tabletLocation || undefined,
        // Where the drawer was opened from. The server counts "pairing_choice" on
        // the first message as the guest choosing Ask the Sommelier.
        entry: entry || undefined,
        // The cards under the last reply, type and id only. The server uses them
        // to keep a spirits/cocktail/beer conversation on that topic when the
        // guest's follow-up names no drink, and to answer "yes" with what was
        // just recommended rather than the pairing-screen wines.
        lastSuggestions: (() => {
          for (let i = thread.length - 1; i >= 0; i--) {
            const m = thread[i];
            if (m.role === "assistant" && Array.isArray(m.suggestions) && m.suggestions.length > 0) {
              return m.suggestions.filter(x => x && x.id && x.type).slice(0, 6).map(x => ({ type: x.type, id: x.id }));
            }
          }
          return [];
        })(),
      };
      let data = null;
      try {
        const res = await rfetch(CHAT_LEAN_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload), signal: controller.signal,
        });
        const lean = await res.json();
        if (res.ok && lean.reply) data = lean;
      } catch (e) {
        // A timeout must not fall through to the fallback - that would start a
        // second, slower call after the guest has already waited 45s.
        if (controller.signal.aborted) throw e;
      }
      if (!data) {
        const res2 = await rfetch(CHAT_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload), signal: controller.signal,
        });
        data = await res2.json();
      }
      if (superseded()) return;
      setMessages(prev => [...prev, {
        role: "assistant",
        text: keyedText(data.replyKey, data.reply) || tr("chat.noReply"),
        suggestions: data.suggestions || [],
      }]);
    } catch {
      if (superseded()) return;
      if (controller.signal.aborted) {
        setMessages(prev => [...prev, { role: "assistant", text: tr("chat.timedOut"), suggestions: [], timedOut: true }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", text: tr("chat.failed"), suggestions: [] }]);
      }
    } finally {
      clearTimeout(timer);
      if (!superseded()) { pendingRef.current = null; setSending(false); }
    }
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  function handleAddToMenu(suggestion, courseRole) {
    if (!onToggleFavorite) return;
    const alreadyAdded = favorites.some(f => f.id === suggestion.id);
    // The chat's own reason for this card, when the turn was a pairing ("Paired
    // with Seared Diver Scallops: its bright acidity cuts the brown butter"). It
    // makes a chat pairing arrive in My Menu explained, the way an instant pairing
    // does. Absent when the card was not recommended WITH anything.
    const pairReason = suggestion.pairReason && suggestion.pairedWith
      ? tr("chat.pairedWith", { dish: suggestion.pairedWith, reason: suggestion.pairReason }) : null;
    if (suggestion.type === "wine") {
      // Filed under the course of the dish it was paired with, when that dish is
      // already on My Menu - the same place a pairing-screen wine lands.
      // English on purpose, not tr(): courseLabel is a grouping key that My Menu
      // matches against its own courseLabels, and it is saved with an emailed
      // menu. Translate where it is SHOWN, never where it is stored.
      const roleLabels = { first: "First Course", main: "Main Course", dessert: "Dessert" };
      const pairedDish = pairReason ? favorites.find(f => f.favoriteType === "food" && String(f.name || "").toLowerCase() === String(suggestion.pairedWith).toLowerCase()) : null;
      onToggleFavorite({ id: suggestion.id, name: suggestion.name, varietal: suggestion.varietal || null, region: suggestion.region || null, glassPrice: suggestion.glassPrice || null, bottlePrice: suggestion.bottlePrice || null, imageUrl: suggestion.imageUrl || null,
        // My Menu shows description, else reason. With a pairing reason the reason
        // is the useful line, as it is for an instant pairing.
        description: pairReason ? null : (suggestion.description || null),
        reason: pairReason, courseLabel: pairedDish ? (roleLabels[pairedDish.courseRole] || null) : null,
        fromPairing: !!pairReason }, "wine");
    } else if (suggestion.type === "food") {
      onToggleFavorite({ id: suggestion.id, name: suggestion.name, price: suggestion.price || null, course: suggestion.course || null, description: suggestion.description || null, reason: pairReason, courseRole: courseRole || "main" }, "food");
    } else if (CHAT_DRINK_FAVORITE_TYPE[suggestion.type]) {
      // ⚠️ Focus group, Oct 2026: spirits, cocktails, beer and N/A drinks fell
      // through this function untouched. The guest tapped Add, the drink went into
      // addedThisSession, the close modal congratulated them on it - and My Menu
      // never had it, because onToggleFavorite was only ever called for wine and
      // food. Stored under the SAME favoriteType the list screens use ("pour", not
      // the chat's "spirit") so a drink starred in chat and on Premium Pours is
      // one entry, and My Menu groups and labels it like any other drink.
      onToggleFavorite({ id: suggestion.id, name: suggestion.name, price: suggestion.price || null, description: suggestion.description || null, imageUrl: suggestion.imageUrl || null, reason: pairReason }, CHAT_DRINK_FAVORITE_TYPE[suggestion.type]);
    } else {
      // An unknown card type cannot be stored, so it must not be counted as
      // added either - that mismatch is exactly what hid the bug above.
      return;
    }
    // Track what was added this session (only additions, not removals)
    if (!alreadyAdded) {
      setAddedThisSession(prev => prev.some(a => a.id === suggestion.id) ? prev : [...prev, suggestion]);
    }
  }

  // When closing, fire toast if anything was added
  const [winePrompt, setWinePrompt] = useState(false);

  function handleClose() {
    // If chat opened from a wine detail page and guest hasn't added that wine yet, prompt them
    if (contextItem && contextItem.type && contextItem.type !== "food" && contextItem.wineId) {
      const alreadyAdded = favorites.some(f => f.id === contextItem.wineId);
      if (!alreadyAdded) {
        setWinePrompt(true);
        return;
      }
    }
    onClose(addedThisSession.length > 0 ? addedThisSession : null);
  }

  function handleWinePromptAdd() {
    if (contextItem?.wineId && contextItem?.wineFull) {
      onToggleFavorite({ ...contextItem.wineFull, fromPairing: false }, "wine");
      setAddedThisSession(prev => [...prev, { type: "wine", id: contextItem.wineId, name: contextItem.name }]);
    }
    setWinePrompt(false);
    onClose(addedThisSession.length > 0 ? addedThisSession : [{ type: "wine", id: contextItem?.wineId, name: contextItem?.name }]);
  }

  function handleWinePromptSkip() {
    setWinePrompt(false);
    onClose(addedThisSession.length > 0 ? addedThisSession : null);
  }

  if (!isOpen || demoBlocked) return null;

  const hasAdded = addedThisSession.length > 0;
  let lastSuggestionMsgIndex = -1;
  messages.forEach((m, i) => { if (m.role === "assistant" && m.suggestions?.length > 0) lastSuggestionMsgIndex = i; });

  return <>
    {winePrompt && contextItem && (
      <div style={{ position: "fixed", inset: 0, zIndex: Z.chatPrompt, background: t.bgOverlay, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: t.bgSurface, borderRadius: 14, padding: "24px 20px", maxWidth: 340, width: "100%", fontFamily: t.fontSerif, textAlign: "center" }}>
          <div style={{ color: t.accent, fontSize: 10, letterSpacing: "3px", textTransform: "uppercase", marginBottom: 10 }}>{tr("chat.winePrompt.title")}</div>
          <div style={{ color: t.textPrimary, fontSize: sz(15), marginBottom: 6 }}>{contextItem.name}</div>
          <div style={{ color: t.textSecondary, fontSize: sz(12), lineHeight: 1.6, marginBottom: 20 }}>{tr("chat.winePrompt.body")}</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handleWinePromptSkip} style={{ flex: 1, background: "transparent", border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, padding: "10px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif }}>{tr("chat.winePrompt.skip")}</button>
            <button onClick={handleWinePromptAdd} style={{ flex: 1, background: t.accent, border: "none", color: t.accentText, padding: "10px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif, fontWeight: 600 }}>{tr("common.addToMyMenu")}</button>
          </div>
        </div>
      </div>
    )}
    <div onClick={handleClose} style={{ position: "fixed", top: 0, left: 0, right: 0, height: keyboardOpen ? `${viewportH}px` : "100%", zIndex: Z.chat, background: t.bgOverlay, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 680, background: t.bgBase, borderRadius: "16px 16px 0 0", boxShadow: `0 -8px 40px ${t.shadow}`, display: "flex", flexDirection: "column", maxHeight: drawerMaxHeight, fontFamily: t.fontSerif }}>

        {/* Header — X becomes "Done ✓" once something has been added */}
        <div style={{ background: t.bgSurface, borderRadius: "16px 16px 0 0", padding: "14px 18px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "3px", textTransform: "uppercase", marginBottom: 2 }}>{tr("chat.title")}</div>
            <div style={{ color: t.textPrimary, fontSize: 13 }}>{restaurantName}</div>
          </div>
          <TextSizeButton />
          <button
            onClick={handleClose}
            style={{ background: hasAdded ? t.accent : t.accentDim, border: hasAdded ? "none" : `0.5px solid ${t.accentBorder}`, color: hasAdded ? t.accentText : t.accent, height: 32, borderRadius: hasAdded ? 8 : "50%", width: hasAdded ? "auto" : 32, padding: hasAdded ? "0 12px" : 0, cursor: "pointer", fontSize: hasAdded ? 12 : 18, fontFamily: t.fontSerif, fontWeight: hasAdded ? 600 : 400, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s", whiteSpace: "nowrap" }}
          >{hasAdded ? tr("chat.done") : "×"}</button>
        </div>

        {/* Message list */}
        <div ref={messagesListRef} style={{ flex: 1, overflowY: "auto", padding: "16px 16px 8px", background: t.bgBase }}>
          {messages.map((m, i) => (
            <div key={i} ref={el => { messageRefs.current[i] = el; }} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={{ maxWidth: "85%", background: m.role === "user" ? t.accentDim : t.bgCard, color: t.textPrimary, border: m.role === "user" ? `0.5px solid ${t.accentBorder}` : `0.5px solid ${t.borderMid}`, borderRadius: m.role === "user" ? "14px 14px 2px 14px" : "14px 14px 14px 2px", padding: "10px 14px", fontSize: sz(13), lineHeight: 1.6 }}>
                  {m.role === "assistant" ? renderChatText(m.text) : m.text}
                </div>
              </div>

              {/* Retry - only on the latest message, and only while idle, so an
                  old notice further up the thread can't resend a stale turn. */}
              {m.timedOut && i === messages.length - 1 && !sending && (
                <div style={{ marginTop: 8, paddingLeft: 4 }}>
                  <button onClick={handleRetry} style={{ background: t.accent, border: "none", color: t.accentText, fontSize: 12, padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontFamily: t.fontSerif, fontWeight: 600 }}>{tr("chat.tryAgain")}</button>
                </div>
              )}

              {/* Suggestion chips */}
              {m.role === "assistant" && m.suggestions && m.suggestions.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6, paddingLeft: 4 }}>
                  {m.suggestions.map((s, si) => {
                    const isAdded = favorites.some(f => f.id === s.id);
                    const sub = s.type === "wine" ? [s.varietal, s.region].filter(Boolean).join(" · ") : s.course;
                    // Gated on `s.type === "wine"` until now, so a cocktail,
                    // beer, spirit or non-alcoholic card could never show a label
                    // however good the data was. One gate killed three things at
                    // once: the thumbnail, the zoom handler and the "Tap label to
                    // enlarge" line all key off this.
                    const hasImage = !!s.imageUrl;
                    return (
                      <div key={si} style={{ display: "flex", alignItems: "center", gap: 10, background: isAdded ? t.successDim : t.bgSurface, border: `0.5px solid ${isAdded ? t.success : t.accentBorder}`, borderRadius: 10, padding: "8px 12px" }}>
                        {/* Label thumbnail or emoji fallback */}
                        <div
                          onClick={hasImage ? () => setZoomedLabel(s) : undefined}
                          style={{ width: 36, height: 50, borderRadius: 3, background: hasImage ? t.labelBackdrop : t.white06, border: `0.5px solid ${hasImage ? t.accent : t.borderMid}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, overflow: "hidden", cursor: hasImage ? "zoom-in" : "default" }}
                        >
                          {hasImage
                            ? <img src={s.imageUrl} alt={s.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : SUGGESTION_ICONS[s.type] || "🍽"}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: t.textPrimary, fontSize: sz(12), fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                          <OrderAs item={s} size={sz(10)} />
                          {sub && <div style={{ color: t.textSecondary, fontSize: sz(10), letterSpacing: "0.5px" }}>{sub}</div>}
                          {/* Price was never rendered here, though the data has
                              always been on the suggestion. A guest reading a
                              recommendation cannot act on it without knowing what
                              it costs. */}
                          {s.type === "wine" && (s.glassPrice || s.bottlePrice) && (
                            <div style={{ display: "flex", gap: 10, marginTop: 3 }}>
                              {s.glassPrice && <span style={{ color: t.textPrimary, fontSize: sz(11.5) }}>{formatPrice(s.glassPrice)} <span style={{ color: t.accent, fontSize: sz(11), letterSpacing: "0.3px" }}>{tr("common.glass")}</span></span>}
                              {s.bottlePrice && <span style={{ color: t.textPrimary, fontSize: sz(11.5) }}>{formatPrice(s.bottlePrice)} <span style={{ color: t.accent, fontSize: sz(11), letterSpacing: "0.3px" }}>{tr("common.bottle")}{s.bottleSize ? ` · ${s.bottleSize}` : ''}</span></span>}
                            </div>
                          )}
                          {/* Drinks carry a single `price`, not glass/bottle, so
                              this is a separate branch rather than a widened
                              condition - the labels differ. A guest shown a whisky
                              with no price cannot act on the recommendation. */}
                          {s.type !== "wine" && s.price && (
                            <div style={{ marginTop: 3 }}>
                              <span style={{ color: t.textPrimary, fontSize: sz(11.5) }}>{formatPrice(s.price)}</span>
                            </div>
                          )}
                          {s.description && (() => {
                            const key = `${i}-${si}`;
                            const open = !!expandedSug[key];
                            // Clamped at two lines by default so a card stays
                            // scannable, but the note has to be reachable - it is
                            // the manager-approved copy and the reason the wine is
                            // being suggested. textSecondary, not textDim: textDim
                            // measures 1.8-2.6:1 on bgSurface across the schemes and
                            // is for decoration only. textSecondary is 5.0:1 or
                            // better on every one.
                            return (
                              <div onClick={() => setExpandedSug(p => ({ ...p, [key]: !open }))}
                                style={{ color: t.textSecondary, fontSize: sz(11), lineHeight: 1.4, marginTop: 3, cursor: "pointer",
                                  ...(open ? {} : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }) }}>
                                {s.description}
                                <span style={{ color: t.accent, marginLeft: 4, whiteSpace: "nowrap" }}>{open ? tr("chat.less") : tr("chat.more")}</span>
                              </div>
                            );
                          })()}
                          {hasImage && <div style={{ color: t.accent, fontSize: 9, letterSpacing: "0.5px", marginTop: 2 }}>{tr("chat.tapLabel")}</div>}
                        </div>
                        {s.type === "food" && !isAdded ? (
                          // For food: show 1ST / MAIN role buttons (skip for Dessert/Entrees which lock to one role)
                          (() => {
                            const isDessert = courseKind(s.course) === "dessert";
                            const isEntree  = courseKind(s.course) === "entree";
                            if (isDessert) return (
                              <button onClick={() => handleAddToMenu(s, "dessert")} style={{ background: t.bgSurface, border: "none", color: t.accent, fontSize: 11, padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, whiteSpace: "nowrap", flexShrink: 0 }}>{tr("chat.add")}</button>
                            );
                            if (isEntree) return (
                              <button onClick={() => handleAddToMenu(s, "main")} style={{ background: t.bgSurface, border: "none", color: t.accent, fontSize: 11, padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, whiteSpace: "nowrap", flexShrink: 0 }}>{tr("chat.add")}</button>
                            );
                            return (
                              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                                <button onClick={() => handleAddToMenu(s, "first")} style={{ background: t.bgSurface, border: `0.5px solid ${t.accentBorder}`, color: t.accent, fontSize: 10, padding: "4px 7px", borderRadius: 5, cursor: "pointer", fontFamily: t.fontSerif, whiteSpace: "nowrap" }}>{tr("chat.addFirst")}</button>
                                <button onClick={() => handleAddToMenu(s, "main")} style={{ background: t.bgSurface, border: `0.5px solid ${t.accentBorder}`, color: t.accent, fontSize: 10, padding: "4px 7px", borderRadius: 5, cursor: "pointer", fontFamily: t.fontSerif, whiteSpace: "nowrap" }}>{tr("chat.addMain")}</button>
                              </div>
                            );
                          })()
                        ) : (
                          <button onClick={() => handleAddToMenu(s)} style={{ background: isAdded ? t.successDim : t.bgSurface, border: isAdded ? `0.5px solid ${t.success}` : "none", color: isAdded ? t.success : t.accent, fontSize: 11, padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, whiteSpace: "nowrap", flexShrink: 0 }}>
                            {isAdded ? tr("common.added") : tr("common.addToMyMenu")}
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* Hint text — only on the last message with suggestions, only if not yet added anything */}
                  {i === lastSuggestionMsgIndex && !hasAdded && (
                    <div style={{ color: t.textSecondary, fontSize: sz(11), fontStyle: "italic", paddingLeft: 4, paddingTop: 2 }}>
                      {tr("chat.hintSave")}
                    </div>
                  )}
                  {/* Hint text — once something added, encourage close */}
                  {i === lastSuggestionMsgIndex && hasAdded && (
                    <div style={{ color: t.textSecondary, fontSize: sz(11), fontStyle: "italic", paddingLeft: 4, paddingTop: 2 }}>
                      {trRich("chat.hintSaved", { done: <strong>{tr("chat.done")}</strong> })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {sending && <ChatStatusIndicator />}
          <div ref={bottomRef} />
        </div>

        {/* Input bar */}
        <div style={{ padding: "8px 12px 16px", borderTop: `0.5px solid ${t.borderMid}`, background: t.bgBase, flexShrink: 0, display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} placeholder={tr("chat.placeholder")} rows={1}
            style={{ flex: 1, background: t.bgCard, border: `0.5px solid ${t.borderMid}`, borderRadius: 20, padding: "10px 14px", fontFamily: t.fontSerif, fontSize: 16, color: t.textPrimary, outline: "none", resize: "none", lineHeight: 1.5, maxHeight: 90, overflowY: "auto" }} />
          <button onClick={handleSend} disabled={!input.trim() || sending}
            style={{ background: input.trim() && !sending ? t.accent : t.accentDim, border: "none", borderRadius: "50%", width: 40, height: 40, cursor: input.trim() && !sending ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.15s" }}>
            <span style={{ color: input.trim() && !sending ? t.accentText : t.accent, fontSize: 16, lineHeight: 1 }}>›</span>
          </button>
        </div>
      </div>
    </div>
    <LabelModal wine={zoomedLabel} onClose={() => setZoomedLabel(null)} zIndex={Z.chatLabelZoom} />
  </>;
}

// ─── Sommelier Toast ──────────────────────────────────────────────────────────
function SommelierToast({ items, onViewMenu, onDismiss }) {
  const t = useTheme();
  useEffect(() => {
    const t = setTimeout(onDismiss, 4500);
    return () => clearTimeout(t);
  }, []);

  if (!items || items.length === 0) return null;
  const label = items.length === 1
    ? tr("toast.addedOne", { name: items[0].name })
    : tr("toast.addedMany", { count: items.length });

  return (
    <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: Z.toast, display: "flex", alignItems: "center", gap: 12, background: t.bgBase, border: `0.5px solid ${t.accent}`, borderRadius: 12, padding: "12px 16px", boxShadow: `0 4px 24px ${t.shadow}`, fontFamily: t.fontSerif, maxWidth: "90vw", animation: "toastIn 0.3s ease" }}>
      <style>{`@keyframes toastIn { from { opacity:0; transform:translateX(-50%) translateY(16px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
      <span style={{ color: t.textPrimary, fontSize: 13, flex: 1 }}>{label}</span>
      <button onClick={onViewMenu} style={{ background: t.accent, border: "none", color: t.accentText, fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, whiteSpace: "nowrap" }}>
        {tr("toast.view")}
      </button>
      <button onClick={onDismiss} style={{ background: "none", border: "none", color: t.textDim, fontSize: 18, cursor: "pointer", lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
    </div>
  );
}

// ─── Sommelier Done Modal ─────────────────────────────────────────────────────
// Shown when chat closes with additions AND food is already selected —
// strong signal the guest has finished building their menu.

function SommelierDoneModal({ foodCount, wineCount, allWine = true, onViewMenu, onKeepBrowsing }) {
  const t = useTheme();
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: Z.chatPrompt, background: t.bgOverlay, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: t.fontSerif }}>
      <div style={{ background: t.bgCard, borderRadius: 16, padding: "32px 28px", maxWidth: 380, width: "100%", boxShadow: `0 16px 64px ${t.shadow}`, textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>★</div>
        <div style={{ color: t.textPrimary, fontSize: 20, fontWeight: 600, marginBottom: 10 }}>{tr("chat.doneModal.title")}</div>
        <div style={{ color: t.textSecondary, fontSize: 14, lineHeight: 1.7, marginBottom: 28 }}>
          {trRich("chat.doneModal.body", {
            dishes: trRich("chat.doneModal.dishes", { n: <strong>{foodCount}</strong> }, { count: foodCount }),
            drinks: trRich(allWine ? "chat.doneModal.wines" : "chat.doneModal.drinks", { n: <strong>{wineCount}</strong> }, { count: wineCount }),
          })}<br/>{tr("chat.doneModal.review")}
        </div>
        <button onClick={onViewMenu}
          style={{ width: "100%", background: t.accent, color: t.accentText, border: "none", padding: "14px", borderRadius: 8, cursor: "pointer", fontSize: 15, fontWeight: 600, fontFamily: t.fontSerif, letterSpacing: "0.5px", marginBottom: 10 }}>
          {tr("chat.doneModal.viewMenu")}
        </button>
        <button onClick={onKeepBrowsing}
          style={{ width: "100%", background: "none", border: `0.5px solid ${t.borderStrong}`, color: t.textSecondary, padding: "12px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontFamily: t.fontSerif }}>
          {tr("chat.doneModal.keepBrowsing")}
        </button>
      </div>
    </div>
  );
}

// ─── Item Pairing Button (Beer & Pours) ──────────────────────────────────────

// Two feature levels, and both have AI. "base" gets instant pairings: the pairing button on each item,
// the "Get a wine pairing" screen and My Menu's new-pairing. "premium" adds the sommelier chat bot on
// top (a paid add-on). Anything else (the retired 'print' value, or none) gets neither, as before.
const planHasPairing = (plan) => plan === "premium" || plan === "base";
const planHasChat = (plan) => plan === "premium";

function ItemPairingButton({ item, onOpenChat, favorites = [], onToggleFavorite, tabletLocation = "all" }) {
  const t = useTheme();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [shownDishes, setShownDishes] = useState([]);
  const pendingResult = useRef(null);
  const msgReadyRef = useRef(false);
  const [msgReady, setMsgReady] = useState(false);
  const markMsgReady = (v) => { msgReadyRef.current = v; setMsgReady(v); };

  function handleMsgComplete() {
    if (pendingResult.current !== null) {
      setResult(pendingResult.current);
      setLoading(false);
      pendingResult.current = null;
    }
    markMsgReady(true);
  }

  async function handlePairing() {
    pendingResult.current = null;
    markMsgReady(false);
    setLoading(true);
    try {
      const res = await rfetch(PAIRING_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "drink_to_food",
          lang: getRequestedLanguage(),
          itemId: item.id,
          itemName: item.name,
          itemDescription: item.description || null,
          itemStyle: item.style || null,
          itemCategory: item.category || null,
          itemABV: item.abv || null,
          excludeDishes: shownDishes,
          location: tabletLocation !== "all" ? tabletLocation : undefined
        })
      });
      const data = await res.json();
      const pairings = data.pairings || [];
      setShownDishes(prev => [...new Set([...prev, ...pairings.map(p => p.name)])]);
      pendingResult.current = pairings;
      if (msgReadyRef.current) { setResult(pairings); setLoading(false); pendingResult.current = null; }
    } catch (e) {
      pendingResult.current = [];
      if (msgReadyRef.current) { setResult([]); setLoading(false); pendingResult.current = null; }
    }
  }

  return (
    <div>
      {!loading && (
        <div style={{ display: "flex", gap: 8, marginBottom: result ? 12 : 0 }}>
          {onOpenChat && (
          <button onClick={() => onOpenChat({ id: item.id, name: item.name, type: item.style || item.category || "beverage" })}
            style={{ flex: 1, background: t.accentDimSm, border: `0.5px solid ${t.accent}`, color: t.accent, padding: "12px 8px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif, lineHeight: 1.3, textAlign: "center" }}>
            {tr("pairing.chatLine1")}<br/>{tr("pairing.chatLine2")}
          </button>
          )}
          <button onClick={handlePairing}
            style={{ flex: 1, background: t.bgSurface, color: t.accent, border: `0.5px solid ${t.accent}`, padding: "12px 8px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif, lineHeight: 1.3, textAlign: "center" }}>
            {tr("pairing.instantLine1")}<br/>{tr("pairing.instantLine2")}
          </button>
        </div>
      )}
      {loading && <LoadingMessages messages={KITCHEN_MESSAGES} onAllShown={handleMsgComplete} />}
      {result && result.length > 0 && (
        <div style={{ marginTop: 2 }}>
          <div style={{ color: t.textSecondary, fontSize: 9, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 8 }}>{tr("pairing.pairsWith")}</div>
          {result.map((p, i) => {
            const isStarred = favorites.some(f => f.id === p.id);
            return (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, paddingBottom: 8, borderBottom: i < result.length - 1 ? `0.5px solid ${t.borderSubtle}` : "none" }}>
                <div style={{ fontSize: 16, flexShrink: 0 }}>🍽</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: t.textPrimary, fontSize: 13, fontWeight: 500, marginBottom: 1 }}>{p.name}</div>
                  <div style={{ color: t.textSecondary, fontSize: 10, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 2 }}>{p.course}</div>
                  <div style={{ color: t.textSecondary, fontSize: 12, fontStyle: "italic", lineHeight: 1.5, marginBottom: p.id ? 6 : 0 }}>{p.reason}</div>
                  {p.id && onToggleFavorite && (
                    <button onClick={() => onToggleFavorite({ id: p.id, name: p.name, course: p.course, description: p.reason, courseRole: "main" }, "food")}
                      style={{ background: isStarred ? t.accentDim : t.accentDimSm, border: `0.5px solid ${isStarred ? t.accent : t.accentBorder}`, color: t.accent, fontSize: 11, padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif }}>
                      {isStarred ? tr("common.addedToMyMenu") : tr("common.addToMyMenu")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {result && result.length === 0 && (
        <div style={{ color: t.textSecondary, fontSize: 12, textAlign: "center", padding: "8px 0" }}>{tr("pairing.noneFound")}</div>
      )}
    </div>
  );
}

// ─── Label Zoom Modal ─────────────────────────────────────────────────────────

// ─── Guest Privacy Notice ─────────────────────────────────────────────────────
// The published text lives here, in code, on purpose. POLICY_VERSION sits beside
// it so the two cannot drift: a wording change that does not bump the version is
// visibly wrong in a diff, and the version stamped on a consent record therefore
// always identifies text that actually existed. Git supplies the timestamp and
// the author, which is a better audit trail than a CMS.
//
// BUMP POLICY_VERSION WHENEVER ANY GUEST-FACING WORDING BELOW CHANGES,
// including CONSENT_TEXT.
const POLICY_VERSION = "1.1";

// The exact string beside the checkbox. Exported to the consent record so that
// "what did they agree to" is answerable years later. Because the restaurant
// name is interpolated, it is built as a function rather than a constant. It is
// in the language the guest saw, which is what the record should hold.
const consentText = (venueName) =>
  tr("myMenu.email.consent", { venue: venueName || tr("common.thisRestaurant") });

function PrivacyNotice({ venueName, listOptIn, onClose }) {
  const t = useTheme();
  const name = venueName || tr("common.thisRestaurant");
  const H = ({ children }) => (
    <div style={{ color: t.accent, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", marginTop: 22, marginBottom: 8 }}>{children}</div>
  );
  const P = ({ children }) => (
    <div style={{ color: t.textSecondary, fontSize: 13, lineHeight: 1.6, marginBottom: 10 }}>{children}</div>
  );
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: Z.labelModal, background: t.bgOverlay, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgSurface, border: `1px solid ${t.borderMid}`, borderRadius: 14, padding: "24px 22px", maxWidth: 460, width: "100%", maxHeight: "85vh", overflowY: "auto", fontFamily: t.fontSerif }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div style={{ color: t.textPrimary, fontSize: 17 }}>{tr("privacy.title")}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.textSecondary, fontSize: 22, lineHeight: 1, cursor: "pointer", padding: 0 }}>×</button>
        </div>

        <P>{tr("privacy.intro", { name })}</P>

        <H>{tr("privacy.usingHeading")}</H>
        <P>{tr("privacy.usingBody")}</P>

        <H>{tr("privacy.aiHeading")}</H>
        <P>{trRich("privacy.aiBody", { notTrained: <strong>{tr("privacy.aiNotTrained")}</strong> })}</P>
        {/* Restored once the notice gained a footer link. Reachable before a
            guest chats, it is a usable warning rather than an after-the-fact
            scold. The real exposure is health data: a pairing conversation
            invites "I am pregnant", "I am allergic to", "I am not drinking". */}
        <P>{tr("privacy.aiAdvice")}</P>

        <H>{tr("privacy.picksHeading")}</H>
        <P>{tr("privacy.picksDevice")}</P>
        <P>{tr("privacy.picksEmailed")}</P>

        <H>{tr("privacy.emailHeading")}</H>
        {listOptIn ? (
          <>
            <P>{tr("privacy.emailNeverRequired")}</P>
            <P>{trRich("privacy.emailWithList", { once: <strong>{tr("privacy.emailOnce")}</strong> }, { name })}</P>
            <ul style={{ color: t.textSecondary, fontSize: 13, lineHeight: 1.6, marginTop: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 5 }}>{tr("privacy.listMayEmail", { name })}</li>
              <li style={{ marginBottom: 5 }}>{tr("privacy.listUnsubscribe")}</li>
              <li>{tr("privacy.listNotOurs")}</li>
            </ul>
          </>
        ) : (
          // No checkbox is shown at this restaurant, so describing one would
          // invite a question the guest cannot act on. The transactional
          // sentence is the whole story here.
          <>
            <P>{tr("privacy.emailNeverRequired")}</P>
            <P>{trRich("privacy.emailNoList", { once: <strong>{tr("privacy.emailOnce")}</strong> })}</P>
          </>
        )}

        <H>{tr("privacy.removeHeading")}</H>
        {listOptIn
          ? <P>{tr("privacy.removeWithList", { name })}</P>
          : <P>{tr("privacy.removeNoList")}</P>}

        <div style={{ borderTop: `0.5px solid ${t.borderSubtle}`, marginTop: 22, paddingTop: 12, color: t.textMuted, fontSize: 10, letterSpacing: "1px" }}>
          {tr("privacy.version", { version: POLICY_VERSION })}
        </div>
      </div>
    </div>
  );
}

// ─── Report incorrect image (Nov 5, 2026) ────────────────────────────────────
// Under every enlarged label a guest can see. The backstop for label
// auto-approval: one wrong shared image reaches every restaurant that pours it.
// Sends only the image URL (plus the item's id and name when the caller has
// them); the server works out which record it is from the URL itself, and
// stores nothing about the guest. It goes into the super user's daily digest.
//
// Always ends on "thanks", even if the request fails: a report is never
// something the guest can retry their way out of, and an error message would
// make a wrong label look like a broken app.
// Reported images are remembered for the page's life, so reopening the label
// says "reported" instead of inviting a second tap.
const REPORTED_IMAGES = new Set();
function ReportImageButton({ item }) {
  const t = useTheme();
  const url = item && item.imageUrl;
  const [state, setState] = useState(() => (url && REPORTED_IMAGES.has(url) ? 'done' : 'idle'));
  useEffect(() => { setState(url && REPORTED_IMAGES.has(url) ? 'done' : 'idle'); }, [url]);
  if (!url) return null;
  async function send(e) {
    e.stopPropagation();
    if (state !== 'idle') return;
    setState('sending');
    try {
      await rfetch(REPORT_IMAGE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: url, itemId: item.id ?? item.itemId ?? null, itemName: item.name || null }),
      });
    } catch (err) { console.error('[reportImage]', err); }
    REPORTED_IMAGES.add(url);
    setState('done');
  }
  if (state === 'done') {
    return (
      <div onClick={e => e.stopPropagation()} role="status"
        style={{ color: t.textDim, fontSize: 11, marginTop: 14, fontFamily: t.fontSerif, cursor: 'default' }}>
        {tr("labelZoom.reportThanks")}
      </div>
    );
  }
  return (
    <button type="button" onClick={send} disabled={state === 'sending'}
      style={{ ...ICON_BTN, gap: 6, margin: '14px auto 0', background: 'none', border: `0.5px solid ${t.borderMid}`, borderRadius: 14,
        color: t.textDim, padding: '6px 14px', fontSize: 11, fontFamily: t.fontSerif, cursor: 'pointer', opacity: state === 'sending' ? 0.6 : 1 }}>
      <LineIcon name="flag" size={12} />{tr("labelZoom.reportImage")}
    </button>
  );
}

// The enlarged label. One component for every guest screen that opens one: the
// lists and wine detail (LabelModal), the chat, and the saved-menu page.
//
// Fixed Nov 5, 2026: the image and its text used to float directly on
// t.bgOverlay. The light themes' overlay is only 55% opaque, so the list showed
// through behind the name and disclaimer and neither could be read. Everything
// now sits on a solid card in the theme's surface colour, so readability no
// longer depends on how dark a theme's overlay is. Tapping anywhere, card
// included, still closes it; only the Report button keeps it open.
function LabelModal({ wine, onClose, zIndex }) {
  const t = useTheme();
  if (!wine || !wine.imageUrl) return null;
  return (
    <div onClick={onClose} role="dialog" aria-label={tr("labelZoom.aria", { name: wine.name })} style={{
      position: "fixed", inset: 0, zIndex: zIndex ?? Z.labelModal,
      background: t.bgOverlay,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16, cursor: "pointer"
    }}>
      <div style={{
        background: t.bgSurface, borderRadius: 12,
        boxShadow: `0 8px 48px ${t.shadowStrong}`,
        width: "min(560px, 100%)", maxHeight: "100%", overflowY: "auto",
        boxSizing: "border-box", padding: "20px 20px 16px",
        display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center"
      }}>
        <img
          src={wine.imageUrl}
          alt={wine.name}
          style={{ maxHeight: "60vh", maxWidth: "100%", objectFit: "contain", borderRadius: 6 }}
        />
        <div style={{ color: t.textPrimary, fontSize: 17, fontFamily: t.fontSerif, marginTop: 16, marginBottom: 4 }}>{wine.name}</div>
        {(wine.varietal || wine.region) && (
          <div style={{ color: t.accent, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", fontFamily: t.fontSerif }}>
            {[wine.varietal, wine.region].filter(Boolean).join(" · ")}
          </div>
        )}
        {/* resolveItemImage returns this on every read path, and it is chosen by
            category - vintage wording for wine, packaging wording for everything
            else. Absent means no image, so nothing renders. */}
        {wine.imageDisclaimer && (
          <div style={{ color: t.textSecondary, fontSize: 11, lineHeight: 1.5, marginTop: 10, maxWidth: 360 }}>
            {disclaimerText(wine.imageDisclaimer)}
          </div>
        )}
        <ReportImageButton item={wine} />
        <div style={{ marginTop: 16, color: t.textDim, fontSize: 11, fontFamily: t.fontSerif, letterSpacing: "1px" }}>
          {tr("labelZoom.tapToCloseCaps")}
        </div>
      </div>
    </div>
  );
}

// ─── Wine Detail Panel ────────────────────────────────────────────────────────

function WineDetailPanel({ wine, onClose, onOpenChat, onZoomLabel, favorites = [], onToggleFavorite, tabletLocation = "all", plan = "premium", screenTitle = tr("lists.wine.title") }) {
  const t = useTheme();
  // Was useIsNarrow() - the expanded item used to stay inline behind the list
  // on a tablet/wide screen, and only went full-screen on a phone. Forced to
  // true on request: leaving the list visible behind the expanded item left
  // guests unsure what to do next or how to close it, so every device now
  // gets the full-screen "own new screen" treatment phones already had.
  // Every use of `narrow` below is this same behavioral choice, not
  // general-purpose responsive sizing, so hardcoding it here is safe.
  const narrow = true;
  const textScale = useTextScale();
  const sz = n => Math.round(n * textScale * 2) / 2;

  // Lock the page behind a full-screen detail view. Without this, scrolling past
  // the end of the detail scrolls the list underneath it, which on a phone looks
  // like the screen has come apart.
  useEffect(() => {
    if (!narrow) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [narrow]);
  const [pairingLoading, setPairingLoading] = useState(false);
  const [pairingResult, setPairingResult] = useState(null);

  const [shownDishes, setShownDishes] = useState([]);
  const pendingDishes = useRef(null);
  const dishReadyRef = useRef(false);
  const [dishMessagesReady, setDishMessagesReady] = useState(false);
  const markDishReady = (v) => { dishReadyRef.current = v; setDishMessagesReady(v); };

  function handleDishMessagesComplete() {
    if (pendingDishes.current !== null) {
      setPairingResult(pendingDishes.current);
      setPairingLoading(false);
      pendingDishes.current = null;
    }
    markDishReady(true);
  }

  async function handlePairing() {
    pendingDishes.current = null;
    markDishReady(false);
    setPairingLoading(true);
    try {
      const res = await rfetch(PAIRING_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "wine_to_food", lang: getRequestedLanguage(), itemId: wine.id, excludeDishes: shownDishes, location: tabletLocation !== "all" ? tabletLocation : undefined })
      });
      const data = await res.json();
      const pairings = data.pairings || [];
      setShownDishes(prev => [...new Set([...prev, ...pairings.map(p => p.name)])]);
      pendingDishes.current = pairings;
      if (dishReadyRef.current) { setPairingResult(pairings); setPairingLoading(false); pendingDishes.current = null; }
    } catch (e) {
      pendingDishes.current = [];
      if (dishReadyRef.current) { setPairingResult([]); setPairingLoading(false); pendingDishes.current = null; }
    }
  }

  return (
    <div style={narrow ? {
      // `inset: 0` alone. Setting an explicit height ALONGSIDE inset made the panel
      // 100dvh tall rather than filling the viewport - and when Safari's address bar
      // is showing, 100dvh is shorter than the visible area, so the list behind
      // showed through as a strip at the bottom. A fixed element with inset:0 always
      // fills exactly what is on screen, whatever the browser chrome is doing.
      // maxWidth/margin match the same tablet-width column every other screen in
      // this app already uses (WineListScreen, ItemListScreen, SommelierChat) -
      // without it, forcing this panel full-width on every device stretched it
      // edge-to-edge on a wide desktop monitor, which is exactly where a lot of
      // prospects will actually be looking at this demo.
      position: "fixed", inset: 0, zIndex: Z.detailPanel,
      maxWidth: 680, margin: "0 auto",
      background: t.bgBase,
      overflowY: "auto", WebkitOverflowScrolling: "touch",
      padding: "0 20px calc(28px + env(safe-area-inset-bottom))",
    } : {
      position: "sticky", bottom: 0, background: t.bgSurface,
      borderTop: `1px solid ${t.borderMid}`, padding: "18px 20px",
      boxShadow: `0 -8px 32px ${t.shadow}`, maxHeight: "70vh", overflowY: "auto",
    }}>
      {/* Full screen, the guest loses every cue about where they are - no list
          behind, no page title. This restores the heading and gives the back
          action a word rather than a glyph. */}
      {narrow && (
        <div style={{
          position: "sticky", top: 0, zIndex: 1, background: t.bgBase,
          margin: "0 -20px", padding: "calc(10px + env(safe-area-inset-top)) 20px 10px",
          borderBottom: `0.5px solid ${t.borderSubtle}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.accent, fontSize: 13, fontFamily: t.fontSerif, cursor: "pointer", padding: 0, letterSpacing: "0.5px" }}>
            ‹ {screenTitle}
          </button>
        </div>
      )}
      <div style={{ display: "flex", gap: 14, marginBottom: 12, marginTop: narrow ? 14 : 0 }}>
        <div style={{ flexShrink: 0 }}>
          <div
            onClick={wine.imageUrl && onZoomLabel ? () => onZoomLabel(wine) : undefined}
            style={{ width: 52, height: 72, borderRadius: 4, background: wine.imageUrl ? t.labelBackdrop : t.white06, border: `0.5px solid ${wine.imageUrl && onZoomLabel ? t.accent : t.borderMid}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, overflow: "hidden", cursor: wine.imageUrl && onZoomLabel ? "zoom-in" : "default" }}>
            {wine.imageUrl ? <img src={wine.imageUrl} alt={wine.name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 4 }} /> : "🍷"}
          </div>
          {wine.imageDisclaimer && (
            <div style={{ color: t.textSecondary, fontSize: 9, fontStyle: "italic", marginTop: 3, width: 52, lineHeight: 1.3, textAlign: "center" }}>
              {tr("wineDetail.representativeLabel")}
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: t.textPrimary, fontSize: sz(16), marginBottom: 3, lineHeight: 1.3 }}>{wine.name}</div>
          <div style={{ color: t.accentSecondary, fontSize: sz(10), letterSpacing: "1px", textTransform: "uppercase" }}>
            {[wine.varietal, wine.region, wine.vintage ? `${wine.vintage}` : null].filter(Boolean).join(" · ")}
          </div>
        </div>
        <button onClick={onClose} aria-label={tr("lists.backToList")} style={narrow ? {
          // On a phone this is the only way back - the list is no longer visible
          // behind it - so it gets a real target rather than a bare glyph.
          background: t.white06, border: `1px solid ${t.borderMid}`, color: t.textPrimary,
          fontSize: 20, cursor: "pointer", lineHeight: 1, alignSelf: "flex-start",
          width: 38, height: 38, borderRadius: 19, flexShrink: 0,
        } : {
          background: "transparent", border: "none", color: t.textSecondary, fontSize: 22,
          cursor: "pointer", padding: "0 4px", alignSelf: "flex-start", lineHeight: 1,
        }}>×</button>
      </div>
      {wine.imageDisclaimer && (
        <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorderSm}`, borderRadius: 6, padding: "6px 10px", marginBottom: 10, fontSize: 11, color: t.textSecondary, fontStyle: "italic", lineHeight: 1.4 }}>
          {disclaimerText(wine.imageDisclaimer)}
        </div>
      )}

      {wine.description ? (
        <div style={{ color: t.textSecondary, fontSize: sz(13), lineHeight: 1.8, marginBottom: 12 }}>{wine.description}</div>
      ) : (
        <div style={{ color: t.textSecondary, fontSize: 12, fontStyle: "italic", marginBottom: 12 }}>{tr("wineDetail.askTastingNotes")}</div>
      )}

      {wine.reviews && wine.reviews !== "null" && (
        <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorderSm}`, borderRadius: 6, padding: "8px 12px", marginBottom: 12 }}>
          <div style={{ color: t.accent, fontSize: 9, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 3 }}>{tr("wineDetail.reviews")}</div>
          <div style={{ color: t.textSecondary, fontSize: sz(12), lineHeight: 1.6 }}>{wine.reviews}</div>
        </div>
      )}

      <div style={{ display: "flex", gap: 24, marginBottom: 14, paddingBottom: 14, borderBottom: `0.5px solid ${t.borderSubtle}` }}>
        {wine.glassPrice && (
          <div>
            <div style={{ color: t.textSecondary, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 2 }}>{tr("wineDetail.glass")}</div>
            <div style={{ color: t.textPrimary, fontSize: 22 }}>{formatPrice(wine.glassPrice)}</div>
          </div>
        )}
        {wine.bottlePrice && (
          <div>
            <div style={{ color: t.textSecondary, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 2 }}>{tr("wineDetail.bottle")}</div>
            <div style={{ color: t.textPrimary, fontSize: 22 }}>{formatPrice(wine.bottlePrice)}</div>
          </div>
        )}
        {!wine.glassPrice && !wine.bottlePrice && (
          <div style={{ color: t.textSecondary, fontSize: 13, fontStyle: "italic", alignSelf: "center" }}>{tr("wineDetail.askPricing")}</div>
        )}
      </div>

      {planHasPairing(plan) && !pairingLoading && (
        <div style={{ display: "flex", gap: 8, marginBottom: pairingResult ? 14 : 0 }}>
          {planHasChat(plan) && onOpenChat && (
          <button onClick={() => onOpenChat({ name: wine.name, type: wine.varietal || "wine", wineId: wine.id, wineFull: { id: wine.id, name: wine.name, varietal: wine.varietal || null, region: wine.region || null, glassPrice: wine.glassPrice || null, bottlePrice: wine.bottlePrice || null, imageUrl: wine.imageUrl || null } })}
            style={{ flex: 1, background: t.accentDimSm, border: `0.5px solid ${t.accent}`, color: t.accent, padding: "12px 8px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif, lineHeight: 1.3, textAlign: "center" }}>
            {tr("pairing.chatLine1")}<br/>{tr("pairing.chatLine2")}
          </button>
          )}
          <button onClick={handlePairing}
            style={{ flex: 1, background: t.bgSurface, color: t.accent, border: `0.5px solid ${t.accent}`, padding: "12px 8px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif, lineHeight: 1.3, textAlign: "center" }}>
            {tr("pairing.instantLine1")}<br/>{tr("pairing.instantLine2")}
          </button>
        </div>
      )}
      {pairingLoading && <LoadingMessages messages={KITCHEN_MESSAGES} onAllShown={handleDishMessagesComplete} />}

      {pairingResult && pairingResult.length > 0 && (
        <div>
          <div style={{ color: t.textSecondary, fontSize: 9, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 10 }}>{tr("pairing.pairsWith")}</div>
          {pairingResult.map((p, i) => {
            const isStarred = favorites.some(f => f.id === p.id);
            return (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, paddingBottom: 10, borderBottom: i < pairingResult.length - 1 ? `0.5px solid ${t.borderSubtle}` : "none" }}>
                <div style={{ fontSize: 18, flexShrink: 0 }}>🍽</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: t.textPrimary, fontSize: sz(13), fontWeight: 500, marginBottom: 2 }}>{p.name}</div>
                  <div style={{ color: t.textSecondary, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 3 }}>{p.course}</div>
                  <div style={{ color: t.textSecondary, fontSize: sz(12), fontStyle: "italic", lineHeight: 1.5, marginBottom: p.id ? 6 : 0 }}>{p.reason}</div>
                  {p.id && onToggleFavorite && (
                    <button onClick={() => onToggleFavorite({ id: p.id, name: p.name, course: p.course, description: p.reason, courseRole: "main" }, "food")}
                      style={{ background: isStarred ? t.accentDim : t.accentDimSm, border: `0.5px solid ${isStarred ? t.accent : t.accentBorder}`, color: t.accent, fontSize: 11, padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif }}>
                      {isStarred ? tr("common.addedToMyMenu") : tr("common.addToMyMenu")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {pairingResult && pairingResult.length === 0 && (
        <div style={{ color: t.textSecondary, fontSize: 12, textAlign: "center", padding: "8px 0" }}>{tr("pairing.noneFound")}</div>
      )}
    </div>
  );
}

// ─── Sommelier Screen ─────────────────────────────────────────────────────────

function SommelierScreen({ chatEnabled = true, onBack, wines = [], favorites = [], onToggleFavorite = () => {}, onShowShortlist = () => {}, tabletLocation = "all", locationNames = { bar: "Bar", dining: "Dining Room" } }) {
  const t = useTheme();
  // Guest text size - see TEXT_SCALES. Scales the dish list and the pairing
  // results; headers, course bars, chips and checkboxes keep their size.
  const textScale = useTextScale();
  const sz = n => Math.round(n * textScale * 2) / 2;
  const chipScale = useChipScale();
  // Short windows (a laptop browser, a phone) get a slimmer pairing panel: the
  // full one took most of a MacBook Air window in French and left one dish row
  // to scroll in (Oct 29, 2026). Tall tablets keep the heading and subtitle.
  const { height: vpHeight } = useViewport();
  const shortScreen = vpHeight < 900;
  const [foodItems, setFoodItems] = useState([]);
  const [loadingFood, setLoadingFood] = useState(true);
  const [activeCourse, setActiveCourse] = useState("All");
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [pairingResult, setPairingResult] = useState(null);
  const [pairingLoading, setPairingLoading] = useState(false);
  const [view, setView] = useState("pick");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatFoods, setChatFoods] = useState([]);
  // Where the chat was opened from, sent with the guest's first message so the
  // server can count "Ask the Sommelier" alongside the other pairing choices.
  const [chatEntry, setChatEntry] = useState(null);
  const [toast, setToast] = useState(null);
  const [doneModal, setDoneModal] = useState(null);

  // ── Pairing choice (Oct 2026, demo focus-group feedback) ──────────────────
  // Most guests already know whether they drink red or white, and one "Instant
  // Pairing" button asked them to give that up. Four choices instead: Red,
  // White/Rosé/Sparkling, Sommelier's Pick (the colour-blind best match - the old
  // Instant Pairing), and Ask the Sommelier (the chat). The first three are
  // matrix lookups; steering guests to them keeps them out of the costlier chat.
  //
  // `colour` on each wine comes from getWines (wineListColour() server-side), the
  // same field the Wine List's colour chips use. A colour button appears only
  // when this list has at least one wine of that colour, and the light button's
  // label names only the styles actually on hand. Before the backend sends
  // `colour`, no colour button shows and the screen degrades to Sommelier's Pick
  // and Ask the Sommelier - the old two choices under new names.
  const [pairChoice, setPairChoice] = useState(null);   // "red" | "light" | "pick"
  const [colourNote, setColourNote] = useState(null);
  // The wine the colour note names, shaped like a pairing card, so the guest can
  // add it from the note instead of going back and choosing again.
  const [colourAlt, setColourAlt] = useState(null);
  const [resultMessage, setResultMessage] = useState(null);
  const listColours = new Set(wines.filter(w => w && w.available !== false && w.colour).map(w => w.colour));
  // Port and other red dessert wines count for the Red button (pairColour from
  // getWines; older backends send only colour).
  const hasRed = listColours.has("red") || wines.some(w => w && w.available !== false && w.pairColour === "dessert-red");
  const LIGHT_PARTS = [["white", "pairing.white"], ["rose", "pairing.rose"], ["sparkling", "pairing.bubbles"]].filter(([c]) => listColours.has(c)).map(([, k]) => tr(k));
  // At most three parts. The joining words are in the strings, not the code:
  // "White, Rosé & Sparkling" is not built the same way in every language.
  const [lp1, lp2, lp3] = LIGHT_PARTS;
  const lightLabel = LIGHT_PARTS.length <= 1 ? (lp1 || "")
    : LIGHT_PARTS.length === 2 ? tr("pairing.lightTwo", { a: lp1, b: lp2 })
    : tr("pairing.lightThree", { a: lp1, b: lp2, c: lp3 });
  const pairChoices = [
    ...(hasRed ? [{ key: "red", label: tr("pairing.red") }] : []),
    ...(LIGHT_PARTS.length > 0 ? [{ key: "light", label: lightLabel }] : []),
    { key: "pick", label: tr("pairing.pick") },
  ];
  const choiceHeading = { red: tr("pairing.heading.red"), light: tr("pairing.heading.light", { styles: lightLabel }), pick: tr("pairing.heading.pick") };
  function openChat(foods, entry) { setChatFoods(foods); setChatEntry(entry); setChatOpen(true); }

  useEffect(() => {
    const locParam = tabletLocation !== "all" ? `?location=${tabletLocation}` : "";
    rfetch(FOOD_URL + locParam).then(r => r.json())
      .then(data => { registerSectionLabels(data.sectionLabels); setCourseOverrides(data.courseOverrides); setFoodItems(data.foodItems || []); setLoadingFood(false); })
      .catch(() => setLoadingFood(false));
  }, [tabletLocation]);

  const [lastShownIds, setLastShownIds] = useState({});
  // Set when the matrix could find no single wine for the whole table and
  // answered per dish instead. Explaining that is the difference between the app
  // looking clever and looking inconsistent.
  const [splitNote, setSplitNote] = useState(null);
  // Set when only ONE wine suits the whole table: the server adds the best wine for
  // each dish on its own, shaped like byCourse, shown beneath the shared wine.
  const [byDishAlt, setByDishAlt] = useState(null);
  // Flattened from whichever shape the result took, so the chat can be told what
  // the guest is actually looking at.
  const shownPairingWines = [
    ...(Array.isArray(pairingResult)
      ? pairingResult
      : (pairingResult?.byCourse || []).flatMap(c => c.pairings || [])),
    ...(Array.isArray(pairingResult) && byDishAlt ? byDishAlt.flatMap(c => c.pairings || []) : []),
    // On screen too, in the note - the chat should know it was suggested.
    ...(colourAlt ? [colourAlt] : []),
  ];

  // The dish list is long and guests scroll a long way down it. Switching to the
  // result view kept that scroll position, so the answer appeared already scrolled
  // past - the guest landed on the last wine card with the heading and the first
  // recommendation above them, and had to scroll up to find out what they had been
  // given.
  //
  // Fires on entering the view AND when the result changes, so "Get Different
  // Pairings" returns to the top too. `auto` rather than `smooth`: the content is
  // being replaced, and animating a scroll through content the guest never asked to
  // see is worse than simply being at the top.
  useEffect(() => {
    if (view !== 'result') return;
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [view, pairingResult]);
  const pendingPairing = useRef(null);
  // A ref, not just state: the completion check runs inside an async function that
  // captured `messagesReady` at render time. Now that the minimum dwell is 2s
  // rather than a full message cycle, the response usually arrives AFTER the flag
  // flips - and a stale `false` would leave the answer sitting in pendingPairing,
  // never rendered.
  const messagesReadyRef = useRef(false);
  const [messagesReady, setMessagesReady] = useState(false);
  const markReady = (v) => { messagesReadyRef.current = v; setMessagesReady(v); };
  const [zoomedLabel, setZoomedLabel] = useState(null);

  function handleMessagesComplete() {
    if (pendingPairing.current !== null) {
      setPairingResult(pendingPairing.current);
      setPairingLoading(false);
      pendingPairing.current = null;
    }
    markReady(true);
  }

  function handleFoodToggle(food, role) {
    const hasRole = selectedFoods.some(f => f.id === food.id && f.courseRole === role);
    let newSelected;
    if (hasRole) {
      newSelected = selectedFoods.filter(f => !(f.id === food.id && f.courseRole === role));
    } else {
      newSelected = [...selectedFoods, { ...food, courseRole: role }];
    }
    setSelectedFoods(newSelected);
    // Add to shortlist when first selection for this dish; remove when all selections cleared
    const hadAny = selectedFoods.some(f => f.id === food.id);
    const hasAny = newSelected.some(f => f.id === food.id);
    const isFav = favorites.some(f => f.id === food.id);
    if (!hadAny && hasAny && !isFav) onToggleFavorite({ id: food.id, name: food.name, price: food.price, course: food.course, courseRole: role, description: food.description }, "food");
    if (hadAny && !hasAny && isFav) onToggleFavorite({ id: food.id, name: food.name, price: food.price, course: food.course, courseRole: role, description: food.description }, "food");
  }

  // Dish-to-wine is served from the precomputed matrix: the wines are chosen by
  // lookup and only the explanation is written live. The model endpoint stays as a
  // fallback so a restaurant whose matrix has not been built yet still works, and
  // so a matrix failure degrades rather than breaking service.
  async function requestPairing(extra, choice = pairChoice) {
    const body = {
      // Absent for Sommelier's Pick: the server's colour-blind path, unchanged.
      ...(choice === "red" || choice === "light" ? { colour: choice } : {}),
      foodIds: selectedFoods.map(f => f.id),
      items: selectedFoods.map(f => ({ id: f.id, courseRole: f.courseRole })),
      type: "food_to_wine",
      explain: true,
      lang: getRequestedLanguage(),
      location: tabletLocation !== "all" ? tabletLocation : undefined,
      ...extra,
    };
    try {
      const res = await rfetch(MATRIX_PAIRING_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      // `vague` IS an answer and must not fall back. The scorer reported that it
      // could not identify the dish, and no statistic over the scores detects
      // that: "Seafood Special" scored a confident 88 with a 60-point span
      // because the model picked a plausible protein and scored that guess
      // coherently. Falling back would hand the same unidentifiable dish to the
      // old engine and get the same unearned confidence, which is precisely what
      // the flag exists to prevent.
      if (data.vague) return data;
      // The chosen colour has nothing on the list right now (a stale tablet, or
      // the last one 86'd). An answer, not a failure: the model path would get
      // the same empty colour and charge for it.
      if (res.ok && data.colourEmpty) return data;
      // `dish_not_scored` DOES fall back, as of Sep 2026. It used to be treated
      // as an answer alongside `vague`, on the reasoning that a stale matrix
      // should be fixed rather than papered over. That was right when the only
      // cause was a rebuild someone had forgotten — it is wrong for a dish the
      // chef added twenty minutes ago.
      //
      // The failure it produced: a guest picks the evening's special, asks for a
      // pairing, and gets NOTHING. Not a worse pairing — no pairing at all, on
      // the dish the kitchen most wants to sell. A model-path answer at ~$0.086
      // is strictly better than silence.
      //
      // Incremental scoring closes most of this window, but not all of it: there
      // is still the gap between the food sync and the scoring job completing,
      // and the case where that job fails. This is the floor under both.
      if (data.error === 'dish_not_scored') {
        console.warn('[pairing] dish not in matrix — falling back to the model path');
      } else {
        // A split answer is a complete answer - one wine per dish because nothing
        // suits the whole table. Falling back here would replace a sommelier's
        // response with the old engine's compromise.
        if (res.ok && data.split && Array.isArray(data.byCourse) && data.byCourse.length > 0) return data;
        if (res.ok && Array.isArray(data.pairings) && data.pairings.length > 0) return data;
        if (res.ok && Array.isArray(data.pairings) && data.exhausted) return data;
      }
    } catch (e) { /* fall through */ }
    const res2 = await rfetch(PAIRING_URL, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return res2.json();
  }

  function storeResult(data) {
    // English on purpose, not tr(): courseLabel is stored on the wine as a My
    // Menu grouping key (see handleAddToMenu in SommelierChat).
    const roleLabels = { first: "First Course", main: "Main Course", dessert: "Dessert" };
    setSplitNote(keyedText(data.splitNoteKey, data.splitNote));
    // "If you're open to it, a white would be an even better match." Server-built
    // from the matrix scores; absent unless the guest chose a colour and the dish
    // clearly wants another.
    setColourNote(colourNoteText(data.colourNoteKey, data.colourNote));
    setColourAlt(data.colourAlt && data.colourAlt.id ? data.colourAlt : null);
    // Shown only when nothing came back, in place of the generic line.
    setResultMessage(keyedText(data.messageKey, data.message));
    const alt = !data.byCourse && Array.isArray(data.byDish) && data.byDish.length > 0
      ? data.byDish.map(c => ({ ...c, pairings: (c.pairings || []).map(p => ({ ...p, courseLabel: c.course })) }))
      : null;
    setByDishAlt(alt);
    if (data.byCourse) {
      // Embed courseLabel directly on each pairing so it travels with the data
      const taggedCourses = data.byCourse.map(c => ({
        ...c,
        pairings: (c.pairings || []).map(p => ({ ...p, courseLabel: c.course }))
      }));
      const ids = {};
      taggedCourses.forEach(c => c.pairings?.forEach(p => { if (p.id) ids[`${c.course}-${p.level}`] = p.id; }));
      setLastShownIds(ids);
      const result = { byCourse: taggedCourses };
      pendingPairing.current = result;
      if (messagesReadyRef.current) { setPairingResult(result); setPairingLoading(false); pendingPairing.current = null; }
    } else {
      // Single course — determine course label from selected foods
      const roles = [...new Set(selectedFoods.map(f => f.courseRole || "main"))];
      const singleCourseLabel = roles.length === 1 ? (roleLabels[roles[0]] || null) : null;
      const pairings = (data.pairings || []).map(p => ({ ...p, courseLabel: singleCourseLabel }));
      const ids = {};
      pairings.forEach(p => { if (p.id) ids[p.level] = p.id; });
      // The per-dish alternatives count as shown too, so "Get Different Pairings"
      // moves past them as well.
      (alt || []).forEach((c, ci) => (c.pairings || []).forEach(p => { if (p.id) ids[`byDish-${ci}`] = p.id; }));
      setLastShownIds(ids);
      pendingPairing.current = pairings;
      if (messagesReadyRef.current) { setPairingResult(pairings); setPairingLoading(false); pendingPairing.current = null; }
    }
  }

  async function handleGetPairings(choice) {
    if (selectedFoods.length === 0) return;
    setPairChoice(choice);
    setPairingLoading(true);
    setPairingResult(null);
    setColourNote(null);
    setColourAlt(null);
    setResultMessage(null);
    pendingPairing.current = null;
    markReady(false);
    setLastShownIds({});
    setView("result");
    try {
      // Passed explicitly: setPairChoice has not landed in this render's closure.
      storeResult(await requestPairing({}, choice));
    } catch (e) {
      pendingPairing.current = [];
      if (messagesReadyRef.current) { setPairingResult([]); setPairingLoading(false); pendingPairing.current = null; }
    }
  }

  async function handleDifferentOptions() {
    if (selectedFoods.length === 0) return;
    setPairingLoading(true);
    setPairingResult(null);
    pendingPairing.current = null;
    markReady(false);
    try {
      storeResult(await requestPairing({ excludeWineIds: lastShownIds }));
    } catch (e) {
      pendingPairing.current = [];
      if (messagesReadyRef.current) { setPairingResult([]); setPairingLoading(false); pendingPairing.current = null; }
    }
  }

  // "Start a New Pairing" on the result screen. Deliberately NOT the home screen's
  // Start Over: that is hardReload(), a full reset for the next guest, and it
  // empties My Menu. A guest here wants to pair their next course or a friend's
  // dish, so this clears the selection and the answer and keeps My Menu, which
  // already holds the dishes and wines they starred. The 10-minute idle
  // hardReload still resets the tablet for the next table.
  function startNewPairing() {
    setSelectedFoods([]);
    setPairingResult(null);
    pendingPairing.current = null;
    setPairingLoading(false);
    setSplitNote(null);
    setByDishAlt(null);
    setColourNote(null);
    setColourAlt(null);
    setResultMessage(null);
    setLastShownIds({});
    setPairChoice(null);
    setActiveCourse("All");
    setView("pick");
    window.scrollTo({ top: 0, behavior: "auto" });
  }

  const availableFood = foodItems.filter(f => !f.excluded);
  const chipSortMap = {};
  availableFood.forEach(f => {
    if (chipSortMap[f.course] === undefined) chipSortMap[f.course] = f.menuSortOrder ?? 999;
    else chipSortMap[f.course] = Math.min(chipSortMap[f.course], f.menuSortOrder ?? 999);
  });
  const courses = ["All", ...[...new Set(availableFood.map(f => f.course))]
    .sort((a, b) => (chipSortMap[a] ?? 999) - (chipSortMap[b] ?? 999))];
  const filtered = activeCourse === "All" ? availableFood : availableFood.filter(f => f.course === activeCourse);

  return (
    <div style={{ background: t.bgBase, minHeight: "100vh", fontFamily: t.fontSerif, maxWidth: 680, margin: "0 auto" }}>
      <div style={{ background: t.bgBase, padding: "0 20px", position: "sticky", top: "var(--somm-banner-h, 0px)", zIndex: 10 }}>
        <div style={{ padding: "10px 0 10px", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={view === "result" ? () => { setView("pick"); setPairingResult(null); } : onBack}
            style={{ background: "none", border: "none", color: t.accent, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, letterSpacing: "1px", display: "flex", alignItems: "center", gap: 4, padding: 0 }}>
            ‹ <span style={{ textTransform: "uppercase", letterSpacing: "2px" }}>{view === "result" ? tr("pairing.back") : tr("common.mainMenu")}</span>
          </button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ color: t.accent, fontSize: 14, letterSpacing: "4px", textTransform: "uppercase" }}>{tr("pairing.title")}</div>
          </div>
          <div style={{ minWidth: 80, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
            <TextSizeButton />
            {favorites.length > 0 && (
              <button onClick={onShowShortlist} style={{ background: t.accentDim, border: `0.5px solid ${t.accentBorder}`, color: t.accent, padding: "4px 10px", borderRadius: 12, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 11 }}>
                ★ {favorites.length}
              </button>
            )}
          </div>
        </div>
        <div style={{ height: "0.5px", background: `linear-gradient(90deg, transparent, ${t.accent}44, transparent)`, marginBottom: 10 }} />
        {view === "pick" && (
          <>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {courses.map(c => (
              <button key={c} onClick={() => setActiveCourse(c)} style={{
                background: activeCourse === c ? t.accent : t.white08,
                border: `0.5px solid ${activeCourse === c ? t.accent : t.borderMid}`,
                color: activeCourse === c ? t.bgBase : t.accent,
                fontSize: Math.round(11 * chipScale * 2) / 2, padding: "5px 13px", borderRadius: 20, cursor: "pointer",
                fontFamily: t.fontSerif, whiteSpace: "nowrap", fontWeight: activeCourse === c ? 600 : 400
              }}>{c === "All" ? tr("pairing.all") : sectionLabel(c)}</button>
            ))}
          </div>
          {/* Instructions are for the empty screen. Once a dish is chosen the
              guest knows what to do, and this was a second sticky band eating
              the list from the top while the choice panel ate it from below. */}
          {selectedFoods.length === 0 && (
          <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorderSm}`, borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
            <div style={{ color: t.textPrimary, fontSize: sz(13), fontFamily: t.fontSerif, fontWeight: 500, marginBottom: 3 }}>
              {tr("pairing.instructions")}
            </div>
            <div style={{ color: t.textSecondary, fontSize: sz(11), fontStyle: "italic", lineHeight: 1.5 }}>
              {tr("pairing.limit")}
            </div>
          </div>
          )}
          </>
        )}
      </div>

      {view === "pick" && (
        <>
        <div>

          <div style={{ background: t.bgSurface }}>
            {loadingFood ? (
              <div style={{ color: t.textSecondary, textAlign: "center", padding: 40 }}>{tr("pairing.loadingMenu")}</div>
            ) : (() => {
              const courseSortMap2 = {};
              filtered.forEach(f => {
                if (courseSortMap2[f.course] === undefined) courseSortMap2[f.course] = f.menuSortOrder ?? 999;
                else courseSortMap2[f.course] = Math.min(courseSortMap2[f.course], f.menuSortOrder ?? 999);
              });
              const courseOrder = [...new Map(filtered.map(f => [f.course, true])).keys()]
                .sort((a, b) => (courseSortMap2[a] ?? 999) - (courseSortMap2[b] ?? 999));
              const byCourse = {};
              filtered.forEach(f => { if (!byCourse[f.course]) byCourse[f.course] = []; byCourse[f.course].push(f); });
              return courseOrder.map(course => (
                <div key={course}>
                  <div style={{ padding: "8px 16px 6px 20px", background: t.courseBandBg, display: "flex", alignItems: "center" }}>
                    <div style={{ flex: 1, color: t.onDeepText, fontSize: sz(9), letterSpacing: "3px", textTransform: "uppercase", fontWeight: 600 }}>{sectionLabel(course)}</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <div style={{ width: 22, textAlign: "center", color: t.onDeepTextDim, fontSize: 8, letterSpacing: "1px", textTransform: "uppercase", visibility: courseKind(course) === "open" ? "visible" : "hidden" }}>{tr("pairing.column.first")}</div>
                      <div style={{ width: 22, textAlign: "center", color: t.onDeepTextDim, fontSize: 8, letterSpacing: "1px", textTransform: "uppercase", visibility: courseKind(course) !== "dessert" ? "visible" : "hidden" }}>{tr("pairing.column.main")}</div>
                      <div style={{ width: 22, textAlign: "center", color: t.onDeepText, fontSize: 8, letterSpacing: "1px", textTransform: "uppercase", visibility: courseKind(course) === "dessert" ? "visible" : "hidden" }}>{tr("pairing.column.dessert")}</div>
                    </div>
                  </div>
                  {byCourse[course].map(food => {
                    const isEntree = courseKind(food.course) === "entree";
                    const isDessert = courseKind(food.course) === "dessert";
                    const chkFirst   = selectedFoods.some(f => f.id === food.id && f.courseRole === "first");
                    const chkMain    = selectedFoods.some(f => f.id === food.id && f.courseRole === "main");
                    const chkDessert = selectedFoods.some(f => f.id === food.id && f.courseRole === "dessert");
                    const anySelected = chkFirst || chkMain || chkDessert;
                    // Enforce max 4 per course
                    const firstCount   = selectedFoods.filter(f => f.courseRole === "first").length;
                    const mainCount    = selectedFoods.filter(f => f.courseRole === "main").length;
                    const dessertCount = selectedFoods.filter(f => f.courseRole === "dessert").length;

                    const Chk = ({ checked, role, disabled }) => (
                      <div onClick={disabled ? null : () => handleFoodToggle(food, role)}
                        style={{ width: 22, height: 22, borderRadius: 5, border: `1.5px solid ${checked ? t.accent : disabled ? "transparent" : t.textSecondary}`, background: checked ? t.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: disabled ? "default" : "pointer", flexShrink: 0, transition: "all 0.15s" }}>
                        {checked && <span style={{ color: t.accentText, fontSize: 11, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                      </div>
                    );

                    return (
                          <div key={food.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 16px 11px 20px", borderBottom: `0.5px solid ${t.borderSubtle}`, background: anySelected ? t.accentDimSm : t.bgSurface, transition: "background 0.15s" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: t.textPrimary, fontSize: sz(13), fontWeight: 500, marginBottom: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{food.name}</div>
                              <OrderAs item={food} size={sz(10.5)} />
                              {food.description && <div style={{ color: t.textSecondary, fontSize: sz(11), lineHeight: 1.3 }}>{food.description}</div>}
                            </div>
                            <div style={{ color: t.textSecondary, fontSize: sz(12), flexShrink: 0, marginRight: 4 }}>{formatPrice(food.price)}</div>
                            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                              <Chk checked={chkFirst}   role="first"   disabled={isEntree || isDessert || (!chkFirst && firstCount >= 4)} />
                              <Chk checked={chkMain}    role="main"    disabled={isDessert || (!chkMain && mainCount >= 4)} />
                              <Chk checked={chkDessert} role="dessert" disabled={!isDessert || (!chkDessert && dessertCount >= 4)} />
                            </div>
                          </div>
                    );
                  })}
                </div>
              ));
            })()}
            {!loadingFood && filtered.length > 0 && <DietaryNote text={tr("pairing.dietaryNote")} size={sz(11)} />}
          </div>
        </div>

        {/* Sticky Find Pairings button */}
        {selectedFoods.length > 0 && (
          <div style={{ position: "sticky", bottom: 0, background: t.bgBase, borderTop: `0.5px solid ${t.borderMid}`, padding: shortScreen ? "8px 16px 10px" : "12px 20px 16px" }}>
            {/* Short screens: one row of chips that scrolls sideways, so four
                dishes never stack into three rows. */}
            <div style={{ display: "flex", gap: 6, flexWrap: shortScreen ? "nowrap" : "wrap", overflowX: shortScreen ? "auto" : "visible", marginBottom: shortScreen ? 8 : 10, paddingBottom: shortScreen ? 2 : 0 }}>
              {selectedFoods.map(f => (
                <div key={f.id} style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorder}`, borderRadius: 14, padding: "4px 8px 4px 10px", display: "flex", alignItems: "center", gap: 6, flexShrink: 0, whiteSpace: "nowrap" }}>
                  <span style={{ color: t.textPrimary, fontSize: 11 }}>{f.name}</span>
                  {f.courseRole !== "main" && <span style={{ color: t.textSecondary, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.5px" }}>{f.courseRole === "first" ? tr("pairing.chip.first") : tr("pairing.chip.dessert")}</span>}
                  <span onClick={e => { e.stopPropagation(); handleFoodToggle(f, f.courseRole); }} style={{ color: t.textDim, fontSize: 16, cursor: "pointer", lineHeight: 1 }}>×</span>
                </div>
              ))}
            </div>
            {/* The three instant choices are peers: one row, one style, filled - they
                are the fast, cheap answer and should read as the main action. Ask
                the Sommelier is a different kind of thing (a conversation), so it
                sits apart, full width, outlined in the scheme's second colour. */}
            {/* Always shown: without it the three buttons do not say what they are
                for. Compact on short screens (decided Oct 30, 2026; it had been
                hidden below 900 px since Oct 29). */}
            <div style={{ color: t.textPrimary, fontSize: shortScreen ? sz(12) : sz(13), textAlign: "center", marginBottom: shortScreen ? 5 : 8, lineHeight: 1.3 }}>
              {tr("pairing.howToPair", { count: selectedFoods.length })}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${pairChoices.length}, 1fr)`, gap: 8 }}>
              {pairChoices.map(c => (
                <button key={c.key} onClick={() => handleGetPairings(c.key)}
                  style={{ background: t.accent, color: t.accentText, border: "none", padding: shortScreen ? "8px 6px" : "10px 6px", minHeight: shortScreen ? 42 : 48, borderRadius: 8, cursor: "pointer", fontFamily: t.fontSerif, fontSize: sz(13), fontWeight: 600, lineHeight: 1.25, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {c.label}
                </button>
              ))}
            </div>
            {chatEnabled && (<>
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: shortScreen ? "6px 0" : "10px 0" }}>
              <div style={{ flex: 1, height: "0.5px", background: t.borderMid }} />
              <span style={{ color: t.textSecondary, fontSize: sz(11), fontStyle: "italic" }}>{tr("pairing.or")}</span>
              <div style={{ flex: 1, height: "0.5px", background: t.borderMid }} />
            </div>
            {/* The subtitle says what the chat is FOR, so guests with a plain
                pairing question take the instant buttons above (cheaper), and a
                guest who wants a spirit or cocktail learns the chat covers it.
                Written in the sommelier's own voice, as an invitation. Quoted
                examples ("something like my favorite wine") read as if the app
                already knew the guest's favorite. */}
            <button onClick={() => openChat(selectedFoods, "pairing_choice")}
              style={{ width: "100%", background: t.accentSecondaryDim || t.accentDimSm, border: `1px solid ${t.accentSecondary || t.accent}`, color: t.accentSecondary || t.accent, padding: shortScreen ? "8px 8px" : "10px 8px", borderRadius: 8, cursor: "pointer", fontFamily: t.fontSerif, fontSize: sz(13), lineHeight: 1.3, textAlign: "center" }}>
              {tr("pairing.ask")}
              {/* Follows the guest's Aa size like the rest of the screen, and a
                  half-step larger at base - at 11px it was hard to read on a phone.
                  Tall screens only: in French it wraps to three lines, and on a
                  short window those lines came straight out of the dish list. */}
              {!shortScreen && (
                <span style={{ display: "block", color: t.textSecondary, fontSize: sz(12), fontStyle: "italic", marginTop: 2, lineHeight: 1.35 }}>
                  {tr("pairing.askSubtitle")}
                </span>
              )}
            </button>
            </>)}
          </div>
        )}
        {chatEnabled && selectedFoods.length === 0 && (
          <div style={{ position: "sticky", bottom: 0, background: t.bgBase, borderTop: `0.5px solid ${t.borderMid}`, padding: "12px 20px 14px", textAlign: "center" }}>
            <button onClick={() => openChat([], "pairing_screen")}
              style={{ background: t.accentSecondaryDim || t.accentDimSm, border: `1px solid ${t.accentSecondary || t.accent}`, color: t.accentSecondary || t.accent, padding: "11px 28px", borderRadius: 8, cursor: "pointer", fontFamily: t.fontSerif, fontSize: sz(13) }}>
              {tr("pairing.ask")}
            </button>
          </div>
        )}
        </>
      )}

      {view === "result" && (
        <div style={{ padding: "20px 20px", background: t.bgBase }}>
          {/* The guest's dishes stay at the top once the answer arrives, not just
              while it loads. They used to vanish with the loading messages, and a
              guest scrolling wine cards had nothing on screen to say what the wines
              were for. Compact after the answer: it is a reminder, not the content. */}
          {selectedFoods.length > 0 && (() => {
            const roleOrder = ["first", "main", "dessert"];
            const grouped = {};
            selectedFoods.forEach(f => {
              const r = f.courseRole || "main";
              if (!grouped[r]) grouped[r] = [];
              grouped[r].push(f);
            });
            const courses = roleOrder.filter(r => grouped[r]);
            return (
              <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorderSm}`, borderRadius: 8, padding: pairingLoading ? "14px 16px" : "10px 14px", marginBottom: pairingLoading ? 20 : 14 }}>
                <div style={{ color: t.textSecondary, fontSize: sz(9), letterSpacing: "2px", textTransform: "uppercase", marginBottom: pairingLoading ? 12 : 8 }}>
                  {pairingLoading ? tr("pairing.selections") : (choiceHeading[pairChoice] || tr("pairing.heading.fallback"))}
                </div>
                {courses.map((role, ci) => (
                  <div key={role} style={{ marginBottom: ci < courses.length - 1 ? 14 : 0 }}>
                    <div style={{ color: t.accent, fontSize: sz(9), letterSpacing: "2px", textTransform: "uppercase", marginBottom: 6 }}>{tr(`pairing.course.${role}`)}</div>
                    {grouped[role].map((f, i) => (
                      <div key={`${f.id}-${role}`} style={{ marginBottom: i < grouped[role].length - 1 ? 6 : 0, paddingLeft: 10, borderLeft: `1.5px solid ${t.accentBorderSm}` }}>
                        <div style={{ color: t.textPrimary, fontSize: sz(13) }}>{f.name}</div>
                        <OrderAs item={f} size={sz(10.5)} />
                        {f.description && pairingLoading && <div style={{ color: t.textSecondary, fontSize: sz(11), fontStyle: "italic" }}>{f.description}</div>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })()}

          {pairingLoading && (
            <LoadingMessages messages={SOMMELIER_MESSAGES} onAllShown={handleMessagesComplete} />
          )}

          {(() => {
            const pairings = Array.isArray(pairingResult) ? pairingResult : null;
            const byCourse = pairingResult?.byCourse || null;
            const hasResults = pairings?.length > 0 || byCourse?.length > 0;
            const isEmpty = pairingResult && !pairingLoading && !hasResults;
            // Without this line a split answer looks like the app failed to pick
            // one wine, rather than deciding the table needs two.
            const SplitNote = splitNote && hasResults ? (
              <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accent}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                <div style={{ color: t.accent, fontSize: sz(12.5), lineHeight: 1.55, fontStyle: "italic" }}>{splitNote}</div>
              </div>
            ) : null;
            // Above the wines, not below: when the guest's colour is a stretch for
            // the dish, they should know before reading three cards, not after.
            //
            // The suggested wine sits INSIDE the note, compact, rather than as a
            // fourth card among the guest's colour: they asked for reds, and a
            // sparkling card in that list reads as the app ignoring them. Here it
            // is visibly a side suggestion that can still be acted on in one tap.
            const altObj = colourAlt ? { id: colourAlt.id, name: colourAlt.name, varietal: colourAlt.varietal, region: colourAlt.region, glassPrice: colourAlt.glassPrice, bottlePrice: colourAlt.bottlePrice, imageUrl: colourAlt.imageUrl || null, reason: null, level: colourAlt.level || null, courseLabel: null, fromPairing: true } : null;
            const altStarred = !!colourAlt && favorites.some(f => f.id === colourAlt.id);
            const toggleAlt = () => {
              if (!altObj) return;
              // Counted on adding only. Fire-and-forget through the pairing endpoint
              // (see the colourAltAdded branch in getMatrixPairing for why not a new
              // function); a lost count never touches the guest.
              if (!altStarred) {
                rfetch(MATRIX_PAIRING_URL, {
                  method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ event: "colourAltAdded", foodIds: selectedFoods.map(f => f.id) }),
                }).catch(() => {});
              }
              onToggleFavorite(altObj);
            };
            const ColourNote = colourNote && hasResults ? (
              <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accent}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                <div style={{ color: t.textPrimary, fontSize: sz(12.5), lineHeight: 1.55, fontStyle: "italic" }}>{colourNote}</div>
                {colourAlt && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, paddingTop: 10, borderTop: `0.5px solid ${t.accentBorder}` }}>
                    {colourAlt.imageUrl && (
                      <div onClick={() => setZoomedLabel({ name: colourAlt.name, varietal: colourAlt.varietal, region: colourAlt.region, imageUrl: colourAlt.imageUrl, imageDisclaimer: colourAlt.imageDisclaimer })}
                        style={{ width: 34, height: 48, borderRadius: 3, flexShrink: 0, overflow: "hidden", border: `0.5px solid ${t.accent}`, cursor: "zoom-in" }}>
                        <img src={colourAlt.imageUrl} alt={colourAlt.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: t.textPrimary, fontSize: sz(13.5) }}>{colourAlt.name}</div>
                      <div style={{ color: t.textSecondary, fontSize: sz(11.5), marginTop: 1 }}>
                        {[colourAlt.glassPrice && tr("pairing.priceGlass", { price: formatPrice(colourAlt.glassPrice) }), colourAlt.bottlePrice && tr("pairing.priceBottle", { price: formatPrice(colourAlt.bottlePrice) })].filter(Boolean).join(" \u00b7 ")}
                      </div>
                    </div>
                    <button onClick={toggleAlt}
                      style={{ flexShrink: 0, background: altStarred ? t.accentDim : t.bgSurface, border: `1px solid ${t.accent}`, color: t.accent, padding: "7px 12px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: sz(12), whiteSpace: "nowrap" }}>
                      {altStarred ? tr("common.added") : tr("common.addToMyMenu")}
                    </button>
                  </div>
                )}
              </div>
            ) : null;

            const WineCard = (p, i) => (
              <div key={i} style={{ background: t.white06, border: `0.5px solid ${t.accentBorder}`, borderRadius: 10, padding: "16px", marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                  <div style={{ background: t.accentDim, border: `0.5px solid ${t.accentBorder}`, borderRadius: 12, padding: "3px 10px" }}>
                    <span style={{ color: t.accent, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase" }}>{levelLabel(p.level)}</span>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {p.glassPrice && <div style={{ color: t.textPrimary, fontSize: sz(13) }}>{formatPrice(p.glassPrice)} <span style={{ color: t.accent, fontSize: sz(10) }}>{tr("common.glass")}</span></div>}
                    {p.bottlePrice && <div style={{ color: t.textPrimary, fontSize: sz(13) }}>{formatPrice(p.bottlePrice)} <span style={{ color: t.accent, fontSize: sz(10) }}>{tr("common.bottle")}</span></div>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
                  {p.imageUrl && <div onClick={() => setZoomedLabel({ name: p.name, varietal: p.varietal, region: p.region, imageUrl: p.imageUrl })} style={{ width: 52, height: 72, borderRadius: 4, flexShrink: 0, overflow: "hidden", border: `0.5px solid ${t.accent}`, cursor: "zoom-in" }}><img src={p.imageUrl} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: t.textPrimary, fontSize: sz(15), marginBottom: 4 }}>{p.name}</div>
                    {(p.varietal || p.region) && <div style={{ color: t.accentSecondary, fontSize: sz(10), letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>{[p.varietal, p.region].filter(Boolean).join(" · ")}</div>}
                  </div>
                </div>
                <div style={{ color: t.textSecondary, fontSize: sz(13), fontStyle: "italic", lineHeight: 1.6 }}>{p.reason}</div>
                {p.id && (() => {
                  const wineObj = { id: p.id, name: p.name, varietal: p.varietal, region: p.region, glassPrice: p.glassPrice, bottlePrice: p.bottlePrice, imageUrl: p.imageUrl || null, reason: p.reason || null, level: p.level || null, courseLabel: p.courseLabel || null, fromPairing: true };
                  const isStarred = favorites.some(f => f.id === p.id);
                  return <button onClick={() => onToggleFavorite(wineObj)} style={{ marginTop: 10, background: isStarred ? t.accentDim : t.accentDimSm, border: `1px solid ${isStarred ? t.accent : t.accent}`, color: t.accent, padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 12, display: "flex", alignItems: "center", gap: 6, width: "100%", justifyContent: "center" }}>{isStarred ? tr("common.addedToMyMenu") : tr("common.addToMyMenu")}</button>;
                })()}
              </div>
            );

            return (
              <>
                {/* Single course */}
                {ColourNote}
                {SplitNote}
                {pairings?.map((p, i) => WineCard(p, i))}

                {/* One wine for the whole table, then a wine for each dish. Same
                    group treatment as a multi-course answer so it reads as a
                    second option rather than more of the first. */}
                {pairings?.length > 0 && byDishAlt && (
                  <div style={{ marginTop: 22 }}>
                    <div style={{ color: t.accent, fontSize: sz(13), letterSpacing: "2px", textTransform: "uppercase", fontWeight: 600, marginBottom: 12, textAlign: "center" }}>
                      {tr("pairing.eachDish")}
                    </div>
                    {byDishAlt.map((courseResult, ci) => (
                      <div key={`alt-${ci}`} style={{ marginBottom: 18 }}>
                        <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorder}`, borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
                          <div style={{ color: t.accent, fontSize: sz(12.5), letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 600 }}>✦ {courseHeading(courseResult.course)}</div>
                        </div>
                        {courseResult.pairings?.map((p, i) => WineCard(p, `alt-${ci}-${i}`))}
                      </div>
                    ))}
                  </div>
                )}

                {/* Multi-course */}
                {byCourse?.map((courseResult, ci) => (
                  <div key={ci} style={{ marginBottom: 24 }}>
                    <div style={{ background: t.accentDimSm, border: `0.5px solid ${t.accentBorder}`, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                      <div style={{ color: t.accent, fontSize: sz(13), letterSpacing: "2px", textTransform: "uppercase", fontWeight: 600, marginBottom: courseResult.dishes?.length > 0 ? 6 : 0 }}>
                        ✦ {courseHeading(courseResult.course)}
                      </div>
                      {courseResult.dishes?.length > 0 && (
                        <div style={{ color: t.textSecondary, fontSize: sz(12), fontStyle: "italic" }}>
                          {courseResult.dishes.join(" · ")}
                        </div>
                      )}
                    </div>
                    {courseResult.pairings?.map((p, i) => WineCard(p, `${ci}-${i}`))}
                  </div>
                ))}

                {!pairingLoading && hasResults && (
                  <div style={{ marginBottom: 8 }} />
                )}

                {isEmpty && <div style={{ color: t.textSecondary, textAlign: "center", padding: "40px 0", fontSize: 14, lineHeight: 1.5 }}>{resultMessage || tr("pairing.noneFound")}</div>}
              </>
            );
          })()}

          {!pairingLoading && pairingResult && (
            <div style={{ position: "sticky", bottom: 0, background: t.bgBase, borderTop: `0.5px solid ${t.borderMid}`, padding: "12px 20px 16px", marginTop: 8 }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                {chatEnabled && (
                <button onClick={() => openChat(selectedFoods, "after_pairing")} style={{ flex: 1, background: t.accentSecondaryDim || t.accentDimSm, border: `1px solid ${t.accentSecondary || t.accent}`, color: t.accentSecondary || t.accent, padding: "11px 8px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif, lineHeight: 1.3, textAlign: "center" }}>
                  {tr("pairing.askLine1")}<br/>{tr("pairing.askLine2")}
                </button>
                )}
                <button onClick={handleDifferentOptions} style={{ flex: 1, background: t.accentDimSm, border: `0.5px solid ${t.accentBorder}`, color: t.accent, padding: "11px 8px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: t.fontSerif, lineHeight: 1.3, textAlign: "center" }}>
                  {tr("pairing.differentLine1")}<br/>{tr("pairing.differentLine2")}
                </button>
              </div>
              <button onClick={onShowShortlist}
                style={{ width: "100%", background: t.accent, color: t.accentText, border: "none", padding: "13px", borderRadius: 8, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 14, fontWeight: 600, letterSpacing: "0.5px", marginBottom: 6 }}>
                {tr("pairing.goToMyMenu")}
              </button>
              <div style={{ color: t.textSecondary, fontSize: 11, textAlign: "center", fontStyle: "italic", lineHeight: 1.5 }}>
                {tr("pairing.repeatNote")}
              </div>
              {/* Same quiet footer-link treatment as the home screen's Start Over,
                  but a different action - see startNewPairing(). */}
              <div style={{ textAlign: "center", marginTop: 10 }}>
                <button onClick={startNewPairing}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 8px", color: t.textSecondary, fontSize: sz(11), letterSpacing: "1.5px", textTransform: "uppercase", fontFamily: t.fontSerif, textDecoration: "underline", textDecorationColor: t.borderStrong }}>
                  {tr("pairing.startNew")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <LabelModal wine={zoomedLabel} onClose={() => setZoomedLabel(null)} />
      {chatEnabled && <SommelierChat isOpen={chatOpen} onClose={(added) => {
          setChatOpen(false);
          if (added) {
            // The chat can now suggest DISHES too ("what food do you recommend?").
            // Those are not drinks, so the "N dishes and M wines" modal counts
            // only what was poured; a dish-only add gets the plain toast.
            const drinksAdded = added.filter(a => a.type !== "food");
            if (selectedFoods.length > 0 && drinksAdded.length > 0) {
              // Guest has food selected + just added wines — strong signal they're done
              // "drinks" rather than "wines" once anything else is in the set - the
              // modal told a guest who added a tequila that he had added a wine.
              setDoneModal({ wineCount: drinksAdded.length, allWine: drinksAdded.every(a => a.type === "wine") });
            } else {
              setToast(added);
            }
          }
        }} contextItem={null} selectedFoods={chatFoods} entry={chatEntry} shownWines={shownPairingWines} favorites={favorites} onToggleFavorite={onToggleFavorite} tabletLocation={tabletLocation} locationNames={locationNames} />}
      {toast && <SommelierToast items={toast} onViewMenu={() => { setToast(null); onShowShortlist(); }} onDismiss={() => setToast(null)} />}
      {doneModal && (
        <SommelierDoneModal
          foodCount={selectedFoods.length}
          wineCount={doneModal.wineCount}
          allWine={doneModal.allWine}
          onViewMenu={() => { setDoneModal(null); onShowShortlist(); }}
          onKeepBrowsing={() => setDoneModal(null)}
        />
      )}
    </div>
  );
}

// ─── Small line icons for the welcome overlay (hand-drawn inline SVG — no
// icon library dependency, just enough to match the cleaner mockup style) ──
function WineGlassIcon({ size = 16, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 3h10a9 9 0 0 1-2.8 7.2A4 4 0 0 1 12 11a4 4 0 0 1-2.2-.8A9 9 0 0 1 7 3z" />
      <path d="M12 11v8" />
      <path d="M8.5 21h7" />
    </svg>
  );
}
function ChatIcon({ size = 16, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}
function ScanIcon({ size = 16, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
    </svg>
  );
}

// A native `title` tooltip is invisible on touch, delayed on desktop and looks
// different per browser - not something to rely on for "this button makes the
// whole widget disappear," which deserves to be seen immediately. This renders
// its own small bubble above the × on hover, identical wherever it's used, so
// the two dismiss buttons (standalone next to the pill, and inside the open
// panel) read as exactly the same control with exactly the same warning.
function DismissX({ onDismiss, extraStyle = {} }) {
  const t = useTheme();
  const [hover, setHover] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-flex", flexShrink: 0, marginLeft: 8 }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button onClick={onDismiss} aria-label={tr("demo.theme.dismissAria")} style={{
        background: t.bgSurface, border: `1px solid ${t.borderMid}`, color: t.textDim,
        borderRadius: "50%", width: 26, height: 26, flexShrink: 0, cursor: "pointer",
        fontSize: 14, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
        ...extraStyle,
      }}>×</button>
      {hover && (
        <div style={{
          position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
          background: t.bgSurface, border: `1px solid ${t.borderMid}`, color: t.textSecondary,
          padding: "6px 10px", borderRadius: 6, fontSize: 10, whiteSpace: "nowrap",
          boxShadow: `0 4px 16px ${t.shadow}`, pointerEvents: "none", fontFamily: t.fontSerif,
        }}>
          {tr("demo.theme.dismissTip")}
        </div>
      )}
    </div>
  );
}

// Sits inside the open panel's header. Deliberately NOT an × - that glyph is
// reserved for DismissX's "gone for the rest of the session" action, and the
// two buttons sit close enough together (one inside the panel, one just
// outside it) that sharing an icon invited exactly the confusion this was
// built to avoid: a minus/collapse glyph here reads as "tuck this away,"
// while × reads as "get rid of it," matching what each one actually does.
function CollapseX({ onCollapse }) {
  const t = useTheme();
  const [hover, setHover] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button onClick={onCollapse} aria-label={tr("demo.theme.collapseAria")} style={{
        background: "none", border: "none", color: t.textDim, fontSize: 16,
        cursor: "pointer", lineHeight: 1, padding: 0, flexShrink: 0, marginLeft: 8,
      }}>−</button>
      {hover && (
        <div style={{
          position: "absolute", bottom: "calc(100% + 8px)", right: 0,
          background: t.bgSurface, border: `1px solid ${t.borderMid}`, color: t.textSecondary,
          padding: "6px 10px", borderRadius: 6, fontSize: 10, whiteSpace: "nowrap",
          boxShadow: `0 4px 16px ${t.shadow}`, pointerEvents: "none", fontFamily: t.fontSerif,
        }}>
          {tr("demo.theme.collapseTip")}
        </div>
      )}
    </div>
  );
}

// ─── Demo Theme Switcher ──────────────────────────────────────────────────────
// Public, no PIN, no owner login - deliberately the opposite of how a real
// restaurant picks its colours (Manager → Settings → Device Setup, behind
// PinScreen). This exists ONLY for the shared public demo tenant, gated by the
// caller on RESTAURANT_ID === 'demo', so a prospect can flip through the whole
// palette on their own without anyone handing them a login. It never writes
// anything to the server - purely a local override of which theme renders,
// forgotten on reload - so it can't be mistaken for changing a real tenant's
// configured colours, and a real restaurant's guests never see it at all.
// Theme names follow the guest's language (demo.theme.names.*); a theme added to
// THEME_CATALOGUE without a name there shows its catalogue label.
function themeLabel(th) {
  if (!th) return "";
  const key = `demo.theme.names.${th.value}`;
  const out = tr(key);
  return out === key ? th.label : out;
}

function DemoThemeSwitcher({ index, defaultIndex = 0, onChange, onDismiss = () => {}, floating = false }) {
  const t = useTheme();
  const [open, setOpen] = useState(false);
  // Browsing is separate from what's actually applied. Cycling ‹ › only moves
  // this draft pointer and updates the swatch/name below - the live page does
  // NOT recolor until Apply is pressed, on request (an earlier version
  // recolored on every arrow tap; this one asks for a deliberate confirm).
  const [draftIndex, setDraftIndex] = useState(index);
  const [justApplied, setJustApplied] = useState(false);

  // Re-sync the draft to whatever is actually live every time the panel is
  // opened, so browsing always starts from the current colour rather than
  // wherever a previous, uncommitted browse happened to leave off.
  function handleOpen() {
    setDraftIndex(index);
    setJustApplied(false);
    setOpen(true);
  }

  function step(dir) {
    setJustApplied(false);
    setDraftIndex(prev => (prev + dir + THEME_CATALOGUE.length) % THEME_CATALOGUE.length);
  }

  function handleApply() {
    onChange(draftIndex);
    setJustApplied(true);
    // Brief confirmation, then collapse back to the pill - the choice is made,
    // there's nothing left to do here, and lingering open reads as if it's
    // still waiting on something.
    setTimeout(() => setOpen(false), 550);
  }

  // A visitor who has tried a few colours often decides the default was best.
  // This puts them straight back on the demo's own theme (defaultIndex, passed
  // from AppContent as the tenant's configured theme, so it follows if that is
  // ever changed) without making them find it in the list. Applies at once -
  // unlike browsing, it is an explicit "go back" - and leaves the panel open so
  // they can keep exploring.
  function handleReset() {
    onChange(defaultIndex);
    setDraftIndex(defaultIndex);
    setJustApplied(false);
  }

  const draft = THEME_CATALOGUE[draftIndex] || THEME_CATALOGUE[0];
  // A swatch chip so browsing isn't just text - resolves the draft theme's own
  // colours (not the currently active ones) directly from THEMES, independent
  // of what's actually rendered right now.
  const draftTheme = getTheme(draft.value);
  const isCurrent = draftIndex === index;

  // Home shows this inline, centered, above the logo - it reads as part of the
  // page there. Every other screen (Wine List, Beer List, etc.) has no logo
  // block to sit above, so it reverts to the original fixed bottom-right
  // corner instead, staying reachable without following the guest down the
  // page as they scroll a long list.
  const floatingStyle = floating
    ? { position: "fixed", right: 16, bottom: 16, zIndex: Z.demoThemeSwitcher }
    : {};

  if (!open) {
    // Inline on Home: the pill is centred exactly under the language picker. The dismiss
    // button would otherwise count towards the pair's width and pull the pill left, so it
    // gets a negative right margin equal to its own footprint (6 gap + 8 margin + 26 button).
    // The pill is capped at the SCREEN width minus 96px so the button always stays on screen
    // (it overhangs into the page's side padding); a longer translation wraps to two lines
    // instead of pushing it off. The cap is viewport-based on purpose: a % of the column
    // measures a wrapper that has already shrunk to fit the pill, and wrapped English text
    // on a 390px phone. Floating (bottom-right, every other screen) is unchanged.
    const X_FOOTPRINT = 40;
    return (
      <div style={floating
        ? { ...floatingStyle, display: "inline-flex", alignItems: "center", gap: 6 }
        : { display: "inline-flex", alignItems: "center", gap: 6 }}>
        {/* The little arrow says "this opens", like the language picker beside it. It is drawn
            with backgroundImage, so the fill is backgroundColor and NOT the `background`
            shorthand: re-setting the shorthand on a live theme change would wipe the arrow's
            size and position and stretch it across the whole pill (see LanguagePicker). */}
        <button onClick={handleOpen} style={{
          backgroundColor: t.bgSurface, border: `1px solid ${t.borderMid}`, color: t.textPrimary,
          borderRadius: 24, padding: "10px 36px 10px 16px", cursor: "pointer",
          fontFamily: t.fontSerif, fontSize: 12, letterSpacing: "0.5px",
          boxShadow: `0 4px 20px ${t.shadow}`, display: "inline-flex", alignItems: "center", gap: 6,
          maxWidth: floating ? undefined : "calc(100vw - 96px)",
          backgroundImage: `linear-gradient(45deg, transparent 50%, ${t.accent} 50%), linear-gradient(135deg, ${t.accent} 50%, transparent 50%)`,
          backgroundPosition: "calc(100% - 18px) 50%, calc(100% - 13px) 50%", backgroundSize: "5px 5px, 5px 5px", backgroundRepeat: "no-repeat",
        }}>
          {tr("demo.theme.pill")}
        </button>
        {/* Permanent, session-long dismiss - separate from the pill itself so
            tapping it never opens the panel by accident. Once tapped, this
            whole widget is gone for the rest of the session (see
            demoSwitcherDismissed in AppContent), for a guest who is happy
            with their pick and does not want a persistent reminder. */}
        <span style={floating ? { display: "inline-flex" } : { display: "inline-flex", marginRight: -X_FOOTPRINT }}>
          <DismissX onDismiss={onDismiss} extraStyle={{ boxShadow: `0 4px 20px ${t.shadow}` }} />
        </span>
      </div>
    );
  }

  return (
    <div style={{ ...(floating ? floatingStyle : { textAlign: "center" }), display: "inline-flex", alignItems: "flex-start", gap: 6 }}>
      <div style={{
        display: "inline-block", textAlign: "left",
        background: t.bgSurface, border: `1px solid ${t.borderMid}`, borderRadius: 14,
        padding: "12px 14px", boxShadow: `0 8px 32px ${t.shadowStrong}`,
        fontFamily: t.fontSerif, width: 220, boxSizing: "border-box",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ color: t.accent, fontSize: 9, letterSpacing: "2px", textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{tr("demo.theme.title")}</div>
        {/* Collapses back to the pill only - the standalone bubble outside this
            panel (below) is the one that permanently dismisses. These used to
            share the × glyph; now they're visually distinct (− vs ×) with
            their own hover tooltips, so which is which doesn't have to be
            guessed from position alone. */}
        <CollapseX onCollapse={() => setOpen(false)} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <button onClick={() => step(-1)} style={{ background: t.white06, border: `1px solid ${t.borderMid}`, color: t.textPrimary, borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 14, flexShrink: 0 }}>‹</button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {/* Swatch: a split paint-chip - half the draft theme's own background,
              half its accent colour - so it reads as an actual colour sample
              rather than a status dot, and a guest can gauge the palette
              before committing to it. */}
          <div style={{
            width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
            overflow: "hidden", display: "flex", border: `1px solid ${t.borderMid}`,
          }}>
            <div style={{ width: "50%", height: "100%", background: draftTheme.bgBase }} />
            <div style={{ width: "50%", height: "100%", background: draftTheme.accent }} />
          </div>
          <div style={{ color: t.textPrimary, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{themeLabel(draft)}</div>
        </div>
        <button onClick={() => step(1)} style={{ background: t.white06, border: `1px solid ${t.borderMid}`, color: t.textPrimary, borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 14, flexShrink: 0 }}>›</button>
      </div>
      <div style={{ color: t.textDim, fontSize: 10, textAlign: "center", marginBottom: 10 }}>
        {tr("demo.theme.count", { current: draftIndex + 1, total: THEME_CATALOGUE.length })}
      </div>
      <button onClick={handleApply}
        style={{
          width: "100%", background: isCurrent ? t.white04 : t.accent,
          color: isCurrent ? t.textDim : t.accentText, border: isCurrent ? `1px solid ${t.borderMid}` : "none",
          padding: "9px", borderRadius: 8, cursor: "pointer",
          fontFamily: t.fontSerif, fontSize: 12, fontWeight: 600,
        }}>
        {justApplied && isCurrent ? tr("demo.theme.applied") : isCurrent ? tr("demo.theme.current") : tr("demo.theme.apply", { name: themeLabel(draft) })}
      </button>
      {index === defaultIndex ? (
        <div style={{ color: t.textDim, fontSize: 10, textAlign: "center", marginTop: 8 }}>
          {tr("demo.theme.usingDefault")}
        </div>
      ) : (
        <button onClick={handleReset} style={{ display: "block", width: "100%", background: "none", border: "none", color: t.textSecondary, fontSize: 11, fontFamily: t.fontSerif, textAlign: "center", textDecoration: "underline", cursor: "pointer", marginTop: 8, padding: 0 }}>
          {tr("demo.theme.reset")}
        </button>
      )}
      </div>
      {/* Standalone, permanent dismiss - same control, same position relative
          to the panel as it has relative to the pill, so it reads as the one
          consistent "get rid of this for the session" action either way. */}
      <DismissX onDismiss={onDismiss} extraStyle={floating ? { boxShadow: `0 4px 20px ${t.shadow}` } : {}} />
    </div>
  );
}

// The browser tab shows the restaurant's own name ("The Gilded Fork"), nothing
// else - it used to read "<name> — Beverage Menu", a leftover from when the app
// served one restaurant. getProfile falls back to the restaurant id when no name
// is saved; a slug like "demo" is worse than the generic title in index.html,
// so that case leaves the tab alone.
function setRestaurantTabTitle(name) {
  const n = String(name || '').trim();
  if (n && n.toLowerCase() !== String(RESTAURANT_ID || '').toLowerCase()) document.title = n;
}

// ─── Guest language picker ───────────────────────────────────────────────────
// Each language is written in its own language, never translated, so a guest
// finds theirs whatever is on screen. Codes are our variants (decided Oct 29,
// 2026). Shown on the home screen unless appSettings.showLanguagePicker === false
// (the owner's switch, written by the Settings tab in step 5).
// The language catalogue (Oct 30, 2026). `label` is the language in itself (what
// guests see); `english` is for the manager screens. Same codes as
// TRANSLATION_LANGS in functions/index.js - keep them in step.
const GUEST_LANGUAGES = [
  { code: "en", label: "English", english: "English" },
  { code: "fr-CA", label: "Français", english: "French (Québec)" },
  { code: "es-MX", label: "Español", english: "Spanish (México)" },
  { code: "de", label: "Deutsch", english: "German" },
  { code: "it", label: "Italiano", english: "Italian" },
  { code: "pl", label: "Polski", english: "Polish" },
  { code: "cs", label: "Čeština", english: "Czech" },
  { code: "sk", label: "Slovenčina", english: "Slovak" },
  { code: "hu", label: "Magyar", english: "Hungarian" },
  { code: "ro", label: "Română", english: "Romanian" },
  { code: "ru", label: "Русский", english: "Russian" },
  { code: "uk", label: "Українська", english: "Ukrainian" },
  { code: "el", label: "Ελληνικά", english: "Greek" },
  { code: "ja", label: "日本語", english: "Japanese" },
  { code: "ko", label: "한국어", english: "Korean" },
  { code: "zh-CN", label: "简体中文", english: "Chinese (Simplified)" },
];
const DEFAULT_LANGUAGE_CODES = ["en", "fr-CA", "es-MX"];
// Pricing (decided Oct 30, 2026): three included (the default plus two); extras
// $3/month each, never more than $10/month; at most eight in all. A super-user can
// raise one restaurant's limit (restaurants/{id}/languageLimit, sent by
// getSettings) - the Custom tier.
const INCLUDED_LANGUAGES = 3;
let languageLimit = 8;
function setLanguageLimit(n) {
  const v = Number(n);
  languageLimit = Number.isInteger(v) && v >= 1 && v <= 50 ? v : 8;
}
function extraLanguagePrice(extras) {
  if (extras <= 0) return 0;
  return Math.min(extras * 3, 10);
}

function languageEntry(code) {
  const base = String(code || "en").split("-")[0].toLowerCase();
  return GUEST_LANGUAGES.find(l => l.code === code) || GUEST_LANGUAGES.find(l => l.code.split("-")[0].toLowerCase() === base) || null;
}
// This restaurant's languages (appSettings.languages), default always included,
// in catalogue order. Read into a module variable by applySettings(), like the
// language itself.
function restaurantLanguageCodes(settings) {
  const s = settings || {};
  const listed = Array.isArray(s.languages) ? s.languages : DEFAULT_LANGUAGE_CODES;
  const def = (languageEntry(s.language) || GUEST_LANGUAGES[0]).code;
  const want = new Set([def, ...listed.map(c => (languageEntry(c) || {}).code).filter(Boolean)]);
  // Default first, then catalogue order, cut at the restaurant's limit.
  const others = GUEST_LANGUAGES.filter(l => want.has(l.code) && l.code !== def).map(l => l.code);
  return [def, ...others].slice(0, languageLimit);
}
let enabledLanguageCodes = DEFAULT_LANGUAGE_CODES;
function setEnabledLanguages(settings) {
  enabledLanguageCodes = restaurantLanguageCodes(settings);
}
function enabledLanguages() {
  return GUEST_LANGUAGES.filter(l => enabledLanguageCodes.includes(l.code));
}

// Translation progress (Oct 30, 2026), from restaurants/{id}/translationStatus,
// written by the translation job after every run. Shared by the owner's Language
// card and the super-user admin portal.
function translationProgress(st) {
  if (!st || st.single || !Array.isArray(st.langs)) return null;
  const total = st.total || 0;
  const rows = st.langs.map(code => {
    const n = (st.covered || {})[code] || 0;
    const sec = st.sections && st.sections.total ? ((st.sections.covered || {})[code] || 0) : null;
    return { code, entry: languageEntry(code), done: n, total, complete: n >= total && (sec === null || sec >= st.sections.total), sections: sec };
  });
  const totalCells = rows.length * total || 1;
  const pct = Math.floor(rows.reduce((a, r) => a + Math.min(r.done, total), 0) * 100 / totalCells);
  const ageMin = st.at ? Math.round((Date.now() - st.at) / 60000) : null;
  return { rows, total, pct, pending: st.pending || 0, stuck: st.stuck || 0, review: st.review || 0, ageMin, sectionsTotal: st.sections ? st.sections.total : 0 };
}
function minutesAgoLabel(m) {
  if (m === null || m === undefined) return "";
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

function LanguagePicker({ current, onChange }) {
  const t = useTheme();
  const base = String(current || "en").split("-")[0];
  const langs = enabledLanguages();
  if (langs.length < 2) return null;
  // More than three: a dropdown, which stays one line on a phone.
  if (langs.length > 3) {
    const active = langs.find(l => l.code.split("-")[0] === base) || langs[0];
    return (
      <select aria-label={tr("home.language")} value={active.code}
        onChange={e => { if (e.target.value !== active.code) onChange(e.target.value); }}
        // Same fill, border, radius, padding, text colour and shadow as the demo's
        // "Select a color theme" pill (DemoThemeSwitcher), so the two read as a set.
        //
        // ⚠️ backgroundColor, NOT the `background` shorthand. This element also sets
        // backgroundImage / Position / Size / Repeat for the little arrow. When the
        // theme changes while the page is open (the demo's theme switcher does exactly
        // that) React re-applies only the style props that changed, and re-setting the
        // shorthand silently resets every background longhand to its initial value. The
        // arrow's two gradients then stretch across the whole control: a solid accent
        // pill with the text hidden. A first render looked fine, which is why it hid.
        style={{
          appearance: "none", WebkitAppearance: "none", backgroundColor: t.bgSurface, color: t.textPrimary,
          border: `1px solid ${t.borderMid}`, borderRadius: 24, padding: "10px 36px 10px 16px",
          fontFamily: t.fontSerif, fontSize: 12, letterSpacing: "0.5px", cursor: "pointer",
          boxShadow: `0 4px 20px ${t.shadow}`,
          backgroundImage: `linear-gradient(45deg, transparent 50%, ${t.accent} 50%), linear-gradient(135deg, ${t.accent} 50%, transparent 50%)`,
          backgroundPosition: "calc(100% - 18px) 50%, calc(100% - 13px) 50%", backgroundSize: "5px 5px, 5px 5px", backgroundRepeat: "no-repeat",
        }}>
        {langs.map(l => <option key={l.code} value={l.code} lang={l.code}>{l.label}</option>)}
      </select>
    );
  }
  return (
    <div role="group" aria-label={tr("home.language")}
      style={{ display: "inline-flex", border: `1px solid ${t.borderMid}`, borderRadius: 24, overflow: "hidden", backgroundColor: t.bgSurface, boxShadow: `0 4px 20px ${t.shadow}` }}>
      {langs.map((l, i) => {
        const active = l.code.split("-")[0] === base;
        return (
          <button key={l.code} lang={l.code} aria-pressed={active}
            onClick={() => { if (!active) onChange(l.code); }}
            style={{
              backgroundColor: active ? t.accentDim : "transparent", color: active ? t.accent : t.textSecondary,
              border: "none", borderLeft: i > 0 ? `1px solid ${t.borderMid}` : "none",
              padding: "10px 16px", fontFamily: t.fontSerif, fontSize: 12, letterSpacing: "0.5px",
              cursor: active ? "default" : "pointer", fontWeight: active ? 600 : 400,
            }}>
            {l.label}
          </button>
        );
      })}
    </div>
  );
}

function HomeScreen({ onNavigate, favorites = [], onShowShortlist = () => {}, onAdminTap = () => {}, tabletLocation = "bar", deviceSetup = {}, locationNames = {}, settings = null, manualMenus = [], plan = "premium", showWelcome = true, onSetShowWelcome = () => {}, isPublicDemoTenant = false, demoThemeIndex = 0, demoDefaultIndex = 0, onSetDemoThemeIndex = () => {}, demoSwitcherDismissed = false, onDismissDemoSwitcher = () => {}, guestLang = "en", onSetGuestLang = () => {} }) {
  const t = useTheme();
  // The list screens are capped at 680 and look near-full on a tablet. This
  // screen was capping its button column at 340 inside that same 680 box, so it
  // painted a half-width strip down the middle and read as a phone layout that
  // had been stretched. Above the wide threshold the column widens and the logo
  // scales with the viewport. Height is read too - see the vertical budget below.
  const { wide, mid, height: viewportHeight, width: viewportWidth } = useViewport();
  // Any tablet, small or large. Vertical spacing works from the real viewport
  // height in both bands; only the sizes below differ.
  const tablet = wide || mid;
  // Footer privacy link. The profile is fetched rather than read from `settings`,
  // which carries appSettings and not the restaurant name or the opt-in flag.
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [privacyProfile, setPrivacyProfile] = useState({ name: "", listOptIn: false });
  React.useEffect(() => {
    rfetch(GET_PROFILE_URL).then(r => r.json())
      .then(d => setPrivacyProfile({ name: d.name || "", listOptIn: d.guestListOptIn === true }))
      .catch(() => {});
  }, []);
  // Used as a wordmark when the restaurant has not uploaded a logo yet. Every
  // new tenant starts in that state, so the fallback must not be another
  // restaurant's artwork.
  const [restaurantDisplayName, setRestaurantDisplayName] = useState("");
  // Item counts per category. Null until loaded. The filter below treats null as
  // "show everything", which on its own means a restaurant with empty categories
  // paints the full list and then drops rows as the counts land - the guest sees
  // menus that are not there. So the screen does not paint at all until the
  // counts have resolved; see countsResolved and the fade gate below.
  const [menuCounts, setMenuCounts] = useState(cachedMenuCounts);
  const [countsResolved, setCountsResolved] = useState(cachedMenuCounts !== null);
  useEffect(() => {
    rfetch(GET_PROFILE_URL).then(r => r.json()).then(d => {
      setRestaurantDisplayName(d.name || "");
      setRestaurantTabTitle(d.name);
    }).catch(() => {});
  }, []);
  // Counts, with background retries (2s, 5s, 10s) while the answer is missing
  // or incomplete. Each answer is MERGED into what is known, so a category
  // that was checked once is not forgotten by a later partial reply. Skipped
  // entirely when this session already holds a complete answer.
  useEffect(() => {
    if (cachedMenuCounts) return;
    let cancelled = false, timer = null, known = {};
    const DELAYS = [2000, 5000, 10000];
    function load(attempt) {
      rfetch(MENU_COUNTS_URL)
        .then(r => { if (!r.ok) throw new Error(`getMenuCounts ${r.status}`); return r.json(); })
        .then(d => {
          if (cancelled) return;
          known = { ...known, ...((d && d.counts) || {}) };
          setMenuCounts(known);
          if (MENU_COUNT_KEYS.every(k => k in known)) { cachedMenuCounts = known; return; }
          if (attempt < DELAYS.length) timer = setTimeout(() => load(attempt + 1), DELAYS[attempt]);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < DELAYS.length) timer = setTimeout(() => load(attempt + 1), DELAYS[attempt]);
        })
        // Resolved on BOTH paths. Gating on success alone would leave a guest
        // staring at a blank screen forever whenever the request failed, which is
        // a far worse failure than the flash this exists to remove.
        .finally(() => { if (!cancelled) setCountsResolved(true); });
    }
    load(0);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);
  const [visible, setVisible] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const tapTimer = useRef(null);
  // Manual session reset. The idle reload only fires after 10 minutes of *no*
  // activity, so a tablet in steady use on a busy night can carry one guest's
  // shortlist to the next table. This is the override for that case.
  //
  // No confirmation step. The two-tap version put a decision in front of whoever
  // is holding the tablet at the moment they are least able to attend to it —
  // a server mid-turnover — and the cost of an accidental tap is a guest
  // re-starring a few wines. Reliability beats protecting the shortlist.

  // Welcome / "how this works" overlay — shows by default on every fresh mount
  // of HomeScreen, which currently happens via the 10-min idle reload (see
  // idleTimer in AppContent). Defaulting to true (rather than reading a
  function handleLogoTap() {
    const next = tapCount + 1;
    setTapCount(next);
    if (tapTimer.current) clearTimeout(tapTimer.current);
    if (next >= 5) { setTapCount(0); onAdminTap(); }
    else { tapTimer.current = setTimeout(() => setTapCount(0), 2000); }
  }

  // Two things decide which buttons render: the per-category counts fetched here,
  // and `settings`, which arrives from AppContent on its own clock. Both treat
  // "not yet known" as "show everything", so the screen has to wait for both or
  // rows still appear and then vanish.
  //
  // Safety valve, same shape as the theme gate in AppContent: if a request hangs
  // rather than failing - captive portal, stalled connection - no callback ever
  // runs and the gate would stay shut indefinitely. After 2.5s, paint with
  // whatever is known. A slightly wrong menu beats a blank screen.
  const [gateTimedOut, setGateTimedOut] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setGateTimedOut(true), 2500);
    return () => clearTimeout(timer);
  }, []);
  const menusReady = gateTimedOut || (countsResolved && settings !== null);

  useEffect(() => {
    if (!menusReady) return;
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, [menusReady]);

  useEffect(() => {
    return () => { if (tapTimer.current) clearTimeout(tapTimer.current); };
  }, []);

  // Determine which menu buttons to show based on location assignment in settings
  // Pairing sits first: it is the reason this app exists, and burying it under
  // five list buttons made it read as an afterthought. Premium tier only —
  // base tier never renders it, so the list simply starts with Wine.
  const ALL_BUTTONS = [
    ...(planHasPairing(plan) ? [{ id: "sommelier", label: tr("home.pairing"), eyebrow: tr("home.pairingEyebrow"), menuType: null }] : []),
    { id: "wine",      label: tr("lists.wine.title"),      menuType: "wine" },
    { id: "beer",      label: tr("lists.beer.title"),      menuType: "beer_pours" },
    { id: "pours",     label: tr("lists.pours.title"),     menuType: "beer_pours" },
    { id: "cocktails", label: tr("lists.cocktails.title"), menuType: "cocktails_na" },
    { id: "nab",       label: tr("lists.nab.title"),       menuType: "cocktails_na" },
  ];

  function menuTypeAvailableAtLocation(menuType) {
    // Manual menus (Menus & Items) of this type decide on their own. When there
    // are none, the original Toast-settings logic below runs unchanged.
    const manualOfType = (manualMenus || []).filter(m => m.menuType === menuType);
    if (menuType && manualOfType.length > 0) {
      return [...((settings && settings.menus) || []).filter(m => m.menuType === menuType), ...manualOfType]
        .some(m => { const locs = m.locations || []; return locs.length === 0 || locs.includes(tabletLocation); });
    }
    if (!settings || !settings.menus || !menuType) return true; // no settings yet — show all
    const menusOfType = settings.menus.filter(m => m.menuType === menuType);
    if (menusOfType.length === 0) return false; // type not configured at all
    // Check if any menu of this type is assigned to current location (or all locations)
    return menusOfType.some(m => {
      const locs = m.locations || [];
      return locs.length === 0 || locs.includes(tabletLocation);
    });
  }

  // A category with no items should not appear at all. The location/menu settings
  // check below handles restaurants that have configured which menus show where;
  // the count check handles the rest, including file-import tenants that have no
  // menu configuration and would otherwise show every category.
  // Counts are guest-VISIBLE items (approved and not hidden), so a category
  // whose items are all hidden or unapproved does not get a button.
  //
  // When a category's count is unknown (not loaded yet, or the server could
  // not check it), the cost of guessing wrong is not the same everywhere:
  // hiding the Wine List is the worst outcome, while an empty Beer or N/A
  // button on a restaurant that serves neither is a visible embarrassment. So
  // unknown Wine shows, unknown optional categories hide - and the retries
  // above bring a wrongly hidden one in a few seconds later.
  function categoryHasItems(btn) {
    if (!btn.menuType) return true;        // the pairing button is not a menu
    const n = menuCounts ? menuCounts[btn.id] : undefined;
    if (n === undefined) return btn.id === "wine";
    return n > 0;
  }

  const buttons = ALL_BUTTONS
    .filter(btn => menuTypeAvailableAtLocation(btn.menuType))
    .filter(categoryHasItems)
    .map(btn => ({ ...btn, available: true }));

  // Device-neutral wording throughout. "This tablet is your sommelier" was written
  // when every install was a mounted tablet; a guest who scanned a QR code is holding
  // their own phone, and telling them otherwise is the first thing they read.
  //
  // deviceSetup.deploymentMode is stored in Device Setup and is not read here - the
  // copy no longer needs to branch. It exists for the walk-away reset, which only a
  // shared device should ever do.
  const WELCOME_STEPS = [
    { id: "browse", Icon: WineGlassIcon, title: tr("home.welcome.browseTitle"), body: tr("home.welcome.browseBody") },
    { id: "ask", Icon: ChatIcon, title: tr("home.welcome.askTitle"), body: tr("home.welcome.askBody") },
    { id: "save", Icon: ScanIcon, title: tr("home.welcome.saveTitle"), body: tr("home.welcome.saveBody") },
  ];

  // Vertical budget. The footer is part of the page rather than a fixed bar, so
  // on a tablet everything above it has to fit the viewport or the byline lands
  // below the fold and the screen looks unfinished.
  //
  // Rather than hardcode a device height, work from the space actually available
  // and the number of buttons this particular restaurant renders - a print-tier
  // tenant with three categories and a premium one with six need different
  // spacing to land in the same place. FIXED_CHROME is an estimate of the
  // furniture above and below the buttons (capped logo block, page padding, the
  // two footer links, the byline); it does not need to be exact because the
  // result is clamped at both ends.
  const FIXED_CHROME = 380;
  const perButton = buttons.length ? (viewportHeight - FIXED_CHROME) / buttons.length : 999;
  const btnPadV = tablet ? Math.max(12, Math.min(22, Math.round(perButton * 0.28))) : null;
  const btnGap = tablet ? Math.max(8, Math.min(16, Math.round(perButton * 0.18))) : 14;
  const logoGap = tablet ? Math.max(18, Math.min(44, Math.round(perButton * 0.4))) : 40;

  return (
    <>
      {/* Welcome overlay */}
      {showWelcome && (
        <div style={{ position: "fixed", inset: 0, zIndex: Z.welcome, background: t.bgOverlay, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: t.bgSurface, borderRadius: 16, padding: "28px 24px", maxWidth: 360, width: "100%", fontFamily: t.fontSerif, boxShadow: `0 12px 48px ${t.shadow}` }}>
            <div style={{ color: t.accent, fontSize: 10, letterSpacing: "3px", textTransform: "uppercase", marginBottom: 6 }}>{tr("home.welcome.eyebrow")}</div>
            <div style={{ color: t.textPrimary, fontSize: 18, fontWeight: 600, lineHeight: 1.35, marginBottom: 6 }}>
              {tr("home.welcome.heading")}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 22 }}>
              {WELCOME_STEPS.map(step => (
                <div key={step.id} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                    background: t.accentDim, border: `0.5px solid ${t.accentBorder}`,
                    display: "flex", alignItems: "center",
                    justifyContent: "center", color: t.accent
                  }}>
                    <step.Icon size={15} />
                  </div>
                  <div>
                    <div style={{ color: t.textPrimary, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{step.title}</div>
                    <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.5 }}>{step.body}</div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => onSetShowWelcome(false)}
              style={{
                width: "100%", background: t.accent, border: "none", color: t.accentText,
                padding: "13px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                letterSpacing: "0.5px", cursor: "pointer", fontFamily: t.fontSerif
              }}
            >
              {tr("home.welcome.start")}
            </button>
          </div>
        </div>
      )}

    <div style={{
      background: t.bgBase, minHeight: "100vh", fontFamily: t.fontSerif,
      // Without this the padding stacks OUTSIDE minHeight:100vh - the element is
      // 100vh + 68px tall before a single button renders, and the byline is
      // permanently below the fold. No amount of spacing tuning fixes it, which
      // is why the earlier attempts only moved the overflow around.
      boxSizing: "border-box",
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "flex-start",
      padding: wide ? "40px 48px 28px" : mid ? "36px 32px 28px" : "48px 32px 40px",
      opacity: visible ? 1 : 0, transition: "opacity 0.6s ease",
      maxWidth: wide ? 1080 : 680, margin: "0 auto"
    }}>
      {/* Ambient glow — scales with the container or it reads as a spotlight on
          a screen twice its width. */}
      <div style={{
        position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)",
        width: wide ? 980 : 600, height: wide ? 400 : 300, pointerEvents: "none",
        background: `radial-gradient(ellipse at 50% 0%, ${t.accentDimSm} 0%, transparent 70%)`
      }} />

      {/* Guest language. Home screen only: a guest switching mid-visit taps Main
          Menu first. Lasts for this visit - Start Over and the idle reset reload
          into ?lang= or the restaurant's default. */}
      {(!settings || settings.showLanguagePicker !== false) && (
        // Deliberately NOT position: relative. The ambient glow above is position: fixed, so it
        // paints over anything not positioned and tints it a shade off the fill. The demo's theme
        // pill sits under the glow, and the picker is meant to look exactly like it, so it sits
        // under the glow too. (A positioned wrapper lifted it above the glow: pure white, visibly
        // different from the pill.)
        <div style={{ marginBottom: 14, textAlign: "center" }}>
          <LanguagePicker current={guestLang} onChange={onSetGuestLang} />
        </div>
      )}

      {/* Public, no-login theme preview - gated on the same publicDemo flag
          the ?theme= link uses, never a hardcoded restaurant id, so it
          extends automatically to any future public-demo tenant and can
          never appear for a paying restaurant's own guests. Sits above the
          logo rather than floating, so it reads as part of the page instead
          of a widget bolted over it. */}
      {isPublicDemoTenant && !demoSwitcherDismissed && (
        <div style={{ marginBottom: 16, textAlign: "center" }}>
          <DemoThemeSwitcher index={demoThemeIndex} defaultIndex={demoDefaultIndex} onChange={onSetDemoThemeIndex} onDismiss={onDismissDemoSwitcher} />
        </div>
      )}

      {/* Logo — tap 5x for manager access */}
      <div onClick={handleLogoTap} style={{ marginBottom: logoGap, textAlign: "center", cursor: "default", userSelect: "none" }}>
        {(() => {
          const barLogo = deviceSetup.barLogoUrl || (deviceSetup.barLogo ? `/logos/${deviceSetup.barLogo}` : null);
          const diningLogo = deviceSetup.diningLogoUrl || (deviceSetup.diningLogo ? `/logos/${deviceSetup.diningLogo}` : null);
          const barLogoAlt = deviceSetup.barAltLogoUrl || null;
          const diningLogoAlt = deviceSetup.diningAltLogoUrl || null;
          const isLightTheme = !!t.isLight;
          // On a light-themed room, a dedicated dark-artwork upload wins if one
          // exists; otherwise fall back to the light/dark-background file, same
          // as before this had two slots.
          const altSrc = tabletLocation === "bar" ? barLogoAlt : tabletLocation === "dining" ? diningLogoAlt : null;
          const usingAlt = isLightTheme && !!altSrc;
          const src = usingAlt ? altSrc
            : tabletLocation === "bar" && barLogo ? barLogo
            : tabletLocation === "dining" && diningLogo ? diningLogo
            : null;
          const alt = tabletLocation === "bar" || tabletLocation === "dining"
            ? roomName(tabletLocation, locationNames)
            : (restaurantDisplayName || "Menu");
          // No logo uploaded yet: show the restaurant's name as a wordmark rather
          // than another restaurant's artwork.
          if (!src) {
            return (
              <div style={{ padding: tablet ? "24px 0" : "18px 0" }}>
                <div style={{ color: t.accent, fontSize: tablet ? 14 : 11, letterSpacing: "4px", textTransform: "uppercase", marginBottom: tablet ? 14 : 10 }}>✦</div>
                <div style={{ color: t.textPrimary, fontSize: wide ? 40 : mid ? 34 : 26, letterSpacing: tablet ? "2px" : "1px", lineHeight: 1.25 }}>{restaurantDisplayName || tr("home.beverageMenu")}</div>
              </div>
            );
          }
          // A white logo file needs a brightness lift on a dark field and looks
          // blown out on a light one. This used to test for one named scheme,
          // which meant every scheme added after it was treated as dark - so
          // five light schemes were boosting a logo that did not need it. The
          // scheme declares its own dominance now.
          //
          // A file uploaded specifically as the dark-room variant already has
          // the right artwork for a light room, so it gets no filter treatment
          // either way - the brightness lift only exists to compensate for
          // reusing a light-background file where none was designed.
          const imgFilter = usingAlt || isLightTheme ? "none" : "brightness(1.5) contrast(1.05)";
          const imgOpacity = usingAlt || isLightTheme ? 1 : 0.95;
          // 42vw rather than 80vw on a tablet: the logo is the widest thing on
          // the screen and at 80vw of 1280 it would swamp the menu beneath it.
          //
          // The HEIGHT cap is what keeps the footer above the fold. Width alone
          // bounds nothing: a square logo file at 460px wide is also 460px tall,
          // which is most of a landscape tablet's viewport before a single menu
          // button has rendered. objectFit lets a tall logo shrink to fit rather
          // than distort.
          return <img src={src} alt={alt} style={{
            width: wide ? "min(460px, 42vw)" : mid ? "min(420px, 68vw)" : "min(340px, 80vw)",
            maxHeight: wide ? "min(160px, 20vh)" : mid ? "min(190px, 20vh)" : "min(220px, 26vh)",
            objectFit: "contain",
            opacity: imgOpacity, filter: imgFilter
          }} />;
        })()}
      </div>

      {/* Menu buttons */}
      {/* One column at every size. Two columns filled the tablet but split the
          list into a shape that reads as a grid of tiles rather than a menu;
          the wider single column keeps the original vertical rhythm and still
          fills the screen. */}
      <div style={{
        width: "100%",
        maxWidth: wide ? 620 : mid ? 540 : 340,
        display: "flex", flexDirection: "column",
        gap: btnGap,
      }}>
        {buttons.map(btn => (
          <button
            key={btn.id}
            onClick={() => btn.available && onNavigate(btn.id)}
            style={{
              background: btn.id === "sommelier"
                ? t.accentDim
                : btn.available ? t.white06 : t.white04,
              border: btn.id === "sommelier"
                ? `1px solid ${t.accentBorder}`
                : `0.5px solid ${btn.available ? t.accentBorderSm : t.white08}`,
              borderRadius: 8,
              padding: tablet
                ? `${btn.id === "sommelier" ? btnPadV + 2 : btnPadV}px ${wide ? 30 : 26}px`
                : (btn.id === "sommelier" ? "20px 24px" : "18px 24px"),
              display: "flex", alignItems: "center", justifyContent: "space-between",
              cursor: btn.available ? "pointer" : "default",
              transition: "all 0.2s", width: "100%",
              opacity: btn.available ? 1 : 0.35,
            }}
            onMouseEnter={e => { if (btn.available) e.currentTarget.style.background = btn.id === "sommelier" ? t.accentDim : t.accentDimSm; }}
            onMouseLeave={e => { if (btn.available) e.currentTarget.style.background = btn.id === "sommelier" ? t.accentDim : t.white06; }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              {btn.eyebrow ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 3, textAlign: "left" }}>
                  <span style={{
                    color: t.accent, opacity: 0.75,
                    fontSize: wide ? 15 : mid ? 14 : 13, letterSpacing: "1.5px",
                    textTransform: "uppercase", fontFamily: t.fontSerif,
                  }}>
                    {btn.eyebrow}
                  </span>
                  <span style={{
                    color: t.accent,
                    fontSize: wide ? 21 : mid ? 19 : 16, letterSpacing: "1.5px", fontWeight: 600,
                    textTransform: "uppercase", fontFamily: t.fontSerif,
                    lineHeight: 1.3
                  }}>
                    {btn.label}
                  </span>
                </div>
              ) : (
                <span style={{
                  color: btn.available ? t.textPrimary : t.textVeryDim,
                  // Every label is one size. The exception is "Non-Alcoholic Beverages", the longest,
                  // and only on a screen too small to hold it at that size: measured with the real
                  // font it wraps to two lines below ~380px in English and Russian and fits from
                  // 390px up (French and German fit even at 360). It used to be 2px smaller with
                  // tighter spacing on EVERY screen, which tablets and desktops never needed.
                  fontSize: btn.id === "nab" && viewportWidth < 384 ? 13 : (wide ? 18 : mid ? 17 : 15),
                  letterSpacing: btn.id === "nab" && viewportWidth < 384 ? "1px" : "2px",
                  textTransform: "uppercase", fontFamily: t.fontSerif,
                  textAlign: "left", lineHeight: 1.3
                }}>
                  {btn.label}
                </span>
              )}
            </div>
            {btn.available ? (
              <span style={{ color: t.accent, fontSize: tablet ? (btn.id === "sommelier" ? (wide ? 26 : 24) : (wide ? 22 : 20)) : (btn.id === "sommelier" ? 22 : 18), lineHeight: 1 }}>›</span>
            ) : (
              <span style={{ color: t.textMuted, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase" }}>{tr("home.soon")}</span>
            )}
          </button>
        ))}
      </div>

      {/* Shortlist button */}
      {favorites.length > 0 && (
        <button onClick={onShowShortlist} style={{ marginTop: 24, background: t.accentDimSm, border: `0.5px solid ${t.accent}`, color: t.accent, padding: "10px 28px", borderRadius: 24, cursor: "pointer", fontFamily: t.fontSerif, fontSize: 13, letterSpacing: "1px", display: "flex", alignItems: "center", gap: 8 }}>
          ★ {tr("home.myMenu")} <span style={{ background: t.accentBorderSm, borderRadius: 10, padding: "1px 8px", fontSize: 12 }}>{favorites.length}</span>
        </button>
      )}

      {/* Footer */}
      <button
        onClick={() => onSetShowWelcome(true)}
        style={{
          marginTop: tablet ? 22 : 32, background: "none", border: "none", cursor: "pointer", padding: 0,
          color: t.accent, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase",
          fontFamily: t.fontSerif, textDecoration: "underline", textDecorationColor: t.accentBorderSm
        }}
      >
        {tr("home.howToUse")}
      </button>

      {/* Start Over — same footer treatment, one step quieter. Sits here rather
          than floating over every screen: this is where a tablet gets handed
          over, and a fixed control collided with this link and looked bolted on.

          One tap, no confirmation. A reload is a complete reset: GUEST_SESSION_ID
          is a module-level const regenerated on load, and favourites live in
          useState — so nothing survives it. The only localStorage keys are the
          theme cache and tabletLocation, both of which SHOULD survive and do. */}
      <button
        onClick={hardReload}
        style={{
          marginTop: 10, background: "none", border: "none", cursor: "pointer", padding: 0,
          color: t.textMuted, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase",
          fontFamily: t.fontSerif, textDecoration: "underline", textDecorationColor: t.borderStrong
        }}
      >
        {tr("home.startOver")}
      </button>

      {/* Desktop QR code — only shown on non-touch Windows desktops */}
      {/Windows/.test(navigator.userAgent) && !('ontouchstart' in window) && (
        <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&ecc=M&margin=1&data=${encodeURIComponent(`https://${RESTAURANT_ID}.getsommsync.com`)}`}
            alt={tr("home.qrAlt")}
            style={{ width: 110, height: 110, display: 'block', borderRadius: 4, border: `1px solid ${t.borderSubtle}` }}
          />
          <div style={{ color: t.textDim, fontSize: 10, letterSpacing: '1px', textTransform: 'uppercase', textAlign: 'center' }}>
            {tr("home.scanWithPhone")}
          </div>
        </div>
      )}

      {/* The build stamp answers "is this tablet running the code I just
          deployed" without a PIN, a console or a guess. Deliberately tiny and
          dim - a guest will never register it, and a manager who has been told
          where to look finds it in two seconds. */}
      {/* textAlign: center - this box is only as wide as "Powered by SyncBev", and the build
          code and device id below it inherited left alignment, so they hugged the left edge
          of the box instead of sitting under the middle of it. */}
      <div style={{ marginTop: "auto", paddingTop: tablet ? 16 : 24, color: t.textSecondary, fontSize: 10, letterSpacing: "2px", textTransform: "uppercase", textAlign: "center" }}>
        {tr("home.poweredBy")}
        {/* Build stamp and kiosk device id on their own line, readable on purpose.
            They were 8px, textMuted at 60% opacity - fine for "is this the new
            build", but a manager asked over the phone to read out the device id
            could barely make it out. Now textSecondary (5:1 or better in every
            scheme) at 11px, in a monospace face so 0/O and 1/l can't be confused
            when spelled out. Still small and quiet enough that guests pass over it. */}
        <div style={{
          marginTop: 4, color: t.textSecondary, fontSize: 11, letterSpacing: "1.5px",
          textTransform: "none", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Roboto Mono', monospace",
        }}>
          {BUILD_ID.slice(0, 7)}
          {/* Last 8 characters: unique enough to find the tablet in Fully Cloud and
              short enough to read over the phone. Lower case, as Fully Cloud shows it. */}
          {fullyDeviceId() && (
            <span title={fullyDeviceId()}> · {fullyDeviceId().slice(-8)}</span>
          )}
        </div>
      </div>
      {/* A permanent route to the notice, separate from the one at the email
          field. Browsing is not quite zero-collection — hosting and functions
          log IPs, and rfetch carries a sessionId — so a guest who gives us
          nothing should still be able to find this. */}
      <div style={{ paddingTop: 8 }}>
        <button onClick={() => setShowPrivacy(true)} style={{ background: "none", border: "none", padding: 0, color: t.textMuted, fontSize: 9, letterSpacing: "1px", textTransform: "uppercase", textDecoration: "underline", cursor: "pointer", fontFamily: t.fontSerif, opacity: 0.75 }}>
          {tr("home.privacy")}
        </button>
      </div>
      {showPrivacy && <PrivacyNotice venueName={privacyProfile.name} listOptIn={privacyProfile.listOptIn} onClose={() => setShowPrivacy(false)} />}
    </div>
    </>
  );
}

// ─── Wine List Screen ─────────────────────────────────────────────────────────

function WineListScreen({ wines, favorites, onToggleFavorite, onBack, onShowShortlist, tabletLocation = "all", locationNames = { bar: "Bar", dining: "Dining Room" }, plan = "premium" }) {
  const t = useTheme();
  const narrow = useIsNarrow();
  const [activeColour, setActiveColour]   = useState("All");
  const [activeTier, setActiveTier]       = useState("All");
  const [activeSubgroup, setActiveSubgroup] = useState("All");
  const [activeVarietal, setActiveVarietal] = useState("All");
  const [showAllGrapes, setShowAllGrapes] = useState(false);
  const [selectedWine, setSelectedWine]   = useState(null);
  const [wineSearch, setWineSearch]       = useState("");
  const [visible, setVisible]             = useState(false);
  const [zoomedLabel, setZoomedLabel]     = useState(null);
  const [chatOpen, setChatOpen]           = useState(false);
  const [chatContext, setChatContext]      = useState(null);
  const [toast, setToast]                 = useState(null);
  useEffect(() => { setTimeout(() => setVisible(true), 50); }, []);

  function handleOpenChat(ctx) { setChatContext(ctx); setChatOpen(true); }

  const availableWines = wines.filter(w => w.available !== false);
  // Colour is the top filter row: Red / White / Rosé / Sparkling / Dessert. It
  // comes from ENRICHMENT (getWines attaches `colour`, worked out by
  // wineListColour() in functions/index.js from the enriched varietal, grapes
  // and name), never from how the restaurant grouped its menu - so it works the
  // same on a Toast list split into House/Cellar, a CSV with no groups, or a
  // list already sectioned by colour.
  // It stacks with everything below it: White + By the Glass is a real
  // question. Each chip appears only when it would show something, and the row
  // only when there are at least two colours - one chip that matches the whole
  // list is noise. A wine with no colour (no varietal yet) still lists under
  // All Wines; it just joins no chip. Before the backend change is deployed
  // every wine arrives without `colour`, so the row simply stays hidden.
  const COLOUR_ORDER  = ["red", "white", "rose", "sparkling", "dessert"];
  const COLOUR_LABELS = { red: tr("wine.colours.red"), white: tr("wine.colours.white"), rose: tr("wine.colours.rose"), sparkling: tr("wine.colours.sparkling"), dessert: tr("wine.colours.dessert") };
  const colours = COLOUR_ORDER.filter(c => availableWines.some(w => w.colour === c));
  const showColourRow = colours.length >= 2;
  // If the active colour drops off the list (its last wine sold out between
  // refreshes), fall back to All rather than show an empty list with no chip lit.
  const colour = showColourRow && colours.includes(activeColour) ? activeColour : "All";
  const colourMatch = w => colour === "All" || w.colour === colour;
  const winesInColour = availableWines.filter(colourMatch);
  // filter(Boolean) matters for file-import restaurants: a CSV with no tier
  // column leaves every wine's tier null, which otherwise renders a blank,
  // unlabelled tab next to "All". The subgroup row two lines down already
  // guards this; the tier row did not.
  // "By the Glass" / "By the Bottle" sit in the same row as the
  // restaurant's own tiers (House, Cellar...), and are filters on price type, not
  // on a tier field - so they work for every restaurant, including ones whose
  // list has no tiers at all. Picking one replaces the tier choice rather than
  // stacking with it, so only one button in the row is ever highlighted.
  // "By the Glass" lists every wine poured by the glass, including ones that
  // also come by the bottle. "By the Bottle" is the opposite cut, deliberately
  // stricter: only wines sold ONLY by the bottle. A wine with a glass price is
  // excluded even though a bottle is also available, so a guest choosing
  // "By the Bottle" sees the wines they cannot get by the glass.
  // Both buttons are offered only when each would show something: with no glass
  // prices at all "By the Glass" would be permanently empty, and with no
  // bottle-only wines "By the Bottle" would be.
  // All of this row is worked out within the chosen colour, so a button that
  // would come up empty for Sparkling is not offered while Sparkling is on.
  const WINE_GLASS = "__glass__", WINE_BOTTLE = "__bottle__";
  function tiersFor(list) {
    const tierOrder = [...new Map(list.map(w => [w.tier, true])).keys()].filter(Boolean);
    const showFormatTabs = list.some(w => w.glassPrice) && list.some(w => w.bottlePrice && !w.glassPrice);
    return ["All", ...(showFormatTabs ? [WINE_GLASS, WINE_BOTTLE] : []), ...tierOrder];
  }
  const tiers = tiersFor(winesInColour);
  // A tier/format pick survives a colour change when it still applies (By the
  // Glass, then White, means white by the glass). When it does not exist in
  // the new colour it is cleared at the tap (pickColour), and read as All here
  // for the one case the tap cannot see: the list refreshing underneath.
  const tier = tiers.includes(activeTier) ? activeTier : "All";
  function pickColour(c) {
    const next = c === "All" ? availableWines : availableWines.filter(w => w.colour === c);
    if (!tiersFor(next).includes(activeTier)) setActiveTier("All");
    setActiveColour(c); setActiveSubgroup("All"); setActiveVarietal("All"); setShowAllGrapes(false); setSelectedWine(null);
  }
  function tierMatch(w, tier) {
    if (tier === "All") return true;
    if (tier === WINE_GLASS) return !!w.glassPrice;
    if (tier === WINE_BOTTLE) return !!w.bottlePrice && !w.glassPrice;
    return w.tier === tier;
  }
  function tierLabel(tier) {
    // With the colour row showing, "All Wines" lives there; this row's own
    // reset reads plain "All" so the header does not say "All Wines" twice.
    if (tier === "All") return showColourRow ? tr("wine.all") : tr("wine.allWines");
    if (tier === WINE_GLASS) return tr("wine.byGlass");
    if (tier === WINE_BOTTLE) return tr("wine.byBottle");
    return TIER_LABELS[tier] || sectionLabel(tier);
  }
  const filteredByTier = winesInColour.filter(w => tierMatch(w, tier));
  const subgroupOrder  = [...new Map(filteredByTier.map(w => [w.subgroup, true])).keys()].filter(Boolean);
  const subgroups      = ["All", ...subgroupOrder];
  const filteredBySubgroup = activeSubgroup === "All" ? filteredByTier : filteredByTier.filter(w => w.subgroup === activeSubgroup);
  const varietalCounts = filteredBySubgroup.reduce((acc, w) => {
    const v = consolidateVarietal(w.varietal);
    if (v) acc[v] = (acc[v] || 0) + 1;
    return acc;
  }, {});
  const varietalsByCount = Object.keys(varietalCounts)
    .sort((a, b) => varietalCounts[b] - varietalCounts[a] || a.localeCompare(b));
  const varietals      = ["All", ...varietalsByCount];
  // Six covers most of a list without wrapping past one row on a phone. The
  // rest stay one tap away rather than behind an "Other" bucket - a guest
  // looking for Malbec would never guess it lives under "Other".
  const GRAPE_VISIBLE  = 6;
  const grapesCollapsed = !showAllGrapes && varietalsByCount.length > GRAPE_VISIBLE;
  // An active grape must stay on screen even when it ranks below the cut, or the
  // filter looks like it cleared itself.
  const shownVarietals = grapesCollapsed
    ? ["All", ...varietalsByCount.slice(0, GRAPE_VISIBLE)
        .concat(varietalsByCount.slice(GRAPE_VISIBLE).filter(v => v === activeVarietal))]
    : varietals;

  const allMatchingWines = wines.filter(w => {
    if (!colourMatch(w)) return false;
    if (!tierMatch(w, tier)) return false;
    if (activeSubgroup !== "All" && w.subgroup !== activeSubgroup) return false;
    if (activeVarietal !== "All" && consolidateVarietal(w.varietal) !== activeVarietal) return false;
    return true;
  });
  const searchFiltered = wineSearch.trim() === ""
    ? allMatchingWines
    : allMatchingWines.filter(w => {
        const q = foldText(wineSearch).trim();
        return searchHit(w.name, q)
          || searchHit(w.varietal, q)
          || searchHit(w.region, q)
          || searchHit(w.description, q);
      });

  // Section headings (Nov 5, 2026). Two fixes, both found on an uploaded PDF list:
  //  1. One heading per section. Grouping was on the exact section string, so
  //     "White Wines – Sparkling" and "WHITE WINES - Sparkling" (a menu read in
  //     pieces, or two uploads) became two sections that LOOK identical, because
  //     headings render in capitals. Now matched ignoring case, dash style and
  //     spacing; the heading shows the first spelling seen.
  //  2. For an UPLOADED list only (WINE_SECTIONS_BY_COLOUR, from getWines):
  //     sections in the order a printed list uses: sparkling, white, rosé, red,
  //     then dessert last. Order used to be first appearance in wineOrder, which
  //     for an upload is upload history: an after-dinner menu uploaded first put
  //     Port at the top, and a list with a regional insert interleaved reds and
  //     whites. A POS list keeps its own order - the restaurant arranged its
  //     menu groups on purpose (House before Cellar, say).
  //     Each section is placed by the colour most of its wines have (from
  //     enrichment, the same `colour` the filter chips use); sections of the same
  //     colour keep the restaurant's own order. A section with no clear colour
  //     (none researched yet, or an even mix) stays next to the section before it.
  const sectionKey = s => String(s).normalize("NFKC").replace(/[\u2010-\u2015\u2212]/g, "-").replace(/\s+/g, " ").trim().toLowerCase();
  const SECTION_COLOUR_RANK = { sparkling: 0, white: 1, rose: 2, red: 3, dessert: 4 };
  const grouped = {};
  const groupLabel = {};
  const sortedForGrouping = [
    ...searchFiltered.filter(w => w.available !== false),
    ...searchFiltered.filter(w => w.available === false),
  ];
  const firstSeen = [];
  sortedForGrouping.forEach(wine => {
    const raw = wine.subgroup || wine.tier || "Wine";
    const key = sectionKey(raw);
    if (!grouped[key]) { grouped[key] = []; groupLabel[key] = raw; firstSeen.push(key); }
    grouped[key].push(wine);
  });
  function sectionRank(key) {
    const counts = {};
    grouped[key].forEach(w => { if (w.colour in SECTION_COLOUR_RANK) counts[w.colour] = (counts[w.colour] || 0) + 1; });
    const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (!ranked.length || (ranked[1] && ranked[1][1] === ranked[0][1])) return null;
    return SECTION_COLOUR_RANK[ranked[0][0]];
  }
  let prevRank = 0;
  const groupOrder = !WINE_SECTIONS_BY_COLOUR ? firstSeen : firstSeen
    .map((key, i) => { const r = sectionRank(key); prevRank = r === null ? prevRank : r; return { key, i, rank: prevRank }; })
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map(g => g.key);

  const availCount = searchFiltered.filter(w => w.available !== false).length;
  const oosCount   = searchFiltered.filter(w => w.available === false).length;
  const countLeft  = `${tr("wine.count", { count: availCount })}${oosCount > 0 ? ` · ${tr("wine.outOfStockCount", { count: oosCount })}` : ""}${wineSearch ? ` · ${tr("common.searchQuery", { query: wineSearch })}` : [colour !== "All" && COLOUR_LABELS[colour], activeVarietal !== "All" ? activeVarietal : activeSubgroup !== "All" ? sectionLabel(activeSubgroup) : tier !== "All" && tierLabel(tier)].filter(Boolean).map(s => ` · ${s}`).join("")}`;

  return (
    <div style={{ background: t.bgBase, minHeight: "100vh", fontFamily: t.fontSerif, maxWidth: 680, margin: "0 auto", opacity: visible ? 1 : 0, transition: "opacity 0.5s ease" }}>
      {/* Shared header — edit ListScreenHeader to change all list screens */}
      <div style={{ position: "sticky", top: "var(--somm-banner-h, 0px)", zIndex: 100 }}>
        <ListScreenHeader title={tr("lists.wine.title")} onBack={onBack} favorites={favorites} onShowShortlist={onShowShortlist}>
          {showColourRow && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              {["All", ...colours].map(c => (
                <FilterBtn key={c} label={c === "All" ? tr("wine.allWines") : COLOUR_LABELS[c]}
                  active={colour === c}
                  onClick={() => pickColour(c)} />
              ))}
            </div>
          )}
          {/* Price format and the restaurant's own tiers. Drops to the small
              pill size under the colour row so the sticky header gains as
              little height as possible on a phone. Under the colour row it
              is hidden when "All" is its only button - nothing to choose.
              Without the colour row it renders exactly as it always has. */}
          {(tiers.length > 1 || !showColourRow) && (
            <div style={{ display: "flex", gap: showColourRow ? 5 : 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
              {tiers.map(t => (
                <FilterBtn key={t} small={showColourRow} label={tierLabel(t)}
                  active={tier === t}
                  onClick={() => { setActiveTier(t); setActiveSubgroup("All"); setActiveVarietal("All"); setShowAllGrapes(false); setSelectedWine(null); }} />
              ))}
            </div>
          )}
          {tier !== "All" && subgroups.length > 2 && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
              <span style={{ color: t.textMuted, fontSize: narrow ? 10.5 : 9, letterSpacing: "2px", textTransform: "uppercase", marginRight: 2 }}>{tr("wine.type")}</span>
              {subgroups.map(s => (
                <FilterBtn key={s} small label={s === "All" ? tr("wine.all") : sectionLabel(s).replace(/^(Cellar |House )/, "")}
                  active={activeSubgroup === s}
                  onClick={() => { setActiveSubgroup(s); setActiveVarietal("All"); setShowAllGrapes(false); setSelectedWine(null); }} />
              ))}
            </div>
          )}
          {varietals.length > 2 && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center", marginBottom: 4 }}>
              <span style={{ color: t.textMuted, fontSize: narrow ? 10.5 : 9, letterSpacing: "2px", textTransform: "uppercase", marginRight: 2 }}>{tr("wine.grape")}</span>
              {shownVarietals.map(v => (
                <FilterBtn key={v} small label={v === "All" ? tr("wine.allGrapes") : v}
                  active={activeVarietal === v}
                  onClick={() => { setActiveVarietal(v); setSelectedWine(null); }} />
              ))}
              {varietalsByCount.length > GRAPE_VISIBLE && (
                <button onClick={() => setShowAllGrapes(!showAllGrapes)}
                  style={{ background: "none", border: "none", color: t.accent, opacity: 0.85, cursor: "pointer",
                    fontFamily: t.fontSerif, fontSize: narrow ? 12 : 11, letterSpacing: "0.5px", padding: "3px 6px" }}>
                  {grapesCollapsed ? tr("wine.moreGrapes", { count: varietalsByCount.length - GRAPE_VISIBLE }) : tr("wine.showFewer")}
                </button>
              )}
            </div>
          )}
          <div style={{ padding: "4px 0 10px", position: "relative" }}>
            <input type="text" placeholder={tr("wine.search")} value={wineSearch}
              onChange={e => setWineSearch(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", background: t.white08, border: `0.5px solid ${t.accentBorderSm}`, color: t.textPrimary, padding: narrow ? "9px 32px 9px 12px" : "8px 32px 8px 12px", borderRadius: 20, fontFamily: t.fontSerif, fontSize: 16, outline: "none", letterSpacing: "0.3px" }}
            />
            {wineSearch && <button onClick={() => setWineSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: t.textDim, cursor: "pointer", fontSize: 18, padding: 0 }}>×</button>}
          </div>
        </ListScreenHeader>
      </div>

      <ListCountBar left={countLeft} right={tr("common.starToSave")} />

      <div style={{ background: t.bgSurface }}>
        {groupOrder.map((group, gi) => (
          <div key={group}>
            <ListSectionHeading label={groupLabel[group] === "Wine" ? tr("wine.defaultGroup") : sectionLabel(groupLabel[group])} borderTop={gi > 0} />
            <div style={{ padding: "0 14px 8px", display: "flex", flexDirection: "column", gap: 1 }}>
              {grouped[group].map(wine => (
                <WineCard key={wine.id} wine={wine} selected={selectedWine === wine.id}
                  onSelect={() => setSelectedWine(selectedWine === wine.id ? null : wine.id)}
                  isFavorited={favorites.some(f => f.id === wine.id)}
                  onToggleFavorite={onToggleFavorite}
                  onZoomLabel={wine.imageUrl ? () => setZoomedLabel(wine) : null} />
              ))}
            </div>
          </div>
        ))}
        {searchFiltered.length === 0 && (
          <div style={{ color: t.textSecondary, textAlign: "center", padding: 40, fontSize: narrow ? 15 : 14 }}>
            {wineSearch ? tr("wine.noMatch", { query: wineSearch }) : tr("wine.noneInSelection")}
          </div>
        )}
      </div>

      {selectedWine && (() => { const wine = wines.find(w => w.id === selectedWine); return wine ? <WineDetailPanel wine={wine} onClose={() => setSelectedWine(null)} onOpenChat={planHasChat(plan) ? handleOpenChat : null} onZoomLabel={(w) => setZoomedLabel(w)} favorites={favorites} onToggleFavorite={onToggleFavorite} tabletLocation={tabletLocation} plan={plan} screenTitle={tr("lists.wine.title")} /> : null; })()}
      <LabelModal wine={zoomedLabel} onClose={() => setZoomedLabel(null)} />
      {planHasChat(plan) && <SommelierChat isOpen={chatOpen} onClose={(added) => { setChatOpen(false); if (added) setToast(added); }} contextItem={chatContext} favorites={favorites} onToggleFavorite={onToggleFavorite} tabletLocation={tabletLocation} locationNames={locationNames} />}
      {toast && <SommelierToast items={toast} onViewMenu={() => { setToast(null); onShowShortlist(); }} onDismiss={() => setToast(null)} />}
      <div style={{ height: 32 }} />
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

function AppContent() {
  // The language lists are fetched in. Changes when settings arrive (a non-English
  // restaurant) and, later, when a guest picks a language: the wine list refetches.
  const [listLang, setListLang] = useState(getRequestedLanguage());
  // The guest's own choice on the home screen (LanguagePicker). Changing listLang
  // re-renders every screen in the new language and refetches the wine list; the
  // other lists fetch when opened. For this visit only: Start Over and the idle
  // reset reload into ?lang= or the restaurant's default, so the next table
  // starts in the house language.
  function changeGuestLanguage(code) {
    // Switch once the language's file is here, so the screen changes in one step.
    loadLanguage(code).then(() => {
      setLanguage(code);
      setListLang(getRequestedLanguage());
    });
  }
  // Re-render every screen when a language file arrives (language files load on
  // demand - see i18n.js).
  const [, setI18nTick] = useState(0);
  useEffect(() => onLanguageLoaded(() => setI18nTick(n => n + 1)), []);
  const [screen, setScreen] = useState("home");
  // Bumped by My Menu's Start a New Pairing. Keys SommelierScreen, so the pairing
  // screen remounts empty - its dishes, answer and choice are local state, and a
  // remount is the one reset that cannot miss a field.
  const [pairingKey, setPairingKey] = useState(0);
  const [wines, setWines] = useState([]);

  // showWelcome lives here so it survives navigation back to HomeScreen.
  // Starts true (show on first load), becomes false when dismissed, stays
  // false for the rest of the session — only re-shows if guest taps "How to Use This App".
  //
  // Not when the page opens straight into Manager (?manager=, from the admin
  // setup checklist or the return from Stripe Checkout): the guest welcome card
  // sits above the Manager screen. If the link turns out not to be for a
  // signed-in manager, the effect below shows it after all.
  const [showWelcome, setShowWelcome] = useState(() =>
    !MANAGER_LINK_VIEWS.includes(new URLSearchParams(window.location.search).get('manager')));

  // Detect Windows desktop — emojis and label images render less crisply on Windows browsers.
  // We show a polite banner rather than trying to fix the underlying OS font rendering.
  const [tabletLocation, setTabletLocationState] = useState(() => {
    // URL param wins — ?location=bar or ?location=dining (used by QR codes on printed menus)
    const urlLoc = new URLSearchParams(window.location.search).get('location');
    if (urlLoc === 'bar' || urlLoc === 'dining') {
      localStorage.setItem("tabletLocation", urlLoc); // write now so the settings useEffect doesn't override
      return urlLoc;
    }
    return localStorage.getItem("tabletLocation") || null;
  });
  const [locationNames, setLocationNames] = useState({ bar: "Bar", dining: "Dining Room" });
  // Seeded from the last known themes for THIS restaurant so the first paint is
  // already right. Platform defaults apply only on a device that has never loaded
  // this tenant.
  const [deviceSetup, setDeviceSetup] = useState(() => {
    const cached = readCachedThemes();
    return {
      defaultLocation: "bar", barLogo: "", diningLogo: "", barLogoUrl: "", diningLogoUrl: "", barAltLogoUrl: "", diningAltLogoUrl: "", printLogoUrl: "",
      ...readCachedLogos(),
      barTheme: (cached && cached.barTheme) || DEFAULT_THEME.name,
      diningTheme: (cached && cached.diningTheme) || DEFAULT_THEME.name,
    };
  });
  const [appSettings, setAppSettings] = useState(null);
  // Returned beside settings by getSettings, never inside it: see getSettings.
  const [manualMenus, setManualMenus] = useState([]);
  const [plan, setPlan] = useState('premium'); // 'base' | 'premium' — default premium so existing restaurants are unaffected
  // Demo tenants are full-featured sites built from a prospect's published menu.
  // They carry the restaurant's name and branding, so they have to say plainly
  // what they are - the link will get forwarded.
  const [demoInfo, setDemoInfo] = useState({ isDemo: false, expired: false });
  // Set from getSettings. An inactive restaurant (switched off in the admin
  // portal) shows RestaurantUnavailable instead of the app.
  const [restaurantInactive, setRestaurantInactive] = useState(false);
  // Used to gate the demo-only theme override below (bar/dining always
  // matching, and a ?theme= URL param) - kept separate from demoInfo.isDemo,
  // which is a different flag entirely (see setDemoStatus on the backend).
  const [isPublicDemoTenant, setIsPublicDemoTenant] = useState(false);
  // Fires only on a public-demo tenant (see consumeDemoTurn on the backend).
  // null = no wall. Otherwise { reason: 'turn_limit' | 'daily_limit', turnsUsed, message }.
  const [demoWall, setDemoWall] = useState(null);
  useEffect(() => {
    function onWall(e) {
      const d = e.detail || {};
      setDemoWall({
        reason: d.error === 'demo_daily_limit' ? 'daily_limit' : 'turn_limit',
        turnsUsed: d.turnsUsed || null,
        message: d.message || null,
      });
    }
    window.addEventListener('ss-demo-wall', onWall);
    return () => window.removeEventListener('ss-demo-wall', onWall);
  }, []);
  function setTabletLocationPersist(loc) { localStorage.setItem("tabletLocation", loc); setTabletLocationState(loc); }
  const resolvedLocation = tabletLocation || "bar";
  // The public demo tenant defaults to Onyx & Chalk rather than the platform-
  // wide DEFAULT_THEME (Parchment & Maple since Nov 2, 2026) when nothing is explicitly
  // configured - every other restaurant's fallback is untouched. This only
  // takes effect if deviceSetup.barTheme/diningTheme is actually empty; a
  // tenant that already has a theme saved in Settings keeps showing that one,
  // since this is just the "nothing picked yet" default, not an override.
  const barThemeKey = deviceSetup.barTheme || (isPublicDemoTenant ? 'onyxAndChalk' : DEFAULT_THEME.name);
  const diningThemeKey = deviceSetup.diningTheme || (isPublicDemoTenant ? 'onyxAndChalk' : DEFAULT_THEME.name);
  // On the public demo only: bar and dining always match (a marketing-site
  // visitor isn't either one, so the split makes no sense here), and a
  // ?theme= URL param can override the configured theme entirely - this is
  // what lets a "choose your demo color" picker on the marketing site send
  // visitors straight into the demo pre-set to their pick. Gated on the real
  // publicDemo flag (fetched above), not a hardcoded restaurant id, so this
  // can never be used to override a paying restaurant's actual configured
  // branding via a shared link, and it extends automatically to any future
  // public-demo tenant without needing this code touched again. getTheme's
  // own fallback-to-default handles an invalid or missing ?theme= value, so
  // no separate validation is needed here.
  const demoThemeParam = isPublicDemoTenant ? new URLSearchParams(window.location.search).get('theme') : null;
  // Interactive, no-login preview for a public demo tenant (see
  // DemoThemeSwitcher below) - a second, in-app layer on top of the ?theme=
  // link above. null means nobody has touched the switcher yet, so the URL
  // param or the tenant's configured theme still wins; once they cycle it,
  // this index takes over for the rest of the session. Never written back to
  // the server, and never reachable at all unless isPublicDemoTenant is true.
  const [demoThemeIndex, setDemoThemeIndex] = useState(null);
  const activeThemeKey = isPublicDemoTenant
    ? (demoThemeIndex !== null ? (THEME_CATALOGUE[demoThemeIndex] || THEME_CATALOGUE[0]).value : (demoThemeParam || barThemeKey))
    : (resolvedLocation === "bar" ? barThemeKey : diningThemeKey);
  // getTheme's fallback is DEFAULT_THEME, which is itself defined as a named
  // scheme upstream - so it returns undefined precisely when a scheme is
  // removed, which is the case it exists to survive. Resolve locally too.
  const activeTheme = getTheme(activeThemeKey) || resolveTheme(activeThemeKey);
  // What the switcher itself should show as "current" - the manual override
  // once set, otherwise wherever activeThemeKey actually landed (the ?theme=
  // param or the configured theme), so opening the panel for the first time
  // doesn't reset to theme #1 out of nowhere.
  const demoSwitcherIndex = demoThemeIndex !== null
    ? demoThemeIndex
    : Math.max(0, THEME_CATALOGUE.findIndex(x => x.value === activeThemeKey));
  // What "Reset to default" returns to: the demo tenant's own configured theme,
  // deliberately NOT the ?theme= link preset, so it always means the same thing.
  const demoDefaultIndex = Math.max(0, THEME_CATALOGUE.findIndex(x => x.value === barThemeKey));
  // Lives here, not inside DemoThemeSwitcher itself - the widget unmounts and
  // remounts every time the guest navigates between Home and a list screen
  // (they're two separate render sites), so a "dismissed" flag kept in the
  // component's own state would reappear on the very next screen. Kept here
  // it survives navigation and is gone for the rest of the session, exactly
  // once, until a reload starts a new one.
  const [demoSwitcherDismissed, setDemoSwitcherDismissed] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPin, setShowPin] = useState(false);
  const [showManager, setShowManager] = useState(false);
  const [accessLevel, setAccessLevel] = useState(null); // "manager" | "admin"
  // ?manager=<view>, from the admin setup checklist: open Manager directly on
  // that screen, skipping the PIN screen, ONLY when the account already signed
  // in (normally via the handoff in the same link) is allowed on this
  // restaurant. The parameter grants nothing by itself, and every action is
  // still checked by the server.
  const { claims: linkClaims, loading: linkAuthLoading } = useAuthUser();
  const [managerLink, setManagerLink] = useState(() => {
    const v = new URLSearchParams(window.location.search).get('manager');
    return MANAGER_LINK_VIEWS.includes(v) ? v : null;
  });
  const [managerInitialView, setManagerInitialView] = useState(null);
  useEffect(() => {
    if (!managerLink || linkAuthLoading) return;
    if (linkClaims && can(linkClaims, 'floor', RESTAURANT_ID)) {
      setAccessLevel(can(linkClaims, 'owner', RESTAURANT_ID) ? 'admin' : 'manager');
      setManagerInitialView(managerLink);
      setShowManager(true);
    } else {
      // Not a manager after all: this is a guest, who gets the welcome card.
      setShowWelcome(true);
    }
    setManagerLink(null);
    try {
      const params = new URLSearchParams(window.location.search);
      params.delete('manager');
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? `?${params}` : ''));
    } catch (e) {}
  }, [managerLink, linkAuthLoading, linkClaims]);
  const idleTimer = useRef(null);
  const [favorites, setFavorites] = useState([]);
  const [showShortlist, setShowShortlist] = useState(false);

  useEffect(() => {
    // Inject Google Fonts for Playfair Display (Tuque's theme)
    if (!document.getElementById('theme-fonts')) {
      const link = document.createElement('link');
      link.id = 'theme-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  // Settings carry the logo URLs, theme, menus-by-location and plan. A failed
  // or error response used to be final for the session: the catch opened the
  // gate in defaults, and a 500 (JSON without `settings`) was treated as a
  // tenant with no logos at all - the restaurant name showed where the logo
  // goes until someone reloaded. Now a bad response never overwrites what the
  // device already has (cached logos/themes), and the call is retried in the
  // background (2s, 5s, 10s) so the screen corrects itself.
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    const RETRY_DELAYS = [2000, 5000, 10000];
    function applySettings(data) {
      // Before setAppSettings, so the render it triggers is already in the
      // restaurant's language. No language set (every restaurant today) = English.
      // ?lang= wins over the restaurant's default.
      if (data.settings) setLanguage(URL_LANG || data.settings.language);
      setCurrencySettings(data.settings);
      setLanguageLimit(data.languageLimit);
      setEnabledLanguages(data.settings);
      setRoomNameOverrides(data.settings && data.settings.locationNamesI18n);
      setListLang(getRequestedLanguage());
      if (data.settings) setAppSettings(data.settings);
      if (Array.isArray(data.manualMenus)) setManualMenus(data.manualMenus);
      setCourseOverrides(data.courseOverrides);
      if (data.plan) setPlan(data.plan);
      if (data.isDemo) setDemoInfo({ isDemo: true, expired: data.demoExpired === true, expiresAt: data.demoExpiresAt || null });
      setRestaurantInactive(data.inactive === true);
      setIsPublicDemoTenant(data.publicDemo === true);
      if (data.settings?.locationNames) setLocationNames(data.settings.locationNames);
      const ds = data.settings?.deviceSetup || { defaultLocation: "bar", barLogo: "", diningLogo: "", barLogoUrl: "", diningLogoUrl: "", barAltLogoUrl: "", diningAltLogoUrl: "", printLogoUrl: "", barTheme: DEFAULT_THEME.name, diningTheme: DEFAULT_THEME.name };
      setDeviceSetup(ds);
      writeCachedThemes(ds.barTheme, ds.diningTheme);
      writeCachedLogos(ds);
      // If this device has never been assigned a location, apply the configured default
      if (!localStorage.getItem("tabletLocation")) {
        const def = ds.defaultLocation || "bar";
        localStorage.setItem("tabletLocation", def);
        setTabletLocationState(def);
      }
      // Open the gate once the restaurant's language file is here (at most 1.5 s),
      // so a French restaurant's first screen is French, not English then French.
      const want = URL_LANG || (data.settings && data.settings.language);
      Promise.race([loadLanguage(want), new Promise(r => setTimeout(r, 1500))])
        .then(() => { if (!cancelled) setSettingsResolved(true); });
    }
    function load(attempt) {
      rfetch(SETTINGS_URL)
        .then(r => {
          if (!r.ok) throw new Error(`getSettings ${r.status}`);
          return r.json();
        })
        .then(data => {
          if (cancelled) return;
          // getSettings answers a tenant with no settings with settings:null
          // and a 200 - that is a real answer and is applied. Only a failed
          // request or an error status is retried.
          applySettings(data || {});
        })
        .catch(err => {
          if (cancelled) return;
          console.warn('[settings] load failed, attempt', attempt + 1, err && err.message);
          // Open the gate on the first failure. Without this a settings outage
          // renders a blank screen to every guest instead of a usable app.
          setSettingsResolved(true);
          if (!localStorage.getItem("tabletLocation")) {
            localStorage.setItem("tabletLocation", "bar");
            setTabletLocationState("bar");
          }
          if (attempt < RETRY_DELAYS.length) {
            retryTimer = setTimeout(() => load(attempt + 1), RETRY_DELAYS[attempt]);
          }
        });
    }
    load(0);
    return () => { cancelled = true; if (retryTimer) clearTimeout(retryTimer); };
  }, []);

  useEffect(() => {
    fetchWines();
    let pollTimer;
    function scheduleNext() {
      pollTimer = setTimeout(() => { fetchWines(true); scheduleNext(); }, getPollingInterval());
    }
    scheduleNext();
    return () => clearTimeout(pollTimer);
  }, [tabletLocation, listLang]);

  // Walk-away reset. A tablet carried back to the host stand and one a guest set
  // down mid-meal look identical to every sensor on the device, so idle time is
  // the only signal available. 10 minutes is the compromise: long enough that a
  // table pausing to talk keeps its shortlist, short enough that an unattended
  // tablet is clean before the next party is likely to be seated. Staff moving
  // faster than that use Start Over on the home screen.
  //
  // A full reload rather than a soft state reset: it also regenerates
  // GUEST_SESSION_ID, so the next table gets its own pairing seed and its own
  // grouping in the session log. A soft reset would have carried both forward.
  useEffect(() => {
    function resetIdle() {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      // The walk-away reset exists for an UNATTENDED GUEST tablet - it has no
      // business firing while a manager has the Manager overlay open. A manager
      // watching a long, hands-off operation (bulk enrichment among them) can
      // easily go 10+ minutes with no touch, click, or scroll while it is
      // legitimately still working - that is exactly the idle signature this
      // timer is built to catch, just aimed at the wrong person. A hard reload
      // mid-enrichment kills the client-side loop driving it (see
      // runBulkEnrichment), which is exactly what happened here: 84 of 100
      // wines enriched, then silently stopped with no error on screen.
      //
      // Suspended entirely rather than merely reset by manager interactions,
      // because "manager is present but not touching anything" is the case
      // that broke, not "manager stepped away."
      if (showManager) return;
      // hardReload rather than a plain reload: this is the ONLY thing that runs
      // on an unattended tablet overnight, so it is also the mechanism by which
      // a deploy actually reaches the floor. A plain reload that gets handed the
      // cached bundle back means a tablet can serve old code indefinitely with
      // nothing on screen to say so.
      idleTimer.current = setTimeout(hardReload, 10 * 60 * 1000);
    }
    const events = ["touchstart", "touchmove", "click", "scroll"];
    events.forEach(e => document.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdle));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [showManager]);

  function toggleFavorite(item, itemType) {
    setFavorites(prev => {
      const exists = prev.find(f => f.id === item.id);
      if (exists) return prev.filter(f => f.id !== item.id);
      return [...prev, { ...item, favoriteType: itemType }];
    });
  }

  async function fetchWines() {
    try {
      // The room decides which menus are open here right now (menu hours and
      // rooms apply to wine since Oct 28, 2026). No room set: every room.
      const res = await rfetch(tabletLocation && tabletLocation !== "all"
        ? `${FIREBASE_URL}?location=${encodeURIComponent(tabletLocation)}` : FIREBASE_URL);
      const data = await res.json();
      if (data.wines) {
        registerSectionLabels(data.sectionLabels);
        WINE_SECTIONS_BY_COLOUR = data.sectionOrder === "colour";
        setWines(Array.isArray(data.wines) ? data.wines : Object.values(data.wines));
        setLastUpdated(data.lastUpdated);
      }
      setError(null);
    } catch (e) { setError("Unable to load wine list"); }
    finally { setLoading(false); }
  }

  // Wine filtering is now handled inside WineListScreen
  // Wine filtering moved to WineListScreen

  const shortlistOverlay = (
    <>
      {showShortlist && <ShortlistScreen favorites={favorites} onRemove={(id) => setFavorites(prev => prev.filter(f => f.id !== id))} onClose={() => setShowShortlist(false)}
        onNewPairing={planHasPairing(plan) ? () => { setShowShortlist(false); setPairingKey(k => k + 1); setScreen("sommelier"); window.scrollTo({ top: 0, behavior: "auto" }); } : null} />}
      {showPin && <PinScreen onSuccess={(level) => { setAccessLevel(level); setShowPin(false); setShowManager(true); }} onCancel={() => setShowPin(false)} />}
      {showManager && <ManagerScreen wines={wines} isAdmin={accessLevel === "admin"} onClose={() => { setShowManager(false); setAccessLevel(null); setManagerInitialView(null); }} tabletLocation={tabletLocation} onSetLocation={setTabletLocationPersist} locationNames={locationNames} plan={plan} onDeviceSetupSaved={setDeviceSetup} onLocationNamesSaved={setLocationNames} deviceSetup={deviceSetup} isPublicDemoTenant={isPublicDemoTenant} initialView={managerInitialView} />}
    </>
  );

  // Paint html/body to match the active theme. index.html sets a sane default,
  // but the palette is only known at runtime and two of the themes are light -
  // without this, switching to a light theme leaves a dark strip visible during
  // iOS overscroll bounce, and vice versa.
  useEffect(() => {
    const bg = activeTheme?.bgBase;
    if (!bg) return;
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', bg);
  }, [activeTheme]);

  // Measured, not assumed: the banner copy wraps to two lines on a phone. The
  // height is published as --somm-banner-h so sticky headers can offset by it
  // without prop-drilling through every screen component.
  const demoBannerRef = useRef(null);
  const [bannerH, setBannerH] = useState(0);
  // Public demo on a phone or tablet: one slim line saying this is a demo, with
  // "Start Free Trial". Desktop visitors get the SyncBev bar instead (see
  // DemoFrameGate), and the copy running inside that bar's iframe shows
  // nothing, since the bar already carries the button. Hidden on our own demo
  // kiosk tablet, and dismissible for the session. It shares the private-demo
  // banner's fixed container, so --somm-banner-h already moves every screen
  // and sticky header down to make room.
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(() => {
    try { return window.sessionStorage.getItem(DEMO_TRIAL_BANNER_DISMISSED_KEY) === '1'; } catch (e) { return false; }
  });
  const showTrialBanner = isPublicDemoTenant && !trialBannerDismissed
    && (() => { try { return window.self === window.top; } catch (e) { return false; } })()
    && !isOwnKioskDevice();
  const connectionIsLost = useConnectionLost();
  const kioskRuntime = isKioskRuntime();
  const showTopBanner = demoInfo.isDemo || showTrialBanner || connectionIsLost;
  useEffect(() => {
    const el = demoBannerRef.current;
    if (!showTopBanner || !el) {
      setBannerH(0);
      document.documentElement.style.setProperty('--somm-banner-h', '0px');
      return;
    }
    const measure = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      setBannerH(h);
      document.documentElement.style.setProperty('--somm-banner-h', h + 'px');
    };
    measure();
    // Rotating a tablet or resizing a desktop window rewraps the copy.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [showTopBanner, screen]);

  // Nothing themed paints until the theme is actually known. A fixed delay was the
  // obvious alternative and is worse on both ends: it makes every guest wait even
  // when settings land in 150ms, and on a slow connection it expires early and
  // flashes anyway. Waiting on the condition is faster in the common case and
  // correct in the bad one.
  //
  // Cached themes count as known, so a mounted tablet never sees this at all. It
  // exists for the cold load - a guest scanning a QR code on their own phone,
  // which is always a first load and cannot be cached for.
  const [settingsResolved, setSettingsResolved] = useState(false);
  useEffect(() => {
    // Safety valve. If the settings request hangs rather than failing - a captive
    // portal, a stalled connection - neither the success nor the error path ever
    // runs, and the gate would stay shut indefinitely. Better to show the default
    // theme after a moment than to show a guest nothing at all.
    const timer = setTimeout(() => setSettingsResolved(true), 2500);
    return () => clearTimeout(timer);
  }, []);
  const themeReady = settingsResolved || appSettings !== null || readCachedThemes() !== null;

  const wrapTheme = (el) => {
    if (!themeReady) {
      // Deliberately empty. Any text or logo here would itself need colours and
      // would be the flash we are removing. A blank field reads as loading; a
      // fully drawn welcome dialog in the wrong palette reads as broken.
      return (
        <div style={{
          position: 'fixed', inset: 0,
          background: (readCachedThemes() && ADMIN_THEME.bgBase) || SAFE_THEME_FLOOR.bgBase,
        }} />
      );
    }
    if (restaurantInactive) return <RestaurantUnavailable />;
    return themedTree(el);
  };

  const themedTree = (el) => (
    <ThemeContext.Provider value={activeTheme}>
      <DemoWallCard wall={demoWall} theme={activeTheme} onClose={() => setDemoWall(null)} />
      {showTopBanner && (
        <div ref={demoBannerRef} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000 }}>
        {/* First in the stack: when this shows, nothing else on screen works. Worded
            for the guest who sees it first, so staff hear about it straight away.
            Clears itself, and a kiosk reloads, when the connection returns. */}
        {connectionIsLost && (
          <div role="alert" style={{
            background: activeTheme.bgSurface, borderBottom: `2px solid ${activeTheme.error}`,
            padding: '9px 14px', textAlign: 'center', fontFamily: activeTheme.fontSerif,
          }}>
            <div style={{ color: activeTheme.error, fontSize: 13, fontWeight: 700, letterSpacing: '0.3px' }}>
              {tr("connection.title")}
            </div>
            <div style={{ color: activeTheme.textPrimary, fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>
              {kioskRuntime ? tr("connection.kiosk") : tr("connection.phone")}
            </div>
          </div>
        )}
        {showTrialBanner && (
          <div style={{
            background: activeTheme.bgSurface, borderBottom: `0.5px solid ${activeTheme.borderMid}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            padding: '0 4px 0 14px', minHeight: 32, fontFamily: activeTheme.fontSerif, fontSize: 12,
          }}>
            <span style={{ color: activeTheme.textSecondary }}>{tr("demo.trialBanner")}</span>
            <a href={DEMO_TRIAL_URL} target="_blank" rel="noopener noreferrer"
              style={{ color: activeTheme.accent, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', padding: '6px 0' }}>
              {tr("demo.startTrial")}
            </a>
            <button type="button" aria-label={tr("demo.hide")}
              onClick={() => { try { window.sessionStorage.setItem(DEMO_TRIAL_BANNER_DISMISSED_KEY, '1'); } catch (e) {} setTrialBannerDismissed(true); }}
              style={{ background: 'transparent', border: 'none', color: activeTheme.textMuted, fontSize: 16, lineHeight: 1, cursor: 'pointer', width: 32, height: 32, flexShrink: 0 }}>×</button>
          </div>
        )}
        {demoInfo.isDemo && (
        <div style={{
          background: activeTheme.accent, borderBottom: `1px solid ${activeTheme.accentBorder}`,
          padding: '7px 14px', textAlign: 'center', fontFamily: activeTheme.fontSerif,
        }}>
          {/* An unattended demo has to explain its own gaps: nobody is standing
              there to say why prices are missing. "May not be current" reads as a
              bug when there are none at all - so name the cause, and make the
              cause the pitch. */}
          <div style={{ color: activeTheme.accentText, fontSize: 11, lineHeight: 1.4, letterSpacing: '0.3px' }}>
            {tr("demo.privateBanner")}
          </div>
        </div>
        )}
        </div>
      )}
      {/* The "open on your phone" banner is gone. It existed because emoji rendered
          badly on Windows Chrome, and the emoji are no longer on the home screen.
          Keeping it would have told every desktop viewer the app was not meant for
          them - which is exactly wrong when a prospect is evaluating it on a laptop. */}
      <div style={{ paddingTop: bannerH }}>
        {el}
      </div>

      {/* The demo theme switcher is on the HOME screen only (its inline copy
          above the logo). It used to float bottom-right on every other screen
          too, and covered too much of the wine list, the pairing results and
          the dish list (owner's request, Oct 25, 2026). The chosen theme
          carries to every screen; changing it again means going Home. */}

    </ThemeContext.Provider>
  );
  if (screen === "home") return wrapTheme(<>{shortlistOverlay}<HomeScreen onNavigate={setScreen} favorites={favorites} onShowShortlist={() => setShowShortlist(true)} onAdminTap={() => setShowPin(true)} tabletLocation={tabletLocation || deviceSetup.defaultLocation || "bar"} deviceSetup={deviceSetup} locationNames={locationNames} settings={appSettings} manualMenus={manualMenus} plan={plan} showWelcome={showWelcome} onSetShowWelcome={setShowWelcome} isPublicDemoTenant={isPublicDemoTenant} demoThemeIndex={demoSwitcherIndex} demoDefaultIndex={demoDefaultIndex} onSetDemoThemeIndex={setDemoThemeIndex} demoSwitcherDismissed={demoSwitcherDismissed} onDismissDemoSwitcher={() => setDemoSwitcherDismissed(true)} guestLang={listLang} onSetGuestLang={changeGuestLanguage} /></>);
  if (screen === "wine") return wrapTheme(<>{shortlistOverlay}<WineListScreen wines={wines} favorites={favorites} onToggleFavorite={(item, type = "wine") => toggleFavorite(item, type)} onBack={() => setScreen("home")} onShowShortlist={() => setShowShortlist(true)} tabletLocation={tabletLocation} locationNames={locationNames} plan={plan} /></>);
  if (screen === "sommelier") return planHasPairing(plan) ? wrapTheme(<>{shortlistOverlay}<SommelierScreen key={pairingKey} chatEnabled={planHasChat(plan)} onBack={() => setScreen("home")} wines={wines} favorites={favorites} onToggleFavorite={(item, type = "wine") => toggleFavorite(item, type)} onShowShortlist={() => setShowShortlist(true)} tabletLocation={tabletLocation} locationNames={locationNames} /></>) : (setScreen("home"), null);
  // The list screens' own star passes only the item, so the default type applies.
  // The chat opened FROM these screens passes a type, and it must win: these used
  // to discard it, so a wine recommended in chat on the Beer List was starred as a
  // beer, and a tequila on the Cocktails screen as a cocktail.
  if (screen === "cocktails") return wrapTheme(<>{shortlistOverlay}<ItemListScreen title={tr("lists.cocktails.title")} allLabel={tr("lists.cocktails.all")} searchPlaceholder={tr("lists.cocktails.search")} endpoint={COCKTAILS_URL} dataKey="cocktails" onBack={() => setScreen("home")} favorites={favorites} onToggleFavorite={(item, type = "cocktail") => toggleFavorite(item, type)} onShowShortlist={() => setShowShortlist(true)} tabletLocation={tabletLocation} locationNames={locationNames} plan={plan} /></>);
  if (screen === "nab") return wrapTheme(<>{shortlistOverlay}<ItemListScreen title={tr("lists.nab.title")} allLabel={tr("lists.nab.all")} searchPlaceholder={tr("lists.nab.search")} endpoint={NAB_URL} dataKey="nab" onBack={() => setScreen("home")} favorites={favorites} onToggleFavorite={(item, type = "nab") => toggleFavorite(item, type)} onShowShortlist={() => setShowShortlist(true)} tabletLocation={tabletLocation} locationNames={locationNames} plan={plan} /></>);
  if (screen === "beer") return wrapTheme(<>{shortlistOverlay}<ItemListScreen title={tr("lists.beer.title")} allLabel={tr("lists.beer.all")} searchPlaceholder={tr("lists.beer.search")} endpoint={BEER_URL} dataKey="beers" onBack={() => setScreen("home")} favorites={favorites} onToggleFavorite={(item, type = "beer") => toggleFavorite(item, type)} onShowShortlist={() => setShowShortlist(true)} tabletLocation={tabletLocation} locationNames={locationNames} plan={plan} /></>);
  if (screen === "pours") return wrapTheme(<>{shortlistOverlay}<ItemListScreen title={tr("lists.pours.title")} allLabel={tr("lists.pours.all")} searchPlaceholder={tr("lists.pours.search")} endpoint={POURS_URL} dataKey="pours" onBack={() => setScreen("home")} favorites={favorites} onToggleFavorite={(item, type = "pour") => toggleFavorite(item, type)} onShowShortlist={() => setShowShortlist(true)} tabletLocation={tabletLocation} locationNames={locationNames} plan={plan} /></>);

  // All screens accounted for above — return home as fallback
  return wrapTheme(<>{shortlistOverlay}<HomeScreen onNavigate={setScreen} favorites={favorites} onShowShortlist={() => setShowShortlist(true)} onAdminTap={() => setShowPin(true)} tabletLocation={tabletLocation || deviceSetup.defaultLocation || "bar"} deviceSetup={deviceSetup} locationNames={locationNames} settings={appSettings} manualMenus={manualMenus} plan={plan} showWelcome={showWelcome} onSetShowWelcome={setShowWelcome} isPublicDemoTenant={isPublicDemoTenant} demoThemeIndex={demoSwitcherIndex} demoDefaultIndex={demoDefaultIndex} onSetDemoThemeIndex={setDemoThemeIndex} demoSwitcherDismissed={demoSwitcherDismissed} onDismissDemoSwitcher={() => setDemoSwitcherDismissed(true)} guestLang={listLang} onSetGuestLang={changeGuestLanguage} /></>);
}

function WineCard({ wine, selected, onSelect, isFavorited, onToggleFavorite, onZoomLabel }) {
  const t = useTheme();
  const [hovered, setHovered] = useState(false);
  // Safe to call the hook here despite one instance per wine: useViewport
  // subscribes to a shared module-level store, so 248 cards share one listener.
  const narrow = useIsNarrow();
  const textScale = useTextScale();
  const F = scaleFonts(narrow
    ? { name: 17.5, meta: 13, desc: 14, more: 12, price: 15, unit: 12,   ask: 12, gbPrice: 14, gbUnit: 11.5 }
    : { name: 16,   meta: 12, desc: 13, more: 11, price: 14, unit: 11.5, ask: 11, gbPrice: 13, gbUnit: 10.5 }, textScale);
  return (
    <div onClick={onSelect} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{
      display: "flex", alignItems: "center", gap: 12,
      background: selected ? t.white08 : hovered ? t.white06 : "transparent",
      borderLeft: selected ? `2px solid ${t.accent}` : "2px solid transparent",
      borderRadius: 8, padding: "11px 8px", cursor: "pointer",
      transition: "all 0.15s", opacity: wine.available === false ? 0.4 : 1
    }}>
      <div
        onClick={onZoomLabel ? e => { e.stopPropagation(); onZoomLabel(); } : undefined}
        style={{ width: 40, height: 56, borderRadius: 3, background: wine.imageUrl ? t.labelBackdrop : t.white06, border: `0.5px solid ${onZoomLabel ? t.accent : t.borderMid}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, overflow: "hidden", cursor: onZoomLabel ? "zoom-in" : "default" }}>
        {wine.imageUrl ? <img src={wine.imageUrl} alt={wine.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🍷"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: t.textPrimary, fontSize: F.name, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{wine.name}</div>
        {wine.varietal && (
          <div style={{ color: t.accentSecondary, fontSize: F.meta, letterSpacing: "0.3px", marginBottom: 2 }}>
            {wine.varietal}{wine.region ? ` · ${wine.region}` : ""}
          </div>
        )}
        {wine.description ? (
          <div style={{ color: t.textSecondary, fontSize: F.desc, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{wine.description} <span style={{ color: t.accent, fontSize: F.more }}>{tr("common.details")}</span></div>
        ) : (
          <div style={{ color: t.accent, fontSize: F.more, fontStyle: "italic" }}>{tr("common.tapForDetails")}</div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <button onClick={e => { e.stopPropagation(); onToggleFavorite && onToggleFavorite(wine); }} style={{ background: "none", border: "none", fontSize: narrow ? 20 : 18, cursor: "pointer", color: isFavorited ? t.accent : t.textSecondary, padding: "2px 0", lineHeight: 1, flexShrink: 0 }}>
          {isFavorited ? "★" : "☆"}
        </button>
        <div style={{ textAlign: "right", flexShrink: 0, minWidth: narrow ? 50 : 44 }}>
        {wine.available === false ? (
          <div style={{ background: t.errorDim, color: t.error, fontSize: narrow ? 11 : 10, padding: "3px 8px", borderRadius: 10, letterSpacing: "1px", textTransform: "uppercase", border: `0.5px solid ${t.errorBorder}` }}>{tr("wine.outOfStock")}</div>
        ) : wine.glassPrice && wine.bottlePrice ? (
          <div style={{ textAlign: "right" }}>
            <div style={{ color: t.textPrimary, fontSize: F.gbPrice, fontWeight: 500 }}>{formatPrice(wine.glassPrice)}<span style={{ color: t.textSecondary }}>/</span>{Math.round(wine.bottlePrice)}</div>
            <div style={{ color: t.accent, fontSize: F.gbUnit, marginTop: 1 }}>
              {tr("wine.glassBottle")}{wine.bottleSize ? ` · ${wine.bottleSize}` : ''}
            </div>
          </div>
        ) : wine.glassPrice ? (
          <>
            {/* The serving word carries as much meaning as the number: a guest
                reading "$40" and missing a 10px "glass" beneath it can believe
                that is the bottle price. Same weight, same colour as the price,
                so the two read as one statement. */}
            <div style={{ color: t.textPrimary, fontSize: F.price, fontWeight: 500 }}>{formatPrice(wine.glassPrice)}</div>
            <div style={{ color: t.accent, fontSize: F.unit, fontWeight: 500, marginTop: 1, letterSpacing: "0.4px" }}>{tr("wine.perGlass")}</div>
          </>
        ) : wine.bottlePrice ? (
          <>
            <div style={{ color: t.textPrimary, fontSize: F.price, fontWeight: 500 }}>{formatPrice(wine.bottlePrice)}</div>
            {/* A 1L is a third more wine than the 750ml a guest assumes. Shown
                only when non-standard - labelling every bottle "750ml" is noise. */}
            <div style={{ color: t.accent, fontSize: F.unit, fontWeight: 500, marginTop: 1, letterSpacing: "0.4px" }}>
              {tr("wine.perBottle")}{wine.bottleSize ? ` · ${wine.bottleSize}` : ''}
            </div>
          </>
        ) : (
          <span style={{ color: t.textSecondary, fontSize: F.ask, fontStyle: "italic" }}>{tr("common.ask")}</span>
        )}
        {/* Same wine also showing from another menu right now (a happy hour):
            name the menu so the two prices read like two printed cards. */}
        {wine.menuLabel && wine.available !== false && (
          <div style={{ color: t.textSecondary, fontSize: F.unit, marginTop: 2 }}>{wine.menuLabel}</div>
        )}
        </div>
      </div>
    </div>
  );
}

// ─── Root Export ─────────────────────────────────────────────────────────────
// Checks for shared menu QR link BEFORE rendering AppContent (avoids hook violations)

// ─── Error Boundary ───────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("App crashed:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const stack = this.state.errorInfo?.componentStack || "";
      return (
        <div style={{ minHeight: "100vh", background: CRASH_SCREEN.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: CRASH_SCREEN.font }}>
          <div style={{ maxWidth: 480, width: "100%" }}>
            <div style={{ color: CRASH_SCREEN.heading, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 12 }}><LineIcon name="alert" inline />App Error</div>
            <div style={{ color: CRASH_SCREEN.text, fontSize: 18, marginBottom: 16, lineHeight: 1.4 }}>{err?.message || "Something went wrong"}</div>
            <div style={{ background: CRASH_SCREEN.panel, border: `0.5px solid ${CRASH_SCREEN.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
              <div style={{ color: CRASH_SCREEN.label, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>Stack Trace</div>
              <pre style={{ color: CRASH_SCREEN.mono, fontSize: 10, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
                {err?.stack || "No stack available"}
              </pre>
            </div>
            {stack && (
              <div style={{ background: CRASH_SCREEN.panel, border: `0.5px solid ${CRASH_SCREEN.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 20 }}>
                <div style={{ color: CRASH_SCREEN.label, fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>Component Stack</div>
                <pre style={{ color: CRASH_SCREEN.mono, fontSize: 10, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>{stack}</pre>
              </div>
            )}
            <button onClick={() => { this.setState({ hasError: false, error: null, errorInfo: null }); window.location.reload(); }}
              style={{ background: CRASH_SCREEN.accentDim, border: `0.5px solid ${CRASH_SCREEN.accentBorder}`, color: CRASH_SCREEN.accent, padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontFamily: CRASH_SCREEN.font, fontSize: 13 }}>
              Reload App
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── SommSync Admin Portal ────────────────────────────────────────────────────
// ─── Restaurant setup checklist (admin Onboarding tab) ───────────────────────
//
// Walks a super user through one restaurant's setup in order, with the real
// state of each step read from the server (getPipelineStatus ?checklist=1).
// The steps differ by where the menu comes from: a manual restaurant uploads
// its menu; a POS restaurant connects and maps menus. Most of the work happens
// in the restaurant's own Manager screen, which opens in a new tab, signed in.

// Always the platform address, not {id}.getsommsync.com: a subdomain has to be
// added by hand in Firebase Hosting and DNS and can take a day for its
// certificate, so a new restaurant usually does not have one yet. ?restaurant=
// works the moment the restaurant exists (see DOMAINS.md). `view` opens Manager
// on that screen once the handoff has signed the user in.
async function openRestaurantTab(restaurantId, view) {
  // Opened before the await so a popup blocker treats it as the click's own window.
  const w = window.open('about:blank', '_blank');
  try {
    const nonce = await mintHandoff(restaurantId);
    const params = new URLSearchParams({ restaurant: restaurantId, ...(view ? { manager: view } : {}), h: nonce });
    const url = `https://sommsync-platform.web.app/?${params}`;
    if (w) w.location.href = url; else window.location.href = url;
  } catch (e) {
    if (w) w.close();
    window.alert(`Could not open ${restaurantId}: ${e.message}`);
  }
}

function SetupChecklist({ restaurantId, refreshKey, onRunResearch, onShowMatrix }) {
  const t = useTheme();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const res = await authFetch(`${PIPELINE_STATUS_URL}?restaurantId=${restaurantId}&checklist=1`);
      const data = await res.json();
      if (data.checklist) { setC(data.checklist); setError(null); } else setError(data.error || 'Could not load the checklist.');
    } catch (e) { setError(e.message); }
  }, [restaurantId]);
  useEffect(() => { setC(null); load(); }, [load, refreshKey]);

  async function setLive(isLive) {
    if (isLive && !window.confirm(`Mark ${c.name} as live? This is what the restaurant list shows as Live.`)) return;
    setBusy(true);
    try {
      const res = await authFetch(UPDATE_RESTAURANT_PLAN_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restaurantId, isLive }) });
      const data = await res.json();
      if (data.ok) await load(); else setError(data.error || 'Not changed.');
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  if (error) return <div style={{ color: t.error, fontSize: 12, marginBottom: 16 }}>{error}</div>;
  if (!c) return <div style={{ color: t.textDim, fontSize: 12, marginBottom: 16 }}>Loading setup status…</div>;

  const manual = !c.liveSync;
  const tzLabel = tzLabelFor(c.timezone);
  const it = c.items || {};
  const itemParts = [[it.wines, 'wines'], [it.foodItems, 'dishes'], [it.beers, 'beers'], [it.pours, 'spirits'], [it.cocktails, 'cocktails'], [it.nab, 'non-alcoholic']]
    .filter(([n]) => n > 0).map(([n, l]) => `${n} ${l}`);
  const r = c.research || {};
  const bev = ['wine', 'beer', 'pours'].map(k => r[k] || { total: 0, researched: 0, approved: 0 });
  const total = bev.reduce((n, x) => n + x.total, 0);
  const researched = bev.reduce((n, x) => n + x.researched, 0);
  const approved = bev.reduce((n, x) => n + x.approved, 0);
  const hasMenu = itemParts.length > 0;
  const open = view => () => openRestaurantTab(restaurantId, view);
  const where = (path) => `Opens ${path} in a new tab.`;

  const steps = [
    { title: 'Restaurant created', state: 'done', detail: `${c.name}. ${tzLabel}. Plan: ${PLAN_LABELS[c.plan] || PLAN_LABELS.print}. Menu source: ${c.posName}.` },
    manual
      ? { title: 'Upload the menu', state: hasMenu ? 'done' : 'todo',
          detail: hasMenu
            ? `${itemParts.join(', ')}.${c.draftsWaiting ? ` ${c.draftsWaiting} upload${c.draftsWaiting === 1 ? '' : 's'} still waiting for review.` : ''}`
            : `Nothing yet. PDF, photos, Word or Illustrator files all work. ${where('Update Menu → Upload a menu')}`,
          warn: c.draftsWaiting > 0,
          action: { label: hasMenu ? 'Update Menu' : 'Upload the menu', onClick: open('sync') } }
      : { title: `Map the ${c.posName} menus`, state: c.posMenusMapped > 0 ? 'done' : 'todo',
          detail: c.posMenusMapped > 0
            ? `${c.posMenusMapped} ${c.posName} menu${c.posMenusMapped === 1 ? '' : 's'} mapped${hasMenu ? `: ${itemParts.join(', ')}` : ''}.${c.posOwned.length < 6 ? ' Categories with no mapped menu stay file-managed.' : ''}`
            : `${where('Settings → Menus')} Each category moves to ${c.posName} once its menu is mapped.`,
          action: { label: 'Settings', onClick: open('settings') } },
    ...(manual ? [{ title: 'Set menu hours and rooms', state: c.manualMenus > 0 ? 'done' : 'optional',
        detail: c.manualMenus > 0 ? `${c.manualMenus} menu${c.manualMenus === 1 ? '' : 's'} set up.` : `Optional. Without menus everything shows all day in both rooms. ${where('Menus & Items')}`,
        action: { label: 'Menus & Items', onClick: open('menus') } }] : []),
    { title: 'Research wine, beer and spirits', state: total === 0 ? 'blocked' : researched >= total ? 'done' : 'todo',
      detail: total === 0 ? 'Nothing to research yet.' : `${researched} of ${total} researched (description, region, label search).`,
      action: total > 0 && researched < total ? { label: manual ? 'Run research' : 'Run sync and research', onClick: onRunResearch } : null },
    { title: 'Approve for guests', state: total === 0 ? 'blocked' : approved >= total ? 'done' : 'todo',
      detail: total === 0 ? 'Guests only see approved items.' : `${approved} of ${total} approved. Guests only see approved items. ${where('Menu items')}`,
      action: total > 0 && approved < total ? { label: 'Review', onClick: open('review') } : null },
    ...(planHasPairing(c.plan) ? [{ title: 'Build pairings', state: !c.matrix || c.matrix.pairableDishes === 0 || it.wines === 0 ? 'blocked' : c.matrix.dishesScored >= c.matrix.pairableDishes ? 'done' : 'todo',
        detail: !c.matrix || c.matrix.pairableDishes === 0 ? 'Needs the food menu and approved wines first.' : `${c.matrix.dishesScored} of ${c.matrix.pairableDishes} dishes scored. Rebuild after a menu change.`,
        action: c.matrix && c.matrix.pairableDishes > 0 ? { label: 'Pairing matrix', onClick: onShowMatrix } : null }] : []),
    { title: 'Logos and theme', state: c.hasLogo ? 'done' : 'optional', detail: c.hasLogo ? 'A logo is uploaded.' : `Optional. ${where('Settings')}`, action: { label: 'Settings', onClick: open('settings') } },
    { title: 'Go live', state: c.isLive ? 'done' : 'todo', detail: c.isLive ? 'Shown as Live in the restaurant list.' : 'Mark it live once guests are using it.',
      action: { label: c.isLive ? 'Back to setup' : 'Mark live', onClick: () => setLive(!c.isLive), quiet: c.isLive } },
  ];
  const nextIdx = steps.findIndex(s => s.state === 'todo');

  return (
    <div style={{ background: t.bgSurface, borderRadius: 12, padding: 24, border: `1px solid ${t.borderSubtle}`, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
        <div style={{ color: t.textPrimary, fontSize: 16, flex: 1 }}>Setup for {c.name}</div>
        <button onClick={load} disabled={busy} style={{ background: 'none', border: 'none', color: t.textDim, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12 }}>Refresh</button>
      </div>
      {steps.map((s, i) => {
        const isNext = i === nextIdx;
        const mark = s.state === 'done' ? '✓' : s.state === 'optional' ? '–' : isNext ? '●' : '';
        const color = s.state === 'done' ? t.success : isNext ? t.accent : t.textDim;
        return (
          <div key={s.title} style={{ display: 'flex', gap: 12, padding: '10px 0', borderTop: i ? `0.5px solid ${t.borderSubtle}` : 'none' }}>
            <div style={{ width: 22, height: 22, flexShrink: 0, borderRadius: '50%', border: `1.5px solid ${color}`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, marginTop: 1 }}>{mark}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: s.state === 'done' ? t.textSecondary : t.textPrimary, fontSize: 14 }}>{s.title}</div>
              <div style={{ color: s.warn ? t.warning : t.textDim, fontSize: 12, lineHeight: 1.5, marginTop: 2 }}>{s.detail}</div>
            </div>
            {s.action && (
              <button onClick={s.action.onClick} disabled={busy}
                style={{ alignSelf: 'center', flexShrink: 0, background: isNext && !s.action.quiet ? t.accent : 'none', color: isNext && !s.action.quiet ? t.bgBase : t.textSecondary,
                  border: isNext && !s.action.quiet ? 'none' : `0.5px solid ${t.borderMid}`, padding: '7px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12, whiteSpace: 'nowrap' }}>
                {s.action.label}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// What each feature level means to a guest. The stored values (premium / base / print) are
// unchanged. Both levels have AI: base is instant pairings (the pairing button on each item, the "Get a
// wine pairing" screen); premium adds the sommelier chat bot (a paid add-on). See planHasPairing / planHasChat.
// print is retired: the print module is part of every plan now. The stored value still exists for restaurants
// saved that way, so it keeps a label (shown on their card) but is no longer offered when choosing a plan.
const PLAN_LABELS = { premium: 'Instant pairings + sommelier chat bot', base: 'Instant pairings', print: 'Print only (retired)' };

// What a restaurant is on in Stripe, in words, for its card in the admin list. Read-only: billing is
// managed in Stripe and by the owner's Account & billing screen. `b` is billingSummary() from the server.
// Returns { main, status, tone } where tone is 'ok' | 'warn' | 'bad' | 'dim', or null when the server
// did not send billing (an older server), so the card simply shows nothing.
function describeBilling(b) {
  if (!b) return null;
  const date = ms => (ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
  const cap = w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : '');
  if (b.access === 'exempt') return { main: 'Billing exempt (no charge)', status: '', tone: 'dim' };
  if (b.access === 'none' || !b.tier) return { main: 'No subscription yet', status: '', tone: 'dim' };
  const parts = [`${cap(b.tier)}${b.interval ? ' ' + b.interval : ''}`];
  if (b.chat) parts.push('with chat bot');
  if (b.extraWines) parts.push(`+${b.extraWines} wine${b.extraWines === 1 ? '' : 's'}`);
  if (b.extraLanguages) parts.push(`+${b.extraLanguages} language${b.extraLanguages === 1 ? '' : 's'}`);
  let status = '', tone = 'ok';
  if (b.status === 'trialing') status = b.trialEnd ? `trial ends ${date(b.trialEnd)}` : 'in trial';
  else if (b.status === 'active') {
    if (b.cancelAtPeriodEnd) { status = `cancels ${date(b.cancelAt || b.currentPeriodEnd)}`; tone = 'warn'; }
    else status = b.currentPeriodEnd ? `renews ${date(b.currentPeriodEnd)}` : 'active';
  } else if (b.status === 'past_due') { status = `payment past due${b.pastDueSince ? ' since ' + date(b.pastDueSince) : ''}`; tone = 'warn'; }
  else { status = (b.status || 'inactive').replace(/_/g, ' '); tone = 'bad'; }
  if (b.pending) {
    const was = { tier: b.tier, interval: b.interval, chat: b.chat };
    const next = [];
    if (b.pending.tier && b.pending.tier !== was.tier) next.push(cap(b.pending.tier));
    if (b.pending.interval && b.pending.interval !== was.interval) next.push(b.pending.interval);
    if (!!b.pending.chat !== !!was.chat) next.push(b.pending.chat ? 'chat bot added' : 'chat bot removed');
    // Scheduled changes land at renewal; say so instead of repeating the renewal date.
    const atRenewal = b.pending.effectiveAt && b.currentPeriodEnd && Math.abs(b.pending.effectiveAt - b.currentPeriodEnd) < 36 * 3600 * 1000;
    if (next.length) status += ` · changing to ${next.join(', ')} ${atRenewal ? 'at renewal' : 'on ' + date(b.pending.effectiveAt)}`;
  }
  return { main: parts.join(' · '), status, tone };
}

// The pop-up warning shown before a restaurant's feature level is changed from its card (like the one for
// the timezone). It says what guests will notice, and, when the billing summary is there, whether Stripe
// agrees. Nothing here changes billing: that stays in Stripe.
function planChangeWarning(r, newPlan, label) {
  const from = PLAN_LABELS[r.plan] || r.plan || 'no plan';
  const to = PLAN_LABELS[newPlan] || newPlan;
  const b = r.billing || null;
  const lines = [`Change ${label} from \"${from}\" to \"${to}\"?`, ''];
  if (r.plan === 'print') {
    lines.push(`Their guest app turns on${planHasChat(newPlan) ? ', with instant pairings and the sommelier chat bot' : ' with instant pairings'}.`);
  } else if (planHasChat(newPlan) && !planHasChat(r.plan)) {
    lines.push('Their guests get the sommelier chat bot on top of instant pairings.');
    if (b && (b.access === 'on' || b.access === 'grace') && !b.chat) lines.push('', 'Their Stripe subscription does not include the chat bot add-on yet.');
  } else if (!planHasChat(newPlan) && planHasChat(r.plan)) {
    lines.push('Their guests lose the sommelier chat bot (the chat buttons disappear). Instant pairings stay.');
    if (b && b.chat) lines.push('', 'Their subscription still includes the chat bot add-on, so it keeps billing until it is removed in Stripe.');
  } else {
    lines.push('Their guests will see a different set of features.');
  }
  lines.push('', 'This only switches the feature. What they pay stays in Stripe.');
  return lines.join('\n');
}

// Small line icons for the admin portal. They replace emoji, which draw as coloured pictures that ignore the
// theme and look different on every device. 16x16 grid, drawn with currentColor, so an icon takes the colour of
// the button or label it sits in and follows whichever theme is active.
const LINE_ICONS = {
  left: <path d="M10 3L5 8l5 5" />,
  right: <path d="M6 3l5 5-5 5" />,
  check: <path d="M3.5 8.5l3 3 6-7" />,
  search: <><circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5L14 14" /></>,
  upload: <path d="M8 11V3M5 6l3-3 3 3M3 13h10" />,
  // "No image": a picture (a sun and a hill) with a slash through it. The frame has a gap where the slash passes, so the slash
  // ends cleanly instead of overshooting a closed frame and leaving round blobs at two corners. The sun is a solid dot.
  noimg: <><path d="M5.2 3H12.2a1.6 1.6 0 0 1 1.6 1.6V9.6" /><path d="M2.2 5.8V11.4a1.6 1.6 0 0 0 1.6 1.6H9.4" /><path d="M2.4 2.7L13.6 13.3" /><circle cx="10.7" cy="6.1" r="1" fill="currentColor" stroke="none" /><path d="M3.7 11.2l1.8-2.1 1.5 1.4" /></>,
  flag: <path d="M4 14V2.5M4 3h7l-1.5 3 1.5 3H4" />,
  // the portal menu
  store: <><path d="M2 6.2l1.3-3.4h9.4L14 6.2" /><path d="M2 6.2a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" /><path d="M3 8.7v4.8h10V8.7" /><path d="M6.5 13.5v-3h3v3" /></>,
  clip: <><path d="M4.5 3.5h7a1 1 0 0 1 1 1V13a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1z" /><path d="M6 3.5v-1h4v1" /><path d="M5.8 9l1.6 1.6L10.4 7.4" /></>,
  image: <><rect x="2.5" y="3" width="11" height="10" rx="1.5" /><circle cx="6" cy="6.6" r="1" /><path d="M2.7 11.6L6 8.4l2.6 2.6 1.8-1.8 3.1 3" /></>,
  pencil: <><path d="M10.8 2.6l2.6 2.6L5.6 13l-3.1.6.6-3.1z" /><path d="M9.3 4.1l2.6 2.6" /></>,
  chart: <><path d="M4 12.5V8M8 12.5V3.5M12 12.5V6" /><path d="M2 13.5h12" /></>,
  user: <><circle cx="8" cy="5.4" r="2.6" /><path d="M3 13.6c0-2.8 2.2-4.5 5-4.5s5 1.7 5 4.5" /></>,
  refresh: <><path d="M13 8a5 5 0 1 1-1.6-3.7" /><path d="M13 2.8v2.9h-2.9" /></>,
  layers: <><path d="M8 2.5l6 3-6 3-6-3z" /><path d="M2 8.5l6 3 6-3" /><path d="M2 11.5l6 3 6-3" /></>,
  // Manager, settings and status (Nov 3, 2026)
  checkCircle: <><circle cx="8" cy="8" r="6" /><path d="M5.4 8.2l1.8 1.8 3.4-3.8" /></>,
  xCircle: <><circle cx="8" cy="8" r="6" /><path d="M5.8 5.8l4.4 4.4M10.2 5.8l-4.4 4.4" /></>,
  // The warning triangle: rounded corners like the rest of the set, a stem and a SOLID dot kept apart (the first version
  // pressed the dot into the stem, and its sharp corners and crowded mark looked cramped at small sizes).
  alert: <><path d="M7.16 3.08Q8 1.6 8.84 3.08L13.81 11.86Q14.8 13.6 12.8 13.6H3.2Q1.2 13.6 2.19 11.86Z" /><path d="M8 6.4v2.6" /><circle cx="8" cy="11.2" r="1" fill="currentColor" stroke="none" /></>,
  hourglass: <><path d="M4.5 2.5h7M4.5 13.5h7" /><path d="M5 2.5c0 3 3 3.6 3 5.5s-3 2.5-3 5.5M11 2.5c0 3-3 3.6-3 5.5s3 2.5 3 5.5" /></>,
  clock: <><circle cx="8" cy="8" r="6" /><path d="M8 4.8V8l2.2 1.4" /></>,
  calendar: <><rect x="2.5" y="3.5" width="11" height="10" rx="1.5" /><path d="M2.5 6.8h11M5.5 2v3M10.5 2v3" /></>,
  pin: <><path d="M8 14s4.5-4 4.5-7.5a4.5 4.5 0 0 0-9 0C3.5 10 8 14 8 14z" /><circle cx="8" cy="6.5" r="1.6" /></>,
  tablet: <><rect x="4.5" y="1.8" width="7" height="12.4" rx="1.6" /><path d="M7 12h2" /></>,
  link: <><path d="M6.8 9.2a2.6 2.6 0 0 0 3.7 0l2-2a2.6 2.6 0 0 0-3.7-3.7l-.6.6" /><path d="M9.2 6.8a2.6 2.6 0 0 0-3.7 0l-2 2a2.6 2.6 0 0 0 3.7 3.7l.6-.6" /></>,
  wine: <><path d="M5 2h6c0 3.5-.6 5.6-3 6.2C5.6 7.6 5 5.5 5 2z" /><path d="M8 8.2V13M5.5 14h5" /></>,
  cocktail: <><path d="M2.5 3h11L8 8.8z" /><path d="M8 8.8V13.5M5.3 14h5.4" /></>,
  utensils: <path d="M4.5 2v4.2a1.5 1.5 0 0 0 3 0V2M6 2v12M11.5 14V2C9.6 3 8.8 5 8.8 7.4V9h2.7" />,
  camera: <><path d="M2.5 5.2h2.2l1-1.7h4.6l1 1.7h2.2v7.3h-11z" /><circle cx="8" cy="8.7" r="2.2" /></>,
  sparkle: <><path d="M8 2.5l1.2 3.3 3.3 1.2-3.3 1.2L8 11.5 6.8 8.2 3.5 7l3.3-1.2z" /><path d="M12.5 11v3M11 12.5h3" /></>,
  download: <path d="M8 2.5v8M5 7.8l3 3 3-3M3 13.5h10" />,
  undo: <><path d="M5.5 3.5L2.8 6.2l2.7 2.7" /><path d="M3 6.2h6.2a3.8 3.8 0 0 1 0 7.6H6" /></>,
  play: <path d="M5 3.2v9.6L12.5 8z" />,
  pause: <path d="M5.5 3v10M10.5 3v10" />,
  repeat: <><path d="M3 7.2V6a2 2 0 0 1 2-2h7.5M10.5 2l2 2-2 2" /><path d="M13 8.8V10a2 2 0 0 1-2 2H3.5M5.5 14l-2-2 2-2" /></>,
  checkbox: <><rect x="2.5" y="2.5" width="11" height="11" rx="2" /><path d="M5.5 8.2l1.8 1.8 3.2-3.6" /></>,
  document: <><path d="M4 1.8h5l3 3v9.4H4z" /><path d="M9 1.8v3h3M6 8h4M6 10.5h4" /></>,
  sliders: <><path d="M3 5h5M11 5h2M3 11h2M8 11h5" /><circle cx="9.5" cy="5" r="1.5" /><circle cx="6.5" cy="11" r="1.5" /></>,
  lock: <><rect x="3.5" y="7" width="9" height="6.5" rx="1.5" /><path d="M5.5 7V5.2a2.5 2.5 0 0 1 5 0V7" /></>,
  radio: <><path d="M4.2 11.8a5 5 0 0 1 0-7.6M11.8 4.2a5 5 0 0 1 0 7.6M6.3 9.7a2.2 2.2 0 0 1 0-3.4M9.7 6.3a2.2 2.2 0 0 1 0 3.4" /><circle cx="8" cy="8" r=".6" /></>,
  chip: <><rect x="4.5" y="4.5" width="7" height="7" rx="1.2" /><path d="M6.5 2v2.5M9.5 2v2.5M6.5 11.5V14M9.5 11.5V14M2 6.5h2.5M2 9.5h2.5M11.5 6.5H14M11.5 9.5H14" /></>,
  ban: <><circle cx="8" cy="8" r="5.5" /><path d="M4.2 11.8L11.8 4.2" /></>,
  card: <><rect x="2" y="3.5" width="12" height="9" rx="1.8" /><path d="M2 7h12M4.5 10.5h2.5" /></>,
};
// `inline` puts the icon at the start of a line of text: it takes the text's size (a little larger), sits on its
// baseline and leaves a gap before the words. Without it the icon is a plain block of `size` pixels.
function LineIcon({ name, size, strokeWidth = 1.7, inline = false }) {
  const px = size === undefined ? (inline ? '1.15em' : 16) : size;
  return (
    <svg width={px} height={px} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
      style={inline ? { flex: 'none', verticalAlign: '-0.2em', marginRight: '0.45em' } : { flex: 'none' }}>
      {LINE_ICONS[name]}
    </svg>
  );
}
// A button that holds an icon and a label side by side.
const ICON_BTN = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 };

// The first of `candidates` that reads on `bg` at the usual 4.5:1, else whichever of black and white reads better.
// For text on a solid colour from the theme (the Approve button): the theme's own surface colour is tried first,
// because each theme tunes its success green to read against it, so nearly every theme keeps its own look.
function readableOn(bg, candidates) {
  const lum = (hex) => {
    const m = /^#([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return null;
    const ch = [0, 2, 4].map(i => parseInt(m[1].slice(i, i + 2), 16) / 255).map(c => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
    return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
  };
  const ratio = (x, y) => { const a = lum(x), b = lum(y); if (a === null || b === null) return null; return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05); };
  for (const c of candidates) { const r = ratio(c, bg); if (r !== null && r >= 4.5) return c; }
  const w = ratio('#ffffff', bg), k = ratio('#000000', bg);
  if (w === null || k === null) return candidates[0];
  return w >= k ? '#ffffff' : '#000000';
}
// (end of line-icon helpers)

// A small coloured dot for a status that is only to be read (green fine, amber worth a look, red a real problem,
// grey neutral). Used on the restaurant cards, where the readouts used to be boxes that looked clickable.
function StatusDot({ tone, t }) {
  const c = { ok: t.success, warn: t.warning, bad: t.error }[tone] || t.textDim;
  return <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c, flex: 'none' }} />;
}

// ─── Master DB cleanup (Nov 4, 2026) ─────────────────────────────────────────
// Phase A of the duplicate cleanup plan in CLAUDE.md: a screen over
// auditMasterDuplicates (report, merge, "not the same") and
// verifyMasterPointers. Three kinds of candidate, safest first:
//   exact        - keys collide once re-derived: the same product
//   nearMiss     - one name is the other plus a word or two at the end
//   sameProducer - same producer (ignoring "Vineyards", "Winery"...) and the
//                  same varietal and vintage; includes real siblings such as
//                  Silver Oak Napa vs Alexander Valley, so read the fields
// Nothing merges without a click and a confirm. The kept record gets the best
// label and any found critic scores; every restaurant item is repointed.
const MASTER_TYPES = [['wines', 'Wines'], ['beers', 'Beers'], ['spirits', 'Spirits']];
const MASTER_SECTIONS = [
  ['exact', 'Same product', 'Their keys collide once rebuilt the current way. Safe to merge.'],
  ['nearMiss', 'Probably the same', 'One name is the other with a word or two added at the end (a region, "Brut", "Estate"). Usually the same wine, but check.'],
  ['sameProducer', 'Same producer — check', 'Same producer, varietal and vintage, but worded differently. Some are duplicates; some are genuinely different wines from one producer (Silver Oak Napa vs Alexander Valley). Compare the highlighted fields.'],
];

function MasterDbTab({ t, restaurants }) {
  const [type, setType] = useState('wines');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null);
  const [keepers, setKeepers] = useState({});
  // Groups ticked for a bulk merge / "not the same" - one confirm for all of them.
  const [selected, setSelected] = useState(() => new Set());
  const [links, setLinks] = useState(null);
  const [linksLoading, setLinksLoading] = useState(false);
  const restoName = id => (restaurants.find(r => r.id === id) || {}).name || id;

  async function runAudit(forType = type) {
    setLoading(true); setMsg(null); setReport(null); setKeepers({}); setSelected(new Set());
    try {
      const res = await authFetch(AUDIT_MASTER_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: forType }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) setMsg({ ok: false, text: `Couldn't run the check: ${data.error || `the server answered ${res.status}`}.` });
      else setReport(data);
    } catch (e) { setMsg({ ok: false, text: `Couldn't run the check: ${e.message}` }); }
    setLoading(false);
  }

  async function runLinks() {
    setLinksLoading(true); setLinks(null);
    try {
      const res = await authFetch(VERIFY_MASTER_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setLinks(await res.json().catch(() => ({ error: `The server answered ${res.status}` })));
    } catch (e) { setLinks({ error: e.message }); }
    setLinksLoading(false);
  }

  function dropGroups(ids) {
    const gone = new Set(ids);
    setReport(prev => prev && ({ ...prev, ...Object.fromEntries(MASTER_SECTIONS.map(([k]) => [k, (prev[k] || []).filter(g => !gone.has(g.id))])) }));
    setSelected(prev => new Set([...prev].filter(id => !gone.has(id))));
  }
  const dropGroup = id => dropGroups([id]);
  const allGroups = () => report ? MASTER_SECTIONS.flatMap(([k]) => report[k] || []) : [];
  const toggle = id => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleSection = groups => setSelected(prev => {
    const n = new Set(prev); const all = groups.every(g => n.has(g.id));
    groups.forEach(g => { if (all) n.delete(g.id); else n.add(g.id); });
    return n;
  });

  // Bulk merge: one confirm, one request. Each group keeps the record marked Keep.
  async function mergeSelected() {
    const groups = allGroups().filter(g => selected.has(g.id));
    if (!groups.length) return;
    const lines = groups.map(g => {
      const w = g.records.find(r => r.key === (keepers[g.id] || g.records[0].key));
      return `• keep "${w.name}", remove ${g.records.length - 1}`;
    });
    if (!window.confirm(`Merge ${groups.length} group(s)?\n\n${lines.slice(0, 25).join('\n')}${lines.length > 25 ? `\n…and ${lines.length - 25} more` : ''}\n\n` +
      `Each keeps the record marked Keep, moves every restaurant item onto it, and carries over the best label and any critic scores. This can't be undone.`)) return;
    setBusyId('bulk'); setMsg(null);
    try {
      const res = await authFetch(AUDIT_MASTER_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, apply: true, groups: groups.map(g => ({ keys: g.keys, winner: keepers[g.id] || g.records[0].key })) }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) { setMsg({ ok: false, text: `Not merged: ${data.error || `the server answered ${res.status}`}.` }); }
      else {
        const applied = data.applied || [];
        const done = groups.filter((g, i) => applied[i] && !applied[i].skipped).map(g => g.id);
        const moved = applied.reduce((n, a) => n + (a.repointed || 0), 0);
        const skipped = applied.filter(a => a.skipped).length;
        dropGroups(done);
        setMsg({ ok: skipped === 0, text: `Merged ${done.length} group(s), ${moved} restaurant item(s) moved.${skipped ? ` ${skipped} skipped (a record in them no longer exists - run Find duplicates again).` : ''}` });
      }
    } catch (e) { setMsg({ ok: false, text: `Not merged: ${e.message}` }); }
    setBusyId(null);
  }

  async function dismissSelected() {
    const groups = allGroups().filter(g => selected.has(g.id));
    if (!groups.length) return;
    if (!window.confirm(`Mark ${groups.length} group(s) as different products? They won't be shown again.`)) return;
    setBusyId('bulk'); setMsg(null);
    try {
      const res = await authFetch(AUDIT_MASTER_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, action: 'dismiss', groups: groups.map(g => g.keys) }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) setMsg({ ok: false, text: `Couldn't save that: ${data.error || `the server answered ${res.status}`}.` });
      else { dropGroups(groups.map(g => g.id)); setMsg({ ok: true, text: `Marked ${groups.length} group(s) as different products.` }); }
    } catch (e) { setMsg({ ok: false, text: `Couldn't save that: ${e.message}` }); }
    setBusyId(null);
  }

  async function merge(g) {
    const winner = keepers[g.id] || g.records[0].key;
    const keep = g.records.find(r => r.key === winner);
    const others = g.records.filter(r => r.key !== winner);
    const ok = window.confirm(
      `Merge into "${keep.name}" (${keep.key})?\n\n` +
      others.map(r => `• "${r.name}" (${r.key}) will be removed`).join('\n') +
      `\n\nEvery restaurant item pointing at a removed record is moved to the kept one. ` +
      `The kept record takes the best approved label and any critic scores found. This can't be undone.`);
    if (!ok) return;
    setBusyId(g.id); setMsg(null);
    try {
      const res = await authFetch(AUDIT_MASTER_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, apply: true, groups: [{ keys: g.keys, winner }] }) });
      const data = await res.json().catch(() => ({}));
      const a = (data.applied || [])[0];
      if (!res.ok || data.error || !a || a.skipped) {
        setMsg({ ok: false, text: `Not merged: ${data.error || (a && a.skipped) || `the server answered ${res.status}`}.` });
      } else {
        dropGroup(g.id);
        setMsg({ ok: true, text: `Merged into ${a.winner}: ${a.removed.length} record(s) removed, ${a.repointed} restaurant item(s) moved` +
          `${a.keptImage ? ', label carried over' : ''}${a.keptScores ? ', critic scores carried over' : ''}.` });
      }
    } catch (e) { setMsg({ ok: false, text: `Not merged: ${e.message}` }); }
    setBusyId(null);
  }

  async function dismiss(g) {
    setBusyId(g.id); setMsg(null);
    try {
      const res = await authFetch(AUDIT_MASTER_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, action: 'dismiss', keys: g.keys }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) setMsg({ ok: false, text: `Couldn't save that: ${data.error || `the server answered ${res.status}`}.` });
      else { dropGroup(g.id); setMsg({ ok: true, text: 'Marked as different products. This group won’t be shown again.' }); }
    } catch (e) { setMsg({ ok: false, text: `Couldn't save that: ${e.message}` }); }
    setBusyId(null);
  }

  const btn = (primary) => ({ padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12,
    background: primary ? t.accent : 'transparent', color: primary ? t.bgBase : t.textSecondary,
    border: `0.5px solid ${primary ? t.accent : t.borderMid}` });
  const cell = { padding: '6px 10px', borderTop: `0.5px solid ${t.borderSubtle}`, verticalAlign: 'top', fontSize: 12, color: t.textPrimary };
  const label = { ...cell, color: t.textDim, whiteSpace: 'nowrap', fontSize: 11 };

  // A render helper, not a component, so typing state elsewhere doesn't remount it.
  function renderGroup(g) {
    const winner = keepers[g.id] || g.records[0].key;
    const fieldNames = Object.keys(g.records[0].fields || {});
    const differs = f => new Set(g.records.map(r => String(r.fields[f] ?? ''))).size > 1;
    const val = v => (v === null || v === undefined || v === '') ? '—' : String(v);
    return (
      <div key={g.id} style={{ background: t.white04, border: `0.5px solid ${selected.has(g.id) ? t.accent : t.borderMid}`, borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: t.textSecondary, cursor: 'pointer', marginBottom: 6 }}>
          <input type="checkbox" checked={selected.has(g.id)} onChange={() => toggle(g.id)} /> Select
        </label>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 180 * g.records.length + 90 }}>
            <tbody>
              <tr>
                <td style={label}>Keep</td>
                {g.records.map(r => (
                  <td key={r.key} style={cell}>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
                      <input type="radio" name={`keep-${g.id}`} checked={winner === r.key}
                        onChange={() => setKeepers(prev => ({ ...prev, [g.id]: r.key }))} />
                      <span>
                        <span style={{ fontFamily: t.fontSerif, fontSize: 13 }}>{r.name}</span>
                        <span style={{ display: 'block', color: t.textDim, fontSize: 10, wordBreak: 'break-all' }}>{r.key}</span>
                      </span>
                    </label>
                  </td>
                ))}
              </tr>
              <tr>
                <td style={label}>Label</td>
                {g.records.map(r => (
                  <td key={r.key} style={cell}>
                    {r.labelImageUrl
                      ? <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <img src={r.labelImageUrl} alt="" style={{ height: 44, width: 'auto', borderRadius: 3 }} />
                          <span style={{ color: r.labelImageApproved ? t.success : t.warning, fontSize: 11 }}>{r.labelImageApproved ? 'approved' : 'not approved'}</span>
                        </span>
                      : <span style={{ color: t.textDim }}>none</span>}
                  </td>
                ))}
              </tr>
              {fieldNames.map(f => (
                <tr key={f}>
                  <td style={label}>{f}</td>
                  {g.records.map(r => (
                    <td key={r.key} style={{ ...cell, color: differs(f) ? t.warning : t.textPrimary, fontWeight: differs(f) ? 600 : 400 }}>{val(r.fields[f])}</td>
                  ))}
                </tr>
              ))}
              {type === 'wines' && (
                <tr>
                  <td style={label}>Critic scores</td>
                  {g.records.map(r => <td key={r.key} style={cell}>{val(r.criticScoresText)}</td>)}
                </tr>
              )}
              <tr>
                <td style={label}>Carried by</td>
                {g.records.map(r => (
                  <td key={r.key} style={cell}>{Object.keys(r.carriedBy || {}).map(restoName).join(', ') || '—'}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button disabled={busyId === g.id || busyId === 'bulk'} onClick={() => merge(g)} style={btn(true)}>Merge into the kept record</button>
          <button disabled={busyId === g.id || busyId === 'bulk'} onClick={() => dismiss(g)} style={btn(false)}>Not the same product</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.6, marginBottom: 16, maxWidth: 680 }}>
        The same real product can end up as two master records when two enrichments word it differently
        ("Rombauer" vs "Rombauer Vineyards"). Each copy is researched, imaged and scored separately. Merging keeps
        one record, moves every restaurant onto it, and carries over the best label and any critic scores.
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        {MASTER_TYPES.map(([id, name]) => (
          <button key={id} onClick={() => { setType(id); setReport(null); setMsg(null); }}
            style={{ ...btn(false), borderColor: type === id ? t.accent : t.borderMid, color: type === id ? t.accent : t.textSecondary }}>{name}</button>
        ))}
        <button onClick={() => runAudit()} disabled={loading} style={{ ...btn(true), marginLeft: 8 }}>{loading ? 'Checking…' : 'Find duplicates'}</button>
        <button onClick={runLinks} disabled={linksLoading} style={btn(false)}>{linksLoading ? 'Checking…' : 'Check links'}</button>
      </div>

      {msg && (
        <div style={{ fontSize: 12, marginBottom: 14, color: msg.ok ? t.success : t.error, fontFamily: t.fontSerif }}>{msg.text}</div>
      )}

      {links && (
        <div style={{ background: t.white04, border: `0.5px solid ${t.borderMid}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: t.textSecondary }}>
          {links.error ? <span style={{ color: t.error }}>Couldn't check links: {links.error}</span> : (
            <>
              <div style={{ color: links.totalDangling ? t.error : t.success, marginBottom: 4 }}>
                {links.totalDangling ? `${links.totalDangling} restaurant item(s) point at a master record that no longer exists.` : 'Every restaurant item points at a master record that exists.'}
              </div>
              <div>{links.totalCarriedByOrphans || 0} master record link(s) name an item that's gone (harmless, usually from a re-import).</div>
              {(links.report || []).filter(r => Object.values(r.categories || {}).some(c => c.dangling)).map(r => (
                <div key={r.restaurantId} style={{ marginTop: 6 }}>
                  {restoName(r.restaurantId)}: {Object.entries(r.categories).filter(([, c]) => c.dangling).map(([k, c]) => `${c.dangling} ${k} (${c.danglingSample.map(x => x.name || x.id).join(', ')})`).join('; ')}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {report && (
        <>
          <div style={{ fontSize: 12, color: t.textDim, marginBottom: 14 }}>
            {report.totalRecords} {type} in the master database · {report.exactCollisions + report.nearMissCandidates + report.sameProducerCandidates} group(s) to review
            {report.dismissedCount ? ` · ${report.dismissedCount} marked as different earlier` : ''}
          </div>
          {selected.size > 0 && (
            <div style={{ position: 'sticky', top: 0, zIndex: 5, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
              background: t.bgSurface || t.bgBase, border: `0.5px solid ${t.accent}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: t.fontSerif }}>{selected.size} selected</span>
              <button disabled={busyId === 'bulk'} onClick={mergeSelected} style={btn(true)}>{busyId === 'bulk' ? 'Working…' : 'Merge selected'}</button>
              <button disabled={busyId === 'bulk'} onClick={dismissSelected} style={btn(false)}>Not the same product</button>
              <button disabled={busyId === 'bulk'} onClick={() => setSelected(new Set())} style={{ ...btn(false), border: 'none' }}>Clear</button>
            </div>
          )}
          {MASTER_SECTIONS.map(([key, title, help]) => (report[key] || []).length > 0 && (
            <div key={key} style={{ marginBottom: 22 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <div style={{ fontFamily: t.fontSerif, fontSize: 15, color: t.textPrimary }}>{title} <span style={{ color: t.textDim, fontSize: 12 }}>({report[key].length})</span></div>
                <button onClick={() => toggleSection(report[key])} style={{ background: 'none', border: 'none', padding: 0, color: t.accent, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12 }}>
                  {report[key].every(g => selected.has(g.id)) ? 'Unselect all' : 'Select all'}
                </button>
              </div>
              <div style={{ color: t.textDim, fontSize: 11.5, margin: '3px 0 10px', maxWidth: 680, lineHeight: 1.5 }}>{help}</div>
              {report[key].map(g => renderGroup(g))}
            </div>
          ))}
          {MASTER_SECTIONS.every(([key]) => !(report[key] || []).length) && (
            <div style={{ color: t.textDim, fontSize: 13, fontStyle: 'italic' }}>No duplicates to review for {type}.</div>
          )}
        </>
      )}
    </div>
  );
}

// Shown instead of the whole app when the restaurant is inactive. Neutral on
// purpose - a guest at the table needs to know the menu isn't here, not why.
function RestaurantUnavailable() {
  const t = ADMIN_THEME;
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: t.bgBase, color: t.textSecondary, fontFamily: t.fontSerif, textAlign: 'center', padding: 24 }}>
      <div style={{ maxWidth: 420 }}>
        <div style={{ color: t.textPrimary, fontSize: 18, marginBottom: 12 }}>{tr("unavailable.title")}</div>
        <div style={{ fontSize: 14, lineHeight: 1.6 }}>{tr("unavailable.body")}</div>
      </div>
    </div>
  );
}

// An on/off switch with its label. `onColor` is the track colour when on, `offColor` when off (a held restaurant
// shows an amber track so it stands out). The click does not bubble, because the whole card is clickable.
function SwitchRow({ t, on, onToggle, label, title, onColor, offColor }) {
  return (
    <button type="button" role="switch" aria-checked={on} title={title} onClick={e => { e.stopPropagation(); onToggle(); }}
      style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12, color: t.textPrimary, textAlign: 'left' }}>
      <span style={{ width: 30, height: 17, borderRadius: 9, background: on ? onColor : (offColor || t.borderMid), position: 'relative', flex: 'none', transition: 'background .15s' }}>
        <span style={{ position: 'absolute', top: 2, left: on ? 15 : 2, width: 13, height: 13, borderRadius: '50%', background: '#ffffff', boxShadow: '0 1px 2px rgba(0,0,0,.3)', transition: 'left .15s' }} />
      </span>
      {label}
    </button>
  );
}

function SommSyncAdminPortal() {
  const t = ADMIN_THEME;
  const { user, claims, loading: authLoading } = useAuthUser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  // The portal is super-user only. An owner who signs in here is told plainly
  // rather than shown an empty shell they cannot use.
  const authed = !!user && can(claims, 'super');
  const [activeTab, setActiveTab] = useState('images');
  const [imageQueue, setImageQueue] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [resolvingId, setResolvingId] = useState(null);
  // Guest "Report incorrect image" reports (reportLabelImage), open ones only.
  const [imageReports, setImageReports] = useState([]);
  const [reportBusyId, setReportBusyId] = useState(null);
  const [reportMsg, setReportMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [actionMsg, setActionMsg] = useState('');
  const fileInputRef = useRef(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState({ phase: 'idle' });
  const [onboardingMsg, setOnboardingMsg] = useState('');
  const pipelineIntervalRef = useRef(null);
  const [masterDbStats, setMasterDbStats] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  // Platform-wide critic-score job state from listRestaurants (see runCriticScoreBatch).
  const [criticPlatform, setCriticPlatform] = useState(null);
  // Accounts
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [acctDraft, setAcctDraft] = useState({ email: '', password: '', role: 'owner', restaurantIds: [] });
  const [acctError, setAcctError] = useState('');
  const [acctBusy, setAcctBusy] = useState(false);
  const [editingAcct, setEditingAcct] = useState(null);
  const [posAdapters, setPosAdapters] = useState([]);
  const [showAddRestaurant, setShowAddRestaurant] = useState(false);
  const [importFiles, setImportFiles] = useState({});
  const [demoOpts, setDemoOpts] = useState({ isDemo: false, days: 30 });
  const [newResto, setNewResto] = useState({ restaurantId: '', posType: 'toast', name: '', timezone: 'America/New_York', plan: 'premium' });
  // The restaurant name is required. A tenant created without one showed as "No name set" until someone noticed, and
  // its name is what guests see in the browser tab, on printed menus and in emails. nameError marks the field red
  // after a refused Save; the ref lets us put the cursor back in it.
  const [nameError, setNameError] = useState(false);
  const newRestoNameRef = useRef(null);
  const [newCreds, setNewCreds] = useState({});
  const [savingResto, setSavingResto] = useState(false);
  const [restoMsg, setRestoMsg] = useState('');
  // The result of the last change made from the restaurant LIST (plan, timezone, rename). restoMsg is only drawn
  // inside the Add Restaurant form, so a refused change used to vanish without a word whenever that form was
  // closed, which looked exactly like "nothing happens". Errors stay longer than successes.
  const [listMsg, setListMsg] = useState(null);
  const listMsgTimer = useRef(null);
  function showListMsg(text, ok) {
    setListMsg({ text, ok });
    clearTimeout(listMsgTimer.current);
    listMsgTimer.current = setTimeout(() => setListMsg(null), ok ? 6000 : 15000);
  }
  // Credential editing. `credsRefs` holds the live DOM nodes rather than state:
  // these are credential fields, and 1Password writes straight into the DOM
  // without firing React's change event, so `value` + state submits a blank
  // string while the field looks filled. Same rule as the login form.
  const [credsEditId, setCredsEditId] = useState('');
  // Tablet provisioning payload editor. Separate from the credentials editor
  // because it is not a credential — the QR carries an APK URL, a checksum and
  // a short Fully Cloud reference, and is rendered to restaurant owners.
  const [provEditId, setProvEditId] = useState('');
  const [provText, setProvText] = useState('');
  const [provBusy, setProvBusy] = useState(false);
  const [provMsg, setProvMsg] = useState('');
  const [credsTest, setCredsTest] = useState(null);
  const [credsBusy, setCredsBusy] = useState(false);
  const [credsMsg, setCredsMsg] = useState('');
  const credsRefs = useRef({});
  // The type the editor is set to. Differs from the stored type when switching
  // (a manual trial connecting Toast).
  const [credsPosType, setCredsPosType] = useState('');
  // No default. With one tenant a default was harmless; with several it means
  // every action on this tab silently targets whichever restaurant happened to be
  // first, including ones that cost money or overwrite live data.
  const [selectedRestoId, setSelectedRestoId] = useState('');
  const [callLog, setCallLog] = useState(null);
  const [callLogBusy, setCallLogBusy] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverageBusy, setCoverageBusy] = useState(false);
  const [matrixBusy, setMatrixBusy] = useState(false);
  const [matrixResult, setMatrixResult] = useState(null);
  const [matrixConfirm, setMatrixConfirm] = useState(false);
  // Incremental scoring validation (testIncrementalScoring). Read-only against the
  // live matrix - results land in pairingMatrixScratch.
  const [incrBusy, setIncrBusy] = useState(false);
  const [incrResult, setIncrResult] = useState(null);
  const [matrixStatus, setMatrixStatus] = useState(null);
  const [inspectName, setInspectName] = useState('');
  const [inspectResult, setInspectResult] = useState(null);
  const [releaseStates, setReleaseStates] = useState({});
  const [demoStates, setDemoStates] = useState({});
  const [publicDemoStates, setPublicDemoStates] = useState({});
  // Platform-wide, not per-restaurant - master records are shared across
  // every tenant, so there is no single restaurant to scope a pause to. See
  // runImageFetchBatch on the backend.
  const [serpPaused, setSerpPaused] = useState(false);
  useEffect(() => {
    authFetch(GET_SERP_PAUSED_URL).then(r => r.json()).then(d => setSerpPaused(d.paused === true)).catch(() => {});
  }, []);
  async function handleToggleSerpPause() {
    const next = !serpPaused;
    setSerpPaused(next);
    try {
      const res = await authFetch(SET_SERP_PAUSED_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paused: next }),
      });
      if (!res.ok) throw new Error('failed');
    } catch {
      setSerpPaused(!next);
      setActionMsg('Could not update SerpApi pause state');
    }
  }
  const [usage, setUsage] = useState(null);
  // null = not checked yet, 'loading' while in flight, otherwise the dry-run
  // result from retryLabelImages: { wouldReset, targets }. A batch enrichment
  // run can hit a transient SerpApi error (rate limit, timeout, brief network
  // blip) that fetchLabelImage's catch block silently collapses to the same
  // null a genuine "no images found" search returns - both get written as
  // labelImageUrl:'NOT_FOUND', indistinguishable from the database alone.
  // Surfaced after Budweiser (about as heavily-photographed a beer as exists)
  // came back NOT_FOUND during a large demo import - a transient failure
  // during a burst of back-to-back searches, not a genuine empty result.
  const [retryCheck, setRetryCheck] = useState(null);
  const [retrying, setRetrying] = useState(false);
  async function handleCheckStuckImages() {
    setRetryCheck('loading');
    try {
      const res = await authFetch(RETRY_LABELS_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ all: true, apply: false }) });
      setRetryCheck(await res.json());
    } catch (e) { setRetryCheck(null); setActionMsg('Could not check for stuck images'); }
  }
  async function handleRetryStuckImages() {
    if (!retryCheck || !retryCheck.targets) return;
    setRetrying(true);
    try {
      const res = await authFetch(RETRY_LABELS_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ all: true, apply: true }) });
      const data = await res.json();
      setRetryCheck(null);
      setActionMsg(`Queued ${data.reset || 0} image search(es) for retry`);
    } catch (e) { setActionMsg('Retry failed'); }
    setRetrying(false);
  }
  const [usageDays, setUsageDays] = useState(30);
  // Baseline/delta measurement. The category breakdown answers "what has this
  // cost", but not "what did the thing I just ran cost" - a second matrix build
  // in the same day is invisible inside a daily total. Mark a point, do the work,
  // read the difference. Persisted so reloading the page does not lose the mark
  // mid-experiment.
  const [usageBaseline, setUsageBaseline] = useState(() => {
    try { const v = localStorage.getItem('usageBaseline'); return v ? JSON.parse(v) : null; }
    catch { return null; }
  });
  const [usageLoading, setUsageLoading] = useState(false);

  async function handleLogin() {
    setLoggingIn(true);
    setLoginError('');
    try {
      const c = await signInEmail(
        emailRef.current?.value || email,
        passwordRef.current?.value || password,
      );
      if (!can(c, 'super')) {
        await authSignOut();
        setLoginError('That account does not have super-user access.');
      }
    } catch (e) {
      setLoginError(friendlyAuthError(e));
    }
    setLoggingIn(false);
  }

  // Portal data loads once the session is established, whether that came from
  // this sign-in or a persisted one restored on reload.
  useEffect(() => {
    if (!authed) return;
    loadImageQueue();
    loadRestaurants();
    loadCorrections();
    loadImageReports();
    pollPipelineStatus();
  }, [authed]);

  async function loadImageReports() {
    try {
      const res = await authFetch(IMAGE_REPORTS_URL);
      const data = await res.json();
      setImageReports(data.reports || []);
    } catch (e) { console.error(e); }
  }

  // requeue: the shared image stops showing to guests everywhere and goes back to
  // the Image Queue below, where next candidate / search more / upload fix it.
  // dismiss: the image is right, or it is the restaurant's own photo / POS image.
  async function resolveImageReport(r, action) {
    setReportBusyId(r.id); setReportMsg('');
    try {
      const res = await authFetch(IMAGE_REPORTS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId: r.id, action }),
      });
      const data = await res.json();
      if (data.error) { setReportMsg(data.error); }
      else if (action === 'requeue') {
        setImageReports(prev => prev.filter(x => !(x.source === 'master' && x.type === r.type && x.masterKey === r.masterKey)));
        setReportMsg(`${r.itemName || r.masterName || 'Image'} is hidden from guests and back in the queue below.`);
        await loadImageQueue();
      } else {
        setImageReports(prev => prev.filter(x => x.id !== r.id));
      }
    } catch (e) { setReportMsg(e.message); }
    setReportBusyId(null);
  }

  // Managers' identity corrections against records that came from the shared
  // database. Their own menu already reflects the change; this decides whether the
  // master record follows.
  async function loadCorrections() {
    try {
      const res = await authFetch(MASTER_CORRECTIONS_URL);
      const data = await res.json();
      setCorrections(data.corrections || []);
    } catch (e) { console.error(e); }
  }

  // The restaurant a correction came from, by name (the id when no name is saved). The "Keep ... only" button says
  // whose menu keeps the change, so a reviewer does not have to work it out from the grey id line.
  function correctionRestaurantName(c) {
    const r = restaurants.find(x => x.id === c.restaurantId);
    return (r && r.name && r.name !== r.id ? r.name : c.restaurantId) || 'this restaurant';
  }

  async function resolveCorrection(id, accept) {
    setResolvingId(id);
    try {
      await authFetch(RESOLVE_CORRECTION_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correctionId: id, accept }),
      });
      setCorrections(prev => prev.filter(c => c.id !== id));
    } catch (e) { console.error(e); }
    setResolvingId(null);
  }

  // Fetched with auth then saved client-side: the endpoint is super-gated, so a
  // plain link would 401.
  async function downloadSessionLog() {
    if (!selectedRestoId) {
      setCallLog(c => ({ ...c, error: 'Pick a restaurant on the Restaurants or Onboarding tab first — the export is scoped to one tenant.' }));
      return;
    }
    setCallLogBusy(true);
    try {
      const res = await authFetch(`${EXPORT_LOG_URL}?restaurantId=${selectedRestoId}`);
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/markdown' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `sommsync-review-${selectedRestoId}-${new Date().toISOString().slice(0, 10)}.md`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) { setCallLog(c => ({ ...c, error: e.message })); }
    setCallLogBusy(false);
  }

  async function loadCallLog() {
    setCallLogBusy(true);
    try {
      const res = await authFetch(CALL_LOG_URL);
      setCallLog(await res.json());
    } catch (e) { setCallLog({ error: e.message }); }
    setCallLogBusy(false);
  }
  async function controlCallLog(payload) {
    setCallLogBusy(true);
    try {
      await authFetch(SET_CALL_LOG_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      await loadCallLog();
    } catch (e) { setCallLog(c => ({ ...c, error: e.message })); }
    setCallLogBusy(false);
  }

  async function loadCoverage() {
    setCoverageBusy(true);
    try {
      const res = await authFetch(LABEL_COVERAGE_URL);
      setCoverage(await res.json());
    } catch (e) { setCoverage({ error: e.message }); }
    setCoverageBusy(false);
  }

  // Clearing labelImageUrl is what makes a record eligible for the fetch queue
  // again — nothing else does. Always confirms first: this is a bulk write.
  async function retryLabels(targets) {
    if (!targets || targets.length === 0) return;
    setCoverageBusy(true);
    try {
      const res = await authFetch(RETRY_LABELS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keys: targets.map(x => x.canonicalKey), apply: true }),
      });
      const data = await res.json();
      if (data.error) { setCoverage(c => ({ ...c, error: data.error })); }
      else { await loadCoverage(); await loadImageQueue(); }
    } catch (e) { setCoverage(c => ({ ...c, error: e.message })); }
    setCoverageBusy(false);
  }

  async function loadImageQueue() {
    setLoading(true);
    try {
      const res = await authFetch(IMAGE_QUEUE_URL);
      const data = await res.json();
      setImageQueue(data.items || []);
      setCurrentIdx(0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  // Release gate per restaurant. Held = SommSync is still doing its accuracy
  // pass and the owner's review screen stays shut.
  async function loadReleaseStates(ids) {
    const out = {};
    await Promise.all((ids || []).map(async id => {
      try {
        const res = await authFetch(`${GET_RELEASE_STATE_URL}?restaurant=${encodeURIComponent(id)}`);
        const d = await res.json();
        out[id] = d?.status || 'released';
      } catch { out[id] = 'released'; }
    }));
    setReleaseStates(out);
  }

  // Demo status is toggleable after import. It used to be settable only on the
  // create form, and the backend ignored an explicit `false` - so a restaurant
  // marked a demo wore the banner permanently with no way to reach the flag.
  async function handleSetDemo(restaurantId, isDemo) {
    setDemoStates(prev => ({ ...prev, [restaurantId]: isDemo }));
    try {
      const res = await authFetch(SET_DEMO_STATUS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId, isDemo }),
      });
      if (!res.ok) throw new Error('failed');
      loadRestaurants();
    } catch {
      setDemoStates(prev => ({ ...prev, [restaurantId]: !isDemo }));
      setActionMsg('Could not update demo status');
    }
  }

  // publicDemo is independent of isDemo (see setDemoStatus on the backend) -
  // it is the flag for a permanent, shared, anonymous-traffic tenant with its
  // own turn/daily caps, not a time-boxed prospect demo. Same endpoint,
  // different field, so toggling one never disturbs the other.
  async function handleSetPublicDemo(restaurantId, publicDemo) {
    setPublicDemoStates(prev => ({ ...prev, [restaurantId]: publicDemo }));
    try {
      const res = await authFetch(SET_DEMO_STATUS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId, publicDemo }),
      });
      if (!res.ok) throw new Error('failed');
      loadRestaurants();
    } catch {
      setPublicDemoStates(prev => ({ ...prev, [restaurantId]: !publicDemo }));
      setActionMsg('Could not update public-demo status');
    }
  }

  async function handleSetRelease(restaurantId, status) {
    setReleaseStates(prev => ({ ...prev, [restaurantId]: status }));
    try {
      const res = await authFetch(SET_RELEASE_STATE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId, status }),
      });
      if (!res.ok) throw new Error('failed');
    } catch {
      // Put the toggle back rather than showing a state the server disagrees with
      setReleaseStates(prev => ({ ...prev, [restaurantId]: status === 'held' ? 'released' : 'held' }));
      setActionMsg('Could not update release state');
    }
  }

  // ── Credentials ────────────────────────────────────────────────────────────
  // Rotating a POS secret used to mean editing the Realtime Database by hand,
  // which works for us and for nobody else. The sequence that matters — test,
  // then write, then drop the cached bearer token — is enforced server-side;
  // this is the part that makes it clickable.

  // Opening the editor clears everything. A green test belongs to the exact
  // credentials that produced it — carrying one over from another restaurant,
  // or from before an edit, would enable Save for a value nobody tested.
  // ── Tablet provisioning payload ────────────────────────────────────────────
  // Paste the QR content from Fully Cloud (Provisioning → the restaurant's
  // profile). It is JSON, not an image URL. The backend validates the shape and
  // returns the Fully Cloud code and the pinned APK version so you can confirm
  // you pasted the right profile without decoding the QR again.
  //
  // Nothing here checks that the stored payload still matches Fully Cloud. If a
  // provisioning profile is repointed at a different configuration, this can go
  // stale silently and the owner's screen will show a code that enrols a tablet
  // into the wrong config. Re-paste after changing a profile.
  function openProvEditor(restaurantId) {
    setProvMsg('');
    setProvText('');
    setProvEditId(prev => (prev === restaurantId ? '' : restaurantId));
  }

  // Decode a downloaded QR image rather than making someone transcribe JSON.
  // Uses the browser's native BarcodeDetector — no library, no new dependency,
  // and no image leaves the machine. Chrome and Edge support it; Safari and
  // Firefox do not, which is why the textarea stays as the fallback path
  // rather than being replaced.
  async function handleProvImage(file) {
    if (!file) return;
    setProvMsg('');
    if (typeof window.BarcodeDetector === 'undefined') {
      setProvMsg('\u274c This browser can\u2019t read QR images. Use Chrome, or paste the payload text below.');
      return;
    }
    setProvBusy(true);
    try {
      const supported = await window.BarcodeDetector.getSupportedFormats();
      if (!supported.includes('qr_code')) throw new Error('QR decoding not available in this browser');

      const bitmap = await createImageBitmap(file);
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const codes = await detector.detect(bitmap);
      bitmap.close?.();

      if (!codes.length) throw new Error('No QR code found in that image. Make sure it is the code itself, not a screenshot of the whole page.');

      const raw = codes[0].rawValue || '';
      // Fail here rather than at save: a QR that decodes to something other
      // than a provisioning payload is almost always the wrong image, and
      // saying so now is clearer than a 400 two clicks later.
      try { JSON.parse(raw); }
      catch (e) { throw new Error('That QR decoded to something that isn\u2019t a provisioning payload.'); }

      setProvText(raw);
      setProvMsg('\u2705 Read the code. Check it below, then Save.');
    } catch (e) {
      setProvMsg(`\u274c ${e.message}`);
    } finally { setProvBusy(false); }
  }

  async function handleSaveProv() {
    if (!provEditId) return;
    setProvBusy(true); setProvMsg('');
    try {
      const res = await authFetch(`${BASE}/setProvisioningPayload?restaurantId=${provEditId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload: provText.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'failed');
      if (data.cleared) {
        setProvMsg('\u2705 Cleared. The owner screen will show the contact message.');
      } else {
        // Surfacing the APK filename matters: a profile built months ago pins an
        // older Fully build, and every tablet provisioned from it installs that
        // version. Seeing it here is the only prompt to refresh the profile.
        const apk = (data.apkUrl || '').split('/').pop() || 'unknown';
        setProvMsg(`\u2705 Saved. Fully Cloud code ${data.code || '(none found)'} \u00b7 ${apk}`);
      }
    } catch (e) {
      setProvMsg(`\u274c ${e.message}`);
    } finally { setProvBusy(false); }
  }

  function openCredsEditor(restaurantId) {
    credsRefs.current = {};
    setCredsTest(null);
    setCredsMsg('');
    const r = restaurants.find(x => x.id === restaurantId) || {};
    // Opening a manual restaurant defaults to the first live POS, because
    // connecting one is the only thing to do here.
    const firstLive = (posAdapters.find(a => a.key !== 'manual') || {}).key || 'toast';
    setCredsPosType(r.posType === 'manual' ? firstLive : (r.posType || 'toast'));
    setCredsEditId(prev => (prev === restaurantId ? '' : restaurantId));
  }

  const credsStoredType = (restaurants.find(x => x.id === credsEditId) || {}).posType || 'toast';
  const credsSwitching = !!credsEditId && !!credsPosType && credsPosType !== credsStoredType;
  const credsEditorFields = (posAdapters.find(a => a.key === credsPosType) || {}).credentialFields || [];

  function collectCreds() {
    const out = {};
    credsEditorFields.forEach(f => {
      const v = credsRefs.current[f.key]?.value?.trim();
      if (v) out[f.key] = v;   // blank means "leave the stored value alone"
    });
    return out;
  }

  async function handleTestCreds() {
    const supplied = collectCreds();
    if (credsSwitching && credsEditorFields.some(f => !supplied[f.key])) {
      setCredsTest({ ok: false, error: 'Fill in every field to connect a new POS.' });
      return;
    }
    if (Object.keys(supplied).length === 0) {
      setCredsTest({ ok: false, error: 'Nothing entered — fill in at least one field to test.' });
      return;
    }
    setCredsBusy(true); setCredsTest(null); setCredsMsg('');
    try {
      const res = await authFetch(TEST_POS_CONNECTION_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId: credsEditId, credentials: supplied, posType: credsPosType }),
      });
      const data = await res.json();
      setCredsTest(data.ok ? data : { ok: false, error: data.error || 'Connection failed' });
    } catch (e) {
      setCredsTest({ ok: false, error: e.message });
    }
    setCredsBusy(false);
  }

  async function handleSaveCreds() {
    const toName = (posAdapters.find(a => a.key === credsPosType) || {}).name || credsPosType;
    if (credsSwitching) {
      const msg = credsPosType === 'manual'
        ? `Disconnect ${credsEditId} from its POS?\n\nNightly syncs and live stock counts stop, and the stored credentials are deleted. The menu stays as it is and becomes editable in Manage Items.`
        : `Connect ${credsEditId} to ${toName}?\n\nNothing on the menu changes yet. Each category moves to ${toName} when you map its ${toName} menu in Settings → Menus; until then it stays as it is.`;
      if (!window.confirm(msg)) return;
    }
    setCredsBusy(true); setCredsMsg('');
    try {
      const res = await authFetch(UPDATE_CREDENTIALS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId: credsEditId, credentials: collectCreds(), ...(credsSwitching ? { posType: credsPosType } : {}) }),
      });
      const data = await res.json();
      if (!data.ok) {
        setCredsMsg(`Not saved — ${data.error || 'unknown error'}`);
      } else if (data.switched) {
        setCredsMsg(data.posType === 'manual'
          ? '\u2705 Disconnected. The restaurant is now managed from files and Menu items.'
          : `\u2705 Connected to ${toName}. Next: map its menus in Settings → Menus, one category at a time. Each one switches over at its next sync.`);
        credsEditorFields.forEach(f => { if (credsRefs.current[f.key]) credsRefs.current[f.key].value = ''; });
        setCredsTest(null);
        loadRestaurants();
      } else {
        const changed = (data.changed || []).length
          ? `Saved: ${(data.changed || []).join(', ')}.`
          : 'Saved — no field differed from what was stored.';
        setCredsMsg(`\u2705 ${changed} Cached token cleared; the next sync will authenticate fresh.`);
        credsEditorFields.forEach(f => { if (credsRefs.current[f.key]) credsRefs.current[f.key].value = ''; });
        setCredsTest(null);
      }
    } catch (e) {
      setCredsMsg(`Not saved — ${e.message}`);
    }
    setCredsBusy(false);
  }

  // ── Accounts ───────────────────────────────────────────────────────────────
  // No self-signup anywhere. Accounts exist because a super user made them.

  async function loadAccounts() {
    setAccountsLoading(true);
    try {
      const res = await authFetch(LIST_ACCOUNTS_URL);
      const data = await res.json();
      setAccounts(data.accounts || []);
    } catch (e) {
      console.error('[loadAccounts]', e);
    }
    setAccountsLoading(false);
  }

  // Mirrors passwordProblem() on the server so the form can say what is wrong
  // before a round trip. The server still decides.
  function passwordProblem(pw) {
    if (!pw || pw.length < 12) return 'At least 12 characters';
    if (!/[a-z]/.test(pw)) return 'Needs a lowercase letter';
    if (!/[A-Z]/.test(pw)) return 'Needs an uppercase letter';
    if (!/[0-9]/.test(pw)) return 'Needs a number';
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Needs a special character';
    if (/\s/.test(pw)) return 'No spaces';
    return null;
  }

  function suggestPassword() {
    // Generated in the browser and shown once. There is no email delivery yet,
    // so the super user reads it out or sends it another way.
    const chars = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%^&*-_';
    const arr = new Uint32Array(18);
    crypto.getRandomValues(arr);
    let pw = Array.from(arr, n => chars[n % chars.length]).join('');
    // Guarantee each class rather than trusting the draw
    return 'Aa1!' + pw.slice(4);
  }

  async function handleCreateAccount() {
    const { email, password, role, restaurantIds } = acctDraft;
    if (!email.includes('@')) { setAcctError('Enter a valid email'); return; }
    const pwp = passwordProblem(password);
    if (pwp) { setAcctError(`Password: ${pwp.toLowerCase()}`); return; }
    if (role === 'owner' && restaurantIds.length === 0) {
      setAcctError('An owner needs at least one restaurant'); return;
    }
    setAcctBusy(true); setAcctError('');
    try {
      const res = await authFetch(CREATE_ACCOUNT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, role, restaurantIds }),
      });
      const data = await res.json();
      if (!res.ok) setAcctError(data.error || 'Could not create account');
      else {
        setShowAddAccount(false);
        setAcctDraft({ email: '', password: '', role: 'owner', restaurantIds: [] });
        await loadAccounts();
      }
    } catch (e) { setAcctError('Could not create account'); }
    setAcctBusy(false);
  }

  async function handleUpdateAccount(uid, changes) {
    setAcctBusy(true); setAcctError('');
    try {
      const res = await authFetch(UPDATE_ACCOUNT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, ...changes }),
      });
      const data = await res.json();
      if (!res.ok) setAcctError(data.error || 'Could not update account');
      else { setEditingAcct(null); await loadAccounts(); }
    } catch (e) { setAcctError('Could not update account'); }
    setAcctBusy(false);
  }

  async function handleDeleteAccount(uid, email) {
    if (!window.confirm(`Remove ${email}? They lose access immediately.`)) return;
    setAcctBusy(true); setAcctError('');
    try {
      const res = await authFetch(DELETE_ACCOUNT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      });
      const data = await res.json();
      if (!res.ok) setAcctError(data.error || 'Could not remove account');
      else await loadAccounts();
    } catch (e) { setAcctError('Could not remove account'); }
    setAcctBusy(false);
  }

  async function loadUsage(days) {
    const range = days || usageDays;
    setUsageLoading(true);
    try {
      const res = await authFetch(`${GET_USAGE_STATS_URL}?days=${range}`);
      const data = await res.json();
      setUsage(data);
    } catch (e) {
      console.error('[loadUsage]', e);
      setUsage({ error: e.message });
    }
    setUsageLoading(false);
  }

  function markUsageBaseline() {
    if (!usage || usage.error) return;
    const snap = {
      at: Date.now(),
      days: usageDays,
      total: usage.total || {},
      byCategory: usage.byCategory || {},
      byRestaurant: usage.byRestaurant || {},
    };
    setUsageBaseline(snap);
    try { localStorage.setItem('usageBaseline', JSON.stringify(snap)); } catch {}
  }
  function clearUsageBaseline() {
    setUsageBaseline(null);
    try { localStorage.removeItem('usageBaseline'); } catch {}
  }

  async function loadRestaurants() {
    try {
      const [restoRes, adapterRes] = await Promise.all([
        authFetch(LIST_RESTAURANTS_URL),
        authFetch(GET_POS_ADAPTERS_URL),
      ]);
      const restoData = await restoRes.json();
      const adapterData = await adapterRes.json();
      setRestaurants(restoData.restaurants || []);
      setCriticPlatform(restoData.criticScoresPlatform || null);
      loadReleaseStates((restoData.restaurants || []).map(r => r.id));
      setPosAdapters(adapterData.adapters || []);
    } catch (e) { console.error(e); }
  }

  async function handleUpdatePlan(restaurantId, newPlan) {
    const r = restaurants.find(x => x.id === restaurantId) || { id: restaurantId };
    const label = r.name && r.name !== r.id ? r.name : restaurantId;
    if (newPlan === r.plan) return;
    // Cancelling leaves the dropdown on the old level: it is controlled by r.plan, which we have not changed.
    if (!window.confirm(planChangeWarning(r, newPlan, label))) return;
    try {
      const res = await authFetch(UPDATE_RESTAURANT_PLAN_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId, plan: newPlan }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        setRestaurants(prev => prev.map(x => x.id === restaurantId ? { ...x, plan: newPlan } : x));
        showListMsg(`✅ ${label} is now on \"${PLAN_LABELS[newPlan] || newPlan}\".`, true);
      } else {
        // This used to do nothing at all when the server refused, so the dropdown just snapped back.
        showListMsg(`❌ ${label}: the level was not changed. ${data.error || `The server answered ${res.status}.`}`, false);
      }
    } catch (e) {
      console.error('Plan update failed:', e);
      showListMsg(`❌ ${label}: the level was not changed. ${e.message}`, false);
    }
  }

  // Inline rename in the restaurant list. The name is what guests see in the
  // browser tab, on printed menus and in emails; before this it could only be
  // set when the restaurant was created.
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  function startRename(r) { setRenamingId(r.id); setRenameDraft(r.name && r.name !== r.id ? r.name : ''); }
  async function handleSaveName(restaurantId) {
    const name = renameDraft.replace(/\s+/g, ' ').trim();
    if (!name) return;
    setRenameSaving(true);
    try {
      const res = await authFetch(UPDATE_RESTAURANT_PLAN_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId, name }),
      });
      const data = await res.json();
      if (data.ok) {
        setRestaurants(prev => prev.map(r => r.id === restaurantId ? { ...r, name: data.name || name } : r));
        setRenamingId(null);
      } else showListMsg(`❌ ${data.error || 'Name not changed'}`, false);
    } catch (e) { showListMsg(`❌ ${e.message}`, false); }
    setRenameSaving(false);
  }

  // Inactive = a restaurant that isn't a customer (left, unpaid, a prospect who
  // went quiet). Nothing is deleted; the server blocks guests, staff, AI calls
  // and every background job for it until it is switched back. See
  // isRestaurantInactive in functions/index.js.
  async function handleSetInactive(restaurantId, inactive) {
    const r = restaurants.find(x => x.id === restaurantId) || { id: restaurantId };
    const label = r.name && r.name !== r.id ? r.name : restaurantId;
    const msg = inactive
      ? `Make ${label} INACTIVE?\n\n` +
        `• Guests see "not available" instead of the menu (tablets, QR codes, saved menus).\n` +
        `• The owner and floor staff can no longer sign in.\n` +
        `• No AI, email, menu sync, translation, image or critic-score work runs for it.\n\n` +
        `Nothing is deleted. Switch it back to Active at any time and everything resumes.`
      : `Make ${label} ACTIVE again?\n\nGuests and staff get access back straight away, and the background jobs pick up anything they skipped.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await authFetch(UPDATE_RESTAURANT_PLAN_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId, inactive }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        setRestaurants(prev => prev.map(x => x.id === restaurantId ? { ...x, inactive: data.inactive === true, inactiveSince: data.inactiveSince || null } : x));
        showListMsg(inactive ? `⏸ ${label} is now inactive.` : `✅ ${label} is active again.`, true);
      } else {
        showListMsg(`❌ ${label}: not changed. ${data.error || `The server answered ${res.status}.`}`, false);
      }
    } catch (e) { showListMsg(`❌ ${label}: not changed. ${e.message}`, false); }
  }

  async function handleUpdateTimezone(restaurantId, timezone) {
    const name = tzLabelFor(timezone);
    if (!window.confirm(`Change ${restaurantId} to ${name}?\n\nMenu hours are read in this timezone, so a lunch menu set to 11–3 will follow the new clock.`)) return;
    try {
      const res = await authFetch(UPDATE_RESTAURANT_PLAN_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId, timezone }),
      });
      const data = await res.json();
      if (data.ok) setRestaurants(prev => prev.map(r => r.id === restaurantId ? { ...r, timezone } : r));
      else showListMsg(`❌ ${data.error || 'Timezone not changed'}`, false);
    } catch (e) { showListMsg(`❌ ${e.message}`, false); }
  }

  async function handleSaveRestaurant() {
    const name = (newResto.name || '').replace(/\s+/g, ' ').trim();
    if (!name) {
      // Ask before creating, not after. The server refuses a blank name too, so nothing else can slip one through.
      setNameError(true); setRestoMsg('');
      window.alert('To add a restaurant you need a restaurant name.\n\nIt is what guests see in the browser tab, on printed menus and in emails.');
      if (newRestoNameRef.current) newRestoNameRef.current.focus();
      return;
    }
    if (!newResto.timezone) {
      window.alert('Choose a time zone for the restaurant.\n\nIt decides when each menu (lunch, dinner, happy hour) is shown.');
      return;
    }
    setSavingResto(true);
    setRestoMsg('');
    try {
      // Manual / file-import tenants are created EMPTY, same as every other
      // POS type - menu content is loaded afterward from Sync with POS →
      // Update Menu from File, which is the one reliable, well-tested import
      // path (it also covers every later re-upload, so there is no separate
      // "first upload" code path to keep in sync with it).
      //
      // This used to attach CSVs and call importMenuData in the same step as
      // creating the tenant. That combined call had no reliable way to show
      // whether it had actually written the menu — a failure and a success
      // looked identical from here — and it duplicated logic the Sync screen
      // already has to have anyway. Splitting tenant creation from menu import
      // removes that ambiguity entirely: creation either succeeds or shows a
      // real error, and menu import is always the same one screen regardless
      // of whether it's the very first upload or the fiftieth.
      const posType = newResto.posType;
      const res = await authFetch(SETUP_RESTAURANT_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restaurantId: newResto.restaurantId,
          posType,
          credentials: posType === 'manual' ? {} : newCreds,
          profile: { name, timezone: newResto.timezone },
          plan: newResto.plan,
        })
      });
      const data = await res.json();
      if (data.ok) {
        // isDemo is a separate, optional follow-up call rather than part of
        // the creation body - setupRestaurant doesn't touch demo fields at
        // all, so this mirrors how publicDemo already has to be set after
        // creation from the restaurant list.
        if (demoOpts.isDemo) {
          await authFetch(SET_DEMO_STATUS_URL, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ restaurantId: newResto.restaurantId, isDemo: true, days: demoOpts.days }),
          }).catch(() => {});
        }
        setRestoMsg(posType === 'manual'
          ? '✅ Restaurant created. Add its menu from Sync with POS → Update Menu from File.'
          : '✅ Restaurant saved!');
        setShowAddRestaurant(false);
        setNewResto({ restaurantId: '', posType: 'toast', name: '', timezone: 'America/New_York', plan: 'premium' });
        setNewCreds({}); setImportFiles({}); setDemoOpts({ isDemo: false, days: 30 }); setNameError(false);
        loadRestaurants();
      } else { setRestoMsg(`❌ ${data.error}`); }
    } catch (e) { setRestoMsg(`❌ ${e.message}`); }
    setSavingResto(false);
  }

  // Status used to load once at sign-in, before any restaurant was selected, so
  // the pipeline panel could show nothing or a previous restaurant's run.
  useEffect(() => {
    setPipelineStatus({ phase: 'idle' }); setOnboardingMsg('');
    clearInterval(pipelineIntervalRef.current); pipelineIntervalRef.current = null;
    if (selectedRestoId) pollPipelineStatus();
  }, [selectedRestoId]);

  async function pollPipelineStatus() {
    if (!selectedRestoId) return;
    try {
      const [statusRes, statsRes] = await Promise.all([
        authFetch(`${PIPELINE_STATUS_URL}?restaurantId=${selectedRestoId}`),
        authFetch(`${BASE}/getMasterDbStats?restaurantId=${selectedRestoId}`),
      ]);
      const data = await statusRes.json();
      const stats = await statsRes.json();
      setPipelineStatus(data);
      setMasterDbStats(stats);
      const isRunning = data.phase && !['idle', 'complete', 'error', 'pendingLargeListApproval', 'awaitingMenu'].includes(data.phase);
      if (isRunning && !pipelineIntervalRef.current) {
        // Pipeline is running but interval not started — start it
        pipelineIntervalRef.current = setInterval(pollPipelineStatus, 5000);
      } else if (!isRunning) {
        clearInterval(pipelineIntervalRef.current);
        pipelineIntervalRef.current = null;
        if (data.phase === 'complete') { setOnboardingMsg('✅ Pipeline complete! Images are processing in the background.'); loadImageQueue(); }
        if (data.phase === 'error') setOnboardingMsg(`❌ Error: ${data.error}`);
        if (data.phase === 'awaitingMenu') setOnboardingMsg(data.message || 'Nothing to research yet.');
      }
    } catch (e) { console.error(e); }
  }

  const selectedResto = restaurants.find(r => r.id === selectedRestoId) || {};
  const selectedIsManual = selectedResto.posType === 'manual';
  const selectedPosName = (posAdapters.find(a => a.key === selectedResto.posType) || {}).name || 'the POS';

  async function startOnboarding() {
    // ⚠️ This and the three functions below it hardcoded restaurantId:'corduroy'
    // regardless of which restaurant card was selected in the portal - a
    // copy-paste leftover from before this screen was multi-tenant.
    // pollPipelineStatus, three lines above this in the same component,
    // already used selectedRestoId correctly; these did not. Fixed 2026-09-22.
    if (!selectedRestoId) { setOnboardingMsg('Pick a restaurant first.'); return; }
    setOnboardingMsg('Starting pipeline...');
    try {
      const res = await authFetch(`${START_ONBOARDING_URL}?restaurantId=${selectedRestoId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restaurantId: selectedRestoId }) });
      const data = await res.json();
      if (data.ok) {
        setOnboardingMsg('🚀 Pipeline running...');
        if (!pipelineIntervalRef.current) {
          pipelineIntervalRef.current = setInterval(pollPipelineStatus, 5000);
        }
      } else { setOnboardingMsg(`Error: ${data.error}`); }
    } catch (e) { setOnboardingMsg(`Error: ${e.message}`); }
  }

  async function pauseOnboarding() {
    if (!selectedRestoId) { setOnboardingMsg('Pick a restaurant first.'); return; }
    setOnboardingMsg('Pausing...');
    try {
      const res = await authFetch(`${PAUSE_ONBOARDING_URL}?restaurantId=${selectedRestoId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restaurantId: selectedRestoId }) });
      const data = await res.json();
      if (data.ok) { setOnboardingMsg('⏸ Paused — progress saved'); pollPipelineStatus(); }
      else { setOnboardingMsg(`Error: ${data.error}`); }
    } catch (e) { setOnboardingMsg(`Error: ${e.message}`); }
  }

  async function resumeOnboarding() {
    if (!selectedRestoId) { setOnboardingMsg('Pick a restaurant first.'); return; }
    setOnboardingMsg('Resuming...');
    try {
      const res = await authFetch(`${RESUME_ONBOARDING_URL}?restaurantId=${selectedRestoId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restaurantId: selectedRestoId }) });
      const data = await res.json();
      if (data.ok) {
        setOnboardingMsg('▶ Resuming from saved position...');
        if (!pipelineIntervalRef.current) pipelineIntervalRef.current = setInterval(pollPipelineStatus, 5000);
      } else { setOnboardingMsg(`Error: ${data.error}`); }
    } catch (e) { setOnboardingMsg(`Error: ${e.message}`); }
  }

  async function restartOnboarding() {
    if (!selectedRestoId) { setOnboardingMsg('Pick a restaurant first.'); return; }
    if (!window.confirm(selectedIsManual
      ? `Re-run research for "${selectedRestoId}"? Uploaded items and the research already done are kept.`
      : `This will clear the research for "${selectedRestoId}"'s POS-synced categories and start over. File-managed categories keep theirs. Are you sure?`)) return;
    setOnboardingMsg('Restarting...');
    try {
      const res = await authFetch(`${RESTART_ONBOARDING_URL}?restaurantId=${selectedRestoId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restaurantId: selectedRestoId }) });
      const data = await res.json();
      if (data.ok) {
        setOnboardingMsg(`↺ ${data.message || 'Restarting'}...`);
        if (!pipelineIntervalRef.current) pipelineIntervalRef.current = setInterval(pollPipelineStatus, 5000);
      } else { setOnboardingMsg(`Error: ${data.error}`); }
    } catch (e) { setOnboardingMsg(`Error: ${e.message}`); }
  }

  async function approveLargeList() {
    setOnboardingMsg('Approving large list — starting AI enrichment...');
    try {
      const res = await authFetch(`${APPROVE_LARGE_LIST_URL}?restaurantId=${selectedRestoId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId: selectedRestoId }),
      });
      const data = await res.json();
      if (data.ok) {
        setOnboardingMsg('✅ Approved — Phase 2 AI enrichment starting...');
        if (!pipelineIntervalRef.current) {
          pipelineIntervalRef.current = setInterval(pollPipelineStatus, 5000);
        }
      } else { setOnboardingMsg(`Error: ${data.error}`); }
    } catch (e) { setOnboardingMsg(`Error: ${e.message}`); }
  }

  // Scores every approved wine against every dish. Costs real money and replaces
  // every pairing a guest can see, so it asks first. Long-running: the endpoint
  // runs to 540s, and a large list can sit near that.
  // Dumps the raw Toast payload for a menu item. Lives here rather than as a URL
  // you can paste, because these endpoints need a bearer token the address bar
  // cannot send.
  async function inspectToastItem() {
    if (!selectedRestoId || !inspectName.trim()) return;
    setInspectResult({ loading: true });
    try {
      const res = await authFetch(`${INSPECT_TOAST_URL}?restaurantId=${selectedRestoId}&name=${encodeURIComponent(inspectName.trim())}`);
      setInspectResult(await res.json());
    } catch (e) { setInspectResult({ error: e.message }); }
  }

  async function loadMatrixStatus() {
    if (!selectedRestoId) return;
    try {
      const res = await authFetch(`${MATRIX_STATUS_URL}?restaurantId=${selectedRestoId}`);
      const data = await res.json();
      setMatrixStatus(data);
      if (data.building) startMatrixPolling();
    } catch (e) { setMatrixStatus({ error: e.message }); }
  }

  // Polls while a build runs. Started when the build begins, and also picked up
  // on a status check, so a manager who reloads mid-run still sees progress.
  const matrixPollRef = useRef(null);
  function startMatrixPolling() {
    if (matrixPollRef.current) return;
    matrixPollRef.current = setInterval(async () => {
      try {
        const res = await authFetch(`${MATRIX_STATUS_URL}?restaurantId=${selectedRestoId}`);
        const data = await res.json();
        setMatrixStatus(data);
        if (!data.building) { clearInterval(matrixPollRef.current); matrixPollRef.current = null; }
      } catch (e) { /* transient; the next tick retries */ }
    }, 3000);
  }
  useEffect(() => () => { if (matrixPollRef.current) clearInterval(matrixPollRef.current); }, []);

  // compare=false scores new wines plus a sample now; compare=true re-checks the
  // latest test run against the current matrix (run it after a rebuild).
  async function runIncrementalTest(compare) {
    if (!selectedRestoId) return;
    setIncrBusy(true);
    setIncrResult(null);
    try {
      const res = await authFetch(`${INCR_TEST_URL}?restaurantId=${selectedRestoId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId: selectedRestoId, compare: !!compare }),
      });
      setIncrResult(await res.json());
    } catch (e) { setIncrResult({ error: e.message }); }
    setIncrBusy(false);
  }

  async function buildPairingMatrix() {
    setMatrixConfirm(false);
    setMatrixBusy(true);
    setMatrixResult(null);
    startMatrixPolling();
    const startedAt = Date.now();
    // A large list cannot be scored inside one Cloud Function invocation - the
    // ceiling is 540s and Wonder Bar needs longer. The endpoint scores what it can,
    // saves progress and returns a resume point; this drives it to completion.
    // The cap exists so a bug cannot spin here forever spending money.
    const MAX_CHUNKS = 12;
    try {
      let resumeFrom = 0, chunks = 0, data = null;
      while (chunks < MAX_CHUNKS) {
        const res = await authFetch(`${GENERATE_MATRIX_URL}?restaurantId=${selectedRestoId}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ restaurantId: selectedRestoId, resumeFrom }),
        });
        data = await res.json();
        chunks++;
        if (data.error) break;
        if (data.done) break;
        if (typeof data.resumeFrom !== 'number' || data.resumeFrom <= resumeFrom) {
          // No forward progress: stop rather than loop on the same batch.
          data = { error: 'Build stopped making progress — check the function logs.' };
          break;
        }
        resumeFrom = data.resumeFrom;
      }
      setMatrixResult(data && data.error ? { error: data.error }
        : { ...data, elapsedSec: Math.round((Date.now() - startedAt) / 1000), chunks });
      loadMatrixStatus();
    } catch (e) {
      // A network or proxy timeout does not mean the build failed - the function
      // may still be running, and its progress is saved either way.
      setMatrixResult({ error: `${e.message}. The build saves progress as it goes — press Build again to resume rather than starting over.` });
    }
    setMatrixBusy(false);
  }

  async function handleApprove() {
    const item = imageQueue[currentIdx];
    if (!item) return;
    setActionMsg('Approving...');
    try {
      await authFetch(APPROVE_IMAGE_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: item.type, canonicalKey: item.canonicalKey }) });
      setActionMsg('Approved!');
      setTimeout(() => {
        setActionMsg('');
        const newQueue = imageQueue.filter((_, i) => i !== currentIdx);
        setImageQueue(newQueue);
        // Stay at same index — next item slides in; clamp if at end
        setCurrentIdx(i => Math.min(i, Math.max(0, newQueue.length - 1)));
      }, 600);
    } catch (e) { setActionMsg('Error'); }
  }

  // The half of "Flag for Review"'s old job that should never have touched
  // content approval. Split out after a super user used Flag for Review as a
  // personal "come back to this one" reminder on several obscure-brewery beers
  // whose SerpApi search found nothing usable - a completely reasonable thing
  // to want to mark, but disqualifyMasterItem's actual effect (reset approved/
  // pendingApproval/uncertain at every restaurant carrying the item) had
  // nothing to do with why they clicked it, and silently un-approved a batch
  // of already-correctly-identified, already-approved beers.
  async function handleNoImageFound() {
    const item = imageQueue[currentIdx];
    if (!item) return;
    setActionMsg('Marking no image found...');
    try {
      await authFetch(NO_IMAGE_FOUND_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: item.type, canonicalKey: item.canonicalKey }) });
      setActionMsg('Marked — upload your own when ready');
      setTimeout(() => {
        setActionMsg('');
        const newQueue = imageQueue.filter((_, i) => i !== currentIdx);
        setImageQueue(newQueue);
        setCurrentIdx(i => Math.min(i, Math.max(0, newQueue.length - 1)));
      }, 800);
    } catch (e) { setActionMsg('Error'); }
  }

  // ⚠️ This button's effect is much broader than its label suggests: it does not
  // just flag this one image - disqualifyMasterItem resets approved/pendingApproval/
  // uncertain AND the image state, and propagates all of that to every restaurant
  // that carries this master record, not just whichever restaurant's item happened
  // to surface it here. Found after this silently un-approved a batch of already-
  // approved, correctly-identified beers during an image review pass that had
  // nothing to do with their content.
  async function handleDisqualify() {
    const item = imageQueue[currentIdx];
    if (!item) return;
    if (!window.confirm(`Flag "${item.displayName || item.canonicalName}" for review? This un-approves it at EVERY restaurant that carries this item, not just its image here - each one goes back to pending review. Their managers will find it under Manage Items → Needs review → Needs further review.`)) return;
    setActionMsg('Flagging for manager review...');
    try {
      await authFetch(DISQUALIFY_IMAGE_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: item.type, canonicalKey: item.canonicalKey, reason: 'Generic/region name — needs manager clarification' }) });
      setActionMsg('Flagged for manager review');
      setTimeout(() => {
        setActionMsg('');
        const newQueue = imageQueue.filter((_, i) => i !== currentIdx);
        setImageQueue(newQueue);
        setCurrentIdx(i => Math.min(i, Math.max(0, newQueue.length - 1)));
      }, 800);
    } catch (e) { setActionMsg('Error'); }
  }

  async function handlePrev() {
    const item = imageQueue[currentIdx];
    if (!item) return;
    if ((item.candidateIndex || 0) === 0) { setActionMsg('Already on first image'); setTimeout(() => setActionMsg(''), 1000); return; }
    setActionMsg('Going back...');
    try {
      const res = await authFetch(PREV_IMAGE_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: item.type, canonicalKey: item.canonicalKey }) });
      const data = await res.json();
      setActionMsg('');
      setImageQueue(prev => prev.map((item2, i) => i === currentIdx ? { ...item2, labelImageUrl: data.labelImageUrl, candidateIndex: data.candidateIndex, candidateCount: data.candidateCount } : item2));
    } catch (e) { setActionMsg('Error'); }
  }

  async function handleReject() {
    const item = imageQueue[currentIdx];
    if (!item) return;
    setActionMsg('Finding next image...');
    try {
      const res = await authFetch(REJECT_IMAGE_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: item.type, canonicalKey: item.canonicalKey, mode: 'cycle' }) });
      const data = await res.json();
      if (data.error) { setActionMsg(`Error: ${data.error}`); return; }
      // Cycling never removes the item from the queue. Looking at candidates is
      // browsing; the wine stays pending until it is approved, uploaded to, or
      // explicitly given up on.
      if (data.action === 'no_candidates') setActionMsg('No stored candidates — try Find More');
      else if (data.action === 'wrapped') setActionMsg('Back to the first image');
      else setActionMsg('Next image');
      setImageQueue(prev => prev.map((item2, i) => i === currentIdx ? { ...item2, labelImageUrl: data.labelImageUrl, candidateIndex: data.candidateIndex, candidateCount: data.candidateCount } : item2));
      setTimeout(() => setActionMsg(''), 1200);
    } catch (e) { setActionMsg('Error'); }
  }

  // Explicit, because it spends a SerpApi call. Never marks the record
  // unresolvable — an empty result may just mean the monthly quota is gone.
  async function handleFindMore() {
    const item = imageQueue[currentIdx];
    if (!item) return;
    setActionMsg('Searching for more images…');
    try {
      const res = await authFetch(REJECT_IMAGE_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: item.type, canonicalKey: item.canonicalKey, mode: 'search_more' }) });
      const data = await res.json();
      if (data.error) { setActionMsg(`Error: ${data.error}`); return; }
      if (data.action === 'none_found') {
        setActionMsg('No more found — the wine stays pending');
        setTimeout(() => setActionMsg(''), 2000);
        return;
      }
      setActionMsg(`Found ${data.candidateCount - (item.candidateCount || 0)} more`);
      setImageQueue(prev => prev.map((item2, i) => i === currentIdx ? { ...item2, labelImageUrl: data.labelImageUrl, candidateIndex: data.candidateIndex, candidateCount: data.candidateCount } : item2));
      setTimeout(() => setActionMsg(''), 1500);
    } catch (e) { setActionMsg('Error'); }
  }

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const item = imageQueue[currentIdx];
    if (!item) return;
    setActionMsg('Uploading...');
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const base64 = ev.target.result.split(',')[1];
        await authFetch(UPLOAD_IMAGE_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: item.type, canonicalKey: item.canonicalKey, imageBase64: base64, contentType: file.type }) });
        setActionMsg('Uploaded & approved!');
        setTimeout(() => { setActionMsg(''); setImageQueue(prev => prev.filter((_, i) => i !== currentIdx)); setCurrentIdx(i => Math.max(0, i - 1)); }, 1000);
      } catch (err) { setActionMsg('Upload failed'); }
    };
    reader.readAsDataURL(file);
  }

  // Firebase restores a persisted session asynchronously. Showing the sign-in
  // form before that resolves makes a signed-in super user think they were
  // logged out and re-enter credentials for no reason.
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: t.bgBase, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: t.fontSerif, color: t.textDim, fontSize: 13 }}>
        Loading...
      </div>
    );
  }

  if (!authed) {
    const field = { ...inputLarge(t), borderRadius: 8, marginBottom: 10 };
    return (
      <div style={{ minHeight: '100vh', background: t.bgBase, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: t.fontSerif }}>
        <div style={{ color: t.accent, fontSize: 11, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: 8 }}>SommSync</div>
        <div style={{ color: t.textPrimary, fontSize: 22, marginBottom: 32 }}>Admin Portal</div>
        <div style={{ background: t.bgSurface, borderRadius: 12, padding: 32, width: 320 }}>
          <input style={field} type="email" placeholder="Email" defaultValue={email} autoComplete="username"
            ref={emailRef} onChange={e => setEmail(e.target.value)} />
          <input style={field} type="password" placeholder="Password" defaultValue={password} autoComplete="current-password"
            ref={passwordRef} onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !loggingIn && handleLogin()} />
          {loginError && <div style={{ color: t.error, fontSize: 12, marginBottom: 12, lineHeight: 1.4 }}>{loginError}</div>}
          <button onClick={handleLogin} disabled={loggingIn}
            style={{ width: '100%', padding: '11px', borderRadius: 8, border: 'none', background: t.accent, color: t.accentText, fontFamily: t.fontSerif, fontSize: 14, cursor: loggingIn ? 'default' : 'pointer', opacity: loggingIn ? 0.6 : 1 }}>
            {loggingIn ? 'Signing in...' : 'Sign In'}
          </button>
        </div>
      </div>
    );
  }

  const item = imageQueue[currentIdx];
  const typeLabel = item?.type === 'wines' ? 'Wine' : item?.type === 'beers' ? 'Beer' : 'Spirit';

  return (
    <div style={{ minHeight: '100vh', background: t.bgBase, fontFamily: t.fontSerif, color: t.textPrimary }}>
      {/* Header */}
      <div style={{ background: t.bgSurface, borderBottom: `1px solid ${t.borderSubtle}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ color: t.accent, fontSize: 10, letterSpacing: '3px', textTransform: 'uppercase' }}>SommSync</div>
          <div style={{ color: t.textPrimary, fontSize: 18 }}>Admin Portal</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: t.textDim, fontSize: 11 }}>{user?.email}</span>
          <button onClick={() => { authSignOut(); setEmail(''); setPassword('');
            if (emailRef.current) emailRef.current.value = '';
            if (passwordRef.current) passwordRef.current.value = ''; }} style={{ background: t.white06, border: 'none', color: t.textSecondary, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Sign Out</button>
        </div>
      </div>
      {/* Tabs. A line icon beside each label. The counts of work waiting (images to review, corrections) are badges
          on the icon rather than "(79)" in the label, and a count of zero shows nothing. Accounts has no badge: it
          counts every account, not ones waiting, and reads 0 until that tab has been opened. The bar scrolls
          sideways on a narrow screen instead of squeezing the tabs. */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${t.borderSubtle}`, padding: '0 24px', overflowX: 'auto' }}>
        {[
          { id: 'restaurants', label: 'Restaurants', icon: 'store' },
          { id: 'onboarding', label: 'Onboarding', icon: 'clip' },
          { id: 'images', label: 'Image Queue', icon: 'image', count: imageQueue.length + imageReports.length },
          { id: 'corrections', label: 'Corrections', icon: 'pencil', count: corrections.length },
          { id: 'master', label: 'Master DB', icon: 'layers' },
          { id: 'usage', label: 'Usage', icon: 'chart' },
          { id: 'accounts', label: 'Accounts', icon: 'user' }
        ].map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); if (tab.id === 'usage' && !usage) loadUsage(); if (tab.id === 'accounts') loadAccounts(); }}
            aria-label={tab.count ? `${tab.label} (${tab.count})` : undefined} aria-current={activeTab === tab.id ? 'page' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 9, flex: 'none', whiteSpace: 'nowrap', padding: '12px 18px', border: 'none', borderBottom: activeTab === tab.id ? `2px solid ${t.accent}` : '2px solid transparent', background: 'transparent', color: activeTab === tab.id ? t.accent : t.textSecondary, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
            <span style={{ position: 'relative', display: 'inline-flex', marginRight: tab.count > 0 ? 13 : 0 }}>
              <LineIcon name={tab.icon} />
              {tab.count > 0 && (
                // lining figures in a sans face: the serif's old-style numerals make "79" look uneven at this size
                <span aria-hidden="true" style={{ position: 'absolute', top: -8, right: -11, minWidth: 17, height: 17, padding: '0 4px', borderRadius: 9, boxSizing: 'border-box', background: t.accent, color: readableOn(t.accent, [t.accentText, t.bgSurface]), fontSize: 10, lineHeight: '17px', textAlign: 'center', fontWeight: 600, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', fontVariantNumeric: 'lining-nums' }}>
                  {tab.count > 99 ? '99+' : tab.count}
                </span>
              )}
            </span>
            {tab.label}
          </button>
        ))}
        <button onClick={() => { loadImageQueue(); loadRestaurants(); loadCorrections(); loadImageReports(); if (activeTab === 'usage') loadUsage(); }} style={{ marginLeft: 'auto', flex: 'none', display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: t.textSecondary, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12, padding: '12px 0 12px 18px' }}><LineIcon name="refresh" size={14} />Refresh</button>
      </div>

      {/* Usage Tab */}
      {activeTab === 'usage' && (
        <div style={{ maxWidth: 860, margin: '0 auto', padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 18, color: t.textPrimary }}>Claude API Usage</div>
              <div style={{ fontSize: 11, color: t.textDim, marginTop: 3 }}>
                Token counts reported by the API. Costs are estimates at list rates.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {/* 1d is today in UTC, not the last 24 hours - usage buckets are keyed
                  by UTC day. Late evening in Phoenix is already tomorrow's bucket, so
                  a fresh 1d view can look empty when work just ran. */}
              {[1, 7, 30, 90].map(d => (
                <button key={d} onClick={() => { setUsageDays(d); loadUsage(d); }}
                  style={{ padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: t.fontSerif,
                    background: usageDays === d ? t.accentDim : t.white04,
                    border: `0.5px solid ${usageDays === d ? t.accent : t.borderMid}`,
                    color: usageDays === d ? t.accent : t.textSecondary }}>
                  {d}d
                </button>
              ))}
            </div>
          </div>

          {/* ── Session log (testing instrument) ─────────────────────────────
              Per-call rows grouped by guest session. The daily buckets answer
              what the platform spent; this answers what one guest interaction
              cost, which is the only question a test day is asking.
              Disposable: turn it off, clear it, and delete platform/callLog. */}
          <div style={{ background: t.bgSurface, border: `1px solid ${callLog && callLog.enabled ? t.accent : t.borderSubtle}`, borderRadius: 8, padding: 14, marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: t.textPrimary, fontSize: 13 }}>
                  Session log
                  {callLog && callLog.enabled && <span style={{ color: t.success, fontSize: 11, marginLeft: 8 }}>recording</span>}
                </div>
                <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 3, lineHeight: 1.5 }}>
                  One row per AI call, grouped by guest session. For testing &mdash; leave off in normal use.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => (callLog ? loadCallLog() : loadCallLog())} disabled={callLogBusy}
                  style={{ padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: t.fontSerif,
                    background: t.white04, border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, opacity: callLogBusy ? 0.5 : 1 }}>
                  {callLog ? 'Refresh' : 'Load'}
                </button>
                {callLog && !callLog.error && (
                  <button onClick={() => controlCallLog({ enabled: !callLog.enabled })} disabled={callLogBusy}
                    style={{ padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: t.fontSerif,
                      background: callLog.enabled ? t.white04 : t.accentDim,
                      border: `0.5px solid ${callLog.enabled ? t.borderMid : t.accent}`,
                      color: callLog.enabled ? t.textSecondary : t.accent, opacity: callLogBusy ? 0.5 : 1 }}>
                    {callLog.enabled ? 'Stop' : 'Start recording'}
                  </button>
                )}
              </div>
            </div>

            {callLog && callLog.error && <div style={{ color: t.warning, fontSize: 12, marginTop: 10 }}>{callLog.error}</div>}

            {callLog && !callLog.error && (callLog.sessions || []).length > 0 && (
              <div style={{ marginTop: 14 }}>
                {callLog.sessions.map(sn => (
                  <div key={sn.session} style={{ padding: '10px 0', borderBottom: `1px solid ${t.borderSubtle}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                      <span style={{ color: t.textPrimary, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sn.session === 'no-session' ? 'Admin / background work' : sn.session}
                      </span>
                      <span style={{ color: t.accent, fontSize: 13, flexShrink: 0 }}>${(sn.costUsd || 0).toFixed(4)}</span>
                    </div>
                    <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 3, lineHeight: 1.5 }}>
                      {sn.calls} call{sn.calls === 1 ? '' : 's'}
                      {sn.freeCalls > 0 && <span style={{ color: t.success }}> · {sn.freeCalls} free (cached)</span>}
                      {sn.restaurantId ? ` · ${sn.restaurantId}` : ''}
                      {sn.durationSec ? ` · ${sn.durationSec}s` : ''}
                      {' · '}{new Date(sn.last).toLocaleTimeString()}
                    </div>
                    <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 2 }}>
                      {Object.entries(sn.categories || {}).map(([c, n]) => `${c.replace(/_/g, ' ')} x${n}`).join(' · ')}
                      {' · '}{(sn.inputTokens || 0).toLocaleString()} in, {(sn.outputTokens || 0).toLocaleString()} out
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                  <span style={{ color: t.textDim, fontSize: 10.5 }}>
                    {callLog.entryCount} call{callLog.entryCount === 1 ? '' : 's'} logged
                  </span>
                  <span style={{ display: 'flex', gap: 12 }}>
                  <button onClick={downloadSessionLog} disabled={callLogBusy}
                    style={{ background: 'none', border: 'none', color: t.accent, cursor: 'pointer', fontSize: 10.5, textDecoration: 'underline' }}>
                    Download review file{selectedRestoId ? ` (${selectedRestoId})` : ' — pick a restaurant first'}
                  </button>
                  <button onClick={() => { if (window.confirm('Clear the log? This deletes the recorded guest conversations as well as the cost rows. Download the review file first if you need it.')) controlCallLog({ clear: true }); }} disabled={callLogBusy}
                    style={{ background: 'none', border: 'none', color: t.textDim, cursor: 'pointer', fontSize: 10.5, textDecoration: 'underline' }}>
                    Clear log
                  </button>
                  </span>
                </div>
              </div>
            )}

            {callLog && !callLog.error && (callLog.sessions || []).length === 0 && (
              <div style={{ color: t.textDim, fontSize: 11.5, marginTop: 10 }}>
                {callLog.enabled ? 'Recording. Nothing logged yet — use the guest app, then refresh.' : 'Not recording.'}
              </div>
            )}
          </div>

          {/* Baseline / delta measurement.
              The buckets are cumulative counters, so no hour or minute bucketing is
              needed to isolate a piece of work: snapshot the counter, do the work,
              read the counter again, subtract. The click is the timestamp. */}
          {usage && !usage.error && (
            <div style={{ background: t.bgSurface, border: `1px solid ${usageBaseline ? t.accent : t.borderSubtle}`, borderRadius: 8, padding: 14, marginBottom: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: t.textPrimary, fontSize: 13 }}>Measure a piece of work</div>
                  <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 3, lineHeight: 1.5 }}>
                    {usageBaseline
                      ? `Baseline set ${new Date(usageBaseline.at).toLocaleString()} over ${usageBaseline.days}d. Run the work, then refresh.`
                      : 'Snapshot the counters, run something, refresh. The difference is what that run cost.'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => loadUsage(usageDays)} disabled={usageLoading}
                    style={{ padding: '6px 12px', borderRadius: 6, cursor: usageLoading ? 'default' : 'pointer', fontSize: 12, fontFamily: t.fontSerif,
                      background: t.white04, border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, opacity: usageLoading ? 0.5 : 1 }}>
                    Refresh
                  </button>
                  <button onClick={usageBaseline ? clearUsageBaseline : markUsageBaseline}
                    style={{ padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: t.fontSerif,
                      background: usageBaseline ? t.white04 : t.accentDim,
                      border: `0.5px solid ${usageBaseline ? t.borderMid : t.accent}`,
                      color: usageBaseline ? t.textSecondary : t.accent }}>
                    {usageBaseline ? 'Clear baseline' : 'Set baseline'}
                  </button>
                </div>
              </div>

              {usageBaseline && (() => {
                const d = (now, was) => (Number(now) || 0) - (Number(was) || 0);
                const tot = {
                  costUsd: d(usage.total?.costUsd, usageBaseline.total?.costUsd),
                  calls: d(usage.total?.calls, usageBaseline.total?.calls),
                  inputTokens: d(usage.total?.inputTokens, usageBaseline.total?.inputTokens),
                  outputTokens: d(usage.total?.outputTokens, usageBaseline.total?.outputTokens),
                  cacheReadTokens: d(usage.total?.cacheReadTokens, usageBaseline.total?.cacheReadTokens),
                  cacheWriteTokens: d(usage.total?.cacheWriteTokens, usageBaseline.total?.cacheWriteTokens),
                };
                // A counter cannot go backwards. If it has, the window moved under
                // the baseline - a UTC day rolled on the 1d view, or the range was
                // changed - and every figure here is meaningless.
                const rangeChanged = usageBaseline.days !== usageDays;
                const wentBackwards = tot.calls < 0 || tot.costUsd < -1e-9;
                const cats = Array.from(new Set([
                  ...Object.keys(usage.byCategory || {}),
                  ...Object.keys(usageBaseline.byCategory || {}),
                ])).map(c => ({
                  cat: c,
                  costUsd: d(usage.byCategory?.[c]?.costUsd, usageBaseline.byCategory?.[c]?.costUsd),
                  calls: d(usage.byCategory?.[c]?.calls, usageBaseline.byCategory?.[c]?.calls),
                })).filter(x => x.calls !== 0 || Math.abs(x.costUsd) > 1e-9)
                  .sort((a, b) => b.costUsd - a.costUsd);

                if (rangeChanged || wentBackwards) {
                  return (
                    <div style={{ marginTop: 12, padding: 10, background: t.white04, borderRadius: 6, color: t.warning, fontSize: 11.5, lineHeight: 1.55 }}>
                      {rangeChanged
                        ? `Baseline was taken over ${usageBaseline.days}d and you are now viewing ${usageDays}d. Different windows are not comparable — switch back or set a new baseline.`
                        : 'The counters read lower than the baseline, which means the window moved beneath it — most likely a UTC day boundary passed while on a short range. Set a new baseline, and prefer 30d for measurements that span midnight UTC.'}
                    </div>
                  );
                }
                if (tot.calls === 0) {
                  return (
                    <div style={{ marginTop: 12, color: t.textDim, fontSize: 11.5 }}>
                      No new API calls since the baseline. Run the work, then hit Refresh.
                    </div>
                  );
                }
                const cacheable = tot.cacheReadTokens + tot.cacheWriteTokens;
                return (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                      {[
                        { label: 'Cost of this work', value: `$${tot.costUsd.toFixed(4)}` },
                        { label: 'Calls', value: tot.calls.toLocaleString() },
                        { label: 'Input', value: tot.inputTokens.toLocaleString() },
                        { label: 'Output', value: tot.outputTokens.toLocaleString() },
                        { label: 'Cache read', value: tot.cacheReadTokens.toLocaleString() },
                      ].map(c => (
                        <div key={c.label} style={{ flex: 1, background: t.white04, border: `1px solid ${t.borderSubtle}`, borderRadius: 6, padding: '10px 12px' }}>
                          <div style={{ color: t.textDim, fontSize: 9.5, letterSpacing: '1.2px', textTransform: 'uppercase' }}>{c.label}</div>
                          <div style={{ color: t.accent, fontSize: 17, marginTop: 5 }}>{c.value}</div>
                        </div>
                      ))}
                    </div>
                    {cats.map(x => (
                      <div key={x.cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${t.borderSubtle}`, fontSize: 12.5 }}>
                        <span style={{ color: t.textPrimary }}>{x.cat.replace(/_/g, ' ')}</span>
                        <span style={{ color: t.textSecondary }}>
                          ${x.costUsd.toFixed(4)}
                          <span style={{ color: t.textDim, fontSize: 11, marginLeft: 8 }}>{x.calls.toLocaleString()} calls</span>
                        </span>
                      </div>
                    ))}
                    {cacheable > 0 && (
                      <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 10, lineHeight: 1.5 }}>
                        {((tot.cacheReadTokens / cacheable) * 100).toFixed(0)}% of cacheable tokens
                        were read rather than written across this work.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {usageLoading && <div style={{ color: t.textDim, fontSize: 13, padding: 20 }}>Loading…</div>}

          {!usageLoading && usage && usage.error && (
            <div style={{ background: t.white04, border: `1px solid ${t.borderSubtle}`, borderRadius: 8, padding: 16, color: t.textSecondary, fontSize: 13 }}>
              Could not load usage: {usage.error}
            </div>
          )}

          {!usageLoading && usage && !usage.error && (usage.total?.calls || 0) === 0 && (
            <div style={{ background: t.white04, border: `1px solid ${t.borderSubtle}`, borderRadius: 8, padding: 20 }}>
              <div style={{ color: t.textPrimary, fontSize: 14, marginBottom: 8 }}>No usage recorded yet</div>
              <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.6 }}>
                Tracking starts from the moment this was deployed — earlier activity was never
                recorded and cannot be recovered. Enrichment, sommelier chat, pairings, label
                scans and About Us drafts will appear here as they run.
              </div>
            </div>
          )}

          {!usageLoading && usage && !usage.error && (usage.total?.calls || 0) > 0 && (
            <>
              {/* Headline */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
                {[
                  { label: 'Est. cost', value: `$${(usage.total.costUsd || 0).toFixed(2)}` },
                  { label: 'API calls', value: (usage.total.calls || 0).toLocaleString() },
                  { label: 'Input tokens', value: (usage.total.inputTokens || 0).toLocaleString() },
                  { label: 'Output tokens', value: (usage.total.outputTokens || 0).toLocaleString() },
                ].map(c => (
                  <div key={c.label} style={{ flex: 1, background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 8, padding: '14px 16px' }}>
                    <div style={{ color: t.textDim, fontSize: 10, letterSpacing: '1.5px', textTransform: 'uppercase' }}>{c.label}</div>
                    <div style={{ color: t.accent, fontSize: 22, marginTop: 6 }}>{c.value}</div>
                  </div>
                ))}
              </div>

              {/* Cache performance — only once there is cache activity to report */}
              {usage.total.cacheHitPct !== null && usage.total.cacheHitPct !== undefined && (
                <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 8, padding: '14px 16px', marginBottom: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div style={{ color: t.textDim, fontSize: 10, letterSpacing: '1.5px', textTransform: 'uppercase' }}>Prompt cache hit rate</div>
                    <div style={{ color: usage.total.cacheHitPct >= 60 ? t.success : t.warning, fontSize: 20 }}>{usage.total.cacheHitPct}%</div>
                  </div>
                  <div style={{ color: t.textDim, fontSize: 10.5, lineHeight: 1.55, marginTop: 8 }}>
                    {(usage.total.cacheReadTokens || 0).toLocaleString()} tokens read from cache,{' '}
                    {(usage.total.cacheWriteTokens || 0).toLocaleString()} written. Reads cost a tenth of
                    normal input; writes cost double. A low rate means the cached block is changing
                    between calls &mdash; usually a menu or stock change &mdash; so the cache is being
                    rebuilt more often than it is used.
                  </div>
                </div>
              )}

              {/* Daily */}
              {usage.daily && usage.daily.length > 0 && (() => {
                const max = Math.max(...usage.daily.map(d => d.costUsd || 0), 0.0001);
                return (
                  <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 8, padding: 16, marginBottom: 18 }}>
                    <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 12 }}>Daily estimated cost</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 90 }}>
                      {usage.daily.map(d => (
                        <div key={d.day} title={`${d.day} — $${(d.costUsd || 0).toFixed(4)} · ${d.calls} calls`}
                          style={{ flex: 1, minWidth: 3, height: `${Math.max(2, ((d.costUsd || 0) / max) * 100)}%`, background: t.accent, opacity: 0.75, borderRadius: '2px 2px 0 0' }} />
                      ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: t.textDim, fontSize: 10, marginTop: 6 }}>
                      <span>{usage.daily[0].day}</span>
                      <span>{usage.daily[usage.daily.length - 1].day}</span>
                    </div>
                  </div>
                );
              })()}

              {/* By restaurant */}
              <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 8, padding: 16, marginBottom: 18 }}>
                <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 12 }}>By restaurant</div>
                {Object.entries(usage.byRestaurant || {}).map(([rid, s]) => (
                  <div key={rid} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${t.borderSubtle}`, fontSize: 13 }}>
                    <span style={{ color: t.textPrimary }}>{rid}</span>
                    <span style={{ color: t.textSecondary }}>
                      ${(s.costUsd || 0).toFixed(2)}
                      <span style={{ color: t.textDim, fontSize: 11, marginLeft: 8 }}>{(s.calls || 0).toLocaleString()} calls</span>
                    </span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 13 }}>
                  <span style={{ color: t.textDim }}>Shared platform work</span>
                  <span style={{ color: t.textDim }}>
                    ${(usage.shared?.costUsd || 0).toFixed(2)}
                    <span style={{ fontSize: 11, marginLeft: 8 }}>{(usage.shared?.calls || 0).toLocaleString()} calls</span>
                  </span>
                </div>
                <div style={{ color: t.textDim, fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>
                  Shared work is image validation and master-database research that other
                  restaurants inherit. It is not charged to any one restaurant, so per-restaurant
                  figures understate true cost to serve.
                </div>
              </div>

              {/* By category */}
              <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 8, padding: 16 }}>
                <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 12 }}>By category</div>
                {Object.entries(usage.byCategory || {}).map(([cat, s]) => (
                  <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${t.borderSubtle}`, fontSize: 13 }}>
                    <span style={{ color: t.textPrimary }}>{cat.replace(/_/g, ' ')}</span>
                    <span style={{ color: t.textSecondary }}>
                      ${(s.costUsd || 0).toFixed(2)}
                      <span style={{ color: t.textDim, fontSize: 11, marginLeft: 8 }}>{(s.calls || 0).toLocaleString()} calls</span>
                    </span>
                  </div>
                ))}
              </div>

              {/* Pairing matrix builds — the number that decides rebuild cadence.
                  Deliberately separate from the category rollup: that answers
                  "what did the platform spend", this answers "what does one
                  restaurant's matrix cost to build", which is what a weekly or
                  monthly rebuild schedule has to be priced against. */}
              {Array.isArray(usage.matrixRuns) && usage.matrixRuns.length > 0 && (() => {
                // Latest successful full build per restaurant is the honest basis
                // for a projection. Averaging across reruns of the same tenant
                // while tuning would understate a real rebuild.
                const latest = {};
                usage.matrixRuns.forEach(r => {
                  if (r.ok === false || r.runType !== 'full') return;
                  if (!latest[r.restaurantId] || (r.at || 0) > (latest[r.restaurantId].at || 0)) latest[r.restaurantId] = r;
                });
                const basis = Object.values(latest);
                const perRebuild = basis.reduce((n, r) => n + (r.costUsd || 0), 0);
                const fmtAgo = ts => {
                  if (!ts) return '';
                  const h = (Date.now() - ts) / 3600000;
                  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m ago`;
                  if (h < 48) return `${Math.round(h)}h ago`;
                  return `${Math.round(h / 24)}d ago`;
                };
                return (
                  <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 8, padding: 16, marginTop: 18 }}>
                    <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 4 }}>Pairing matrix builds</div>
                    <div style={{ color: t.textDim, fontSize: 10.5, lineHeight: 1.55, marginBottom: 14 }}>
                      One-time cost to score every wine against every dish. Guests are served from
                      the stored matrix, so this is a build cost, not a cost to serve.
                    </div>

                    {basis.length > 0 && (
                      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                        {[
                          { label: 'Rebuild all tenants', value: `$${perRebuild.toFixed(2)}`, sub: `${basis.length} restaurant${basis.length === 1 ? '' : 's'}` },
                          { label: 'Weekly cadence', value: `$${(perRebuild * 4.33).toFixed(2)}`, sub: 'per month' },
                          { label: 'Monthly cadence', value: `$${perRebuild.toFixed(2)}`, sub: 'per month' },
                        ].map(c => (
                          <div key={c.label} style={{ flex: 1, background: t.white04, border: `1px solid ${t.borderSubtle}`, borderRadius: 6, padding: '11px 13px' }}>
                            <div style={{ color: t.textDim, fontSize: 9.5, letterSpacing: '1.2px', textTransform: 'uppercase' }}>{c.label}</div>
                            <div style={{ color: t.accent, fontSize: 18, marginTop: 5 }}>{c.value}</div>
                            <div style={{ color: t.textDim, fontSize: 10, marginTop: 2 }}>{c.sub}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {usage.matrixRuns.map(r => {
                      const cacheable = (r.cacheReadTokens || 0) + (r.cacheWriteTokens || 0);
                      const hit = cacheable > 0 ? (r.cacheReadTokens / cacheable) * 100 : null;
                      return (
                        <div key={r.id} style={{ padding: '10px 0', borderBottom: `1px solid ${t.borderSubtle}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ color: t.textPrimary, fontSize: 13 }}>
                              {r.restaurantId}
                              {r.ok === false && <span style={{ color: t.warning, fontSize: 11, marginLeft: 8 }}>failed</span>}
                              {r.runType && r.runType !== 'full' && <span style={{ color: t.textDim, fontSize: 11, marginLeft: 8 }}>{r.runType.replace(/_/g, ' ')}</span>}
                            </span>
                            <span style={{ color: t.accent, fontSize: 13 }}>${(r.costUsd || 0).toFixed(4)}</span>
                          </div>
                          <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 4, lineHeight: 1.5 }}>
                            {(r.dishesScored || 0)} dishes &times; {(r.wineCount || 0)} wines
                            {r.cells ? ` = ${r.cells.toLocaleString()} cells` : ''}
                            {r.costPerDish ? ` · $${r.costPerDish.toFixed(4)}/dish` : ''}
                            {r.durationMs ? ` · ${Math.round(r.durationMs / 1000)}s` : ''}
                            {r.calls ? ` · ${r.calls} calls` : ''}
                            {' · '}{fmtAgo(r.at)}
                          </div>
                          <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 2 }}>
                            {hit === null ? (
                              <span style={{ color: t.warning }}>no cache activity — the wine block was not cached</span>
                            ) : (
                              <>
                                cache {' '}
                                <span style={{ color: (r.calls > 1 && hit < 50) ? t.warning : t.success }}>{hit.toFixed(0)}%</span>
                                {' '}· {(r.cacheReadTokens || 0).toLocaleString()} read, {(r.cacheWriteTokens || 0).toLocaleString()} written
                                {r.calls > 1 && hit < 50 && ' — wine block is being rewritten between batches'}
                              </>
                            )}
                          </div>
                          {r.error && <div style={{ color: t.warning, fontSize: 10.5, marginTop: 3 }}>{r.error}</div>}
                        </div>
                      );
                    })}

                    <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 12, lineHeight: 1.55 }}>
                      Projections assume every tenant rebuilds on the same schedule and cost scales
                      with the most recent successful build. A restaurant that adds wines or dishes
                      will cost more than its last run suggests.
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* Accounts Tab */}
      {activeTab === 'accounts' && (
        <div style={{ maxWidth: 700, margin: '0 auto', padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div style={{ color: t.textPrimary, fontSize: 16 }}>Accounts ({accounts.length})</div>
            <button onClick={() => { setShowAddAccount(true); setAcctError(''); setAcctDraft({ email: '', password: suggestPassword(), role: 'owner', restaurantIds: [] }); }}
              style={{ background: t.accent, border: 'none', color: t.bgBase, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
              + Add Account
            </button>
          </div>
          <div style={{ color: t.textDim, fontSize: 11, marginBottom: 20, lineHeight: 1.6 }}>
            Super users reach every restaurant. Owners reach only the ones listed against them.
            Floor managers do not have accounts — they use their restaurant's PIN.
          </div>

          {acctError && <div style={{ color: t.error, fontSize: 12, marginBottom: 14 }}>{acctError}</div>}

          {/* Add account form */}
          {showAddAccount && (
            <div style={{ background: t.bgSurface, borderRadius: 10, padding: 18, marginBottom: 20, border: `1px solid ${t.accent}` }}>
              <div style={{ color: t.textPrimary, fontSize: 14, marginBottom: 14 }}>New Account</div>

              <label style={{ color: t.textDim, fontSize: 10, letterSpacing: '1.5px', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Email</label>
              <input value={acctDraft.email} onChange={e => setAcctDraft(d => ({ ...d, email: e.target.value }))}
                style={{ width: '100%', boxSizing: 'border-box', background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '8px 10px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13, marginBottom: 12 }} />

              <label style={{ color: t.textDim, fontSize: 10, letterSpacing: '1.5px', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Initial Password</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                <input value={acctDraft.password} onChange={e => setAcctDraft(d => ({ ...d, password: e.target.value }))}
                  style={{ flex: 1, background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '8px 10px', borderRadius: 6, fontFamily: t.fontMono, fontSize: 13 }} />
                <button onClick={() => setAcctDraft(d => ({ ...d, password: suggestPassword() }))}
                  style={{ background: 'none', border: `0.5px solid ${t.borderMid}`, color: t.textDim, padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 11 }}>
                  ↻ New
                </button>
              </div>
              <div style={{ color: t.textDim, fontSize: 10, marginBottom: 12, lineHeight: 1.5 }}>
                Copy this now — it is not stored anywhere you can read it back. Tell them to change it after signing in.
              </div>

              <label style={{ color: t.textDim, fontSize: 10, letterSpacing: '1.5px', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Role</label>
              <select value={acctDraft.role} onChange={e => setAcctDraft(d => ({ ...d, role: e.target.value, restaurantIds: e.target.value === 'super' ? [] : d.restaurantIds }))}
                style={{ width: '100%', background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '8px 10px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13, marginBottom: 12 }}>
                <option value="owner">Restaurant Owner</option>
                <option value="super">Super User</option>
              </select>

              {acctDraft.role === 'owner' && (
                <>
                  <label style={{ color: t.textDim, fontSize: 10, letterSpacing: '1.5px', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Restaurants</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                    {restaurants.map(r => {
                      const on = acctDraft.restaurantIds.includes(r.id);
                      return (
                        <button key={r.id}
                          onClick={() => setAcctDraft(d => ({ ...d, restaurantIds: on ? d.restaurantIds.filter(x => x !== r.id) : [...d.restaurantIds, r.id] }))}
                          style={{ background: on ? t.accent : 'none', border: `0.5px solid ${on ? t.accent : t.borderMid}`, color: on ? t.bgBase : t.textDim, padding: '5px 12px', borderRadius: 14, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12 }}>
                          {r.name}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleCreateAccount} disabled={acctBusy}
                  style={{ flex: 1, background: t.accent, border: 'none', color: t.bgBase, padding: '9px', borderRadius: 8, cursor: acctBusy ? 'default' : 'pointer', fontFamily: t.fontSerif, fontSize: 13, opacity: acctBusy ? 0.6 : 1 }}>
                  {acctBusy ? 'Creating…' : 'Create Account'}
                </button>
                <button onClick={() => { setShowAddAccount(false); setAcctError(''); }}
                  style={{ background: 'none', border: `0.5px solid ${t.borderMid}`, color: t.textDim, padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Account list */}
          {accountsLoading && <div style={{ color: t.textDim, fontSize: 13 }}>Loading…</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {accounts.map(a => (
              <div key={a.uid} style={{ background: t.bgSurface, borderRadius: 10, padding: '14px 16px', border: `1px solid ${t.borderSubtle}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: t.textPrimary, fontSize: 14 }}>
                      {a.email}
                      {a.uid === user?.uid && <span style={{ color: t.textDim, fontSize: 11 }}> · you</span>}
                    </div>
                    <div style={{ color: t.textDim, fontSize: 11, marginTop: 3 }}>
                      {a.role === 'super' ? 'Super user · all restaurants'
                        : `Owner · ${(a.restaurantIds || []).join(', ') || 'no restaurants assigned'}`}
                    </div>
                    <div style={{ color: t.textDim, fontSize: 10, marginTop: 3 }}>
                      {a.lastSignIn ? `Last signed in ${new Date(a.lastSignIn).toLocaleDateString()}` : 'Never signed in'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => { setEditingAcct(editingAcct === a.uid ? null : a.uid); setAcctError(''); }}
                      style={{ background: 'none', border: `0.5px solid ${t.borderMid}`, color: t.textDim, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 11 }}>
                      {editingAcct === a.uid ? 'Close' : 'Edit'}
                    </button>
                    {a.uid !== user?.uid && (
                      <button onClick={() => handleDeleteAccount(a.uid, a.email)} disabled={acctBusy}
                        style={{ background: 'none', border: `0.5px solid ${t.borderMid}`, color: t.error, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 11 }}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {editingAcct === a.uid && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.borderSubtle}` }}>
                    {a.role === 'owner' && (
                      <>
                        <div style={{ color: t.textDim, fontSize: 10, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 6 }}>Restaurants</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                          {restaurants.map(r => {
                            const on = (a.restaurantIds || []).includes(r.id);
                            return (
                              <button key={r.id} disabled={acctBusy}
                                onClick={() => handleUpdateAccount(a.uid, {
                                  restaurantIds: on ? a.restaurantIds.filter(x => x !== r.id) : [...(a.restaurantIds || []), r.id],
                                })}
                                style={{ background: on ? t.accent : 'none', border: `0.5px solid ${on ? t.accent : t.borderMid}`, color: on ? t.bgBase : t.textDim, padding: '5px 12px', borderRadius: 14, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12 }}>
                                {r.name}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                    <button onClick={() => {
                        const pw = window.prompt('New password for ' + a.email + ' (12+ chars, upper, lower, number, special):');
                        if (!pw) return;
                        const problem = passwordProblem(pw);
                        if (problem) { setAcctError(`Password: ${problem.toLowerCase()}`); return; }
                        handleUpdateAccount(a.uid, { password: pw });
                      }} disabled={acctBusy}
                      style={{ background: 'none', border: `0.5px solid ${t.borderMid}`, color: t.textDim, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12 }}>
                      Set new password
                    </button>
                    <div style={{ color: t.textDim, fontSize: 10, marginTop: 8, lineHeight: 1.5 }}>
                      Changing a role, scope or password signs them out of every device.
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Restaurants Tab */}
      {activeTab === 'restaurants' && (
        <div style={{ maxWidth: 700, margin: '0 auto', padding: 24 }}>
          {/* Platform-wide, not scoped to one restaurant - see runImageFetchBatch.
              Warning-colored deliberately: this pauses real image fetching for
              every tenant, including live ones, so it should be impossible to
              forget it's on. Meant for the window around a heavy debugging or
              re-enrichment session, not a standing setting. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: serpPaused ? (t.warningDim || t.accentDim) : t.white04, border: `0.5px solid ${serpPaused ? (t.warningBorder || t.accentBorder) : t.borderMid}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: serpPaused ? (t.warning || t.accent) : t.textDim, fontFamily: t.fontSerif }}>
              {serpPaused
                ? '⏸ Label image fetching is PAUSED platform-wide — no restaurant is spending SerpApi quota right now.'
                : 'Label image fetching is live — pause it before a heavy testing or re-enrichment session.'}
            </div>
            <button onClick={handleToggleSerpPause}
              style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, fontFamily: t.fontSerif, cursor: 'pointer', whiteSpace: 'nowrap',
                background: serpPaused ? t.accent : t.white06,
                border: `0.5px solid ${serpPaused ? t.accent : t.borderMid}`,
                color: serpPaused ? t.bgBase : t.textSecondary }}>
              {serpPaused ? 'Resume Fetching' : 'Pause Fetching'}
            </button>
          </div>

          {/* Critic-score lookups (criticScoreScheduler, every 10 min). Read-only:
              the pause is platform/criticScores/paused in the console. A fatal
              error - usually web search switched off in the Anthropic Console -
              stops every run until it is fixed, so it is shown in red. */}
          {criticPlatform && (() => {
            const cp = criticPlatform;
            const err = cp.lastError && cp.pendingPlatform > 0 ? cp.lastError : null;
            const tone = err ? t.error : cp.paused ? (t.warning || t.accent) : t.textDim;
            const text = err
              ? `⚠ Critic-score lookups are failing: ${err.message} (${new Date(err.at).toLocaleString()}). Check that web search is on in the Anthropic Console.`
              : cp.paused
                ? `⏸ Critic-score lookups are PAUSED (platform/criticScores/paused). ${cp.pendingPlatform} wines waiting.`
                : cp.pendingPlatform > 0
                  ? `Critic-score lookups running — ${cp.pendingPlatform} wines platform-wide still to look up (15 every 10 minutes).`
                  : 'Critic-score lookups: every wine has been looked up.';
            return (
              <div style={{ background: err ? (t.errorDim || t.white04) : t.white04, border: `0.5px solid ${err ? t.error : t.borderMid}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: tone, fontFamily: t.fontSerif }}>
                {text}
              </div>
            );
          })()}

          {/* Dry-run by default (retryLabelImages requires apply:true to actually
              write anything) - the target list below is the safety net, letting
              a super user eyeball what's about to be reset before committing,
              rather than trusting `since`-style filtering alone. */}
          <div style={{ background: t.white04, border: `0.5px solid ${t.borderMid}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ fontSize: 12, color: t.textDim, fontFamily: t.fontSerif }}>
                {retryCheck === null && 'A batch enrichment run can hit a transient SerpApi error that gets recorded the same as "no image found" — check for master records stuck this way.'}
                {retryCheck === 'loading' && 'Checking…'}
                {retryCheck && retryCheck !== 'loading' && `${retryCheck.wouldReset} item(s) platform-wide currently stuck at "no image found."`}
              </div>
              <button onClick={handleCheckStuckImages} disabled={retryCheck === 'loading'}
                style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, fontFamily: t.fontSerif, cursor: 'pointer', whiteSpace: 'nowrap', background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textSecondary }}>
                <LineIcon name="repeat" inline />Check for Stuck Images
              </button>
            </div>
            {retryCheck && retryCheck !== 'loading' && retryCheck.wouldReset > 0 && (
              <div style={{ marginTop: 10, borderTop: `0.5px solid ${t.borderMid}`, paddingTop: 10 }}>
                <div style={{ fontSize: 11, color: t.textDim, marginBottom: 8, maxHeight: 120, overflowY: 'auto' }}>
                  {retryCheck.targets.slice(0, 30).map(tg => (
                    <div key={tg.canonicalKey} style={{ padding: '2px 0' }}>{tg.canonicalName || tg.canonicalKey}</div>
                  ))}
                  {retryCheck.targets.length > 30 && <div style={{ opacity: 0.6 }}>…and {retryCheck.targets.length - 30} more</div>}
                </div>
                <button onClick={handleRetryStuckImages} disabled={retrying}
                  style={{ width: '100%', padding: '7px', borderRadius: 6, fontSize: 12, fontFamily: t.fontSerif, cursor: 'pointer', background: t.accent, border: 'none', color: t.bgBase, fontWeight: 600 }}>
                  {retrying ? 'Retrying…' : `Retry ${retryCheck.wouldReset} image search(es)`}
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ color: t.textPrimary, fontSize: 16 }}>Restaurants ({restaurants.length})</div>
            <button onClick={() => setShowAddRestaurant(true)}
              style={{ background: t.accent, border: 'none', color: t.bgBase, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
              + Add Restaurant
            </button>
          </div>

          {listMsg && (
            <div role="status" style={{ color: listMsg.ok ? t.success : t.error, fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>{listMsg.text}</div>
          )}

          {/* Add Restaurant Form. Opens right under the "Restaurants (N)" header, above the list: it used
              to render after the whole list, so on a long list "+ Add Restaurant" appeared to do nothing. */}
          {showAddRestaurant && (
            <div style={{ background: t.bgSurface, borderRadius: 12, padding: 24, border: `1px solid ${t.borderSubtle}`, marginBottom: 20 }}>
              <div style={{ color: t.textPrimary, fontSize: 15, marginBottom: 16 }}>Add New Restaurant</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ color: t.textSecondary, fontSize: 11, marginBottom: 4 }}>Restaurant ID (slug, e.g. "corduroy")</div>
                  <input value={newResto.restaurantId} onChange={e => setNewResto(p => ({ ...p, restaurantId: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                    style={{ width: '100%', background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '8px 12px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13, boxSizing: 'border-box' }} placeholder="e.g. bellmore-grill" />
                </div>
                <div>
                  <div style={{ color: t.textSecondary, fontSize: 11, marginBottom: 4 }}>Restaurant Name (required)</div>
                  <input ref={newRestoNameRef} value={newResto.name} maxLength={80} aria-required="true" aria-invalid={nameError}
                    onChange={e => { setNewResto(p => ({ ...p, name: e.target.value })); if (nameError) setNameError(false); }}
                    style={{ width: '100%', background: t.white06, border: `${nameError ? '1px' : '0.5px'} solid ${nameError ? t.error : t.borderMid}`, color: t.textPrimary, padding: '8px 12px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13, boxSizing: 'border-box' }} placeholder="e.g. Bellmore Grill" />
                  {nameError && <div role="alert" style={{ color: t.error, fontSize: 11, marginTop: 4 }}>A restaurant name is required.</div>}
                </div>
                <div>
                  <div style={{ color: t.textSecondary, fontSize: 11, marginBottom: 4 }}>POS System</div>
                  <select value={newResto.posType} onChange={e => { setNewResto(p => ({ ...p, posType: e.target.value })); setNewCreds({}); }}
                    style={{ width: '100%', background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '8px 12px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13 }}>
                    {posAdapters.map(a => <option key={a.key} value={a.key}>{a.name}</option>)}
                  </select>
                </div>
                <TimezonePicker t={t} mode="form" value={newResto.timezone} onChange={tz => setNewResto(p => ({ ...p, timezone: tz }))}
                  selectStyle={{ width: '100%', background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '8px 12px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13 }} />
                <div>
                  <div style={{ color: t.textSecondary, fontSize: 11, marginBottom: 4 }}>Plan</div>
                  <select value={newResto.plan} onChange={e => setNewResto(p => ({ ...p, plan: e.target.value }))}
                    style={{ width: '100%', background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '8px 12px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13 }}>
                    {/* Feature level only. What a restaurant pays comes from its Stripe
                        plan (Account & billing); the old dollar figures here were
                        placeholders from before Stripe and are gone on purpose. */}
                    <option value="premium">✦ {PLAN_LABELS.premium}</option>
                    <option value="base">◇ {PLAN_LABELS.base}</option>
                  </select>
                </div>
                {/* Manual/file-import tenants are created empty. Menu content
                    is loaded afterward from Sync with POS → Update Menu from
                    File - the same screen used for every later re-upload, so
                    there is exactly one import code path instead of two. */}
                {newResto.posType === 'manual' && (
                  <div style={{ background: t.white06, borderRadius: 8, padding: 14, border: `0.5px solid ${t.borderMid}` }}>
                    <div style={{ color: t.textPrimary, fontSize: 12, marginBottom: 4 }}>No credentials needed</div>
                    <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5 }}>
                      This creates an empty tenant. Add the wine, food, beer, pours, cocktail
                      and non-alcoholic lists afterward from the restaurant's own
                      Manager → Update Menu → Update Menu from File screen.
                    </div>
                  </div>
                )}
                {/* Demo site: a full-featured tenant that expires. Used to show a
                    prospect the product running on their own menu. */}
                <div style={{ background: t.white06, borderRadius: 8, padding: 14, border: `0.5px solid ${t.borderMid}` }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={demoOpts.isDemo}
                      onChange={e => setDemoOpts(p => ({ ...p, isDemo: e.target.checked }))} />
                    <span style={{ color: t.textPrimary, fontSize: 12 }}>Demo site (expires)</span>
                  </label>
                  {demoOpts.isDemo && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ color: t.textSecondary, fontSize: 11, marginBottom: 4 }}>Expires after (days)</div>
                      <input type="number" min="1" max="365" value={demoOpts.days}
                        onChange={e => setDemoOpts(p => ({ ...p, days: e.target.value }))}
                        style={{ width: 100, background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '6px 10px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13 }} />
                      <div style={{ color: t.textDim, fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
                        Full premium features. After this date the guest app shows a contact
                        page and AI calls stop; data is retained for 30 more days.
                      </div>
                    </div>
                  )}
                </div>
                {/* Dynamic credential fields based on POS type */}
                {(posAdapters.find(a => a.key === newResto.posType)?.credentialFields || []).map(field => (
                  <div key={field.key}>
                    <div style={{ color: t.textSecondary, fontSize: 11, marginBottom: 4 }}>{field.label}</div>
                    <input type={field.type} value={newCreds[field.key] || ''} onChange={e => setNewCreds(p => ({ ...p, [field.key]: e.target.value }))}
                      style={{ width: '100%', background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '8px 12px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                ))}
                {restoMsg && <div style={{ color: restoMsg.startsWith('✅') ? t.success : t.error, fontSize: 12 }}>{restoMsg}</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button onClick={handleSaveRestaurant} disabled={savingResto}
                    style={{ flex: 1, background: t.accent, border: 'none', color: t.bgBase, padding: '11px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 14 }}>
                    {savingResto ? 'Saving...' : 'Save Restaurant'}
                  </button>
                  <button onClick={() => { setShowAddRestaurant(false); setRestoMsg(''); setNameError(false); }}
                    style={{ background: t.white06, border: 'none', color: t.textSecondary, padding: '11px 20px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Restaurant list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            {restaurants.map(r => {
              const bl = describeBilling(r.billing);
              const blTone = bl ? { ok: t.textSecondary, warn: t.warning, bad: t.error, dim: t.textSecondary }[bl.tone] : null;
              const held = releaseStates[r.id] === 'held';
              const demoOn = demoStates[r.id] !== undefined ? demoStates[r.id] : r.isDemo;
              const pubOn = publicDemoStates[r.id] !== undefined ? publicDemoStates[r.id] : r.publicDemo;
              const colLabel = { fontSize: 9.5, letterSpacing: '1.6px', textTransform: 'uppercase', color: t.textSecondary, marginBottom: 6 };
              const stop = e => e.stopPropagation();
              // Matrix health. Read-only: there is nothing to toggle, and the Onboarding tab is where a build is
              // started. It is here because a missing matrix is otherwise invisible: the guest still gets a
              // pairing, just from the model path at ~14x the cost and without the tier ladder. Red is a real
              // problem; amber is drift worth knowing about.
              const mx = r.matrix || null;
              const mxBad = !!mx && (mx.state === 'none' || mx.state === 'partial' || mx.state === 'gaps');
              const mxTone = !mx ? null : mxBad ? 'bad' : mx.state === 'drift' ? 'warn' : mx.state === 'ok' ? 'ok' : 'dim';
              const mxAge = mx && mx.generatedAt ? ` · built ${Math.max(0, Math.round((Date.now() - mx.generatedAt) / 86400000))}d ago` : '';
              // Translation status (Oct 30, 2026): coverage across the restaurant's languages, from the last
              // translation run. Red: stuck items, or work pending but no run for 3 h+ (the sweep is hourly).
              // Amber: still translating.
              const tp = translationProgress(r.translationStatus);
              const tpStalled = !!tp && tp.pending > 0 && tp.ageMin !== null && tp.ageMin > 180;
              const tpBad = !!tp && (tp.stuck > 0 || tpStalled);
              const tpTone = !tp ? null : tpBad ? 'bad' : tp.pending > 0 ? 'warn' : 'ok';
              const tpDetail = tp ? tp.rows.map(x => `${x.entry ? x.entry.english : x.code}: ${x.done}/${x.total}${x.sections !== null ? `, headings ${x.sections}/${tp.sectionsTotal}` : ''}`).join('\n') : '';
              const phase = r.pipelinePhase;
              const phTone = phase === 'complete' ? 'ok' : phase === 'idle' ? 'dim' : phase === 'error' ? 'bad' : 'warn';
              // Critic scores (Nov 3, 2026): this restaurant's wines in the critic-score lookup job.
              // Amber while lookups are pending or old recalled scores are still showing; red on errors.
              const cs = r.criticScores || null;
              const csTone = !cs ? null : r.inactive ? 'dim' : cs.error > 0 ? 'bad' : (cs.pending > 0 || cs.legacy > 0) ? 'warn' : 'ok';
              const csText = !cs ? null : r.inactive ? 'Critic scores: paused (inactive)'
                : cs.pending > 0 ? `Critic scores: ${cs.found} found · ${cs.pending} to look up`
                : `Critic scores: ${cs.found} of ${cs.total} wines${cs.error ? ` · ${cs.error} errors` : ''}`;
              const csTitle = cs ? [
                `Found (verified, with source): ${cs.found}`,
                `Searched, none verifiable: ${cs.none}`,
                `No vintage on the list (no lookup): ${cs.noVintage}`,
                `Typed by a manager: ${cs.manual}`,
                `Waiting for lookup: ${cs.pending}`,
                `Lookup failed 3 times: ${cs.error}`,
                `Old recalled score still showing: ${cs.legacy}`,
                cs.noMaster ? `Not in the master database (uncertain items): ${cs.noMaster}` : null,
              ].filter(Boolean).join('\n') : undefined;
              const hrow = (key, tone, text, title) => (
                <div key={key} title={title} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, lineHeight: 1.9, color: t.textPrimary }}>
                  <StatusDot tone={tone} t={t} /><span>{text}</span>
                </div>
              );
              return (
                <div key={r.id} onClick={() => { setSelectedRestoId(r.id); setActiveTab('onboarding'); }}
                  style={{ background: t.bgSurface, borderRadius: 12, padding: '16px 18px 14px', border: `1px solid ${r.id === selectedRestoId ? t.accent : t.borderSubtle}`, cursor: 'pointer' }}>
                  {/* Who it is, where it stands, and the feature level. */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      {renamingId === r.id ? (
                        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input value={renameDraft} autoFocus maxLength={80} placeholder="Restaurant name"
                            aria-label={`Name for ${r.id}`}
                            onChange={e => setRenameDraft(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveName(r.id); if (e.key === 'Escape') setRenamingId(null); }}
                            style={{ background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '5px 8px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 16, width: 220 }} />
                          <button type="button" onClick={() => handleSaveName(r.id)} disabled={renameSaving || !renameDraft.trim()}
                            style={{ background: t.accent, border: 'none', color: t.bgBase, padding: '6px 10px', borderRadius: 6, fontSize: 11, fontFamily: t.fontSerif, cursor: 'pointer' }}>
                            {renameSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button type="button" onClick={() => setRenamingId(null)}
                            style={{ background: 'transparent', border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, padding: '6px 10px', borderRadius: 6, fontSize: 11, fontFamily: t.fontSerif, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {/* listRestaurants falls back to the id when no name is
                              saved; say so rather than passing the slug off as a name. */}
                          <div style={{ color: r.name && r.name !== r.id ? t.textPrimary : t.warning, fontSize: 14 }}>
                            {r.name && r.name !== r.id ? r.name : 'No name set'}
                          </div>
                          <button type="button" onClick={e => { e.stopPropagation(); startRename(r); }}
                            style={{ background: 'transparent', border: 'none', color: t.accent, fontSize: 11, fontFamily: t.fontSerif, cursor: 'pointer', padding: '2px 4px', textDecoration: 'underline' }}>
                            Rename
                          </button>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 3, fontSize: 12, color: t.textSecondary }}>
                        <span>{r.id} · {(posAdapters.find(a => a.key === r.posType) || {}).name || r.posType}</span>
                        <TimezonePicker t={t} mode="card" value={r.timezone} onChange={tz => handleUpdateTimezone(r.id, tz)}
                          selectStyle={{ background: t.white06, border: `1px solid ${t.borderMid}`, color: t.textSecondary, padding: '3px 6px', borderRadius: 6, fontSize: 11, fontFamily: t.fontSerif, cursor: 'pointer' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 'none' }}>
                      {r.inactive ? (
                        <span title={r.inactiveSince ? `Inactive since ${new Date(r.inactiveSince).toLocaleDateString()}` : 'Inactive'}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: t.error, whiteSpace: 'nowrap' }}>
                          <StatusDot tone="bad" t={t} />Inactive
                        </span>
                      ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: r.isLive ? t.success : t.warning, whiteSpace: 'nowrap' }}>
                        <StatusDot tone={r.isLive ? 'ok' : 'warn'} t={t} />{r.isLive ? 'Live' : 'Setup'}
                      </span>
                      )}
                      <select
                        value={r.plan || 'premium'}
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); handleUpdatePlan(r.id, e.target.value); }}
                        style={{ background: r.plan === 'premium' ? t.accentDim : t.white06, border: `1px solid ${r.plan === 'premium' ? t.accentBorder : t.borderMid}`, color: r.plan === 'premium' ? t.accent : t.textSecondary, padding: '6px 10px', borderRadius: 8, fontSize: 12, fontFamily: t.fontSerif, cursor: 'pointer' }}>
                        <option value="premium">✦ {PLAN_LABELS.premium}</option>
                        <option value="base">◇ {PLAN_LABELS.base}</option>
                        {r.plan === 'print' && <option value="print">▤ {PLAN_LABELS.print}</option>}
                      </select>
                    </div>
                  </div>
                  {/* Three columns, each with a heading: what they are billed (read-only; change it in Stripe or the
                      owner's Account & billing screen, the dropdown above is the feature switch, a different
                      thing), what is healthy, and the switches. */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 1.45fr 1.1fr', gap: 20, borderTop: `1px solid ${t.borderSubtle}`, borderBottom: `1px solid ${t.borderSubtle}`, margin: '14px 0 12px', padding: '13px 0' }}>
                    <div style={{ minWidth: 0 }} title="From Stripe. Billing is managed in Stripe and in the owner's Account & billing screen.">
                      <div style={colLabel}>Billing</div>
                      {bl ? (
                        <>
                          <div style={{ fontSize: 12.5, color: t.textSecondary, lineHeight: 1.5 }}>{bl.main}</div>
                          {bl.status && <div style={{ fontSize: 11.5, color: blTone, marginTop: 2, lineHeight: 1.4 }}>{bl.status}</div>}
                        </>
                      ) : <div style={{ fontSize: 12.5, color: t.textDim }}>–</div>}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={colLabel}>Health</div>
                      {mx && hrow('mx', mxTone, `Pairing matrix: ${mx.label}`, `Pairing matrix: ${mx.label}${mxAge}${mx.flagged ? ` · ${mx.flagged} flagged dishes` : ''}`)}
                      {tp && hrow('tp', tpTone, `Translations: ${tp.rows.length} langs · ${tp.pct}%${tp.stuck ? ` · ${tp.stuck} stuck` : ''}`, `Translations · ${tp.pct}% · ${tp.pending} items pending${tp.stuck ? ` · ${tp.stuck} stuck` : ''}${tp.review ? ` · ${tp.review} owner texts to review` : ''} · last run ${minutesAgoLabel(tp.ageMin)}${tpStalled ? ' (stalled?)' : ''}\n${tpDetail}`)}
                      {cs && hrow('cs', csTone, csText, csTitle)}
                      {hrow('ph', phTone, `Pipeline: ${phase}`, undefined)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={colLabel}>Switches</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                        <SwitchRow t={t} on={!r.inactive} label={r.inactive ? 'Inactive' : 'Active'} onColor={t.success} offColor={t.error}
                          title={r.inactive ? 'Blocked: no guests, no staff sign-in, no AI or background work. Click to make it active again.' : 'Click to make this restaurant inactive (left, unpaid, or not a customer). Nothing is deleted.'}
                          onToggle={() => handleSetInactive(r.id, !r.inactive)} />
                        <SwitchRow t={t} on={!held} label={held ? 'Held' : 'Released'} onColor={t.success} offColor={t.warning}
                          onToggle={() => handleSetRelease(r.id, held ? 'released' : 'held')} />
                        <SwitchRow t={t} on={!!demoOn} label="Demo site" onColor={t.warning}
                          title={demoOn ? 'Guests see the DEMO banner. Click to turn it off.' : 'Click to mark this a demo — guests will see a banner explaining the data came from published menus.'}
                          onToggle={() => handleSetDemo(r.id, !demoOn)} />
                        <SwitchRow t={t} on={!!pubOn} label="Public demo" onColor={t.success}
                          title={pubOn ? 'Anonymous visitors are turn/daily capped and see the email wall. Click to turn off.' : 'Click to mark this the public demo tenant — anonymous traffic gets a per-visitor turn cap and a tenant-wide daily ceiling.'}
                          onToggle={() => handleSetPublicDemo(r.id, !pubOn)} />
                      </div>
                    </div>
                  </div>
                  {/* Actions. Outline buttons so they stand apart from the readouts and switches. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={stop}>
                    <button
                      onClick={() => openCredsEditor(r.id)}
                      title={r.posType === 'manual' ? 'Connect this restaurant to its POS' : 'Change the POS credentials for this restaurant'}
                      style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontFamily: t.fontSerif, cursor: 'pointer', whiteSpace: 'nowrap',
                        background: credsEditId === r.id ? t.accentDim : 'transparent',
                        border: `1px solid ${credsEditId === r.id ? t.accentBorder : t.borderMid}`,
                        color: credsEditId === r.id ? t.accent : t.textPrimary }}>
                      {r.posType === 'manual' ? 'Connect POS' : 'Credentials'}
                    </button>
                    <button
                      onClick={() => openProvEditor(r.id)}
                      title="Paste the Fully Kiosk provisioning QR content for this restaurant"
                      style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontFamily: t.fontSerif, cursor: 'pointer', whiteSpace: 'nowrap',
                        background: provEditId === r.id ? t.accentDim : 'transparent',
                        border: `1px solid ${provEditId === r.id ? t.accentBorder : t.borderMid}`,
                        color: provEditId === r.id ? t.accent : t.textPrimary }}>
                      Tablet QR
                    </button>
                    <a href={`${EXPORT_ENRICHMENT_URL}?restaurant=${encodeURIComponent(r.id)}`} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, color: t.textSecondary, textDecoration: 'underline', marginLeft: 6, whiteSpace: 'nowrap' }}>
                      Export
                    </a>
                  </div>
                </div>
              );
            })}
            {restaurants.length === 0 && <div style={{ color: t.textDim, fontSize: 13, textAlign: 'center', padding: 40 }}>No restaurants configured yet</div>}
          </div>

          {/* Tablet provisioning payload. Paste-text rather than image upload:
              decoding a QR in the browser needs a library this app does not
              carry, and Fully Cloud shows the payload as text anyway. */}
          {provEditId && (
            <div style={{ background: t.bgSurface, borderRadius: 12, padding: 20, border: `1px solid ${t.accentBorder}`, marginBottom: 24 }}>
              <div style={{ color: t.textPrimary, fontSize: 15, marginBottom: 6 }}>
                Tablet Provisioning QR &mdash; {(restaurants.find(r => r.id === provEditId) || {}).name || provEditId}
              </div>
              <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.6, marginBottom: 14 }}>
                Fully Cloud &rarr; Provisioning &rarr; this restaurant&rsquo;s profile &rarr; Start &amp; show
                Instructions. Download the QR image and drop it here &mdash; it is read in the
                browser, nothing is uploaded. Save an empty box to remove it; the owner screen
                then tells them to contact us.
                <br /><br />
                Re-paste after repointing a provisioning profile at a different configuration.
                Nothing detects that drift, and a stale code enrols a tablet into the wrong setup.
              </div>

              <label style={{ display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontSize: 12, fontFamily: t.fontSerif, cursor: provBusy ? 'default' : 'pointer',
                background: t.white06, border: `0.5px dashed ${t.borderMid}`, color: t.textSecondary, marginBottom: 12, opacity: provBusy ? 0.6 : 1 }}>
                {provBusy ? 'Reading\u2026' : '\u2191 Upload QR image'}
                <input type="file" accept="image/*" disabled={provBusy} style={{ display: 'none' }}
                  onChange={e => { handleProvImage(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
              <div style={{ color: t.textDim, fontSize: 10, marginBottom: 10 }}>
                or paste the payload text directly:
              </div>

              <textarea
                value={provText}
                onChange={e => { setProvText(e.target.value); setProvMsg(''); }}
                placeholder={'{"android.app.extra.PROVISIONING_DEVICE_ADMIN_COMPONENT_NAME": ...}'}
                spellCheck={false}
                style={{ width: '100%', minHeight: 120, background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '10px 12px', borderRadius: 6, fontFamily: 'monospace', fontSize: 11, lineHeight: 1.5, boxSizing: 'border-box', resize: 'vertical' }} />

              {provMsg && (
                <div style={{ color: provMsg.startsWith('\u2705') ? t.success : t.error, fontSize: 12, lineHeight: 1.5, marginTop: 10 }}>{provMsg}</div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={handleSaveProv} disabled={provBusy}
                  style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontFamily: t.fontSerif, cursor: provBusy ? 'default' : 'pointer',
                    background: t.accentDim, border: `0.5px solid ${t.accentBorder}`, color: t.accent, opacity: provBusy ? 0.6 : 1 }}>
                  {provBusy ? 'Saving\u2026' : 'Save'}
                </button>
                <button onClick={() => { setProvEditId(''); setProvText(''); setProvMsg(''); }}
                  style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontFamily: t.fontSerif, cursor: 'pointer',
                    background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textDim }}>
                  Close
                </button>
              </div>
            </div>
          )}

          {/* POS connection editor: rotates a secret, or switches POS type
              (a manual trial connecting Toast). Deliberately NOT the Add
              Restaurant form reused: that one .set()s the credentials node,
              overwrites plan/name/timezone and resets syncPipeline, which is
              right at creation and wrong for either of these. */}
          {credsEditId && (
            <div style={{ background: t.bgSurface, borderRadius: 12, padding: 20, border: `1px solid ${t.accentBorder}`, marginBottom: 24 }}>
              <div style={{ color: t.textPrimary, fontSize: 15, marginBottom: 6 }}>
                POS Connection &mdash; {(restaurants.find(r => r.id === credsEditId) || {}).name || credsEditId}
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ color: t.textSecondary, fontSize: 11, marginBottom: 4 }}>POS system</div>
                <select value={credsPosType}
                  onChange={e => { setCredsPosType(e.target.value); setCredsTest(null); setCredsMsg(''); }}
                  style={{ width: '100%', background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '8px 12px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13 }}>
                  {posAdapters.map(a => <option key={a.key} value={a.key}>{a.name}{a.key === credsStoredType ? ' (current)' : ''}</option>)}
                </select>
              </div>
              {credsSwitching ? (
                <div style={{ background: t.white06, borderRadius: 8, padding: 12, color: t.textSecondary, fontSize: 11, lineHeight: 1.6, marginBottom: 16 }}>
                  {credsPosType === 'manual' ? (
                    <>Disconnecting stops nightly syncs and live stock counts and deletes the stored credentials. The menu stays exactly as it is and becomes editable in Manage Items.</>
                  ) : (
                    <>Connecting changes only the credentials; plan, timezone and the menu stay as they are.
                    A category moves to {(posAdapters.find(a => a.key === credsPosType) || {}).name} when its menu is mapped in Settings &rarr; Menus,
                    and its next sync then replaces that category's items. Items whose ids match (a Toast Items export with the GUID column)
                    keep their research, approvals and images; items uploaded any other way arrive as new.</>
                  )}
                </div>
              ) : (
                <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.6, marginBottom: 16 }}>
                  Leave a field blank to keep what is stored. The secret is never sent back to
                  this screen, so it always renders empty. Saving clears the cached POS token &mdash;
                  without that, a rotated secret keeps working on the old token for up to a day
                  and the change looks verified when nothing was tested.
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {credsEditorFields.map(field => (
                  <div key={field.key}>
                    <div style={{ color: t.textSecondary, fontSize: 11, marginBottom: 4 }}>
                      {field.label}
                      {field.type === 'password' && !credsSwitching && <span style={{ color: t.textDim }}> &middot; blank keeps the current one</span>}
                    </div>
                    <input
                      type={field.type}
                      defaultValue=""
                      autoComplete="off"
                      ref={el => { credsRefs.current[field.key] = el; }}
                      onInput={() => setCredsTest(null)}
                      style={{ width: '100%', background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '8px 12px', borderRadius: 6, fontFamily: t.fontSerif, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                ))}

                {credsTest && (
                  <div style={{ background: t.white06, border: `0.5px solid ${credsTest.ok ? t.accentBorder : t.borderMid}`, borderRadius: 8, padding: 12, color: credsTest.ok ? t.success : t.error, fontSize: 12, lineHeight: 1.5 }}>
                    {credsTest.ok ? `\u2705 ${credsTest.detail || 'Connected'}` : `\u274c ${credsTest.error}`}
                  </div>
                )}
                {credsMsg && (
                  <div style={{ color: credsMsg.startsWith('\u2705') ? t.success : t.error, fontSize: 12, lineHeight: 1.5 }}>{credsMsg}</div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {!(credsSwitching && credsPosType === 'manual') && <button onClick={handleTestCreds} disabled={credsBusy}
                    style={{ flex: 1, background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '11px', borderRadius: 8, cursor: credsBusy ? 'default' : 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                    {credsBusy ? 'Working...' : 'Test connection'}
                  </button>}
                  {/* Save is gated on a passing test. The server re-verifies
                      anyway — this is the honest UI, not the enforcement. */}
                  {(() => {
                    const toManual = credsSwitching && credsPosType === 'manual';
                    const ready = toManual || !!credsTest?.ok;
                    const label = toManual ? 'Disconnect POS'
                      : credsSwitching ? `Connect ${(posAdapters.find(a => a.key === credsPosType) || {}).name || credsPosType}`
                      : 'Save credentials';
                    return (
                      <button onClick={handleSaveCreds} disabled={credsBusy || !ready}
                        style={{ flex: 1, background: ready ? t.accent : t.white06, border: 'none', color: ready ? t.bgBase : t.textDim, padding: '11px', borderRadius: 8, cursor: ready && !credsBusy ? 'pointer' : 'default', fontFamily: t.fontSerif, fontSize: 14 }}>
                        {label}
                      </button>
                    );
                  })()}
                  <button onClick={() => openCredsEditor('')}
                    style={{ background: t.white06, border: 'none', color: t.textSecondary, padding: '11px 20px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                    Close
                  </button>
                </div>
                {!credsTest?.ok && !(credsSwitching && credsPosType === 'manual') && (
                  <div style={{ color: t.textDim, fontSize: 11 }}>Test the connection before saving.</div>
                )}
              </div>
            </div>
          )}

        </div>
      )}

      {/* Onboarding Tab */}
      {activeTab === 'onboarding' && (
        <div style={{ maxWidth: 640, margin: '0 auto', padding: 24 }}>

          {/* Every action on this tab writes to one restaurant. Which one must be
              a deliberate choice, not a leftover from a previous screen. */}
          <div style={{ background: t.bgSurface, border: `1px solid ${selectedRestoId ? t.borderSubtle : t.accent}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 10 }}>
              {selectedRestoId ? 'Working on' : 'Choose a restaurant'}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {restaurants.map(r => (
                <button key={r.id} onClick={() => { setSelectedRestoId(r.id); setMatrixResult(null); setMatrixConfirm(false); setMatrixStatus(null); }}
                  style={{ padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontFamily: t.fontSerif,
                    background: selectedRestoId === r.id ? t.accentDim : t.white04,
                    border: `1px solid ${selectedRestoId === r.id ? t.accent : t.borderMid}`,
                    color: selectedRestoId === r.id ? t.accent : t.textSecondary }}>
                  {r.name || r.id}
                </button>
              ))}
              {restaurants.length === 0 && (
                <span style={{ color: t.textDim, fontSize: 12 }}>No restaurants loaded — hit Refresh above.</span>
              )}
            </div>
          </div>

          {!selectedRestoId ? (
            <div style={{ color: t.textDim, fontSize: 12.5, textAlign: 'center', padding: '30px 0', lineHeight: 1.6 }}>
              Select a restaurant to continue.<br />
              These tools sync menus, rebuild pairings and spend money — they act on one tenant at a time.
            </div>
          ) : (
          <>

          <SetupChecklist restaurantId={selectedRestoId} refreshKey={pipelineStatus.phase}
            onRunResearch={() => { startOnboarding(); const el = document.getElementById('onboarding-pipeline'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
            onShowMatrix={() => { const el = document.getElementById('onboarding-matrix'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }} />

          {/* ── Pairing matrix ──────────────────────────────────────────────
              Scores every approved wine against every dish once, offline, so a
              guest pairing becomes a lookup instead of a model call. Run it
              after enrichment is approved and the food menu is settled - it
              reads approved wines only, and scoring unapproved copy would go
              stale the moment a manager edited it. */}
          <div id="onboarding-matrix" style={{ background: t.bgSurface, borderRadius: 12, padding: 24, border: `1px solid ${t.borderSubtle}`, marginBottom: 20 }}>
            <div style={{ color: t.textPrimary, fontSize: 16, marginBottom: 8 }}>Pairing Matrix</div>
            <div style={{ color: t.textSecondary, fontSize: 13, marginBottom: 18, lineHeight: 1.5 }}>
              Scores every approved wine against every dish for <strong>{selectedRestoId}</strong>.
              Runs once and is served from storage, so guests get an instant pairing with no
              model call. Rerun after a menu change.
            </div>

            {/* Status is the thing a manager needs without asking: the matrix can be
                silently wrong (a dish description edited in Toast) or silently
                incomplete (a special added after the last build), and neither is
                visible from the guest app. */}
            {!matrixStatus && (
              <div style={{ marginBottom: 14 }}>
                <button onClick={loadMatrixStatus}
                  style={{ background: 'none', border: `0.5px solid ${t.borderMid}`, color: t.textSecondary,
                    cursor: 'pointer', fontSize: 12, fontFamily: t.fontSerif, padding: '7px 14px', borderRadius: 8 }}>
                  Check matrix status
                </button>
              </div>
            )}

            {matrixStatus && !matrixStatus.error && matrixStatus.building && (
              <div style={{ background: t.white04, borderRadius: 8, padding: 12, marginBottom: 14 }}>
                <div style={{ color: t.accent, fontSize: 12, marginBottom: 8 }}>
                  Building — {matrixStatus.progress.dishesScored} of {matrixStatus.progress.dishesTotal} dishes scored
                  <span style={{ color: t.textDim, marginLeft: 8 }}>
                    batch {matrixStatus.progress.batchesDone} of {matrixStatus.progress.batchesTotal} · {matrixStatus.progress.elapsedSec}s
                  </span>
                </div>
                <div style={{ height: 5, background: t.borderSubtle, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', background: t.accent, borderRadius: 3,
                    width: `${Math.round(((matrixStatus.progress.batchesDone || 0) / Math.max(1, matrixStatus.progress.batchesTotal)) * 100)}%`,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
                <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 7, lineHeight: 1.5 }}>
                  Guests keep getting pairings from the current matrix until this finishes.
                  Leaving this page will not stop it.
                </div>
              </div>
            )}

            {matrixStatus && !matrixStatus.error && !matrixStatus.building && (
              <div style={{ background: t.white04, borderRadius: 8, padding: 12, marginBottom: 14 }}>
                {!matrixStatus.exists ? (
                  <div style={{ color: t.warning, fontSize: 12 }}>No matrix has been built for this restaurant yet.</div>
                ) : (
                  <>
                    <div style={{ color: matrixStatus.needsBuild ? t.warning : t.success, fontSize: 12, marginBottom: 6 }}>
                      {matrixStatus.needsBuild ? 'Rebuild recommended' : 'Matrix is up to date'}
                      <span style={{ color: t.textDim, marginLeft: 8 }}>
                        {matrixStatus.dishCount} dishes · {matrixStatus.wineCount} wines
                      </span>
                    </div>
                    {(matrixStatus.changedDishes || []).length > 0 && (
                      <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5 }}>
                        Edited since the last build — scored against wording that no longer exists:{' '}
                        <span style={{ color: t.textSecondary }}>{matrixStatus.changedDishes.join(', ')}</span>
                      </div>
                    )}
                    {(matrixStatus.addedDishes || []).length > 0 && (
                      <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginTop: 3 }}>
                        Added since the last build — no pairings yet:{' '}
                        <span style={{ color: t.textSecondary }}>{matrixStatus.addedDishes.join(', ')}</span>
                      </div>
                    )}
                    {matrixStatus.removedDishes > 0 && (
                      <div style={{ color: t.textDim, fontSize: 11, marginTop: 3 }}>
                        {matrixStatus.removedDishes} dish(es) no longer on the menu.
                      </div>
                    )}

                    {(matrixStatus.vagueDishes || []).length > 0 && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${t.borderSubtle}` }}>
                        <div style={{ color: t.warning, fontSize: 12, marginBottom: 4 }}>
                          Hidden from guests ({matrixStatus.vagueDishes.length})
                        </div>
                        <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.55, marginBottom: 6 }}>
                          These do not say enough for a wine to be recommended confidently. They are
                          withheld from the guest menu rather than paired on a guess. Add a description
                          in Toast naming the protein and preparation, then rebuild.
                        </div>
                        {matrixStatus.vagueDishes.map(d => (
                          <div key={d.id} style={{ color: t.textSecondary, fontSize: 11.5, padding: '3px 0' }}>
                            {d.name}
                            <span style={{ color: t.textDim, marginLeft: 8 }}>
                              {d.hasDescription ? 'description too general' : 'no description in Toast'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {!matrixConfirm && (
              <button onClick={() => setMatrixConfirm(true)} disabled={matrixBusy}
                style={{ background: matrixBusy ? t.white04 : t.accent, border: 'none',
                  color: matrixBusy ? t.textDim : t.bgBase, padding: '10px 18px', borderRadius: 8,
                  cursor: matrixBusy ? 'default' : 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                {matrixBusy ? 'Building — this can take several minutes…' : 'Build pairing matrix'}
              </button>
            )}

            {matrixConfirm && (
              <div style={{ background: t.white04, borderRadius: 8, padding: 14 }}>
                <div style={{ color: t.textPrimary, fontSize: 13, marginBottom: 6 }}>
                  Rebuild the matrix for {selectedRestoId}?
                </div>
                <div style={{ color: t.textDim, fontSize: 11.5, lineHeight: 1.55, marginBottom: 12 }}>
                  This makes paid AI calls and replaces every existing pairing for this
                  restaurant. Set a usage baseline first if you want to measure what it costs.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={buildPairingMatrix}
                    style={{ background: t.accent, border: 'none', color: t.bgBase, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                    Yes, build it
                  </button>
                  <button onClick={() => setMatrixConfirm(false)}
                    style={{ background: 'none', border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {matrixResult && matrixResult.error && (
              <div style={{ marginTop: 14, padding: 12, background: t.white04, borderRadius: 8, color: t.warning, fontSize: 12, lineHeight: 1.55 }}>
                {matrixResult.error}
              </div>
            )}

            {matrixResult && !matrixResult.error && (() => {
              const c = matrixResult.cost || {};
              const flagged = Object.entries(matrixResult.flags || {});
              return (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                    {[
                      { label: 'Cost', value: `$${(c.usd || 0).toFixed(4)}` },
                      { label: 'Per dish', value: c.perDish ? `$${c.perDish.toFixed(4)}` : '—' },
                      { label: 'Calls', value: c.calls || 0 },
                      { label: 'Duration', value: `${c.durationSec ?? matrixResult.elapsedSec ?? 0}s` },
                    ].map(x => (
                      <div key={x.label} style={{ flex: 1, background: t.white04, border: `1px solid ${t.borderSubtle}`, borderRadius: 6, padding: '10px 12px' }}>
                        <div style={{ color: t.textDim, fontSize: 9.5, letterSpacing: '1.2px', textTransform: 'uppercase' }}>{x.label}</div>
                        <div style={{ color: t.accent, fontSize: 17, marginTop: 5 }}>{x.value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ color: t.textSecondary, fontSize: 12.5, marginBottom: 10 }}>
                    Scored {matrixResult.dishesScored} of {matrixResult.dishesTotal} dishes
                    against {matrixResult.wines} wines.
                  </div>

                  {/* The cache line is the one to read first. Near-zero reads across
                      several calls means the wine block is being rewritten between
                      batches and the cost figure above is inflated. */}
                  <div style={{ color: t.textDim, fontSize: 11, marginBottom: 14, lineHeight: 1.5 }}>
                    {c.cacheHitPct === null || c.cacheHitPct === undefined ? (
                      <span style={{ color: t.warning }}>
                        No cache activity — the wine block was not cached. Expected on a very
                        short list; on a long one it means the cost above is several times what
                        it should be.
                      </span>
                    ) : (
                      <>
                        Cache <span style={{ color: (c.calls > 1 && c.cacheHitPct < 40) ? t.warning : t.success }}>{c.cacheHitPct}%</span>
                        {' '}· {(c.cacheReadTokens || 0).toLocaleString()} read, {(c.cacheWriteTokens || 0).toLocaleString()} written
                        {' '}· {(c.inputTokens || 0).toLocaleString()} in, {(c.outputTokens || 0).toLocaleString()} out
                      </>
                    )}
                  </div>

                  {(matrixResult.problems || []).length > 0 && (
                    <div style={{ padding: 12, background: t.white04, borderRadius: 8, marginBottom: 14 }}>
                      <div style={{ color: t.warning, fontSize: 12, marginBottom: 6 }}>
                        {matrixResult.problems.length} problem{matrixResult.problems.length === 1 ? '' : 's'} during the build
                      </div>
                      {matrixResult.problems.map((p, i) => (
                        <div key={i} style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5 }}>{p}</div>
                      ))}
                    </div>
                  )}

                  {/* Flagged dishes: where the scoring says it is unsure. These are
                      what a manager should read - not 11,000 cells. */}
                  <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 8 }}>
                    Dishes worth reviewing ({flagged.length})
                  </div>
                  {flagged.length === 0 ? (
                    <div style={{ color: t.textDim, fontSize: 11.5, lineHeight: 1.5 }}>
                      Nothing flagged. Every dish found a confident top three with a clear
                      ranking — worth spot-checking a few anyway on a first run.
                    </div>
                  ) : (
                    flagged.map(([dishId, info]) => (
                      <div key={dishId} style={{ padding: '8px 0', borderBottom: `1px solid ${t.borderSubtle}` }}>
                        <div style={{ color: t.textPrimary, fontSize: 12.5 }}>{info.name || dishId}</div>
                        <div style={{ color: t.warning, fontSize: 11, marginTop: 2, lineHeight: 1.5 }}>
                          {(info.flags || []).join(' · ')}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              );
            })()}

            {/* Incremental scoring test. Never touches the live matrix. */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${t.borderSubtle}` }}>
              <div style={{ color: t.textPrimary, fontSize: 14, marginBottom: 6 }}>Test incremental scoring</div>
              <div style={{ color: t.textDim, fontSize: 11.5, lineHeight: 1.55, marginBottom: 12 }}>
                Scores wines added since the last build, plus a sample of five already-scored wines,
                without changing what guests see. To check a new wine: run the test, build the matrix,
                then compare.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => runIncrementalTest(false)} disabled={incrBusy || matrixBusy}
                  style={{ background: 'none', border: `0.5px solid ${t.accent}`, color: incrBusy ? t.textDim : t.accent, padding: '8px 16px', borderRadius: 8, cursor: incrBusy ? 'default' : 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                  {incrBusy ? 'Working…' : 'Run test'}
                </button>
                <button onClick={() => runIncrementalTest(true)} disabled={incrBusy || matrixBusy}
                  style={{ background: 'none', border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, padding: '8px 16px', borderRadius: 8, cursor: incrBusy ? 'default' : 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                  Compare last test with current matrix
                </button>
              </div>

              {incrResult && incrResult.error && (
                <div style={{ marginTop: 12, color: t.warning, fontSize: 12, lineHeight: 1.55 }}>{incrResult.error}</div>
              )}

              {incrResult && !incrResult.error && (() => {
                const o = incrResult.overall || {};
                const newIds = new Set((incrResult.targets || []).filter(x => x.isNew).map(x => x.wineId));
                return (
                  <div style={{ marginTop: 14, fontSize: 12, color: t.textSecondary, lineHeight: 1.55 }}>
                    {incrResult.mode === 'compare' && !incrResult.matrixRebuiltSinceTest && (
                      <div style={{ color: t.warning, marginBottom: 8 }}>
                        The matrix has not been rebuilt since this test, so new wines have nothing to compare against yet.
                      </div>
                    )}
                    <div style={{ marginBottom: 8 }}>
                      {o.compared
                        ? <>Overall: {o.pctWithinBand}% of {o.compared} scores within 5 points · average gap {o.meanAbs} · largest {o.maxAbs} · bias {o.meanSigned > 0 ? '+' : ''}{o.meanSigned}</>
                        : <>No scores to compare yet.</>}
                    </div>
                    {(incrResult.perWine || []).map(w => (
                      <div key={w.wineId} style={{ padding: '6px 0', borderBottom: `1px solid ${t.borderSubtle}` }}>
                        <div style={{ color: t.textPrimary }}>{w.name}{newIds.has(w.wineId) ? ' (new)' : ''}</div>
                        <div style={{ color: t.textDim, fontSize: 11 }}>
                          {w.compared
                            ? `${w.pctWithinBand}% within 5 · average gap ${w.meanAbs} · largest ${w.maxAbs} · bias ${w.meanSigned > 0 ? '+' : ''}${w.meanSigned}`
                            : 'Not in the current matrix — build the matrix, then compare.'}
                        </div>
                      </div>
                    ))}
                    {(incrResult.problems || []).map((p, i) => (
                      <div key={i} style={{ color: t.warning, fontSize: 11, marginTop: 4 }}>{p}</div>
                    ))}
                    {incrResult.cost && (
                      <div style={{ color: t.textDim, fontSize: 11, marginTop: 8 }}>
                        Cost ${incrResult.cost.usd} over {incrResult.cost.calls} call{incrResult.cost.calls === 1 ? '' : 's'}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          <div style={{ background: t.bgSurface, borderRadius: 12, padding: 24, border: `1px solid ${t.borderSubtle}`, marginBottom: 20 }}>
            <div style={{ color: t.textPrimary, fontSize: 16, marginBottom: 8 }}>Inspect a Toast item</div>
            <div style={{ color: t.textSecondary, fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
              Shows the raw payload Toast sends for a menu item, and where it sits in the
              menu tree. Useful when an item is not appearing in the app.
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input value={inspectName} onChange={e => setInspectName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') inspectToastItem(); }}
                placeholder="part of the item name, e.g. potato leek"
                style={{ flex: 1, background: t.white04, border: `1px solid ${t.borderMid}`, color: t.textPrimary, padding: '9px 12px', borderRadius: 8, fontSize: 13, fontFamily: t.fontSerif }} />
              <button onClick={inspectToastItem}
                style={{ background: t.white04, border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                Inspect
              </button>
            </div>
            {inspectResult && (
              <div style={{ background: t.white04, borderRadius: 8, padding: 12, maxHeight: 380, overflow: 'auto' }}>
                {inspectResult.loading ? (
                  <div style={{ color: t.textDim, fontSize: 12 }}>Fetching menus from Toast…</div>
                ) : inspectResult.error ? (
                  <div style={{ color: t.warning, fontSize: 12 }}>{inspectResult.error}</div>
                ) : (
                  <>
                    <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 8 }}>
                      {inspectResult.found} match{inspectResult.found === 1 ? '' : 'es'}
                    </div>
                    <pre style={{ color: t.textDim, fontSize: 10.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                      {JSON.stringify(inspectResult.hits, null, 2)}
                    </pre>
                  </>
                )}
              </div>
            )}
          </div>

          <div id="onboarding-pipeline" style={{ background: t.bgSurface, borderRadius: 12, padding: 24, border: `1px solid ${t.borderSubtle}`, marginBottom: 20 }}>
            <div style={{ color: t.textPrimary, fontSize: 16, marginBottom: 8 }}>{selectedIsManual ? 'Research' : 'Sync and research'}</div>
            <div style={{ color: t.textSecondary, fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
              {selectedIsManual ? (
                <>Researches the uploaded wine, beer and spirits in batches you can pause, then queues label image searches. Upload the menu first.</>
              ) : (
                <><strong>Phase 1</strong>: pull the mapped menus from {selectedPosName}<br/>
                <strong>Phase 2</strong>: research wine, beer and spirits<br/>
                <strong>Phase 3</strong>: queue label image searches<br/>
                Categories with no mapped menu are file-managed and are researched without a sync.</>
              )}
            </div>

            {/* Pipeline status display */}
            {pipelineStatus.phase && pipelineStatus.phase !== 'idle' && (
              <div style={{ background: t.white04, borderRadius: 8, padding: 16, marginBottom: 16 }}>
                {[
                  { key: 'toast', label: selectedIsManual ? 'Checking the uploaded menu' : `Phase 1: Sync from ${selectedPosName}` },
                  { key: 'enriching', label: selectedIsManual ? 'Researching' : 'Phase 2: Research' },
                  { key: 'images', label: selectedIsManual ? 'Queueing label images' : 'Phase 3: Image queue' },
                ].map(ph => {
                  const phases = ['starting', 'toast', 'pendingLargeListApproval', 'enriching', 'images', 'complete'];
                  const currentIdx2 = phases.indexOf(pipelineStatus.phase);
                  const phIdx = phases.indexOf(ph.key);
                  const isDone = currentIdx2 > phIdx || pipelineStatus.phase === 'complete';
                  const isActive = pipelineStatus.phase === ph.key;
                  let detail = '';
                  if (isActive && ph.key === 'enriching') {
                    const cat = pipelineStatus.enrichmentCategory || '';
                    const done = masterDbStats?.[cat === 'wine' ? 'wines' : cat === 'beer' ? 'beers' : 'spirits']?.total || 0;
                    const total = pipelineStatus.itemCounts?.[cat] || '?';
                    detail = cat ? ` — ${cat} (${done} of ${total})` : '';
                  } else if (isActive && ph.key === 'toast') {
                    detail = pipelineStatus.currentCategory ? ` — ${pipelineStatus.currentCategory}` : '';
                  } else if (isActive && ph.key === 'images') {
                    // Show image progress
                    if (masterDbStats) {
                      const totalEnriched = (masterDbStats.wines?.total || 0) + (masterDbStats.beers?.total || 0) + (masterDbStats.spirits?.total || 0);
                      const totalImages = (masterDbStats.wines?.withImages || 0) + (masterDbStats.beers?.withImages || 0) + (masterDbStats.spirits?.withImages || 0);
                      detail = totalEnriched > 0 ? ` — ${totalImages} of ${totalEnriched} images found` : '';
                    }
                  }
                  return (
                    <div key={ph.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', background: isDone ? t.successDim : isActive ? t.accentDim : t.white04, border: `1.5px solid ${isDone ? t.success : isActive ? t.accent : t.borderSubtle}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                        {isDone ? '✓' : isActive ? '●' : ''}
                      </div>
                      <div style={{ color: isDone ? t.success : isActive ? t.accent : t.textDim, fontSize: 13 }}>
                        {ph.label}{detail}{isActive ? ' ...' : ''}
                      </div>
                    </div>
                  );
                })}
                {masterDbStats && ['enriching', 'images', 'complete'].includes(pipelineStatus.phase) && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: `0.5px solid ${t.borderSubtle}` }}>
                    <div style={{ color: t.textDim, fontSize: 10, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 6 }}>
                      This restaurant <span style={{ textTransform: 'none', letterSpacing: 0 }}>· master DB is shared across all tenants</span>
                    </div>
                    {[
                      { label: 'Wines', key: 'wines', cat: 'wine' },
                      { label: 'Beers', key: 'beers', cat: 'beer' },
                      { label: 'Spirits', key: 'spirits', cat: 'pours' },
                    ].map(({ label, key, cat }) => {
                      const s = masterDbStats[key] || { total: 0, withImages: 0, notFound: 0 };
                      // Per-restaurant progress, not the master total. The master DB
                      // is platform-wide and deduplicated, so comparing it against
                      // one restaurant's item count compares two different things -
                      // which is how beers read "26 of 33 enriched".
                      const mine = (masterDbStats.restaurant || {})[key] || null;
                      const isCurrentCat = pipelineStatus.enrichmentCategory === cat;
                      return (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, gap: 10 }}>
                          <div style={{ color: isCurrentCat ? t.accent : t.textSecondary, fontSize: 12, flexShrink: 0 }}>
                            {label}{isCurrentCat && <span style={{ marginLeft: 6, display: 'inline-flex', verticalAlign: 'middle' }}><LineIcon name="refresh" size={13} /></span>}
                          </div>
                          <div style={{ color: t.textSecondary, fontSize: 12, textAlign: 'right' }}>
                            {mine ? `${mine.enriched} / ${mine.items} enriched` : '—'}
                            <span style={{ color: t.textDim }}>
                              {' · '}{s.total} in master
                              {s.withImages > 0 ? `, ${s.withImages} with images` : ''}
                              {s.notFound > 0 ? ` (${s.notFound} no image)` : ''}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {pipelineStatus.phase === 'complete' && <div style={{ color: t.success, fontSize: 13, marginTop: 8 }}>✅ Pipeline complete — all enrichment and images done</div>}
                {pipelineStatus.phase === 'error' && <div style={{ color: t.error, fontSize: 13, marginTop: 8 }}>❌ Error: {pipelineStatus.error}</div>}
              </div>
            )}

            {onboardingMsg && <div style={{ color: t.accent, fontSize: 13, marginBottom: 12 }}>{onboardingMsg}</div>}

            {/* Large list approval gate */}
            {pipelineStatus.phase === 'pendingLargeListApproval' && (
              <div style={{ background: t.accentDimSm, border: `1px solid ${t.accentBorder}`, borderRadius: 10, padding: 18, marginBottom: 14 }}>
                <div style={{ color: t.accent, fontSize: 14, fontWeight: 600, marginBottom: 8 }}><LineIcon name="alert" inline />Large List Detected</div>
                <div style={{ color: t.textSecondary, fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
                  Phase 1 complete. This restaurant has <strong style={{ color: t.textPrimary }}>{pipelineStatus.largeListTotal} total beverages</strong> (wine + beer + spirits), which exceeds the {LARGE_LIST_THRESHOLD}-item threshold.
                  {pipelineStatus.largeListEstimatedCost && (
                    <> Estimated enrichment cost: <strong style={{ color: t.textPrimary }}>${pipelineStatus.largeListEstimatedCost}</strong>.</>
                  )}
                  <br/>Review the item counts below before approving Phase 2.
                </div>
                {pipelineStatus.itemCounts && (
                  <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                    {[['Wine', 'wine'], ['Beer', 'beer'], ['Spirits', 'pours']].map(([label, key]) => (
                      <div key={key} style={{ flex: 1, background: t.white04, borderRadius: 6, padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ color: t.textPrimary, fontSize: 20, fontWeight: 600 }}>{pipelineStatus.itemCounts[key] || 0}</div>
                        <div style={{ color: t.textDim, fontSize: 11, marginTop: 2 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={approveLargeList}
                    style={{ flex: 2, background: t.accent, border: 'none', color: t.bgBase, padding: '12px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 14, fontWeight: 600 }}>
                    <LineIcon name="checkCircle" inline />Approve & Continue to Phase 2
                  </button>
                  <button onClick={restartOnboarding}
                    style={{ flex: 1, background: t.white06, border: `1px solid ${t.borderMid}`, color: t.textSecondary, padding: '12px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                    ↺ Restart
                  </button>
                </div>
              </div>
            )}

            {/* Dynamic action buttons based on pipeline phase */}
            {(!pipelineStatus.phase || ['idle', 'complete', 'error', 'awaitingMenu'].includes(pipelineStatus.phase)) && (
              <button onClick={startOnboarding}
                style={{ width: '100%', background: t.accent, border: 'none', color: t.bgBase, padding: '14px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 15, fontWeight: 600 }}>
                {selectedIsManual ? 'Run research' : 'Start sync and research'}: {selectedResto.name || selectedRestoId}
              </button>
            )}

            {['starting', 'toast', 'enriching', 'images'].includes(pipelineStatus.phase) && (
              <button onClick={pauseOnboarding}
                style={{ width: '100%', background: t.errorDim, border: `1px solid ${t.errorBorder}`, color: t.error, padding: '14px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 15, fontWeight: 600 }}>
                <LineIcon name="pause" inline />Pause Onboarding
              </button>
            )}

            {pipelineStatus.phase === 'paused' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={resumeOnboarding}
                  style={{ flex: 2, background: t.successDim, border: `1px solid ${t.success}`, color: t.success, padding: '14px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 14, fontWeight: 600 }}>
                  <LineIcon name="play" inline />Resume from {pipelineStatus.enrichmentCategory || 'saved position'}
                </button>
                <button onClick={restartOnboarding}
                  style={{ flex: 1, background: t.white06, border: `1px solid ${t.borderMid}`, color: t.textSecondary, padding: '14px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 14 }}>
                  ↺ Restart
                </button>
              </div>
            )}

            {['complete', 'error'].includes(pipelineStatus.phase) && (
              <button onClick={restartOnboarding}
                style={{ width: '100%', background: t.white06, border: `1px solid ${t.borderMid}`, color: t.textSecondary, padding: '10px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13, marginTop: 8 }}>
                ↺ Run Again
              </button>
            )}

            <button onClick={pollPipelineStatus} style={{ width: '100%', background: 'transparent', border: `1px solid ${t.borderMid}`, color: t.textSecondary, padding: '10px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12, marginTop: 8 }}>
              ↺ Check Status
            </button>
          </div>

          </>
          )}
        </div>
      )}

      {/* Zoom modal */}
      {zoomedImage && (
        <div onClick={() => setZoomedImage(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 2000, background: t.bgOverlay, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, cursor: 'zoom-out' }}>
          <img src={zoomedImage.imageUrl} alt={zoomedImage.name}
            style={{ maxHeight: '85vh', maxWidth: '90vw', objectFit: 'contain', borderRadius: 8, boxShadow: `0 8px 48px ${t.shadowStrong}` }}
            onClick={e => e.stopPropagation()} />
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <div style={{ color: SEMANTIC.lightboxText, fontSize: 18, fontFamily: t.fontSerif }}>{zoomedImage.name}</div>
            <div style={{ color: SEMANTIC.lightboxTextDim, fontSize: 12, marginTop: 4 }}>Tap anywhere to close</div>
          </div>
        </div>
      )}

      {/* Image Queue Tab */}
      {activeTab === 'master' && <MasterDbTab t={t} restaurants={restaurants} />}

      {activeTab === 'corrections' && (
        <div style={{ padding: 24 }}>
          <div style={{ color: t.textSecondary, fontSize: 12, lineHeight: 1.6, marginBottom: 16, maxWidth: 620 }}>
            A manager corrected one of these items after it came from the shared database.
            Their own menu already shows the change &mdash; this only decides whether the master
            record follows, and whether every future restaurant inherits it.
          </div>

          {corrections.length === 0 && (
            <div style={{ color: t.textDim, fontSize: 13, fontStyle: 'italic' }}>Nothing awaiting review.</div>
          )}

          {corrections.map(c => (
            <div key={c.id} style={{ background: t.white04, border: `0.5px solid ${t.borderMid}`, borderRadius: 8, padding: '14px 16px', marginBottom: 10, maxWidth: 620 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: t.textPrimary, fontSize: 14, fontFamily: t.fontSerif }}>{c.itemName}</div>
                  <div style={{ color: t.textDim, fontSize: 10, marginTop: 2 }}>
                    {c.restaurantId} · {c.masterType} · {c.masterKey}
                  </div>
                </div>
                <div style={{ color: t.textDim, fontSize: 10, whiteSpace: 'nowrap' }}>
                  {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''}
                </div>
              </div>

              <div style={{ marginTop: 10, marginBottom: 12 }}>
                {Object.entries(c.changed || {}).map(([field, d]) => (
                  <div key={field} style={{ fontSize: 12, color: t.textSecondary, lineHeight: 1.7 }}>
                    <span style={{ color: t.textDim }}>{field}</span>{'  '}
                    <span style={{ textDecoration: 'line-through', color: t.textDim }}>{String(d.from ?? '—')}</span>
                    {'  →  '}
                    <strong style={{ color: t.textPrimary }}>{String(d.to ?? '—')}</strong>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => resolveCorrection(c.id, true)} disabled={resolvingId === c.id}
                  title="The master record follows this change, and every restaurant that gets this item later inherits it."
                  style={{ background: t.successDim, border: `0.5px solid ${t.successBorder}`, color: t.success, padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12 }}>
                  ✓ Apply to master
                </button>
                <button onClick={() => resolveCorrection(c.id, false)} disabled={resolvingId === c.id}
                  title={`Only ${correctionRestaurantName(c)}'s own menu keeps this change. The master record stays as it is.`}
                  style={{ background: 'transparent', border: `0.5px solid ${t.borderMid}`, color: t.textDim, padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12 }}>
                  Keep for {correctionRestaurantName(c)} only
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'images' && (
        <div style={{ maxWidth: 640, margin: '0 auto', padding: 24 }}>

          {/* ── Guest image reports ──────────────────────────────────────────
              From the "Report incorrect image" button under every enlarged
              label. Also in the daily digest email. Only a shared (master)
              image can be sent back to the queue; a restaurant's own photo or
              a POS image is theirs to change, so those can only be dismissed. */}
          {imageReports.length > 0 && (
            <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div style={{ color: t.textPrimary, fontSize: 14 }}>Guest image reports ({imageReports.length})</div>
              <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.5, marginTop: 4, marginBottom: 10 }}>
                Guests said these labels look wrong. Send a shared image back to the queue to hide it from guests and pick a better one.
              </div>
              {reportMsg && <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 10 }}>{reportMsg}</div>}
              {imageReports.map(r => (
                <div key={r.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0', borderTop: `0.5px solid ${t.borderSubtle}` }}>
                  {r.imageUrl && (
                    <img src={r.imageUrl} alt={r.itemName || ''} onClick={() => setZoomedImage({ imageUrl: r.imageUrl, name: r.itemName || r.masterName || '' })}
                      style={{ width: 44, height: 64, objectFit: 'contain', borderRadius: 3, border: `0.5px solid ${t.borderMid}`, cursor: 'zoom-in', flex: 'none' }} />
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ color: t.textPrimary, fontSize: 13, fontFamily: t.fontSerif }}>{r.itemName || r.masterName || 'Unknown item'}</div>
                    <div style={{ color: t.textDim, fontSize: 10, marginTop: 2, lineHeight: 1.6 }}>
                      {r.restaurantName || r.restaurantId} · reported {r.count}× · last {r.lastReportedAt ? new Date(r.lastReportedAt).toLocaleString() : ''}
                      <br />
                      {r.source === 'master' ? `Shared image${r.approvedBy === 'auto' ? ' (auto-approved)' : ''} · ${r.type}/${r.masterKey}`
                        : r.source === 'restaurant' ? 'The restaurant’s own photo — only they can change it'
                        : 'POS image — only the restaurant can change it'}
                      {r.itemId ? ` · item ${r.itemId}` : ''}
                      {r.stale ? ' · already changed since the guest saw it' : ''}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      {r.source === 'master' && !r.stale && (
                        <button onClick={() => resolveImageReport(r, 'requeue')} disabled={reportBusyId === r.id}
                          title="Hides this image from guests at every restaurant that carries it and puts it back in the Image Queue."
                          style={{ background: t.white06, border: `0.5px solid ${t.borderMid}`, color: t.textPrimary, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12 }}>
                          Send back to queue
                        </button>
                      )}
                      <button onClick={() => resolveImageReport(r, 'dismiss')} disabled={reportBusyId === r.id}
                        style={{ background: 'transparent', border: `0.5px solid ${t.borderMid}`, color: t.textDim, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 12 }}>
                        {r.source === 'master' && !r.stale ? 'Image is right' : 'Dismiss'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Label coverage ───────────────────────────────────────────────
              Master-database view, not per restaurant. The review queue below
              only shows records that have an image awaiting a verdict; anything
              marked NOT_FOUND or never fetched is invisible there. This is where
              those go, so a wine skipped by a stray click is findable again. */}
          <div style={{ background: t.bgSurface, border: `1px solid ${t.borderSubtle}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: t.textPrimary, fontSize: 14 }}>Label coverage</div>
                <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 3 }}>
                  Master database · every record without an approved image
                </div>
              </div>
              <button onClick={() => { setCoverageOpen(o => !o); if (!coverage) loadCoverage(); }}
                style={{ padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: t.fontSerif,
                  background: t.white04, border: `0.5px solid ${t.borderMid}`, color: t.textSecondary }}>
                {coverageOpen ? 'Hide' : 'Show'}
              </button>
            </div>

            {coverageOpen && (
              <div style={{ marginTop: 14 }}>
                {coverageBusy && <div style={{ color: t.textDim, fontSize: 12 }}>Loading…</div>}
                {coverage && coverage.error && (
                  <div style={{ color: t.warning, fontSize: 12 }}>{coverage.error}</div>
                )}
                {coverage && !coverage.error && coverage.summary && (
                  <>
                    {Object.entries(coverage.summary).map(([type, sm]) => (
                      <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${t.borderSubtle}`, fontSize: 12 }}>
                        <span style={{ color: t.textPrimary }}>{type}</span>
                        <span style={{ color: t.textDim }}>
                          <span style={{ color: t.success }}>{sm.approved} approved</span>
                          {sm.pendingReview > 0 && <> · {sm.pendingReview} to review</>}
                          {sm.notFound > 0 && <> · <span style={{ color: t.warning }}>{sm.notFound} no image</span></>}
                          {sm.awaitingFetch > 0 && <> · {sm.awaitingFetch} awaiting fetch</>}
                          {' '}· {sm.total} total
                        </span>
                      </div>
                    ))}

                    {(coverage.notFound || []).length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <div style={{ color: t.textSecondary, fontSize: 12 }}>
                            Marked &ldquo;no image&rdquo; ({coverage.notFound.length})
                          </div>
                          <button onClick={() => retryLabels(coverage.notFound)} disabled={coverageBusy}
                            style={{ padding: '5px 11px', borderRadius: 6, cursor: coverageBusy ? 'default' : 'pointer', fontSize: 11.5, fontFamily: t.fontSerif,
                              background: t.accentDim, border: `0.5px solid ${t.accent}`, color: t.accent, opacity: coverageBusy ? 0.5 : 1 }}>
                            Retry all
                          </button>
                        </div>
                        <div style={{ color: t.textDim, fontSize: 10.5, lineHeight: 1.55, marginBottom: 10 }}>
                          Retrying clears the marker and puts the record back in the fetch queue.
                          It does not search immediately &mdash; the scheduler picks it up, and it
                          will find nothing while the monthly image-search quota is spent.
                        </div>
                        {coverage.notFound.map(x => (
                          <div key={`${x.type}/${x.canonicalKey}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: `1px solid ${t.borderSubtle}` }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: t.textPrimary, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.canonicalName}</div>
                              <div style={{ color: t.textDim, fontSize: 10.5, marginTop: 2 }}>
                                {x.type} · on {x.restaurantCount} menu{x.restaurantCount === 1 ? '' : 's'}
                                {x.restaurants && x.restaurants.length > 0 && ` (${x.restaurants.join(', ')})`}
                              </div>
                            </div>
                            <button onClick={() => retryLabels([x])} disabled={coverageBusy}
                              style={{ flexShrink: 0, padding: '5px 11px', borderRadius: 6, cursor: coverageBusy ? 'default' : 'pointer', fontSize: 11.5, fontFamily: t.fontSerif,
                                background: t.white04, border: `0.5px solid ${t.borderMid}`, color: t.textSecondary, opacity: coverageBusy ? 0.5 : 1 }}>
                              Retry
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {(coverage.awaitingFetch || []).length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ color: t.textSecondary, fontSize: 12, marginBottom: 6 }}>
                          Awaiting fetch ({coverage.awaitingFetch.length})
                        </div>
                        <div style={{ color: t.textDim, fontSize: 10.5, lineHeight: 1.5 }}>
                          Already queued &mdash; no action needed. These are picked up automatically
                          when the fetch scheduler next runs.
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', color: t.textDim, padding: 60 }}>Loading image queue...</div>
          ) : imageQueue.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', margin: '0 auto 14px', background: t.successDim, border: `1px solid ${t.success}`, color: t.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LineIcon name="check" size={24} strokeWidth={2} />
              </div>
              <div style={{ color: t.success, fontSize: 16 }}>All images approved</div>
              <div style={{ color: t.textDim, fontSize: 12, marginTop: 8 }}>Nothing in the queue right now</div>
            </div>
          ) : (
            <div>
              {/* Progress */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ color: t.textSecondary, fontSize: 12 }}>{currentIdx + 1} of {imageQueue.length} images</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} disabled={currentIdx === 0}
                    style={{ background: t.white06, border: 'none', color: t.textPrimary, padding: '4px 12px', borderRadius: 6, cursor: currentIdx === 0 ? 'default' : 'pointer', opacity: currentIdx === 0 ? 0.4 : 1 }}>←</button>
                  <button onClick={() => setCurrentIdx(i => Math.min(imageQueue.length - 1, i + 1))} disabled={currentIdx === imageQueue.length - 1}
                    style={{ background: t.white06, border: 'none', color: t.textPrimary, padding: '4px 12px', borderRadius: 6, cursor: currentIdx === imageQueue.length - 1 ? 'default' : 'pointer', opacity: currentIdx === imageQueue.length - 1 ? 0.4 : 1 }}>→</button>
                </div>
              </div>

              {item && (
                <div style={{ background: t.bgSurface, borderRadius: 12, overflow: 'hidden', border: `1px solid ${t.borderSubtle}` }}>
                  {/* Image */}
                  <div style={{ background: t.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 480, cursor: item.labelImageUrl && item.labelImageUrl !== 'NOT_FOUND' ? 'zoom-in' : 'default' }}
                    onClick={() => item.labelImageUrl && item.labelImageUrl !== 'NOT_FOUND' && setZoomedImage({ name: item.displayName || item.canonicalName, imageUrl: item.labelImageUrl })}>
                    {item.labelImageUrl && item.labelImageUrl !== 'NOT_FOUND' ? (
                      <img src={item.labelImageUrl} alt={item.displayName || item.canonicalName} style={{ maxHeight: 540, maxWidth: '100%', objectFit: 'contain' }} />
                    ) : (
                      <div style={{ color: t.textDim, fontSize: 14 }}>No image available</div>
                    )}
                  </div>
                  {item.labelImageUrl && item.labelImageUrl !== 'NOT_FOUND' && (
                    <div style={{ textAlign: 'center', color: t.textDim, fontSize: 10, padding: '4px 0', background: t.bgDeep }}>Tap image to zoom</div>
                  )}
                  {/* Info */}
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                      <div style={{ color: t.textPrimary, fontSize: 17, fontWeight: 600 }}>{item.displayName || item.canonicalName}</div>
                      <div style={{ color: t.textDim, fontSize: 11, background: t.white06, padding: '2px 8px', borderRadius: 10 }}>{typeLabel}</div>
                    </div>
                    {/* Shown only when it differs from displayName above - the two
                        can genuinely diverge (see displayName's comment on the
                        backend), and surfacing the mismatch here is the whole
                        point: a reviewer can see "Nolet's Distillery Vodka" is
                        just this item's internal match-key, not a sign the wrong
                        product's image is showing. */}
                    {item.displayName && item.displayName !== item.canonicalName && (
                      <div style={{ color: t.textDim, fontSize: 11, marginBottom: 2 }}>Master DB key name: {item.canonicalName}</div>
                    )}
                    <div style={{ color: t.textDim, fontSize: 11, marginBottom: 16 }}>
                      {item.varietal && <span>{item.varietal}{item.region ? ` · ${item.region}` : ''}</span>}
                    </div>
                    {/* Action message */}
                    {actionMsg && <div style={{ color: t.accent, fontSize: 13, marginBottom: 12, textAlign: 'center' }}>{actionMsg}</div>}
                    {/* Candidate counter */}
                    <div style={{ color: t.textDim, fontSize: 11, marginBottom: 8, textAlign: 'center' }}>
                      Candidate {(item.candidateIndex || 0) + 1} of {item.candidateCount || 1} · Next cycles back to 1
                      <div style={{ marginTop: 2 }}>Key: {item.canonicalKey}</div>
                    </div>
                    {/* Actions. Line icons, not emoji (they follow the theme and draw the same everywhere).
                        Approve is the one solid button: it is what gets pressed most, and its text is the theme's
                        surface colour where that reads (4.5:1), otherwise the theme's deep colour or black/white. */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      <button onClick={handlePrev} disabled={(item.candidateIndex || 0) === 0}
                        style={{ ...ICON_BTN, flex: 1, background: t.white06, border: `1px solid ${t.borderMid}`, color: (item.candidateIndex || 0) === 0 ? t.textDim : t.textSecondary, padding: '12px', borderRadius: 8, cursor: (item.candidateIndex || 0) === 0 ? 'default' : 'pointer', fontFamily: t.fontSerif, fontSize: 14, opacity: (item.candidateIndex || 0) === 0 ? 0.4 : 1 }}>
                        <LineIcon name="left" />Prev
                      </button>
                      <button onClick={handleApprove}
                        style={{ ...ICON_BTN, flex: 2, background: t.success, border: `1px solid ${t.success}`, color: readableOn(t.success, [t.bgSurface, t.bgDeep]), padding: '12px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 14, fontWeight: 600 }}>
                        <LineIcon name="check" />Approve
                      </button>
                      <button onClick={handleReject}
                        style={{ ...ICON_BTN, flex: 1, background: t.errorDim, border: `1px solid ${t.errorBorder}`, color: t.error, padding: '12px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 14 }}>
                        Next<LineIcon name="right" />
                      </button>
                    </div>
                    {/* The three routine image actions share one row (it used to be three rows), so the card is
                        shorter to scroll through a long queue. */}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={handleFindMore}
                        style={{ ...ICON_BTN, flex: 1, background: t.infoDim, border: `1px solid ${t.infoBorder}`, color: t.info, padding: '10px 6px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                        <LineIcon name="search" />Find More Images
                      </button>
                      <button onClick={() => fileInputRef.current?.click()}
                        style={{ ...ICON_BTN, flex: 1, background: t.accentDimSm, border: `1px solid ${t.accentBorder}`, color: t.accent, padding: '10px 6px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                        <LineIcon name="upload" />Upload Own
                      </button>
                      <button onClick={handleNoImageFound} title="Clears the image search result only - content approval is untouched, so this is safe to use freely"
                        style={{ ...ICON_BTN, flex: 1, background: t.white06, border: `1px solid ${t.borderMid}`, color: t.textSecondary, padding: '10px 6px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                        <LineIcon name="noimg" />No Image Found
                      </button>
                    </div>
                    {/* Deliberately its own row, set apart below a rule, away from the routine actions above -
                        this one resets content approval at every restaurant carrying the
                        item, not just its image, so it should not sit adjacent to buttons
                        used on every single item. See handleDisqualify's confirm() dialog. */}
                    <div style={{ borderTop: `1px solid ${t.borderSubtle}`, marginTop: 12, paddingTop: 12 }}>
                      <button onClick={handleDisqualify}
                        title="Use this when the item itself looks wrong (for example the picture is a different wine), not just a poor picture. It un-approves the item at every restaurant that carries it, and each one goes back to its manager for review."
                        style={{ ...ICON_BTN, width: '100%', background: 'transparent', border: `1px solid ${t.warningBorder}`, color: t.warning, padding: '9px', borderRadius: 8, cursor: 'pointer', fontFamily: t.fontSerif, fontSize: 13 }}>
                        <LineIcon name="flag" />Flag for Review
                      </button>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The admin portal is cross-tenant, so it is confined to the platform host.
// A restaurant subdomain is a shared-device surface; the portal is not.
const ADMIN_PORTAL_HOSTS = [
  'sommsync-platform.web.app',
  'sommsync-platform.firebaseapp.com',
  'admin.getsommsync.com',
  'localhost',
  '127.0.0.1',
];

function AdminPortalBlocked() {
  const t = ADMIN_THEME;
  return (
    <div style={{ minHeight: '100vh', background: t.bgBase, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', fontFamily: t.fontSerif, padding: 24, textAlign: 'center' }}>
      <div style={{ color: t.accent, fontSize: 11, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: 10 }}>SommSync</div>
      <div style={{ color: t.textPrimary, fontSize: 17, marginBottom: 10 }}>Not available here</div>
      <div style={{ color: t.textDim, fontSize: 13, lineHeight: 1.6, maxWidth: 340 }}>
        The admin portal can only be opened from the platform address, not from a restaurant’s own site.
      </div>
    </div>
  );
}

// ─── Public demo: device frame on desktop ─────────────────────────────────────
// A prospect opening a demo on a laptop gets a SyncBev bar across the top -
// logo, a demo tag, a Full screen / Tablet / Phone switch and "Start Free
// Trial" (public demo) -
// that stays there in every view. Under it the app runs:
//   - Full screen (the default): filling the rest of the window, so a
//     prospect gets the roomy view first;
//   - Tablet: inside a portrait tablet frame (the kiosk orientation);
//   - Phone: inside a phone frame (the QR-guest view);
// so the framed views read as "this is what your guests see at the table".
//
// WHO GETS IT - all four must hold:
//   1. The tenant is a demo, of either kind, as getSettings reports it:
//        - the public demo (publicDemo: The Gilded Fork at demo.getsommsync.com,
//          anonymous marketing traffic), or
//        - a private demo (isDemo: a site built from one prospect's own menu),
//          unless it has expired - an expired demo shows its own notice plainly.
//      Read from the backend, never a hardcoded restaurant id, so a real
//      restaurant can never get it and a new demo gets it with no code change.
//      A private demo that converts to a paying restaurant loses isDemo, and
//      the frame goes with it (see the cache note below).
//   2. The viewer is on a desktop: a real mouse (hover + fine pointer) and a
//      window big enough to frame anything. Tablets, phones and the Fully Kiosk
//      fleet are touch devices and always get the plain full-screen app.
//   3. The page is not already the framed copy (it runs inside the iframe).
//   4. Not arriving through a manager link or a sign-in handoff, so staff
//      working on a demo tenant from a desktop get the plain app.
//
// HOW: in every view the app is an <iframe> of this same page under the bar -
// in Full screen it fills the space; in Tablet/Phone it runs at a real device
// size (800x1280 tablet portrait, 390x844 phone), scaled down to fit. It is the
// same iframe element throughout, so switching views keeps the visitor's place. A real iframe rather than a resized <div> because the
// app leans on the viewport throughout - position:fixed drawers and overlays,
// innerWidth/innerHeight breakpoints, visualViewport - and only an iframe gives
// it a genuine device-sized viewport. Same origin, so auth, the demo token and
// localStorage all behave exactly as on a tablet.
//
// Whether a tenant is a demo is cached per origin (each tenant is its own
// subdomain), so a repeat visit decides at once; a first desktop visit waits for
// one getSettings call, which the app makes before its first paint anyway. The
// cache is re-checked on every load, so a converted private demo loses the bar
// on its owner's next visit. The chosen view survives a reload for the rest of
// the browser session.
const DEMO_FRAME_CACHE_KEY = 'ss_demo_frame';  // '0' | 'public' | 'private'
const DEMO_FRAME_VIEW_KEY = 'ss_demo_frame_view';  // sessionStorage: 'full' | 'tablet' | 'phone'
const DEMO_FRAME_DEVICES = {
  full:   { label: 'Full screen' },
  tablet: { w: 800, h: 1280, bezel: 22, radius: 38, screenRadius: 12, label: 'Tablet' },
  phone:  { w: 390, h: 844,  bezel: 12, radius: 54, screenRadius: 42, label: 'Phone' },
};
const DEMO_FRAME_BAR_H = 64;

// Our own demo kiosk tablet (taken to prospects in person) runs the public demo
// in Fully Kiosk, where a "Start Free Trial" link to another site is a dead end.
// Its Fully start URL carries ?kiosk=1; the flag is remembered on the device so
// it survives the app dropping the query string.
const DEMO_KIOSK_KEY = 'ss_kiosk_device';
function isOwnKioskDevice() {
  try {
    if (new URLSearchParams(window.location.search).get('kiosk') === '1') {
      window.localStorage.setItem(DEMO_KIOSK_KEY, '1');
      return true;
    }
    return window.localStorage.getItem(DEMO_KIOSK_KEY) === '1';
  } catch (e) { return false; }
}
const DEMO_TRIAL_BANNER_DISMISSED_KEY = 'ss_trial_banner_dismissed';

function isDesktopViewer() {
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches
      && window.innerWidth >= 1024 && window.innerHeight >= 600;
  } catch (e) { return false; }
}

// What kind of demo getSettings says this tenant is: 'public', 'private' or
// null (a real restaurant, or an expired private demo).
function demoFrameKind(data) {
  if (!data) return null;
  if (data.publicDemo === true) return 'public';
  if (data.isDemo === true && data.demoExpired !== true) return 'private';
  return null;
}

function DemoFrameGate({ children, skip }) {
  const eligible = !skip && (() => {
    try { if (window.self !== window.top) return false; } catch (e) { return false; }
    return isDesktopViewer();
  })();
  // 'pending' | 'app' | 'public' | 'private'
  const [mode, setMode] = useState(() => {
    if (!eligible) return 'app';
    let cached = null;
    try { cached = window.localStorage.getItem(DEMO_FRAME_CACHE_KEY); } catch (e) {}
    return cached === 'public' || cached === 'private' ? cached : cached === '0' ? 'app' : 'pending';
  });
  const [view, setView] = useState(() => {
    let v = null;
    try { v = window.sessionStorage.getItem(DEMO_FRAME_VIEW_KEY); } catch (e) {}
    return v && DEMO_FRAME_DEVICES[v] ? v : 'full';
  });
  useEffect(() => {
    if (!eligible) return;
    let done = false;
    // Never leave a desktop owner on a blank page if settings are slow.
    const giveUp = setTimeout(() => { if (!done) setMode(m => (m === 'pending' ? 'app' : m)); }, 4000);
    rfetch(SETTINGS_URL).then(r => r.json()).then(data => {
      done = true;
      const kind = demoFrameKind(data);
      try { window.localStorage.setItem(DEMO_FRAME_CACHE_KEY, kind || '0'); } catch (e) {}
      // A pending page takes the answer. A page in the bar that is no longer a
      // demo (converted, or expired) drops it at once. A page already showing
      // the plain app keeps it; a newly-made demo gets the bar next load rather
      // than tearing down an app the visitor is already using.
      setMode(m => (m === 'app' ? m : (kind || 'app')));
    }).catch(() => { done = true; setMode(m => (m === 'pending' ? 'app' : m)); });
    return () => clearTimeout(giveUp);
  }, []);
  const choose = v => {
    try { window.sessionStorage.setItem(DEMO_FRAME_VIEW_KEY, v); } catch (e) {}
    setView(v);
  };
  if (mode === 'pending') return <div style={{ minHeight: '100vh', background: '#eceae4' }} />;
  if (mode === 'public' || mode === 'private') return <DemoDeviceFrame kind={mode} device={view} onDevice={choose} />;
  return children;
}

function DemoDeviceFrame({ kind = 'public', device = 'full', onDevice }) {
  const setDevice = onDevice;
  // This outer page owns the browser tab; the app inside the iframe titles only
  // its own document, which no tab shows. So name the tab here as well.
  useEffect(() => {
    rfetch(GET_PROFILE_URL).then(r => r.json()).then(d => setRestaurantTabTitle(d.name)).catch(() => {});
  }, []);
  const [win, setWin] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setWin({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    // The bar's wordmark font. Loaded only here, so the guest app never pays for it.
    if (!document.getElementById('demo-frame-font')) {
      const link = document.createElement('link');
      link.id = 'demo-frame-font';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@500;600;800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const full = device === 'full';
  const d = full ? DEMO_FRAME_DEVICES.tablet : DEMO_FRAME_DEVICES[device];
  // Room for the device: window minus bar, padding and the caption line.
  const availH = win.h - DEMO_FRAME_BAR_H - 32 - 34 - 2 * d.bezel;
  const availW = win.w - 64 - 2 * d.bezel;
  const scale = Math.max(0.3, Math.min(1, availH / d.h, availW / d.w));
  const screenW = Math.round(d.w * scale);
  const screenH = Math.round(d.h * scale);

  // Same page, inside the frame. The iframe's own copy sees window.top !==
  // window.self and renders the plain app.
  const src = window.location.pathname + window.location.search + window.location.hash;

  const font = "'Montserrat', 'Helvetica Neue', Arial, sans-serif";
  const tab = active => ({
    border: 'none', borderRadius: 999, padding: '9px 18px', minHeight: 36, cursor: 'pointer',
    fontFamily: font, fontSize: 13, fontWeight: 600,
    background: active ? '#ffffff' : 'transparent', color: active ? '#1a1817' : '#55534d',
    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
  });

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#eceae4', fontFamily: font, color: '#1a1817', overflow: 'hidden' }}>
      <header style={{ height: DEMO_FRAME_BAR_H, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '0 32px', background: '#f6f5f1', borderBottom: '1px solid #d9d6ce' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <SwirlingGlass stemColor="#1A1817" size={36} still />
          <span style={{ fontWeight: 800, fontSize: 21, letterSpacing: 1 }}>
            <span style={{ color: '#1a1817' }}>SYNC</span><span style={{ color: '#8e9413' }}>BEV</span>
          </span>
          <span style={{ marginLeft: 12, padding: '5px 10px', border: '1px solid #c9c5bb', borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: '#55534d' }}>{kind === 'private' ? 'Your demo' : 'Live demo'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <div role="group" aria-label="Preview size" style={{ display: 'flex', background: '#e2dfd7', borderRadius: 999, padding: 4, gap: 2 }}>
            {Object.keys(DEMO_FRAME_DEVICES).map(key => (
              <button key={key} type="button" aria-pressed={device === key} onClick={() => setDevice(key)} style={tab(device === key)}>
                {DEMO_FRAME_DEVICES[key].label}
              </button>
            ))}
          </div>
          {/* Public demo only. A private demo's prospect is already talking to
              us - that site was built for them - so a sign-up button there is noise.
              Opens the marketing site's trial page in a new tab so the demo stays open. */}
          {kind === 'public' && (
            <a href={DEMO_TRIAL_URL} target="_blank" rel="noopener noreferrer" style={{ background: '#1a1817', color: '#ffffff', textDecoration: 'none', fontSize: 14, fontWeight: 600, padding: '11px 18px', borderRadius: 8 }}>Start Free Trial</a>
          )}
        </div>
      </header>

      {/* One element tree for all three views - only styles change - so React
          keeps the same iframe and the visitor's place in the app survives a
          switch. Full screen drops the bezel and lets the screen fill the space. */}
      <main style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: full ? 'stretch' : 'center', justifyContent: 'center', gap: 14, padding: full ? 0 : '16px 32px' }}>
        <div style={full
          ? { flex: 1, minHeight: 0, display: 'flex' }
          : { background: '#1c1c1c', borderRadius: d.radius * scale + d.bezel / 2, padding: d.bezel, boxShadow: '0 30px 60px rgba(26,24,23,0.28), inset 0 0 0 2px #2e2e2e', flexShrink: 0, display: 'flex' }}>
          <div style={full
            ? { flex: 1, overflow: 'hidden', background: '#000000', position: 'relative' }
            : { width: screenW, height: screenH, borderRadius: d.screenRadius * scale, overflow: 'hidden', background: '#000000', position: 'relative' }}>
            <iframe
              title="SommSync live demo"
              src={src}
              style={full
                ? { width: '100%', height: '100%', border: 0, display: 'block' }
                : { width: d.w, height: d.h, border: 0, display: 'block', transform: `scale(${scale})`, transformOrigin: 'top left' }}
            />
          </div>
        </div>
        {!full && (
          <p style={{ margin: 0, fontSize: 13, color: '#6b6a65', textAlign: 'center' }}>
            {kind === 'private'
              ? "This is your menu as your guests would see it. At the table it runs full-screen on the tablet, locked in portrait."
              : "You're seeing what a guest sees at the table. On a restaurant table it runs full-screen on the tablet, locked in portrait."}
          </p>
        )}
      </main>
    </div>
  );
}

export default function App() {
  const urlParams = new URLSearchParams(window.location.search);

  // Remove the hard-reload cache-buster from the address bar. Read into
  // urlParams above first, so anything that inspects the query string this pass
  // still sees what it arrived with.
  useEffect(() => { stripReloadParam(); }, []);

  // An owner signing in at getsommsync.com arrives here with a one-time nonce.
  // Redeemed before anything renders, then stripped from the address bar so it
  // does not sit in history or leak through a referrer.
  const [handoff, setHandoff] = useState(() => urlParams.has('h') ? 'pending' : 'none');
  useEffect(() => {
    if (handoff !== 'pending') return;
    redeemHandoffFromUrl().then(r => setHandoff(r && r.ok ? 'done' : 'failed'));
  }, []);
  if (handoff === 'pending') {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: ADMIN_THEME.bgBase, color: ADMIN_THEME.textDim,
      fontFamily: ADMIN_THEME.fontSerif, fontSize: 13 }}>Signing in...</div>;
  }

  // SommSync super user admin portal.
  //
  // Only reachable on the platform host. There is no legitimate reason to open
  // the cross-tenant portal from a restaurant's own subdomain, and allowing it
  // means a tablet where a super user once signed in keeps portal access to
  // every tenant for as long as the session persists.
  //
  // This hides the UI, not the data — the endpoints do the real enforcing.
  if (urlParams.get('somm_admin') === 'true') {
    return ADMIN_PORTAL_HOSTS.includes(window.location.hostname.toLowerCase())
      ? <SommSyncAdminPortal />
      : <AdminPortalBlocked />;
  }
  // Short DB-backed code: ?m=ABC123
  const menuCode = urlParams.get('m');
  if (menuCode) return <GuestMenuLoader menuCode={menuCode} />;
  // Legacy long-URL code: ?menu=BASE64 (kept for backward compatibility)
  const menuParam = urlParams.get('menu');
  if (menuParam) {
    try {
      const sharedFavorites = decodeFavorites(menuParam);
      if (sharedFavorites && sharedFavorites.length > 0) return <GuestMenuScreen favorites={sharedFavorites} savedAt={sharedFavorites._savedAt} />;
    } catch(e) {}
  }
  // RESTAURANT_ID is null when the URL gave no ?restaurant= param and the
  // hostname isn't a real tenant subdomain (e.g. the bare platform URL with
  // nothing else attached). This used to silently fall through to Corduroy's
  // real menu; now it shows an explicit message instead of guessing at a
  // real restaurant's data. The menu-code path above DOES depend on it: the
  // saved menu is looked up under RESTAURANT_ID (from ?restaurant= or the
  // subdomain), which the emailed link and the QR code both carry.
  if (!RESTAURANT_ID) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: ADMIN_THEME.bgBase, color: ADMIN_THEME.textDim,
      fontFamily: ADMIN_THEME.fontSerif, fontSize: 13, textAlign: 'center', padding: 24 }}>
      No restaurant specified for this address.
    </div>;
  }
  // Desktop visitors to a demo (public or private) get the SyncBev bar with a
  // Full screen / Tablet / Phone switch; every other case renders the app
  // exactly as before. See DemoFrameGate.
  const skipDemoFrame = urlParams.has('manager') || handoff !== 'none';
  return <ErrorBoundary><DemoFrameGate skip={skipDemoFrame}><AppContent /></DemoFrameGate></ErrorBoundary>;
}
